// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { ptModuleTips } from './pt-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
import { enVehicleMonitoring, enRecordLinks } from './en-extras.js';
import { enQuestionnaireSeed } from './en-extras.js';
export const pt = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            update_checks_recorded: 'Update checks recorded',
            update_devices_count: 'Linked devices: {count}',
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Inicie sessão na sua conta',
        email: 'E-mail',
        password: 'Palavra-passe',
        sign_in: 'Iniciar sessão',
        signing_in: 'A iniciar sessão...',
        mfa_code: 'Código MFA',
        mfa_placeholder: 'Introduza o código de 6 dígitos',
        mfa_instruction: 'Introduza o código de 6 dígitos da sua aplicação de autenticação.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Verificar',
        verifying: 'A verificar...',
        forgot_password: 'Esqueceu a palavra-passe?',
        account_locked: 'A conta está bloqueada. Tente novamente mais tarde.',
        account_deactivated: 'A conta foi desativada. Contacte o seu administrador.',
        invalid_mfa: 'Código MFA inválido. Tente novamente.',
        invalid_credentials: 'E-mail ou palavra-passe inválidos.',
        login_failed: 'Falha no início de sessão',
        session_limit_reached: 'A sua conta atingiu o limite de sessões ativas. Termine a sessão noutro dispositivo ou peça ao administrador que revogue uma sessão existente e tente novamente.',
        session_expired: 'A sua sessão expirou. Recarregue a página e tente novamente.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Esqueceu a Palavra-passe',
            subtitle: 'Introduza o seu endereço de e-mail e enviaremos uma ligação para repor a sua palavra-passe.',
            send: 'Enviar Ligação de Reposição',
            sending: 'A enviar...',
            success: 'Se esse e-mail existir, foi enviada uma ligação para reposição da palavra-passe.',
            back_to_login: 'Voltar ao Início de Sessão',
        },
        reset_password: {
            title: 'Repor Palavra-passe',
            subtitle: 'Introduza a sua nova palavra-passe abaixo.',
            new_password: 'Nova Palavra-passe',
            confirm_password: 'Confirmar Palavra-passe',
            reset: 'Repor Palavra-passe',
            resetting: 'A repor...',
            success: 'A sua palavra-passe foi reposta com sucesso.',
            go_to_login: 'Ir para o Início de Sessão',
            password_mismatch: 'As palavras-passe não coincidem.',
            invalid_token: 'Token de reposição inválido ou em falta. Solicite uma nova ligação de reposição de palavra-passe.',
            requirements_title: 'Requisitos da palavra-passe:',
            password_requirements: 'A palavra-passe deve cumprir os seguintes requisitos:',
            rules: {
                min_length: 'Pelo menos 12 caracteres',
                uppercase: 'Pelo menos uma letra maiúscula',
                lowercase: 'Pelo menos uma letra minúscula',
                digit: 'Pelo menos um dígito',
                special: 'Pelo menos um carácter especial',
            },
        },
        verify_email: {
            title: 'Verificação de E-mail',
            verifying: 'A verificar o seu endereço de e-mail...',
            success: 'O seu e-mail foi verificado com sucesso.',
            invalid_token: 'Token de verificação inválido ou em falta.',
            failed: 'A verificação de e-mail falhou. O token pode ser inválido ou ter expirado.',
            go_to_login: 'Ir para o Início de Sessão',
        },
        offline: {
            title: 'Está sem ligação',
            message: 'Parece que perdeu a ligação à Internet. Verifique a sua rede e tente novamente.',
        },
    },
    intake: {
        title: 'Pedido de Exercício de Direitos',
        subtitle: 'Submeta um pedido de exercício de direitos à nossa organização',
        fields: {
            full_name: 'Nome Completo',
            email: 'Endereço de Correio Eletrónico',
            phone: 'Número de Telefone (opcional)',
            request_type: 'Tipo de Pedido',
            description: 'Descreva o Seu Pedido',
            preferred_language: 'Idioma Preferido',
            consent: 'Dou o meu consentimento para o tratamento deste pedido',
        },
        submit: 'Submeter Pedido',
        validation: {
            name_required: 'O nome completo é obrigatório',
            email_required: 'É necessário um endereço de correio eletrónico válido',
            type_required: 'Por favor selecione um tipo de pedido',
            description_required: 'Por favor descreva o seu pedido',
            consent_required: 'Deve consentir o tratamento para continuar',
        },
        error_title: 'Erro',
        config_load_error: 'Não foi possível carregar a configuração. Por favor, verifique o URL.',
        organization_label: 'Organização',
        customer_reference: 'Número de referência do cliente',
        country: 'País / Jurisdição',
        preferred_channel: 'Canal de resposta preferido',
        channels: {
            email: 'E-mail',
            postal: 'Correio',
            phone: 'Telefone',
            portal: 'Portal',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Pedido Recebido',
            message: 'O seu pedido foi registado com sucesso. O seu número de referência é:',
            next_steps: 'Iremos tratar o seu pedido com a maior brevidade possível, em conformidade com a legislação aplicável. O prazo previsto de tratamento é de 30 dias úteis.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Acesso (DSAR)',
        deletion: 'Apagamento',
        correction: 'Retificação / Atualização',
        objection: 'Oposição',
        portability: 'Portabilidade dos Dados',
        restriction: 'Limitação do Tratamento',
        consent_withdrawal: 'Retirada do consentimento',
        protective_measures: 'Solicitar medidas e soluções de proteção de dados pessoais',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Novo',
        in_review: 'Em Análise',
        waiting_on_requester: 'A Aguardar Resposta do Requerente',
        waiting_on_internal_data: 'A Aguardar Dados Internos',
        approved: 'Aprovado',
        closed: 'Encerrado',
        rejected: 'Rejeitado',
    },
    admin: {
        nav: {
            rights_requests: 'Pedidos de Direitos',
            templates: 'Modelos',
            response_templates: 'Modelos de Resposta',
            data_mapping: 'Registo de Atividades de Tratamento',
            governance: 'Governação',
            consent: 'Consentimento',
            assessments: 'Avaliações',
            classification: 'Classificação',
            monitoring: 'Monitorização',
            incidents: 'Incidentes',
            onboarding: 'Integração',
            connectors: 'Conectores',
            vendor_risk: 'Risco de Fornecedores',
            deletion_orchestration: 'Orquestração de Apagamento',
            discovery: 'Descoberta de Dados',
            incident_ops: 'Operações de Incidentes',
            program_governance: 'Governação de Programa',
            access_accountability: 'Responsabilização de Acessos',
            consent_properties: 'Propriedades de Consentimento',
            localization: 'Localização',
            regulatory: 'Regulatório',
            partner_workspace: 'Espaço de Parceiros',
            audit_trail: 'Trilho de Auditoria',
            forseti: 'Forseti',
            settings: 'Definições',
            help_center: 'Centro de Ajuda',
            dashboard: 'Painel de Controlo',
            platform: 'Plataforma',
            integrations: 'Integrações',
            cmp_dsar_sync: 'Sincronização CMP DSAR',
            cmp_integration: 'Integração CMP',
            purpose_mapping: 'Mapeamento de Finalidades',
            webhook_config: 'Configuração de Webhooks',
            bulk_sync: 'Sincronização em Massa',
            privacy_scanner: 'Analisador de Privacidade',
            privacy_monitoring: 'Monitorização de Privacidade',
            real_time_scanner: 'Analisador em Tempo Real',
            compliance_forms: 'Formulários de Conformidade',
            forms_dashboard: 'Painel de Formulários',
            form_templates: 'Modelos',
            form_catalog: 'Catálogo de Formulários',
            pack_catalog: 'Catálogo de Pacotes',
            org_groups: 'Grupos de Organizações',
            consolidation: 'Consolidação',
            organizations: 'Organizações',
            org_list: 'Todas as organizações',
            intake_configs: 'Configurações de entrada',
            task_management: 'Gestão de tarefas',
            unified_audit: 'Auditoria Unificada',
            evidence_packs: 'Pacotes de Evidência',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Períodos de Auditoria',
            regulatory_updates: 'Atualizações Regulatórias',
            departments: 'Departamentos',
        },
        sidebar_category_data_privacy_rights: 'Privacidade de Dados & Direitos',
        sidebar_category_vendor_third_party: 'Gestão de Fornecedores & Terceiros',
        sidebar_category_risk_assessment: 'Risco & Avaliação',
        sidebar_category_operations_automation: 'Operações & Automatização',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Gestão de Tarefas & Fluxo de Trabalho',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP e Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'Registo de atividades',
            data_mapping_classification: 'Classificação',
            data_mapping_governance: 'Governação',
            access_live_monitoring: 'Monitorização em tempo real',
            access_reviews_investigations: 'Revisões e Investigações',
            access_evidence_policies: 'Evidências e Políticas',
            classification_taxonomies: 'Taxonomias e Etiquetas',
            classification_workflow: 'Fluxo de trabalho',
            classification_insights: 'Análises',
            connectors_runtime: 'Tempo de execução',
            connectors_due_diligence: 'Diligência devida',
            connectors_coverage: 'Cobertura',
            cp_definitions: 'Definições',
            cp_lifecycle: 'Ciclo de vida',
            cp_quality: 'Qualidade e Evidências',
            deletion_execution: 'Execução',
            deletion_vendors: 'Fornecedores',
            deletion_evidence: 'Evidências e Integração',
            discovery_rules: 'Regras e Modelos',
            discovery_scanning: 'Análise',
            discovery_review: 'Fluxo de revisão',
            discovery_insights: 'Análises',
            governance_oversight: 'Supervisão',
            governance_reporting: 'Relatórios',
            governance_vendors: 'Fornecedores e Transferências internacionais',
            governance_lifecycle: 'Ciclo de vida',
            incident_ops_dashboards: 'Painéis',
            incident_ops_sla: 'Configuração de SLA',
            incident_ops_playbooks: 'Manuais',
            incident_ops_vendor_regulator: 'Fornecedor e Autoridade',
            incident_ops_evidence: 'Evidências e Revisão',
            incidents_workload: 'Carga de trabalho',
            incidents_regulatory: 'Regulatório',
            incidents_reporting: 'Relatórios',
            localization_content: 'Conteúdo',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observabilidade',
            monitoring_metrics: 'Métricas',
            monitoring_programs: 'Programas',
            monitoring_tasks: 'Tarefas',
            monitoring_controls: 'Controlos',
            org_entities: 'Entidades',
            org_config: 'Configuração',
            org_frameworks: 'Referenciais e Consolidação',
            partner_overview: 'Visão geral',
            partner_partners: 'Parceiros',
            partner_delivery: 'Entrega',
            partner_support: 'Suporte',
            platform_identity: 'Identidade e Funções',
            platform_access_control: 'Controlo de acesso',
            platform_auth_session: 'Autenticação e Sessão',
            platform_access_reviews: 'Revisões de acesso',
            platform_files: 'Ficheiros',
            platform_notifications: 'Notificações',
            platform_module_config: 'Configuração de módulos',
            platform_ops: 'Operações e Registos',
            pg_health: 'Estado do programa',
            pg_maturity: 'Maturidade',
            pg_audit: 'Auditoria e Evidências',
            pg_training: 'Formação',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Obrigações',
            pg_documents: 'Documentos',
            vr_overview: 'Visão geral',
            vr_onboarding: 'Integração',
            vr_assessment: 'Avaliação',
            vr_oversight: 'Supervisão',
        },
        tickets: {
            title: 'Pedidos de direitos',
            create: 'Adicionar pedido',
            filters: 'Filtros',
            search_placeholder: 'Search tickets...',
            export_csv: 'Exportar CSV',
            exporting: 'Exportando...',
            bulk_export_failed: 'Falha na exportação em massa',
            bulk_export: {
                label: 'Exportar',
                format: {
                    pdf: 'PDF Resumo',
                    zip: 'ZIP (pacotes de evidências por caso)',
                    csv: 'CSV',
                },
                warning_over_50: 'Isso gerará aproximadamente {count} pacotes de casos - pode levar vários minutos. Continuar?',
                blocked_over_500: 'Limite o filtro para 500 casos ou menos antes de exportar ZIP (atual: {count}).',
                success: 'Exportação em massa salva no navegador de arquivos ({count} casos)',
                failed: 'Falha na exportação em massa',
                continue: 'Continuar',
                cancel: 'Cancelar',
            },
            no_results: 'Nenhum requests encontrado',
            ticket_id: 'ID do ticket',
            request_type: 'Pedido Tipo',
            requester: 'Requerente',
            source: 'Fonte',
            all_sources: 'Todas as fontes',
            assigned_to: 'Atribuído Para',
            created: 'Criado',
            load_error: 'Falha ao load cases. Por favor, tente novamente.',
            case_creation_failed: 'Falha ao criar caso',
            business_unit: 'Unidade de negócio',
            reference: 'Referência',
            sla: {
                title: 'Estado SLA',
                on_track: '{days} dias restantes',
                approaching: 'Prazo próximo',
                overdue: '{days} dias em atraso',
                ack_overdue: '{hours}h além do prazo de confirmação',
                ack_warning: 'Confirmação prestes a vencer',
                closed: 'Encerrado',
                completed_in: 'Concluído em {days} dias',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Resposta Modelos',
            new: 'Novo modelo',
            edit: 'Editar Modelo',
            all_types: 'Todos os tipos',
            all_locales: 'Todos os idiomas',
            type_access: 'Acesso/DSAR',
            type_deletion: 'Eliminação',
            type_correction: 'Correção',
            type_objection: 'Oposição',
            type_portability: 'Portabilidade',
            type_other: 'Outro',
            type_restriction: 'Restrição',
            type_all: 'All',
            locale_en: 'Inglês',
            locale_vi: 'Vietnamita',
            locale_all: 'Ambos',
            name: 'Nome',
            request_type: 'Pedido Tipo',
            locale: 'Idioma',
            subject: 'Titular',
            body: 'Corpo',
            name_placeholder: 'Nome do modelo...',
            subject_placeholder: 'Assunto do e-mail/carta...',
            body_placeholder: 'Corpo do modelo. Use {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} como espaços reservados.',
            available_placeholders: 'Marcadores de posição disponíveis:',
            create_template: 'Adicionar modelo',
            preview: 'Pré-visualização',
            close_preview: 'Fechar pré-visualização',
            default_badge: 'Predefinido',
            no_templates: 'Nenhum templates encontrado',
            confirm_delete: 'Eliminar este template?',
            load_error: 'Falha ao load templates. Por favor, tente novamente.',
            create_error: 'Falha ao create template. Por favor, tente novamente.',
            update_error: 'Falha ao update template. Por favor, tente novamente.',
            delete_error: 'Falha ao delete template. Por favor, tente novamente.',
            preview_error: 'Falha ao preview template. Por favor, tente novamente.',
            response_templates_badge: 'Modelos de Resposta',
            browse_form_templates: 'Explorar Modelos de Formulários de Conformidade',
            templates_loaded: 'Modelos carregados',
            sample_organization: 'Organização de exemplo',
        },
        detail: {
            requester_info: 'Informações do requerente',
            status: 'Estado',
            assignment: 'Atribuição',
            notes: 'Notas',
            audit_trail: 'Trilha de auditoria',
            add_note: 'Adicionar Note',
            case_not_found: 'Caso não encontrado',
            load_error: 'Falha ao load case. Por favor, tente novamente.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Falha ao add note. Por favor, tente novamente.',
            submitted_from: 'Enviado de',
            via_cmp: 'através de AesirX CMP',
            cmp_ticket: 'Ticket CMP:',
            select_template: 'Selecionar um Modelo',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Detalhes',
                evidence: 'Evidência',
                communications: 'Comunicações',
                escalations: 'Escalamentos',
                audit_trail: 'Trilha de auditoria',
                fulfillment: 'Cumprimento',
                response: 'Resposta',
                extension: 'Extensão',
            },
            requester: {
                name: 'Nome',
                email: 'E-mail',
                phone: 'Telefone',
                language: 'Idioma',
                source: 'Fonte',
                customer_ref: 'Ref. do cliente',
                country: 'País',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Canal preferido',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Em Rastrear',
                due_soon: 'Prazo próximo',
                overdue: 'Em atraso',
                days_remaining: 'd restantes',
                due_date: 'Data de vencimento',
                breached: 'Violado',
                paused: 'Em pausa',
                total_paused_days: 'Total de dias em pausa',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Dias decorridos',
                progress: 'Progresso',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identidade Verificação',
                verified: 'Verificado',
                not_verified: 'Não verificado',
                method: 'Método',
                notes: 'Notas',
                verified_by: 'Verificado Por',
                verified_at: 'Verificado Em',
                warning: 'Identidade não yet verificado.',
                verify_button: 'Verificar Identidade',
                select_method: '-- Selecionar --',
                methods: {
                    email_confirmation: 'Confirmação por e-mail',
                    id_document: 'ID Documento',
                    phone_call: 'Chamada telefónica',
                    in_person: 'Presencialmente',
                    other: 'Outro',
                },
            },
            closure: {
                reason: 'Motivo de encerramento',
                summary: 'Resumo de encerramento',
                summary_placeholder: 'Resumo de como o pedido foi resolvido...',
                reason_required: 'O resumo e o motivo de encerramento são obrigatórios',
                response_not_sent: 'Uma resposta formal deve ser enviada ao titular dos dados antes de encerrar este caso',
                reasons: {
                    fulfilled: 'Cumprido',
                    partially_fulfilled: 'Parcialmente cumprido',
                    unfounded: 'Infundado',
                    excessive: 'Excessivo',
                    withdrawn: 'Retirado',
                    duplicate: 'Duplicar',
                    identity_unverified: 'Identidade não verificada',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exportar',
            export_pdf: 'Exportar PDF',
            export_zip: 'Exportar ZIP',
            case_file_pdf: 'Exportar PDF (arquivo de caso)',
            evidence_pack_zip: 'Exportar ZIP (pacote de evidências)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Sinalizar como causador de violação',
                modal: {
                    title: 'Sinalizar caso como causador de violação',
                    type_label: 'Tipo de violação',
                    type: {
                        misdirected_response: 'Resposta mal direcionada',
                        unauthorised_disclosure: 'Divulgação não autorizada',
                        identity_mismatch: 'Incompatibilidade de identidade',
                        other: 'Outro',
                    },
                    description: 'Descrição (opcional)',
                    description_placeholder: 'O que aconteceu? (máximo de 1000 caracteres)',
                    confirm: 'Sinalizar e iniciar o relógio de notificação',
                    cancel: 'Cancelar',
                    flagging: 'Sinalizando...',
                },
                banner: {
                    title: 'Violação sinalizada: {type}',
                    detected_at: 'Detectado em {timestamp}',
                    filing_deadline: 'Prazo de notificação: em {hours} horas',
                    overdue: 'Prazo de notificação: VENCIDO',
                },
                toasts: {
                    flagged: 'Caso sinalizado como causador de violação - relógio de notificação iniciado',
                    flag_failed: 'Falha ao sinalizar o caso como causador da violação',
                },
            },
            export: {
                generating: 'Gerando... aguarde',
                success: 'Exportação salva no navegador de arquivos',
                failed: 'Falha na exportação - verifique o registro de auditoria',
            },
            no_notes: 'Sem notas ainda',
            assign_placeholder: 'ID do utilizador a atribuir...',
            reassign: 'Reatribuir',
            evidence: {
                checklist_title: 'Lista de verificação de evidências',
                complete_pct: '% concluído',
                no_checklist: 'Sem itens de lista',
                attachments: 'Anexos',
                upload_attachment: 'Carregar anexo',
                upload_button: 'Carregar',
                uploading: 'A carregar...',
                attachment_uploaded: 'Anexo carregado com sucesso',
                upload_error: 'Falha ao carregar anexo',
                category: 'Categoria',
                categories: {
                    identity_proof: 'Prova de identidade',
                    correspondence: 'Correspondência',
                    internal_data: 'Dados internos',
                    legal: 'Jurídico',
                    other: 'Outro',
                },
                no_attachments: 'Sem anexos',
                references_title: 'Referências de evidências',
                no_references: 'Sem referências',
                add_reference: 'Adicionar Reference',
                ref_type: 'Tipo',
                ref_value: 'Valor',
                ref_desc: 'Descrição',
                ref_value_placeholder: 'URL, ID de e-mail ou referência...',
                ref_desc_placeholder: 'Descrição...',
                ref_types: {
                    url: 'URL',
                    email_id: 'E-mail ID',
                    crm_record: 'Registo de CRM',
                    ticket_ref: 'Referência do ticket',
                    other: 'Outro',
                },
            },
            communications: {
                title: 'Comunicações',
                add: 'Adicionar Message',
                no_records: 'Sem comunicações registadas',
                direction: 'Direção',
                channel: 'Canal',
                visibility: 'Visibilidade',
                subject: 'Titular',
                body: 'Corpo',
                send: 'Enviar',
                inbound: 'Entrada',
                outbound: 'Saída',
                internal: 'Interno',
                external: 'Externo',
                tab_all: 'All',
                tab_internal: 'Interno',
                tab_external: 'Externo',
                subject_placeholder: 'Titular (optional)',
                body_placeholder: 'Corpo da mensagem...',
                channels: {
                    email: 'E-mail',
                    portal: 'Portal',
                    phone: 'Telefone',
                    postal: 'Correio postal',
                    in_person: 'Presencialmente',
                },
                apply_template: 'Aplicar Modelo',
                select_template: 'Selecionar modelo...',
                no_templates: 'Nenhum modelo disponível',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Modelo aplicado à resposta.',
                template_apply_error: 'Falha ao aplicar modelo.',
            },
            escalations: {
                title: 'Escalamento Histórico',
                no_events: 'Sem eventos de escalada',
                tier: 'Nível',
                threshold: 'Limiar',
                action: 'Ação',
                target: 'Destino',
                triggered_at: 'Acionado em',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'Sincronização AesirX CMP',
                sync_status: 'Sincronizar Estado',
                last_synced: 'Último Sincronizado',
                not_synced: 'Não Sincronizado',
                resync: 'Ressincronizar agora',
                syncing: 'A sincronizar...',
                sync_history: 'Sincronizar Histórico (Last 10)',
                direction: 'Direção',
                time: 'Hora',
                retries: 'Novas tentativas',
                error_col: 'Erro',
            },
            audit: {
                filter_event_placeholder: 'Filtrar por tipo de evento...',
                filter_actor_placeholder: 'Filtrar por ator...',
            },
            fulfillment: {
                title: 'Cumprimento departamental',
                assign_departments: 'Atribuir departamentos',
                department: 'Departamento',
                assignee: 'Responsável',
                deadline: 'Prazo',
                evidence_count: 'Evidência',
                signoff: 'Aprovação',
                signed_off: 'Aprovado',
                pending_signoff: 'Pendente',
                start_button: 'Iniciar',
                complete_button: 'Marcar como concluído',
                signoff_button: 'Assinar',
                no_tasks: 'Nenhuma tarefa de cumprimento atribuída ainda.',
                tasks_table: 'Tarefas de cumprimento',
                progress_label: 'Progresso de cumprimento',
                overdue: 'em atraso',
                assigned_success: 'Tarefa de cumprimento departamental atribuída com sucesso.',
                assign_error: 'Falha ao atribuir tarefa de cumprimento.',
                signoff_success: 'Aprovação departamental registada.',
                signoff_error: 'Falha ao registar aprovação.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Adicionar evidência',
                evidence_uploaded: 'Evidência carregada e ligada',
                evidence_upload_error: 'Falha ao carregar evidência',
                suggest_data_stores: 'Sugerir armazéns de dados',
                suggest_error: 'Falha ao carregar sugestões de armazenamento.',
                suggest_stores_title: 'Armazenamentos de dados sugeridos',
                suggest_stores_none: 'Nenhum armazenamento de dados corresponde a este caso. Configure sistemas no Mapeamento de Dados para melhorar as sugestões.',
                suggest_stores_add: 'Adicionar ao caso',
                suggest_stores_added: 'Armazenamento de dados adicionado ao caso',
                no_departments_title: 'Nenhum departamento configurado',
                no_departments_body: 'Os departamentos permitem encaminhar tarefas de atendimento de DSR para uma função (Privacidade, Jurídico, RH, Operações de TI). Instale departamentos de amostra ou peça ao seu administrador para configurar departamentos nas configurações da organização.',
                no_departments_cta: 'Instalar dados de amostra',
                department_placeholder: 'Nome do departamento...',
                assignee_placeholder: 'ID do utilizador a atribuir...',
                assign_button: 'Atribuir',
                status_pending: 'Pendente',
                status_in_progress: 'Em andamento',
                status_completed: 'Concluído',
                status_verified: 'Verificado',
                confirm_approve: 'Tem certeza de que deseja aprovar esta extensão?',
                confirm_deny: 'Tem certeza de que deseja negar esta extensão?',
            },
            response: {
                title: 'Geração de resposta',
                select_template: 'Modelo de resposta',
                choose_template: 'Selecionar um modelo...',
                generate: 'Gerar resposta',
                preview: 'Pré-visualização da resposta',
                send: 'Enviar resposta',
                delivery_method: {
                    label: 'Método de entrega',
                    email: 'E-mail',
                    portal: 'Portal',
                    postal: 'Correio postal',
                    in_person: 'Pessoalmente',
                },
                recipient: {
                    email: 'E-mail do destinatário',
                    portal: 'ID de usuário do portal',
                    address: 'Endereço postal',
                },
                auto_generate_btn: 'Gerar e enviar resposta',
                auto_generate_success: 'Resposta gerada e entregue',
                auto_generate_missing_recipient: 'Forneça um destinatário antes de enviar.',
                auto_fields: 'Campos preenchidos automaticamente',
                case_id: 'ID do caso',
                requester_name: 'Nome do requerente',
                rights_type: 'Tipo de direito',
                history: 'Respostas enviadas',
                generated_at: 'Gerado',
                confirmation: 'Confirmação',
                sent_label: 'Enviado',
                draft_label: 'Rascunho',
                generated_success: 'Resposta gerada com sucesso.',
                generate_error: 'Falha ao gerar resposta.',
                sent_success: 'Resposta enviada com sucesso.',
                send_error: 'Falha ao enviar resposta.',
                load_error: 'Falha ao carregar modelos de resposta.',
                pdf_title: 'Carta de resposta ao titular dos dados',
                download_pdf: 'Descarregar PDF',
                download_all: 'Descarregar tudo',
                download_error: 'Falha ao descarregar a resposta',
            },
            extension: {
                title: 'Extensões de prazo',
                acknowledgment_deadline: 'Prazo de confirmação',
                fulfillment_deadline: 'Prazo de cumprimento',
                request_extension: 'Solicitar extensão',
                reason: 'Motivo',
                reason_placeholder: 'Motivo para solicitar tempo adicional...',
                additional_days: 'Dias adicionais',
                submit_request: 'Submeter pedido de extensão',
                history: 'Histórico de extensões',
                requested_at: 'Solicitado',
                approve: 'Aprovar',
                deny: 'Rejeitar',
                request_success: 'Pedido de extensão submetido.',
                request_error: 'Falha ao submeter pedido de extensão.',
                approved_success: 'Extensão aprovada.',
                denied_success: 'Extensão rejeitada.',
                decision_error: 'Falha ao processar decisão de extensão.',
                status_pending: 'Pendente',
                status_approved: 'Aprovado',
                status_denied: 'Negado',
                confirm_approve: 'Tem certeza de que deseja aprovar esta solicitação?',
                confirm_deny: 'Tem certeza de que deseja negar esta solicitação?',
                decided_by: 'Decidido por',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Exportar CSV',
            exporting: 'A exportar...',
            search_placeholder: 'Pesquisar detalhes de auditoria...',
            filter_by_actor: 'Filtrar por ator...',
            all_event_types: 'Todos Evento Types',
            seq: '#',
            timestamp: 'Marca temporal',
            event: 'Evento',
            case_col: 'Caso',
            actor: 'Ator',
            summary: 'Resumo',
            no_events: 'Nenhum audit events encontrado',
            export_success: 'Trilha de auditoria exportada com sucesso.',
            export_error: 'Falha ao export audit csv. Por favor, tente novamente.',
            load_error: 'Falha ao load audit trail. Por favor, tente novamente.',
            announce_loaded: 'Trilha de auditoria carregada',
            start_date: 'Data de início',
            end_date: 'Data de fim',
            per_page: '{count} / página',
            unknown: 'Desconhecido',
            source_filter: {
                label: 'Fonte',
                all: 'Todos',
                module: 'Módulo',
                forseti: 'Forseti',
                aria_label: 'Filtrar eventos de auditoria por origem',
            },
            event_types: {
                case_created: 'Caso criado',
                status_changed: 'Estado alterado',
                case_assigned: 'Caso atribuído',
                note_added: 'Nota adicionada',
                attachment_added: 'Anexo adicionado',
                attachment_deleted: 'Anexo Eliminado',
                reference_added: 'Referência adicionada',
                checklist_updated: 'Lista de verificação atualizada',
                identity_verified: 'Identidade Verificado',
                closure_summary_added: 'Resumo de encerramento',
                approval_granted: 'Aprovação concedida',
                template_applied: 'Modelo aplicado',
                export_generated: 'Exportação gerada',
                sla_warning: 'SLA Aviso',
                sla_overdue: 'SLA Em atraso',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Trilha de Auditoria Unificada',
                module_filter: 'Módulo',
                framework_filter: 'Referencial',
                select_framework: 'Selecionar um referencial...',
                entity_type_filter: 'Tipo de entidade',
                action_filter: 'Ação',
                actor_filter: 'Ator',
                select_actor: 'Selecionar um ator...',
                search_filter: 'Pesquisar',
                search_placeholder: 'Pesquisar eventos de auditoria...',
                filters_heading: 'Filtros',
                date_range: 'Intervalo de datas',
                no_events_found: 'Nenhum evento de auditoria encontrado.',
                event_count: 'Contagem de eventos',
                action: 'Ação',
                actor: 'Ator',
                date_range_start: 'Início do intervalo de datas',
                date_range_end: 'Fim do intervalo de datas',
                filtering_events: 'Filtrando eventos de auditoria',
                framework_codes: 'Códigos de enquadramento',
                entity_type_col: 'Tipo de entidade',
                aria_table: 'Eventos de auditoria unificados',
                page_of: 'Página {page} de {total}',
                entity_types: {
                    user: 'Usuário',
                    case: 'Caso',
                    incident: 'Incidente',
                    assessment: 'Avaliação',
                    vendor: 'Fornecedor',
                    consent: 'Consentimento',
                    policy: 'Política',
                    data_map: 'Mapa de dados',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Criar',
                    update: 'Atualizar',
                    delete: 'Excluir',
                    view: 'Ver',
                    export: 'Exportar',
                    status_change: 'Mudança de status',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Pacotes de Evidência de Auditoria',
                assemble_pack: 'Montar Pacote de Evidência',
                framework: 'Referencial',
                select_framework: 'Selecionar um referencial...',
                framework_code_label: 'Código da estrutura',
                entity_types: 'Tipos de entidade',
                select_entity_types: 'Selecionar tipos de entidade...',
                period: 'Período',
                period_start: 'Início do período',
                period_end: 'Fim do período',
                completeness: 'Completude',
                no_packs_found: 'Nenhum pacote de evidência encontrado.',
                verify: 'Verificar',
                export_pack: 'Exportar',
                exporting: 'A exportar...',
                pack_assembled: 'Pacote de evidências montado',
                pack_exported: 'Pacote de evidências exportado',
                aria_table: 'Pacotes de evidências de auditoria',
                aria_export_pack: 'Exportar pacote',
                export_format: 'Formato de exportação',
                export_in_progress: 'Exportação em andamento...',
                status_draft: 'Rascunho',
                status_assembled: 'Montado',
                status_verified: 'Verificado',
                status_submitted: 'Submetido',
                entity_type_labels: {
                    user: 'Usuário',
                    case: 'Caso',
                    incident: 'Incidente',
                    assessment: 'Avaliação',
                    vendor: 'Fornecedor',
                    consent: 'Consentimento',
                    policy: 'Política',
                    data_map: 'Mapa de dados',
                },
                template_id: 'ID do modelo',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Módulo',
                    action: 'Ação',
                    actor_id: 'Ator',
                    actor_role: 'Actor Role',
                    entity_type: 'Tipo de entidade',
                    entity_id: 'ID da entidade',
                    summary: 'Resumo',
                    timestamp: 'Carimbo de data e hora',
                    record_type: 'Record Type',
                    framework_code: 'Estrutura',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Pacote de evidências — Enquadramento: {framework}',
                export_header_period: 'Período: {start} a {end}',
                export_header_completeness: 'Completude: {score}%',
                export_header_status: 'Estado: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Períodos de Auditoria',
                create_period: 'Criar Período de Auditoria',
                framework_code_label: 'Código da estrutura',
                select_framework: 'Selecionar um referencial...',
                period_label: 'Rótulo do período',
                period_start: 'Início do período',
                period_end: 'Fim do período',
                start: 'Começar',
                end: 'Fim',
                framework: 'Estrutura',
                retention_days: 'Dias de retenção',
                section_period_dates: 'Datas do período',
                no_periods_found: 'Nenhum período de auditoria encontrado.',
                period_created: 'Período de auditoria criado',
                aria_table: 'Períodos de auditoria',
                suggested_boundaries: 'Limites sugeridos',
                suggested_boundaries_info: 'Os limites de período sugeridos derivam do ciclo de reporte regulatório de cada enquadramento. Ajuste conforme necessário para a sua organização.',
                reporting_cycle: 'Ciclo de reporte',
                suggested_start: 'Início sugerido',
                suggested_end: 'Fim sugerido',
                suggested_next: 'Próximo período sugerido',
                cycle_monthly: 'Mensalmente',
                cycle_quarterly: 'Trimestralmente',
                cycle_semi_annual: 'Semestral',
                cycle_annual: 'Anual',
                cycle_custom: 'Personalizado',
                period_deleted: 'Período de auditoria excluído',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Referência do cliente',
            country: 'País / Jurisdição',
            preferred_channel: 'Canal preferido',
            channels: {
                email: 'E-mail',
                postal: 'Correio postal',
                phone: 'Telefone',
                portal: 'Portal',
            },
            source: 'Fonte',
            sources: {
                admin_manual: 'Manual de administração',
                public_form: 'Formulário Público',
                cmp_gateway: 'CMP Portal',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefone recebido',
                postal_received: 'Postal Recebido',
                email_received: 'E-mail recebido',
                in_person: 'Pessoalmente',
                legacy_import: 'Importação herdada',
            },
            required_fields: 'O tipo de pedido, o nome e o e-mail são obrigatórios',
            create_error: 'Falha ao create case. Por favor, tente novamente.',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Definições',
            request_types: 'Tipos de Pedido',
            request_types_desc: 'Configure quais os tipos de pedido ativados para o formulário público de receção.',
            notifications: 'Notificações',
            ticket_numbering: 'Numeração de Pedidos',
            ticket_format: 'Formato atual',
            ticket_prefix: 'Prefixo',
            ticket_prefix_note: 'não configurável',
            sla_deadlines: 'Prazos de SLA',
            sla_desc: 'Defina o tempo máximo de resposta (em dias de calendário) para cada tipo de pedido. Os casos que se aproximem do limite irão apresentar avisos.',
            days: 'dias',
            warning_threshold: 'Limite de aviso',
            escalation_label: 'Ativar escalonamento automático em caso de incumprimento do SLA',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Guardar Configuração de SLA',
            saving: 'A guardar...',
            saved: 'Guardado com sucesso',
            announce_loaded: 'Configurações carregadas',
            email_on_creation: 'Notificação por correio eletrónico na criação do pedido',
            send_confirmation: 'Enviar correio eletrónico de confirmação ao requerente',
            users: 'Usuários',
            roles: 'Funções',
            api_keys: 'API Chaves',
            mfa: 'MFA',
            sessions: 'Sessões',
            webhooks: 'Webhooks',
            modules: 'Módulos',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Acesso (DSAR)',
                deletion: 'Apagamento',
                correction: 'Retificação',
                portability: 'Portabilidade',
                objection: 'Oposição',
                restriction: 'Limitação',
                consent_withdrawal: 'Retirada do consentimento',
                protective_measures: 'Medidas de proteção',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Atribuições de secção',
            assign_section: 'Atribuir secção',
            section_ref: 'Referência de secção',
            section_label: 'Etiqueta de secção',
            department_id: 'Departamento',
            assignee_id: 'Responsável',
            mandatory: 'Obrigatório',
            assign: 'Atribuir',
            no_assignments: 'Sem atribuições de secção',
            status: 'Estado',
            completion: 'Conclusão',
            mark_complete: 'Concluir',
        },
        consolidation: {
            title: 'Progresso de consolidação',
            add_child: 'Adicionar subtarefa',
            child_task_id: 'ID de subtarefa',
            department_id: 'Departamento',
            link: 'Ligação',
            overall_completion: 'Conclusão geral',
            departments_complete: 'Departamentos concluídos',
            overdue: 'Em atraso',
            ready: 'Pronto',
            not_ready: 'Não pronto',
            master_status: 'Estado principal',
            department_breakdown: 'Detalhes por departamento',
            department: 'Departamento',
            status: 'Estado',
            completion: 'Conclusão',
            due_date: 'Data de vencimento',
            overdue_flag: 'Em atraso',
            child_tasks: 'Subtarefas',
            task: 'Tarefa',
        },
        conflict_detection: {
            title: 'Deteção de conflitos',
            unresolved: 'não resolvido',
            no_conflicts: 'Nenhum conflito detetado',
            section: 'Secção',
            field: 'Campo',
            dept_a: 'Departamento A',
            value_a: 'Valor A',
            dept_b: 'Departamento B',
            value_b: 'Valor B',
            severity: 'Gravidade',
            status: 'Estado',
            resolved: 'Resolvido',
            open: 'Aberto',
            resolve: 'Resolver',
        },
        org_maturity: {
            title: 'Maturidade Organizacional',
            departments: 'Departamentos',
            department_hierarchy: 'Hierarquia de Departamentos',
            create_department: 'Criar Departamento',
            department_name: 'Nome do Departamento',
            department_code: 'Código',
            level: 'Nível',
            division: 'Divisão',
            department: 'Departamento',
            team: 'Equipa',
            parent_department: 'Departamento Superior',
            head_user: 'Chefe de Departamento',
            no_departments: 'Nenhum departamento configurado.',
            user_mappings: 'Atribuições de Utilizadores',
            map_user: 'Atribuir Utilizador',
            user_id: 'ID do Utilizador',
            role: 'Função',
            no_users: 'Nenhum utilizador atribuído a este departamento.',
            remove: 'Remover',
            framework_assignments: 'Atribuições de Referenciais',
            assign_framework: 'Atribuir Referencial',
            entity_id: 'ID da Entidade',
            entity_type: 'Tipo de Entidade',
            framework_id: 'ID do Referencial',
            framework_code: 'Código do Referencial',
            no_assignments: 'Sem atribuições de referenciais.',
            compliance_rollup: 'Resumo de Conformidade',
            entity_name: 'Entidade',
            total_assessments: 'Avaliações Totais',
            completed: 'Concluído',
            completion_pct: 'Conclusão %',
            open_gaps: 'Lacunas Abertas',
            critical_gaps: 'Críticas',
            compliance_score: 'Pontuação',
            no_rollup: 'Sem dados de resumo de conformidade disponíveis.',
            entity_summary: 'Resumo de Conformidade da Entidade',
            frameworks_assigned: 'Referenciais',
            overall_score: 'Pontuação Geral',
            load_departments_error: 'Falha ao carregar os departamentos',
            load_users_error: 'Falha ao carregar os utilizadores',
            department_created: 'Departamento criado',
            department_created_announce: 'Departamento criado',
            create_department_error: 'Falha ao criar o departamento',
            user_mapped: 'Usuário mapeado',
            user_mapped_announce: 'Usuário mapeado ao departamento',
            map_user_error: 'Falha ao associar o utilizador',
            user_removed: 'Usuário removido',
            load_rollup_error: 'Falha ao carregar o resumo',
            load_assignments_error: 'Falha ao carregar as atribuições',
            framework_assigned: 'Estrutura atribuída',
            framework_assigned_announce: 'Estrutura atribuída à entidade',
            assign_framework_error: 'Falha ao atribuir o quadro normativo',
            assignment_removed: 'Atribuição removida',
            enter_entity_id_placeholder: 'Digite o ID da entidade para ver atribuições',
            entity_type_organization: 'Organização',
            entity_type_department: 'Departamento',
        },
        onboarding_maturity: {
            title: 'Maturidade de integração',
            quick_starts: 'Inícios rápidos',
            progress: 'Progresso da integração',
            personas: 'Caminhos de Personagem',
            persona_onboarding: 'Integração de Personagem',
            quick_start_scenarios: 'Cenários de início rápido',
            start: 'Iniciar',
            restart: 'Reiniciar',
            estimated_minutes: 'Minutos estimados',
            steps: 'Passos',
            scenario: 'Cenário',
            no_scenarios: 'Não há cenários de início rápido disponíveis.',
            persona_id: 'ID da pessoa',
            persona_label: 'Pessoa',
            role_description: 'Descrição da função',
            primary_modules: 'Módulos Primários',
            recommended_tours: 'Passeios recomendados',
            sample_data_modules: 'Módulos de dados de amostra',
            quick_start_scenario: 'Cenário de início rápido',
            no_personas: 'Nenhum caminho de persona disponível.',
            framework_setup: 'Quadros regulamentares',
            framework_code: 'Código Estrutural',
            framework_name: 'Nome da Estrutura',
            related_modules: 'Módulos Relacionados',
            persona_relevance: 'Personas relevantes',
            no_frameworks: 'Nenhuma opção de configuração de estrutura disponível.',
            tier_config: 'Configuração do Módulo de Camada',
            tier: 'Nível',
            available_modules: 'Módulos Disponíveis',
            module_count: 'Contagem de módulos',
            overall_completion: 'Conclusão geral',
            wizard_completed: 'Assistente concluído',
            tours_completed: 'Passeios concluídos',
            quick_starts_completed: 'Inícios rápidos concluídos',
            selected_frameworks: 'Estruturas selecionadas',
            no_progress: 'Não há dados de progresso de integração disponíveis.',
            detected_persona: 'Personagem detectada',
            current_tier: 'Nível atual',
            completion_pct: 'Conclusão',
            progress_page: {
                loaded: 'Progresso carregado',
                no_progress: 'Não há dados de progresso de integração disponíveis.',
                overall: 'Progresso geral',
                detected_persona: 'Persona detetada',
                tier: 'Nível',
                modules: 'módulos',
                wizard: 'Assistente',
                completed: 'Concluído',
                pending: 'Pendente',
                tours: 'Visitas guiadas',
                quick_starts: 'Inícios rápidos',
                frameworks: 'Quadros selecionados',
                no_frameworks: 'Nenhum quadro selecionado.',
                go_to_wizard: 'Concluir o assistente de configuração',
                framework_selection: 'Seleção de quadro',
                framework_toggle: 'Ativar ou desativar quadros para o seu programa de conformidade.',
                framework_updated: 'Seleção de quadro atualizada.',
                view_tours: 'Ver visitas guiadas',
                view_quick_starts: 'Ver inícios rápidos',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Selecione uma persona para ver módulos, visitas guiadas e cenários de início rápido recomendados.',
                no_personas: 'Não há caminhos de persona disponíveis.',
                modules: 'Módulos',
                tours: 'Visitas guiadas',
                quick_start: 'Início rápido',
                details: 'Detalhes',
                primary_modules: 'Módulos principais',
                recommended_tours: 'Visitas guiadas recomendadas',
                framework_options: 'Quadros regulamentares',
                no_frameworks: 'Não há opções de configuração do quadro disponíveis.',
                no_frameworks_explain: 'Ainda não há quadros regulamentares instalados para a sua organização, pelo que não há nada a configurar aqui.',
                no_frameworks_action: 'Instale um pacote de mercado para adicionar os quadros que se aplicam a si',
                start_tour: 'Iniciar',
                no_tours: 'Não há visitas guiadas disponíveis.',
                help_center: 'Centro de ajuda',
                no_articles: 'Nenhum artigo encontrado.',
                primary: 'Principal',
                framework_code: 'Código do quadro',
                framework_label: 'Rótulo',
                region: 'Região',
                category: 'Categoria',
                enabled: 'Ativado',
                disabled: 'Desativado',
                tier_modules: 'Configuração de módulos por nível',
                tiers_loaded: 'Configurações de nível carregadas',
                no_tier_config: 'Não há configuração de nível disponível.',
                view_tours: 'Ver visitas de módulos',
                view_quick_start: 'Iniciar início rápido',
                toggle_framework: 'Alternar quadro',
                framework_updated: 'Seleção de quadro atualizada.',
                tier_desc_light: 'Fundamentos de privacidade — bases de conformidade PDPL',
                tier_desc_standard: 'Suite de conformidade completa — todos os módulos com fluxos de trabalho',
                tier_desc_enterprise: 'Governança avançada e automatização — multi-entidade, relatórios de grupo',
                persona_highlight: 'Os seus módulos',
                aria_go_to_module: '{name} — vá para o módulo',
                aria_start_tour: 'Iniciar {title}',
                aria_quick_start: 'Início rápido: {title}',
                aria_help_article: 'Artigo de ajuda: {title}',
                aria_view_framework: '{label}{primary} — ver detalhes da estrutura',
                aria_primary_for_persona: '{name} (principal para esta persona)',
                primary_for_persona_suffix: ' (primário para esta persona)',
                aria_view_in_persona_paths: '{name} - visualizar em caminhos de persona',
                progress_of_total: '{completed} de {total} concluído',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Pratique fluxos de trabalho comuns com cenários de início rápido guiados.',
                no_scenarios: 'Não há cenários de início rápido disponíveis.',
                started: 'Início rápido iniciado',
                status_completed: 'Concluído',
                status_in_progress: 'Em progresso',
                status_not_started: 'Não iniciado',
                minutes: 'min',
                steps: 'passos',
                step: 'Passo',
                restart: 'Reiniciar',
                start: 'Iniciar',
                go_to_module: 'Ir para o módulo',
                next_step: 'Próximo passo',
                complete: 'Concluir',
                reset: 'Repor',
                step_of: 'de',
                action_navigate: 'Navegar',
                action_form_fill: 'Preencher formulário',
                action_review: 'Rever',
                action_confirm: 'Confirmar',
                step_changed: 'Passo {current} de {total}: {title}',
                completed_message: 'Início rápido concluído com sucesso!',
                reset_message: 'O início rápido foi reposto.',
            },
            persona_p_vn_01_label: 'Empresa DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Proprietário do Departamento',
            persona_p_vn_02_description: 'Líder do departamento responsável pelos relatórios de conformidade seccionais',
            persona_p_vn_03_label: 'Conformidade de comércio eletrônico',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Líder de Governança de Dados',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Segurança cibernética e operações jurídicas',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Líder de governança',
            persona_p_vn_07_description: 'Liderar a supervisão do AI governança do sistema, avaliações de risco e classificação',
            persona_p_vn_08_label: 'Governo transfronteiriço e de fornecedores',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Líder de resposta a violações',
            persona_p_vn_09_description: 'Liderar a coordenação de notificação de violação e resposta a incidentes',
            persona_p_vn_10_label: 'Líder de Auditoria Interna',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'Responsável de dados de RH e colaboradores',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Responsável de conformidade de filial multinacional',
            persona_p_vn_12_description: 'EPD em filial vietnamita de empresa estrangeira que necessita de camada de conformidade local',
            persona_p_vn_13_label: 'Responsável de conformidade educacional',
            persona_p_vn_13_description: 'Responsável de conformidade em escola estrangeira que gere a proteção de dados de alunos e encarregados de educação',
            qs_hr_consent_title: 'Registar consentimentos de colaboradores',
            qs_hr_consent_description: 'Percurso do registo de consentimento de colaboradores e geração de relatório de cobertura.',
            qs_subsidiary_dpia_title: 'Criar avaliação AIPD de filial',
            qs_subsidiary_dpia_description: 'Percurso da criação de uma AIPD para uma atividade de tratamento de filial.',
            qs_student_consent_title: 'Configurar recolha de consentimento de alunos',
            qs_student_consent_description: 'Percurso da criação de políticas de consentimento parental para dados de alunos.',
            persona_p_vn_14_label: 'Assessor Jurídico Externo',
            persona_p_vn_14_description: 'Sócio sênior de escritório de advocacia assessorando diversas organizações clientes sobre conformidade PDPL',
            persona_p_vn_15_label: 'Gerente de Operações de Escritório de Advocacia',
            persona_p_vn_15_description: 'Coordenador de engajamento do cliente gerenciando rastreamento de entregas e coordenação de prazos',
            persona_p_vn_16_label: 'Proprietário da empresa / CEO',
            persona_p_vn_16_description: 'CEO ou GM que é legalmente o controlador de dados sob PDPL, responsável pelo investimento em conformidade',
            qs_dpia_filing_title: 'Conclua seu primeiro arquivamento DPIA',
            qs_dept_compliance_title: 'Envie sua primeira seção de conformidade do departamento',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Configurar o tratamento de dados de assinantes de telecomunicações',
            qs_data_flow_title: 'Mapeie seu primeiro fluxo de dados',
            qs_incident_response_title: 'Configurar o fluxo de trabalho de resposta a incidentes',
            qs_ai_risk_title: 'Execute uma avaliação de risco do sistema AI',
            qs_cross_border_title: 'Avalie uma transferência de dados transfronteiriça',
            qs_breach_notification_title: 'Simule um fluxo de trabalho de notificação de violação',
            qs_audit_trail_title: 'Revise uma trilha de auditoria de conformidade',
            qs_legal_advisor_portfolio_title: 'Revise o portfólio de conformidade de vários clientes',
            qs_legal_advisor_portfolio_description: 'Revise o status de conformidade em diversas organizações clientes.',
            qs_law_firm_ops_title: 'Acompanhe o status da entrega do cliente',
            qs_law_firm_ops_description: 'Monitore o progresso da entrega de consultoria e o envolvimento do cliente.',
            qs_ceo_dashboard_title: 'Ver painel de conformidade executiva',
            qs_ceo_dashboard_description: 'Revise o status de conformidade de alto nível e as métricas de investimento.',
        },
        rights_requests: {
            response_templates: {
                title: 'Modelos de resposta',
                subtitle: 'Gerencie os modelos de carta de resposta DSR que o ResponseGenerator usa para redigir respostas aos titulares dos dados.',
                create_btn: 'Novo modelo',
                edit_btn: 'Editar modelo',
                filter_right_type: 'Filtrar por tipo certo',
                filter_locale: 'Filtrar por localidade',
                filter_status: 'Filtrar por status',
                all_right_types: 'Todos os tipos certos',
                any_right_type: 'Qualquer (aplica-se a todos os tipos)',
                all_locales: 'Todas as localidades',
                all_statuses: 'Todos',
                status_active: 'Ativo',
                status_inactive: 'Inativo',
                empty_state: 'Nenhum modelo corresponde aos filtros atuais.',
                columns: {
                    template_id: 'ID do modelo',
                    outcome: 'Resultado',
                    locale: 'Local',
                    right_type: 'Tipo certo',
                    is_active: 'Estado',
                    last_updated: 'Última atualização',
                    actions: 'Ações',
                },
                actions: {
                    edit: 'Editar',
                    clone: 'Clonar',
                    deactivate: 'Desativar',
                    reactivate: 'Reativar',
                    preview: 'Visualização',
                },
                form: {
                    template_id: 'ID do modelo',
                    template_id_help: 'Identificador em minúsculas, por ex. dsr_access_fulfilled_vi',
                    outcome: 'Resultado',
                    locale: 'Local',
                    right_type: 'Tipo certo',
                    subject: 'Assunto',
                    body: 'Modelo de corpo',
                    body_help: 'Use espaços reservados como {case_reference}, {data_subject_name}, {fulfilled_at}. Mínimo de 15 linhas para cartas com várias seções.',
                    is_active: 'Ativo',
                    official_form_id: 'ID do formulário oficial (opcional)',
                },
                outcomes: {
                    fulfilled: 'Cumprido',
                    partially_fulfilled: 'Parcialmente cumprido',
                    denied: 'Negado',
                },
                clone_modal_title: 'Clonar modelo',
                clone_target_locale: 'Local de destino',
                clone_help: 'Um novo modelo será criado na localidade de destino usando este modelo como ponto de partida.',
                preview_modal_title: 'Visualização do modelo',
                preview_note: 'A visualização usa espaços reservados de caso de demonstração DSR-DEMO-0020. A entrega real substitui os dados de casos ao vivo.',
                preview_unavailable: 'A visualização requer um caso existente. Anexe este modelo a um caso e use a ação Rascunho de resposta na página de detalhes do caso.',
                deactivate_confirm: 'Desativar este modelo? Continuará a aplicar-se aos casos existentes que o façam referência.',
                toasts: {
                    created: 'Modelo criado',
                    updated: 'Modelo atualizado',
                    deactivated: 'Modelo desativado',
                    reactivated: 'Modelo reativado',
                    cloned: 'Modelo clonado',
                    load_error: 'Falha ao carregar modelos',
                    save_error: 'Falha ao salvar o modelo',
                    clone_error: 'Falha ao clonar modelo',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Departamentos',
                subtitle: 'Gerencie os departamentos que encaminham casos de DSR, tarefas DPIA e fluxos de trabalho de conformidade.',
                create_btn: 'Novo Departamento',
                edit_btn: 'Editar Departamento',
                columns: {
                    name: 'Nome',
                    description: 'Descrição',
                    active_cases: 'Casos Ativos',
                    members: 'Membros',
                    status: 'Estado',
                    actions: 'Ações',
                },
                status_active: 'Ativo',
                status_inactive: 'Inativo',
                form: {
                    name: 'Nome',
                    description: 'Descrição',
                },
                actions: {
                    edit: 'Editar',
                    deactivate: 'Desativar',
                    reactivate: 'Reativar',
                    delete: 'Excluir',
                    manage_members: 'Gerenciar membros',
                },
                delete_modal_title: 'Excluir departamento',
                delete_warning: 'Este departamento ainda tem casos ativos. Escolha um departamento para reatribuí-los antes da exclusão.',
                delete_no_cases: 'Este departamento não possui casos ativos e pode ser excluído com segurança.',
                reassign_prompt: 'Reatribuir casos ativos para:',
                reassign_none_available: 'Nenhum outro departamento ativo disponível. Crie outro departamento primeiro.',
                member_section_title: 'Membros',
                add_member: 'Adicionar membro',
                remove_member: 'Remover',
                member_role: 'Função',
                no_members: 'Nenhum membro mapeado para este departamento.',
                toasts: {
                    created: 'Departamento criado',
                    updated: 'Departamento atualizado',
                    deactivated: 'Departamento desativado',
                    reactivated: 'Departamento reativado',
                    deleted: 'Departamento excluído',
                    reassigned_count: '{count} caso(s) reatribuído(s)',
                    member_added: 'Membro adicionado',
                    member_removed: 'Membro removido',
                    load_error: 'Falha ao carregar departamentos',
                    save_error: 'Falha ao salvar departamento',
                    delete_error: 'Falha ao excluir departamento',
                },
            },
            retention_policies: {
                title: 'Políticas de retenção',
                subtitle: 'Configure janelas de retenção por tipo de destino. Documentos de verificação de identidade usam isso para carimbar retention_expires_at em uploads futuros.',
                create_btn: 'Nova Política',
                edit_btn: 'Editar política',
                columns: {
                    target_type: 'Tipo de destino',
                    retention_days: 'Retenção (dias)',
                    owner: 'Proprietário da política',
                    effective_from: 'Eficaz a partir de',
                    last_reviewed: 'Última revisão',
                    status: 'Estado',
                    actions: 'Ações',
                },
                status_active: 'Ativo',
                status_inactive: 'Inativo',
                form: {
                    target_type: 'Tipo de destino',
                    target_type_help: 'A qual categoria de dados a janela de retenção se aplica. Atualmente, apenas são suportados documentos de verificação de identidade DSR.',
                    retention_days: 'Janela de retenção (dias)',
                    retention_days_help: 'Entre 1 e 3.650 dias (10 anos no máximo).',
                    owner: 'Proprietário da política',
                    owner_help: 'Geralmente o DPO. Responsável pela decisão de retenção sob DG-2.',
                    effective_from: 'Eficaz a partir de',
                },
                target_types: {
                    dsr_identity_verification_document: 'Documentos de verificação de identidade DSR',
                },
                deactivate_notice: 'A desativação não afeta os anexos já carimbados – apenas uploads futuros não receberão mais uma janela de retenção.',
                deactivate_confirm: 'Desativar esta política de retenção?',
                toasts: {
                    created: 'Política de retenção criada',
                    updated: 'Política de retenção atualizada',
                    deactivated: 'Política de retenção desativada',
                    load_error: 'Falha ao carregar políticas de retenção',
                    save_error: 'Falha ao salvar a política de retenção',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Sistemas',
            categories: 'Categorias de dados',
            registry: 'Registro de dados',
            purposes: 'Finalidades de processamento',
            retention: 'Retenção',
            flows: 'Fluxos de Dados',
            activities: 'Atividades de Tratamento',
            evidence: 'Evidências',
            exports: 'Exportações',
            attestation: 'Ciclos de Atestado',
            governance: 'Governança',
            review: 'Tarefas de Revisão',
            inventory: 'Inventário',
            exceptions: 'Exceções',
            history: 'Histórico',
            ropa_completeness: 'Completude ROPA',
        },
        ropa_completeness_autofix: {
            ask_button: 'Pergunte a Forseti',
            drafting: 'Elaboração…',
            accept: 'Aceitar',
            reject: 'Rejeitar',
            accepted: 'Aceito — inscreva-se por meio do editor de atividades.',
            rejected: 'Rejeitado.',
            needs_human_input: 'Forseti precisa de mais contexto de atividade (por exemplo, um propósito documentado) antes de redigir este campo.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'Relatório de integridade ROPA',
            subtitle: 'Auditoria de higiene por campo de seus registros de atividades de processamento, abrangidas pela estrutura regulatória.',
            framework_label: 'Estrutura',
            kpi_total: 'Atividades pontuadas',
            kpi_critical: 'Crítico',
            kpi_high: 'Alto',
            kpi_conditional: 'Condicional',
            field_chart_heading: 'Campos ausentes por frequência',
            drilldown_heading: 'Atividades com lacunas',
            col_activity: 'Atividade',
            col_field: 'Campo',
            col_severity: 'Gravidade',
            col_condition: 'Condição',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Crítico',
            severity_high: 'Alto',
            severity_conditional: 'Condicional',
            severity_informational: 'Informativo',
            include_informational: 'Incluir informações',
            export_csv: 'Baixar CSV',
            exporting: 'Exportando...',
            loading: 'Carregando…',
            empty_state: 'Todas as atividades ativas possuem registros ROPA completos para esta estrutura.',
            error_fetch: 'Falha ao carregar o relatório de conclusão.',
            error_unsupported_framework: 'Esta estrutura não é suportada.',
            error_framework_not_installed: 'Esta estrutura não está instalada para sua organização.',
            no_misses_for_field: 'Nenhuma atividade faltando neste campo.',
            framework_PDPL: 'PDPL (Lei 91/2025 + Decreto 356)',
            framework_GDPR: 'GDPR Artigo 30',
            framework_DECREE_165: 'Decreto 165 (Lei de Dados do Vietnã)',
            framework_ISO_27701: 'ISO 27701 (informativo)',
        },
        registry: {
            // Sprint 64.72.0 — cabeçalhos de coluna da exportação do registo de dados.
            export_col: {
                registry_item_id: 'ID do item do registo',
                name: 'Nome',
                description: 'Descrição',
                field_identifiers: 'Identificadores de campo',
                category_id: 'ID da categoria',
                category_name: 'Nome da categoria',
                data_type: 'Tipo de dados',
                effective_sensitivity: 'Sensibilidade efetiva',
                sensitivity_override: 'Sensibilidade substitutiva',
                data_subjects: 'Titulares dos dados',
                volume_band: 'Escalão de volume',
                source_system_ids: 'ID dos sistemas de origem',
                processing_activity_ids: 'ID das atividades de tratamento',
                retention_policy_id: 'ID da política de conservação',
                effective_retention_policy_id: 'ID da política de conservação efetiva',
                purposes: 'Finalidades',
                collection_method: 'Método de recolha',
                legal_basis: 'Fundamento jurídico',
                data_tier: 'Nível de dados',
                data_tier_source: 'Origem do nível de dados',
                storage_locations: 'Locais de armazenamento',
                cross_border: 'Transfronteiriço',
                status: 'Estado',
                source_form_submission_id: 'ID de submissão do formulário de origem',
                source_form_template_id: 'ID do modelo do formulário de origem',
                last_reviewed_at: 'Última revisão',
                next_review_at: 'Próxima revisão',
                created_at: 'Criado em',
                updated_at: 'Atualizado em',
                data_element: 'Elemento de dados',
                data_category: 'Categoria de dados',
                systems: 'Sistemas',
                retention_policy: 'Política de conservação',
                name_of_processing: 'Nome do tratamento',
                categories_of_personal_data: 'Categorias de dados pessoais',
                categories_of_data_subjects: 'Categorias de titulares dos dados',
                recipients: 'Destinatários',
                third_country_transfers: 'Transferências para países terceiros',
                retention_period: 'Prazo de conservação',
                doc_title: 'Registo de dados',
                doc_generated: 'Exportação gerada em {timestamp}',
                doc_item_count: '{count} item/itens do registo',
                doc_template_full: 'Completo',
            },
            export_vocab: {
                data_type: {
                    text: 'Texto',
                    number: 'Número',
                    date: 'Data',
                    datetime: 'Data e hora',
                    boolean: 'Sim/não',
                    binary: 'Binário',
                    structured: 'Estruturado',
                    file: 'Ficheiro',
                    identifier: 'Identificador',
                    geolocation: 'Geolocalização',
                },
                collection_method: {
                    direct_from_subject: 'Diretamente junto do titular dos dados',
                    third_party: 'De um terceiro',
                    derived: 'Derivado de outros dados',
                    observed: 'Observado',
                    inferred: 'Inferido',
                    public_source: 'Fonte pública',
                },
                data_subject_type: {
                    customer: 'Cliente',
                    employee: 'Trabalhador',
                    contractor: 'Prestador de serviços',
                    prospect: 'Cliente potencial',
                    minor: 'Menor',
                    patient: 'Doente',
                    student: 'Estudante',
                    beneficiary: 'Beneficiário',
                    vendor_contact: 'Contacto do fornecedor',
                    website_visitor: 'Visitante do sítio web',
                    other: 'Outro',
                },
                volume_band: {
                    lt_1k: 'Menos de 1.000 registos',
                    '1k_to_10k': 'De 1.000 a 10.000 registos',
                    '10k_to_100k': 'De 10.000 a 100.000 registos',
                    '100k_to_1m': 'De 100.000 a 1 milhão de registos',
                    gt_1m: 'Mais de 1 milhão de registos',
                    unknown: 'Desconhecido',
                },
                tier_source: {
                    assessed_and_approved: 'Avaliado e aprovado',
                    manual_legacy: 'Adotado de um valor anterior',
                    legacy_platform_field: 'Valor herdado da plataforma',
                    scheme_scoped_only: 'Avaliado segundo níveis próprios de cada esquema — nenhum código único abrange várias escalas de níveis legais',
                },
            },
            page: {
                title: 'Registro de dados',
                subtitle: 'Elementos de dados pessoais que sua organização possui, organizados em categorias de dados.',
                empty_state: 'Ainda não há itens de registro. Adicione um item manualmente ou importe de um cadastro regulatório.',
                empty_filter_state: 'Nenhum item corresponde aos seus filtros.',
            },
            filter: {
                category: 'Categoria',
                sensitivity: 'Sensibilidade',
                data_subject: 'Titular dos dados',
                status: 'Estado',
                data_tier: 'Camada de dados',
                cross_border: 'Transfronteiriço',
                has_owner: 'Tem proprietário',
                has_form_source: 'Importado do formulário',
                search_placeholder: 'Nome de pesquisa, descrição ou identificador de campo…',
                clear: 'Limpar filtros',
                option_all: 'Todos',
            },
            col: {
                name: 'Nome',
                category: 'Categoria',
                sensitivity: 'Sensibilidade',
                data_subjects: 'Titulares dos dados',
                systems: 'Sistemas',
                activities: 'Atividades',
                owner: 'Proprietário',
                retention: 'Retenção',
                data_tier: 'Camada de dados',
                status: 'Estado',
                last_reviewed: 'Última revisão',
                actions: 'Ações',
            },
            action: {
                add_manual: 'Adicionar item',
                add_from_form: 'Adicionar do registro regulatório',
                import_history: 'Importar envios anteriores',
                export: 'Exportar',
                archive: 'Arquivo',
                restore: 'Restaurar',
                edit: 'Editar',
                assign_owner: 'Atribuir proprietário',
                attach_evidence: 'Anexar evidências',
                open_lineage: 'Linhagem aberta',
            },
            modal_editor: {
                section_identity: 'Identidade',
                section_classification: 'Classificação',
                section_subjects: 'Assuntos e Volume',
                section_location: 'Localização e transferência',
                section_systems: 'Sistemas e Atividades',
                section_retention: 'Retenção e Legal',
                section_governance: 'Governança',
                section_notes: 'Notas',
                hint_inherited: '(herdado da categoria)',
                hint_overridden: '(substituído)',
                placeholder_field_identifiers: 'por exemplo, clientes.primary_email, hris.employee_id',
                validation_name_required: 'O nome é obrigatório',
                validation_category_required: 'A categoria é obrigatória',
                validation_data_subjects_required: 'É necessário pelo menos um tipo de titular de dados',
                label_data_type: 'Tipo de dados',
                label_field_identifiers: 'Identificadores de campo (separados por vírgula)',
                label_data_subject_types: 'Tipos de titulares de dados',
                label_volume_band: 'Banda de volume',
                label_storage_locations: 'Locais de armazenamento (separados por vírgula)',
                placeholder_storage_locations: 'ISO-3166 códigos ou texto livre (por exemplo, VN, Hetzner EU)',
                label_cross_border: 'Transferência transfronteiriça',
                label_source_systems: 'Sistemas de origem',
                label_processing_activities: 'Atividades de processamento',
                label_collection_method: 'Método de coleta',
                label_purposes: 'Finalidades',
                hint_multi_select: 'Segure Cmd/Ctrl para selecionar vários.',
                option_inherit_category: '- (herdar da categoria)',
                option_dash: '-',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Pré-visualização da importação',
                label_to_create: 'Para criar',
                label_to_update: 'Para atualizar',
                label_skipped_archived: 'Ignorado (arquivado)',
                label_conflicts: 'Conflitos',
                commit: 'Importar',
                cancel: 'Cancelar',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Categoria',
                section_systems: 'Sistemas de origem',
                section_activities: 'Atividades de processamento',
                section_flows: 'Fluxos de dados',
                section_retention: 'Retenção eficaz',
                section_governance: 'Governança',
                section_evidence: 'Evidência',
                section_audit: 'Trilha de auditoria recente',
                section_source_form: 'Envio do formulário de origem',
                badge_inherited: 'Herdado',
                badge_overridden: 'Substituído',
            },
            toast: {
                created: 'Item de registro criado.',
                updated: 'Item de registro atualizado.',
                archived: 'Item de registro arquivado.',
                imported: '{count} itens importados.',
                conflict_warning: 'Os campos {count} mantiveram suas edições — veja a lista de conflitos.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Inventário de Sistemas',
            new: 'Novo Sistema',
            edit: 'Editar Sistema',
            name: 'Nome',
            owner: 'Responsável',
            criticality: 'Criticidade',
            dpa_status: 'Estado do APD',
            location: 'Localização',
            system_type: 'Tipo de Sistema',
            metadata: 'Metadados (JSON)',
            department: 'Departamento',
            hosting_location: 'Localização do Alojamento',
            hosting_provider: 'Fornecedor de Alojamento',
            data_residency_country: 'País de Residência dos Dados',
            hosting: 'Alojamento',
            no_systems: 'Nenhum sistema encontrado',
            confirm_archive: 'Arquivar sistema "{name}"?',
            count: 'sistemas',
            count_one: 'sistema',
            type_internal: 'Interno',
            type_external: 'Externo',
            type_cloud_service: 'Serviço em Nuvem',
            type_on_premise: 'Local',
            type_hybrid: 'Híbrido',
            status_active: 'Ativo',
            status_decommissioning: 'Em Desativação',
            status_decommissioned: 'Desativado',
            status_planned: 'Planeado',
            dpa_not_required: 'Não Necessário',
            dpa_required_missing: 'Necessário em Falta',
            dpa_in_progress: 'Em Progresso',
            dpa_signed: 'Assinado',
            all_criticalities: 'Todas as Criticidades',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Categorias de Dados',
            new: 'Nova Categoria',
            edit: 'Editar Categoria',
            name: 'Nome',
            sensitivity: 'Sensibilidade',
            tags: 'Etiquetas',
            used_in: 'Utilizado Em',
            examples: 'Exemplos',
            examples_hint: 'Exemplos (separados por vírgulas)',
            default: 'Predefinido',
            all_sensitivities: 'Todas as Sensibilidades',
            all_statuses: 'Todos os Estados',
            no_categories: 'Nenhuma categoria encontrada',
            confirm_delete: 'Eliminar categoria "{name}"?',
            count: 'categorias',
            count_one: 'categoria',
            status_draft: 'Rascunho',
            status_proposed: 'Proposto',
            status_under_review: 'Em Revisão',
            status_approved: 'Aprovado',
            status_deprecated: 'Obsoleto',
            status_updated: 'Estado da categoria atualizado',
            propose: 'Propor',
            approve: 'Aprovar',
            deprecate: 'Tornar Obsoleto',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Finalidades do Tratamento',
            new: 'Nova Finalidade',
            edit: 'Editar Finalidade',
            name: 'Nome',
            legal_basis: 'Base Jurídica',
            retention_period: 'Período de Retenção (dias)',
            justification: 'Justificação de Interesse Legítimo',
            legal_basis_detail: 'Detalhe da Base Jurídica',
            all_legal_bases: 'Todas as Bases Jurídicas',
            no_purposes: 'Nenhuma finalidade encontrada',
            confirm_delete: 'Eliminar "{name}"?',
            default: 'Predefinido',
            count: '{count} finalidades',
            count_one: '{count} finalidade',
        },
        retention: {
            title: 'Políticas de Retenção',
            new: 'Nova Política',
            edit: 'Editar Política',
            name: 'Nome',
            retention_days: 'Dias de Retenção',
            disposal_method: 'Método de Eliminação',
            data_categories: 'Categorias de Dados',
            activities: 'Atividades',
            review_frequency: 'Frequência de Revisão',
            review_frequency_days: 'Frequência de Revisão (dias)',
            no_policies: 'Nenhuma política de retenção encontrada',
            confirm_delete: 'Eliminar "{name}"?',
            count: '{count} políticas',
            count_one: '{count} política',
            days: 'dias',
            disposal_deletion: 'Eliminação',
            disposal_anonymization: 'Anonimização',
            disposal_archival: 'Arquivamento',
            review_overdue: 'Revisão atrasada',
            review_status: 'Estado da revisão',
            review_ok: 'No prazo',
            department: 'Departamento',
            all_departments: 'Todos os departamentos',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Vista de fluxos',
            view_list: 'Lista',
            view_map: 'Mapa',
            title: 'Fluxos de Dados',
            new: 'Novo Fluxo',
            edit: 'Editar Fluxo',
            name: 'Nome',
            from_system: 'Sistema de Origem',
            to_system: 'Sistema de Destino',
            flow_type: 'Tipo de Fluxo',
            cross_border: 'Transfronteiriço',
            risk_level: 'Nível de Risco',
            mechanism: 'Mecanismo',
            transfer_mechanism: 'Mecanismo de Transferência',
            frequency: 'Frequência',
            source_country: 'País de Origem',
            destination_country: 'País de Destino',
            transfer_safeguard: 'Garantia de Transferência',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Todos os Níveis de Risco',
            no_flows: 'Nenhum fluxo de dados encontrado',
            confirm_archive: 'Arquivar "{name}"?',
            count: '{count} fluxos',
            count_one: '{count} fluxo',
            status_active: 'Ativo',
            status_inactive: 'Inativo',
            status_under_review: 'Em Revisão',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Transferência de Ficheiros',
            mechanism_database_replication: 'Replicação de Base de Dados',
            mechanism_manual: 'Manuais',
            mechanism_email: 'Correio Eletrónico',
            mechanism_other: 'Outro',
            frequency_real_time: 'Tempo Real',
            frequency_hourly: 'Hora a Hora',
            frequency_daily: 'Diário',
            frequency_weekly: 'Semanal',
            frequency_monthly: 'Mensal',
            frequency_on_demand: 'A Pedido',
            frequency_one_time: 'Única Vez',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdição',
        },
        activities: {
            title: 'Atividades de Tratamento',
            new: 'Nova Atividade',
            edit: 'Editar Atividade',
            activity_title: 'Título',
            category: 'Categoria',
            legal_basis: 'Base Jurídica',
            dpa_required: 'DPIA Required',
            ropa_generated: 'ROPA Gerado',
            version: 'Versão',
            generate_ropa: 'Gerar ROPA',
            controller: 'Responsável pelo Tratamento',
            controller_name: 'Nome do Responsável pelo Tratamento',
            controller_contact: 'Contacto do Responsável pelo Tratamento',
            legal_basis_detail: 'Detalhe da Base Jurídica',
            organization_info: 'Organization Information',
            org_address: 'Endereço da organização',
            tax_code: 'Código fiscal',
            contact_person: 'Pessoa de contacto',
            contact_email: 'E-mail de contacto',
            contact_phone: 'Telefone de contacto',
            select_entity: 'Selecione…',
            no_entities: 'Nenhum item encontrado',
            dpo_name: 'Nome do EPD',
            dpo_contact: 'Contacto do EPD',
            processor_name: 'Nome do Subcontratante',
            processor_contact: 'Contacto do Subcontratante',
            data_subjects: 'Titulares dos Dados',
            data_subjects_hint: 'Titulares dos Dados (separados por vírgulas)',
            recipients: 'Destinatários',
            recipients_hint: 'Destinatários (separados por vírgulas)',
            security_measures: 'Medidas de Segurança',
            dpia_reference: 'Referência AIPD',
            no_activities: 'Nenhuma atividade de tratamento encontrada',
            confirm_archive: 'Arquivar "{name}"?',
            count: '{count} atividades',
            count_one: '{count} atividade',
            ropa_success: 'Registo RoPA gerado. Vá a Exportações para o descarregar.',
            ropa_error: 'Falha ao gerar ROPA',
            required_label: 'Obrigatório',
            status_draft: 'Rascunho',
            status_active: 'Ativo',
            status_under_review: 'Em Revisão',
            status_archived: 'Arquivado',
            decree_165_heading: 'Nível de dados do Decreto 165 (Lei de Dados do Vietnã)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Camada de dados',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Camada de dados',
            data_tier_regular: 'Normal',
            data_tier_important: 'Importante',
            data_tier_core: 'Núcleo',
            transfer_mechanism_label: 'Mecanismo de transferência transfronteiriça',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Referência de avaliação de segurança',
            security_assessment_reference_placeholder: 'por exemplo MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Repositório de Evidências',
            new: 'Nova Evidência',
            edit: 'Editar Evidência',
            name: 'Nome',
            evidence_type: 'Tipo de Evidência',
            linked_entities: 'Entidades Associadas',
            uploaded: 'Carregado',
            expiry: 'Validade',
            file_url: 'URL do Ficheiro',
            source_mode_label: 'Fonte da evidência',
            source_mode_file: 'Carregar ficheiro',
            source_mode_url: 'Ligar URL externa',
            attachment_label: 'Anexo',
            link_to: 'Associar Evidência A',
            entity_type: 'Tipo de Entidade',
            entity_id: 'ID da Entidade',
            no_evidence: 'Nenhuma evidência encontrada',
            manage_links: 'Gerenciar links',
            unlink: 'Desvincular',
            confirm_delete: 'Eliminar evidência "{name}"?',
            count: '{count} itens',
            count_one: '{count} elemento',
            type_document: 'Documento',
            type_screenshot: 'Captura de Ecrã',
            type_policy: 'Política',
            type_contract: 'Contrato',
            type_assessment: 'Avaliação',
            type_correspondence: 'Correspondência',
            type_other: 'Outro',
            linked_type_system: 'Sistema',
            linked_type_data_flow: 'Fluxo de dados',
            linked_type_processing_activity: 'Atividade de processamento',
            linked_type_data_category: 'Categoria de dados',
            linked_type_purpose: 'Objetivo',
            linked_type_retention_policy: 'Política de retenção',
            link_entity: 'Link para entidade',
            link_success: 'Entidade vinculada com sucesso',
            confirm_unlink: 'Tem certeza de que deseja desvincular esta entidade?',
            unlink_success: 'Entidade desvinculada com sucesso',
            no_linked_entities: 'Nenhuma entidade vinculada',
            select_entity: 'Selecione…',
            no_entities: 'Nenhum item',
        },
        exports: {
            title: 'Exportações de Dados',
            recent: 'Exportações Recentes',
            export_type: 'Tipo de Exportação',
            format: 'Formato',
            requested_by: 'Solicitado Por',
            requested_at: 'Solicitado Em',
            download: 'Transferir',
            csv_export: 'Exportação CSV',
            csv_description: 'Transferir dados em formato CSV para análise em folha de cálculo.',
            pdf_reports: 'Relatórios PDF',
            pdf_description: 'Gerar relatórios formatados para documentação de conformidade.',
            csv_systems: 'Inventário de Sistemas',
            csv_categories: 'Categorias de Dados',
            csv_flows: 'Fluxos de Dados',
            csv_activities: 'Atividades de Tratamento',
            csv_cross_border: 'Fluxos Transfronteiriços',
            csv_retention_policies: 'Políticas de retenção',
            csv_evidence: 'Itens de evidência',
            csv_governance_assignments: 'Atribuições de Governança',
            pdf_ropa: 'Registo ROPA',
            pdf_systems: 'Relatório de Sistemas',
            pdf_cross_border: 'Relatório Transfronteiriço',
            pdf_flow_map: 'Mapa de Fluxos de Dados',
            export_failed: 'Exportação falhada',
            pdf_generated: 'PDF gerado com sucesso.',
            ropa: 'Registro RoPA',
            retention_policies: 'Políticas de retenção',
            governance_assignments: 'Atribuições de Governança',
            col_entity: 'Entidade de exportação',
            col_description: 'Descrição',
            desc_ropa: 'Registro ROPA completo de todas as atividades de processamento com base legal, status DPIA e controles.',
            desc_systems: 'Inventário de sistemas com proprietário, tags de categoria de dados e status de conformidade.',
            desc_activities: 'Atividades de processamento individual com controlador, base legal e classificação DPIA.',
            desc_categories: 'Categorias de dados e níveis de sensibilidade usados em classificações e auditorias.',
            desc_purposes: 'Finalidades do tratamento com a base jurídica registada para cada uma.',
            desc_flows: 'Fluxos de dados entre sistemas, incluindo rotas internas e transfronteiriças.',
            desc_cross_border: 'Apenas fluxos transfronteiriços, com mecanismo de transferência e avaliação de risco.',
            desc_retention_policies: 'Políticas de retenção, métodos de descarte e cadência de revisão.',
            desc_evidence: 'Provas anexadas às atividades de processamento: certificados, contratos DPA, relatórios DPIA.',
            desc_governance_assignments: 'Atribuições de governança: proprietários, aprovadores e referências ISO 27001.',
            unified_heading: 'Exportar Entidades',
            unified_description: 'Exporte qualquer entidade de mapeamento de dados no formato CSV, XLSX, PDF ou DOCX. Todas as exportações são salvas no Navegador de Arquivos.',
            export_button_label: 'Exportar',
            csv_download_success: 'CSV transferido com sucesso.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Ciclos de Atestação',
            create: 'Novo Ciclo',
            name: 'Nome do Ciclo',
            year: 'Ano',
            deadline: 'Prazo',
            scope: 'Âmbito',
            status: 'Estado',
            tasks: 'Tarefas do Departamento',
            assign_tasks: 'Atribuir Tarefas',
            department: 'Departamento',
            assignee: 'Responsável',
            progress: 'Progresso',
            stale_entries: 'Entradas Desatualizadas',
            detect_stale: 'Detetar Desatualizadas',
            export_bundle: 'Exportar Pacote',
            no_cycles: 'Ainda sem ciclos de atestação.',
            create_success: 'Ciclo de atestação criado.',
            assign_success: 'Tarefas atribuídas com sucesso.',
            complete_success: 'Tarefa concluída.',
            stale_detected: 'Entradas desatualizadas detetadas.',
            stale_systems: 'Sistemas desatualizados',
            stale_flows: 'Fluxos desatualizados',
            no_stale: 'Nenhuma entrada desatualizada detetada.',
            stale_name: 'Nome',
            stale_announcement: 'Encontradas {0} entradas de sistema desatualizadas e {1} fluxos desatualizados',
            last_updated: 'Última atualização',
            status_draft: 'Rascunho',
            status_active: 'Ativo',
            status_completed: 'Concluído',
            task_status_pending: 'Pendente',
            task_status_in_progress: 'Em andamento',
            task_status_completed: 'Concluído',
            scope_systems: 'Sistemas',
            scope_flows: 'Fluxos de dados',
            scope_activities: 'Atividades',
            scope_categories: 'Categorias',
            scope_purposes: 'Finalidades',
            scope_retention: 'Retenção',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Atribuições de Governança',
            assign: 'Atribuir Propriedade',
            entity_type: 'Tipo de Entidade',
            entity: 'Entidade',
            role: 'Função',
            assigned_by: 'Atribuído por',
            assigned_at: 'Atribuído em',
            remove: 'Remover',
            no_assignments: 'Sem atribuições de governança.',
            assign_success: 'Propriedade atribuída.',
            remove_success: 'Atribuição removida.',
            assignee: 'Atribuído',
            department: 'Departamento',
            all_departments: 'Todos os departamentos',
            roles: {
                data_owner: 'Proprietário de Dados',
                data_steward: 'Administrador de Dados',
                data_custodian: 'Custodiante de Dados',
                informed: 'Informado',
            },
            entity_types: {
                system: 'Sistema',
                flow: 'Fluxo de dados',
                activity: 'Atividade',
                category: 'Categoria',
                purpose: 'Finalidade',
                retention_policy: 'Política de retenção',
            },
        },
        risk_assessment: {
            title: 'Avaliação de Riscos',
            create: 'Nova Avaliação',
            safeguard_type: 'Tipo de Salvaguarda',
            adequacy_status: 'Estado de Adequação',
            risk_factors: 'Fatores de Risco',
            mitigations: 'Medidas de Mitigação',
            reviewer: 'Revisor',
            decision: 'Decisão',
            approve: 'Aprovar',
            reject: 'Rejeitar',
            no_assessments: 'Sem avaliações de riscos.',
            create_success: 'Avaliação de riscos criada.',
            approve_success: 'Avaliação aprovada.',
            reject_success: 'Avaliação rejeitada.',
            one_per_line: 'Um por linha',
            adequacy_adequate: 'Adequado',
            adequacy_inadequate: 'Inadequado',
            adequacy_under_review: 'Em revisão',
            adequacy_not_assessed: 'Não avaliado',
            decision_approved: 'Aprovado',
            decision_rejected: 'Rejeitado',
        },
        review: {
            title: 'Tarefas de Revisão',
            description: 'As tarefas de revisão são criadas automaticamente quando uma categoria de dados é proposta. Para criar uma nova tarefa, vá a Categorias de Dados e clique em Propor numa categoria rascunho.',
            count: 'tarefas de revisão',
            summary: 'Resumo da Revisão',
            pending: 'Pendentes',
            resolved_today: 'Resolvidas Hoje',
            escalated: 'Escaladas',
            overdue: 'Atrasadas (>5 dias)',
            all_statuses: 'Todos os Estados',
            status_pending: 'Pendente',
            status_resolved: 'Resolvido',
            status_escalated: 'Escalado',
            category_name: 'Categoria',
            system_name: 'Sistema',
            owner: 'Responsável',
            resolve: 'Resolver',
            resolve_task: 'Resolver Tarefa de Revisão',
            resolution: 'Resolução',
            resolution_approve: 'Aprovar',
            resolution_modify: 'Modificar e Aprovar',
            resolution_reject: 'Rejeitar',
            resolution_escalate: 'Escalar',
            notes: 'Notas de Resolução',
            notes_required: 'Notas obrigatórias (mínimo 10 caracteres) para Modificar, Rejeitar ou Escalar',
            resolved_success: 'Tarefa de revisão resolvida',
            bulk_approve: 'Aprovação em Massa',
            bulk_reject: 'Rejeição em Massa',
            bulk_success: '{count} tarefas processadas',
            no_tasks: 'Nenhuma tarefa de revisão encontrada',
        },
        inventory: {
            title: 'Inventário de Classificação',
            count: 'sistemas',
            summary: 'Resumo do Inventário',
            total_systems: 'Total de Sistemas',
            total_labels: 'Total de Etiquetas',
            system_name: 'Sistema',
            label_count: 'Etiquetas',
            approved_count: 'Aprovados',
            pending_count: 'Pendentes',
            no_items: 'Nenhum item de inventário encontrado',
        },
        exceptions: {
            title: 'Exceções de Classificação',
            count: 'exceções',
            all_severities: 'Todas as Severidades',
            all_statuses: 'Todos os Estados',
            system_name: 'Sistema',
            severity_label: 'Severidade',
            status_open: 'Aberta',
            status_resolved: 'Resolvida',
            status_accepted_risk: 'Risco Aceite',
            resolve: 'Resolver',
            resolve_exception: 'Resolver Exceção',
            resolution_type: 'Tipo de Resolução',
            notes: 'Notas',
            justification_required: 'Justificação obrigatória (mínimo 10 caracteres) para risco aceite',
            resolved_success: 'Exceção resolvida',
            no_exceptions: 'Nenhuma exceção encontrada',
        },
        history: {
            title: 'Histórico de Classificação',
            count: 'eventos',
            actor: 'Ator',
            action: 'Ação',
            entity_type: 'Tipo de Entidade',
            changes: 'Alterações',
            view_lineage: 'Ver Linhagem',
            lineage_title: 'Linhagem de Classificação',
            no_events: 'Nenhum evento de histórico encontrado',
            no_lineage: 'Nenhum dado de linhagem disponível',
            modified: 'Modificado',
            created: 'Criado',
            removed: 'Removido',
            action_created: 'Criado',
            action_updated: 'Atualizado',
            action_archived: 'Arquivado',
            action_approved: 'Aprovado',
            action_deprecated: 'Obsoleto',
            entity_label: 'Etiqueta',
            entity_taxonomy: 'Taxonomia',
            entity_review_task: 'Tarefa de Revisão',
            entity_exception: 'Exceção',
            before: 'Antes',
            after: 'Depois',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Trilha de auditoria de governança',
        audit_trail_subtitle: 'Fluxo de auditoria multi-entidade para ações de governança — aprovações, execuções de playbook, ligações de evidência e mais.',
        audit_trail_item_count: 'eventos de auditoria',
        audit_trail_fetch_error: 'Falha ao obter eventos de auditoria.',
        audit_trail_no_events: 'Nenhum evento de auditoria encontrado para os filtros atuais.',
        audit_trail_entity_type: 'Tipo de entidade',
        audit_trail_entity_id: 'ID da entidade',
        audit_trail_action: 'Ação',
        audit_trail_actor: 'Ator',
        audit_trail_timestamp: 'Carimbo de data/hora',
        audit_trail_date_from: 'De',
        audit_trail_date_to: 'Até',
        audit_trail_no_rows_to_export: 'Sem linhas para exportar.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Fornecedores',
            subprocessors: 'Subcontratantes',
            cross_border: 'Transfronteiriço',
            drift: 'Monitor de Desvios',
            scores: 'Pontuação de Conformidade',
            playbooks: 'Manuais de Procedimentos',
            approvals: 'Aprovações',
            audit_trail: 'Trilha de auditoria',
            board_report: 'Relatório do Conselho',
            evidence: 'Evidências',
            onboarding: 'Pipeline de integração',
            dpa_reviews: 'Revisões de DPA',
            periodic_reviews: 'Revisões periódicas',
            offboarding: 'Desvinculação',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Registo de Fornecedores',
            new: 'Novo Fornecedor',
            edit: 'Editar Fornecedor',
            name: 'Nome',
            jurisdiction: 'Jurisdição',
            dpa_status: 'Estado do APD',
            risk_level: 'Nível de Risco',
            onboarding_status: 'Estado de Integração',
            contact_email: 'Correio Eletrónico de Contacto',
            contact_name: 'Nome do Contacto',
            contact_phone: 'Telefone de Contacto',
            website: 'Sítio Web',
            services_csv: 'Serviços (separados por vírgulas)',
            country: 'País',
            country_placeholder: 'ex. PT',
            last_reviewed: 'Última Revisão',
            offboard: 'Desvincular',
            offboard_vendor: 'Desvincular Fornecedor',
            offboard_reason: 'Motivo da desvinculação',
            no_vendors: 'Nenhum fornecedor encontrado',
            vendor_count: '{count} fornecedor(es)',
            all_dpa_status: 'Todos os Estados DPA',
            all_risk_levels: 'Todos os Níveis de Risco',
            all_onboarding: 'Todas as Integrações',
            fetch_error: 'Falha. Por favor, tente novamente.',
            save_error: 'Falha ao guardar fornecedor',
            offboard_error: 'Falha ao desvincular',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Subcontratante',
            sub_processor: 'Subcontratante Ulterior',
            controller: 'Responsável pelo Tratamento',
            joint_controller: 'Responsáveis Conjuntos',
        },
        onboarding_statuses: {
            pending_review: 'Revisão Pendente',
            approved: 'Aprovado',
            active: 'Ativo',
            suspended: 'Suspenso',
            offboarded: 'Desvinculado',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Não Necessário',
            required_missing: 'Necessário em Falta',
            draft: 'Rascunho',
            under_review: 'Em Revisão',
            signed: 'Assinado',
            expired: 'Expirado',
        },
        risk_levels: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Baixo',
        },
        approval_statuses: {
            pending: 'Pendente',
            approved: 'Aprovado',
            rejected: 'Rejeitado',
            expired: 'Expirado',
        },
        approval_action_types: {
            vendor_onboarding: 'Integração de Fornecedor',
            vendor_offboarding: 'Desvinculação de Fornecedor',
            cross_border_acceptance: 'Aprovação Transfronteiriça',
            playbook_activation: 'Ativação do Playbook',
            drift_accept_risk: 'Desvio Risco Aceite',
            activity_activation: 'Ativação de Atividade',
        },
        drift_check_types: {
            dpa_expiry: 'Expiração do DPA',
            dpa_missing: 'DPA em Falta',
            review_overdue: 'Revisão em Atraso',
            cross_border_gap: 'Lacuna Transfronteiriça',
            sub_processor_chain: 'Cadeia de Subcontratantes',
            risk_assessment_overdue: 'Avaliação de Risco em Atraso',
            retention_policy_missing: 'Política de Retenção em Falta',
            evidence_expiring: 'Evidência a Expirar',
        },
        drift_statuses: {
            open: 'Aberto',
            acknowledged: 'Confirmado',
            in_progress: 'Em Progresso',
            resolved: 'Resolvido',
            accepted_risk: 'Risco Aceite',
        },
        drift_entity_types: {
            vendor: 'Fornecedor',
            system: 'Sistema',
            data_flow: 'Fluxo de Dados',
            processing_activity: 'Atividade de Tratamento',
            evidence_item: 'Elemento de Evidência',
        },
        playbook_statuses: {
            draft: 'Rascunho',
            active: 'Ativo',
            archived: 'Arquivado',
        },
        playbook_action_types: {
            delete_data: 'Eliminar Dados',
            verify_deletion: 'Verificar Eliminação',
            notify_vendor: 'Notificar Fornecedor',
            confirm_receipt: 'Confirmar Receção',
            document_evidence: 'Documentar',
            other: 'Outro',
        },
        playbook_target_types: {
            system: 'Sistema',
            vendor: 'Fornecedor',
        },
        dpa_chain_statuses: {
            complete: 'Completo',
            incomplete: 'Incompleto',
            unknown: 'Desconhecido',
        },
        sub_processor_statuses: {
            active: 'Ativo',
            terminated: 'Cessado',
        },
        evidence_types: {
            dpa_agreement: 'Acordo DPA',
            risk_assessment: 'Avaliação de Risco',
            audit_report: 'Relatório de Auditoria',
            certification: 'Certificação',
            deletion_confirmation: 'Confirmação de Eliminação',
            training_record: 'Registo de Formação',
            incident_report: 'Relatório de Incidente',
            document: 'Documento',
            screenshot: 'Captura de Ecrã',
            policy: 'Política',
            contract: 'Contrato',
            assessment: 'Avaliação',
            correspondence: 'Correspondência',
            other: 'Outro',
        },
        linkable_entity_types: {
            vendor: 'Fornecedor',
            transfer_mechanism: 'Mecanismo de Transferência',
            playbook: 'Livro de procedimentos',
            execution: 'Execução',
            system: 'Sistema',
            activity: 'Atividade',
        },
        subprocessors: {
            title: 'Relações com Subcontratantes',
            new: 'Nova Associação',
            vendor: 'Fornecedor',
            subprocessor: 'Subcontratante',
            relationship_type: 'Tipo de Relação',
            countries: 'Países',
            countries_csv: 'Países (separados por vírgulas)',
            dpa_chain: 'Cadeia DPA',
            dpa_chain_status: 'Estado da Cadeia DPA',
            no_processors: 'Nenhum subcontratante encontrado.',
            no_subprocessors: 'Nenhum subcontratante ulterior encontrado.',
            processor_vendor: 'Fornecedor Subcontratante',
            select_processor: 'Selecionar Subcontratante',
            select_vendor: 'Selecionar Fornecedor',
            services: 'Serviços',
            sub_processor_vendor_id: 'ID do Fornecedor Subcontratante Ulterior',
            terminate: 'Cessar',
            confirm_terminate: 'Tem a certeza de que pretende cessar esta relação?',
            create_error: 'Falha ao criar associação.',
            fetch_error: 'Falha ao obter subcontratantes.',
            processor: 'Subcontratante',
            services_csv: 'Serviços (separados por vírgulas)',
            terminate_error: 'Falha ao cessar.',
            version_history_heading: 'Controle de versão de alteração de subprocessador',
            change_tracking_note: 'Todas as alterações no relacionamento do subprocessador são versionadas. Cada modificação registra o estado antes/depois para trilha de auditoria.',
            history_retention_note: 'O histórico de alterações é retido durante todo o período de retenção de dados. Revise os logs de alterações por meio da visualização detalhada do fornecedor.',
            vendor_risk_cross_view_heading: 'Registo de subcontratantes (Vendor Risk)',
            vendor_risk_cross_view_source: 'Origem: Registo Vendor Risk',
            vendor_risk_cross_view_empty: 'Sem entradas no registo Vendor Risk.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Relatório de Governação Transfronteiriça',
            flows: 'Fluxos Transfronteiriços',
            transfers: 'Transferências Transfronteiriças',
            detected_gaps: 'Lacunas Detetadas',
            safeguards: 'Garantias',
            cross_border_vendors: 'Fornecedores Transfronteiriços',
            destination: 'Destino',
            no_cross_border_vendors: 'Nenhum fornecedor transfronteiriço encontrado.',
            no_safeguard: 'Sem garantia',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Locais de Tratamento',
            export_error: 'Falha ao exportar relatório.',
            export_pdf: 'Exportar PDF',
            export_success: 'Relatório exportado com sucesso.',
            exporting: 'A exportar...',
            fetch_error: 'Falha ao obter dados transfronteiriços.',
            source: 'Origem',
            pdpl_p2_heading: 'PDPL-P2 Conformidade de transferência transfronteiriça',
            dossier_prep_note: 'Preparação do dossiê: reúna a avaliação de impacto, a base jurídica e proteja a documentação antes do arquivamento.',
            filing_tracking_note: 'Rastreamento de arquivamento: monitore o status do envio junto à Autoridade (PDPC) desde a solicitação inicial até a aprovação.',
            supplement_loop_note: 'Ciclo suplementar: responder aos pedidos de informação da Autoridade e reenviar dossiês alterados conforme necessário.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Pesquisa de fornecedor',
            vendor_search_placeholder: 'Pesquisar por nome do fornecedor…',
            jurisdiction_filter_label: 'Jurisdição',
            filings_section_heading: 'Arquivos transfronteiriços',
            filings_empty: 'Ainda não há registros. Crie um para iniciar um dossiê.',
            filings_create_button: 'Novo arquivamento',
            filings_table_id: 'ID de arquivamento',
            filings_table_jurisdiction: 'Jurisdição',
            filings_table_type: 'Tipo de formulário',
            filings_table_status: 'Estado',
            filings_table_updated: 'Atualizado',
            filings_table_open: 'Abrir',
            filings_detail_title: 'Arquivamento Transfronteiriço',
            filings_back_to_list: 'Voltar para Transfronteiriço',
            filings_status_heading: 'Ciclo de vida',
            filings_evidence_heading: 'Evidência',
            filings_history_heading: 'Trilha de auditoria',
            filings_field_filing_id: 'ID de arquivamento',
            filings_field_jurisdiction: 'Jurisdição',
            filings_field_type: 'Tipo de formulário',
            filings_field_status: 'Estado',
            filings_field_reference: 'Referência de autoridade',
            filings_field_mps_receipt: 'MPS recibo',
            filings_field_submitted_at: 'Enviado em',
            filings_field_acknowledged_at: 'Reconhecido em',
            filings_field_archived_at: 'Arquivado em',
            filings_field_version: 'Versão',
            filings_field_notes: 'Notas',
            filings_action_advance: 'Status avançado',
            filings_action_reject: 'Rejeitar',
            filings_action_archive: 'Arquivo',
            filings_action_submit: 'Marcar como enviado',
            filings_action_acknowledge: 'Marco reconheceu',
            filings_action_resume_draft: 'Retomar como rascunho',
            filings_status_draft: 'Rascunho',
            filings_status_assessed: 'Avaliado',
            filings_status_pending_review: 'Revisão pendente',
            filings_status_ready_to_file: 'Pronto para arquivar',
            filings_status_submitted: 'Enviado',
            filings_status_acknowledged: 'Reconhecido',
            filings_status_rejected: 'Rejeitado',
            filings_status_archived: 'Arquivado',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Novo pedido transfronteiriço',
            filings_new_form_jurisdiction: 'Jurisdição',
            filings_new_form_type: 'Tipo de formulário',
            filings_new_form_notes: 'Notas (opcional)',
            filings_new_submit: 'Criar',
            filings_new_cancel: 'Cancelar',
            filings_reason_label: 'Razão',
            filings_reference_label: 'Número de referência da autoridade',
            filings_mps_receipt_label: 'MPS referência do recibo',
            filings_create_error: 'Falha ao criar arquivo.',
            filings_create_success: 'Arquivo criado.',
            filings_update_error: 'Falha ao atualizar o arquivamento.',
            filings_update_success: 'Arquivo atualizado.',
            filings_fetch_error: 'Falha ao buscar registros.',
            filings_archive_confirm: 'Arquivar este arquivo? Isto não pode ser desfeito.',
            filings_status_rejected_detour: 'O arquivamento foi rejeitado após análise. Retome como rascunho para reiniciar o fluxo.',
            recent_exports_heading: 'Exportações recentes',
            recent_exports_empty: 'Ainda não há exportações. Clique em "Exportar PDF" para gerar um.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Monitor de Desvios de Configuração',
            drift_type: 'Tipo de Desvio',
            severity: 'Gravidade',
            detected_at: 'Detetado Em',
            resolve: 'Resolver',
            all_check_types: 'Todos os Tipos de Verificação',
            all_entity_types: 'Todos os Tipos de Entidade',
            event_count: 'Contagem de Eventos',
            accept_risk: 'Aceitar Risco',
            acknowledge: 'Confirmar',
            assign_error: 'Falha ao atribuir.',
            assign_event: 'Atribuir',
            assigned: 'Atribuído',
            enter_resolution_notes: 'Introduzir notas de resolução...',
            enter_user_id: 'Introduzir ID do utilizador...',
            entity: 'Entidade',
            fetch_error: 'Falha ao obter eventos de desvio.',
            no_events: 'Nenhum evento de desvio encontrado.',
            resolution_notes: 'Notas de Resolução',
            update_status_error: 'Falha ao atualizar estado.',
        },
        scores: {
            title: 'Painel de Pontuação de Conformidade',
            overall: 'Pontuação Global de Conformidade',
            breakdown: 'Desagregação da Pontuação por Módulo',
            trend: 'Tendência Histórica',
            module: 'Módulo',
            score: 'Pontuação',
            grade: 'Classificação',
            calculated_at: 'Calculado Em',
            generate_board_report: 'Gerar Relatório para a Direção',
            lang_english: 'Inglês',
            lang_vietnamese: 'Vietnamita',
            fetch_error: 'Falha ao obter pontuações de conformidade.',
            generate_report: 'Gerar Relatório',
            generating: 'A gerar...',
            recommendations: 'Recomendações',
            report_error: 'Falha ao gerar relatório.',
            factor_owner_assigned: 'Proprietário atribuído',
            factor_data_categories_linked: 'Categorias de dados vinculadas',
            factor_purposes_linked: 'Finalidades vinculadas',
            factor_data_flows_documented: 'Fluxos de dados documentados',
            factor_processing_activity_linked: 'Atividade de tratamento vinculada',
            factor_dpa_status: 'Estado do DPA',
            factor_retention_policy_linked: 'Política de retenção vinculada',
            factor_last_reviewed: 'Última revisão dentro do limite',
            factor_risk_assessment_current: 'Avaliação de riscos atualizada',
            factor_sub_processors_documented: 'Subcontratantes documentados',
            factor_cross_border_safeguards: 'Salvaguardas transfronteiriças',
            factor_contact_info_complete: 'Informações de contacto completas',
            factor_onboarding_approved: 'Integração aprovada',
            factor_avg_system_score: 'Pontuação média do sistema',
            factor_avg_vendor_score: 'Pontuação média do fornecedor',
            factor_processing_activity_coverage: 'Cobertura das atividades de tratamento',
            factor_ropa_completeness: 'Completude do RoPA',
            factor_open_drift_events: 'Eventos de desvio em aberto',
        },
        playbooks: {
            title: 'Manuais de Procedimentos de Apagamento',
            new: 'Novo Manual',
            edit: 'Editar Manual',
            steps: 'Passos',
            add_step: 'Adicionar Passo',
            remove_step: 'Remover Passo',
            data_categories_csv: 'Categorias de Dados (separados por vírgulas)',
            estimated_duration: 'Duração Estimada',
            estimated_duration_label: 'Duração Estimada',
            evidence_description: 'Descrição da Evidência',
            no_playbooks: 'Nenhum manual encontrado.',
            playbook_count: 'Número de Manuais',
            requires_evidence: 'Requer Evidência',
            step_label: 'Rótulo do Passo',
            target_name: 'Nome do alvo',
            target_type: 'Tipo de Alvo',
            action_type: 'Tipo de Ação',
            archive_error: 'Falha ao arquivar.',
            confirm_archive: 'Tem a certeza de que pretende arquivar este manual?',
            data_categories: 'Categorias de Dados',
            days: 'dias',
            fetch_error: 'Falha ao obter manuais.',
            sla_days: 'SLA (dias)',
            target_id: 'ID do Alvo',
            template_class_heading: 'Classificação do modelo',
            official: 'Oficial',
            internal: 'Interno',
            classification: 'Classificação',
            official_note: 'Oficial: modelos obrigatórios regulatórios alinhados com PDPL, GDPR ou requisitos específicos do setor.',
            internal_note: 'Internos: procedimentos específicos da organização não derivados de modelos regulatórios.',
            active_executions_heading: 'Execuções ativas',
            no_active_executions: 'Sem execuções ativas.',
            playbook_label: 'Manual',
            ticket_id_label: 'Bilhete',
            progress_label: 'Progresso',
            actions_label: 'Ações',
            view_steps_label: 'Ver passos',
            execution_detail_title: 'Detalhe da execução',
            playbook_missing: 'Manual não encontrado.',
            evidence_required_label: 'Prova necessária',
            upload_evidence_label: 'Carregar prova',
            mark_complete_label: 'Marcar como concluído',
            evidence_required_toast: 'É necessária prova antes de concluir este passo.',
            evidence_blocking_message: 'Anexe um ficheiro de prova para continuar.',
            step_completed_toast: 'Passo concluído.',
            executions_fetch_error: 'Falha ao carregar execuções.',
            step_evidence_default_name: 'Prova do passo de execução',
            attached_evidence_label: 'Provas anexadas',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Fluxos de Aprovação',
            new: 'Nova Aprovação',
            edit: 'Editar Aprovação',
            request_type: 'Tipo de Pedido',
            requester: 'Requerente',
            approve: 'Aprovar',
            reject: 'Rejeitar',
            justification: 'Justificação',
            all_action_types: 'Todos os Tipos de Ação',
            decided_by: 'Decidido Por',
            no_approvals: 'Nenhuma aprovação encontrada.',
            approval_count: 'Número de Aprovações',
            approval_notes_optional: 'Notas (opcional)',
            approve_error: 'Falha ao aprovar.',
            approve_request: 'Aprovar Pedido',
            decision: 'Decisão',
            entity: 'Entidade',
            fetch_error: 'Falha ao obter aprovações.',
            reject_error: 'Falha ao rejeitar.',
            reject_request: 'Rejeitar Pedido',
            rejection_notes_required: 'Notas de rejeição obrigatórias',
            rejection_notes_required_msg: 'Por favor, indique o motivo da rejeição.',
            requested_at: 'Solicitado Em',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Prova de apoio (opcional)',
            decision_evidence_name: 'Prova de decisão',
        },
        evidence: {
            title: 'Evidências de Governação',
            new: 'Nova Evidência',
            link_to: 'Associar A',
            entity_id_placeholder: 'ID da Entidade',
            file_path_placeholder: '/caminho/para/ficheiro',
            evidence_label: 'Rótulo da Evidência',
            file_path: 'Caminho do Ficheiro',
            source_mode_label: 'Fonte da evidência',
            source_mode_file: 'Carregar ficheiro',
            source_mode_url: 'Ligar URL externa',
            attachment_label: 'Anexo',
            item_count: 'Número de Itens',
            link_button: 'Associar Evidência',
            confirm_delete: 'Tem a certeza de que pretende eliminar esta evidência?',
            enter_entity_id: 'Introduzir ID da entidade...',
            entity_id: 'ID da Entidade',
            entity_type: 'Tipo de Entidade',
            expires: 'Expira',
            expires_at: 'Expira Em',
            fetch_error: 'Falha ao obter evidências.',
            link_error: 'Falha ao associar evidência.',
            no_evidence: 'Nenhuma evidência encontrada.',
            tags_csv: 'Etiquetas (separadas por vírgulas)',
            uploaded: 'Carregado',
            url: 'URL',
            framework_tag_heading: 'Evidências Marcadas pela Estrutura',
            framework_tag_note: 'Cada registro de evidência captura o código da estrutura, o ator e o carimbo de data/hora. Filtre por estrutura para isolar registros relevantes para auditoria.',
            framework_code: 'Código Estrutural',
            filter_by_framework: 'Filtrar por código de estrutura...',
            actor: 'Ator',
        },
        certification: {
            not_applicable: 'Não aplicável',
            not_certified: 'Não certificado',
            pending: 'Pendente',
            certified: 'Certificado',
            revoked: 'Revogado',
        },
        onboarding_pipeline: {
            title: 'Pipeline de integração',
            new_workflow: 'Novo fluxo de integração',
            vendor_id: 'ID do fornecedor',
            stages: 'Etapas',
            current_stage: 'Etapa atual',
            advance: 'Avançar etapa',
            advance_success: 'Etapa avançada com sucesso',
            workflow_status: 'Estado do fluxo',
            initiated: 'Iniciado',
            in_progress: 'Em andamento',
            completed: 'Concluído',
            cancelled: 'Cancelado',
            no_workflows: 'Nenhum fluxo de integração encontrado',
            stages_placeholder: 'Etapa 1, Etapa 2, Etapa 3',
            assigned_to: 'Atribuído a',
            notes: 'Notas',
            pdpl_lifecycle_heading: 'PDPL Ciclo de vida do fornecedor – integração',
            due_diligence_note: 'O estágio de due diligence é obrigatório antes da integração de qualquer processador em PDPL. Verifique o escopo do processamento de dados, a base legal e as medidas de segurança.',
            contract_review_note: 'A revisão do contrato deve confirmar as cláusulas do DPA, os limites de retenção de dados e as restrições do subprocessador.',
            create_dpa_review_cta: 'Criar revisão DPA',
            dpa_review_linked: 'Revisão DPA ligada',
        },
        dpa_review: {
            title: 'Revisões de DPA',
            new_review: 'Nova revisão de DPA',
            review_type: 'Tipo de revisão',
            findings: 'Constatações',
            outcome: 'Resultado',
            complete_review: 'Concluir revisão',
            complete_success: 'Revisão concluída com sucesso',
            link_assessment: 'Vincular avaliação de segurança',
            link_success: 'Avaliação vinculada com sucesso',
            assessment_id: 'ID da avaliação',
            combined_title: 'Avaliação combinada',
            no_reviews: 'Nenhuma revisão de DPA encontrada',
            telecom_disclosure_heading: 'Governança de divulgação de telecomunicações',
            disclosure_trigger_note: 'As análises acionadas por divulgação capturam eventos de compartilhamento de dados de assinantes de telecomunicações de acordo com a Lei VN Telecom.',
            telecom_t2_note: 'A aprovação do nível TELECOM-T2 é necessária antes de divulgar os dados do assinante às autoridades.',
            quarterly_log_note: 'Use o registro trimestral para documentar todos os eventos de divulgação e manter uma trilha de auditoria contínua.',
        },
        periodic_review: {
            title: 'Revisões periódicas',
            new_review: 'Nova revisão periódica',
            review_type: 'Tipo de revisão',
            due_date: 'Data de vencimento',
            next_review: 'Próxima data de revisão',
            trigger_reassessment: 'Acionar reavaliação',
            reassessment_reason: 'Motivo da reavaliação',
            reassessment_success: 'Reavaliação acionada com sucesso',
            complete_success: 'Revisão concluída com sucesso',
            scheduled: 'Agendada',
            in_progress: 'Em andamento',
            completed: 'Concluída',
            overdue: 'Atrasada',
            material_change: 'Alteração significativa',
            no_reviews: 'Nenhuma revisão periódica encontrada',
            mark_complete: 'Marcar como concluído',
            pdpl_periodic_heading: 'PDPL Ciclo de vida do fornecedor – revisões periódicas',
            scheduled_review_note: 'As revisões periódicas devem ser agendadas pelo menos uma vez por ano para todos os processadores ativos. As revisões atrasadas acionam alertas de conformidade.',
            material_change_note: 'Mudanças materiais no processamento de dados exigem reavaliação imediata, independentemente do cronograma.',
            overdue_badge: 'Em atraso',
            overdue_aria: 'Revisão em atraso',
            only_overdue_filter: 'Apenas em atraso',
        },
        offboarding: {
            title: 'Desvinculação de fornecedor',
            initiate: 'Iniciar desvinculação',
            initiate_success: 'Desvinculação iniciada com sucesso',
            reason: 'Motivo da desvinculação',
            data_return_required: 'Devolução de dados obrigatória',
            verify_data: 'Verificar devolução de dados',
            verify_success: 'Devolução de dados verificada com sucesso',
            sign_off: 'Aprovar',
            sign_off_success: 'Desvinculação aprovada com sucesso',
            status_initiated: 'Iniciada',
            status_data_pending: 'Devolução de dados pendente',
            status_data_verified: 'Devolução de dados verificada',
            status_completed: 'Concluída',
            status_cancelled: 'Cancelada',
            task_id: 'ID da tarefa',
            no_tasks: 'Nenhuma tarefa de desvinculação encontrada',
            evidence_id: 'ID de evidência',
            pdpl_offboarding_heading: 'PDPL Ciclo de vida do fornecedor — desativação e exclusão',
            deletion_capture_note: 'O offboarding deve capturar a confirmação de exclusão de dados do fornecedor. Verifique o retorno dos dados antes da aprovação.',
            evidence_attachment_note: 'Anexe evidências de exclusão (certificados, capturas de tela, confirmações de fornecedores) para completar o registro de desligamento.',
            verify_mode_label: 'Método de verificação',
            verify_mode_upload: 'Carregar novo ficheiro',
            verify_mode_link: 'Ligar prova existente',
            verify_mode_none: 'Nenhum dado a devolver',
            upload_data_return_evidence: 'Carregar prova de devolução de dados',
            no_data_justification_label: 'Justificação',
            no_data_justification_placeholder: 'Explica por que não há dados a devolver',
            no_data_justification_hint: 'Mínimo 20 caracteres',
            upload_required_toast: 'Anexe um ficheiro para continuar',
            evidence_link_required_toast: 'Selecione uma prova para continuar',
            no_data_justification_required_toast: 'Forneça uma justificação de pelo menos 20 caracteres',
            data_return_evidence_name: 'Prova de devolução de dados',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Inicial',
            annual: 'Anual',
            renewal: 'Renovação',
            incident_triggered: 'Por incidente',
        },
        periodic_review_types: {
            initial: 'Inicial',
            annual: 'Anual',
            renewal: 'Renovação',
            incident_triggered: 'Por incidente',
            material_change: 'Alteração material',
            disclosure_triggered: 'Por divulgação',
            quarterly_log: 'Registo trimestral',
        },
        dpa_review_statuses: {
            pending: 'Pendente',
            in_progress: 'Em andamento',
            completed: 'Concluído',
            cancelled: 'Cancelado',
        },
        onboarding_stage_names: {
            due_diligence: 'Due diligence',
            contract_review: 'Revisão de contrato',
            security_assessment: 'Avaliação de segurança',
            data_mapping: 'Mapeamento de dados',
            approval: 'Aprovação',
            integration: 'Integração',
        },
        access_denied_title: 'Acesso negado',
        access_denied: 'Não tem permissão para visualizar esta página. Contacte o seu administrador.',
    },
    consent: {
        nav: {
            policies: 'Políticas',
            purposes: 'Catálogo de Finalidades',
            workflows: 'Fluxos de Trabalho',
            snapshots: 'Capturas CMP',
            change_awareness: 'Deteção de Alterações',
            audit: 'Auditoria de Consentimento',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Análise de lacunas',
            page_title: 'Análise de lacuna de consentimento',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Estruturas',
            org_flags_label: 'Contexto da organização',
            flag_withdrawal: 'Tem mecanismo de retirada global documentado',
            flag_parental: 'Possui fluxo de consentimento dos pais para menores de 16 anos',
            flag_cross_border: 'Tem registro transfronteiriço',
            run_label: 'Execute a análise de lacunas',
            running_label: 'Correndo…',
            requires_advisor_label: 'Requer permissão de ai_advisor:chat.',
            results_heading: 'Lacunas surgiram ({count})',
            tokens_label: '≈ {count} tokens',
            empty_label: 'Nenhuma lacuna detectada para as estruturas selecionadas.',
            remediation_label: 'Correção sugerida:',
            dismiss_label: 'Descartar como não aplicável',
            resolve_label: 'Resolver',
            dismissed_label: 'Rejeitado como não aplicável',
            resolved_label: 'Resolvido',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Importar fluxos de consentimento',
            nav_title: 'Importação em massa',
            description: 'Faça upload de um ficheiro CSV para importar em massa fluxos de trabalho de consentimento.',
            select_file: 'Selecionar ficheiro CSV',
            file_help: 'CSV com cabeçalhos correspondentes aos campos do fluxo de trabalho.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Colunas obrigatórias: Tipo de fluxo, Titular dos dados, Canal.',
            file_too_large: 'Ficheiro demasiado grande. O tamanho máximo é 2MB.',
            preview: 'Pré-visualização',
            columns_detected: 'Colunas detetadas: {count}',
            column_mapping: 'Mapeamento de colunas',
            import_button: 'Importar',
            import_complete: 'Importação concluída',
            import_error: 'A importação falhou',
            result: 'Resultado da importação',
            total: 'Total',
            imported: 'Importado',
            skipped: 'Ignorado',
            failed: 'Falhado',
            errors: 'Erros',
            error_row: 'Linha',
            error_field: 'Campo',
            error_message: 'Mensagem',
            history: 'Histórico de importação',
            no_history: 'Sem histórico de importação.',
            history_file: 'Nome do ficheiro',
            history_date: 'Data',
            create_profiles_label: 'Criar perfis de Titular dos dados para novos titulares',
            create_profiles_help: 'Importações sem um perfil correspondente criam um automaticamente. Desmarque para importar apenas fluxos de trabalho.',
            profiles_created: 'Perfis criados',
            profiles_matched: 'Perfis correspondentes',
            subject_kind_help: 'Colunas opcionais: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'E-mail inválido na coluna subject_email.',
            error_subject_unknown_kind: 'Valor de subject_kind desconhecido. Esperado: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key é obrigatório quando subject_kind=external_ref',
        },
        policies: {
            title: 'Políticas de Consentimento',
            new: 'Nova Política',
            edit: 'Editar Política',
            name: 'Nome',
            version: 'Versão',
            policy_text: 'Texto da Política',
            publish: 'Publicar',
            versions: 'Versões',
            hide_versions: 'Ocultar versões',
            submit_for_review: 'Submeter para Rever',
            suspend: 'Suspender',
            retire: 'Retirar',
            jurisdictions: 'Jurisdições',
            jurisdictions_csv: 'Jurisdições (separadas por vírgulas)',
            approval_mode: 'Modo de aprovação',
            reviewer_ids: 'IDs de revisores (separados por vírgulas)',
            tags_csv: 'Etiquetas (separadas por vírgulas)',
            effective_date: 'Data de vigência',
            review_date: 'Rever Data',
            consent_model_statement: 'Declaração do modelo de consentimento',
            consent_model_placeholder: 'Declaração opcional do modelo de consentimento...',
            default_settings: 'Predefinido Definições (JSON)',
            changes: 'Alterações',
            fetch_error: 'Falha ao fetch consent policies. Por favor, tente novamente.',
            save_error: 'Falha ao save consentimento política. Por favor, tente novamente.',
            submit_review_error: 'Falha ao submit policy for review. Por favor, tente novamente.',
            activate_error: 'Falha ao activate policy. Por favor, tente novamente.',
            suspend_error: 'Falha ao suspend policy. Por favor, tente novamente.',
            retire_error: 'Falha ao retire policy. Por favor, tente novamente.',
            submit_review_success: 'Política enviada para revisão.',
            activate_success: 'Política ativada.',
            suspend_success: 'Política suspensa.',
            retire_success: 'Política desativada.',
            versions_error: 'Falha ao fetch policy versions. Por favor, tente novamente.',
            invalid_json: 'JSON inválido no campo de definições predefinidas. Por favor, tente novamente.',
            page_title: 'Políticas de consentimento',
            version_updated: 'Atualizado',
            version_initial: 'Versão inicial',
            json_format: 'Formato JSON',
            sensitive: 'Sensível',
            flag_sensitive: 'Sensível à bandeira',
            flag_sensitive_title: 'Sinalizar política como sensível',
            require_enhanced_review: 'Exigir revisão aprimorada',
            enhanced_review_success: 'Requisito de revisão aprimorado ativado.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Selecione as categorias de dados confidenciais aplicáveis do PDPL Artigo 2:',
            no_policies: 'Nenhuma política de consentimento encontrada. Crie a sua primeira política para começar.',
            approval_success: 'Política aprovada.',
            reviewers_label: 'Revisores',
            enhanced_review_title: 'Revisão aprofundada necessária',
            enhanced_review_badge: 'Revisão aprofundada necessária',
            enhanced_review_status_pending: 'Revisão pendente',
            enhanced_review_status_approved: 'Revisão aprovada',
            enhanced_review_status_rejected: 'Revisão rejeitada',
            enhanced_review_reviewed_by: 'Revisto por',
            enhanced_review_notes: 'Notas de revisão',
            enhanced_review_approve: 'Aprovar revisão',
            enhanced_review_reject: 'Rejeitar revisão',
            enhanced_review_notes_required: 'As notas de revisão são obrigatórias',
            enhanced_review_approved: 'Revisão aprofundada aprovada',
            enhanced_review_rejected: 'Revisão aprofundada rejeitada — política redefinida para rascunho',
            enhanced_review_reset_to_draft: 'Revisão aprofundada rejeitada — política redefinida para rascunho',
            enhanced_review_reject_warning: 'A rejeição irá redefinir esta política para o estado Rascunho. O DPO deve resubmetê-la para revisão após resolver os problemas.',
            submitted_modal_title: 'Enviado para revisão aprofundada',
            submitted_modal_body: 'Esta política foi enviada. Um revisor Jurídico/Privacidade deve aprová-la antes que possa ser ativada.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Dados de saude',
            biometric: 'Dados biometricos',
            political: 'Opinioes politicas',
            ethnic: 'Origem etnica',
            genetic: 'Dados geneticos',
            criminal: 'Registos criminais',
        },
        purposes: {
            title: 'Catálogo de Finalidades',
            new: 'Nova Finalidade',
            edit: 'Editar Finalidade',
            name: 'Nome',
            legal_basis: 'Base Jurídica',
            retention_days: 'Período de Retenção (dias)',
            is_active: 'Ativo',
            filter_by_category: 'Filtrar por categoria',
            policy_ids: 'IDs de políticas (separados por vírgulas)',
            ropa_activity_ids: 'IDs de atividades ROPA (separados por vírgulas)',
            policies: 'Políticas',
            deprecate: 'Tornar obsoleto',
            fetch_error: 'Falha ao fetch consent purposes. Por favor, tente novamente.',
            save_error: 'Falha ao save consent purpose. Por favor, tente novamente.',
            deprecate_error: 'Falha ao deprecate consent purpose. Por favor, tente novamente.',
            page_title: 'Catálogo de Finalidade',
            no_purposes: 'Nenhuma finalidade de consentimento encontrada. Crie a sua primeira finalidade para começar.',
            category_personal_data: 'Dados Pessoais',
            category_sensitive_data: 'Dados confidenciais',
            category_health_data: 'Dados de saúde',
            category_financial_data: 'Dados Financeiros',
            category_biometric_data: 'Dados biométricos',
            category_children_data: 'Dados infantis',
            category_location_data: 'Dados de localização',
            category_communications_data: 'Dados de comunicações',
            category_other: 'Outro',
        },
        workflows: {
            title: 'Fluxos de Trabalho de Consentimento',
            new: 'Novo Fluxo de Trabalho',
            edit: 'Editar Fluxo de Trabalho',
            workflow_type: 'Tipo de Fluxo de Trabalho',
            user_id: 'ID do Utilizador',
            purpose_id: 'ID da Finalidade',
            purpose_ids_csv: 'Finalidade ID (comma-separated)',
            reason: 'Motivo',
            process: 'Processar',
            data_subject_ref: 'Dados Titular Reference',
            data_subject: 'Dados Titular',
            channel: 'Canal',
            policy_id: 'Política ID',
            scope: 'Âmbito',
            legal_grounds: 'Bases jurídicas',
            assessment: 'Avaliação',
            select_outcome: 'Selecione Outcome',
            partially_approved: 'Aprovado parcialmente',
            denied: 'Negado',
            outcome_upheld: 'Confirmado',
            outcome_pending: 'Pendente',
            outcome_denied: 'Negado',
            status_count: 'Contagem por estado',
            channel_filter: 'Filtrar por canal',
            add_action: '+ Ação',
            by_type: 'Por Tipo',
            by_status: 'Por Estado',
            downstream_actions: 'Ações a jusante',
            downstream_actions_for: 'Downstream Ações para {subject} ({type})',
            add_downstream_action: 'Adicionar Downstream Action',
            assigned_to: 'Atribuído Para',
            fetch_error: 'Falha ao fetch consent workflows. Por favor, tente novamente.',
            dashboard_error: 'Falha ao fetch workflow dashboard. Por favor, tente novamente.',
            save_error: 'Falha ao save consent workflow. Por favor, tente novamente.',
            status_update_error: 'Falha ao update workflow status. Por favor, tente novamente.',
            assessment_error: 'Falha ao update workflow assessment. Por favor, tente novamente.',
            add_action_error: 'Falha ao add workflow action. Por favor, tente novamente.',
            update_action_error: 'Falha ao update workflow action. Por favor, tente novamente.',
            type_refusal: 'Recusa',
            type_withdrawal: 'Retirada',
            type_restriction: 'Restrição',
            type_objection: 'Oposição',
            status_received: 'Recebido',
            status_processing: 'Tratamento',
            status_under_assessment: 'Em avaliação',
            status_implemented: 'Implementado',
            evidence_button: 'Evidência',
            evidence_modal_title: 'Evidência de fluxo de trabalho',
            no_evidence: 'Nenhuma evidência anexada a este fluxo de trabalho.',
            upload_evidence_label: 'Carregar evidências de fluxo de trabalho',
            completed_at: 'Concluído em',
            effective_date: 'Data Efetiva',
            page_title: 'Fluxos de trabalho de consentimento',
            no_workflows: 'Nenhum fluxo de consentimento encontrado. Os fluxos são criados quando os titulares dos dados exercem os seus direitos.',
            type_grant: 'Concessão',
            propagation_none: 'Nenhum',
            propagation_pending: 'Pendente',
            propagation_pending_warning: 'Não é possível concluir — a propagação de retirada ainda está pendente',
            propagation_completed: 'Concluído',
            propagation_partial: 'Parcial',
            propagation_incomplete_error: 'Não é possível concluir o fluxo enquanto a propagação de retirada ainda estiver pendente',
            propagation_status: 'Estado de propagação',
        },
        snapshots: {
            title: 'Capturas CMP',
            new: 'Nova Captura',
            label: 'Etiqueta',
            snapshot_date: 'Data da Captura',
            purposes_count: 'Número de Finalidades',
            compare: 'Comparar',
            snapshot_data: 'Dados da Captura (JSON)',
            select_left: 'Selecione Left Snapshot',
            select_right: 'Selecione Right Snapshot',
            comparison_results: 'Resultados de comparação',
            field: 'Campo',
            left: 'Esquerda',
            right: 'Direita',
            close_comparison: 'Fechar comparação',
            trigger: 'Acionador',
            trigger_manual: 'Manuais',
            trigger_policy_approval: 'Aprovação de políticas',
            cmp_version: 'Versão de CMP',
            policy_version_id: 'Política Versão ID',
            banner_config: 'Configuração do banner (JSON)',
            active_purposes: 'Ativo Finalidades (JSON)',
            active_legal_bases: 'Bases jurídicas ativas (JSON)',
            domain_scope: 'Âmbito do domínio',
            domain_scope_csv: 'Âmbito do domínio (separado por vírgulas)',
            raw_config: 'Configuração bruta (JSON)',
            checksum: 'Soma de verificação',
            created: 'Criado',
            fetch_error: 'Falha ao fetch cmp snapshots. Por favor, tente novamente.',
            save_error: 'Falha ao save cmp snapshot. Por favor, tente novamente.',
            compare_error: 'Falha ao compare snapshots. Por favor, tente novamente.',
            page_title: 'Instantâneos de consentimento',
            json_format: 'Formato JSON',
            json_array_format: 'formato de matriz JSON',
            manual_snapshot_note: 'Apenas para administradores/desenvolvedores. Em operação normal, os instantâneos são criados automaticamente via webhook CMP ou na aprovação de políticas. Cole o JSON exportado do seu fornecedor CMP (ex. OneTrust, Cookiebot).',
            banner_config_hint: 'Cole o objeto de configuração do banner da sua exportação CMP.',
            active_purposes_hint: 'Array JSON de objetos de finalidades ativas copiados da sua exportação CMP.',
            active_legal_bases_hint: 'Array JSON de objetos de bases legais ativas copiados da sua exportação CMP.',
            raw_config_hint: 'JSON de configuração bruto completo do seu CMP. Os outros campos JSON são extraídos daqui. Uma soma de verificação SHA-256 é calculada automaticamente.',
            changed_field: 'Campo alterado: ',
            no_snapshots: 'Nenhum instantâneo CMP encontrado. Os instantâneos são criados quando as políticas são aprovadas ou acionados manualmente.',
            detail_title: 'Detalhe do instantâneo',
            view_snapshot_detail: 'Ver detalhe do instantâneo',
            policy_version: 'Versão da política',
            removed: 'Antes',
            added: 'Depois',
        },
        change_awareness: {
            title: 'Deteção de Alterações de Consentimento',
            change_type: 'Tipo de Alteração',
            entity_type: 'Tipo de Entidade',
            entity_id: 'ID da Entidade',
            detected_at: 'Detetado Em',
            acknowledge: 'Confirmar',
            new_alert: 'Novo alerta',
            new_alert_form: 'Novo Change Awareness Alert',
            total_open: 'Total Abrir',
            by_severity: 'Por Gravidade',
            snapshot_id: 'ID de instantâneo',
            update_status: 'Atualizar Estado',
            resolution_notes: 'Notas de resolução',
            resolution_required: 'Notas de resolução (obrigatórias)',
            resolution_required_msg: 'As notas de resolução são obrigatórias ao resolver um item.',
            status_open: 'Abrir',
            status_under_review: 'Em revisão',
            status_resolved: 'Resolvido',
            status_accepted: 'Aceite',
            severity_informational: 'Informativo',
            severity_requires_review: 'Requer revisão',
            severity_policy_violation: 'Violação de política',
            create_reconciliation: 'Criar tarefa de reconciliação',
            reconciliation_created: 'Tarefa de reconciliação criada com sucesso.',
            reconciliation_error: 'Falha ao criar tarefa de reconciliação. Por favor, tente novamente.',
            fetch_error: 'Falha ao fetch change awareness items. Por favor, tente novamente.',
            dashboard_error: 'Falha ao fetch change awareness dashboard. Por favor, tente novamente.',
            create_error: 'Falha ao create change awareness item. Por favor, tente novamente.',
            status_update_error: 'Falha ao update change awareness status. Por favor, tente novamente.',
            page_title: 'Conscientização sobre a mudança',
            no_items: 'Nenhum item de consciência de alterações encontrado. Os itens são criados quando são detetadas alterações na configuração do consentimento.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Trilho de Auditoria de Consentimento',
            event_type: 'Tipo de Evento',
            user_id: 'ID do Utilizador',
            timestamp: 'Data e Hora',
            details: 'Detalhes',
            actor: 'Ator',
            filter_by_entity_type: 'Filtrar por tipo de entidade',
            filter_by_action: 'Filtrar por ação',
            fetch_error: 'Falha ao fetch consent audit trail. Por favor, tente novamente.',
            page_title: 'Auditoria de consentimento',
            export_csv: 'Exportar CSV',
            exporting: 'Exportando…',
            export_success: 'Trilha de auditoria exportada com sucesso.',
            export_error: 'Falha ao exportar a trilha de auditoria de consentimento. Por favor, tente novamente.',
            no_entries: 'Nenhuma entrada de auditoria encontrada. Os eventos de auditoria são registados automaticamente quando os dados de consentimento mudam.',
            entity_policy: 'Política',
            entity_purpose: 'Finalidade',
            entity_workflow: 'Fluxo de trabalho',
            entity_consent_record: 'Registo de consentimento',
            entity_change_awareness: 'Consciência de alterações',
            entity_cmp_snapshot: 'Instantâneo CMP',
            action_created: 'Criado',
            action_updated: 'Atualizado',
            action_status_changed: 'Estado alterado',
            action_drift_detected: 'Desvio detetado',
            action_snapshot_created: 'Instantâneo criado',
            action_approval_submitted: 'Aprovação submetida',
            action_archived: 'Arquivado',
            action_purpose_auto_mapped: 'Finalidade atribuída automaticamente',
            action_purpose_mapping_updated: 'Atribuição de finalidade atualizada',
            action_webhook_configured: 'Webhook configurado',
            entity_propagation: 'Propagação',
            entity_reconciliation: 'Reconciliação',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Propagação confirmada',
            action_propagation_failed: 'Falha na propagação',
            action_consent_captured: 'Consentimento capturado',
            action_reconciliation_created: 'Reconciliação criada',
            action_reconciliation_resolved: 'Reconciliação resolvida',
            action_submitted_for_review: 'Submetido para revisão',
            action_activated: 'Ativado',
            action_suspended: 'Suspenso',
            action_retired: 'Retirado',
            action_enhanced_review_required: 'Revisão aprimorada necessária',
            action_enhanced_review_approved: 'Revisão aprofundada aprovada',
            action_enhanced_review_rejected: 'Revisão aprofundada rejeitada — política redefinida para rascunho',
            action_consented: 'Consentido',
            action_consented_customize: 'Consentimento personalizado',
            action_revoked: 'Revogado',
            action_rejected: 'Rejeitado',
        },
        web_records: {
            title: 'Registros de consentimento da Web',
            page_title: 'Registros de consentimento da Web',
            domain: 'Domínio',
            visitor_id: 'ID do visitante',
            consent_action: 'Ação de consentimento',
            received_at: 'Recebido em',
            admin_status: 'Status do administrador',
            revoke: 'Revogar',
            reject: 'Rejeitar',
            reason: 'Razão',
            reason_placeholder: 'Razão para esta ação (opcional)',
            revoke_confirm: 'Revogar este registro de consentimento?',
            reject_confirm: 'Rejeitar este registro de consentimento?',
            override_success: 'Status do registro atualizado com sucesso.',
            override_error: 'Falha ao atualizar o status do registro.',
            no_records: 'Nenhum registro de consentimento da web encontrado.',
            fetch_error: 'Falha ao carregar registros de consentimento da web.',
            filter_by_domain: 'Filtrar por domínio',
            filter_by_action: 'Filtrar por ação',
            active: 'Ativo',
        },
        legal_bases: {
            all: 'Todas as bases jurídicas',
        },
        common_statuses: {
            suspended: 'Suspenido',
            retired: 'Retirado',
            deprecated: 'Obsoleto',
        },
        approval_modes: {
            all: 'All',
            any: 'Any',
        },
        cmp_integration: {
            title: 'Integração AesirX CMP',
            back_to_consent: 'Voltar para Consentimento',
            back_to_integration: 'Voltar para Integração',
            webhook_status: 'Webhook Estado',
            events_received: 'Eventos recebidos',
            web_consent_records: 'Web Consentimento Registos',
            unmapped_purposes: 'Finalidades não mapeadas',
            integration_management: 'Integração Gestão',
            webhook_configuration: 'Webhook Configuração',
            webhook_config_desc: 'Configure o endpoint do webhook de CMP e gira o segredo HMAC.',
            purpose_mapping: 'Finalidade Mapeamento',
            purpose_mapping_desc: 'Mapeie os identificadores de finalidade de CMP para as finalidades do catálogo de consentimento de GRC.',
            bulk_sync: 'Sincronização em massa',
            bulk_sync_desc: 'Importe dados históricos de consentimento e visualize o histórico de sincronização.',
            consent_analytics: 'Análise de consentimento web (últimos 30 dias)',
            consent_grant_rate: 'Consentimento Grant Taxa',
            top_domain: 'Domínio principal',
            records: 'registos',
            banner_interactions: 'Interações do banner',
            consent_actions: 'Ações de consentimento',
            opt_in_trend: 'Tendência opt-in/opt-out',
            records_by_domain: 'Registros por Domínio',
            date: 'Data',
            grants: 'Consentimentos concedidos',
            denials: 'Recusas',
            not_configured: 'Não configurado',
            load_error: 'Falha ao load integration data. Por favor, tente novamente.',
            webhook_not_configured: 'Webhook não configured.',
            webhook_not_configured_msg: 'Por favor, primeiro {link}.',
            configure_webhook_link: 'configurar o webhook',
            setup_wizard: 'Assistente de configuração',
            step_configure_webhook: 'Configurar Webhook',
            step_map_purposes: 'Mapear finalidades',
            step_run_sync: 'Executar sincronização em massa inicial',
            step_verify_data: 'Verificar Dados em Painel de controlo',
            configure: 'Configurar',
            view_dashboard: 'Ver painel de controlo',
            syncing: 'A sincronizar...',
            sync_now: 'Sincronizar Agora',
            sync_completed: 'Sincronizar concluído: {imported} importado, {skipped} skipped, {failed} falhou.',
            sync_failed: 'Sincronizar falhou',
            last_sync_result: 'Último Sincronizar Resultado',
            imported: 'Importado',
            skipped: 'Ignorado',
            errors: 'Erros',
            index: 'Índice',
            error: 'Erro',
            sync_history: 'Sincronizar Histórico',
            no_sync_jobs: 'Ainda não foram registados trabalhos de sincronização.',
            load_sync_error: 'Falha ao load sync data. Por favor, tente novamente.',
            purpose_mapping_title: 'CMP Finalidade Mapeamento',
            auto_mapping: 'A mapear auto...',
            auto_map_purposes: 'Auto-Map Finalidades',
            unmapped_warning: '{count} finalidade(s) não mapeada(s) - essas finalidades CMP precisam ser mapeadas para finalidades de GRC.',
            mapping_saved: 'Mapeamento guardado.',
            mapping_deleted: 'Mapeamento eliminado.',
            auto_map_complete: 'Mapeamento automático concluído: {mapped} mapeados, {unmatched} não mapeados.',
            auto_map_failed: 'Auto-map falhou',
            save_mapping_error: 'Falha ao save mapping. Por favor, tente novamente.',
            delete_mapping_error: 'Falha ao delete mapping. Por favor, tente novamente.',
            load_data_error: 'Falha ao load data. Por favor, tente novamente.',
            cmp_purpose: 'Finalidade CMP',
            grc_purpose: 'Finalidade GRC',
            select_grc_purpose: '-- Selecionar GRC Finalidade --',
            auto_mapped: 'Mapeado auto.',
            mapped: 'Mapeado',
            unmapped: 'Não mapeado',
            no_mappings: 'Nenhuma correspondência de finalidade encontrada. As correspondências são criadas automaticamente quando eventos de consentimento são recebidos.',
            total_mappings: 'Total: {count} mapeamento(s)',
            webhook_config_title: 'Webhook Configuração',
            current_configuration: 'Atual Configuração',
            setup_webhook: 'Configurar webhook',
            setup_webhook_desc: 'Configure o webhook do AesirX CMP para receber eventos de consentimento.',
            allowed_domains: 'Domínios permitidos (um por linha)',
            webhook_secret_warning: 'Segredo do webhook (guarde agora -- não será mostrado novamente):',
            last_event: 'Último Evento',
            none_received: 'Nenhum',
            update_domains: 'Atualizar domínios',
            pause_webhook: 'Pausar webhook',
            resume_webhook: 'Retomar webhook',
            regenerate_secret: 'Regenerar segredo',
            configuring: 'A configurar...',
            create_webhook_config: 'Adicionar webhook configuração',
            webhook_endpoint: 'Endpoint do webhook',
            webhook_endpoint_desc: 'Configure o seu AesirX CMP para enviar eventos de consentimento para:',
            webhook_signature_note: 'Inclua a assinatura HMAC-SHA256 no cabeçalho {header}.',
            configured_success: 'Webhook configured com sucesso',
            config_updated: 'Configuração atualizado.',
            config_error: 'Falha ao configure webhook. Por favor, tente novamente.',
            update_error: 'Falha ao update configuration. Por favor, tente novamente.',
            toggle_error: 'Falha ao toggle webhook. Por favor, tente novamente.',
            deactivated: 'Webhook desativado.',
            deactivate_error: 'Falha ao deactivate webhook. Por favor, tente novamente.',
            page_title: 'CMP Integração',
            webhook_config_page_title: 'CMP Configuração de webhook',
            bulk_sync_page_title: 'Remapear registros',
            purpose_mapping_page_title: 'Mapeamento de finalidade',
            data_loaded: 'CMP dados de integração carregados',
            sync_all: 'Sincronizar tudo',
            sync_selected: 'Sincronizar selecionados',
            sync_status: 'Estado da sincronização',
            last_sync: 'Última sincronização',
            no_records: 'Nenhum registo encontrado.',
            sync_in_progress: 'Sincronização em curso...',
            sync_complete: 'Sincronização concluída.',
            sync_error: 'A sincronização falhou. Por favor, tente novamente.',
            map_purpose: 'Associar finalidade',
            unmap: 'Desassociar',
            mapping_status: 'Estado da associação',
            save_mapping: 'Guardar associação',
            remap_unmapped_records: 'Remapear registros não mapeados',
            remap_records: 'Remapear registros',
            add_mappings_first: 'Adicione mapeamentos de propósito primeiro (Etapa 2)',
            remap_result: 'Resultado do remapeamento',
            remap_result_desc: 'Processou todos os registros de consentimento com finalidades não mapeadas ou parcialmente mapeadas.',
            total_processed: 'Total Processado',
            newly_mapped: 'Recentemente mapeado',
            still_unmapped_label: 'Ainda não mapeado',
            still_unmapped_msg: 'Os registros {count} ainda têm finalidades não mapeadas. {link} e clique em Remapear novamente.',
            add_more_mappings: 'Adicione mais mapeamentos',
            remap_complete: 'Remapeamento concluído: {remapped} recentemente mapeado, {still_unmapped} ainda não mapeado.',
            add_mapping: 'Adicionar mapeamento',
            cmp_purpose_id_label: 'CMP ID da finalidade *',
            cmp_purpose_id_required: 'CMP O ID da finalidade é obrigatório',
            cmp_purpose_name_label: 'CMP Nome da finalidade',
            grc_purpose_label: 'Objetivo do GRC',
        },
        propagation: {
            title: 'Propagação de retirada',
            systems: 'Sistemas vinculados',
            status: 'Estado',
            confirmed: 'Confirmado',
            pending: 'Pendente',
            failed: 'Falhado',
            confirm: 'Confirmar retirada',
            fail: 'Marcar como falhado',
            failure_reason: 'Motivo da falha',
            can_close: 'Todos os sistemas confirmados — o fluxo pode ser encerrado',
            cannot_close: 'A aguardar confirmações do sistema',
            no_tasks: 'Sem tarefas de propagação.',
            confirm_success: 'Retirada do sistema confirmada.',
            fail_success: 'Sistema marcado como falhado.',
            propagate_success: 'Retirada propagada aos sistemas vinculados.',
            propagate: 'Propagar para sistemas',
            add_system: 'Adicionar sistema',
            remove_system: 'Remover',
            system_id: 'ID do sistema',
            system_name: 'Nome do sistema',
            owner_id: 'ID do proprietário',
            propagate_error: 'Falha ao propagar a retirada. Por favor, tente novamente.',
            no_systems: 'Adicione pelo menos um sistema.',
            page_title: 'Propagação de retirada',
            workflow_id: 'Selecione o fluxo de trabalho de retirada',
            system_id_hint: 'ex. crm-001, erp-prod',
            owner_id_hint: 'Proprietário do sistema',
            elapsed: 'Decorrido',
        },
        sensitive: {
            title: 'Políticas de dados sensíveis',
            flag: 'Marcar como sensível',
            categories: 'Categorias sensíveis',
            enhanced_review: 'Exigir revisão aprofundada',
            enhanced_review_success: 'Revisão aprofundada exigida.',
            flag_success: 'Política marcada como sensível.',
            no_sensitive: 'Sem políticas de dados sensíveis.',
        },
        reconciliation: {
            title: 'Reconciliação de discrepâncias',
            create: 'Nova tarefa de reconciliação',
            system: 'Sistema',
            owner: 'Proprietário',
            description: 'Descrição',
            resolution: 'Resolução',
            resolve: 'Resolver',
            update_grc: 'Atualizar GRC',
            update_cmp: 'Atualizar CMP',
            accept_difference: 'Aceitar diferença',
            escalate: 'Escalar',
            resolution_notes: 'Notas de resolução',
            no_tasks: 'Sem tarefas de reconciliação.',
            resolve_success: 'Tarefa de reconciliação resolvida.',
            create_success: 'Tarefa de reconciliação criada.',
            open: 'Aberto',
            resolved: 'Resolvido',
            escalated: 'Escalado',
            dashboard_open: 'Tarefas abertas',
            dashboard_resolved: 'Tarefas resolvidas',
            dashboard_escalated: 'Tarefas escaladas',
            page_title: 'Reconciliação de incompatibilidade',
            my_tasks: 'Minhas tarefas',
            all_tasks: 'Todas as tarefas',
            severity: 'Gravidade',
        },
        sync: {
            title: 'Sincronização CMP',
            direction: 'Direção',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Conflito',
            resolve_conflict: 'Resolver conflito',
            no_events: 'Sem eventos de sincronização.',
            resolve_success: 'Conflito de sincronização resolvido.',
            create_success: 'Evento de sincronização registado.',
            page_title: 'CMP Sincronizar',
            detect_drift: 'Detectar conflitos',
            detecting: 'Detectando...',
            drift_result: '{detected} conflito(s) detectado(s) fora de {total} objetivo(s) comparado(s).',
            no_drift: 'Nenhum novo conflito detectado.',
            drift_error: 'Falha ao executar a detecção de conflitos.',
            resolve_modal_title: 'Resolver Conflitos',
            resolve_field: 'Campo',
            resolve_grc_value: 'Valor GRC',
            resolve_cmp_value: 'CMP Valor',
            accept_cmp: 'Aceitar valor CMP',
            accept_cmp_desc: 'Atualize o GRC para corresponder ao CMP — o valor CMP se torna o novo valor do GRC.',
            keep_grc: 'Mantenha o valor GRC',
            keep_grc_desc: 'Encerre este conflito sem alterar o GRC — notifique o proprietário do CMP para alinhar.',
            resolve_applied: 'Conflito resolvido. Finalidade do GRC atualizada para corresponder a CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Este relatório de prontidão é consultivo. Não aprova, compila, materializa, apresenta nem envia o dossiê.',
            run_label: 'Execute o pré-voo',
            running: 'Executando pré-voo…',
            assemble_label: 'Pacote de montagem automática',
            assembling: 'Montagem…',
            verdict_label: 'Veredicto pré-voo',
            tokens_label: '≈ {count} tokens',
            remediation_label: 'Correção',
            no_gaps: 'Nenhuma lacuna pré-voo detectada. O arquivo está pronto para ser montado.',
            override_label: 'Substituir veredicto do bloco – a montagem terá carimbo de auditoria.',
            accept_label: 'Aceitar relatório',
            reject_label: 'Rejeitar relatório',
            assembled_label: 'Pacote de envio montado',
            package_id_label: 'ID do pacote',
            package_filename_label: 'Nome do arquivo',
            package_hash_label: 'Hash do pacote',
            evidence_count_label: '{count} itens de evidência agrupados',
            error_generic: 'Não foi possível executar o pré-voo ou montar o pacote. Tente novamente ou verifique o status do pedido.',
        },
        errors: {
            fetch: 'Falha ao load assessments. Por favor, tente novamente.',
            save: 'Falha ao save assessment. Por favor, tente novamente.',
            fetch_gap_report_failed: 'Falha ao fetch gap report. Por favor, tente novamente.',
            fetch_transfers_failed: 'Falha ao fetch transfers. Por favor, tente novamente.',
            save_transfer_failed: 'Falha ao save transfer. Por favor, tente novamente.',
            submit_review_failed: 'Falha ao submit for review. Por favor, tente novamente.',
            process_decision_failed: 'Falha ao process decision. Por favor, tente novamente.',
            deprecate_failed: 'Falha ao deprecate. Por favor, tente novamente.',
            evaluate_risk_failed: 'Falha ao evaluate risk. Por favor, tente novamente.',
            fetch_safeguards_failed: 'Falha ao fetch safeguards. Por favor, tente novamente.',
            save_safeguard_failed: 'Falha ao save safeguard. Por favor, tente novamente.',
            update_safeguard_status_failed: 'Falha ao atualizar o estado da salvaguarda. Por favor, tente novamente.',
        },
        nav: {
            dpias: 'AIPDs',
            transfers: 'Avaliações de Transferência',
            dashboard: 'Painel de Controlo',
            gap_report: 'Relatório de Lacunas',
        },
        risk_level_critical: 'Risco crítico',
        risk_level_high: 'Risco alto',
        risk_level_medium: 'Risco médio',
        risk_level_low: 'Risco baixo',
        severity_critical: 'Severidade crítica',
        severity_high: 'Severidade alta',
        severity_medium: 'Severidade média',
        severity_low: 'Severidade baixa',
        overdue: 'Em atraso',
        framework_filter_label: 'Estrutura',
        aria_filter_by_framework: 'Filtrar por estrutura',
        dashboard: {
            title: 'Painel de Avaliações',
            page_title: 'Painel de avaliações',
            announce_loaded: 'Painel de avaliações carregado',
            announce_chart_view: 'A mostrar vista de gráfico',
            announce_table_view: 'A mostrar vista de tabela de dados',
            aria_metrics_table: 'Métricas do painel de avaliações',
            metric_col: 'Métrica',
            value_col: 'Valor',
            dpia_prefix: 'AIPD',
            transfer_prefix: 'Transferência',
            risk_prefix: 'Risco',
            aria_dpias_by_status: 'AIPD por estado',
            aria_transfers_by_status: 'Transferências por estado',
            aria_top_risks: 'Principais riscos',
            aria_top_risks_table: 'Principais riscos nas AIPD',
            overdue_dpias_attention: '{count} avaliações AIPD em atraso requerem atenção',
            no_overdue_dpias: 'Sem avaliações AIPD em atraso',
            overdue_transfers_attention: '{count} avaliações de transferência em atraso requerem atenção',
            no_overdue_transfers: 'Sem avaliações de transferência em atraso',
            dpias_overview: 'Resumo das AIPDs',
            transfers_overview: 'Resumo das Avaliações de Transferência',
            overdue_items: 'Itens em Atraso',
            overdue_dpias: 'AIPDs em Atraso',
            overdue_transfers: 'Transferências em Atraso',
            count: 'Contagem',
            no_data: 'Sem dados disponíveis.',
            no_risks: 'Nenhum risks encontrado',
            no_dashboard_data: 'Não há dados do painel disponíveis.',
            fetch_error: 'Falha ao fetch dashboard data. Por favor, tente novamente.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'AIPDs',
            announce_submitted_for_review: 'AIPD submetida para revisão',
            announce_deprecated: 'AIPD obsoleta',
            announce_risks_loaded: '{count} risco(s) carregado(s)',
            announce_mitigations_loaded: '{count} medida(s) de mitigação carregada(s)',
            announce_risk_deleted: 'Risco eliminado',
            announce_mitigation_status_updated: 'Estado da medida de mitigação atualizado',
            aria_filter: 'Filtrar AIPDs',
            aria_filter_by_status: 'Filtrar por estado',
            aria_filter_by_lead: 'Filtrar por líder de avaliação',
            aria_table: 'Avaliações de impacto na proteção de dados',
            announce_found: '{count} AIPD(s) encontrada(s)',
            announce_decision: 'AIPD {decision}',
            mitigation_start: 'Iniciar',
            mitigation_mark_implemented: 'Marcar como implementado',
            mitigation_verify: 'Verificar',
            title: 'Avaliações de Impacto sobre a Proteção de Dados',
            new: 'Nova AIPD',
            edit: 'Editar AIPD',
            dpia_title: 'Título',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Atividade',
            risk_score: 'Pontuação de Risco',
            risks: 'Riscos',
            mitigations: 'Mitigações',
            add_risk: 'Adicionar Risco',
            add_mitigation: 'Adicionar Mitigação',
            necessity: 'Necessidade / Proporcionalidade',
            likelihood: 'Probabilidade',
            impact: 'Impacto',
            severity_label: 'Gravidade',
            purpose: 'Finalidade',
            consultation_notes: 'Notas de consulta',
            dpo_opinion: 'Parecer do EPD',
            processing_activity_ids: 'IDs de atividades de tratamento (separados por vírgulas)',
            system_ids: 'IDs de sistema (separados por vírgulas)',
            vendor_ids: 'IDs de fornecedores (separados por vírgulas)',
            data_types: 'Tipos de dados (separados por vírgulas)',
            data_types_short: 'Tipos de dados',
            data_subjects: 'Titulares de dados (separados por vírgulas)',
            assessment_lead_id: 'ID do responsável da avaliação',
            assessment_lead: 'Responsável da avaliação',
            dpo_id: 'ID EPD',
            reviewer_ids: 'IDs de revisores (separados por vírgulas)',
            approval_mode: 'Modo de aprovação',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Exigir aprovação do EPD',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Rever Data',
            submit_for_review: 'Submeter para Rever',
            approve_reject: 'Aprovar/Rejeitar',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Tornar obsoleto',
            deprecated: 'Obsoleto',
            hide_risks: 'Ocultar Riscos',
            hide_mitigations: 'Ocultar mitigações',
            comments: 'Comentários',
            submit_decision: 'Submeter decisão',
            category_label: 'Categoria',
            category_data_breach: 'Dados Violação',
            category_unauthorized_access: 'Acesso não autorizado',
            category_function_creep: 'Expansão de funções',
            category_re_identification: 'Re-identificação',
            category_discrimination: 'Discriminação',
            category_surveillance: 'Vigilância',
            category_other: 'Outro',
            affected_rights: 'Direitos afetados (separados por vírgulas)',
            no_risks: 'Nenhum risks encontrado',
            no_mitigations: 'Nenhum mitigations encontrado',
            no_dpias: 'Nenhum dpias encontrado',
            mitigation_type: 'Tipo',
            mitigation_technical: 'Técnico',
            mitigation_organizational: 'Organizacional',
            mitigation_contractual: 'Contratual',
            risk_ids: 'IDs de riscos (separados por vírgulas)',
            responsible_party: 'Parte responsável',
            responsible_party_placeholder: 'Ou introduzir nome manualmente',
            affected_rights_placeholder: 'Os direitos selecionados aparecem aqui',
            affected_rights_freetext_placeholder: 'Introduzir direitos afetados (separados por vírgulas)',
            responsible: 'Responsável',
            target_date: 'Data alvo',
            created: 'Criado',
            fetch_error: 'Falha ao fetch dpias. Por favor, tente novamente.',
            save_error: 'Falha ao save dpia. Por favor, tente novamente.',
            submit_review_error: 'Falha ao submit dpia for review. Por favor, tente novamente.',
            decision_error: 'Falha ao process decision. Por favor, tente novamente.',
            deprecate_error: 'Falha ao deprecate dpia. Por favor, tente novamente.',
            fetch_risks_error: 'Falha ao fetch risks. Por favor, tente novamente.',
            save_risk_error: 'Falha ao save risk. Por favor, tente novamente.',
            delete_risk_error: 'Falha ao delete risk. Por favor, tente novamente.',
            fetch_mitigations_error: 'Falha ao fetch mitigations. Por favor, tente novamente.',
            save_mitigation_error: 'Falha ao save mitigation. Por favor, tente novamente.',
            update_mitigation_status_error: 'Falha ao atualizar o estado da mitigação. Por favor, tente novamente.',
            link_form_template: 'Modelo de Formulário Regulatório de Link',
            link_form_template_placeholder: 'Selecione um modelo de formulário DPIA (opcional)',
            link_form_template_none: 'Nenhum',
            linked_form: 'Formulário vinculado',
            linked_form_creating: 'Criando formulário vinculado...',
            linked_form_error: 'Falha ao criar instância de formulário vinculado. O DPIA foi criado sem um formulário vinculado.',
            view_linked_form: 'Ver formulário',
            announce_created_with_form: 'DPIA criado com instância de formulário vinculada',
            creation_mode: 'DPIA Modo de criação',
            mode_standard: 'Padrão DPIA',
            mode_template: 'Use modelo de formulário regulatório',
            select_form_template: 'Selecione o modelo de formulário',
            create_and_fill_form: 'Criar e preencher formulário',
            template_sections_title: 'Seções de modelo',
            field_singular: 'campo',
            field_plural: 'campos',
            no_sections: 'Nenhuma seção encontrada neste modelo.',
            template_mode_hint: 'Após a criação, você será redirecionado para preencher o formulário regulatório completo.',
            linked_dossier_filings: 'Submissões de dossiê associadas',
            no_linked_dossier_filings: 'Nenhuma submissão de dossiê associada a esta AIPD.',
            view_dossier_filing: 'Ver submissão',
            hide_dossier_filings: 'Ocultar submissões de dossiê',
            supporting_evidence: 'Evidências de apoio',
            hide_supporting_evidence: 'Ocultar evidências',
            no_supporting_evidence: 'Nenhuma evidência de apoio anexada.',
            upload_supporting_evidence: 'Carregar evidências de apoio',
            view_form_responses: 'Ver respostas do formulário',
            regulatory_framework: 'Quadro regulamentar',
            all_frameworks: 'Todos os quadros',
            no_templates_for_market: 'Nenhum modelo disponível para o mercado selecionado. Selecione um mercado diferente ou crie um modelo.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Transferências de dados',
            announce_found: '{count} avaliação(ões) de transferência encontrada(s)',
            announce_submitted_for_review: 'Transferência submetida para revisão',
            announce_deprecated: 'Transferência obsoleta',
            announce_decision: 'Transferência {decision}',
            announce_risk_eval_completed: 'Avaliação de riscos concluída',
            announce_safeguard_status_updated: 'Estado da medida de proteção atualizado',
            announce_safeguards_loaded: '{count} medida(s) de proteção carregada(s)',
            aria_filter: 'Filtrar avaliações de transferência',
            aria_filter_by_status: 'Filtrar por estado',
            aria_filter_by_mechanism: 'Filtrar por mecanismo de transferência',
            aria_table: 'Avaliações de transferência de dados',
            title: 'Avaliações de Transferências Transfronteiriças',
            new: 'Nova Transferência',
            edit: 'Editar Transferência',
            name: 'Nome',
            from_country: 'País de Origem',
            to_country: 'País de Destino',
            safeguards: 'Garantias',
            risk_level: 'Nível de Risco',
            assessment_notes: 'Notas da Avaliação',
            mechanism: 'Mecanismo',
            transfer_mechanism: 'Mecanismo de transferência',
            transfer_mechanism_detail: 'Detalhe do mecanismo de transferência',
            all_mechanisms: 'Todos os mecanismos',
            mechanism_other: 'Outro',
            data_types: 'Tipos de dados (separados por vírgulas)',
            data_subjects: 'Titulares de dados (separados por vírgulas)',
            purpose: 'Finalidade',
            recipient_name: 'Nome do destinatário',
            create_success: 'Avaliação de transferência criada.',
            update_success: 'Avaliação de transferência atualizada.',
            recipient_details: 'Detalhes do destinatário',
            recipient_details_placeholder: 'Função, localização e sistemas do destinatário e detalhes de transferência subsequente',
            vendor_id: 'Fornecedor ID',
            processing_activity_ids: 'IDs de atividades de tratamento (separados por vírgulas)',
            reviewer_ids: 'IDs de revisores (separados por vírgulas)',
            approval_mode: 'Modo de aprovação',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Exigir aprovação do EPD',
            review_date: 'Rever Data',
            created: 'Criado',
            deprecated: 'Obsoleto',
            submit_for_review: 'Submeter para Rever',
            approve_reject: 'Aprovar/Rejeitar',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Tornar obsoleto',
            risk_eval: 'Avaliação de risco',
            hide_safeguards: 'Ocultar salvaguardas',
            mechanism_proof: 'Prova de Mecanismo',
            hide_mechanism_proof: 'Ocultar prova de mecanismo',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Nenhuma prova de mecanismo anexada.',
            upload_mechanism_proof: 'Prova do mecanismo de upload',
            comments: 'Comentários',
            submit_decision: 'Submeter decisão',
            data_sensitivity: 'Sensibilidade dos dados (1-5)',
            country_risk: 'País Risco (1-5)',
            safeguard_adequacy: 'Adequação da salvaguarda (1-5)',
            volume_factor: 'Fator de volume (1-5)',
            evaluate: 'Avaliar',
            add_safeguard: 'Adicionar Safeguard',
            responsible_party: 'Parte responsável',
            responsible_party_placeholder: 'Ou introduzir nome manualmente',
            responsible: 'Responsável',
            implementation_date: 'Data de implementação',
            implementation: 'Implementação',
            expiry_date: 'Data de expiração',
            expiry: 'Expiração',
            expire: 'Expirar',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Nenhum safeguards encontrado',
            no_transfers: 'Nenhum transfer assessments encontrado',
            fetch_error: 'Falha ao fetch transfer assessments. Por favor, tente novamente.',
            save_error: 'Falha ao save transferência avaliação. Por favor, tente novamente.',
            submit_review_error: 'Falha ao submit for review. Por favor, tente novamente.',
            decision_error: 'Falha ao process decision. Por favor, tente novamente.',
            deprecate_error: 'Falha ao deprecate transfer. Por favor, tente novamente.',
            risk_eval_error: 'Falha ao evaluate risk. Por favor, tente novamente.',
            fetch_safeguards_error: 'Falha ao fetch safeguards. Por favor, tente novamente.',
            save_safeguard_error: 'Falha ao save safeguard. Por favor, tente novamente.',
            update_safeguard_status_error: 'Falha ao atualizar o estado da salvaguarda. Por favor, tente novamente.',
            safeguard_type_encryption_in_transit: 'Encriptação em trânsito',
            safeguard_type_encryption_at_rest: 'Encriptação em repouso',
            safeguard_type_access_controls: 'Acesso Controlos',
            safeguard_type_contractual_clauses: 'Cláusulas contratuais',
            safeguard_type_data_minimization: 'Minimização de dados',
            safeguard_type_pseudonymization: 'Pseudonimização',
            safeguard_type_audit_rights: 'Direitos de auditoria',
            safeguard_type_breach_notification: 'Violação Notificação',
            safeguard_type_data_localization: 'Localização de dados',
            safeguard_type_other: 'Outro',
            activate: 'Ativar',
            all_statuses: 'Todos os status',
            any: 'Any',
            mechanism_detail: 'Detalhe do mecanismo',
            safeguard_types: {
                encryption_in_transit: 'Criptografia em trânsito',
                encryption_at_rest: 'Criptografia em repouso',
                access_controls: 'Controles de acesso',
                contractual_clauses: 'Cláusulas contratuais',
                data_minimization: 'Minimização de dados',
                pseudonymization: 'Pseudonimização',
                audit_rights: 'Direitos de auditoria',
                breach_notification: 'Notificação de violação',
                data_localization: 'Localização de dados',
                other: 'Outro',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Relatório de lacunas de avaliações',
            announce_loaded: 'Relatório de lacunas carregado. {count} lacuna(s) identificada(s)',
            aria_activities_without_dpia: 'Atividades sem avaliação AIPD',
            aria_activities_unclassified: 'Atividades com requisito de AIPD não classificado',
            activities_unclassified_section_title: 'Atividades com requisito de AIPD não classificado',
            activities_unclassified_description: 'Estas atividades não foram revistas para determinar se uma AIPD é legalmente exigida. Classifique cada uma para eliminar o aviso.',
            activities_unclassified_empty: 'Todas as atividades ativas estão classificadas.',
            aria_unmitigated_risks: 'Riscos altos e críticos não mitigados',
            aria_transfers_without_safeguards: 'Transferências sem medidas de proteção adequadas',
            aria_expired_assessments: 'Avaliações expiradas que requerem renovação',
            title: 'Relatório de Lacunas de Avaliação',
            no_dpia: 'Atividades Sem AIPD',
            no_assessment: 'Transferências Sem Avaliação',
            unmitigated: 'AIPDs com Riscos Não Mitigados',
            activity_id: 'ID de atividade',
            dpia_id: 'ID AIPD',
            assessment_id: 'Avaliação ID',
            expired_assessments: 'Expirado Avaliações',
            no_gaps_activities: 'Sem lacunas encontradas. Todas as atividades têm DPIAs.',
            no_unmitigated_risks: 'Nenhum unmitigated high/critical risks encontrado',
            no_gaps_safeguards: 'Sem lacunas encontradas. Todas as transferências têm salvaguardas adequadas.',
            no_expired: 'Nenhum expired assessments encontrado',
            no_report_data: 'Não há dados do relatório de lacunas disponíveis.',
            fetch_error: 'Falha ao fetch gap report. Por favor, tente novamente.',
            no_expired_assessments: 'Sem avaliações expiradas.',
            no_gaps_dpia: 'Nenhum dpia gaps encontrado',
            no_data: 'Sem dados disponíveis.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Detalhe da apresentação do dossiê',
            page_title_list: 'Casos de apresentação do dossiê',
            announce_filings_found: '{count} apresentação(ões) encontrada(s)',
            announce_results: '{count} resultados',
            aria_filter_by_status: 'Filtrar por estado',
            title: 'Casos de arquivamento de dossiês',
            nav_label: 'Arquivamentos de Dossiês',
            create: 'Criar arquivamento de dossiê',
            form_type: 'Tipo de formulário',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referência',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Data de envio',
            outcome: 'Resultado',
            parent_filing: 'Pai',
            no_filings: 'Nenhum arquivo de dossiê encontrado.',
            fetch_error: 'Falha ao buscar arquivamentos de dossiês. Por favor, tente novamente.',
            save_error: 'Falha ao criar arquivamento de dossiê. Por favor, tente novamente.',
            created: 'Arquivamento de dossiê criado.',
            loaded: 'Arquivo de dossiê carregado.',
            not_found: 'Arquivamento de dossiê não encontrado.',
            back_to_list: 'Voltar à lista',
            section_assignments: 'Atribuições de seção',
            assign_sections: 'Atribuir seções',
            section_id: 'Seção',
            department: 'Departamento',
            assignee: 'Cessionário',
            deadline: 'Prazo',
            no_assignments: 'Ainda não há atribuições de seção.',
            sections_assigned_msg: 'Seções atribuídas.',
            assign_error: 'Falha ao atribuir seções. Por favor, tente novamente.',
            lifecycle: 'Ciclo de vida de arquivamento',
            submission: 'Detalhes do envio',
            submission_channel: 'Canal de envio',
            confirmation_number: 'Número de confirmação',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Enviar para revisão',
            submitted_for_review: 'Enviado para revisão interna.',
            submit_review_error: 'Falha ao enviar para análise. Por favor, tente novamente.',
            legal_signoff: 'Aprovação Legal',
            legal_signoff_done: 'Aprovação legal concluída.',
            legal_signoff_error: 'Falha ao concluir a aprovação legal. Por favor, tente novamente.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Falha ao aprovar o arquivamento. Por favor, tente novamente.',
            materialisation_status_column: 'Artefato de envio',
            materialisation_na: 'Não aplicável',
            materialisation_pending: 'Pendente',
            materialisation_success: 'Materializado',
            materialisation_failed: 'Falha',
            materialisation_banner_title: 'Não foi possível criar o artefato de envio',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Tentar novamente',
            materialisation_retry_count: 'Novas tentativas anteriores:',
            materialisation_retry_success: 'Artefato de envio materializado com sucesso.',
            materialisation_retry_announce: 'Nova tentativa bem-sucedida',
            filter_materialisation_failed: 'Mostrar apenas falhas de materialização',
            column_title: 'Título',
            column_form_type: 'Formulário',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Solicitação de Suplemento',
            supplement_requests: 'Solicitações de suplementos',
            supplement_response: 'Resposta suplementar',
            record_supplement: 'Solicitação de suplemento de registro',
            supplement_recorded: 'Solicitação de suplemento registrada.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Falha ao registrar solicitação de suplemento. Por favor, tente novamente.',
            submit_supplement: 'Enviar resposta complementar',
            supplement_submitted_msg: 'Resposta complementar enviada.',
            supplement_submit_error: 'Falha ao enviar resposta complementar. Por favor, tente novamente.',
            response_status: 'Status da resposta',
            response_notes: 'Notas de resposta',
            resubmit: 'Reenviar',
            resubmitted_msg: 'Dossiê reenviado.',
            resubmit_error: 'Falha ao reenviar o dossiê. Por favor, tente novamente.',
            outcome_error: 'Falha ao registrar o resultado. Por favor, tente novamente.',
            accepted: 'Aceite',
            rejected: 'Rejeitado',
            close: 'Fechar',
            closed_msg: 'Arquivamento encerrado.',
            close_error: 'Falha ao fechar o arquivamento. Por favor, tente novamente.',
            change_filing: 'Criar arquivo de alterações',
            change_filing_created: 'Arquivo de alterações criado.',
            change_filing_error: 'Falha ao criar arquivo de alterações. Por favor, tente novamente.',
            change_reason: 'Motivo da mudança',
            filing_lineage: 'Linhagem de arquivamento',
            timers: 'Temporizadores ativos',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Prazo do Suplemento',
            timer_resubmission_review: 'Prazo para reenvio',
            timer_department_input: 'Prazo do Departamento',
            days_remaining: 'dias restantes',
            days_overdue: 'dias em atraso',
            cover_letter: 'Carta de apresentação',
            checklist: 'Lista de verificação',
            evidence_index: 'Índice de evidências',
            status_draft: 'Rascunho',
            status_internal_review: 'Revisão interna',
            status_legal_signoff: 'Aprovação Legal',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Suplemento solicitado',
            status_supplement_submitted: 'Suplemento enviado',
            status_resubmitted: 'Resubmetido',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Encerrado',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Rascunho',
            step_internal_review: 'Revisão interna',
            step_legal_signoff: 'Aprovação Legal',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Suplemento solicitado',
            step_supplement_submitted: 'Suplemento enviado',
            step_resubmitted: 'Resubmetido',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Encerrado',
            linked_dpia: 'AIPD associada',
            linked_dpia_none: 'Nenhuma AIPD associada a esta submissão.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Ver AIPD',
            linked_dpia_label: 'Esta submissão de dossiê está associada à seguinte AIPD:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Integridade do artefato de envio',
            page_title: 'Integridade do artefato de envio',
            page_description: 'Detecte e corrija problemas de materialização de dossiês. As listas têm como escopo sua organização.',
            failed_title: 'Materializações falhadas',
            failed_empty: 'Nenhuma materialização falhada – bom.',
            pending_title: 'Aprovado mas não materializado',
            pending_empty: 'Todos os registros aprovados possuem um dossiê de apoio.',
            orphan_title: 'Dossiês órfãos',
            orphan_description: 'Dossiês criados por materialização que não são mais referenciados por nenhum arquivamento – normalmente o perdedor de uma corrida simultânea. Listado para investigação; a limpeza é manual.',
            orphan_empty: 'Nenhum dossiê órfão detectado.',
            materialise_button: 'Materialize agora',
            column_failure_reason: 'Motivo da falha',
            column_failed_at: 'Falha em',
            column_retries: 'Novas tentativas',
            column_approved_at: 'Aprovado em',
            column_dossier_id: 'ID do dossiê',
            column_source_filing_id: 'Arquivamento de origem',
            column_dossier_type: 'Tipo',
            column_created_at: 'Criado em',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Acesso negado',
        access_denied: 'Você não tem as permissões necessárias para acessar o módulo Classificação. Entre em contato com seu administrador.',
        nav: {
            taxonomies: 'Taxonomias',
            labeling: 'Etiquetagem',
            lifecycle: 'Ciclo de Vida',
            gaps: 'Relatório de Lacunas',
            exports: 'Exportações',
        },
        exports: {
            title: 'Exportações de classificação',
            csv_section: 'Exportações CSV',
            pdf_section: 'Relatórios PDF',
            inventory: 'Inventário',
            classification_register: 'Registo de classificação',
            raci: 'Matriz RACI',
            exceptions: 'Exceções',
            evidence_pack: 'Pacote de evidências',
            delta_report: 'Relatório delta',
            executive_summary: 'Resumo executivo',
            exporting: 'A exportar…',
            export_success: 'Exportação concluída com sucesso.',
            export_error: 'A exportação falhou. Por favor, tente novamente.',
            csv_download: 'Download CSV pronto.',
            pdf_download: 'Geração de PDF iniciada.',
            no_data: 'Sem dados disponíveis para exportar.',
        },
        delta: {
            title: 'Detalhe das alterações',
            added: 'Adicionados',
            removed: 'Removidos',
            changed: 'Alterados',
            added_count: 'Adicionados ({count})',
            removed_count: 'Removidos ({count})',
            changed_count: 'Alterados ({count})',
            object_id: 'ID do objeto',
            object_type: 'Tipo de objeto',
            taxonomy: 'Taxonomia',
            level: 'Nível',
            previous: 'Anterior',
            current: 'Atual',
            no_records: 'Sem registos nesta secção.',
            empty_section: 'Esta secção está vazia.',
        },
        schedule: {
            title: 'Agendar ciclo de descoberta',
            enabled: 'Ativado',
            interval_days: 'Intervalo (dias)',
            interval_label: 'Executar a cada N dias',
            manual_only: 'Apenas manual',
            every_n_days: 'A cada {n} dias',
            save: 'Guardar agendamento',
            cancel: 'Cancelar',
            schedule_updated: 'Agendamento atualizado com sucesso.',
        },
        taxonomies: {
            title: 'Taxonomias de Classificação',
            new: 'Nova Taxonomia',
            edit: 'Editar Taxonomia',
            name: 'Nome',
            levels: 'Níveis de Classificação',
            seed_defaults: 'Predefinições iniciais',
            scope: 'Âmbito',
            scope_organization: 'Organização',
            scope_module_specific: 'Específico do módulo',
            deprecate: 'Tornar obsoleto',
            hide_levels: 'Ocultar níveis',
            versions: 'Versões',
            hide_versions: 'Ocultar versões',
            add_level: 'Adicionar nível',
            new_level: 'Novo nível',
            edit_level: 'Editar Nível',
            code: 'Código',
            label: 'Rótulo',
            color: 'Cor',
            ordinal: 'Ordinal',
            handling_requirements: 'Requisitos de tratamento',
            version_history: 'Versão Histórico',
            changes: 'Alterações',
            failed_fetch: 'Falha ao fetch taxonomies. Por favor, tente novamente.',
            failed_save: 'Falha ao save taxonomy. Por favor, tente novamente.',
            failed_activate: 'Falha ao activate taxonomy. Por favor, tente novamente.',
            failed_deprecate: 'Falha ao deprecate taxonomy. Por favor, tente novamente.',
            failed_seed: 'Falha ao seed default taxonomies. Por favor, tente novamente.',
            failed_fetch_levels: 'Falha ao fetch taxonomy levels. Por favor, tente novamente.',
            failed_save_level: 'Falha ao save taxonomy level. Por favor, tente novamente.',
            failed_delete_level: 'Falha ao delete taxonomy level. Por favor, tente novamente.',
            failed_fetch_versions: 'Falha ao fetch taxonomy versions. Por favor, tente novamente.',
            loaded_count: '{count} taxonomias carregadas',
            defaults_seeded: 'Taxonomias predefinidas inicializadas.',
            no_levels: 'Nenhum nível de classificação definido — adicione níveis para definir sua hierarquia de classificação.',
            no_versions: 'Ainda sem histórico de versões — versões são criadas quando uma taxonomia ativa é atualizada.',
        },
        labeling: {
            title: 'Etiquetagem de Dados',
            new: 'Nova Etiqueta',
            edit: 'Editar Etiqueta',
            entity_type: 'Tipo de Entidade',
            entity_id: 'ID da Entidade',
            taxonomy: 'Taxonomia',
            classification_level: 'Nível de Classificação',
            applied_by: 'Aplicado Por',
            applied_at: 'Aplicado Em',
            bulk_apply: 'Aplicação em Massa',
            bulk_remove: 'Remoção em Massa',
            all_taxonomies: 'Todas as taxonomias',
            system: 'Sistema',
            dataset: 'Conjunto de dados',
            level_code_placeholder: 'Código de nível...',
            select_taxonomy: 'Selecione taxonomy...',
            select_level: 'Selecione level...',
            rationale: 'Fundamentação',
            review_date: 'Rever Data',
            review: 'Rever',
            selected: 'selecionado',
            select_all_labels: 'Selecionar todos os rótulos',
            loaded_count: '{count} rótulos carregados',
            hide_stale_labels: 'Ocultar etiquetas obsoletas',
            show_conflicts: 'Mostrar conflitos',
            hide_conflicts: 'Ocultar conflitos',
            label_conflicts: 'Conflitos de etiquetas',
            conflict_reason: 'Motivo do conflito',
            labels: 'Rótulos',
            failed_fetch: 'Falha ao fetch labels. Por favor, tente novamente.',
            failed_fetch_taxonomies: 'Falha ao fetch taxonomies. Por favor, tente novamente.',
            failed_fetch_levels: 'Falha ao fetch taxonomy levels. Por favor, tente novamente.',
            failed_save: 'Falha ao save label. Por favor, tente novamente.',
            failed_review: 'Falha ao review label. Por favor, tente novamente.',
            failed_bulk: 'Falha ao bulk label. Por favor, tente novamente.',
            failed_stale: 'Falha ao fetch stale labels. Por favor, tente novamente.',
            failed_conflicts: 'Falha ao fetch label conflicts. Por favor, tente novamente.',
            review_permission_denied: 'Você não tem permissão para revisar este rótulo.',
            review_not_found: 'Rótulo não encontrado — pode ter sido excluído.',
            approve_selected: 'Aprovar selecionados',
            reject_selected: 'Rejeitar selecionados',
            approved: 'Aprovado',
            rejected: 'Rejeitado',
            labels_count: 'rótulos',
            failed_count: 'falharam',
            confirm_approve: 'Confirmar aprovação',
            confirm_reject: 'Confirmar rejeição',
            approve_n_labels: 'Aprovar {count} rótulos?',
            reject_n_labels: 'Rejeitar {count} rótulos?',
            submit_for_review: 'Enviar para revisão',
            approve: 'Aprovar',
            reject: 'Rejeitar',
            parent_system: 'Sistema pai',
            parent_system_placeholder: 'Selecionar sistema pai...',
            parent_system_help: 'Usado para deteccao de conflitos.',
            dataset_level: 'Nivel do conjunto de dados',
            parent_level: 'Nivel pai',
            bulk_created: '{created} rotulo(s) criado(s)',
            bulk_skipped: '{skipped} ignorado(s)',
            bulk_all_skipped: 'Todos os {skipped} ignorados',
            ids_hint: 'Digite IDs separados por virgulas',
            delete_confirm: 'Excluir rotulo para "{id}"?',
        },
        lifecycle: {
            title: 'Monitorização do Ciclo de Vida dos Dados',
            new: 'Nova Entrada',
            current_stage: 'Fase Atual',
            last_transition: 'Última Transição',
            transition: 'Transição',
            view_history: 'Ver Histórico',
            stages: {
                creation: 'Criação',
                collection: 'Recolha',
                processing: 'Tratamento',
                storage: 'Armazenamento',
                retention: 'Retenção',
                disposal: 'Eliminação',
                archived: 'Arquivado',
                active: 'Ativo',
                pending_deletion: 'Pendente Eliminação',
                deleted: 'Eliminado',
                on_hold: 'Em espera',
            },
            all_stages: 'Todas as etapas',
            object_id: 'ID do objeto',
            object_type: 'Tipo de objeto',
            policy_reference: 'Referência de política',
            retention_period_days: 'Retenção Período (days)',
            retention_basis: 'Retenção Base',
            deletion_method: 'Método de eliminação',
            deletion_secure_erase: 'Apagamento seguro',
            deletion_anonymization: 'Anonimização',
            deletion_physical_destruction: 'Destruição física',
            deletion_other: 'Outro',
            responsible_party: 'Parte responsável',
            responsible_user_id: 'ID do utilizador responsável',
            retention_expiry: 'Expiração da retenção',
            hold_reason: 'Motivo da espera',
            hold: 'Em espera',
            release: 'Lançamento',
            hide_events: 'Ocultar Eventos',
            events_timeline: 'Eventos Cronologia',
            add_event: 'Adicionar evento',
            event_type: 'Evento Tipo',
            create_event: 'Adicionar evento',
            from_stage: 'Da etapa',
            to_stage: 'Para a etapa',
            actor: 'Ator',
            reason_placeholder: 'Motivo...',
            go: 'Go',
            show_expired_retentions: 'Mostrar Expirado Retentions',
            hide_expired_retentions: 'Ocultar Expirado Retentions',
            expired_retentions_alert: 'Expirado Retentions Alerta',
            failed_fetch: 'Falha ao fetch lifecycle records. Por favor, tente novamente.',
            failed_save: 'Falha ao save lifecycle record. Por favor, tente novamente.',
            failed_transition: 'Falha ao transition lifecycle stage. Por favor, tente novamente.',
            failed_hold: 'Falha ao place hold. Por favor, tente novamente.',
            failed_release: 'Falha ao release hold. Por favor, tente novamente.',
            failed_fetch_events: 'Falha ao fetch lifecycle events. Por favor, tente novamente.',
            failed_create_event: 'Falha ao create lifecycle event. Por favor, tente novamente.',
            failed_expired: 'Falha ao fetch expired retentions. Por favor, tente novamente.',
            hold_reason_prompt: 'Introduza hold reason:',
            loaded_count: '{count} registos de ciclo de vida carregados',
            stage_transition_for: 'Transição de fase para {id}',
            event_created: 'Criado',
            event_updated: 'Atualizado',
            event_approved: 'Aprovado',
            event_deprecated: 'Obsoleto',
            event_proposed: 'Proposto',
            event_reviewed: 'Revisado',
            event_archived: 'Arquivado',
            event_restored: 'Restaurado',
        },
        gap_report: {
            title: 'Relatório de Lacunas de Classificação',
            unlabeled: 'Dados Sem Etiqueta',
            missing_lifecycle: 'Sem Ciclo de Vida',
            stale_labels: 'Etiquetas Desatualizadas',
            run_detection: 'Executar deteção de lacunas',
            detecting: 'A detetar...',
            detection_complete: 'Deteção de lacunas concluída. {count} lacuna(s) detetada(s).',
            detection_failed: 'A deteção de lacunas falhou.',
            by_severity: 'Por Gravidade',
            by_gap_type: 'Por tipo de lacuna',
            all_gap_types: 'Todos os tipos de lacuna',
            conflicting_labels: 'Rótulos em conflito',
            expired_retention: 'Expirado Retenção',
            missing_handling: 'Tratamento de valores em falta',
            info: 'Informação',
            gap_type: 'Tipo de lacuna',
            object_id: 'ID do objeto',
            object_type: 'Tipo de objeto',
            detected_at: 'Detetado em',
            open: 'Abrir',
            acknowledged: 'Receção confirmada',
            remediated: 'Remediado',
            accepted: 'Aceite',
            acknowledge: 'Confirmar receção',
            remediate: 'Remediar',
            accept_risk: 'Aceitar risco',
            accept_risk_prompt: 'Introduza risk acceptance justification:',
            failed_fetch: 'Falha ao fetch gaps. Por favor, tente novamente.',
            failed_acknowledge: 'Falha ao acknowledge gap. Por favor, tente novamente.',
            failed_remediate: 'Falha ao remediate gap. Por favor, tente novamente.',
            failed_accept_risk: 'Falha ao accept gap risk. Por favor, tente novamente.',
            failed_detect: 'Falha ao run gap detection. Por favor, tente novamente.',
            loaded_count: '{count} lacunas de classificação carregadas',
            related_object_id: 'ID do objeto relacionado',
            related_object_type: 'Tipo do objeto relacionado',
            gap_types: {
                unlabeled_asset: 'Ativo nao rotulado',
                missing_lifecycle: 'Ciclo de vida ausente',
                expired_retention: 'Retencao expirada',
                extended_pending_deletion: 'Exclusao pendente estendida',
            },
        },
        label_workflow: {
            title: 'Fluxo de etiquetas',
            proposed: 'Proposta',
            under_review: 'Em revisão',
            approved: 'Aprovada',
            deprecated: 'Obsoleta',
            submit_review: 'Submeter para revisão',
            approve: 'Aprovar etiqueta',
            deprecate: 'Tornar obsoleta',
            bulk_approve: 'Aprovação em massa',
            bulk_approve_success: 'Etiquetas aprovadas com sucesso.',
            submit_success: 'Etiqueta submetida para revisão.',
            approve_success: 'Etiqueta aprovada.',
            deprecate_success: 'Etiqueta tornada obsoleta.',
        },
        review_tasks: {
            title: 'Tarefas de revisão',
            create: 'Nova tarefa de revisão',
            system: 'Sistema',
            owner: 'Proprietário',
            description: 'Descrição',
            resolution: 'Resolução',
            resolve: 'Resolver',
            approve: 'Aprovar',
            modify: 'Modificar',
            reject: 'Rejeitar',
            escalate: 'Escalar',
            escalated: 'Tarefas escaladas',
            no_tasks: 'Sem tarefas de revisão.',
            resolve_success: 'Tarefa de revisão resolvida.',
            status_pending: 'Pendente',
            status_resolved: 'Resolvida',
            status_escalated: 'Escalada',
            notes: 'Notas',
            create_success: 'Tarefa de revisão criada.',
            no_tasks_detail: 'Ainda sem tarefas de revisão — crie uma tarefa para atribuir revisões de rótulos aos proprietários de sistemas.',
            label_id: 'ID do rótulo',
            system_id: 'ID do sistema',
            system_name: 'Nome do sistema',
            owner_id: 'ID do proprietário',
            resolution_col: 'Resolucao',
            notes_col: 'Notas',
        },
        exceptions: {
            title: 'Fila de exceções',
            create: 'Nova exceção',
            severity: 'Gravidade',
            system: 'Sistema',
            owner: 'Proprietário',
            resolve: 'Resolver',
            no_exceptions: 'Nenhuma exceção encontrada.',
            resolve_success: 'Exceção resolvida.',
            dashboard_open: 'Exceções abertas',
            dashboard_resolved: 'Exceções resolvidas',
            dashboard_total: 'Total',
            dashboard_by_severity: 'Por gravidade',
            status_open: 'Aberta',
            status_resolved: 'Resolvida',
            status_accepted_risk: 'Risco aceite',
            create_success: 'Exceção criada.',
            no_exceptions_detail: 'Ainda sem exceções — exceções são criadas a partir de lacunas de alta severidade que requerem tratamento especial.',
            gap_id: 'ID da lacuna',
            system_id: 'ID do sistema',
            system_name: 'Nome do sistema',
            owner_id: 'ID do proprietário',
            resolution_notes: 'Notas de resolução',
            resolution_notes_placeholder: 'Descreva como esta exceção foi resolvida...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Mapa de dados empresarial',
            inventory: 'Inventário de dados',
            total_systems: 'Total de sistemas',
            total_labels: 'Total de etiquetas',
            approved_count: 'Etiquetas aprovadas',
            proposed_count: 'Etiquetas propostas',
            no_data: 'Sem dados de classificação disponíveis.',
            no_approved_labels: 'Ainda sem objetos rotulados — um objeto aparece no mapa de dados assim que tem um rótulo de classificação, seja proposto, em revisão ou aprovado.',
            loaded_count: 'Mapa de dados corporativos carregado',
        },
        discovery: {
            title: 'Ciclos de descoberta',
            create: 'Novo ciclo',
            cycle_name: 'Nome do ciclo',
            run_delta: 'Executar deteção delta',
            delta_success: 'Deteção delta concluída.',
            create_success: 'Ciclo de descoberta criado.',
            no_cycles: 'Sem ciclos de descoberta.',
            status_created: 'Criado',
            status_scanning: 'Digitalizando',
            status_review: 'Revisão',
            status_approved: 'Aprovado',
            status_published: 'Publicado',
            status_draft: 'Rascunho',
            running: 'Em execução',
            completed: 'Concluído',
            cancelled: 'Cancelado',
            delta_result_summary: 'Encontrados {added} adicionados, {removed} removidos, {changed} rótulos alterados.',
            delta_results: 'Resultados delta',
            view_results: 'Ver resultados',
            scope_type: 'Tipo de escopo',
            scope_type_all: 'Todos',
            scope_type_systems: 'Sistemas',
            scope_type_datasets: 'Conjuntos de dados',
            scope_ids: 'IDs de escopo',
            scope_ids_placeholder: 'Insira os IDs separados por vírgulas',
            cycle_explanation: 'Um ciclo de descoberta verifica sua infraestrutura de dados para identificar, classificar e rastrear ativos de dados. Configure o escopo para direcionar sistemas ou conjuntos de dados específicos.',
            empty_state_title: 'Ainda não há ciclos de descoberta',
            empty_state_description: 'Crie seu primeiro ciclo de descoberta para começar a digitalizar e classificar seus ativos de dados.',
            empty_state_cta: 'Crie o primeiro ciclo',
            validation_name_required: 'O nome do ciclo é obrigatório',
            validation_scope_type_required: 'O tipo de escopo é obrigatório',
            validation_scope_ids_required: 'É necessário pelo menos um ID de escopo',
        },
        regulatory: {
            sensitivity_importance_info: 'A Lei de Dados (Lei 60/2024/QH15, req. 18e4bfd1) requer classificação por sensibilidade e importância como dimensões separadas. Crie uma taxonomia para cada dimensão para atender a esse requisito.',
            handling_rules_info: 'Cada nível de classificação deve definir regras de tratamento por necessidade. b89981ac ("Definir regras de tratamento por nível de classificação").',
            template_internal_ids_info: 'Todos os modelos de classificação usam IDs internos (por exemplo, vn_data_law_data_classification_record). Nenhum modelo é apresentado como formulário oficial do ministério.',
            ai_risk_tiers_info: 'A Lei de IA (Lei 134/2025/QH15, req. 3a87ad63) define quatro níveis de risco de sistemas de IA: mínimo, limitado, alto e inaceitável. Crie uma taxonomia de risco de IA com esses níveis.',
            ai_risk_tier_minimal: 'Minimo',
            ai_risk_tier_limited: 'Limitado',
            ai_risk_tier_high: 'Alto',
            ai_risk_tier_unacceptable: 'Inaceitavel',
            ai_register_info: 'Os registros de classificação AI devem ser vinculados a uma entrada de registro do sistema AI (req. 0f4475d3) capturando: nome do sistema, proprietário, finalidade, contexto de implantação, nível de risco, envolvimento do fornecedor.',
            ai_template_internal_info: 'Os modelos de classificação de IA usam IDs internos (por exemplo, AI-RISK-01). Nenhum ID de espaço reservado oficial de IA (AI-HC-01 até AI-HC-08) é usado.',
            ai_audit_trail_info: 'As alterações de classificação AI produzem uma trilha de auditoria versionada por UC-VN-06: ator, carimbo de data/hora, valor anterior, novo valor e justificativa.',
            lifecycle_data_d1_info: 'Mapa de transições do ciclo de vida para etapas do procedimento de lei de dados DATA-D1: catalogar, classificar, definir regras de tratamento, capturar evidências, agendar revisões.',
            lifecycle_deletion_link_info: 'Um ativo de dados classificado que atinge o fim da vida útil deve acionar um registro de agendamento de exclusão por meio do módulo Orquestração de exclusão.',
            lifecycle_evidence_info: 'Cada mudança de estado do ciclo de vida (rascunho, ativo, em revisão, arquivado, excluído) captura ator, carimbo de data/hora e justificativa como uma entrada de trilha de auditoria.',
            escalation_timers_info: 'UC-VN-10 prazos de escalonamento: 5 dias úteis para revisão do proprietário, 48 horas para escalonamento de exceção, 72 horas para itens sensíveis/de alto risco.',
            escalation_owner_review: '5 dias uteis',
            escalation_exception: '48 horas',
            escalation_high_risk: '72 horas',
            gap_risk_areas_info: 'Requerimento de Lei de Dados f6c3060a ("Avaliação de risco de dados") requer análise de lacunas em 4 áreas de risco: privacidade, segurança cibernética, controle de acesso e risco operacional.',
            gap_risk_area_privacy: 'Privacidade',
            gap_risk_area_cybersecurity: 'Ciberseguranca',
            gap_risk_area_access: 'Controlo de acesso',
            gap_risk_area_operational: 'Operacional',
            gap_framework_filter_info: 'Filtre as lacunas por estrutura regulatória (VN_DATA_LAW ou VN_AI_LAW_2026) para ver as lacunas relevantes para cada escopo regulatório.',
            framework_vn_data_law: 'Lei de Dados VN',
            framework_vn_ai_law: 'Lei de IA VN 2026',
            enterprise_map_approved_info: 'Este mapa de dados mostra apenas registros de classificação aprovados por UC-VN-10 etapa 10 do fluxo de trabalho ("Os rótulos aprovados tornam-se a verdade atual da empresa"). Os registros preliminares e propostos são excluídos.',
            exports_bundle_info: 'Os pacotes de exportação incluem: registro de classificação, regras de manuseio, propriedade/RACI e pacote de evidências — correspondendo aos requisitos de exportação UC-VN-10 e ao formato da folha de evidências DATA-EVD-01.',
        },
    },
    monitoring: {
        no_permission_title: 'Permissoes insuficientes',
        no_permission_message: 'Voce nao tem permissao para acessar este modulo de monitoramento.',
        errors: {
            fetch: 'Falha ao load. Por favor, tente novamente.',
            save: 'Falha ao save. Por favor, tente novamente.',
            fetch_programs_failed: 'Falha ao fetch programs. Por favor, tente novamente.',
            fetch_kpi_failed: 'Falha ao fetch kpi data. Por favor, tente novamente.',
            create_snapshot_failed: 'Falha ao create snapshot. Por favor, tente novamente.',
            fetch_periods_failed: 'Falha ao fetch periods. Por favor, tente novamente.',
            create_period_failed: 'Falha ao create period. Por favor, tente novamente.',
            auto_generate_failed: 'Falha ao auto-generate periods. Por favor, tente novamente.',
            start_period_failed: 'Falha ao start period. Por favor, tente novamente.',
            close_period_failed: 'Falha ao close period. Por favor, tente novamente.',
            save_program_failed: 'Falha ao save program. Por favor, tente novamente.',
            activate_program_failed: 'Falha ao activate program. Por favor, tente novamente.',
            pause_program_failed: 'Falha ao pause program. Por favor, tente novamente.',
            complete_program_failed: 'Falha ao complete program. Por favor, tente novamente.',
            archive_program_failed: 'Falha ao archive program. Por favor, tente novamente.',
            fetch_ric_templates_failed: 'Falha ao fetch ric templates. Por favor, tente novamente.',
            fetch_ric_cases_failed: 'Falha ao fetch ric cases. Por favor, tente novamente.',
            create_ric_case_failed: 'Falha ao create ric case. Por favor, tente novamente.',
            transition_ric_failed: 'Falha ao transition ric case. Por favor, tente novamente.',
            add_follow_up_failed: 'Falha ao add follow-up. Por favor, tente novamente.',
            respond_follow_up_failed: 'Falha ao respond to follow-up. Por favor, tente novamente.',
            link_evidence_failed: 'Falha ao link evidence. Por favor, tente novamente.',
            fetch_task_templates_failed: 'Falha ao fetch task templates. Por favor, tente novamente.',
            save_task_template_failed: 'Falha ao save task template. Por favor, tente novamente.',
            delete_task_template_failed: 'Falha ao delete task template. Por favor, tente novamente.',
            delete_framework_link_failed: 'Falha ao eliminar a ligação ao quadro regulamentar. Por favor, tente novamente.',
            seed_defaults_failed: 'Falha ao seed defaults. Por favor, tente novamente.',
            fetch_tasks_failed: 'Falha ao fetch tasks. Por favor, tente novamente.',
            create_task_failed: 'Falha ao create task. Por favor, tente novamente.',
            instantiate_tasks_failed: 'Falha ao instantiate tasks. Por favor, tente novamente.',
            start_task_failed: 'Falha ao start task. Por favor, tente novamente.',
            complete_task_failed: 'Falha ao complete task. Por favor, tente novamente.',
            defer_task_failed: 'Falha ao defer task. Por favor, tente novamente.',
            block_task_failed: 'Falha ao block task. Por favor, tente novamente.',
            override_complete_failed: 'Falha ao override complete. Por favor, tente novamente.',
            assign_task_failed: 'Falha ao assign task. Por favor, tente novamente.',
            update_checklist_failed: 'Falha ao update checklist. Por favor, tente novamente.',
        },
        nav: {
            programs: 'Programas',
            periods: 'Períodos',
            task_templates: 'Modelos de Tarefas',
            tasks: 'Tarefas',
            kpi: 'Painel de KPIs',
            ric: 'Inquéritos de Reguladores',
            framework_link: 'Monitorização de Quadro',
            authority_readiness: 'Prontidão Autoridade',
            change_review: 'Revisão de Alterações',
            raci: 'Matriz RACI',
        },
        programs: {
            page_title: 'Programas de monitorização',
            title: 'Programas de Monitorização',
            new: 'Novo Programa',
            edit: 'Editar Programa',
            name: 'Nome',
            scope: 'Âmbito',
            frequency: 'Frequência',
            owner: 'Responsável',
            activate: 'Ativar',
            pause: 'Pausar',
            resume: 'Retomar',
            complete: 'Concluir',
            archive: 'Arquivar',
            custom_frequency_days: 'Personalizado Frequência (days)',
            select_user: 'Selecionar utilizador…',
            assign_training: 'Atribuir formação',
            linked_forms: 'Formulários vinculados',
            linked_forms_none: 'Ainda não há formulários vinculados',
            loaded_count: '{0} programas carregados',
            scope_labels: {
                full: 'Completo',
                partial: 'Parcial',
                targeted: 'Direcionado',
                consent: 'Consentimento',
                dpia: 'DPIA',
                vendors: 'Fornecedores',
                classification: 'Classificação',
                rights_requests: 'Pedidos de direitos',
                data_mapping: 'Mapeamento de dados',
                transfers: 'Transferências',
                security_monitoring: 'Monitoramento de segurança',
                authority_response: 'Resposta da autoridade',
                evidence_preservation: 'Preservação de Evidências',
                localization_review: 'Revisão de localização',
                ai_system_registration: 'AI Registro do sistema',
                ai_pre_deployment: 'AI Avaliação pré-implantação',
                ai_transparency: 'AI Verificação de transparência',
                ai_incident_review: 'AI Revisão de incidente',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Períodos de monitoramento',
            title: 'Períodos de Revisão',
            new: 'Novo Período',
            label: 'Etiqueta do Período',
            start_date: 'Data de Início',
            end_date: 'Data de Fim',
            reviewers: 'Revisores',
            auto_generate: 'Gerar Períodos Automaticamente',
            start: 'Iniciar',
            close: 'Encerrar',
            change_digest: 'Resumo de Alterações',
            no_digest: 'Nenhum resumo disponível.',
            reviewers_count: 'Contagem de revisores',
            select_program: 'Selecione Program',
            select_program_prompt: 'Selecione program to view periods.',
            hide_digest: 'Ocultar resumo',
            loaded_count: 'Períodos {0} carregados',
            periods_auto_generated: 'Períodos gerados automaticamente',
            label_placeholder: 'ex. 2026-Q1',
        },
        task_templates: {
            page_title: 'Modelos de tarefas',
            title: 'Modelos de Tarefas',
            new: 'Novo Modelo',
            edit: 'Editar Modelo',
            category: 'Categoria',
            priority: 'Prioridade',
            checklist_items: 'Itens da Lista de Verificação',
            effort: 'Esforço Estimado (horas)',
            pre_seeded: 'Pré-carregado',
            seed_defaults: 'Carregar Predefinições',
            add_checklist: 'Adicionar Item à Lista de Verificação',
            guidance: 'Notas de Orientação',
            all_categories: 'Todas as categorias',
            loaded_count: 'Modelos de tarefa {0} carregados',
            defaults_seeded: 'Modelos de tarefas padrão propagados',
            checklist_item_label: 'Item da lista de verificação {0}',
            remove_checklist_item: 'Remover item da lista de verificação {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Tarefas de revisão',
            title: 'Tarefas de Revisão',
            new: 'Nova Tarefa',
            assigned_to: 'Atribuído A',
            due_date: 'Data Limite',
            checklist: 'Lista de Verificação',
            start: 'Iniciar',
            complete: 'Concluir',
            defer: 'Adiar',
            block: 'Bloquear',
            override_complete: 'Forçar Conclusão',
            link_evidence: 'Associar Evidência',
            direct_upload_label: 'Carregar ficheiro de evidência',
            instantiate: 'Instanciar a partir de Modelos',
            defer_reason: 'Motivo do adiamento',
            override_justification: 'Justificação para forçar conclusão',
            filter_assignee: 'Filtrar por atribuído',
            select_period: 'Selecione período',
            select_program: 'Selecione Program',
            select_program_period_prompt: 'Selecione program and period to view tasks.',
            assign_placeholder: 'Atribuir para...',
            loaded_count: '{0} tarefas carregadas',
            tasks_instantiated: 'Tarefas criadas a partir de modelos',
            overdue: 'Em atraso',
            checklist_for: 'lista de verificação para',
            manage_checklist: 'Gerir lista de verificação',
            add_checklist_item: 'Adicionar item',
            checklist_item_label: 'Item',
            checklist_item_description: 'Descrição',
            remove_checklist_item: 'Remover item',
            save_checklist: 'Guardar lista de verificação',
            no_checklist_items: 'Ainda não há itens.',
            confirm_delete_attachment: 'Tem a certeza de que pretende eliminar este ficheiro de evidência? Esta ação não pode ser anulada.',
        },
        kpi: {
            page_title: 'Painel KPI',
            title: 'Painel de KPIs',
            kpi_name: 'Nome do KPI',
            current_value: 'Valor Atual',
            target: 'Objetivo',
            trends: 'Tendências',
            capture_snapshot: 'Capturar instantâneo',
            select_program: 'Selecione Program',
            select_program_prompt: 'Selecione program to view KPIs.',
            snapshot: 'Instantâneo',
            kpi_labels: {
                on_track: 'Em Rastrear',
                at_risk: 'Em Risco',
                behind: 'Atrasado',
                rights_request_sla_rate: 'Taxa de solicitação de direitos SLA',
                vendor_review_currency: 'Moeda de avaliação do fornecedor',
                dpia_completion_rate: 'DPIA Taxa de conclusão',
                classification_coverage: 'Cobertura de Classificação',
                consent_policy_currency: 'Moeda da Política de Consentimento',
                security_event_detection_rate: 'Taxa de detecção de eventos de segurança',
                evidence_preservation_compliance_rate: 'Taxa de conformidade de preservação de evidências',
                authority_response_timeliness: 'Pontualidade da resposta da autoridade',
                localization_readiness_score: 'Pontuação de prontidão para localização',
                ai_register_completeness: 'AI Integridade do registro do sistema',
                pre_deployment_gate_pass_rate: 'Taxa de passagem do portão de pré-implantação',
                transparency_disclosure_coverage: 'Cobertura de divulgação de transparência',
                ai_incident_readiness_score: 'AI Pontuação de prontidão para incidentes',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'KPIs da legislação de segurança cibernética: taxa de detecção de eventos de segurança, taxa de conformidade de preservação de evidências, pontualidade de resposta da autoridade, pontuação de prontidão de localização.',
            ai_law_kpi_info: 'AI KPIs legais: AI Integridade do registro do sistema, taxa de aprovação do portão de pré-implantação, cobertura de divulgação de transparência, AI pontuação de prontidão para incidentes.',
            no_data: 'Sem dados disponíveis.',
            snapshot_date: 'Data de instantâneo',
            loaded_count: '{0} instantâneos KPI carregados',
            snapshot_captured: 'Instantâneo KPI capturado',
            showing_chart: 'A mostrar vista de gráfico',
            showing_table: 'A mostrar vista de tabela',
            aria_current_values: 'Valores atuais KPI',
            kpi_label: 'KPI',
            actual: 'Real',
            trend: 'Tendência',
            notes: 'Notas',
            trend_up: 'Em alta',
            trend_down: 'Em baixa',
            trend_stable: 'Estável',
        },
        ric: {
            page_title: 'Casos RIC',
            title: 'Casos de Inquérito de Reguladores',
            new: 'Novo Caso RIC',
            reference: 'Referência',
            regulator: 'Regulador',
            inquiry_type: 'Tipo de Inquérito',
            deadline: 'Prazo de Resposta',
            lead: 'Responsável',
            follow_ups: 'Seguimentos',
            add_follow_up: 'Adicionar Seguimento',
            respond: 'Responder',
            question: 'Questão',
            response: 'Resposta',
            asked_at: 'Pedido em',
            no_follow_ups: 'Sem acompanhamentos ainda.',
            received_date: 'Data de receção',
            responded_at: 'Respondido em',
            hide_follow_ups: 'Ocultar Follow-ups',
            inquiry_types: {
                general: 'Geral',
                investigation: 'Investigação',
                audit: 'Auditoria',
                complaint: 'Reclamação',
                general_audit: 'Auditoria geral',
                rights_requests_review: 'Revisão de pedidos de direitos',
                cross_border_transfer_review: 'Revisão de transferências transfronteiriças',
                vendor_governance_review: 'Revisão de governança de fornecedores',
                consent_management_review: 'Revisão de gestão de consentimento',
                data_mapping_ropa_review: 'Revisão de mapeamento de dados / ROPA',
                incident_readiness_review: 'Revisão de preparação para incidentes',
                security_access_control_review: 'Revisão de segurança e controlo de acesso',
                targeted_processing_review: 'Revisão de processamento dirigido',
                bank_readiness_pack: 'Pacote de preparação bancária',
            },
            link_evidence: 'Ligar Evidência',
            loaded_count: '{0} casos RIC carregados',
            to_follow_up: 'para pergunta de acompanhamento',
            overdue: 'Atrasado',
            overdue_indicator: 'Prazo de resposta excedido',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Recebido',
                in_triage: 'Em triagem',
                evidence_gathering: 'Recolha de evidências',
                response_drafting: 'Rascunho de resposta',
                internal_review: 'Revisão interna',
                submitted: 'Submetido',
                follow_up: 'Acompanhamento',
                closed: 'Fechado',
            },
        },
        framework_link: {
            title: 'Monitorização ligada ao quadro',
            link_program: 'Ligar programa ao quadro',
            framework_code: 'Código do quadro',
            select_framework: 'Selecionar um quadro...',
            select_program: 'Selecionar um programa...',
            readiness_score: 'Pontuação de prontidão',
            no_links: 'Nenhum programa ligado ao quadro encontrado',
            seed_defaults: 'Criar modelos predefinidos',
            program_id: 'ID do programa',
            program: 'Programa',
        },
        authority_readiness: {
            title: 'Prontidão para pedidos de autoridade',
            create_entry: 'Nova entrada de prontidão',
            select_framework: 'Selecionar um quadro...',
            framework_code: 'Quadro',
            readiness_status: 'Estado de prontidão',
            readiness_package: 'Pacote de prontidão',
            run_drill: 'Executar exercício de prontidão',
            drill_success: 'Exercício de prontidão registado',
            last_drill: 'Último exercício',
            never_drilled: 'Nunca',
            no_readiness: 'Nenhum dado de prontidão de autoridade encontrado',
            entry_created: 'Entrada de prontidão criada',
            ready: 'Pronto',
            partial: 'Parcial',
            not_ready: 'Não pronto',
            items: 'items',
        },
        change_review: {
            title: 'Revisão de alterações regulamentares',
            new_review: 'Nova revisão de alterações',
            change_id: 'ID de alteração',
            select_change: 'Selecionar uma alteração...',
            select_framework: 'Selecionar um quadro...',
            impact_assessment: 'Avaliação de impacto',
            decision: 'Decisão',
            no_action: 'Sem ação',
            internal_update: 'Atualização interna',
            authority_filing: 'Submissão à autoridade',
            no_reviews: 'Nenhuma revisão de alterações encontrada',
            auto_cycle: 'Ciclo de revisão automático',
            cadence: 'Frequência de revisão',
            framework: 'Estrutura',
        },
        export_csv: 'Exportar CSV',
        exporting: 'Exportando…',
        export_success: 'CSV exportado com sucesso.',
        export_failed: 'Falha na exportação de CSV.',
        export_attestation_pack: 'Pacote de Atestado de Exportação',
        staleness: {
            badge_label: '{0} itens obsoletos',
            days_since: '{0} dias desde a última atualização',
            detail_title: 'Itens obsoletos',
            accept_justification: 'Aceite com justificativa',
            justification_label: 'Justificativa',
            no_stale: 'Nenhum item obsoleto encontrado.',
            threshold_label: 'Limite de desatualização (dias)',
            threshold_min: 'Mínimo 30 dias',
            threshold_max: 'Máximo 730 dias',
            threshold_saved: 'Limite de inatividade atualizado.',
        },
        csv_columns: {
            name: 'Nome',
            status: 'Estado',
            owner: 'Proprietário',
            created_at: 'Criado em',
            updated_at: 'Atualizado em',
            scope: 'Escopo',
            frameworks: 'Estruturas',
            period_label: 'Etiqueta do período',
            start_date: 'Data de início',
            end_date: 'Data de término',
            task_count: 'Contagem de tarefas',
            completion_rate: 'Taxa de conclusão',
            title: 'Título',
            assigned_to: 'Atribuído a',
            due_date: 'Data de vencimento',
            priority: 'Prioridade',
            completed_at: 'Concluído em',
            kpi_code: 'KPI Código',
            kpi_label: 'KPI Etiqueta',
            target: 'Alvo',
            actual: 'Real',
            trend: 'Tendência',
            reference: 'Referência',
            regulator: 'Regulador',
            inquiry_type: 'Tipo de consulta',
            lead: 'Liderar',
            received_date: 'Data de Recebimento',
            deadline: 'Prazo',
            framework_code: 'Código Estrutural',
            readiness_status: 'Status de prontidão',
            package_count: 'Contagem de pacotes',
            change_id: 'Alterar ID',
            impact_assessment: 'Avaliação de Impacto',
            decision: 'Decisão',
            program: 'Programa',
            readiness_score: 'Pontuação de prontidão',
            category: 'Categoria',
            checklist_count: 'Contagem da lista de verificação',
            effort_hours: 'Esforço (horas)',
            version: 'Versão',
            department: 'Departamento',
            function_area: 'Área de Função',
            role: 'Função',
            assignee: 'Cessionário',
            frequency: 'Frequência',
        },
        raci: {
            page_title: 'Matriz RACI de Governança',
            title: 'Matriz RACI de Governança',
            department: 'Departamento',
            function_area: 'Área de Função',
            responsible: 'Responsável',
            accountable: 'Responsável',
            consulted: 'Consultado',
            informed: 'Informado',
            assign_role: 'Atribuir função RACI',
            select_program: 'Selecione o programa',
            select_program_prompt: 'Selecione um programa para visualizar a matriz RACI.',
            no_assignments: 'Nenhuma atribuição RACI encontrada.',
            role_updated: 'Atribuição RACI atualizada.',
            role_labels: {
                R: 'Responsável',
                A: 'Responsável',
                C: 'Consultado',
                I: 'Informado',
            },
            loaded_count: 'Atribuições RACI {0} carregadas',
            single_accountability_warning: 'Somente uma função de Responsável (A) é permitida por área de função. Atribuir uma nova função de Responsável substituirá a existente.',
            cybersec_function_areas: 'Áreas de função da Lei de Segurança Cibernética: Resposta a Incidentes, Preservação de Evidências, Cooperação de Autoridades, Revisão de Localização.',
            ai_law_function_areas: 'AI Áreas de função jurídica: Registro de sistema, avaliação pré-implantação, transparência, tratamento de incidentes.',
            suggested_function_areas: 'Áreas funcionais sugeridas',
        },
        prior_cycle: {
            link_label: 'Link para o ciclo anterior',
            select_prior: 'Selecione o ciclo anterior…',
            linked: 'Ano após ano',
            no_prior: 'Nenhum ciclo anterior disponível.',
            linked_success: 'Ciclo anterior vinculado.',
        },
        digest_table: {
            module: 'Módulo',
            entity_type: 'Tipo de entidade',
            change_type: 'Tipo de alteração',
            description: 'Descrição',
            changed_at: 'Alterado em',
        },
        view_related_ric: 'Ver casos RIC relacionados',
        linked_program: 'Programa Vinculado',
        nav_raci: 'Matriz RACI',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Rascunho de resposta',
            drafting: 'Elaboração…',
            results_heading: 'Respostas elaboradas ({count})',
            tokens_label: '≈ {count} tokens',
            counsel_label: 'Revisão do advogado necessária:',
            counsel_default: 'Pelo menos uma resposta requer análise do advogado antes do envio.',
            requires_counsel_label: 'Requer revisão do advogado.',
            accept_label: 'Aceitar rascunho',
            reject_label: 'Rejeitar rascunho',
            error_generic: 'Não foi possível redigir a resposta. Tente novamente ou refine o texto da consulta.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Cronograma de notificação de violação de computação',
            computing: 'Computação…',
            results_heading: 'Prazos de notificação de violação ({count})',
            tokens_label: '≈ {count} tokens',
            handoff_label: 'Forseti sugere:',
            empty_label: 'Nenhuma estrutura instalada declara um dever de notificação para este incidente.',
            mandatory_label: 'Campos obrigatórios:',
            draft_label: 'Rascunho de notificação',
            remaining_label: '{count}h restantes',
            overdue_label: 'ATRASADO por {count}h',
            acknowledge_label: 'Confirmar o cronograma',
            acknowledging: 'Reconhecendo…',
            error_generic: 'Não foi possível calcular o cronograma da violação. Tente novamente.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmado Violação',
            no: 'Não um Violação',
            uncertain: 'Pendente',
        },
        errors: {
            fetch: 'Falha ao load incidents. Por favor, tente novamente.',
            save: 'Falha ao save incident. Por favor, tente novamente.',
            fetch_failed: 'Falha ao fetch incidents. Por favor, tente novamente.',
            save_failed: 'Falha ao save incident. Por favor, tente novamente.',
            transition_failed: 'Falha ao transition incident. Por favor, tente novamente.',
            close_failed: 'Falha ao close incident. Por favor, tente novamente.',
            fetch_timeline_failed: 'Falha ao fetch timeline. Por favor, tente novamente.',
            fetch_decisions_failed: 'Falha ao fetch decisions. Por favor, tente novamente.',
            triage_failed: 'Falha ao update triage. Por favor, tente novamente.',
            impact_update_failed: 'Falha ao update impacto avaliação. Por favor, tente novamente.',
            breach_update_failed: 'Falha ao update violação avaliação. Por favor, tente novamente.',
            create_timeline_failed: 'Falha ao create timeline entry. Por favor, tente novamente.',
            create_decision_failed: 'Falha ao create decision. Por favor, tente novamente.',
            notification_update_failed: 'Falha ao update notification. Por favor, tente novamente.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Falha ao fetch tasks. Por favor, tente novamente.',
            create_task_failed: 'Falha ao create task. Por favor, tente novamente.',
            update_task_status_failed: 'Falha ao atualizar o estado da tarefa. Por favor, tente novamente.',
            update_checklist_failed: 'Falha ao update checklist. Por favor, tente novamente.',
            assign_task_failed: 'Falha ao assign task. Por favor, tente novamente.',
            escalate_task_failed: 'Falha ao escalate task. Por favor, tente novamente.',
            fetch_templates_failed: 'Falha ao fetch templates. Por favor, tente novamente.',
            permission_denied: 'Voce nao tem permissao para acessar esta secao. Entre em contato com seu administrador para solicitar acesso.',
        },
        harm_likelihood: {
            unlikely: 'Improvável',
            possible: 'Possível',
            likely: 'Provável',
            very_likely: 'Muito provável',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-mail',
            phone: 'Telefone',
            internal_system: 'Interno Sistema',
            third_party: 'Terceiro',
            automated_detection: 'Deteção automatizada',
        },
        task_transition_labels: {
            start: 'Início',
            complete: 'Concluir',
            block: 'Bloquear',
            defer: 'Adiar',
            cancel: 'Cancelar',
            unblock: 'Desbloquear',
        },
        transition_labels: {
            start: 'Início',
            complete: 'Concluir',
            block: 'Bloquear',
            defer: 'Adiar',
        },
        nav: {
            all: 'Todos os Incidentes',
            tasks: 'Tarefas de Resposta',
            reports: 'Relatórios',
            breach_notifications: 'Notificações de violação',
            supplements: 'Suplementos',
            remediation: 'Remediação',
            authority_requests: 'Pedidos de autoridade',
            disclosure_register: 'Registo de divulgação',
        },
        list: {
            page_title: 'Incidentes',
            title: 'Incidentes',
            new: 'Novo Incidente',
            edit: 'Editar Incidente',
            incident_title: 'Título',
            severity: 'Gravidade',
            reported_at: 'Reportado Em',
            reported_by: 'Reportado Por',
            assigned_to: 'Atribuído A',
            view_detail: 'Ver Detalhe',
            reference: 'Referência',
            lead: 'Responsável',
            reported_via: 'Reportado via',
            discovery_date: 'Data de descoberta',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Ocultar Detalhes',
            close_incident: 'Fechar Incidente',
            close_justification_prompt: 'Por favor, forneça uma justificação para encerrar este incidente.',
            justification: 'Justificação',
            lead_id: 'ID do responsável',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Contacto',
            category: 'Categoria',
            not_assessed: 'Não avaliado',
        },
        detail: {
            page_title: 'Detalhe do Incidente',
            title: 'Detalhe do Incidente',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Resumo',
                triage: 'Triagem',
                impact: 'Avaliação de Impacto',
                breach: 'Avaliação de Violação',
                timeline: 'Cronologia',
                decisions: 'Decisões',
                evidence: 'Evidência',
                audit_trail: 'Trilha de auditoria',
            },
            evidence_upload_label: 'Anexar ficheiro de evidência',
            evidence_uploaded: 'Evidência carregada',
            evidence_deleted: 'Evidência eliminada',
            evidence_delete_error: 'Falha ao eliminar evidência',
            evidence_uploaded_at: 'Carregado',
            evidence_empty: 'Não existem ficheiros de evidência anexados a este incidente.',
            evidence_size: 'Tamanho',
            triage_notes: 'Notas de Triagem',
            initial_classification: 'Classificação Inicial',
            individuals_affected: 'Número de Titulares Afetados',
            data_categories: 'Categorias de Dados Envolvidas',
            systems_compromised: 'Sistemas Comprometidos',
            geographic_scope: 'Âmbito Geográfico',
            breach_determination: 'Determinação de Violação',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Notificação Obrigatória',
            notification_deadline: 'Prazo de Notificação',
            pdpc_reportable: 'Reportável à CNPD',
            add_timeline: 'Adicionar Entrada na Cronologia',
            add_decision: 'Adicionar Decisão',
            not_found: 'Incidente não found.',
            team_members: 'Membros da equipa',
            checklist: 'Lista de verificação',
            completed: 'Concluído',
            impact_severity_score: 'Calculated Impacto Gravidade Pontuação',
            records_affected: 'Registos afetados',
            potential_consequences: 'Consequências potenciais',
            harm_likelihood: 'Probabilidade de dano',
            legal_basis: 'Base jurídica',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdições',
            jurisdictions_comma: 'Jurisdições (separadas por vírgulas)',
            jurisdiction: 'Jurisdição',
            authority_status: 'Estado da autoridade',
            subject_status: 'Titular Estado',
            authority_deadline: 'Prazo da autoridade',
            subject_deadline: 'Titular Prazo',
            mark_authority_done: 'Marcar autoridade como concluída',
            mark_subject_done: 'Marcar titular como concluído',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Não avaliado',
            event_type: 'Evento Tipo',
            timestamp: 'Marca temporal',
            actor: 'Ator',
            no_timeline_events: 'Ainda sem eventos na cronologia.',
            decision_type: 'Tipo de decisão',
            decision: 'Decisão',
            rationale: 'Fundamentação',
            decision_made: 'Decisão tomada',
            no_decisions: 'Nenhuma decisão registada ainda.',
            no_audit_events: 'Nenhum evento de auditoria registrado ainda.',
            export_pdf: 'Exportar PDF',
            pdf_title: 'Relatório Resumo de Incidente',
            description: 'Descrição',
            linked_records: 'Registos Associados',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Tarefas de resposta a incidentes',
            title: 'Tarefas de Resposta a Incidentes',
            new: 'Nova Tarefa',
            edit: 'Editar Tarefa',
            task_type: 'Tipo de Tarefa',
            assignee: 'Responsável',
            due_date: 'Data Limite',
            dependencies: 'Dependências',
            mark_complete: 'Marcar como Concluído',
            templates: 'Modelos de Tarefas',
            select_incident: 'Selecionar Incidente',
            all_statuses: 'Todos os Estados',
            all_categories: 'Todas as Categorias',
            new_response_task: 'Nova Tarefa de Resposta',
            category: 'Categoria',
            priority: 'Prioridade',
            depends_on: 'Depende de (IDs de tarefas separados por vírgula)',
            checklist_items: 'Itens da Lista de Verificação',
            remove: 'Remover',
            add: 'Adicionar',
            new_checklist_item: 'Novo item da lista de verificação',
            no_templates: 'Sem modelos disponíveis.',
            assign_task: 'Atribuir Tarefa',
            assign_to: 'Atribuir A',
            assign: 'Atribuir',
            select_incident_prompt: 'Por favor selecione um incidente para visualizar as tarefas de resposta.',
            checklist: 'Lista de Verificação',
            hide_checklist: 'Ocultar Lista de Verificação',
            escalate: 'Escalonar',
            escalations: 'Escalonamentos',
            no_checklist_items: 'Sem itens na lista de verificação.',
        },
        reports: {
            page_title: 'Relatórios de incidentes',
            title: 'Relatórios de Incidentes',
            summary: 'Estatísticas Resumidas',
            by_severity: 'Por Gravidade',
            by_status: 'Por Estado',
            recent: 'Incidentes Recentes',
            total_incidents: 'Total de Incidentes',
            open: 'Abertos',
            closed: 'Encerrados',
            export_csv: 'Exportar CSV',
            export_pdf: 'Exportar PDF',
            export_success: 'Relatório exportado com sucesso.',
            export_error: 'Falha ao exportar relatório.',
            reference: 'Referência',
            view_report: 'Ver Relatório',
        },
        statuses: {
            reported: 'Reportado',
            triage: 'Triagem',
            contained: 'Contido',
            investigation: 'Investigação',
            remediation: 'Remediação',
            closed: 'Encerrado',
            false_positive: 'Falso Positivo',
        },
        task_statuses: {
            pending: 'Pendente',
            in_progress: 'Em Curso',
            completed: 'Concluído',
            blocked: 'Bloqueado',
            cancelled: 'Cancelado',
        },
        severity: {
            low: 'Baixo',
            medium: 'Médio',
            high: 'Alto',
            critical: 'Crítico',
        },
        task_types: {
            containment: 'Contenção',
            investigation: 'Investigação',
            remediation: 'Remediação',
            communication: 'Comunicação',
            documentation: 'Documentação',
            notification: 'Notificação',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Dados Violação',
            unauthorized_access: 'Acesso não autorizado',
            data_loss: 'Perda de dados',
            system_compromise: 'Comprometimento do sistema',
            malware: 'Programas maliciosos',
            phishing: 'Phishing',
            insider_threat: 'Ameaça interna',
            other: 'Outro',
        },
        reported_via_options: {
            email: 'E-mail',
            phone: 'Telefone',
            internal_system: 'Interno Sistema',
            third_party: 'Terceiro',
            automated_detection: 'Deteção automatizada',
        },
        harm_likelihood_options: {
            unlikely: 'Improvável',
            possible: 'Possível',
            likely: 'Provável',
            very_likely: 'Muito provável',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmado Violação',
            suspected_breach: 'Violação suspeita',
            not_a_breach: 'Não um Violação',
            pending: 'Pendente',
        },
        timeline_event_types: {
            detection: 'Deteção',
            escalation: 'Escalamento',
            containment: 'Contenção',
            investigation_start: 'Início da investigação',
            evidence_collected: 'Evidências recolhidas',
            notification_sent: 'Notificação Enviado',
            remediation_action: 'Ação de remediação',
            status_change: 'Alteração de estado',
            communication: 'Comunicação',
            other: 'Outro',
        },
        decision_types: {
            containment: 'Contenção',
            notification: 'Notificação',
            remediation: 'Remediação',
            escalation: 'Escalamento',
            closure: 'Encerramento',
            other: 'Outro',
        },
        task_transitions: {
            start: 'Início',
            complete: 'Concluir',
            block: 'Bloquear',
            cancel: 'Cancelar',
            unblock: 'Desbloquear',
        },
        announce: {
            loaded: '{count} incidentes carregados',
            reports_loaded: '{count} relatórios de incidentes carregados',
            tasks_loaded: '{count} tarefas carregadas',
            created: 'Incidente criado com sucesso',
            updated: 'Incidente atualizado com sucesso',
            closed: 'Incidente fechado com sucesso',
            status_changed: 'Status do incidente alterado',
            triage_completed: 'Triagem concluída com sucesso',
            impact_updated: 'Avaliação de impacto atualizada',
            breach_updated: 'Avaliação de violação atualizada',
            timeline_added: 'Evento de linha do tempo adicionado',
            decision_recorded: 'Decisão registrada',
            notification_updated: 'Status da notificação atualizado',
            task_created: 'Tarefa criada com sucesso',
            task_status_changed: 'Status da tarefa alterado',
            checklist_completed: 'Item da lista de verificação concluído',
            checklist_unchecked: 'Item da lista de verificação desmarcado',
            task_assigned: 'Tarefa atribuída com sucesso',
            task_escalated: 'Tarefa escalonada',
            export_initiated: 'Exportação iniciada',
        },
        breach_notifications: {
            page_title: 'Notificações de violação',
            title: 'Notificações de violação',
            new_notification: 'Nova notificação',
            no_notifications: 'Nenhuma notificação de violação encontrada',
            status: 'Estado',
            submit_to_mps: 'Enviar para MPS',
            decision: 'Decisão',
            decision_maker: 'Tomador de decisões',
            notify: 'Notificar',
            do_not_notify: 'Não notifique',
            rationale: 'Justificativa',
            filing_deadline: 'Prazo de arquivamento',
            overdue: 'Atrasado',
            due_soon: 'Próximo do prazo',
            on_track: 'No prazo',
            legal_review: 'Revisão Jurídica',
            incident_id: 'ID do incidente',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Avaliação',
                decision_to_notify: 'Decisão de Notificar',
                notification_drafted: 'Notificação elaborada',
                legal_review: 'Revisão Jurídica',
                submitted_to_authority: 'Submetido à autoridade',
                supplement_requested: 'Suplemento solicitado',
                supplement_submitted: 'Suplemento enviado',
                acknowledged: 'Reconhecido',
                closed: 'Fechado',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Solicitações de suplementos',
            title: 'Solicitações de suplementos',
            new_supplement: 'Novo suplemento',
            no_supplements: 'Nenhuma solicitação de suplemento encontrada',
            request_details: 'Detalhes da solicitação',
            response_package: 'Pacote de Resposta',
            submit_response: 'Enviar resposta',
            deadline: 'Prazo',
            notification_id: 'ID de notificação',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Insira um ID de notificação para visualizar suplementos.',
            supplement_statuses: {
                requested: 'Solicitado',
                drafting: 'Rascunho',
                submitted: 'Enviado',
                accepted: 'Aceito',
                rejected: 'Rejeitado',
            },
        },
        remediation: {
            page_title: 'Ações de remediação',
            title: 'Ações de remediação',
            new_action: 'Nova ação',
            no_actions: 'Nenhuma ação de correção encontrada',
            description: 'Descrição',
            owner: 'Proprietário',
            department: 'Departamento',
            deadline: 'Prazo',
            statuses: {
                planned: 'Planejado',
                in_progress: 'Em andamento',
                completed: 'Concluído',
                verified: 'Verificado',
            },
            verify: 'Verifique',
            incident_id: 'ID do incidente',
            enter_incident_id: 'Insira um ID de incidente para visualizar as ações de correção.',
            start: 'Começar',
            complete_action: 'Concluir',
        },
        authority_requests: {
            page_title: 'Solicitações de autoridade',
            title: 'Solicitações de autoridade',
            new_request: 'Nova solicitação',
            no_requests: 'Nenhuma solicitação de autoridade encontrada',
            authority_name: 'Nome da autoridade',
            request_details: 'Detalhes da solicitação',
            response_package: 'Pacote de Resposta',
            submission_channel: 'Canal de envio',
            submit_response: 'Enviar resposta',
            deadline: 'Prazo',
            confirmation: 'Confirmação',
            incident_id_optional: 'ID do incidente (opcional)',
            response_context_label: 'Contexto da resposta (para materialização de documento de submissão)',
            response_context_none: '(ignorar materialização)',
            response_context_supplement: 'Resposta suplementar',
            response_context_information: 'Resposta a pedido de informação',
            response_context_inspection: 'Resposta a inspeção',
            materialisation_status_column: 'Documento de submissão',
            materialisation_pending: 'Pendente',
            materialisation_success: 'Materializado',
            materialisation_failed: 'Falhado',
            materialisation_retry_button: 'Tentar novamente',
            ar_statuses: {
                received: 'Recebido',
                verified: 'Verificado',
                assembling: 'Montagem',
                submitted: 'Submetido',
                closed: 'Fechado',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Revisão Jurídica',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Lista de verificação',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Registro de Divulgação',
            title: 'Registro de Divulgação',
            new_entry: 'Nova entrada',
            no_entries: 'Nenhuma entrada de registro de divulgação encontrada',
            disclosure_type: 'Tipo de divulgação',
            disclosure_date: 'Data de divulgação',
            recipient: 'Destinatário',
            authority_name: 'Nome da autoridade',
            channel: 'Canal',
            content_summary: 'Resumo do conteúdo',
            incident_id_optional: 'ID do incidente (opcional)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Assistente de Configuração',
            tours: 'Visitas Guiadas',
            sample_data: 'Dados de Exemplo',
        },
        wizard: {
            title: 'Assistente de Configuração',
            welcome: 'Bem-vindo ao AesirX ComplianceOne',
            welcome_message: 'Vamos configurar a sua organização. Este assistente irá guiá-lo através da configuração inicial.',
            setup_complete: 'Configuração Concluída',
            skipped_message: 'O assistente de configuração foi ignorado. Pode executá-lo a qualquer momento para configurar a sua organização.',
            start_setup: 'Iniciar Configuração',
            skip_wizard: 'Saltar Assistente',
            go_to_admin: 'Vá para Administrador',
            skip_for_now: 'Saltar por Agora',
            next_step: 'Passo Seguinte',
            previous_step: 'Passo Anterior',
            finish_setup: 'Concluir Configuração',
            rerun: 'Executar Assistente Novamente',
            step_label: 'Passo {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'O nome da organização é obrigatório.',
            validation_admin_email_required: 'O correio eletrónico do administrador é obrigatório.',
            validation_admin_email_invalid: 'Por favor, introduza um endereço de correio eletrónico válido.',
            validation_cmp_endpoint_required: 'O endpoint CMP é obrigatório quando o CMP está ativado.',
            steps: {
                license_activation: 'Ativação de licença',
                org_profile: 'Perfil da Organização',
                module_selection: 'Seleção de Módulos',
                cmp_integration: 'Integração CMP',
                notification_preferences: 'Notificações',
                review: 'Revisão e Confirmação',
            },
            step_descriptions: {
                license_activation: 'Insira sua chave de licença para ativar seu pacote e determinar quais módulos estão disponíveis.',
                org_profile: 'Configure o nome da organização, jurisdição, setor, tamanho e idioma preferido.',
                module_selection: 'Escolha quais módulos de conformidade ativar para a sua organização.',
                cmp_integration: 'Configure a integração com a Consent Management Platform (opcional).',
                notification_preferences: 'Configure como recebe notificações sobre eventos de conformidade.',
                review: 'Reveja a sua configuração e finalize a instalação.',
            },
            license_step: {
                validate_button: 'Validar licença',
                skip_warning: 'O acesso ao módulo será limitado ao nível de pacote padrão. Você pode ativar uma licença posteriormente em Plataforma > Configurações de licença.',
            },
            module_descriptions: {
                rights_requests: 'Trate pedidos de acesso e eliminação de dados pessoais.',
                data_mapping: 'Mapeie sistemas, fluxos de dados e atividades de processamento.',
                governance: 'Supervisione fornecedores, aprovações, desvios e os registos de evidências.',
                consent: 'Gerencie políticas de consentimento e catálogos de finalidades.',
                consent_properties: 'Gerencie o consentimento em múltiplas propriedades digitais.',
                assessments: 'Realize avaliações de impacto e transferências transfronteiriças de dados.',
                classification: 'Classifique e rotule ativos de dados com rastreamento do ciclo de vida.',
                monitoring: 'Revisões de conformidade programadas e rastreamento de KPI.',
                incidents: 'Reporte e gerencie incidentes de segurança e privacidade.',
                connectors: 'Conecte-se a sistemas externos para troca automatizada de dados.',
                vendor_risk: 'Pontue fornecedores, rastreie contratos e gerencie a devida diligência.',
                deletion_orchestration: 'Coordene a eliminação de dados entre sistemas e fornecedores.',
                discovery: 'Descubra e classifique dados em toda a sua infraestrutura.',
                incident_ops: 'Playbooks avançados de incidentes, rastreamento de SLA e escalações.',
                program_governance: 'Avaliações de maturidade, relatórios executivos e prontidão para auditoria.',
                access_accountability: 'Monitore o acesso de utilizadores, sessões e fluxos de justificação.',
                localization: 'Pacotes de conformidade específicos do mercado e enquadramentos regulatórios.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Espaço de trabalho de parceiros e revendedores.',
                privacy_scanner: 'Monitorização de privacidade em tempo real de propriedades digitais.',
                onboarding: 'Assistente de configuração, visitas guiadas e gestão de dados de exemplo.',
                compliance_forms: 'Modelos de conformidade interativos e geração de documentos.',
                audit_trail: 'Registo de auditoria imutável abrangente e retenção.',
                organizations: 'Gestão multi-organização e de grupos.',
                task_management: 'Planeamento unificado de tarefas, motor de fluxo de trabalho e vistas de calendário.',
            },
            module_categories: {
                data_privacy_rights: 'Privacidade e direitos de dados',
                vendor_third_party: 'Gerenciamento de fornecedores e terceiros',
                risk_assessment: 'Risco e Avaliação',
                operations_automation: 'Operações e Automação',
                platform_admin: 'Plataforma e Administração',
            },
            package_tier_label: 'O Seu Pacote',
            package_module_unavailable: 'Não incluído no seu pacote',
            select_all: 'Selecionar Tudo',
            deselect_all: 'Desselecionar Tudo',
            load_sample_data: 'Carregar dados de exemplo após a configuração',
            load_sample_data_description: 'Instale dados de demonstração realistas para explorar a plataforma. Pode ser removido mais tarde em Integração > Dados de Exemplo.',
            language_label: 'Idioma Preferido',
            language_description: 'Escolha o idioma predefinido para a sua organização.',
            admin_account_title: 'Conta de Administrador',
            admin_account_created: 'Conta de administrador criada',
            admin_full_name: 'Nome completo',
            admin_email: 'Endereço de e-mail',
            admin_password: 'Palavra-passe',
            admin_confirm_password: 'Confirmar palavra-passe',
            admin_show_password: 'Mostrar palavra-passe',
            admin_hide_password: 'Ocultar palavra-passe',
            admin_name_placeholder: 'por exemplo Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Mínimo 8 caracteres',
            admin_password_confirm_placeholder: 'Reintroduzir palavra-passe',
            validation_admin_name_required: 'O nome do administrador é obrigatório',
            validation_admin_password_required: 'A palavra-passe é obrigatória',
            validation_admin_password_min_length: 'A palavra-passe deve ter pelo menos 8 caracteres',
            validation_passwords_mismatch: 'As palavras-passe não coincidem',
            notification_email_enabled: 'Ativar Notificações por E-mail',
            notification_email_description: 'Receba notificações por e-mail sobre atribuições de tarefas, violações de SLA e eventos de conformidade.',
            notification_digest_frequency: 'Frequência do Resumo',
            notification_digest_daily: 'Diário',
            notification_digest_weekly: 'Semanal',
            notification_digest_none: 'Sem resumo',
            notification_in_app_enabled: 'Ativar Notificações da Plataforma',
            notification_in_app_description: 'Mostrar emblema e gaveta de notificações para alertas em tempo real.',
            jurisdictions: {
                vietnam: 'Vietname',
                asean: 'ASEAN',
                eu_eea: 'UE/EEE',
                global: 'Globais',
            },
            languages: {
                en: 'Inglês',
                vi: 'Tiếng Việt',
                da: 'Dinamarquês',
                sv: 'Sueca',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Alemão',
                fi: 'Suomi',
                nl: 'Holanda',
                fr: 'Francês',
                lb: 'Lëtzebuergesch',
                ga: 'gaélico',
                es: 'Espanhol',
                ca: 'Catalã',
                eu: 'Euskara',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Serviços Financeiros',
                healthcare: 'Saúde',
                technology: 'Tecnologia',
                telecommunications: 'Telecomunicações',
                government: 'Governo',
                education: 'Educação',
                manufacturing: 'Indústria Transformadora',
                retail: 'Comércio a Retalho',
                other: 'Outro',
            },
            placeholders: {
                org_name: 'por exemplo, ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'usuário1@empresa.vn, usuário2@empresa.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Nome da Organização',
                jurisdiction: 'Jurisdição',
                industry: 'Setor de Atividade',
                org_size: 'Dimensão da Organização',
                language: 'Idioma Preferido',
                admin_email: 'Correio Eletrónico do Administrador',
                team_emails: 'Correios Eletrónicos dos Membros da Equipa (separados por vírgula)',
                enable_cmp: 'Ativar Integração CMP',
                cmp_endpoint: 'Endpoint da API CMP',
                email_notifications: 'Notificações por E-mail',
                in_app_notifications: 'Notificações da Plataforma',
                digest_frequency: 'Frequência do Resumo',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Visitas Guiadas aos Módulos',
            start: 'Iniciar Visita',
            continue_tour: 'Continuar Visita',
            replay: 'Repetir Visita',
            exit: 'Sair da Visita',
            skip: 'Saltar Visita',
            next: 'Seguinte',
            previous: 'Anterior',
            finish: 'Concluir Visita',
            step_of: 'Passo {current} de {total}',
            info: 'Faça visitas guiadas a cada módulo para conhecer as principais funcionalidades e fluxos de trabalho. As visitas podem ser repetidas a qualquer momento.',
            autoplay: 'Reprodução automática',
            interactive: 'Interativo',
            play_all: 'Jogue tudo',
            search_tours: 'Pesquisar passeios...',
            duration: 'Duração',
            steps_label: '{count} etapas',
            mode: 'Modo',
            pause: 'Pausa',
            resume: 'Currículo',
            speed: 'Velocidade',
            restart: 'Reiniciar',
            reset: 'Redefinir',
            reset_success: 'O progresso do tour foi redefinido.',
            category_core_privacy: 'Privacidade Central',
            category_governance: 'Governança',
            category_operations: 'Operações',
            category_platform: 'Plataforma',
            category_advanced: 'Avançado',
            category_data_privacy_rights: 'Privacidade e direitos de dados',
            category_vendor_third_party: 'Gerenciamento de fornecedores e terceiros',
            category_risk_assessment: 'Risco e Avaliação',
            category_operations_automation: 'Operações e Automação',
            category_platform_admin: 'Plataforma e Administração',
            steps_count: '{count} etapas',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Gestão de Dados de Exemplo',
            select_modules: 'Selecionar Módulos',
            current_status: 'Estado Atual',
            operation_history: 'Histórico de Operações',
            load: 'Carregar Dados de Exemplo',
            clear: 'Limpar Dados de Exemplo',
            processing: 'A processar...',
            loaded: 'Carregado',
            not_loaded: 'Não Carregado',
            records: 'Registos de Exemplo',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Tem a certeza de que pretende limpar todos os dados de exemplo dos módulos selecionados?',
            confirm_clear_title: 'Limpar dados de exemplo',
            confirm_load_title: 'Carregar dados de exemplo',
            confirm_load: 'Tem certeza de que deseja carregar dados de exemplo para os módulos selecionados?',
            action_load: 'Carregar',
            action_clear: 'Limpar',
            info: 'Carregue dados de demonstração realistas para formação e demonstrações. Os dados de exemplo estão etiquetados e podem ser removidos com segurança sem afetar os dados reais.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Centro de Ajuda',
            search: 'Pesquisar',
            search_placeholder: 'Pesquisar artigos de ajuda...',
            clear: 'Limpar',
            all_categories: 'Todas as Categorias',
            all_modules: 'Todos os Módulos',
            back: 'Voltar ao Centro de Ajuda',
            callout_important: 'Importante',
            callout_tip: 'Dica',
            no_results: 'Não foram encontrados artigos correspondentes à sua pesquisa.',
            no_articles: 'Ainda não existem artigos de ajuda disponíveis. Os artigos aparecerão aqui à medida que o conteúdo for adicionado.',
            results_for: '{count} resultado(s) para "{query}"',
            quick_links: {
                getting_started: 'Começar',
                getting_started_desc: 'Instalação, configuração e guia de início rápido.',
                user_guide: 'Guia do Utilizador',
                user_guide_desc: 'Documentação de utilização módulo a módulo.',
                admin_guide: 'Guia do Administrador',
                admin_guide_desc: 'Configuração, manutenção e resolução de problemas.',
                faq: 'Perguntas Frequentes',
                faq_desc: 'Perguntas frequentes e respostas.',
            },
            search_articles_aria: 'Pesquisar artigos de ajuda',
            article_metadata_aria: 'Metadados do artigo',
            faq_editor_title: 'Editor de perguntas frequentes do centro de ajuda',
            faq_editor_description: 'Adicione, edite e remova pares de pergunta e resposta em qualquer artigo do centro de ajuda. O editor ainda não está disponível; este endereço está reservado para ele.',
            faq_editor_empty: 'O editor de perguntas frequentes ainda não está disponível. Até lá, peça ao seu administrador para adicionar secções de perguntas frequentes a um artigo do centro de ajuda.',
            articles_list_aria: 'Artigos de ajuda',
            category_nav_aria: 'Navegação por categorias',
            quick_links_aria: 'Links rápidos para categorias de ajuda',
            returned_to_list: 'Retornou à lista de artigos',
            opening_article: 'Abrindo artigo: {title}',
            read_article_aria: 'Ler artigo: {title}',
            pagination_nav_aria: 'Paginação de artigos',
            error_unavailable: 'O centro de ajuda está atualmente indisponível. Por favor, tente novamente mais tarde.',
            retry: 'Tentar novamente',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'p. ex. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Guardar',
        cancel: 'Cancelar',
        system_actor: 'Sistema',
        delete: 'Eliminar',
        edit: 'Editar',
        create: 'Criar',
        update: 'Atualizar',
        loading: 'A carregar...',
        error: 'Ocorreu um erro',
        success: 'Sucesso',
        logout: 'Terminar Sessão',
        actions: 'Ações',
        retrying: 'Tentando novamente…',
        permission_denied: 'Você não tem acesso a esta página.',
        status: 'Estado',
        description: 'Descrição',
        name: 'Nome',
        created_at: 'Criado Em',
        updated_at: 'Atualizado Em',
        yes: 'Sim',
        no: 'Não',
        confirm: 'Confirmar',
        confirm_dialog_title: 'Confirm action',
        back: 'Voltar',
        next: 'Seguinte',
        previous: 'Anterior',
        search: 'Pesquisar',
        filter: 'Filtrar',
        page_x_of_y: 'Página {page} de {total}',
        pagination_label: 'Paginação',
        view_mode_label: 'Modo de visualização',
        export: 'Exportar',
        exporting: 'A exportar…',
        sidebar_navigation: 'Navegação lateral de administração',
        items_count: '{count} itens',
        items_count_one: '{count} item',
        export_success: 'Documento gerado com sucesso.',
        generate_pdf: 'Gerar PDF',
        import: 'Importar',
        download: 'Transferir',
        upload: 'Carregar',
        view: 'Ver',
        close: 'Fechar',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Submeter',
        saved: 'Guardado com sucesso',
        saving: 'A guardar...',
        submitting: 'A submeter...',
        created: 'Criado com sucesso',
        all: 'Todos',
        none: 'Nenhum',
        select: 'Selecionar',
        select_user: 'Selecione o usuário...',
        custom_input: 'Introduzir manualmente',
        required: 'Obrigatório',
        optional: 'Opcional',
        notes: 'Notas',
        tags: 'Etiquetas',
        archive: 'Arquivar',
        clone: 'Clonar',
        refresh: 'Atualizar',
        retry: 'Tentar Novamente',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Repor',
        add: 'Adicionar',
        remove: 'Remover',
        removing: 'A remover…',
        attachment: 'Anexo',
        upload_file: 'Carregar ficheiro',
        upload_file_required: 'Carregar ficheiro (obrigatório)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Arraste e solte ou clique para procurar',
        upload_max_size: 'Tamanho máximo: {size} MB',
        upload_accepted_types: 'Aceites: {types}',
        upload_in_progress: 'A carregar {name}...',
        upload_size_exceeded: 'O tamanho do ficheiro excede o tamanho máximo permitido de {size} MB.',
        upload_type_not_accepted: 'Tipo de ficheiro não aceite. Tipos aceites: {types}',
        upload_succeeded: 'O ficheiro "{name}" foi carregado.',
        upload_failed: 'Não foi possível carregar o ficheiro.',
        select_entity_type_first: 'Selecione primeiro um tipo de entidade',
        failed_to_remove_file: 'Falha ao remover o ficheiro.',
        entity_type_label: 'Tipo de entidade',
        entity_label: 'Entidade',
        entity_reference: 'Referência de entidade',
        failed_to_load_entities: 'Falha ao carregar entidades',
        enable: 'Ativar',
        disable: 'Desativar',
        no_data: 'Dados não encontrados',
        confirm_delete: 'Tem a certeza de que pretende eliminar este item?',
        confirm_action: 'Tem a certeza de que pretende continuar?',
        save_success: 'Guardado com sucesso',
        save_error: 'Falha ao guardar',
        delete_success: 'Eliminado com sucesso',
        delete_error: 'Falha ao eliminar',
        hour: 'hora',
        hours: 'horas',
        load_error: 'Falha ao carregar dados',
        operation_error: 'Operação falhou',
        something_went_wrong: 'Algo correu mal',
        go_home: 'Ir para o Painel de Controlo',
        access_denied: 'Acesso Negado',
        no_permission: 'Não tem permissão para aceder a esta página.',
        no_permission_title: 'Permissões insuficientes',
        no_permission_message: 'Você não tem permissão para acessar esta página.',
        unknown: 'Desconhecido',
        back_to_dashboard: 'Voltar ao Painel de Controlo',
        id: 'ID',
        type: 'Tipo',
        date: 'Data',
        created_by: 'Criado Por',
        updated_by: 'Atualizado Por',
        page_of: 'Página {page} de {total}',
        showing_items: 'A apresentar {count} itens',
        items_per_page: 'Itens por página',
        no_results: 'Nenhum registo corresponde aos seus filtros',
        no_items: 'Nenhum item encontrado',
        sort_by: 'Ordenar por',
        ascending: 'Ascendente',
        descending: 'Descendente',
        all_statuses: 'Todos os Estados',
        all_types: 'Todos os Tipos',
        all_categories: 'Todas as Categorias',
        all_severities: 'Todas as Gravidades',
        date_range: 'Intervalo de Datas',
        from_date: 'De',
        to_date: 'Até',
        clear_filters: 'Limpar Filtros',
        apply_filters: 'Aplicar Filtros',
        save_changes: 'Guardar Alterações',
        discard_changes: 'Descartar Alterações',
        confirm_discard: 'Tem alterações por guardar. Pretende descartá-las?',
        copy: 'Copiar',
        duplicate: 'Duplicar',
        move: 'Mover',
        assign: 'Atribuir',
        unassign: 'Remover Atribuição',
        approve: 'Aprovar',
        reject: 'Rejeitar',
        deleting: 'A eliminar...',
        processing: 'A processar...',
        loading_page: 'A carregar página...',
        operation_success: 'Operação concluída com sucesso',
        operation_failed: 'Operação falhada. Tente novamente.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
            gov_vendor_read: 'read vendors',
            vendor_questionnaire_read: 'read vendor questionnaires',
            ai_governance_read: 'read AI systems',
            dpia_read: 'read data processing impact assessments',
            incident_read: 'read incidents',
            dm_evidence_read: 'read Data Mapping evidence',
            vehicle_monitoring_read: 'read vehicle monitoring devices',
            forms_instance_read: 'read form records',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '-',
        network_error: 'Erro de rede. Verifique a sua ligação.',
        session_expired: 'Sessão expirada. Inicie sessão novamente.',
        test: 'Testar',
        pause: 'Pausar',
        resume: 'Retomar',
        activate: 'Ativar',
        last_sync: 'Última Sincronização',
        health: 'Estado',
        email: 'E-mail',
        role: 'Função',
        title: 'Título',
        owner: 'Responsável',
        priority: 'Prioridade',
        category: 'Categoria',
        details: 'Detalhes',
        overview: 'Resumo',
        configuration: 'Configuração',
        history: 'Histórico',
        new_item: 'Novo',
        edit_item: 'Editar',
        select_option: 'Selecionar...',
        total: 'Total',
        active: 'Ativo',
        inactive: 'Inativo',
        enabled: 'Ativado',
        disabled: 'Desativado',
        last_login: 'Último Início de Sessão',
        never: 'Nunca',
        password_changed: 'Palavra-passe alterada. A sessão será terminada.',
        password_change_failed: 'Falha ao alterar a palavra-passe',
        passwords_mismatch: 'As palavras-passe não coincidem',
        install: 'Instalar',
        install_named: 'Instalar {name}',
        uninstall: 'Desinstalar',
        installed: 'Instalado',
        available: 'Disponível',
        catalog: 'Catálogo',
        published: 'Publicado',
        version: 'Versão',
        market: 'Mercado',
        language: 'Idioma',
        framework: 'Enquadramento',
        template: 'Modelo',
        connector_type: 'Tipo de Conector',
        polling_interval: 'Intervalo de Consulta (minutos)',
        new_instance: 'Nova Instância',
        change_password: 'Alterar Palavra-passe',
        current_password: 'Palavra-passe Atual',
        new_password: 'Nova Palavra-passe',
        confirm_password: 'Confirmar Palavra-passe',
        comments: 'Comentários',
        completed: 'Concluído',
        contact: 'Contacto',
        hide: 'Ocultar',
        hide_details: 'Ocultar Detalhes',
        label: 'Rótulo',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'A mostrar',
        page: 'Página',
        preview: 'Pré-visualização',
        print: 'Imprimir',
        show: 'Mostrar',
        status_active: 'Ativo',
        status_completed: 'Concluído',
        status_draft: 'Rascunho',
        status_in_progress: 'Em curso',
        status_pending: 'Pendente',
        view_details: 'Ver detalhes',
        all_priorities: 'Todas as prioridades',
        pipeline: 'Gasoduto',
        all_requests: 'Todos Pedidos',
        schedule: 'Agendar',
        deploy: 'Implementar',
        rollback: 'Reverter',
        advance_stage: 'Avançar etapa',
        submit_for_approval: 'Submeter para Approval',
        approval_log: 'Registo de aprovação',
        approver: 'Aprovador',
        decision: 'Decisão',
        decided_at: 'Decidido em',
        rollout_stages: 'Etapas de implementação',
        approval_comment: 'Comentário de aprovação',
        optional_comment: 'Comentário opcional...',
        impact_summary: 'Impacto Resumo',
        target_property_ids: 'IDs de propriedades de destino (separados por vírgulas)',
        policy_id: 'Política ID',
        target_version: 'Versão de destino',
        target_version_placeholder: 'p. ex. 2.0.0',
        scheduled_label: 'Agendado',
        scheduled_start: 'Agendado Início',
        scheduled_end: 'Agendado Fim',
        properties_count: '{count} propriedades',
        results: 'Resultados',
        comparison_results: 'Resultados de comparação',
        past_comparison_reports: 'Relatórios de comparação anteriores',
        scanning: 'A analisar...',
        run_inconsistency_scan: 'Executar Inconsistency Analisar',
        property_ids_label: 'IDs de propriedade (separados por vírgulas, mín 2)',
        comparison_type: 'Tipo de comparação',
        golden_template_id: 'ID do modelo de referência (opcional)',
        leave_blank_compare: 'Deixe em branco para comparar propriedades entre si',
        add_notes_placeholder: 'Adicionar your analysis notes here...',
        covered_of_active: '{covered} de {active} propriedades ativas cobertas',
        fetch_error: 'Falha ao fetch data. Por favor, tente novamente.',
        run_comparison_error: 'Falha ao run comparison. Por favor, tente novamente.',
        save_notes_error: 'Falha ao save notes. Por favor, tente novamente.',
        scan_complete: 'Inconsistency scan concluído com sucesso',
        scan_error: 'Falha ao run inconsistency scan. Por favor, tente novamente.',
        min_two_properties: 'Forneça pelo menos 2 IDs de propriedade para comparação.',
        comma_separated: 'separados por vírgulas',
        language_english: 'Inglês',
        language_vietnamese: 'Vietnamita',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Autor',
        dependencies: 'Dependências',
        checksum: 'Soma de verificação',
        changelog: 'Registo de alterações',
        tier: 'Nível',
        phone: 'Telefone',
        launch: 'Lançar',
        complete: 'Concluir',
        back_to_list: 'Voltar para Lista',
        clear_selection: 'Limpar seleção',
        deactivate: 'Desativar',
        scope: 'Âmbito',
        deadline: 'Prazo',
        progress: 'Progresso',
        frequency: 'Frequência',
        reviewer: 'Revisor',
        user: 'Utilizador',
        campaign: 'Campanha',
        items: 'Itens',
        next_run: 'Seguinte Executar',
        last_run: 'Último Executar',
        overdue: 'EM ATRASO',
        creating: 'A criar...',
        launching: 'A lançar...',
        loaded: 'loaded',
        page_size: 'Tamanho da página',
        per_page: '/página',
        urgent: 'Urgente',
        pagination: 'Paginação',
        metric: 'Métrica',
        value: 'Valor',
        country: 'País',
        showing_chart_view: 'Exibindo visualização de gráfico',
        showing_table_view: 'Exibindo visualização de tabela de dados',
        policies: 'Políticas',
        compliant: 'Compatível',
        non_compliant_status: 'Não conforme',
        all_data: 'Todos os dados',
        sr_urgent: 'Urgente',
        n_per_page: '{n} / página',
        select_user_placeholder: '-- Selecione o usuário --',
        field_required: 'Este campo é obrigatório',
        results_found: 'resultados encontrados',
        select_placeholder: '-- Selecionar --',
        unknown_user: 'Utilizador desconhecido',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Exportar CSV',
        not_applicable: 'N/A',
        aria_overdue_since: 'Em atraso desde {date}',
        placeholders: {
            enter_justification: 'Introduza justification',
            justification: 'Justificação',
            campaign_name_example: 'Q1 2026 Acesso Rever',
            optional_description: 'Descrição opcional',
            enter_user_id: 'Introduza user ID of the reviewer',
            enter_user_id_short: 'Introduza user ID',
            schedule_name_example: 'Trimestral Acesso Attestation',
            enter_property_id: 'Introduza property ID',
            enter_policy_id: 'Introduza política id',
            property_or_group_id: 'ID de propriedade ou grupo',
            describe_template_purpose: 'Descreva o propósito do modelo...',
            step_title: 'Título do passo...',
            detailed_instructions: 'Instruções detalhadas...',
            filter_by_taxonomy_id: 'Filtrar por ID de taxonomia...',
            describe_classification_rule: 'Descreva a regra de classificação...',
            describe_dedup_rule: 'Descreva a regra de deduplicação...',
            describe_import_template: 'Descreva o modelo de importação...',
            reason_for_rejection: 'Motivo de rejeição...',
            jurisdiction_codes_example: 'VN, SG, UE',
            filter_by_locale: 'Filtrar por idioma...',
            filter_by_event_type: 'Filtrar por tipo de evento',
            filter_by_actor_id: 'Filtrar por ID de ator',
            filter_by_market_code: 'Filtrar por código de mercado...',
            filter_by_framework_id: 'Filtrar por enquadramento ID...',
            add_comment: 'Adicionar comment...',
            provide_rejection_reason: 'Forneça um motivo para rejeitar esta submissão...',
            filter_by_jurisdiction: 'Filtrar por código de jurisdição...',
            enter_report_title: 'Introduza report title',
            enter_name: 'Introduza name',
            enter_description: 'Introduza description',
            enter_org_id: 'Introduza Org ID to add',
            describe_evidence_gap: 'Descreva a lacuna de evidência...',
            expected_evidence: 'Que evidência é esperada',
            entity_identifier: 'Identificador da entidade',
            remediation_steps: 'Passos para remediar esta lacuna',
            user_id_or_name: 'ID de utilizador ou nome',
            reporting_period_id: 'ID do período de relatório',
            maturity_model_id: 'ID do modelo de maturidade',
            describe_maturity_model: 'Descreva este modelo de maturidade...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Forneça um motivo para negar este pedido',
            describe_emergency: 'Descreva a situação de emergência que requer acesso elevado (mínimo 20 caracteres)',
            enter_delegate_id: 'Introduza user ID of the delegate',
            provide_delegation_reason: 'Forneça um motivo para esta delegação',
            client_identifier: 'Identificador de cliente',
            client_secret: 'Segredo do cliente',
            time_format: 'HH:MM',
            describe_sod_rule: 'Descreva o propósito desta regra SoD...',
            enter_role_id: 'Introduza role ID',
            explain_exception: 'Explique por que esta exceção é necessária...',
            describe_compensating_controls: 'Descreva os controlos compensatórios em vigor...',
            full_name: 'Nome completo',
            min_chars: 'Mín 12 caracteres',
            enter_override_justification: 'Introduza justification for overriding this gate evaluation',
            enter_evidence_id: 'Insira o ID da evidência para vincular:',
            date_format: 'DD/MM/AAAA',
            describe_pack_contents: 'Descreva o conteúdo e o propósito do pacote',
            select_reviewers: 'Selecionar revisores...',
        },
        aria_previous_page: 'Página anterior',
        aria_next_page: 'Próxima página',
        aria_results_per_page: 'Resultados por página',
        per_page_25: '25/página',
        per_page_50: '50/página',
        per_page_100: '100/página',
        select_language: 'Selecionar idioma',
        today: 'Hoje',
        tomorrow: 'Amanhã',
        dashboard_load_error: 'Não foi possível carregar o painel. Por favor, tente novamente.',
        enum_grade: {
            red: 'Vermelho',
            amber: 'Âmbar',
            yellow: 'Amarelo',
            green: 'Verde',
        },
        enum_severity: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Baixo',
        },
        enum_priority: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Baixo',
        },
        enum_status: {
            pending: 'Pendente',
            in_progress: 'Em andamento',
            completed: 'Concluído',
            blocked: 'Bloqueado',
            escalated: 'Escalado',
            cancelled: 'Cancelado',
            overdue: 'Atrasado',
            not_started: 'Não iniciado',
            new: 'Novo',
            open: 'Aberto',
            closed: 'Fechado',
            acknowledged: 'Reconhecido',
            resolved: 'Resolvido',
        },
        enum_department: {
            unassigned: 'Não atribuído',
        },
        enum_form_category: {
            assessment: 'Avaliação',
            assessment_report: 'Relatório de Avaliação',
            dpia: 'DPIA',
            authority_submission: 'Envio de autoridade',
            cross_border_transfer: 'Transferência transfronteiriça',
            vendor: 'Fornecedor',
            breach: 'Violação',
            consent: 'Consentimento',
            governance: 'Governança',
            register: 'Cadastre-se',
            monitoring: 'Monitoramento',
            dpa: 'APD',
            dsr: 'DSR',
            privacy: 'Privacidade',
            incident: 'Incidente',
            org: 'Organização',
            sector: 'Setor',
            classification: 'Classificação',
            discovery: 'Descoberta',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Solicitações de direitos',
            assessments: 'Avaliações',
            incidents: 'Incidentes',
            incident_ops: 'Operações de incidentes',
            monitoring: 'Monitoramento',
            vendor_risk: 'Risco do fornecedor',
            governance: 'Governança',
            deletion_orchestration: 'Exclusão',
            access_review: 'Revisão de acesso',
            consent: 'Consentimento',
            consent_properties: 'Propriedades de consentimento',
            forms: 'Formulários',
            localization: 'Localização',
            program_governance: 'Governança do Programa',
            privacy_scanner: 'Verificador de privacidade',
            classification: 'Classificação',
            discovery: 'Descoberta',
            manual: 'Manuais',
            data_mapping: 'Mapeamento de dados',
        },
        enum_risk_level: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Baixo',
        },
        enum_request_type: {
            all: 'All',
            access: 'Acesso (DSAR)',
            deletion: 'Exclusão',
            correction: 'Correção',
            objection: 'Objeção',
            portability: 'Portabilidade',
            restriction: 'Restrição',
            consent_withdrawal: 'Retirada de consentimento',
            protective_measures: 'Medidas de proteção',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'Inglês',
            vi: 'Vietnamita',
            all: 'Ambos',
        },
        enum_event_type: {
            case_created: 'Caso criado',
            status_changed: 'Status alterado',
            case_assigned: 'Caso atribuído',
            note_added: 'Nota adicionada',
            attachment_added: 'Anexo adicionado',
            attachment_deleted: 'Anexo excluído',
            reference_added: 'Referência adicionada',
            checklist_updated: 'Lista de verificação atualizada',
            identity_verified: 'Identidade verificada',
            closure_summary_added: 'Resumo de encerramento',
            approval_granted: 'Aprovação concedida',
            template_applied: 'Modelo aplicado',
            export_generated: 'Exportação gerada',
            sla_warning: 'SLA Aviso',
            sla_overdue: 'SLA Vencido',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Exportação falhada',
        you: 'You',
        export_failed: 'A exportação falhou. Por favor, tente novamente.',
        filters: 'Filtros',
        failed: 'Falhado',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Removido',
        draft: 'Rascunho',
        active: 'Ativo',
        inactive: 'Inativo',
        pending: 'Pendente',
        in_progress: 'Em Curso',
        completed: 'Concluído',
        archived: 'Arquivado',
        published: 'Publicado',
        approved: 'Aprovado',
        rejected: 'Rejeitado',
        cancelled: 'Cancelado',
        blocked: 'Bloqueado',
        deferred: 'Adiado',
        not_started: 'Não Iniciado',
        dismissed: 'Dispensado',
        failed: 'Falhado',
        in_review: 'Em Rever',
        deprecated: 'Obsoleto',
        scheduled: 'Agendado',
        deploying: 'A implementar',
        deployed: 'Implementado',
        paused: 'Em pausa',
        rolled_back: 'Revertido',
        received: 'Recebido',
        processing: 'Tratamento',
        under_assessment: 'Em avaliação',
        implemented: 'Implementado',
        pending_approval: 'Aprovação pendente',
        pending_deletion: 'Pendente Eliminação',
        on_hold: 'Em espera',
        deleted: 'Eliminado',
        expired: 'Expirado',
        outdated: 'Desatualizado',
        drifted: 'Desviado',
        decommissioned: 'Descomissionado',
        match: 'Correspondência',
        different: 'Diferente',
        missing: 'Em falta',
        confirmed: 'Confirmado',
        revoked: 'Revogado',
        escalated: 'Escalado',
        system: 'Sistema',
        custom: 'Personalizado',
        warning: 'Aviso',
        blocking: 'Bloqueante',
        analyst: 'Analista',
        viewer: 'Visualizador',
        daily: 'Diário',
        weekly: 'Semanal',
        policies: 'Políticas',
        denied: 'Negado',
        verified: 'Verificado',
        detected: 'Detectado',
        ignored: 'Ignorado',
        open: 'Abrir',
        resolved: 'Resolvido',
        suspended: 'Suspenso',
        retired: 'Aposentado',
        review: 'Revisão',
        generated: 'Gerado',
        exported: 'Exportado',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Baixo',
        medium: 'Médio',
        high: 'Alto',
        critical: 'Crítico',
    },
    sensitivity: {
        standard: 'Padrão',
        sensitive: 'Sensível',
        special_category: 'Categoria Especial',
        badge_sensitive: 'Sensível',
    },
    change_types: {
        policy_update: 'Política Atualizar',
        new_binding: 'Novo Binding',
        remove_binding: 'Remover vinculação',
        configuration_change: 'Alteração de configuração',
        banner_update: 'Banner Atualizar',
    },
    comparison_types: {
        full: 'Completo',
        banner_only: 'Banner Apenas',
        purposes_only: 'Finalidades Apenas',
        legal_bases_only: 'Apenas bases jurídicas',
        consent_settings: 'Consentimento Definições',
    },
    lifecycle_stages: {
        active: 'Ativo',
        archived: 'Arquivado',
        pending_deletion: 'Pendente Eliminação',
        deleted: 'Eliminado',
        on_hold: 'Em espera',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Dados pessoais',
        consent_record: 'Consentimento Registo',
        user_account: 'Utilizador Conta',
        audit_log: 'Auditoria Registo',
        document: 'Documento',
        backup: 'Cópia de segurança',
    },
    risk_categories: {
        data_breach: 'Dados Violação',
        unauthorized_access: 'Acesso não autorizado',
        data_loss: 'Perda de dados',
        regulatory_non_compliance: 'Não conformidade regulamentar',
        third_party_risk: 'Third-Party Risco',
        operational: 'Operacional',
        technical: 'Técnico',
        legal: 'Jurídico',
    },
    mitigation_types: {
        technical: 'Técnico',
        organizational: 'Organizacional',
        contractual: 'Contratual',
        procedural: 'Processual',
    },
    safeguard_types: {
        encryption_in_transit: 'Criptografia em trânsito',
        encryption_at_rest: 'Criptografia em repouso',
        access_controls: 'Controles de acesso',
        contractual_clauses: 'Cláusulas Contratuais',
        data_minimization: 'Minimização de dados',
        pseudonymization: 'Pseudonimização',
        audit_rights: 'Direitos de auditoria',
        breach_notification: 'Notificação de violação',
        data_localization: 'Localização de dados',
        other: 'Outro',
    },
    scoring_methods: {
        weighted_average: 'Média ponderada',
        simple_average: 'Média simples',
        maximum: 'Máximo',
        manual: 'Manuais',
    },
    dpa_status: {
        not_required: 'Não Necessário',
        required: 'Necessário',
        in_progress: 'Em Curso',
        completed: 'Concluído',
        signed: 'Assinado',
    },
    disposal_methods: {
        anonymize: 'Anonimizar',
        pseudonymize: 'Pseudonimizar',
        aggregate: 'Agregar',
        delete: 'Eliminar',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Painel de Controlo',
        no_data: 'Sem dados disponíveis no painel de controlo.',
        no_data_heading: 'Ainda sem dados em tempo real',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Cobertura de consentimento',
        ctia_filings: 'Declarações CTIA',
        next_dpia_review: 'Próxima revisão de DPIA',
        next_review_none: 'Nenhuma agendada',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Declarações MPS de incidente',
        compliance_score: 'Pontuação de Conformidade',
        grade: 'Classificação',
        no_score: 'Ainda não foi calculada a pontuação de conformidade.',
        rights_requests: 'Pedidos de Direitos',
        open: 'Abertos',
        sla_breached: 'SLA Incumprido',
        sla_approaching: 'SLA Próximo do Prazo',
        closed_month: 'Encerrados (Mês)',
        incidents: 'Incidentes e Violações',
        active: 'Ativos',
        breaches: 'Violações',
        overdue_tasks: 'Tarefas em Atraso',
        breach_72h_notice: 'O prazo de notificação regulatória de 72 horas se aplica a violações ativas',
        recommendations: 'Recomendações',
        take_action: 'Tome uma atitude',
        drift_events: 'Desvios e Aprovações',
        open_events: 'Eventos Abertos',
        pending_approvals: 'Aprovações Pendentes',
        monitoring: 'Tarefas de Monitorização',
        programs: 'Programas',
        overdue: 'Em Atraso',
        pending: 'Pendentes',
        ric_cases: 'Casos RIC',
        coverage: 'Cobertura de Dados',
        systems: 'Sistemas',
        activities: 'Atividades',
        vendors: 'Fornecedores',
        dpias: 'AIPDs',
        open_gaps: 'lacunas abertas',
        view_as_table: 'Ver como tabela',
        view_as_chart: 'Ver como grafico',
        table_metric_header: 'Metrica',
        table_value_header: 'Valor',
        announce_chart_view: 'A mostrar vista de grafico',
        announce_table_view: 'A mostrar vista de tabela',
        section_core_metrics: 'Metricas principais',
        section_task_management: 'Gestao de tarefas',
        section_compliance_insights: 'Perspetivas de conformidade',
        configure_dashboard: 'Configurar painel',
        config_col_active: 'Ativo',
        config_col_widget: 'Widget',
        collapse_section: 'Recolher',
        expand_section: 'Expandir',
        reset_to_defaults: 'Repor predefinições',
        preferences_saved: 'Preferências do painel guardadas',
        preferences_reset: 'Painel reposto para predefinições do cargo',
        preferences_load_error: 'Falha ao carregar preferências do painel',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Pontuação média do sistema',
        factor_avg_vendor_score: 'Pontuação média do fornecedor',
        factor_processing_coverage: 'Cobertura da atividade de processamento',
        factor_ropa_completeness: 'Completude RoPA',
        factor_drift_penalty: 'Penalidade de eventos de drift',
        rec_improve_system_gov: 'Melhore a documentação de governança do sistema',
        rec_improve_vendor_gov: 'Melhore a documentação de governança do fornecedor',
        rec_document_processing: 'Atividades de processamento de documentos para sistemas descobertos',
        rec_generate_ropa: 'Gerar registros RoPA para atividades de processamento ativas',
        rec_resolve_drift: 'Resolva eventos abertos de desvio críticos e de alta gravidade',
        score_good: 'Bom',
        score_fair: 'Razoável',
        score_poor: 'Fraco',
        score_critical: 'Crítico',
        drag_to_reorder: 'Arraste para reordenar',
        widget_moved: 'Widget movido',
        press_space_to_grab: 'Prima Espaço para agarrar, teclas de seta para mover, Espaço para largar',
        reorder_instructions: 'Use arrastar e largar ou atalhos de teclado para reordenar os widgets',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Visão geral das tarefas',
            pending: 'Pendente',
            in_progress: 'Em andamento',
            overdue: 'Atrasado',
            done_7d: 'Concluído (7d)',
            by_priority: 'Por prioridade',
            view_all_tasks: 'Ver todas as tarefas',
            upcoming_tasks: 'Próximas tarefas (próximos 7 dias)',
            no_upcoming: 'Nenhuma tarefa a ser entregue nos próximos 7 dias.',
            view_calendar: 'Ver calendário',
            overdue_alerts: 'Atrasado',
            no_overdue: 'Sem tarefas atrasadas. Tudo está no caminho certo.',
            assigned_to: 'Atribuído a',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Ver todos os atrasos',
            notifications: 'Notificações recentes',
            unread: 'unread',
            no_notifications: 'Sem notificações. Vocês estão todos atualizados!',
            view_all_notifications: 'Ver todas as notificações',
            load_error: 'Falha ao carregar dados.',
            day_late: 'um dia atrasado',
            days_late_plural: 'dias atrasado',
            due_today: 'Vencimento hoje',
            just_now: 'agora mesmo',
            minutes_short: 'eu',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Estatísticas entre organizações',
            by_organization: 'Por organização',
            overdue_across_orgs: 'tarefas atrasadas nas organizações',
            overdue_count_label: '{count} overdue tasks',
            total: 'Total',
            tasks: 'tasks',
            organization: 'Organização',
            current_org: 'Organização Atual',
            all_orgs: 'Todas as organizações',
            all_organizations: 'Todas as organizações',
            filter_by_org: 'Filtrar por organização',
            cross_org: 'Entre organizações',
            empty_calendar: 'Nenhuma tarefa para este período. Crie sua primeira tarefa ou verifique seus filtros.',
            empty_my_tasks: 'Você não tem tarefas atribuídas. Ótimo trabalho!',
            empty_overdue: 'Sem tarefas atrasadas. Tudo está no caminho certo.',
            empty_notifications: 'Sem notificações. Vocês estão todos atualizados!',
            empty_workflows: 'Nenhum fluxo de trabalho configurado. Procure modelos para começar.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'As Minhas Tarefas',
            no_tasks: 'Nenhuma tarefa atribuída a si.',
            load_error: 'Falha ao carregar tarefas.',
            overdue: 'em atraso',
            remaining: 'restante',
            view_all: 'Ver Todas as Tarefas',
        },
        my_section_status_widget: {
            title: 'Estado das Minhas Secções',
            overall_completion: 'Geral',
            sections_count: 'Secções',
            no_sections: 'Nenhuma secção atribuída ao seu departamento.',
            load_error: 'Falha ao carregar o estado das secções.',
            department_tasks_title: 'Tarefas do departamento',
            no_department_tasks: 'O seu departamento não tem tarefas.',
            department_tasks_error: 'Falha ao carregar as tarefas do departamento.',
            no_department: 'Não está associado a nenhum departamento.',
        },
        revision_requests_widget: {
            title: 'Pedidos de Revisão',
            no_revisions: 'Sem pedidos de revisão. Todas as secções estão no caminho certo.',
            load_error: 'Falha ao carregar os pedidos de revisão.',
        },
        consent_summary: {
            title: 'Consentimento',
            active: 'Ativo',
            withdrawn_30d: 'Retirado (30d)',
            expiring_7d: 'Expirando (7d)',
        },
        transfer_assessments: {
            title: 'Avaliações de transferência',
            open: 'Abrir',
            approved: 'Aprovado',
            gaps_flagged: 'Lacunas sinalizadas',
        },
        breach_notifications: {
            title: 'Notificações de violação',
            deadline_72h: 'Prazo de 72h',
            submitted: 'Enviado',
            pending: 'Pendente',
        },
        authority_requests: {
            title: 'Solicitações de autoridade',
            pending: 'Pendente',
            overdue: 'Atrasado',
            responded_30d: 'Respondido (30d)',
        },
        data_subjects_widget: {
            title: 'Titulares dos dados',
            total: 'Total',
            high_risk: 'Alto risco',
            active_30d: 'Ativo (30d)',
        },
        audit_activity_summary: {
            title: 'Atividade de auditoria',
            events_today: 'Eventos hoje',
            events_7d: 'Eventos (7d)',
            unique_actors_30d: 'Atores únicos (30d)',
        },
        onboarding_progress: {
            title: 'Progresso da integração',
            completion_pct: 'Completo',
            status_label: 'Estado',
            current_step: 'Etapa atual',
            status: {
                in_progress: 'Em andamento',
                completed: 'Concluído',
                skipped: 'Ignorado',
                not_started: 'Não iniciado',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sector Overlay Reports Due [PT]',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filtrar por enquadramento',
            all_frameworks: 'Todos os enquadramentos',
            filing_lifecycle: 'Ciclo de vida de submissões',
            filing_pipeline: 'Pipeline de submissões',
            draft: 'Rascunho',
            internal_review: 'Revisão Interna',
            approved: 'Aprovado',
            submitted: 'Enviado',
            supplement_requested: 'Suplemento solicitado',
            resubmitted: 'Reenviado',
            accepted: 'Aceito',
            rejected: 'Rejeitado',
            closed: 'Fechado',
            deadline: 'Prazo',
            days_remaining: 'dias restantes',
            days_overdue: 'dias de atraso',
            no_filings: 'Sem submissões ativas.',
            department_consolidation: 'Consolidação de departamentos',
            departments_completed: 'Departamentos concluídos',
            departments_pending: 'Departamentos pendentes',
            departments_overdue: 'Departamentos em atraso',
            no_consolidation_cases: 'Sem casos multidepartamentais em curso.',
            vendor_risk: 'Risco de fornecedores e terceiros',
            pending_assessment: 'Avaliação pendente',
            overdue_questionnaires: 'Questionários em atraso',
            dpa_review_queue: 'Fila de revisão DPA',
            risk_distribution: 'Distribuição de Risco',
            total_vendors: 'Total de fornecedores',
            by_risk_level: 'Por nível de risco',
            no_vendor_data: 'Sem dados de risco de fornecedores disponíveis.',
            discovery_summary: 'Descoberta de dados',
            discovery_classification: 'Descoberta e classificação de dados',
            active_scans: 'Análises ativas',
            labels_pending: 'Etiquetas com revisão pendente',
            exceptions_unresolved: 'Exceções não resolvidas',
            pending_review: 'Revisão pendente',
            exceptions: 'Exceções',
            approved_labels: 'Etiquetas aprovadas',
            total_classified: 'Total classificado',
            no_discovery_data: 'Sem dados de descoberta disponíveis.',
            ai_governance: 'Governança de sistemas de IA',
            systems_inventory: 'Sistemas em inventário',
            systems_in_inventory: 'Sistemas no inventário',
            classification_breakdown: 'Divisão de classificação',
            dossier_completion: 'Conclusão do dossiê',
            by_classification: 'Por classificação',
            labeling_status: 'Estado de etiquetagem',
            not_started: 'Não iniciado',
            no_ai_data: 'Sem dados de governança de IA disponíveis.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Resposta a violações e incidentes',
            active_breaches: 'Violações ativas',
            notification_status: 'Estado de notificação',
            remediation_progress: 'Progresso de remediação',
            supplement_loops: 'Ciclos de suplemento',
            no_breach_data: 'Sem casos de violação ativos.',
            audit_readiness: 'Prontidão para auditorias e avaliações',
            assessments: 'Avaliações',
            gap_findings: 'Achados de lacunas',
            evidence_completion: 'Conclusão de evidências',
            export_ready: 'Pronto para exportar',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Não pronto',
            no_audit_data: 'Sem dados de prontidão de auditoria disponíveis.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Ciberseguranca 2018',
            framework_label_vn_ecommerce: 'VN Comercio eletronico 2023',
            framework_label_vn_telecom: 'VN Telecomunicacoes 2023',
            framework_label_vn_ai: 'VN Lei IA 2025',
            framework_label_gdpr: 'RGPD',
            framework_label_unspecified: 'Não especificado',
            my_department: 'Meu departamento',
            my_tasks: 'Minhas tarefas',
            section_status: 'Estado da secção',
            revision_requests: 'Pedidos de revisão',
            completion: 'Conclusão',
            reason: 'Motivo',
            requested_at: 'Solicitado em',
            no_tasks_assigned: 'Sem tarefas atribuídas.',
            no_sections: 'Sem secções atribuídas ao seu departamento.',
            no_revisions: 'Sem pedidos de revisão.',
            load_error: 'Falha ao carregar dados.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instâncias',
            credentials: 'Cofre de Credenciais',
            event_bus: 'Barramento de Eventos',
            definitions: 'Definições',
            brds: 'Captura de BRD',
            security_reviews: 'Revisões de Segurança',
            poc_scorecards: 'Cartões de Pontuação POC',
            automation_planning: 'Planeamento automatização',
            system_inventory: 'Inventário de sistemas',
            traceability_matrix: 'Matriz rastreabilidade',
            gap_register: 'Registo de lacunas',
        },
        instances: {
            title: 'Instâncias de Conectores',
            page_title: 'Conectores',
            add: 'Adicionar Instância',
            test: 'Testar Ligação',
            sync_now: 'Sincronizar Agora',
            new_instance: 'Novo Connector Instance',
            no_instances: 'Nenhum connector instances encontrado',
            instance_count: '{count} instância(s)',
            confirm_delete: 'Eliminar a instância do conector "{name}"?',
            all_connector_types: 'Todos os tipos de conector',
            select_connector: 'Selecione connector...',
            fetch_error: 'Falha ao fetch connector instances. Por favor, tente novamente.',
            fetch_definitions_error: 'Falha ao fetch connector definitions. Por favor, tente novamente.',
            create_error: 'Falha ao create connector instance. Por favor, tente novamente.',
            test_error: 'Falha ao test connector. Por favor, tente novamente.',
            status_error: 'Falha ao atualizar o estado do conector. Por favor, tente novamente.',
            delete_error: 'Falha ao delete connector instance. Por favor, tente novamente.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status atualizado',
            reactivate: 'Reativar',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Cofre de Credenciais',
            page_title: 'Credenciais do conector',
            add: 'Adicionar Credencial',
            rotate: 'Rodar',
            expired: 'Expirado',
            expiring: 'A Expirar Brevemente',
            orphaned: 'Órfão',
            new_credential: 'Novo Credential',
            no_credentials: 'Nenhum credentials encontrado',
            no_expiry: 'Sem expiração',
            linked_connectors: 'Conectores ligados',
            expires_at: 'Expira Em',
            value_label: 'Valor * (apenas escrita)',
            confirm_delete_title: 'Confirmar exclusão',
            confirm_delete_message: 'Excluir esta credencial? Os conectores vinculados perderão o acesso.',
            rotate_title: 'Rotacionar credencial',
            new_credential_value: 'Novo valor de credencial',
            fetch_error: 'Falha ao load credentials. Por favor, tente novamente.',
            health_error: 'Falha ao load health. Por favor, tente novamente.',
            create_error: 'Falha ao create credential. Por favor, tente novamente.',
            rotate_error: 'Falha ao rotate credential. Por favor, tente novamente.',
            delete_error: 'Falha ao delete credential. Por favor, tente novamente.',
        },
        event_bus: {
            title: 'Barramento de Eventos',
            page_title: 'Barramento de eventos',
            events: 'Eventos',
            subscriptions: 'Subscrições',
            dlq: 'Fila de Mensagens Mortas',
            publish: 'Publicar Evento',
            retry: 'Tentar Novamente',
            dismiss: 'Dispensar',
            search_events: 'Pesquisar eventos...',
            filter_event_type: 'Filtrar por tipo de evento...',
            event_type: 'Evento Tipo',
            source_module: 'Módulo de origem',
            timestamp: 'Marca temporal',
            delivery_status: 'Estado de entrega',
            correlation_id: 'ID de correlação',
            tracking_id: 'ID de rastreamento',
            delivery_status_hint: 'O status de entrega é atualizado automaticamente conforme os eventos são processados.',
            no_events: 'Nenhum events encontrado',
            total_with_count: '{count} no total',
            prev: 'Ant.',
            new_subscription: '+ Novo Subscrição',
            subscriber_type: 'Tipo de subscritor',
            subscriber_id: 'ID do subscritor',
            event_type_pattern: 'Evento Tipo Pattern',
            event_type_pattern_placeholder: 'p. ex. incident.*',
            filter_expression: 'Expressão de filtro',
            filter_expression_placeholder: 'Filtro opcional',
            subscriber_type_hint: 'Escolha como os eventos serão entregues: webhook envia solicitações HTTP, e-mail envia notificações, fila roteia para uma fila de mensagens.',
            subscriber_id_hint: 'Insira o destino dos eventos. Para webhooks, use uma URL. Para e-mail, use um endereço. Para filas, use o nome da fila.',
            event_type_pattern_hint: 'Corresponda eventos por tipo usando curingas. Use * para corresponder a qualquer segmento (ex. incident.* corresponde a incident.created, incident.resolved).',
            filter_expression_hint: 'Opcional. Filtre eventos adicionalmente por conteúdo (ex. severity=high).',
            subscriber_id_placeholder_webhook: 'ex. https://seu-app.com/webhook',
            subscriber_id_placeholder_email: 'ex. alertas@sua-empresa.com',
            subscriber_id_placeholder_queue: 'ex. fila-eventos',
            event_pattern: 'Padrão de evento',
            filter: 'Filtrar',
            enabled: 'Ativado',
            delivery_stats: 'Estatísticas de entrega',
            no_subscriptions: 'Nenhum subscriptions encontrado',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} entregues / {failed} falhados',
            confirm_delete_subscription: 'Eliminar este subscription?',
            unresolved: 'Não resolvido',
            resolved: 'Resolvido',
            event_id: 'Evento ID',
            subscription_id: 'Subscrição ID',
            error_code: 'Código de erro',
            error_message: 'Mensagem de erro',
            attempts: 'Tentativas',
            last_failed: 'Último Falhou',
            no_dlq_entries: 'Nenhum dead letter entries encontrado',
            dlq_count: '{count} entrada(s) no total',
            fetch_events_error: 'Falha ao fetch events. Por favor, tente novamente.',
            fetch_subscriptions_error: 'Falha ao fetch subscriptions. Por favor, tente novamente.',
            fetch_dlq_error: 'Falha ao fetch dlq entries. Por favor, tente novamente.',
            create_subscription_error: 'Falha ao create subscription. Por favor, tente novamente.',
            toggle_subscription_error: 'Falha ao toggle subscription. Por favor, tente novamente.',
            delete_subscription_error: 'Falha ao delete subscription. Por favor, tente novamente.',
            retry_error: 'Tentar novamente falhou',
            dismiss_error: 'Descarte falhado',
            event_published: 'Evento publicado',
            publish_event_error: 'Não foi possível publicar o evento. Tente novamente.',
            priority_label: 'Prioridade',
            priority_low: 'Baixa',
            priority_normal: 'Normais',
            priority_high: 'Alta',
            payload_helper_text: 'Insira os dados do evento em formato JSON. Exemplo: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Subscrição criada',
            subscription_toggled: 'Subscrição alternada',
            subscription_deleted: 'Subscrição eliminada',
            dlq_retried: 'Mensagem DLQ reprocessada',
            dlq_dismissed: 'Mensagem DLQ descartada',
        },
        definitions: {
            title: 'Definições do conector',
            page_title: 'Definições de conectores',
            add: 'Adicionar definição',
            new_definition: 'Nova definição de conector',
            edit_definition: 'Editar definição do conector',
            no_definitions: 'Nenhuma definição de conector encontrada',
            definition_count: '{count} definições',
            all_categories: 'Todas as categorias',
            select_category: 'Selecione uma categoria...',
            capabilities: 'Capacidades',
            capabilities_hint: 'Capacidades (separadas por vírgula)',
            capabilities_placeholder: 'por exemplo ler, escrever, sincronizar',
            created: 'Definição do conector criada',
            updated: 'Definição do conector atualizada',
            fetch_error: 'Falha ao buscar definições do conector. Por favor, tente novamente.',
            create_error: 'Falha ao criar a definição do conector',
            update_error: 'Falha ao atualizar a definição do conector',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'Captura de BRD',
            page_title: 'Captura BRD',
            create_brd: 'Criar BRD',
            brd_title: 'Título do BRD',
            description: 'Descrição',
            requirements: 'Requisitos',
            status: 'Estado',
            stakeholders: 'Partes interessadas',
            connector_label: 'Conector',
            select_connector: 'Selecionar um conector...',
            approval: 'Aprovação',
            no_brds_found: 'Nenhum BRD encontrado',
            decision_records: 'Registos de decisões',
            create_decision: 'Criar decisão',
            alternatives_considered: 'Alternativas consideradas',
            alternative_name: 'Nome da alternativa',
            reason_rejected: 'Motivo de rejeição',
            add_alternative: 'Adicionar alternativa',
            remove_alternative: 'Remover alternativa {name}',
            alternatives: 'Alternativas',
            rationale: 'Justificação',
            selected_connector: 'Conector selecionado',
            comma_separated_names: 'Nomes separados por vírgulas',
            brd_created: 'BRD criado',
            decision_record_created: 'Registro de decisão criado',
            no_decision_records_found: 'Nenhum registro de decisão encontrado',
            aria_brds_table: 'BRDs',
            aria_decision_records_table: 'Registros de decisão',
            aria_approve: 'Aprovar {name}',
            aria_archive: 'Arquivar {name}',
            brd_title_help: 'Um nome curto e legível para este BRD – por ex. "Integração de tickets Jira", "lançamento do Okta SSO".',
            brd_title_placeholder: 'por exemplo Integração de tíquetes Jira',
            description_help: 'O problema comercial que esse conector resolve. Que resultado você espera quando estiver no ar?',
            description_placeholder: 'por exemplo Automatize a criação de tickets de incidente a partir de alertas de monitoramento para que a chamada seja paginada em 60 segundos.',
            connector_help: 'Opcional. Escolha um conector para o qual este BRD se destina – deixe em branco se o conector ainda não tiver sido escolhido.',
            requirements_help: 'Lista separada por vírgulas de recursos essenciais - por exemplo. "Login OAuth2, entrega de webhook, exportação de auditoria" .',
            requirements_placeholder: 'Login OAuth2, entrega de webhook, exportação de auditoria',
            stakeholders_help: 'Nomes separados por vírgula ou e-mails de pessoas que devem assinar.',
            selected_connector_help: 'Escolha o conector ao qual esta decisão se aplica.',
            rationale_help: 'Por que este conector foi escolhido. Cubra os fatores decisivos para que um futuro leitor entenda a chamada.',
            rationale_placeholder: 'por exemplo Escolhemos o Jira porque ele já possui nosso histórico de tickets e a equipe é treinada nele.',
            alternatives_considered_help: 'Outros conectores avaliados e porque não foram escolhidos. Adiciona defensibilidade para auditoria.',
            alternative_name_placeholder: 'por exemplo Serviço agora',
            reason_rejected_placeholder: 'por exemplo Custo 3x maior, sem vantagem para nosso caso de uso',
        },
        security_reviews: {
            title: 'Revisões de Segurança',
            page_title: 'Revisões de segurança',
            create_review: 'Criar revisão',
            connector_name: 'Nome do conector',
            connector_label: 'Conector',
            select_connector: 'Selecionar um conector...',
            reviewer: 'Revisor',
            select_reviewer: 'Selecionar um revisor...',
            findings: 'Conclusões (uma por linha)',
            risk_level: 'Nível de risco',
            risk_level_placeholder: 'p. ex. alto, médio, baixo',
            notes: 'Notas',
            review_status: 'Estado da revisão',
            checklist: 'Lista de verificação',
            privacy_impact: 'Impacto na privacidade',
            approval_gate: 'Porta de aprovação',
            no_reviews_found: 'Nenhuma revisão encontrada',
            review_created: 'Revisão de segurança criada',
            create_control_mapping: 'Criar mapeamento de controle',
            control_mapping_created: 'Mapeamento de controle criado',
            connector_id_label: 'ID do conector',
            control_id_label: 'ID do controle',
            control_name_label: 'Nome do controle',
            aria_approve: 'Aprovar {name}',
            aria_reject: 'Rejeitar {name}',
            scan_imported: 'Scan importado com sucesso',
            import_scan: 'Importar scan',
            scan_format: 'Formato do scan',
            scan_data: 'Dados do scan',
            import_results: 'Resultados da importação',
            recommendations: 'Recomendações',
            auto_populated_from: 'Preenchido automaticamente de {tool}',
            manual_entry: 'Entrada manual',
            automation_type: 'Tipo de automação',
            select_automation_type: 'Selecione o tipo de automação...',
            automation_full: 'Completo — o conector automatiza totalmente esse controle',
            automation_partial: 'Parcial — o conector contribui com evidências; os humanos ainda agem',
            automation_monitoring: 'Apenas monitoramento – o conector observa, não impõe',
            control_id_help: 'Use o código do seu esquema de conformidade (por exemplo, ISO-27001-A.12.1, SOC2-CC6.1 ou seu ID de controle interno).',
            control_name_help: 'Nome curto e legível (por exemplo, "Restrição de acesso à rede").',
            description_label: 'Descrição',
            load_sarif_example: 'Carregar exemplo SARIF',
            load_json_example: 'Carregar exemplo personalizado JSON',
            scan_format_help: 'SARIF é o padrão OASIS emitido pela maioria dos scanners (Snyk, CodeQL, Semgrep). Use JSON personalizado para ferramentas que não emitem SARIF.',
            scan_data_help: 'Cole a saída completa da verificação, carregue um arquivo .sarif/.json ou clique no botão Carregar exemplo para ver a forma.',
            upload_file_label: 'Carregar arquivo .sarif ou .json',
            invalid_scan_json: 'Não foi possível analisar os dados da verificação como JSON. Verifique se há erros de sintaxe próximos ao caractere destacado.',
            save_as_review: 'Salvar como revisão de segurança',
            discard_preview: 'Descartar visualização',
            preview_hint: 'Esta é uma prévia – nada foi salvo ainda. Clique em “Salvar como revisão de segurança” para criar uma revisão pendente pré-preenchida com esses valores.',
            reviewer_required_for_save: 'Escolha um revisor para salvar esta verificação como uma revisão de segurança.',
        },
        poc_scorecards: {
            title: 'Cartões de Pontuação POC',
            page_title: 'Scorecards POC',
            create_scorecard: 'Criar cartão de pontuação',
            connector_label: 'Conector',
            select_connector: 'Selecionar um conector...',
            vendor_name: 'Nome do fornecedor',
            recommendation: 'Recomendação',
            recommendation_placeholder: 'p. ex. Prosseguir, Não prosseguir, Necessita avaliação adicional',
            criteria: 'Critérios de avaliação',
            criterion_name: 'Nome do critério',
            score: 'Pontuação',
            max_score: 'Pontuação máxima',
            notes: 'Notas',
            add_criterion: 'Adicionar critério',
            remove_criterion: 'Remover critério {name}',
            total_score: 'Pontuação total',
            api_quality: 'Qualidade da API',
            reliability: 'Fiabilidade',
            support_quality: 'Qualidade do suporte',
            cost_score: 'Pontuação de custo',
            overall_score: 'Pontuação global',
            evidence: 'Evidência',
            no_scorecards_found: 'Nenhum cartão de pontuação encontrado',
            scorecard_created: 'Scorecard POC criado',
            connector_help: 'Escolha o conector ao qual esta avaliação POC se destina.',
            vendor_name_help: 'O fornecedor ou produto que está sendo avaliado - por ex. "Okta", "Datadog", "Floco de neve".',
            vendor_name_placeholder: 'por exemplo Okta, Datadog, floco de neve',
            recommendation_help: 'Sua chamada final após a avaliação. Seja breve e decisivo.',
            criteria_help: 'Pontue o fornecedor em cada critério de avaliação. Adicione uma linha por critério (API qualidade, confiabilidade, suporte, custo, etc.).',
            criterion_name_help: 'O que você está pontuando - por ex. "API Qualidade", "Confiabilidade", "Suporte", "Custo".',
            criterion_name_placeholder: 'por exemplo API Qualidade',
            score_help: 'Pontuação atribuída para este critério. Não deve exceder a pontuação máxima.',
            max_score_help: 'Pontuação máxima disponível para este critério. Use a mesma escala nas linhas para que os totais sejam comparáveis.',
            notes_help: 'Opcional. Adicione evidências, advertências ou links – qualquer coisa que explique a pontuação.',
        },
        common_labels: {
            aria_filter_instances: 'Filtrar instâncias de conectores',
            aria_filter_definitions: 'Filtrar definições de conectores',
            aria_filter_credentials: 'Filtrar credenciais',
            aria_filter_events: 'Filtrar eventos',
            aria_filter_dlq: 'Filtrar fila de mensagens mortas',
            aria_instance_summary: 'Resumo da instância do conector',
            aria_active_connectors: 'Conectores ativos',
            aria_error_connectors: 'Conectores com erro',
            aria_credential_health: 'Resumo da saúde das credenciais',
            aria_items_per_page: 'Itens por página',
            show_password: 'Mostrar senha',
            hide_password: 'Ocultar senha',
            payload_json_label: 'Carga útil (JSON)',
            payload_invalid_json: 'Carga útil deve ser JSON válido',
            placeholder_event_type: 'ex. incident.created',
            placeholder_source_module: 'ex. monitoring',
            placeholder_version: 'ex. 1.0',
        },
        statuses: {
            configuring: 'Configurando',
            testing: 'Teste',
            active: 'Ativo',
            paused: 'Pausado',
            error: 'Erro',
            disabled: 'Desativado',
            draft: 'Rascunho',
            approved: 'Aprovado',
            archived: 'Arquivado',
            pending: 'Pendente',
            rejected: 'Rejeitado',
        },
        system_types: {
            application: 'Aplicação',
            database: 'Banco de dados',
            service: 'Service',
            infrastructure: 'Infraestrutura',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Local',
            cloud: 'Nuvem',
            hybrid: 'Híbrido',
            managed: 'Gerenciado',
        },
        system_statuses: {
            active: 'Ativo',
            decommissioned: 'Descomissionado',
            planned: 'Planejado',
            under_review: 'Em revisão',
        },
        automation_statuses: {
            planning: 'Planejamento',
            in_progress: 'Em andamento',
            completed: 'Concluído',
            on_hold: 'Em espera',
        },
        gap_types: {
            functional: 'Funcional',
            technical: 'Técnico',
            security: 'Segurança',
            compliance: 'Conformidade',
            process: 'Processo',
        },
        severity_labels: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Baixo',
        },
        gap_statuses: {
            open: 'Aberto',
            in_progress: 'Em andamento',
            mitigated: 'Mitigado',
            closed: 'Fechado',
            accepted: 'Aceito',
        },
        verification_statuses: {
            not_started: 'Não iniciado',
            in_progress: 'Em andamento',
            verified: 'Verificado',
            failed: 'Falhou',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Interno',
            email: 'E-mail',
            queue: 'Fila',
        },
        credential_types: {
            password: 'Senha',
            api_key: 'Chave API',
            oauth2_client: 'Cliente OAuth2',
            certificate: 'Certificado',
            token: 'Símbolo',
            ssh_key: 'Chave SSH',
            imap_credentials: 'Credenciais IMAP',
        },
        categories: {
            hr: 'RH',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Nuvem',
            security: 'Segurança',
            itsm: 'ITSM',
            custom: 'Personalizado',
        },
        delivery_statuses: {
            pending: 'Pendente',
            delivered: 'Entregue',
            partial: 'Parcial',
            failed: 'Falhou',
        },
        automation_planning: {
            title: 'Planeamento de automatização',
            page_title: 'Planeamento de automatização',
            program_name: 'Nome do programa',
            program_status: 'Estado',
            description: 'Descrição',
            linked_artifacts: 'Artefactos associados',
            create_program: 'Criar programa',
            edit_program: 'Editar programa',
            no_programs_found: 'Nenhum programa encontrado',
            program_created: 'Programa criado',
            program_updated: 'Programa atualizado',
            target_date: 'Data-alvo',
            owner: 'Proprietário',
            select_status: 'Selecionar estado...',
            aria_programs_table: 'Programas de automatização',
            aria_edit: 'Editar {name}',
            export_programs: 'Exportar programas',
            title_label: 'Título do programa',
            title_help: 'Um nome curto e legível para o programa de automação – por exemplo. "Lançamento do incidente do terceiro trimestre para Jira", "PDPL automação de solicitação de acesso".',
            title_placeholder: 'por exemplo Implementação do incidente no Jira no terceiro trimestre',
            description_help: 'O que este programa cobre e o resultado que você espera quando estiver no ar. Opcional, mas recomendado.',
            description_placeholder: 'por exemplo Conecte alertas de monitoramento ao Jira para que o plantão seja paginado em 60 segundos.',
            owner_label: 'Proprietário do programa',
            owner_help: 'A pessoa responsável por este programa. Eles receberão notificações sobre progresso e portões.',
            select_owner: 'Selecione um proprietário...',
            frameworks_in_scope: 'Estruturas no escopo',
            frameworks_in_scope_help: 'Estruturas de conformidade que este programa suporta. Ajuda a trilha de auditoria a mostrar quais regulamentações a automação é regida.',
            select_frameworks: 'Selecione estruturas...',
            status_draft: 'Rascunho',
            status_active: 'Ativo',
            status_completed: 'Concluído',
            status_archived: 'Arquivado',
            change_status: 'Alterar status',
            status_updated: 'Status atualizado',
            aria_change_status: 'Alterar status para {name}',
        },
        system_inventory: {
            title: 'Inventário de sistemas',
            page_title: 'Inventário de sistemas',
            system_name: 'Nome do sistema',
            system_type: 'Tipo de sistema',
            hosting: 'Alojamento',
            status: 'Estado',
            description: 'Descrição',
            create_system: 'Criar sistema',
            edit_system: 'Editar sistema',
            no_systems_found: 'Nenhum sistema encontrado',
            system_created: 'Sistema criado',
            system_updated: 'Sistema atualizado',
            select_type: 'Selecionar tipo...',
            select_hosting: 'Selecionar alojamento...',
            select_status: 'Selecionar estado...',
            owner: 'Proprietário',
            department: 'Departamento',
            data_categories: 'Categorias de dados',
            data_categories_placeholder: 'Separados por vírgulas, ex. PII, Financeiro, Saúde',
            jurisdiction: 'Jurisdição',
            jurisdiction_placeholder: 'ex. UE, EUA, Global',
            interfaces: 'Interfaces',
            interfaces_placeholder: 'Separados por vírgulas, ex. API REST, SFTP, JDBC',
            owner_placeholder: 'Selecione um proprietário responsável...',
            department_placeholder: 'Selecione a unidade organizacional responsável...',
            system_name_hint: 'Use o nome do sistema ou aplicação reconhecido pela empresa.',
            system_type_hint: 'Classifique o sistema para que os relatórios possam agrupar ativos semelhantes.',
            hosting_hint: 'Identifique onde o sistema corre para efeitos de residência, segurança e revisão de fornecedores.',
            status_hint: 'Defina o estado do ciclo de vida utilizado por revisões, exportações e evidências de auditoria.',
            owner_hint: 'Escolha o utilizador ativo responsável pela governação do sistema e pelas tarefas de acompanhamento.',
            department_hint: 'Utilize a unidade organizacional responsável pela operação ou governação deste sistema.',
            data_categories_hint: 'Liste as classes de dados processadas por este sistema, separadas por vírgulas.',
            jurisdiction_hint: 'Registe a principal jurisdição legal ou de alojamento para o mapeamento de conformidade.',
            interfaces_hint: 'Liste os caminhos de conector ou integração utilizados para trocar dados.',
            description_hint: 'Resuma o que o sistema faz e porque é importante para o mapa de dados.',
            owner_department_required: 'Proprietário e Departamento são obrigatórios.',
            aria_systems_table: 'Inventário de sistemas',
            aria_edit: 'Editar {name}',
            export_systems: 'Exportar sistemas',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Matriz de rastreabilidade',
            page_title: 'Matriz de rastreabilidade',
            brd_selector: 'Selecionar BRD',
            requirement: 'Requisito',
            linked_artifact: 'Artefacto associado',
            verification_status: 'Estado de verificação',
            verified_by: 'Verificado por',
            create_entry: 'Criar entrada',
            edit_entry: 'Editar entrada',
            no_entries_found: 'Nenhuma entrada encontrada',
            entry_created: 'Entrada criada',
            entry_updated: 'Entrada atualizada',
            select_brd: 'Selecionar um BRD...',
            select_status: 'Selecionar estado...',
            aria_matrix_table: 'Matriz de rastreabilidade',
            aria_edit: 'Editar entrada para {name}',
            export_matrix: 'Exportar matriz',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'ID do requisito',
        },
        gap_register: {
            title: 'Registo de lacunas',
            page_title: 'Registo de lacunas',
            gap_title: 'Título da lacuna',
            gap_type: 'Tipo de lacuna',
            severity: 'Gravidade',
            status: 'Estado',
            description: 'Descrição',
            remediation_plan: 'Plano de remediação',
            assigned_to: 'Atribuído a',
            due_date: 'Data de vencimento',
            create_gap: 'Criar lacuna',
            edit_gap: 'Editar lacuna',
            no_gaps_found: 'Nenhuma lacuna encontrada',
            gap_created: 'Lacuna criada',
            gap_updated: 'Lacuna atualizada',
            select_type: 'Selecionar tipo...',
            select_severity: 'Selecionar gravidade...',
            select_status: 'Selecionar estado...',
            aria_gaps_table: 'Registo de lacunas',
            aria_edit: 'Editar {name}',
            export_gaps: 'Exportar lacunas',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Vinculação de quadro',
            framework_linkage_placeholder: 'ex. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Âmbito do quadro',
            framework_scope_placeholder: 'ex. GDPR, PCI-DSS',
            procedure_linkage: 'Vinculação de procedimento',
            procedure_linkage_placeholder: 'ex. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Fonte regulatória',
            regulatory_source_placeholder: 'ex. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Mapeamento de controlos',
            control_mappings_section: 'Mapeamentos de controle',
            no_control_mappings: 'Ainda não há mapeamentos de controle. Clique em “Criar mapeamento de controle” para adicionar um.',
            automation_type_col: 'Automação',
        },
        gap_register_extra: {
            framework_filter: 'Quadro',
            framework_id: 'ID do quadro',
            framework_id_placeholder: 'ex. GDPR, ISO 27001',
            all_frameworks: 'Todos os quadros',
        },
        poc_scorecards_extra: {
            criteria_count: 'Critérios',
        },
        export_labels: {
            export_brds: 'Exportar BRDs',
            export_decision_records: 'Exportar registos de decisões',
            export_security_reviews: 'Exportar revisões de segurança',
            export_poc_scorecards: 'Exportar cartões POC',
            export_instances: 'Exportar instâncias',
            export_definitions: 'Exportar definições',
            export_credentials: 'Exportar credenciais',
            export_events: 'Exportar eventos',
            exporting: 'A exportar...',
            export_success: 'Exportação concluída',
            export_error: 'Falha na exportação',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Extrair obrigações',
            extracting: 'Extraindo…',
            results_heading: 'Obrigações extraídas ({count})',
            tokens_label: '≈ {count} tokens',
            no_obligations: 'Nenhum padrão de obrigação detectado neste texto contratual.',
            col_class: 'Classe',
            col_summary: 'Resumo (editável)',
            col_excerpt: 'Trecho da cláusula',
            col_due_date: 'Data de vencimento (editável)',
            col_priority: 'Prioridade',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Período de aviso',
            class_audit: 'Auditoria certa',
            class_insurance: 'Seguro',
            class_sla: 'Resposta SLA',
            class_breach: 'Notificação de violação',
            class_renewal: 'Janela de renovação',
            citations_label: 'Citações',
            accept_hint: 'Aceitar criará uma tarefa por obrigação (cap 8) com a prioridade + data de vencimento mostrada.',
            accept_label: 'Aceite obrigações',
            reject_label: 'Rejeitar relatório',
            resolve_summary: 'Resolução: {status} · {tasks} tarefa(s) criada(s)',
            error_generic: 'Não foi possível extrair obrigações. Tente novamente ou verifique o texto do contrato.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Rascunho do raciocínio',
            drafting: 'Elaboração de raciocínio…',
            results_heading: 'Raciocínio elaborado por AI ({risk_level})',
            tokens_label: '≈ {count} tokens',
            narrative_label: 'Narrativa de justificação',
            controls_heading: 'Controles de compensação',
            no_controls: 'Nenhum controle de compensação sugerido.',
            citations_label: 'Citações',
            accept_label: 'Aceite o raciocínio',
            reject_label: 'Rejeitar rascunho',
            accept_hint: 'Accept registra o raciocínio sobre o rascunho persistente para a linhagem IAT. A narrativa + controles não atualizam automaticamente o registro de aceitação – é necessário colar o operador.',
            resolve_summary: 'Resolução: {status}',
            error_generic: 'Não foi possível redigir o raciocínio. Tente novamente ou verifique o status de aceitação do risco.',
        },
        redline_assistant: {
            run_label: 'Redline com biblioteca',
            running: 'Correndo linha vermelha…',
            results_heading: 'Sugestões de linhas vermelhas ({count})',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'Não foram detectadas lacunas nas cláusulas padrão.',
            jurisdiction_label: 'Jurisdição',
            col_kind: 'Gentil',
            col_severity: 'Gravidade',
            col_title: 'Sugestão',
            col_excerpt: 'Trecho correspondente',
            col_apply: 'Aplicar',
            kind_missing: 'Padrão ausente',
            kind_weaker: 'Mais fraco que o padrão',
            kind_jurisdiction: 'Sobreposição de jurisdição',
            proposed_label: 'Texto proposto',
            citations_label: 'Citações',
            accept_label: 'Aceitar selecionado',
            reject_label: 'Rejeitar relatório',
            partial_label: 'Aplicar seleção',
            accept_hint: 'Aplicar registros de seleção das sugestões escolhidas no rascunho da linhagem IAT. Nenhuma edição automática é aplicada ao texto do contrato – o operador define a linha vermelha.',
            resolve_summary: 'Resolução: {status} · {count} sugestão(ões) aplicadas',
            error_generic: 'Não foi possível produzir uma linha vermelha. Tente novamente ou verifique o status do contrato.',
            clause_library_link: 'Gerenciar biblioteca de cláusulas',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Biblioteca de Cláusulas',
            add_label: 'Adicionar cláusula',
            edit_label: 'Editar',
            delete_label: 'Excluir',
            slug_label: 'Lesma',
            title_label: 'Título',
            body_label: 'Corpo',
            jurisdiction_label: 'Jurisdição',
            tags_label: 'Tags (separadas por vírgula)',
            must_contain_label: 'Marcadores obrigatórios (separados por vírgula)',
            severity_label: 'Gravidade quando ausente',
            no_clauses: 'Ainda não há cláusulas. Adicione um para propagar o assistente redline.',
            save_label: 'Salvar',
            cancel_label: 'Cancelar',
            load_error: 'Não foi possível carregar a biblioteca de cláusulas.',
            save_error: 'Não foi possível salvar a cláusula.',
            delete_error: 'Não foi possível excluir a cláusula.',
            delete_confirm: 'Excluir cláusula "{slug}"? Isto não pode ser desfeito.',
        },
        subprocessor_impact: {
            assess_label: 'Avalie o impacto',
            assessing: 'Avaliando o impacto…',
            results_heading: 'Impacto do subprocessador ({severity})',
            tokens_label: '≈ {count} tokens',
            risk_delta_label: 'Delta de risco',
            notification_heading: 'Notificação do cliente',
            subject_label: 'Assunto',
            deadline_label: 'Prazo de notificação',
            body_label: 'Corpo de notificação',
            required_fields_label: 'Campos da notificação (artigo 28.º, n.º 2)',
            mitigation_heading: 'Etapas de mitigação',
            citations_label: 'Citações',
            accept_label: 'Aceitar avaliação',
            reject_label: 'Rejeitar avaliação',
            accept_hint: 'Aceitar criará uma tarefa de notificação do cliente com o prazo SLA como data de vencimento.',
            resolve_summary: 'Resolução: {status}{task}',
            error_generic: 'Não foi possível avaliar esta alteração no subprocessador. Tente novamente ou verifique o registro de alterações.',
        },
        onboarding_evidence: {
            collect_label: 'Executar coleta de evidências AI',
            collecting: 'Coletando evidências…',
            results_heading: 'Coleta de evidências ({tier})',
            tokens_label: '≈ {count} tokens',
            findings_heading: 'Evidência existente',
            missing_heading: 'Artefatos desaparecidos',
            no_findings: 'Nenhuma evidência existente detectada.',
            no_missing: 'Não faltam artefatos obrigatórios para este nível de risco.',
            next_step_label: 'Próxima etapa',
            class_dpa: 'Contrato de Processamento de Dados',
            class_soc2: 'Relatório SOC 2',
            class_iso27001: 'Certificado ISO 27001',
            class_insurance: 'Apólice de seguro cibernético',
            class_incident_history: 'Incidente anterior',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Aceitar relatório',
            reject_label: 'Rejeitar relatório',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Não foi possível executar a coleta de evidências AI. Tente novamente ou verifique o status do caso de integração.',
        },
        no_permission_title: 'Permissões insuficientes',
        no_permission_message: 'Você não tem permissão para acessar o gerenciamento de riscos do fornecedor.',
        unknown_vendor: 'Fornecedor desconhecido',
        nav: {
            dashboard: 'Painel de riscos',
            scoring: 'Pontuação de riscos',
            questionnaires: 'Questionários',
            contracts: 'Contratos',
            gates: 'Portas de aprovação',
            reviews: 'Avaliações de fornecedores',
            signals: 'Sinais de risco',
            reassessments: 'Reavaliações',
            portfolio: 'Portfólio de riscos',
            acceptances: 'Aceitação de riscos',
            review_routing: 'Encaminhamento de revisões',
            onboarding_cases: 'Casos de integração',
            sub_processor_register: 'Registo de subcontratantes',
        },
        dashboard: {
            title: 'Painel de risco de fornecedores',
            page_title: 'Painel de risco do fornecedor',
            loading: 'A carregar dashboard data...',
            risk_tier_header: 'Nível de risco',
            count_header: 'Contar',
            percentage_header: 'Porcentagem',
            sections_nav: 'Seções de risco do fornecedor',
            urgent_expiry: 'Urgente: expira em 14 dias',
            no_previous_tier: 'Nenhum nível anterior',
            view_as_chart: 'Ver como gráfico',
            view_as_table: 'Ver como tabela',
            showing_chart: 'Exibindo vista de gráfico',
            showing_table: 'Exibindo vista de tabela',
            load_error: 'Falha ao load vendor risk dashboard. Por favor, tente novamente.',
            risk_distribution: 'Risco Distribution Visão geral',
            recent_tier_changes: 'Recente Nível Changes',
            no_tier_changes: 'Sem alterações de nível recentes.',
            review_kpis: 'KPIs de revisão',
            total_vendors_reviewed: 'Total Fornecedores Reviewed',
            reviews_by_outcome: 'Revisões por resultado',
            contract_coverage: 'Cobertura contratual',
            total_vendors: 'Total Fornecedores',
            with_active_dpa: 'Com Ativo DPA',
            without_dpa: 'Sem DPA',
            dpa_coverage: 'Cobertura DPA',
            active_contracts_by_type: 'Ativo Contratos por Tipo',
            expiring_contracts: 'Contratos a expirar (próximos 90 dias)',
            no_expiring_contracts: 'Nenhum contrato a expirar nos próximos 90 dias.',
            vendor: 'Fornecedor',
            previous_tier: 'Anterior Nível',
            current_tier: 'Atual Nível',
            changed_at: 'Alterado em',
            expires_at: 'Expira Em',
            days_remaining: 'Dias restantes',
            days: 'dias',
            vendors_label: 'fornecedores',
            no_data: 'Sem dados',
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Baixo',
            expiring_acceptances_heading: 'Aceitações de risco a expirar',
            expiring_30d_label: 'Expira em 30 dias',
            expiring_60d_label: 'Expira em 60 dias',
            expiring_90d_label: 'Expira em 90 dias',
        },
        scoring: {
            title: 'Pontuação de riscos',
            page_title: 'Pontuação de risco do fornecedor',
            filter_scores: 'Filtrar pontuações de risco',
            filter_by_tier: 'Filtrar por nível de risco',
            scores_pagination: 'Paginação de pontuações de risco',
            config_dimensions: 'Configuração de dimensões de pontuação',
            scores_loaded: '{count} pontuações de risco carregadas',
            weight_must_equal_100: 'O peso total deve ser igual a 100 (atual: {current})',
            out_of_10: 'de 10, classificado como {tier}',
            dimension_name: 'Nome da dimensão {index}',
            dimension_weight: '{name} peso (porcentagem)',
            must_equal_100: 'deve ser igual a 100',
            config_title: 'Configuração de pontuação',
            scores_title: 'Fornecedor Risco Scores',
            new_score_title: 'Pontuação um Fornecedor',
            new: 'Novo pontuação',
            vendor_id: 'Fornecedor ID',
            risk_tier: 'Risco Nível',
            score: 'Pontuação',
            scored_at: 'Pontuado em',
            no_scores: 'Nenhum risk scores encontrado',
            all_tiers: 'Todos os níveis',
            load_config_error: 'Falha ao load risk scoring configuration. Por favor, tente novamente.',
            update_config: 'Atualizar configuração de pontuação',
            update_config_error: 'Falha ao update risk scoring configuration. Por favor, tente novamente.',
            load_scores_error: 'Falha ao load vendor risk scores. Por favor, tente novamente.',
            calculate_error: 'Falha ao calculate vendor risk score. Por favor, tente novamente.',
            weight: 'peso',
            score_range: 'Pontuação (1-5)',
            method: 'Método',
        },
        questionnaires: {
            title: 'Questionários',
            page_title: 'Questionários de fornecedores',
            filter_questionnaires: 'Filtros de questionário',
            questionnaire_views: 'Visualizações do questionário',
            templates_list: 'Lista de modelos de questionário',
            instances_list: 'Lista de instâncias de questionário',
            filter_by_status: 'Filtrar por status do questionário',
            templates_per_page: 'Modelos por página',
            instances_per_page: 'Instâncias por página',
            select_template: 'Selecione o modelo de questionário',
            template_category: 'Categoria de modelo',
            templates_tab: 'Modelos',
            instances_tab: 'Instâncias',
            new: 'Novo Questionnaire',
            new_template: 'Novo modelo',
            new_instance: 'Nova instância',
            vendor_id: 'Fornecedor ID',
            template_id: 'Modelo ID',
            assigned_to: 'Atribuído Para',
            due_date: 'Data de vencimento',
            responses: 'Respostas',
            risk_score: 'Conclusão',
            no_questionnaires: 'Nenhum questionnaires encontrado',
            load_templates_error: 'Falha ao load questionnaire templates. Por favor, tente novamente.',
            load_instances_error: 'Falha ao load questionnaires. Por favor, tente novamente.',
            create_template_error: 'Falha ao create questionnaire template. Por favor, tente novamente.',
            initiate_error: 'Falha ao initiate questionnaire. Por favor, tente novamente.',
            submit_error: 'Falha ao submit questionnaire. Por favor, tente novamente.',
            review_error: 'Falha ao review questionnaire. Por favor, tente novamente.',
            resubmit: 'Reenviar',
            approve_error: 'Falha ao aprovar questionário',
            reject_error: 'Falha ao rejeitar questionário',
            resubmit_error: 'Falha ao reenviar questionário',
            templates_loaded: '{count} modelos de questionário carregados',
            instances_loaded: '{count} instâncias de questionário carregadas',
            fill_responses_cta: 'Preencher respostas',
            fill_responses_title: 'Preencher respostas do questionário',
            template_missing: 'Modelo não encontrado.',
            responses_saved: 'Respostas guardadas.',
            load_template_error: 'Falha ao carregar modelo.',
            version_history: 'Histórico de versões',
            download_previous_version: 'Descarregar versão anterior',
            trigger: 'Gatilho',
            template_label: 'Modelo',
            reviewer: 'Revisor',
            reviewer_required: 'Um revisor deve ser designado para os questionários {trigger} (separação de funções).',
            select_option: 'Selecione uma opção',
            multi_choice_hint: 'Selecione todas as opções aplicáveis',
            category_initial_onboarding: 'Integração inicial',
            category_annual_review: 'Revisão anual',
            category_incident_triggered: 'Acionado por incidente',
            category_contract_renewal: 'Renovação de contrato',
            template_editor: {
                page_title: 'Editar modelo de questionário',
                preview_page_title: 'Visualizar modelo de questionário',
                edit_structure: 'Editar estrutura',
                preview: 'Visualização',
                actions: 'Ações',
                back_to_list: 'Voltar aos modelos',
                load_error: 'Falha ao carregar o modelo',
                save_error: 'Falha ao salvar a estrutura do modelo',
                saved: 'Estrutura do modelo salva.',
                sections_heading: 'Seções',
                questions_heading: 'Perguntas',
                add_section: 'Adicionar seção',
                edit_section: 'Editar seção',
                delete_section: 'Excluir seção',
                move_section_up: 'Mover seção para cima',
                move_section_down: 'Mover seção para baixo',
                confirm_delete_section: 'Excluir seção "{title}"? Todas as suas perguntas serão removidas.',
                section_title: 'Título da seção',
                section_description: 'Descrição da seção',
                section_id: 'ID da seção',
                section_id_hint: 'Gerado automaticamente, a menos que você defina um explicitamente.',
                no_sections: 'Nenhuma seção ainda. Adicione uma seção para começar.',
                no_questions: 'Ainda não há perguntas nesta seção.',
                add_question: 'Adicionar pergunta',
                edit_question: 'Editar pergunta',
                delete_question: 'Excluir pergunta',
                move_question_up: 'Mover pergunta para cima',
                move_question_down: 'Mover pergunta para baixo',
                confirm_delete_question: 'Excluir pergunta "{text}"?',
                question_text: 'Texto da pergunta',
                question_type: 'Tipo de pergunta',
                question_id: 'ID da pergunta',
                evidence_required: 'Evidência necessária',
                risk_weight: 'Peso de risco',
                scoring_map: 'Mapa de pontuação',
                scoring_map_json_hint: 'JSON valores de resposta de mapeamento de objeto para pontuações numéricas.',
                scoring_map_invalid_json: 'O mapa de pontuação deve ser válido JSON.',
                options_heading: 'Opções de resposta',
                add_option: 'Adicionar opção',
                remove_option: 'Opção de remoção',
                option_label: 'Etiqueta',
                option_value: 'Valor',
                option_risk_weight: 'Peso de risco',
                min_options_error: 'As questões de escolha devem ter pelo menos 2 opções.',
                option_label_required: 'Cada opção precisa de um rótulo.',
                option_value_required: 'Cada opção precisa de um valor.',
                option_value_unique: 'Os valores das opções devem ser exclusivos.',
                branching_rules_heading: 'Regras de ramificação',
                branching_rules_hint: 'Mostre ou oculte esta pergunta com base na resposta de uma pergunta anterior.',
                add_branching_rule: 'Adicionar regra de ramificação',
                remove_branching_rule: 'Remover regra de ramificação',
                branching_condition_question: 'Se pergunta',
                branching_condition_value: 'tem valor',
                branching_action: 'então',
                branching_action_show: 'mostre esta pergunta',
                branching_action_hide: 'ocultar esta pergunta',
                branching_select_condition_question: 'Selecione a pergunta anterior',
                branching_select_condition_value: 'Selecione o valor',
                save: 'Salvar estrutura',
                saving: 'Salvando...',
                cancel: 'Cancelar',
                close: 'Fechar',
                question_text_required: 'O texto da pergunta é obrigatório.',
                section_title_required: 'O título da seção é obrigatório.',
                type_single_choice: 'Escolha única',
                type_multi_choice: 'Múltipla escolha',
                type_free_text: 'Texto livre',
                type_numeric: 'Numérico',
                type_date: 'Data',
                type_file_upload: 'Carregamento de arquivo',
                type_yes_no: 'Sim / Não',
                preview_heading: 'Visualização do modelo',
                preview_subtitle: 'Visualização somente leitura de como o questionário aparece para um fornecedor.',
                preview_no_content: 'Este modelo ainda não possui seções ou perguntas.',
                preview_branching_notice: 'As regras de ramificação são avaliadas durante o preenchimento; a visualização mostra todas as perguntas.',
            },
            review_modal: {
                title: 'Revise as respostas do questionário',
                open_cta: 'Revisão',
                loading: 'Carregando questionário...',
                template_missing: 'Modelo não encontrado.',
                load_error: 'Falha ao carregar o questionário para revisão.',
                annotation_heading: 'Anotações',
                annotation_placeholder: 'Adicione uma anotação de revisor para esta pergunta...',
                annotation_status_label: 'Status da anotação',
                annotation_status_acceptable: 'Aceitável',
                annotation_status_concern: 'Preocupação',
                annotation_status_requires_action: 'Requer ação',
                review_comments_label: 'Comentários gerais da revisão',
                review_comments_placeholder: 'Resuma as descobertas da sua revisão...',
                justification_label: 'Justificativa da decisão',
                justification_placeholder: 'Explique o motivo da sua decisão...',
                reject_requires_comments: 'Rejeitar um questionário requer comentários de revisão.',
                decision_heading: 'Decisão',
                decision: {
                    approved: 'Aprovar',
                    rejected: 'Rejeitar',
                    additional_info_requested: 'Solicite informações adicionais',
                },
                submit: 'Enviar avaliação',
                submitting: 'Enviando avaliação...',
                cancel: 'Cancelar',
                submit_success: 'Revisão enviada.',
                submit_error: 'Falha ao enviar avaliação.',
                separation_of_duties_error: 'Você não pode revisar um questionário que você foi designado para preencher. A separação de funções requer um revisor diferente.',
                decision_required: 'Selecione uma decisão de revisão.',
                announce_opened: 'Modal de revisão aberto.',
                announce_submitted: 'Revisão enviada.',
            },
            instance_detail: {
                submitted_at: 'Enviado',
                reviewed_at: 'Revisado',
                reviewer: 'Revisor',
                review_decision: 'Decisão',
                review_comments: 'Rever comentários',
                decision_approved: 'Aprovado',
                decision_rejected: 'Rejeitado',
                decision_additional_info_requested: 'Mais informações solicitadas',
                decision_pending: 'Revisão pendente',
                expand_comments: 'Mostrar comentários completos',
                collapse_comments: 'Ocultar comentários completos',
                no_reviewer: 'Nenhum revisor atribuído',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Rascunho',
                in_progress: 'Em Curso',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Aprovado',
                rejected: 'Rejeitado',
                expired: 'Expirado',
            },
            export: {
                portfolio_label: 'Portfólio de exportação',
                instance_label: 'Exportar',
                portfolio_success: 'Exportação de portfólio pronta.',
                instance_success: 'Exportação do questionário pronta.',
                portfolio_error: 'Falha ao exportar o portfólio do questionário.',
                instance_error: 'Falha ao exportar o questionário.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Algumas questões são ocultadas por regras de ramificação.',
                cycle_warning: 'Um ciclo foi detectado nas regras de ramificação. As perguntas afetadas ficam visíveis por padrão.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Contratos',
            page_title: 'Contratos de fornecedores',
            new: 'Novo contrato',
            vendor_id: 'Fornecedor ID',
            filter_contracts: 'Filtrar contratos',
            filter_by_document_type: 'Filtrar por tipo de documento',
            filter_by_status: 'Filtrar por status do contrato',
            per_page: 'Contratos por página',
            expired_notice: 'Este contrato expirou',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Documento Tipo',
            expiry_date: 'Data de expiração',
            no_contracts: 'Nenhum contracts encontrado',
            all_document_types: 'Todos Documento Types',
            load_error: 'Falha ao load contracts. Por favor, tente novamente.',
            create_error: 'Falha ao create contract. Por favor, tente novamente.',
            delete_error: 'Falha ao delete contract. Por favor, tente novamente.',
            update_error: 'Falha ao atualizar o estado. Por favor, tente novamente.',
            confirm_delete: 'Eliminar contrato "{title}"?',
            renewal_date: 'Data de renovação',
            effective_date: 'Data de vigência',
            parties: 'Partes',
            summary: 'Resumo',
            n_contracts: '{count} contratos',
            n_contract: '{count} contrato',
            contracts_loaded: '{count} contratos carregados',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Adendo de segurança',
            doc_type_scc_annex: 'Anexo SCC',
            doc_type_sub_processor_list: 'Lista de suboperadores',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Outro',
            status_draft: 'Rascunho',
            status_under_negotiation: 'Em negociação',
            status_pending_signature: 'Aguardando assinatura',
            status_active: 'Ativo',
            status_expired: 'Expirado',
            status_terminated: 'Encerrado',
            status_superseded: 'Substituído',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Portas de aprovação',
            page_title: 'Avaliações de portões',
            new: 'Novo Gate Evaluation',
            vendor_id: 'Fornecedor ID',
            gate_name: 'Nome da porta',
            outcome: 'Resultado',
            evaluator: 'Avaliador',
            no_gates: 'Nenhum gate evaluations encontrado',
            load_error: 'Falha ao load gate evaluations. Por favor, tente novamente.',
            evaluate_error: 'Falha ao evaluate vendor gate. Por favor, tente novamente.',
            detail_error: 'Falha ao fetch evaluation detail. Por favor, tente novamente.',
            override_error: 'Falha ao override gate evaluation. Por favor, tente novamente.',
            criteria_results: 'Resultados dos critérios',
            evaluate_form: 'Avalie a porta do fornecedor',
            per_page: 'Avaliações por página',
            passed: 'Aprovado',
            failed: 'Falhou',
            override: 'Substituição',
            override_justification: 'Justificação de substituição',
            override_placeholder: 'Introduza justification for overriding this gate evaluation',
            approval_id: 'ID de aprovação',
            overall_passed: 'Aprovado',
            overall_failed: 'Reprovado',
            overall_overridden: 'Substituído',
            evaluations_loaded: '{count} avaliações de portão carregadas',
            create_approval_cta: 'Criar aprovação',
        },
        reviews: {
            title: 'Avaliações de fornecedores',
            page_title: 'Avaliações de fornecedores',
            new: 'Novo rever',
            vendor_id: 'Fornecedor ID',
            filter_by_outcome: 'Filtrar por resultado da revisão',
            per_page: 'Comentários por página',
            kpi_summary: 'Revise o resumo KPI',
            review_outcome_label: 'Revise o resultado',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Editar resultado da revisão',
            reviews_loaded: '{count} avaliações de fornecedores carregadas',
            reviewer: 'Revisor',
            review_type: 'Rever Tipo',
            outcome: 'Resultado',
            findings: 'Constatações',
            no_reviews: 'Nenhum vendor reviews encontrado',
            load_kpis_error: 'Falha ao fetch vendor review kpis. Por favor, tente novamente.',
            load_error: 'Falha ao fetch vendor reviews. Por favor, tente novamente.',
            update_error: 'Falha ao atualizar avaliação do fornecedor',
            create_error: 'Falha ao create vendor review. Por favor, tente novamente.',
            total_reviewed: 'Total Fornecedores Reviewed',
            total_reviews: 'Total de revisões',
            satisfactory: 'Satisfatório',
            concerns_identified: 'Preocupações identificadas',
            remediation_required: 'Remediação necessária',
            escalated: 'Escalado',
            program_id: 'ID do programa',
            period_id: 'Período ID',
            task_id: 'Tarefa ID',
            recommendations: 'Recomendações',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Sinais de Risco',
            page_title: 'Sinais de risco',
            new_signal: 'Novo sinal de risco',
            signal_type: 'Tipo de sinal',
            severity: 'Gravidade',
            description: 'Descrição',
            source: 'Fonte',
            vendor_id: 'ID do fornecedor',
            certification_expiry: 'Expiração de certificação',
            breach_notification: 'Notificação de violação',
            financial_instability: 'Instabilidade financeira',
            compliance_change: 'Alteração de conformidade',
            custom: 'Personalizado',
            active: 'Ativo',
            acknowledged: 'Reconhecido',
            resolved: 'Resolvido',
            no_signals: 'Nenhum sinal de risco encontrado',
            source_external_feed: 'Fonte externa',
            source_manual: 'Manuais',
            source_integration: 'Integração',
            source_regulatory: 'Regulatório',
            source_media: 'Comunicação social',
            trigger_reassessment_prompt: 'Este sinal é de alta gravidade. Iniciar uma reavaliação do fornecedor agora?',
            reassessment_triggered: 'Reavaliação iniciada.',
            reassessment_trigger_error: 'Não foi possível iniciar a reavaliação.',
        },
        reassessments: {
            title: 'Tarefas de Reavaliação',
            page_title: 'Tarefas de Reavaliação',
            trigger: 'Iniciar reavaliação',
            trigger_success: 'Reavaliação iniciada com sucesso',
            reason: 'Motivo',
            deadline: 'Prazo',
            assigned_to: 'Atribuído a',
            vendor_id: 'ID do fornecedor',
            outcome: 'Resultado',
            new_risk_level: 'Novo nível de risco',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Concluir reavaliação',
            complete_success: 'Reavaliação concluída com sucesso',
            pending: 'Pendente',
            in_progress: 'Em andamento',
            completed: 'Concluído',
            cancelled: 'Cancelado',
            no_tasks: 'Nenhuma tarefa de reavaliação encontrada',
            risk_trend: 'Tendência de risco',
            notes: 'Notas',
        },
        portfolio: {
            title: 'Portfólio de Riscos',
            page_title: 'Portfólio de Risco',
            dashboard: 'Painel do portfólio',
            vendor_id: 'ID do fornecedor',
            total_vendors: 'Total de fornecedores',
            average_score: 'Pontuação média de risco',
            high_risk_count: 'Fornecedores de alto risco',
            trend_direction: 'Direção da tendência',
            improved: 'Melhorado',
            stable: 'Estável',
            degraded: 'Degradado',
            vendor_trend: 'Tendência de fornecedores',
            outstanding_questionnaires: 'Questionários pendentes',
            overdue_assessments: 'Avaliações em atraso',
            no_outstanding: 'Nenhum questionário pendente',
            no_overdue: 'Nenhuma avaliação em atraso',
            due_date: 'Data de vencimento',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Registo de Aceitação de Riscos',
            page_title: 'Registro de Aceitação de Risco',
            new_acceptance: 'Nova aceitação de risco',
            vendor_id: 'ID do fornecedor',
            risk_level: 'Nível de risco',
            conditions: 'Condições',
            justification: 'Justificação',
            expiry_date: 'Data de expiração',
            create_success: 'Aceitação de risco criada com sucesso',
            review_condition: 'Rever condição',
            condition_met: 'Condição cumprida',
            condition_not_met: 'Condição não cumprida',
            review_success: 'Condição revista com sucesso',
            export_register: 'Exportar registo',
            active: 'Ativo',
            expired: 'Expirado',
            revoked: 'Revogado',
            no_acceptances: 'Nenhuma aceitação de risco encontrada',
            met_count: '{met}/{total} encontrado',
        },
        review_routing: {
            title: 'Encaminhamento de Revisões',
            page_title: 'Revise o roteamento',
            route_to_reviewers: 'Enviar para revisores',
            route_success: 'Enviado para revisores com sucesso',
            vendor_id: 'ID do fornecedor',
            reviewer_id: 'ID do revisor',
            reviewer_role: 'Função do revisor',
            questionnaire_id: 'ID do questionário',
            review_status: 'Estado da revisão',
            reviewers_label: 'Revisores (user_id, função por linha)',
            pending: 'Pendente',
            in_review: 'Em revisão',
            completed: 'Concluído',
            no_routing: 'Nenhum encaminhamento de revisão encontrado',
            primary_reviewer: 'Revisor principal',
            secondary_reviewer: 'Revisor secundário',
            approver: 'Aprovador',
        },
        onboarding: {
            title: 'Casos de Integração de Fornecedores',
            page_title: 'Casos de Integração de Fornecedores',
            new_case: 'Novo caso de integração',
            case_detail: 'Caso de Integração',
            open_mps_dossier_filing: 'Abrir submissão MPS',
            dossier_filing_description: 'Cria uma nova submissão MPS pré-preenchida a partir deste caso de onboarding. A submissão ficará ligada ao caso.',
            dossier_filing_dpia_id: 'ID DPIA',
            dossier_filing_dpia_id_placeholder: 'ex. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Título da submissão',
            dossier_filing_form_type: 'Tipo de formulário',
            dossier_filing_submit: 'Criar submissão',
            dossier_filing_validation_error: 'ID DPIA e título são necessários.',
            dossier_filing_created: 'Submissão MPS criada',
            dossier_filing_error: 'Falha ao criar submissão MPS.',
            linked_questionnaire_due_date: 'Data limite',
            linked_questionnaire_assignee: 'Atribuído a',
            linked_questionnaire_submitted_at: 'Submetido em',
            score_last_scored: 'Última pontuação',
            score_dimensions: 'Dimensões',
            linked_contract_document_type: 'Tipo de documento',
            linked_contract_effective_date: 'Data de entrada em vigor',
            linked_contract_expiry_date: 'Data de expiração',
            linked_sub_processor_countries: 'Países de processamento',
            linked_sub_processor_last_assessment: 'Última avaliação',
            linked_sub_processor_next_assessment: 'Próxima avaliação',
            case_id: 'ID do caso',
            vendor: 'Fornecedor',
            case_type: 'Tipo de caso',
            service_description: 'Descrição do serviço',
            data_categories: 'Categorias de dados',
            systems_accessed: 'Sistemas acedidos',
            jurisdiction: 'Jurisdição',
            hosting_model: 'Modelo de alojamento',
            controller_role: 'Papel de responsável/subcontratante',
            status_pipeline: 'Pipeline de estado',
            tabs_label: 'Secções do caso de integração',
            per_page: 'Casos por página',
            filter_cases: 'Filtrar casos de integração',
            cases_count: 'casos',
            cases_loaded: 'casos de integração carregados',
            no_cases: 'Nenhum caso de integração encontrado.',
            vendor_lookup_placeholder: 'Introduza o ID do fornecedor',
            data_categories_placeholder: 'ex., dados_pessoais, financeiros, saúde',
            systems_placeholder: 'ex., CRM, ERP, Sistema RH',
            status_open: 'Aberto',
            status_in_progress: 'Em progresso',
            status_awaiting_approval: 'A aguardar aprovação',
            status_approved: 'Aprovado',
            status_remediation_required: 'Remediação necessária',
            status_closed: 'Fechado',
            type_new_vendor: 'Novo fornecedor',
            type_service_change: 'Alteração de serviço',
            type_renewal: 'Renovação',
            type_reassessment: 'Reavaliação',
            tab_overview: 'Visão geral',
            tab_due_diligence: 'Devida diligência',
            tab_security_assessment: 'Avaliação de segurança',
            tab_contractual_controls: 'Controlos contratuais',
            tab_sub_processors: 'Subcontratantes',
            tab_approval_gates: 'Portas de aprovação',
            tab_evidence: 'Evidência',
            tab_placeholder: 'O conteúdo desta secção será preenchido durante o processo de integração.',
            completion_not_started: 'Não iniciado',
            completion_in_progress: 'Em progresso',
            completion_completed: 'Concluído',
            completion_requiring_attention: 'Requer atenção',
            gate_name: 'Nome da porta',
            approver: 'Aprovador',
            decision: 'Decisão',
            comments: 'Comentários',
            decide: 'Tomar decisão',
            decision_approved: 'Aprovado',
            decision_rejected: 'Rejeitado',
            decision_deferred: 'Adiado',
            decision_pending: 'Pending',
            no_approvals: 'Nenhuma porta de aprovação configurada para este caso.',
            approval_success: 'Decisão de aprovação registada com sucesso',
            approval_error: 'Não foi possível registar a decisão de aprovação',
            transition_status: 'Transitar estado',
            confirm_transition: 'Transitar caso de "{from}" para "{to}"?',
            transition_success: 'Estado do caso transitado com sucesso',
            transition_error: 'Não foi possível transitar o estado do caso',
            create_success: 'Caso de integração criado com sucesso',
            create_error: 'Não foi possível criar o caso de integração',
            load_error: 'Não foi possível carregar os casos de integração',
            detail_error: 'Não foi possível carregar os detalhes do caso',
            export: 'Exportar',
            export_error: 'Não foi possível exportar o caso de integração',
            evidence_tab_description: 'Carregue e exporte provas para o caso de integração.',
            upload_evidence_label: 'Carregar prova',
            attached_evidence_heading: 'Provas anexadas',
            no_evidence_attached: 'Ainda sem provas anexadas.',
            export_bundle_button: 'Exportar conjunto de provas',
            load_attachments_error: 'Falha ao carregar provas.',
            linked_questionnaires_heading: 'Questionários ligados',
            no_linked_questionnaires: 'Sem questionários ligados.',
            linked_scores_heading: 'Pontuações ligadas',
            no_linked_scores: 'Sem pontuações ligadas.',
            latest_score_label: 'Pontuação mais recente',
            linked_contracts_heading: 'Contratos ligados',
            no_linked_contracts: 'Sem contratos ligados.',
            linked_sub_processors_heading: 'Subcontratantes ligados',
            no_linked_sub_processors: 'Sem subcontratantes ligados.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Marcar como concluído',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Registo de Subcontratantes',
            page_title: 'Registo de Subcontratantes',
            new_record: 'Novo subcontratante',
            edit_record: 'Editar subcontratante',
            onboarding_case: 'Caso de integração',
            services: 'Serviços prestados',
            data_categories: 'Categorias de dados',
            processing_countries: 'Países de tratamento',
            dpa_chain_status: 'Estado da cadeia DPA',
            certifications: 'Certificações',
            last_assessment: 'Última avaliação',
            next_assessment: 'Próxima avaliação',
            change_history: 'Histórico de alterações',
            history: 'Histórico',
            changed_by: 'Alterado por',
            comma_separated: 'Valores separados por vírgulas',
            filter_records: 'Filtrar registos',
            filter_by_dpa: 'Filtrar por estado da cadeia DPA',
            all_dpa_statuses: 'Todos os estados DPA',
            per_page: 'Registos por página',
            records_count: 'subcontratantes',
            records_loaded: 'registos carregados',
            no_records: 'Nenhum subcontratante encontrado.',
            no_history: 'Nenhum histórico de alterações disponível.',
            dpa_active: 'Ativo',
            dpa_pending: 'Pendente',
            dpa_expired: 'Expirado',
            dpa_not_required: 'Não necessário',
            create_success: 'Subcontratante criado com sucesso',
            create_error: 'Não foi possível criar o subcontratante',
            update_success: 'Subcontratante atualizado com sucesso',
            update_error: 'Não foi possível atualizar o subcontratante',
            load_error: 'Não foi possível carregar o registo',
            history_error: 'Não foi possível carregar o histórico de alterações',
            export_csv: 'Exportar CSV',
            export_json: 'Exportar JSON',
            export_error: 'Não foi possível exportar os dados',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Painel de orquestração',
            runs: 'Execuções de orquestração',
            tasks: 'Tarefas de execução',
            templates: 'Modelos de lista de verificação',
            vendor_requests: 'Pedidos de fornecedores',
            proof_chain: 'Cadeia de provas',
            dsr_integration: 'Integração DSR',
            vendor_verification: 'Verificação de fornecedores',
            completeness: 'Painel de completude',
        },
        dashboard: {
            title: 'Painel de orquestração',
            loading: 'A carregar dashboard data...',
            active_runs: 'Execuções ativas',
            completion_rate: 'Taxa de conclusão',
            avg_completion_time: 'Tempo médio de conclusão',
            overdue_tasks: 'Em atraso Tarefas',
            vendor_compliance: 'Fornecedor Conformidade',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Execuções recentes',
            no_recent_runs: 'Sem execuções de orquestração recentes.',
            run_id: 'Executar ID',
            ticket_id: 'ID do ticket',
            progress: 'Progresso',
            systems: 'Sistemas',
            vendors: 'Fornecedores',
            load_error: 'Falha ao carregar o painel de orquestração. Por favor, tente novamente.',
            aria_table: 'Execuções recentes de orquestração',
        },
        runs: {
            title: 'Execuções de orquestração',
            new: 'Novo executar',
            run_id: 'Executar ID',
            ticket_id: 'ID do ticket',
            progress: 'Progresso',
            systems: 'Sistemas',
            vendors: 'Fornecedores',
            data_subject_ref: 'Dados Titular Ref',
            data_categories: 'Categorias de dados',
            enable_proof_chain: 'Ativar cadeia de prova',
            start: 'Iniciar',
            started: 'Execução iniciada',
            mark_ready: 'Marcar como pronto',
            marked_ready: 'Execução marcada como pronta',
            complete: 'Completo',
            complete_exceptions: 'Completo com exceções',
            pause: 'Pausa',
            resume: 'Currículo',
            cancel: 'Cancelar',
            no_runs: 'Nenhum orchestration runs encontrado',
            load_error: 'Falha ao carregar execuções de orquestração. Por favor, tente novamente.',
            create_error: 'Falha ao criar execução de orquestração',
            aria_filter_status: 'Filtrar por status de execução',
            aria_table: 'Execuções de orquestração',
            placeholder_ticket_id: 'por exemplo, TKT-000123',
            placeholder_data_subject_ref: 'por exemplo, DS-REF-001',
            placeholder_data_categories: 'por exemplo, personal_data, financeiro, saúde',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Tarefas de execução',
            task_id: 'Tarefa ID',
            run_id: 'Executar ID',
            system_name: 'Sistema Nome',
            assigned_to: 'Atribuído Para',
            sla_deadline: 'SLA Prazo',
            progress: 'Progresso',
            enter_run_id: 'Introduza Run ID to filter...',
            select_run: 'Selecione uma execução para visualizar tarefas',
            unassigned: 'Não atribuído',
            overdue: 'Em atraso',
            assign_task: 'Atribuir Tarefa',
            assignee_id: 'Atribuído ID',
            update_status: 'Atualizar Estado',
            new_status: 'Novo estado',
            no_transitions: 'Esta tarefa está num estado final e não pode mudar de estado.',
            select_status_placeholder: 'Selecione um novo estado',
            skip_justification: 'Justificação da omissão',
            failure_reason: 'Motivo da falha',
            no_tasks: 'Nenhum execution tasks encontrado',
            new_task: 'Nova tarefa',
            system_id: 'ID do sistema',
            data_classification: 'Classificação de dados',
            load_error: 'Falha ao carregar execution tasks. Tente novamente.',
            assign_error: 'Falha ao assign task',
            status_error: 'Falha ao atualizar o estado da tarefa',
            no_tasks_for_run: 'Esta execução de eliminação ainda não tem tarefas de execução.',
            aria_filter_run_id: 'Filtrar por ID de execução',
            aria_filter_status: 'Filtrar por status da tarefa',
            aria_table: 'Tarefas de execução',
            view_steps: 'Ver etapas',
            steps_modal_title: 'Etapas de execução',
            step_title: 'Etapa',
            step_status: 'Estado da etapa',
            step_notes: 'Notas',
            step_evidence: 'Ficheiros de evidência',
            step_evidence_required: 'É necessária evidência para esta etapa',
            step_evidence_missing: 'Ainda não foi carregada evidência',
            step_save: 'Guardar etapa',
            step_saved: 'Etapa atualizada',
            step_save_error: 'Falha ao atualizar etapa',
            create_success: 'Tarefa de execução criada',
            create_error: 'Falha ao criar a tarefa de execução',
            step_upload_label: 'Anexar evidência',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Modelos de lista de verificação',
            new: 'Novo modelo',
            template_id: 'Modelo ID',
            system_type: 'Sistema Tipo',
            est_duration: 'Duração est.',
            steps: 'Passos',
            action_type: 'Ação Tipo',
            instructions: 'Instruções',
            mandatory: 'Obrigatório',
            add_step: 'Adicionar Step',
            step_n: 'Passo',
            no_templates: 'Nenhum checklist templates encontrado',
            all_system_types: 'Todos Sistema Types',
            load_error: 'Falha ao carregar modelos de lista de verificação. Por favor, tente novamente.',
            create_error: 'Falha ao criar modelo de lista de verificação',
            aria_filter_system_type: 'Filtrar por tipo de sistema',
            aria_filter_status: 'Filtrar por status do modelo',
            aria_table: 'Modelos de lista de verificação',
            placeholder_name: 'por exemplo, lista de verificação de exclusão de banco de dados',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Modelo atualizado com sucesso',
            create_success_msg: 'Modelo criado com sucesso',
            update_error: 'Falha ao atualizar o modelo',
            activate_success: 'Modelo ativado com sucesso',
            activate_error: 'Falha ao ativar o modelo',
            archive_success: 'Modelo arquivado com sucesso',
            archive_error: 'Falha ao arquivar o modelo',
            clone_success: 'Modelo clonado com sucesso',
            clone_error: 'Falha ao clonar o modelo',
        },
        vendor_requests: {
            title: 'Fornecedor Eliminação Pedidos',
            new: 'Novo pedido',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Pedido ID',
            vendor_name: 'Fornecedor Nome',
            vendor_id: 'Fornecedor ID',
            data_subject_ref: 'Dados Titular Ref',
            data_categories: 'Categorias de dados',
            legal_basis: 'Base jurídica',
            sla_days: 'Dias SLA',
            requested_completion: 'Conclusão solicitada',
            communications: 'Comunicações',
            total_requests: 'Total Pedidos',
            no_requests: 'Nenhum vendor deletion requests encontrado',
            filter_vendor_id: 'Filtrar por Fornecedor ID...',
            change_status: 'Alterar Estado',
            new_status: 'Novo Estado',
            actions: 'Ações',
            load_error: 'Falha ao carregar solicitações de exclusão de fornecedor. Por favor, tente novamente.',
            create_error: 'Falha ao criar solicitação de exclusão de fornecedor',
            dashboard_error: 'Falha ao carregar o painel de exclusão do fornecedor. Por favor, tente novamente.',
            aria_filter_status: 'Filtrar por status da solicitação',
            aria_filter_vendor_id: 'Filtrar por ID do fornecedor',
            aria_table: 'Solicitações de exclusão de fornecedor',
            placeholder_vendor_id: 'por exemplo, VND-001',
            placeholder_vendor_name: 'por exemplo, Acme Analytics',
            placeholder_data_subject_ref: 'por exemplo, DS-REF-001',
            placeholder_data_categories: 'por exemplo, personal_data, análises',
            placeholder_legal_basis: 'por exemplo, PDPL Artigo 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Visualizador de cadeia de prova',
            enter_run_id: 'Introduza executar id...',
            load_chain: 'Carregar cadeia',
            verify_chain: 'Verificar cadeia',
            verifying: 'A verificar...',
            chain_valid: 'A cadeia é válida',
            chain_invalid: 'A cadeia está quebrada',
            chain_length: 'Comprimento da cadeia',
            broken_at_index: 'Quebrado no índice',
            no_proof_records: 'Nenhum registo de prova encontrado para esta execução.',
            proof_id: 'ID de prova',
            entity_type: 'Tipo de entidade',
            entity_id: 'ID da entidade',
            event_hash: 'Hash do evento',
            previous_hash: 'Hash anterior',
            attestation: 'Atestação',
            attestation_actor: 'Realizado por',
            attestation_signed_at: 'Assinado em',
            attestation_signature: 'Assinatura',
            attestation_evidence_refs: 'Referências de evidências',
            run_field_label: 'Execução de eliminação',
            select_run: 'Selecione uma execução de eliminação…',
            aria_select_run: 'Execução de eliminação para a pesquisa da cadeia de provas',
            chain_broken_here: 'Cadeia quebrada aqui',
            load_error: 'Falha ao carregar a cadeia de prova. Por favor, tente novamente.',
            verify_error: 'Falha ao verificar a cadeia de prova. Por favor, tente novamente.',
            aria_run_id: 'ID de execução para pesquisa de cadeia de prova',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Alterar estado',
            request_type: 'Tipo de pedido',
            data_subject_hint: 'Identificador do titular dos dados cujos dados serão apagados (por ex., e-mail ou ID de cliente). Normalmente corresponde ao requerente do caso DSR selecionado.',
            title: 'Integração de eliminação DSR',
            create_run: 'Criar execução de eliminação DSR',
            dsr_case_id: 'ID do caso DSR',
            data_subject_id: 'ID do titular dos dados',
            systems: 'Sistemas alvo',
            route_to_department: 'Encaminhar para departamento',
            confirm_deletion: 'Confirmar eliminação',
            no_runs: 'Nenhuma execução de eliminação DSR encontrada',
            status: 'Estado da eliminação',
            deadline: 'Prazo',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Verificação de eliminação de fornecedores',
            create_followup: 'Criar seguimento',
            deadline: 'Prazo de confirmação',
            escalation_contact: 'Contacto de escalação',
            escalate: 'Escalar',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Nenhum seguimento de fornecedor encontrado',
            vendor_request: 'Solicitação do fornecedor',
            vendor: 'Fornecedor',
            unknown_vendor: 'Fornecedor desconhecido',
            overdue_badge: 'Em atraso',
            mark_confirmed: 'Marcar como confirmado',
            mark_confirmed_for: 'Marcar a eliminação como confirmada para {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Eliminação de fornecedor confirmada',
            confirmation_failed: 'Falha ao confirmar a eliminação de fornecedor',
            select_request: 'Selecione uma solicitação do fornecedor…',
            escalation_contact_placeholder: 'p. ex. dpo@vendor.example',
            create_success: 'Seguimento do fornecedor criado com sucesso',
            create_failed: 'Falha ao criar o seguimento do fornecedor',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Eliminação de fornecedor escalada com sucesso',
            escalation_failed: 'Falha ao escalar a eliminação de fornecedor',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Completude da eliminação',
            check_conflicts: 'Verificar conflitos de retenção',
            no_conflicts: 'Nenhum conflito de retenção encontrado',
            conflict_resolution: 'Resolução de conflitos',
            extend_retention: 'Prolongar retenção',
            proceed_deletion: 'Prosseguir com a eliminação',
            escalate_conflict: 'Escalar',
            resolve: 'Resolver conflito',
            data_subject_id: 'ID do titular dos dados',
            placeholder_data_subject_id: 'Insira o ID do titular dos dados',
            data_subject_hint: 'Identificador da pessoa cuja eliminação está a verificar (por ex., e-mail, ID de cliente ou referência de DSR). Escolha um titular conhecido ou escreva um.',
            guidance: 'Introduza ou selecione um ID de titular dos dados, opcionalmente escolha sistemas e execute a verificação para ver a integridade da eliminação e os conflitos de retenção.',
            completion: 'Conclusão da eliminação',
            pending_items: 'Itens pendentes',
            system: 'Sistema',
            policy: 'Política',
            retention_end: 'Fim da retenção',
            completed_items: 'Itens Concluídos',
            last_review_label: 'Última revisão',
            days_ago: 'dias atrás',
            review_overdue: 'Ciclo de revisão excedido. É necessária uma nova revisão.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Pendente',
            planning: 'Planeamento',
            ready: 'Pronto',
            in_progress: 'Em Progresso',
            paused: 'Pausado',
            completed: 'Concluído',
            completed_with_exceptions: 'Concluído com Exceções',
            cancelled: 'Cancelado',
        },
        task_status_labels: {
            pending: 'Pendente',
            assigned: 'Atribuído',
            in_progress: 'Em Progresso',
            awaiting_verification: 'Aguardando Verificação',
            verified: 'Verificado',
            failed: 'Falhado',
            skipped: 'Ignorado',
        },
        system_type_labels: {
            database: 'Base de Dados',
            saas_application: 'Aplicação SaaS',
            file_storage: 'Armazenamento de Ficheiros',
            email_system: 'Sistema de Email',
            analytics_platform: 'Plataforma Analítica',
            backup_system: 'Sistema de Backup',
            vendor_managed: 'Gerido pelo Fornecedor',
            custom: 'Personalizado',
        },
        template_status_labels: {
            draft: 'Rascunho',
            active: 'Ativo',
        },
        action_type_labels: {
            manual_check: 'Verificação Manual',
            api_call: 'Chamada API',
            sql_query: 'Consulta SQL',
            script_execution: 'Execução de Script',
            file_deletion: 'Eliminação de Ficheiro',
            verification: 'Verificação',
            notification: 'Notificação',
            approval: 'Aprovação',
        },
        request_status_labels: {
            draft: 'Rascunho',
            sent: 'Enviado',
            acknowledged: 'Confirmado',
            in_progress: 'Em Progresso',
            completed: 'Concluído',
            overdue: 'Atrasado',
            escalated: 'Escalado',
            disputed: 'Contestado',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Iniciação',
            data_deletion: 'Eliminação de Dados',
            completion: 'Conclusão',
            verification: 'Verificação',
            rollback: 'Reversão',
        },
        count_labels: {
            one_template: '1 modelo',
            n_templates: '{n} modelos',
            one_run: '1 execução',
            n_runs: '{n} execuções',
            one_task: '1 tarefa',
            n_tasks: '{n} tarefas',
            one_request: '1 pedido',
            n_requests: '{n} pedidos',
        },
        aria: {
            completion_rate: 'Taxa de conclusão: {pct}%',
            vendor_compliance: 'Conformidade do fornecedor: {pct}%',
            run_progress: 'Progresso: {pct}%',
            run_progress_systems: 'Progresso: {completed} de {total} sistemas processados',
            chain_broken: 'Cadeia partida neste registo',
            chain_valid: 'Cadeia válida neste registo',
            chain_unknown: 'Estado da cadeia desconhecido',
            escalate_vendor: 'Escalar {id}',
            resolve_conflict: 'Resolver conflito para {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'A integridade da exclusão deve ser revisada pelo menos a cada 365 dias. Os registros que excedem esse limite são sinalizados para nova revisão obrigatória para manter a conformidade regulatória.',
            runs_data_d1_lifecycle_info: 'Os estados de execução da orquestração são mapeados para o ciclo de vida DATA-D1: planejamento = catálogo, pronto = classificar, in_progress = aplicar regras, concluído = evidência gerada, completed_with_exceptions = revisão necessária. Cada transição é registrada na cadeia de prova.',
            vendor_record_linkage_info: 'Cada solicitação de exclusão de fornecedor é vinculada ao seu registro de fornecedor por meio de vendor_id. A confirmação da conclusão requer evidência criptográfica da cadeia de prova antes que a solicitação possa ser marcada como concluída.',
            templates_internal_classification_info: 'Todos os modelos de lista de verificação de exclusão são classificados como documentos organizacionais internos. Eles não possuem IDs de formulário regulatório oficial e não devem ser enviados como registros regulatórios.',
            proof_chain_evidence_info: 'Cada registro de prova captura o assunto/categoria dos dados, carimbo de data/hora, ator, base legal e um hash SHA-256. Juntos, esses campos produzem evidências de exclusão criptográfica que atendem aos requisitos de auditoria regulatória.',
            retention_data_law_info: 'Os cronogramas de retenção neste painel são derivados da classificação da Lei de Dados atribuída a cada categoria de dados. Quando uma classificação muda, as regras de retenção vinculadas são atualizadas automaticamente para garantir o alinhamento regulatório.',
            dsr_erasure_route_info: 'As execuções de eliminação de DSR estão vinculadas ao caso de Solicitação de Direitos originário. O prazo regulamentar (normalmente 30 dias nos termos do GDPR Art. 17) é rastreado automaticamente. Os casos atrasados ​​são sinalizados para escalonamento.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Painel de descoberta',
            scans: 'Verificações de descoberta',
            staging_area: 'Área de preparação',
            dedup_rules: 'Regras de deduplicação',
            classification_rules: 'Regras de classificação',
            suggestions: 'Fila de sugestões',
            analytics: 'Análise de sugestões',
            import_templates: 'Modelos de importação',
            connectors: 'Gestão de conectores',
            discovery_cycles: 'Ciclos de descoberta',
            owner_review: 'Revisão do proprietário',
            classification_lineage: 'Histórico de classificação',
        },
        dashboard: {
            title: 'Painel de descoberta',
            loading: 'A carregar dashboard data...',
            total_scans: 'Total de análises',
            active_scans: 'Análises ativas',
            completed_30d: 'Concluído (30d)',
            failed_30d: 'Falhou (30d)',
            systems_discovered: 'Sistemas descobertos',
            pending_staged_records: 'Pendente Staged Registos',
            recent_scans: 'Análises recentes',
            no_recent_scans: 'Sem análises recentes.',
            scan_id: 'Analisar ID',
            discovered: 'Descoberto',
            view_all_scans: 'Ver All Scans',
            staging_area: 'Área de staging',
            suggestion_queue: 'Fila de sugestões',
            records_reviewed: 'Registos revisados',
            records_approved: 'Registos aprovados',
            exceptions_pending: 'Exceções pendentes',
            view_as_chart: 'Ver como gráfico',
            view_as_table: 'Ver como tabela',
        },
        scans: {
            title: 'Verificações de descoberta',
            new: 'Novo analisar',
            scan_id: 'Analisar ID',
            scan_type: 'Analisar Tipo',
            progress: 'Progresso',
            discovered: 'Descoberto',
            no_scans: 'Nenhum discovery scans encontrado',
            all_scan_types: 'Todos Analisar Types',
            source_config: 'Config. da fonte (JSON)',
            invalid_json: 'JSON inválido na configuração da fonte',
            load_error: 'Falha ao load discovery scans. Por favor, tente novamente.',
            create_error: 'Falha ao create discovery scan. Por favor, tente novamente.',
            change_status: 'Alterar status',
            new_status: 'Novo status',
            actions: 'Ações',
            type_endpoint: 'Ponto de extremidade',
            type_spreadsheet_import: 'Importação de planilha',
            type_cmdb_sync: 'Sincronização CMDB',
            type_cloud_sync: 'Sincronização na nuvem',
            status_pending: 'Pendente',
            status_running: 'Em execução',
            status_completed: 'Concluído',
            status_failed: 'Falhou',
            status_cancelled: 'Cancelado',
            source_config_placeholder: '{"host": "...", "porta": 443}',
            adapter_not_implemented_toast: 'Este tipo de análise ainda não está disponível. Só é possível criar análises de endpoint.',
            adapter_not_implemented_warning: 'Ainda não disponível. Não é possível criar análises deste tipo até que o suporte seja lançado.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Área de preparação',
            record_id: 'Registo ID',
            source_scan: 'Análise de origem',
            entity_type: 'Tipo de entidade',
            suggested_name: 'Nome sugerido',
            confidence: 'Confiança',
            no_records: 'Nenhum staged records encontrado',
            promote: 'Promover',
            discard: 'Descartar',
            review: 'Rever',
            merge: 'Fundir',
            merge_title: 'Mesclar registro',
            merge_target_id: 'ID do alvo de fusão',
            reject_reason_placeholder: 'Motivo de rejeição...',
            promotion_status: 'Estado de promoção',
            dedup_status: 'Estado de dedup.',
            all_source_types: 'Todos os tipos de fonte',
            all_promotion_statuses: 'Todos os estados de promoção',
            all_dedup_statuses: 'Todos os estados de dedup.',
            load_error: 'Falha ao load staged records. Por favor, tente novamente.',
            promote_error: 'Falha ao promote record. Por favor, tente novamente.',
            reject_error: 'Falha ao reject record. Por favor, tente novamente.',
            merge_error: 'Falha ao merge record. Por favor, tente novamente.',
            review_error: 'Falha ao mark record for review. Por favor, tente novamente.',
            bulk_promote_error: 'Falha ao bulk promote records. Por favor, tente novamente.',
            bulk_reject_error: 'Falha ao bulk reject records. Por favor, tente novamente.',
            service_type_web_server: 'Servidor web',
            service_type_database: 'Base de dados',
            service_type_api_gateway: 'Gateway de API',
            service_type_storage: 'Armazenamento',
            service_type_authentication: 'Autenticação',
            service_type_analytics: 'Análise',
            service_type_unknown: 'Desconhecido',
            source_type_endpoint_scan: 'Verificação de ponto de extremidade',
            source_type_spreadsheet: 'Planilha',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Inventário na nuvem',
            promotion_status_new: 'Novo',
            promotion_status_under_review: 'Em revisão',
            promotion_status_promoted: 'Promovido',
            promotion_status_rejected: 'Rejeitado',
            promotion_status_merged: 'Mesclado',
            dedup_status_unmatched: 'Sem correspondência',
            dedup_status_potential_match: 'Correspondência potencial',
            dedup_status_exact_match: 'Correspondência exata',
            dedup_status_resolved: 'Resolvido',
            confirm_bulk_reject: 'Tem certeza de que deseja rejeitar os registros selecionados?',
            scan_batch_id: 'ID do lote',
            promote_to_registry_title: 'Promover para registro de dados',
            promote_modal_instructions: 'Cria um item de Registro de Dados com collection_method: vínculo observado e de procedência com este registro de descoberta. O item começa no status de rascunho para revisão.',
            promote_label_category: 'Categoria',
            promote_label_name_override: 'Nome (substituição opcional)',
            placeholder_name_override: 'O padrão é o nome/nome do host do registro de descoberta',
            promote_label_sensitivity: 'Sensibilidade proposta',
            inherit_from_category: 'Herdar da categoria',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Regras de deduplicação',
            new: 'Novo Rule',
            rule_name: 'Nome da regra',
            match_fields: 'Campos de correspondência',
            threshold: 'Limiar',
            no_rules: 'Nenhum dedup rules encontrado',
            seed_defaults: 'Predefinições iniciais',
            match_type: 'Tipo de correspondência',
            similarity_threshold: 'Limiar de semelhança',
            field_name: 'Campo Nome',
            load_error: 'Falha ao load dedup rules. Por favor, tente novamente.',
            create_error: 'Falha ao create dedup rule. Por favor, tente novamente.',
            toggle_error: 'Falha ao toggle dedup rule. Por favor, tente novamente.',
            seed_error: 'Falha ao seed defaults. Por favor, tente novamente.',
            priority: 'Prioridade',
            enabled_status: 'Habilitado',
            disabled_status: 'Desativado',
            default_badge: 'Padrão',
            enable: 'Habilitar',
            disable: 'Desativar',
            match_type_exact: 'Exato',
            match_type_fuzzy: 'Aproximado',
            match_type_regex: 'Regex',
            match_type_ip_range: 'Faixa de IP',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Regras de classificação',
            new: 'Novo Rule',
            rule_name: 'Nome da regra',
            pattern: 'Padrão',
            classification: 'Classificação',
            no_rules: 'Nenhum classification rules encontrado',
            seed_defaults: 'Predefinições iniciais',
            run_rules_now: 'Executar regras agora',
            run_rules_success: 'Geradas {count} novas sugestões de classificação',
            run_rules_no_results: 'Nenhuma nova sugestão gerada — todas as regras correspondentes já possuem sugestões pendentes',
            run_rules_error: 'Falha ao executar regras de classificação. Por favor, tente novamente.',
            taxonomy_id: 'ID de taxonomia',
            level_id: 'Nível ID',
            condition_logic: 'Lógica de condição',
            weight: 'Peso',
            conditions: 'Condições',
            condition_field: 'Campo',
            condition_operator: 'Operador',
            condition_value: 'Valor',
            effectiveness: 'Eficácia',
            match_count: 'Correspondências',
            approval_count: 'Aprovações',
            rejection_count: 'Rejeições',
            filter_taxonomy: 'Filtrar por ID de taxonomia...',
            load_error: 'Falha ao load classification rules. Por favor, tente novamente.',
            create_error: 'Falha ao create classification rule. Por favor, tente novamente.',
            seed_error: 'Falha ao seed defaults. Por favor, tente novamente.',
            toggle_error: 'Falha ao toggle rule. Por favor, tente novamente.',
            priority: 'Prioridade',
            enabled_status: 'Habilitado',
            disabled_status: 'Desativado',
            enable: 'Habilitar',
            disable: 'Desativar',
            logic_and: 'E',
            logic_or: 'OU',
            operator_equals: 'Igual a',
            operator_contains: 'Contém',
            operator_matches_regex: 'Corresponde ao Regex',
            operator_in_list: 'Na lista',
            operator_not_equals: 'Diferente de',
            operator_not_contains: 'Não contém',
            enabled_label: 'Ativado',
            disabled_label: 'Desativado',
            taxonomy_label: 'Taxonomia',
            operator_label: 'Operador',
            value_label: 'Valor',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Fila de sugestões',
            entity_type: 'Tipo de entidade',
            suggested_name: 'Nome sugerido',
            source: 'Fonte',
            confidence: 'Confiança',
            accept: 'Aceitar',
            reject: 'Rejeitar',
            no_suggestions: 'Sem sugestões na fila.',
            modify_approve: 'Modificar e aprovar',
            delegate: 'Delegar',
            delegate_user_id: 'ID do utilizador delegado',
            new_level_id: 'Novo nível id',
            reject_reason_placeholder: 'Motivo de rejeição...',
            current_level: 'Atual Nível',
            suggested_level: 'Nível sugerido',
            filter_taxonomy: 'Filtrar por ID de taxonomia...',
            all_confidence: 'Todas as faixas de confiança',
            load_error: 'Falha ao load suggestions. Por favor, tente novamente.',
            approve_error: 'Falha ao approve suggestion. Por favor, tente novamente.',
            reject_error: 'Falha ao reject suggestion. Por favor, tente novamente.',
            modify_error: 'Falha ao modify & approve suggestion. Por favor, tente novamente.',
            delegate_error: 'Falha ao delegate suggestion. Por favor, tente novamente.',
            bulk_approve_error: 'Falha ao bulk approve suggestions. Por favor, tente novamente.',
            bulk_reject_error: 'Falha ao bulk reject suggestions. Por favor, tente novamente.',
            suggestion_type_new_label: 'Nova etiqueta',
            suggestion_type_validation: 'Validação',
            suggestion_type_reclassification: 'Reclassificação',
            status_pending: 'Pendente',
            status_approved: 'Aprovado',
            status_rejected: 'Rejeitado',
            status_superseded: 'Substituído',
            confidence_high: 'Alta',
            confidence_medium: 'Média',
            confidence_low: 'Baixa',
            confirm_bulk_reject: 'Tem certeza de que deseja rejeitar as sugestões selecionadas?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Todas as taxonomias',
        },
        analytics: {
            title: 'Análise de sugestões',
            total_suggestions: 'Total de sugestões',
            accepted: 'Aceite',
            rejected: 'Rejeitado',
            pending: 'Pendente',
            acceptance_rate: 'Taxa de aceitação',
            by_entity_type: 'Por tipo de entidade',
            by_source: 'Por fonte',
            avg_decision_time: 'Tempo médio de decisão',
            rule_effectiveness: 'Eficácia da regra',
            by_status: 'Por Estado',
            by_confidence: 'Por faixa de confiança',
            load_error: 'Falha ao load suggestion analytics. Por favor, tente novamente.',
            confidence_high: 'Alta',
            confidence_medium: 'Média',
            confidence_low: 'Baixa',
            by_confidence_band: 'Por faixa de confiança',
            approval_rate: 'Taxa de aprovação',
            by_label_state: 'Por estado de rótulo',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Modelos de importação',
            new: 'Novo modelo',
            template_name: 'Modelo Nome',
            file_format: 'Formato de ficheiro',
            column_mappings: 'Mapeamentos de colunas',
            no_templates: 'Nenhum import templates encontrado',
            source_column: 'Coluna de origem',
            target_field: 'Campo de destino',
            transform: 'Transformar',
            source_column_label: 'Coluna de origem',
            target_field_label: 'Campo de destino',
            transform_label: 'Transformar',
            confirm_delete: 'Tem a certeza de que pretende eliminar this import template?',
            load_error: 'Falha ao load import templates. Por favor, tente novamente.',
            save_error: 'Falha ao save import template. Por favor, tente novamente.',
            delete_error: 'Falha ao delete import template. Por favor, tente novamente.',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Gestão de conectores',
            new: 'Novo Connector',
            connector_name: 'Nome do conector',
            connector_type: 'Tipo',
            last_sync: 'Último Sincronizar',
            no_connectors: 'Nenhum connectors encontrado',
            cmdb_syncs: 'Sincronizações CMDB',
            cloud_syncs: 'Sincronizações cloud',
            new_sync: 'Novo sincronizar',
            no_syncs: 'Nenhum syncs encontrado',
            cmdb_error: 'Falha ao load cmdb scans. Por favor, tente novamente.',
            cloud_error: 'Falha ao load cloud scans. Por favor, tente novamente.',
            trigger_error: 'Falha ao trigger sync. Por favor, tente novamente.',
            trigger_sync_error: 'Falha ao trigger {type}',
            cmdb_section_title: 'Sincronizações CMDB',
            cloud_section_title: 'Sincronizações na nuvem',
            column_connector: 'Conector',
            action_start: 'Iniciar',
            confirm_cancel_sync: 'Cancelar esta sincronização?',
            start_error: 'Falha ao iniciar sincronização',
            cancel_error: 'Falha ao cancelar sincronização',
            type_endpoint: 'Verificação de endpoint',
            type_spreadsheet_import: 'Importação de planilha',
            type_cmdb_sync: 'Sincronização CMDB',
            type_cloud_sync: 'Sincronização na nuvem',
        },
        discovery_cycles: {
            title: 'Ciclos de descoberta',
            new_cycle: 'Novo ciclo de descoberta',
            cycle_name: 'Nome do ciclo',
            scope_type: 'Tipo de âmbito',
            scope_ids: 'IDs de âmbito',
            cadence_days: 'Frequência (dias)',
            status: 'Estado',
            no_cycles: 'Nenhum ciclo de descoberta encontrado',
            compare: 'Comparar ciclos',
            delta_report: 'Relatório de alterações',
            new_records: 'Novos registos',
            changed_records: 'Registos alterados',
            removed_records: 'Registos removidos',
            schedule: 'Agendar análise',
            scope_organization: 'Organização',
            scope_department: 'Departamento',
            scope_system: 'Sistema',
            scope_systems: 'Sistemas',
            bulk_actions: 'Ações em massa',
            selected: '{count} selecionado(s)',
            bulk_create: 'Criação em massa',
            bulk_assign_scope: 'Atribuir escopo em massa',
            bulk_advance: 'Avançar status em massa',
            confirm_bulk_action: 'Confirmar ação em massa',
            bulk_confirm_message: 'Tem certeza de que deseja realizar esta ação?',
            cycles_affected: '{count} ciclos serão afetados',
            select_all: 'Selecionar tudo',
            select_cycle: 'Selecionar ciclo {name}',
            from_template: 'a partir do modelo',
            bulk_results: 'Resultados em massa',
            failed_label: 'falhou',
            template_id: 'ID do modelo',
            count: 'Quantidade',
            advance_to: 'Estado alvo',
            bulk_success: 'Operação em massa concluída',
        },
        owner_review: {
            title: 'Tarefas de revisão do proprietário',
            assign_owner: 'Atribuir ao proprietário',
            owner_id: 'Proprietário',
            decision: 'Decisão',
            approve: 'Aprovar',
            modify: 'Modificar',
            reject: 'Rejeitar',
            escalate: 'Escalar',
            no_tasks: 'Nenhuma tarefa de revisão encontrada',
            bulk_resolve: 'Resolução em massa',
            exceptions: 'Itens de exceção',
            no_exceptions: 'Nenhum item de exceção encontrado',
            record_id_label: 'ID do registro',
            selected: 'selecionado',
            notes: 'Notas',
            select_all: 'Selecionar Tudo',
            select: 'Selecionar',
            confirm_resolve: 'Confirmar',
        },
        classification_lineage: {
            title: 'Histórico de classificação',
            link_suggestion: 'Associar a rótulo proposto',
            suggestion_id: 'Sugestão',
            proposed_label_id: 'Rótulo proposto',
            no_lineage: 'Nenhum dado de histórico encontrado',
            full_lineage: 'Histórico completo',
            record_id_label: 'ID do registro',
            staged_record_label: 'Registro encenado',
            enter_staged_record_id: 'Insira o ID do registro preparado',
            scan_id_label: 'ID de digitalização',
            select_record: 'Selecionar um registo',
            select_suggestion: 'Selecionar uma sugestão',
            select_label: 'Selecionar uma etiqueta',
        },
        common_labels: {
            n_rules: '{n} regras',
            one_rule: '1 regra',
            n_scans: '{n} verificações',
            one_scan: '1 varredura',
            n_syncs: '{n} sincroniza',
            one_sync: '1 sincronização',
            n_records: '{n} registros',
            one_record: '1 registro',
            n_templates: '{n} modelos',
            one_template: '1 modelo',
            n_suggestions: '{n} sugestões',
            one_suggestion: '1 sugestão',
            n_selected: '{n} selecionado',
            dashboard_loaded: 'Painel de descoberta carregado',
            n_scans_loaded: '{n} verificações carregadas',
            n_staged_records_loaded: '{n} registros preparados carregados',
            confirm_delete_import_template: 'Tem certeza de que deseja excluir este modelo de importação?',
            invalid_json_source_config: 'JSON inválido em source_config',
            scope_organization: 'Organização',
            scope_department: 'Departamento',
            scope_system: 'Sistema',
            aria_filter_scans: 'Filtrar verificações',
            aria_filter_staged_records: 'Filtrar registros preparados',
            aria_filter_suggestions: 'Filtros',
            aria_filter_by_taxonomy_id: 'Filtrar por ID de taxonomia',
            aria_filter_by_enabled_status: 'Filtrar por status ativado',
            aria_filter_by_source_type: 'Filtrar por tipo de fonte',
            aria_filter_by_promotion_status: 'Filtrar por status de promoção',
            aria_filter_by_dedup_status: 'Filtrar por status de eliminação de duplicação',
            aria_filter_by_scan_type: 'Filtrar por tipo de verificação',
            aria_filter_by_status: 'Filtrar por status',
            aria_filter_by_confidence: 'Filtrar por faixa de confiança',
            aria_select_all_records: 'Selecione todos os registros',
            aria_select_record: 'Selecione o registro {id}',
            aria_select_all_suggestions: 'Selecione todas as sugestões',
            aria_select_suggestion: 'Selecione a sugestão {id}',
            aria_promote_record: 'Promover registro {id}',
            aria_discard_record: 'Registro de descarte {id}',
            aria_merge_record: 'Mesclar registro {id}',
            aria_review_record: 'Revisar registro {id}',
            aria_accept_suggestion: 'Aceitar sugestão {id}',
            aria_reject_record: 'Rejeitar registro',
            aria_merge_record_dialog: 'Mesclar registro',
            aria_create_new_scan: 'Criar nova verificação',
            aria_sync_progress: 'Progresso da sincronização: {processed} de {total} itens processados',
            aria_scan_progress: 'Progresso da verificação: {processed} de {total} itens processados',
            aria_confidence: 'Confiança: {pct}%',
            aria_effectiveness: 'Eficácia: {pct}',
            aria_promote_n_records: 'Promova {n} registros selecionados',
            aria_discard_n_records: 'Descartar {n} registros selecionados',
            placeholder_pii_detection_rule: 'por exemplo, regra de detecção de PII',
            placeholder_tax_id: 'por exemplo, TAX-001',
            placeholder_lvl_id: 'por exemplo, LVL-001',
            placeholder_service_type: 'por exemplo, service_type',
            placeholder_database: 'por exemplo, banco de dados',
            placeholder_hostname_match: 'por exemplo, correspondência exata do nome do host',
            placeholder_hostname: 'por exemplo, nome do host',
            placeholder_hr_systems_csv: 'por exemplo, Sistemas de RH CSV Importação',
            placeholder_server_name: 'por exemplo, nome do servidor',
            placeholder_hostname_field: 'por exemplo, nome do host',
            placeholder_lowercase: 'por exemplo, minúsculas',
            placeholder_source_config: '{"host": "...", "porta": 443}',
            placeholder_stg_id: 'por exemplo, STG-000123',
            placeholder_lvl_002: 'por exemplo, LVL-002',
            placeholder_usr_id: 'por exemplo, USR-000123',
            decision_for: 'Decisão para {id}',
        },
        exports: {
            title: 'Exportar',
            inventory: 'Inventário de dados',
            classification_register: 'Registo de classificação',
            raci: 'Matriz RACI',
            exceptions: 'Relatório de exceções',
            evidence_pack: 'Pacote de evidências',
            delta_report: 'Relatório de alterações',
            executive_summary: 'Resumo executivo',
            export_button: 'Exportar',
            downloading: 'A transferir...',
            download_complete: 'Transferência concluída.',
            download_error: 'Falha ao transferir exportação. Tente novamente.',
        },
        flags: {
            title: 'Marcadores a jusante',
            pending: 'Marcadores pendentes',
            cross_border_review_needed: 'Revisão transfronteiriça necessária',
            sharing_disclosure_needed: 'Divulgação de partilha necessária',
            dpia_review_needed: 'Revisão AIPD necessária',
            acknowledge: 'Confirmar',
            resolve: 'Resolver',
            no_flags: 'Sem marcadores a jusante.',
        },
        delta: {
            title: 'Resumo de alterações',
            new_count: '{n} novos',
            removed_count: '{n} removidos',
            changed_count: '{n} alterados',
            re_review_triggered: 'Reavaliação acionada',
            create_re_review: 'Criar reavaliação',
            recent_alerts: 'Alertas de alterações recentes',
            no_alerts: 'Sem alertas de alterações recentes.',
            threshold_exceeded: 'Limiar de alterações ultrapassado (>10%)',
        },
        regulatory: {
            dashboard_governance_info: 'Este painel mostra o status de governança em nível de ciclo: sistemas verificados, registros preparados, registros revisados, registros aprovados e exceções pendentes. Isso corresponde aos critérios de sucesso do líder de governança: "painel ao vivo de quem atualizou seu inventário, classificação e compartilhamento de logs" (P-VN-05).',
            export_artifacts_info: 'As exportações de descoberta incluem todos os tipos de artefatos necessários por UC-VN-10 e DATA-EVD-01: inventário de dados aprovado, registro de classificação, mapeamento de propriedade/RACI, fila de exceções, resumo executivo, pacote de evidências e relatório delta.',
            scan_inventory_pipeline_info: 'Cada varredura concluída produz registros compatíveis com o modelo vn_data_law_data_inventory_register (requisito 88dd9877). Os ativos de dados descobertos preenchem automaticamente o registro de inventário de dados para satisfazer a obrigação da Lei de Dados de manter um inventário de dados digitais.',
            connector_linkage_info: 'Cada configuração de conector deve incluir: tipo de sistema (por exemplo, CMDB, plataforma de nuvem), referência de armazenamento de dados (banco de dados, armazenamento de arquivos, SaaS) e vinculação de processos de negócios (UC-VN-10 registros de suporte necessários). Esses campos garantem que os ativos descobertos sejam rastreáveis ​​até seu sistema de origem e contexto de negócios.',
            owner_routing_info: 'As tarefas de revisão são roteadas para o departamento correto ou proprietário do aplicativo com base na propriedade do sistema/dados (UC-VN-10 etapa 5). O roteamento usa o registro de propriedade do sistema, e não a atribuição ad hoc.',
            owner_actions_audit_info: 'Os proprietários podem aprovar, modificar, rejeitar ou escalar cada classificação (UC-VN-10 etapa 6). Cada ação captura o ator, o carimbo de data/hora e o motivo, produzindo eventos de evidência de auditoria de governança de acordo com o requisito f12fc090 (Atestado anual de governança). A trilha de auditoria mostra quem revisou, o que foi alterado e quando.',
            escalation_timers_info: 'Cronômetros de escalonamento: o prazo de revisão do proprietário é de 5 dias úteis; o escalonamento de exceção é de 48 horas (UC-VN-10 temporizadores). Os itens não resolvidos são escalonados automaticamente quando o prazo expira. Os temporizadores são configuráveis ​​de acordo com a política organizacional.',
            original_suggestion_preserved_info: 'A sugestão original de classificação da máquina/sistema é preservada junto com cada substituição do proprietário (UC-VN-10 requisito de evidência). A sugestão original nunca é substituída e permanece visível para fins de auditoria.',
            dedup_lineage_info: 'As regras de desduplicação evitam entradas duplicadas de inventário quando o mesmo ativo de dados é descoberto em diversas varreduras ou conectores. Os metadados de varredura mais recentes são preservados como registro primário, mantendo referências de linhagem a todas as descobertas anteriores para rastreabilidade de auditoria.',
            label_states_info: 'O Analytics distingue quatro estados de rótulo para revisão de governança: sugerido pela máquina (pendente), aprovado pelo proprietário, modificado pelo proprietário e rejeitado. Esses estados permitem que os auditores avaliem a precisão da automação e o envolvimento do proprietário.',
            import_pipeline_info: 'Os modelos de importação devem produzir registros compatíveis com o pipeline de descoberta. Os registros importados entram na preparação com os mesmos requisitos de metadados dos registros digitalizados: identificação do sistema de origem, ID do lote e carimbo de data/hora. Os mapeamentos de colunas devem ter como alvo os campos definidos em vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'A linhagem de classificação traça o caminho completo: digitalização/importação -> preparação -> revisão do proprietário -> rótulo aprovado -> entrada de registro (UC-VN-10 etapas 1 a 10). Cada estágio mostra ID do registro, ator, carimbo de data/hora e transição de estado para rastreabilidade completa da auditoria.',
            staging_workflow_info: 'Os registros preparados são visualmente diferenciados das entradas de inventário aprovadas por seu selo de status de promoção (UC-VN-10 etapa 3). Cada registro retém metadados de origem: sistema de origem, ID do lote de digitalização e carimbo de data/hora da descoberta para rastreabilidade total da auditoria.',
            suggestion_data_d1_info: 'As sugestões estão alinhadas com o procedimento de classificação da Lei de Dados DATA-D1: (1) catalogar ativos de dados, (2) classificar por nível de sensibilidade, (3) definir regras de tratamento. Cada sugestão inclui dimensões de sensibilidade e importância de acordo com o requisito 18e4bfd1.',
            downstream_flags_info: 'Itens sinalizados para compartilhamento, transferência transfronteiriça ou tratamento de alto risco acionam fluxos de trabalho downstream vinculados: avaliação de transferência transfronteiriça, avaliação de risco de compartilhamento e revisão DPIA (UC-VN-10 etapa 8).',
            delta_detection_info: 'Os ciclos de descoberta suportam a detecção delta de acordo com UC-VN-10 etapa 11: cada ciclo mostra quais registros são novos, alterados ou removidos desde a última linha de base aprovada. Alterações materiais (>10% delta) acionam automaticamente fluxos de trabalho de nova revisão.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Falha ao load. Por favor, tente novamente.',
            save: 'Falha ao save. Por favor, tente novamente.',
            fetch_escalation_log_failed: 'Falha ao fetch escalation log. Por favor, tente novamente.',
            fetch_activations_failed: 'Falha ao fetch activations. Por favor, tente novamente.',
            deactivate_failed: 'Falha ao deactivate. Por favor, tente novamente.',
            complete_activation_failed: 'Falha ao complete activation. Por favor, tente novamente.',
            fetch_playbook_templates_failed: 'Falha ao fetch playbook templates. Por favor, tente novamente.',
            create_playbook_template_failed: 'Falha ao create playbook template. Por favor, tente novamente.',
            clone_template_failed: 'Falha ao clone template. Por favor, tente novamente.',
            update_status_failed: 'Falha ao atualizar o estado. Por favor, tente novamente.',
            deprecate_template_failed: 'Falha ao deprecate template. Por favor, tente novamente.',
            seed_defaults_failed: 'Falha ao seed defaults. Por favor, tente novamente.',
            fetch_regulator_exports_failed: 'Falha ao fetch regulator exports. Por favor, tente novamente.',
            create_regulator_export_failed: 'Falha ao create regulator export. Por favor, tente novamente.',
            update_submission_status_failed: 'Falha ao update submission status. Por favor, tente novamente.',
            fetch_response_metrics_failed: 'Falha ao fetch response metrics. Por favor, tente novamente.',
            fetch_sla_dashboard_failed: 'Falha ao fetch sla dashboard. Por favor, tente novamente.',
            fetch_sla_definitions_failed: 'Falha ao fetch sla definitions. Por favor, tente novamente.',
            create_sla_definition_failed: 'Falha ao create sla definition. Por favor, tente novamente.',
            seed_sla_defaults_failed: 'Falha ao seed sla defaults. Por favor, tente novamente.',
            fetch_vendor_coordination_failed: 'Falha ao fetch vendor coordination. Por favor, tente novamente.',
            fetch_vendor_detail_failed: 'Falha ao fetch vendor detail. Por favor, tente novamente.',
            create_vendor_coordination_failed: 'Falha ao create vendor coordination. Por favor, tente novamente.',
            add_communication_failed: 'Falha ao add communication. Por favor, tente novamente.',
            add_info_request_failed: 'Falha ao add info request. Por favor, tente novamente.',
            add_action_request_failed: 'Falha ao add action request. Por favor, tente novamente.',
        },
        nav: {
            playbooks: 'Modelos de guia operacional',
            sla_definitions: 'Definições de SLA',
            sla_dashboard: 'Painel de SLA',
            escalation_log: 'Registo de escalamento',
            regulator_exports: 'Exportações regulatórias',
            deadlines: 'Prazos de notificação',
            vendor_coordination: 'Coordenação de fornecedores',
            metrics: 'Métricas de resposta',
            playbook_links: 'Ligações de Playbook',
            regulatory_sla: 'SLA regulamentar',
            coordination_dashboard: 'Coordenação',
            evidence_chain: 'Cadeia de provas',
            post_review: 'Revisão pós-incidente',
        },
        playbooks: {
            page_title: 'Modelos de manual',
            activations_page_title: 'Ativações do manual',
            title: 'Modelos de guia operacional',
            new: 'Novo modelo',
            seed_defaults: 'Predefinições iniciais',
            scenario_type: 'Tipo de cenário',
            severity_applicability: 'Aplicabilidade da gravidade',
            all_scenario_types: 'Todos os tipos de cenário',
            new_playbook: 'Novo Playbook Template',
            clone: 'Clonar',
            clone_template: 'Clonar Modelo',
            template_name: 'Nome do modelo',
            deprecate: 'Tornar obsoleto',
            confirm_deprecate: 'Tornar o modelo "{name}" obsoleto?',
            create_template: 'Adicionar modelo',
            update_status: 'Atualizar Estado',
            no_templates: 'Nenhum playbook templates encontrado',
            deactivation_reason_placeholder: 'Motivo de desativação...',
            description_placeholder: 'Descrição do livro de procedimentos...',
            name_placeholder: 'Nome do livro de procedimentos...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'Definições SLA',
            title: 'Definições de SLA',
            new: 'Novo SLA Definition',
            sla_name: 'SLA Nome',
            target_hours: 'Horas alvo',
            escalation_after: 'Escalado após',
            applies_to: 'Aplica-se a',
            no_definitions: 'Nenhum sla definitions encontrado',
            target_value_placeholder: 'Valor de destino',
            name_placeholder: 'Nome do SLA...',
            threshold_hours_label: 'Horas limiar',
            severity_label: 'Gravidade',
            all_types: 'Todos os tipos',
            all_severities: 'Todas as gravidades',
            default_label: 'Predefinido',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Compatível',
            page_title: 'Painel SLA',
            title: 'Painel de SLA',
            loading: 'A carregar sla data...',
            compliance_rate: 'Conformidade Taxa',
            breached: 'Violado',
            at_risk: 'Em Risco',
            on_track: 'Em Rastrear',
            by_severity: 'Por Gravidade',
            total_records: 'Total de registos',
            met: 'Cumprido',
            sla_records: 'Registos SLA',
            sla_reference: 'Referência SLA',
            record_id: 'ID de registo',
            metric_type: 'Tipo de métrica',
            severity: 'Gravidade',
            elapsed: 'Decorrido',
            clock_start: 'Início do relógio',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Registo de escalamento',
            title: 'Registo de escalamento',
            incident_id: 'Incidente ID',
            escalated_by: 'Escalado por',
            escalated_to: 'Escalado para',
            reason: 'Motivo',
            escalated_at: 'Escalado em',
            no_escalations: 'Nenhum escalations encontrado',
            filter_incident_id: 'Filtrar por Incidente ID',
            escalation_id: 'ID de escalamento',
            level: 'Nível',
            level_range: 'Nível (L1–L4)',
            create_escalation: 'Criar escalamento',
            incident_id_placeholder: 'p. ex. INC-001',
            target_role_placeholder: 'p. ex. dpo, incident_manager',
            select_placeholder: '— selecionar —',
        },
        regulator_exports: {
            page_title: 'Exportações regulatórias',
            title: 'Exportações regulatórias',
            new: 'Novo exportar',
            incident_id: 'Incidente ID',
            regulator: 'Regulador',
            export_format: 'Formato',
            exported_at: 'Exportado Em',
            no_exports: 'Nenhum regulator exports encontrado',
            enter_incident_id: 'Introduza incidente id',
            export_id: 'ID de exportação',
            watermark: 'Marca de água',
            submission_status: 'Estado de submissão',
            all_jurisdictions: 'Todas as jurisdições',
            no_transitions: 'Não há mais transições disponíveis de',
            select_new_status: '— selecionar novo estado —',
            sections_included: 'Secções incluídas',
            form_classification: 'Classificação',
            submission_channel: 'Canal',
        },
        deadlines: {
            title: 'Prazos de notificação',
            incident_id: 'Incidente ID',
            deadline: 'Prazo',
            regulator: 'Regulador',
            remaining: 'Restante',
            no_deadlines: 'Nenhum notification deadlines encontrado',
        },
        vendor_coordination: {
            page_title: 'Coordenação de fornecedores',
            title: 'Coordenação de fornecedores',
            new: 'Novo Coordination',
            incident_id: 'Incidente ID',
            vendor_name: 'Fornecedor Nome',
            contact: 'Contacto',
            coordination_status: 'Estado de coordenação',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Nenhum vendor coordination items encontrado',
            assessment_placeholder: 'Avaliação inicial do fornecedor...',
            coordination_id: 'ID de coordenação',
            communications: 'Comunicações',
            info_requests: 'Solicitações de informações',
            action_requests: 'Solicitações de ação',
            direction: 'Direção',
            channel_label: 'Canal',
            no_transitions: 'Não há mais transições disponíveis.',
            vendor_ref_number: 'Número de referência',
            report_date: 'Data do relatório',
            contact_email: 'E-mail',
            contact_phone: 'Telefone',
            channel_email: 'E-mail',
            channel_meeting: 'Reunião',
            channel_phone: 'Telefone',
            channel_portal: 'Portal',
            incident_id_placeholder: 'Filtrar por Incidente ID...',
            priority_critical: 'Crítico',
            priority_high: 'Alto',
            priority_low: 'Low',
            priority_medium: 'Médio',
            vendor_id_placeholder: 'Filtrar por Fornecedor ID...',
            description_placeholder: 'Descrição...',
            filter_vendor_id: 'Filtrar por Fornecedor ID',
            inbound: 'Entrada',
            outbound: 'Saída',
            summary_placeholder: 'Resumo...',
            priority_label: 'Prioridade',
            due_date_label: 'Data de vencimento',
            info_request_desc: 'Descrição do pedido de informação',
            action_request_desc: 'Descrição da solicitação de ação',
            content_label: 'Conteúdo',
            select_incident: 'Selecionar Incidente',
            select_vendor: 'Selecionar Fornecedor',
            discovery_date: 'Data de descoberta',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Ator',
        },
        metrics: {
            page_title: 'Métricas de resposta',
            title: 'Métricas de resposta',
            loading: 'A carregar metrics data...',
            avg_response_time: 'Avg Resposta Hora',
            avg_resolution_time: 'Tempo médio de resolução',
            total_incidents: 'Total Incidentes',
            by_severity: 'Por Gravidade',
            by_status: 'Por Estado',
            trend: 'Tendência',
            start_sla_clock: 'Iniciar relógio SLA',
            sla_records: 'SLA Registros',
            no_sla_records: 'Ainda não há registos SLA.',
            record_id: 'ID do registro',
            severity: 'Gravidade',
            progress: 'Progresso',
            incident_id_required: 'O ID do incidente é obrigatório',
            sla_definition_required: 'A definição SLA é obrigatória',
            metric_type_required: 'O tipo de métrica é obrigatório',
            threshold_required: 'As horas limiar são obrigatórias',
            sla_clock_started: 'Relógio SLA iniciado',
            sla_definition: 'Definição SLA',
            no_definitions: 'Não foram encontradas definições SLA.',
            select_definition: 'Selecionar definição SLA...',
            select_metric_type: 'Selecionar tipo de métrica...',
            select_severity: 'Selecionar gravidade...',
            threshold_hours: 'Limiar (horas)',
            warning_threshold_pct: 'Limiar de aviso (%)',
            start_clock: 'Iniciar relógio',
            starting: 'A iniciar...',
            metric_label: 'Métrica',
            value_label: 'Valor',
            incident_id: 'ID do incidente',
            threshold_placeholder: 'ex. 24',
            metric_types: {
                triage_time: 'Tempo de triagem',
                containment_time: 'Tempo de contenção',
                notification_time: 'Tempo de notificação',
                resolution_time: 'Tempo de resolução',
            },
            severities: {
                critical: 'Crítico',
                high: 'Alto',
                medium: 'Médio',
                low: 'Low',
            },
            sla_reference: 'Referência SLA',
        },
        playbook_links: {
            page_title: 'Ligações Playbook',
            title: 'Ligações de notificação de playbook',
            new_link: 'Ligar playbook a notificação',
            activation_id: 'ID de ativação',
            notification_id: 'ID de notificação',
            select_activation: 'Selecionar uma ativação...',
            select_notification: 'Selecionar uma notificação...',
            no_links: 'Nenhuma ligação de playbook encontrada',
            seed_pdpl: 'Adicionar ligações de playbook modelo',
            created: 'Ligação de playbook criada',
            seeded: 'Ligações de playbook modelo adicionadas',
            create_failed: 'Falha ao criar a ligação de playbook',
            seed_failed: 'Falha ao adicionar as ligações de playbook modelo',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Nenhum quadro instalado rege a resposta a incidentes, pelo que não há prazos a adicionar.',
                declared_but_none_active: 'Está instalado um quadro que rege a resposta a incidentes, mas não está em vigor.',
                no_declared_deadlines: 'Os quadros de resposta a incidentes instalados não definem prazos de notificação.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Prazo',
            deadline_units: {
                hours: '{count} horas',
                working_days: '{count} dias úteis',
                calendar_days: '{count} dias corridos',
                days: '{count} dias',
                weeks: '{count} semanas',
                months: '{count} meses',
                years: '{count} anos',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Mapeamentos SLA regulatórios',
            title: 'Mapeamentos de SLA regulamentar',
            new_mapping: 'Novo mapeamento de SLA',
            sla_definition: 'Definição de SLA',
            select_sla_definition: 'Selecionar uma definição de SLA...',
            framework_code: 'Código do quadro',
            deadline_type: 'Tipo de prazo',
            deadline_hours: 'Horas de prazo',
            seed_pdpl: 'Adicionar mapeamentos a partir dos prazos instalados',
            no_mappings: 'Nenhum mapeamento de SLA regulamentar encontrado',
            dashboard: 'Painel de SLA regulamentar',
            deadline_types: {
                initial_notification: 'Notificação Inicial',
                supplement_response: 'Resposta ao Suplemento',
                authority_response: 'Resposta da autoridade',
                remediation_report: 'Relatório de correção',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Referência Estatutária',
        },
        coordination_dashboard: {
            page_title: 'Painel de coordenação',
            title: 'Painel de coordenação',
            incident_id: 'ID de incidente',
            select_incident: 'Selecionar um incidente...',
            team_role: 'Função da equipa',
            members: 'Membros',
            tasks: 'Tarefas',
            sla_status: 'Estado de SLA',
            no_teams: 'Nenhuma equipa de coordenação encontrada',
            new_team: 'Nova equipa de coordenação',
            enter_incident_id: 'Introduza um ID de incidente para ver as equipas de coordenação.',
            member_ids_placeholder: 'usuário1, usuário2',
            edit_team: 'Editar equipa',
            task_count: 'Contagem de tarefas',
            tasks_completed: 'Concluído',
            team_roles: {
                security: 'Segurança',
                legal: 'Jurídico',
                it: 'IT',
                management: 'Gestão',
                department_owner: 'Responsável de departamento',
                external: 'Externo',
            },
        },
        evidence_chain: {
            page_title: 'Cadeia de evidências',
            title: 'Cadeia de custódia de provas',
            new_entry: 'Nova entrada de prova',
            select_incident: 'Selecionar um incidente...',
            actor: 'Ator',
            select_actor: 'Selecionar um ator...',
            evidence_id: 'ID de prova',
            action: 'Ação',
            recipient: 'Destinatário',
            no_entries: 'Nenhuma entrada de cadeia de provas encontrada',
            enter_incident_id: 'Introduza um ID de incidente para ver a cadeia de evidências.',
            evidence_id_placeholder: 'ex. EVD-2026-0001',
            evidence_id_help: 'Uma referência única para o item de evidência — um ficheiro de registo, uma imagem de disco ou uma captura de ecrã. Uma sugestão está preenchida; edite-a para corresponder ao seu catálogo de evidências.',
            select_incident_first: 'Selecione um incidente acima antes de adicionar uma entrada de evidência.',
            for_incident: 'Para o incidente',
            actions: {
                collected: 'Coletado',
                shared: 'Compartilhado',
                exported: 'Exportado',
                archived: 'Arquivado',
            },
        },
        post_review: {
            page_title: 'Revisão pós-incidente',
            title: 'Revisão pós-incidente',
            new_review: 'Nova revisão',
            select_incident: 'Selecionar um incidente...',
            lessons_learned: 'Lições aprendidas',
            recommendations: 'Recomendações',
            improvement_actions: 'Ações de melhoria',
            submit_for_review: 'Submeter para revisão',
            aria_submit_review: 'Submeter revisão {name} para revisão',
            aria_sign_off: 'Aprovar revisão {name}',
            sign_off: 'Aprovação',
            no_reviews: 'Nenhuma revisão pós-incidente encontrada',
            enter_incident_id: 'Introduza um ID de incidente para ver a revisão pós-incidente.',
            one_per_line: 'Um por linha',
            statuses: {
                draft: 'Rascunho',
                in_review: 'Em revisão',
                signed_off: 'Aprovado',
                closed: 'Encerrado',
            },
        },
        activations: {
            title: 'Ativações do manual',
            activation_id: 'ID de ativação',
            phases: 'Fases',
            activated_at: 'Ativado em',
            activated_by: 'Ativado por',
            deactivate: 'Desativar',
            complete: 'Concluir',
            deactivation_reason: 'Motivo de desativação',
            evidence_button: 'Evidência',
            evidence_modal_title: 'Evidência de ativação',
            no_evidence: 'Nenhuma evidência anexada a esta ativação.',
            upload_evidence_label: 'Carregar evidência de ativação',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Me guie pelas lacunas',
            running: 'Lacunas de caminhada…',
            results_heading: 'Roteiro de melhoria ({count} lacuna{plural})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Nenhuma lacuna detectada – todos os domínios na meta ou acima dela.',
            col_priority: 'Prioridade',
            col_domain: 'Domínio',
            col_levels: 'Atual → Alvo',
            col_severity: 'Gravidade',
            col_actions: 'Ações de remediação',
            citations_label: 'Citações',
            accept_label: 'Aceitar roteiro',
            reject_label: 'Rejeitar rascunho',
            export_label: 'Exportar DOCX',
            exported_label: 'Roteiro exportado',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Resolução: {status}',
            resolve_status_accepted: 'Aceito',
            resolve_status_rejected: 'Rejeitado',
            error_generic: 'Não foi possível executar o passo a passo do gap. Tente novamente ou verifique o status da avaliação.',
        },
        policy_gap: {
            run_label: 'Detectar lacunas políticas',
            running: 'Detectando lacunas…',
            results_heading: 'Lacunas políticas ({count})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Não foram detectadas lacunas de cobertura.',
            col_severity: 'Gravidade',
            col_kind: 'Gentil',
            col_clause: 'Cláusula/requisito',
            kind_missing: 'Cláusula ausente',
            kind_weak: 'Cobertura fraca',
            kind_stale: 'Obsoleto (revisão atrasada)',
            remediation_label: 'Correção sugerida',
            citations_label: 'Citações',
            accept_label: 'Aceitar relatório de lacunas',
            reject_label: 'Rejeitar relatório',
            export_label: 'Exportar DOCX',
            accept_hint: 'Aceitar registra o relatório de lacunas no rascunho persistente. Use Exportar DOCX para gerar um Relatório de Auditoria de Política; o operador conduz a edição real da política.',
            resolve_summary: 'Resolução: {status}',
            error_generic: 'Não foi possível detectar lacunas políticas. Tente novamente ou verifique o status do documento.',
        },
        remediation_plan_generator: {
            run_label: 'Gerar plano a partir de lacunas',
            running: 'Gerando plano…',
            results_heading: 'Plano de remediação ({phase_count} fase{plural})',
            tokens_label: '≈ {count} tokens',
            phase_label: 'Fase {index}',
            window_label: 'Janela',
            owner_label: 'Função de proprietário',
            evidence_label: 'Tipos de evidências necessários',
            tasks_label: 'Tarefas',
            no_phases: 'Nenhuma fase produzida - o conjunto de lacunas de entrada pode estar vazio.',
            citations_label: 'Citações',
            accept_label: 'Aceitar plano',
            reject_label: 'Rejeitar plano',
            export_label: 'Exportar DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Resolução: {status} · {tasks} tarefa(s) criada(s)',
            error_generic: 'Não foi possível gerar o plano de correção. Tente novamente ou verifique a entrada do conjunto de lacunas.',
            source_picker_label: 'Fonte',
            source_all: 'Todos',
            source_consent: 'Análise de lacuna de consentimento',
            source_policy: 'Detecção de lacunas políticas',
            source_vendor: 'Integração do fornecedor',
            load_upstream_label: 'Carregar do upstream',
            loading_upstream: 'Carregando…',
            upstream_count: '{count} lacuna(s) carregada(s)',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Modelos de maturidade',
            maturity_assessments: 'Avaliações de maturidade',
            trend_dashboard: 'Painel de tendências',
            reporting_periods: 'Períodos de relatório',
            executive_summaries: 'Resumos executivos',
            audit_readiness: 'Preparação para auditoria',
            evidence_gaps: 'Lacunas de evidência',
            program_health: 'Estado do programa',
            obligation_mappings: 'Mapeamentos de obrigações',
            governance_documents: 'Documentos de governança',
            remediation_plans: 'Planos de remediação',
            governance_model: 'Modelo de governança',
            document_drafting: 'Elaboração de documentos',
            document_issuance: 'Emissão de documentos',
            acknowledgment_tracking: 'Rastreamento de confirmações',
            change_log: 'Registro de alterações',
            review_cadence: 'Cadência de revisão',
        },
        maturity_models: {
            title: 'Modelos de maturidade',
            new: 'Novo Model',
            seed_defaults: 'Predefinições iniciais',
            domains: 'Domínios',
            activate: 'Ativar',
            no_models: 'Nenhum maturity models encontrado',
            new_maturity_model: 'Novo Maturity Model',
            create_model: 'Adicionar Model',
            load_error: 'Falha ao carregar modelos de maturidade. Tente novamente.',
            create_error: 'Falha ao criar modelo de maturidade',
            status_error: 'Falha ao atualizar status do modelo',
            seed_error: 'Falha ao gerar valores padrão',
            aria_seed_defaults: 'Gerar modelos de maturidade padrão',
            aria_create_new: 'Criar novo modelo de maturidade',
            loading_sr: 'Carregando modelos de maturidade',
            model_name_placeholder: 'ex., Modelo de maturidade de privacidade',
            status_changed: 'Status do modelo alterado para {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Avaliações de maturidade',
            new: 'Novo avaliação',
            model: 'Modelo',
            assessor: 'Avaliador',
            overall_score: 'Pontuação geral',
            overall_score_help: 'Uma classificação de maturidade ponderada de 0 a 5, calculada automaticamente quando uma avaliação é finalizada. Aumente-a atribuindo uma pontuação mais alta a cada domínio durante a avaliação e, em seguida, levando a avaliação através de Enviar para revisão → Finalizar.',
            assessed_at: 'Avaliado em',
            no_assessments: 'Nenhum maturity assessments encontrado',
            load_error: 'Falha ao carregar avaliações de maturidade. Tente novamente.',
            create_error: 'Falha ao criar avaliação',
            start_error: 'Falha ao iniciar avaliação',
            archive_error: 'Falha ao arquivar avaliação',
            eval_error: 'Falha ao carregar avaliação para avaliação',
            eval_min_score: 'Avalie pelo menos um domínio',
            eval_save_error: 'Falha ao salvar avaliações de domínio',
            review_error: 'Falha ao enviar para revisão',
            finalize_error: 'Falha ao finalizar avaliação',
            start: 'Iniciar',
            start_confirm_title: 'Iniciar avaliação',
            start_confirm_message: 'Tem certeza de que deseja iniciar esta avaliação?',
            archive_confirm_title: 'Arquivar avaliação',
            archive_confirm_message: 'Tem certeza de que deseja arquivar esta avaliação?',
            evaluate_domains: 'Avaliar',
            evaluate_domains_title: 'Avaliar domínios',
            submit_for_review: 'Enviar para revisão',
            finalize: 'Finalizar',
            archive: 'Arquivar',
            all_reviewers: 'Todos os revisores devem aprovar',
            any_reviewer: 'Qualquer revisor pode aprovar',
            select_level: 'Selecionar nível de maturidade...',
            findings_placeholder: 'Descobertas, notas, referências de evidências...',
            no_domains: 'Nenhum domínio encontrado para este modelo.',
        },
        trend_dashboard: {
            title: 'Painel de tendências',
            loading: 'A carregar trend data...',
            score_trend: 'Tendência de pontuação',
            domain_comparison: 'Comparação de domínios',
            period: 'Período',
            load_error: 'Falha ao carregar resumo de tendência. Tente novamente.',
            metrics_load_error: 'Falha ao carregar métricas de tendência. Tente novamente.',
            module: 'Módulo',
            metric: 'Métrica',
            value: 'Valor',
            trend: 'Tendência',
            aria_filters: 'Filtros de métricas de tendência',
            showing_chart: 'Exibindo vista de gráfico',
            showing_table: 'Exibindo vista de tabela',
            create_metric: 'Criar métrica',
            data_points: 'Pontos de dados',
            trend_direction: 'Direção da tendência',
            unit: 'Unidade',
        },
        reporting_periods: {
            title: 'Períodos de relatório',
            new: 'Novo período',
            period_name: 'Período Nome',
            start_date: 'Início Data',
            end_date: 'Fim Data',
            no_periods: 'Nenhum reporting periods encontrado',
            load_error: 'Falha ao carregar períodos de relatório. Tente novamente.',
            create_error: 'Falha ao criar período de relatório',
            auto_generate_error: 'Falha ao gerar automaticamente períodos',
            status_error: 'Falha ao atualizar status do período',
            start_collecting: 'Iniciar coleta',
            mark_as_draft: 'Marcar como rascunho',
            submit_for_review: 'Enviar para revisão',
            publish: 'Publicar',
            auto_generate: 'Gerar automaticamente períodos',
            period_label_placeholder: 'ex., T1 2026',
            status_updated: 'Status do período atualizado',
        },
        executive_summaries: {
            title: 'Resumos executivos',
            new: 'Novo resumo',
            period: 'Período',
            generated_at: 'Gerado em',
            generated_by: 'Gerado por',
            no_summaries: 'Nenhum executive summaries encontrado',
            generate: 'Gerar',
            load_error: 'Falha ao carregar resumos executivos. Tente novamente.',
            generate_error: 'Falha ao gerar resumo executivo',
            save_error: 'Falha ao salvar resumo executivo',
        },
        audit_readiness: {
            title: 'Preparação para auditoria',
            loading: 'A carregar audit readiness data...',
            overall_readiness: 'Preparação geral',
            by_domain: 'Por Domínio',
            evidence_coverage: 'Cobertura de evidências',
            gaps: 'Lacunas',
            load_error: 'Falha ao carregar última prontidão. Tente novamente.',
            history_error: 'Falha ao carregar histórico de prontidão. Tente novamente.',
            calculate_error: 'Falha ao calcular prontidão de auditoria',
            aria_recalculate: 'Recalcular pontuação de prontidão',
            aria_current_readiness: 'Prontidão atual de auditoria',
            aria_history: 'Histórico de pontuação de prontidão',
            recalculated: 'Prontidão de auditoria recalculada',
            loading_sr: 'Carregando dados de prontidão',
        },
        evidence_gaps: {
            title: 'Lacunas de evidência',
            domain: 'Domínio',
            control: 'Controlo',
            gap_type: 'Tipo de lacuna',
            severity: 'Gravidade',
            no_gaps: 'Nenhum evidence gaps encontrado',
            load_error: 'Falha ao carregar lacunas de evidência. Tente novamente.',
            stats_error: 'Falha ao carregar estatísticas. Tente novamente.',
            create_error: 'Falha ao criar lacuna de evidência',
            update_error: 'Falha ao atualizar lacuna',
            status_error: 'Falha ao atualizar status',
            aria_create_new: 'Criar nova lacuna de evidência',
            aria_statistics: 'Estatísticas de lacunas',
            loading_sr: 'Carregando lacunas',
            status_updated: 'Status da lacuna atualizado',
            entity_type_placeholder: 'ex., fornecedor, sistema',
            all_modules: 'Todos os domínios',
            all_severities: 'Todas as gravidades',
        },
        program_health: {
            title: 'Estado do programa',
            loading: 'A carregar program health data...',
            overall_health: 'Saúde geral',
            by_module: 'Por módulo',
            risk_areas: 'Áreas de risco',
            recommendations: 'Recomendações',
            load_error: 'Falha ao carregar dados de saúde do programa. Tente novamente.',
            aria_key_metrics: 'Métricas-chave de saúde do programa',
            aria_module_scorecard: 'Detalhamento do scorecard do módulo',
            loading_sr: 'Carregando dados de saúde do programa',
            trending_up: 'Tendência de alta',
            trending_down: 'Tendência de queda',
            trending_stable: 'Estável',
            data_loaded: 'Dados de saúde do programa carregados',
        },
        obligation_mappings: {
            title: 'Mapeamentos de obrigações',
            create_mapping: 'Criar mapeamento de obrigação',
            framework: 'Quadro regulamentar',
            select_framework: 'Selecionar um quadro...',
            obligation: 'Obrigação',
            obligation_ref: 'Referência de obrigação',
            obligation_description: 'Descrição da obrigação',
            role: 'Papel',
            assigned_role: 'Papel atribuído',
            select_role: 'Selecionar um papel...',
            assigned_department: 'Departamento atribuído',
            select_department: 'Selecionar um departamento...',
            control_ids: 'IDs de controlo',
            control_ids_placeholder: 'ex.: A.5.1, CC6.1',
            control_ids_hint: 'Códigos de referência de controlo separados por vírgulas.',
            raci_assignments: 'Atribuições RACI',
            create_raci: 'Criar atribuição RACI',
            activity: 'Atividade',
            activity_name: 'Nome da atividade',
            activity_description: 'Descrição da atividade',
            responsible: 'Responsável',
            select_users: 'Selecionar utilizadores...',
            accountable: 'Prestador de contas',
            select_user: 'Selecionar um utilizador...',
            consulted: 'Consultado',
            informed: 'Informado',
            review_frequency: 'Frequência de revisão',
            frequency_monthly: 'Mensal',
            frequency_quarterly: 'Trimestral',
            frequency_semi_annual: 'Semestral',
            frequency_annual: 'Anual',
            no_mappings_found: 'Nenhum mapeamento de obrigações encontrado.',
            no_raci_found: 'Nenhuma atribuição RACI encontrada.',
            mapping_created: 'Mapeamento de obrigação criado',
            mapping_updated: 'Mapeamento de obrigação atualizado',
            mapping_deleted: 'Mapeamento de obrigação eliminado',
            raci_created: 'Atribuição RACI criada',
            raci_updated: 'Atribuição RACI atualizada',
            raci_deleted: 'Atribuição RACI eliminada',
            aria_edit_mapping: 'Editar mapeamento {name}',
            aria_delete_mapping: 'Eliminar mapeamento {name}',
            aria_edit_raci: 'Editar RACI {name}',
            aria_delete_raci: 'Eliminar RACI {name}',
            confirm_delete_mapping: 'Eliminar este mapeamento de obrigação?',
            confirm_delete_raci: 'Eliminar esta atribuição RACI?',
            table_view: 'Vista de tabela',
            matrix_view: 'Vista de matriz',
            print_export: 'Imprimir / Exportar',
            filter_framework: 'Filtrar por framework',
            no_matrix_data: 'Nenhuma atribuição RACI para exibir na vista de matriz.',
            aria_print_export: 'Imprimir ou exportar a matriz RACI',
        },
        governance_documents: {
            title: 'Documentos de governança',
            create_document: 'Criar documento de governança',
            document_title: 'Título do documento',
            document_type: 'Tipo de documento',
            content_url: 'URL do conteúdo',
            content_url_placeholder: 'https://...',
            framework_codes: 'Códigos de quadro',
            select_frameworks: 'Selecionar quadros...',
            effective_date: 'Data de vigência',
            review_date: 'Data de revisão',
            status: 'Estado',
            version: 'Versão',
            acknowledgments: 'Confirmações',
            acknowledge: 'Confirmar',
            acknowledged_by: 'Confirmado por',
            department: 'Departamento',
            no_documents_found: 'Nenhum documento de governança encontrado.',
            draft: 'Rascunho',
            review: 'Revisão',
            approved: 'Aprovado',
            issued: 'Emitido',
            superseded: 'Substituído',
            document_created: 'Documento criado',
            document_acknowledged: 'Documento reconhecido',
            policy: 'Política',
            procedure: 'Procedimento',
            standard: 'Padrão',
            guideline: 'Diretriz',
        },
        governance_model: {
            title: 'Modelo de governança',
            create_model: 'Modelo de governança',
            edit_model: 'Modelo de governança',
            model_name: 'Model',
            description: 'Descrição',
            governance_type: 'Type',
            type_centralized: 'Centralizado',
            type_federated: 'Federado',
            type_hybrid: 'Híbrido',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Mensalmente',
            freq_quarterly: 'Trimestralmente',
            freq_semi_annual: 'Semestral',
            freq_annual: 'Anual',
            committees: 'Comitês',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandato',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Escopo',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Atividade',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'Você precisa da permissão regulatory_framework:read para selecionar estruturas. Peça a um administrador para concedê-lo.',
            select_members: 'Selecione os membros do comitê',
            select_from_role: 'Selecione a função de delegação',
            select_to_role: 'Selecione a função de recebimento',
            mandate_markdown_hint: 'Markdown suportado: **negrito**, listas, links.',
            scope_tab_obligation: 'Obrigação',
            scope_tab_framework: 'Estrutura',
            scope_tab_free_text: 'Texto livre',
            select_obligation: 'Selecione uma obrigação',
            select_framework: 'Selecione uma estrutura',
            aria_remove_committee: 'Remover comité {n}',
            aria_remove_delegation_rule: 'Remover regra de delegação {n}',
            no_frameworks_available: 'Nenhum quadro disponível. Adicione primeiro quadros regulatórios.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Elaboração de documentos',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Estado',
            drafter: 'Desenhador',
            deadline: 'Prazo',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Atribuir',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Revisor',
            select_reviewer: '...',
            comments: 'Comentários',
            review_action: 'Action',
            approve: 'Aprovar',
            request_changes: 'Solicitar alterações',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Emissão de documentos',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Estado',
            version: 'Versão',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Rastreamento de confirmações',
            document_title: 'Documento',
            acknowledged_count: 'Reconhecido',
            pending_count: 'Pendente',
            percentage: '%',
            details: 'Detalhes',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Usuário',
            department: 'Departamento',
            acknowledged_at: 'Date',
            status: 'Estado',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Registro de alterações',
            date: 'Data',
            document: 'Documento',
            change_type: 'Type',
            actor: 'Ator',
            summary: 'Resumo',
            export_csv: 'Exportar CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Cadência de revisão',
            configure_cadence: 'Cadência de revisão',
            edit_cadence: 'Cadência de revisão',
            document: 'Documento',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Estado',
            overdue: 'Atrasado',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Planos de remediação',
            create_plan: 'Criar plano de remediação',
            gap_id: 'Referência de lacuna',
            select_gap: 'Selecionar uma lacuna...',
            plan_title: 'Título',
            description: 'Descrição',
            priority: 'Prioridade',
            priority_critical: 'Crítica',
            priority_high: 'Alta',
            priority_medium: 'Média',
            priority_low: 'Baixa',
            assigned_to: 'Atribuído a',
            select_user: 'Selecionar um utilizador...',
            target_date: 'Data alvo',
            framework_code: 'Quadro',
            select_framework: 'Selecionar um quadro...',
            gap_finding: 'Constatação de lacuna',
            action_items: 'Itens de ação',
            owner: 'Proprietário',
            deadline: 'Prazo',
            planned: 'Planeado',
            in_progress: 'Em curso',
            completed: 'Concluído',
            verified: 'Verificado',
            no_plans_found: 'Nenhum plano de remediação encontrado.',
            evidence: 'Evidência',
            plan_created: 'Plano de remediação criado',
            action_items_hint: 'Itens de ação (um por linha)',
        },
        statuses: {
            identified: 'Identificado',
            assigned: 'Atribuído',
            in_remediation: 'Em Remediação',
            resolved: 'Resolvido',
            accepted_risk: 'Risco Aceito',
            planned: 'Planejado',
            in_progress: 'Em andamento',
            under_review: 'Em revisão',
            finalized: 'Finalizado',
            archived: 'Arquivado',
            draft: 'Rascunho',
            active: 'Ativo',
            upcoming: 'Próximos',
            collecting: 'Coletando',
            published: 'Publicado',
            review: 'Revisão',
            approved: 'Aprovado',
            issued: 'Emitido',
            superseded: 'Substituído',
            completed: 'Concluído',
            verified: 'Verificado',
        },
        severities: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Low',
        },
        priorities: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Low',
        },
        gap_types: {
            missing: 'Desaparecido',
            expired: 'Expirado',
            insufficient: 'Insuficiente',
        },
        document_types: {
            policy: 'Política',
            procedure: 'Procedimento',
            standard: 'Padrão',
            guideline: 'Diretriz',
            template: 'Modelo (interno)',
        },
        maturity_levels: {
            level_1: 'Nível 1 – Inicial',
            level_2: 'Nível 2 – Desenvolvimento',
            level_3: 'Nível 3 – Definido',
            level_4: 'Nível 4 – Gerenciado',
            level_5: 'Nível 5 – Otimizando',
        },
        grades: {
            excellent: 'Excelente',
            good: 'Bom',
            fair: 'Justo',
            poor: 'Pobre',
            critical: 'Crítico',
            needs_improvement: 'Precisa de melhorias',
            not_assessed: 'Não avaliado',
        },
        modules: {
            data_mapping: 'Mapeamento de dados',
            consent: 'Consentimento',
            governance: 'Governança',
            incidents: 'Incidentes',
            monitoring: 'Monitoramento',
            classification: 'Classificação',
            vendor_risk: 'Risco de fornecedores',
            discovery: 'Descoberta',
            deletion: 'Exclusão',
        },
        cadences: {
            monthly: 'Mensalmente',
            quarterly: 'Trimestralmente',
            semi_annual: 'Semestral',
            annual: 'Anual',
        },
        section_labels: {
            opening: 'Abertura',
            key_metrics: 'Métricas-chave',
            highlights: 'Destaques',
            concerns: 'Preocupações',
            recommendations: 'Recomendações',
            outlook: 'Perspectivas',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} lacuna',
        count_gaps_plural: '{count} lacunas',
        count_summaries: '{count} resumo',
        count_summaries_plural: '{count} resumos',
        count_assessments: 'Avaliação {count}',
        count_assessments_plural: '{count} avaliações',
        count_models: 'Modelo {count}',
        count_models_plural: '{count} modelos',
        count_periods: 'Período {count}',
        count_periods_plural: '{count} períodos',
        count_metrics: 'Métrica {count}',
        count_metrics_plural: '{count} métricas',
        unknown_user: 'Usuário desconhecido',
        unknown_gap: 'Lacuna desconhecida',
        no_permission_title: 'Permissões insuficientes',
        no_permission_message: 'Você não tem permissão para acessar esta página.',
        ac_vn_frameworks_title: 'Cobertura da estrutura VN',
        ac_vn_frameworks_body: 'As avaliações de maturidade abrangem todos os 6 quadros regulamentares vietnamitas: PDPL, Lei de Dados, Lei de Telecomunicações, Lei de Segurança Cibernética, Lei AI e Lei de Comércio Eletrónico — cada uma com critérios de avaliação específicos do quadro.',
        ac_controller_processor_title: 'Controlador vs. Processador',
        ac_controller_processor_body: 'Os mapeamentos de obrigações distinguem entre as responsabilidades do controlador de dados e do processador de dados. Atribua funções usando a matriz RACI para garantir que cada obrigação tenha um proprietário claro, quer a organização atue como controladora ou processadora.',
        ac_versioned_evidence_title: 'Trilha de evidências versionadas',
        ac_versioned_evidence_body: 'Cada transição de status (Rascunho, Revisão, Aprovado, Emitido) cria um registro com carimbo de data/hora e versão que serve como evidência auditável. O Log de alterações captura o histórico completo do ciclo de vida do documento.',
        ac_remediation_gap_link_title: 'Vinculado a lacunas de avaliação',
        ac_remediation_gap_link_body: 'Cada plano de remediação está diretamente ligado a uma lacuna de avaliação identificada. Atribua um proprietário e defina um prazo para acompanhar o progresso da resolução desde a lacuna até o fechamento.',
        ac_annual_attestation_title: 'Ciclo Anual de Certificação',
        ac_annual_attestation_body: 'Os períodos de relatório estão alinhados com o requisito de certificação anual da Lei de Dados Vietnamita. Use a cadência "Anual" (ciclo de 365 dias) para gerar automaticamente períodos alinhados ao atestado.',
        ac_evidence_per_obligation_title: 'Evidência por obrigação estrutural',
        ac_evidence_per_obligation_body: 'As lacunas nas evidências identificam provas faltantes, expiradas ou insuficientes para cada obrigação-quadro. Filtre por domínio para ver quais requisitos regulatórios não possuem evidências de apoio.',
        ac_cross_framework_title: 'Completude entre estruturas',
        ac_cross_framework_body: 'A preparação para auditoria fornece uma visão de integridade entre estruturas. As pontuações dos componentes refletem a cobertura de todas as estruturas mapeadas, destacando áreas que precisam de atenção antes de uma auditoria.',
        ac_cadence_default_title: 'Cadência de revisão padrão',
        ac_cadence_default_body: 'A cadência de revisão padrão é de 180 dias. As revisões atrasadas são sinalizadas automaticamente. Ajuste a frequência por documento conforme necessário — as opções variam de 30 a 365 dias.',
        ac_templates_internal_title: 'Classificação do modelo',
        ac_templates_internal_body: 'Os modelos são classificados como documentos internos. Utilize o tipo de documento "Modelo" para marcar artefactos de governação reutilizáveis ​​com âmbito de distribuição apenas interno.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Escritas pendentes',
        subtitle: 'Os clientes Claude externos propõem escritas aqui. Aprove ou recuse cada entrada — o Forseti só executa após autorização do administrador.',
        no_pending: 'Sem escritas pendentes.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Ferramenta',
        col_preview: 'Pré-visualização',
        col_key: 'Chave API',
        col_requested: 'Solicitada',
        col_expires: 'Expira',
        col_status: 'Estado',
        status_pending: 'Pendente',
        status_approved: 'Aprovada',
        status_declined: 'Recusada',
        status_expired: 'Expirada',
        status_executed: 'Executada',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Aprovar',
        decline_label: 'Recusar',
        refresh_label: 'Atualizar',
        error_generic: 'Não foi possível carregar as escritas pendentes.',
        confirm_approve: 'Aprovar irá executar a ferramenta na sua organização com as permissões resolvidas do titular da API key. Continuar?',
        confirm_decline: 'Recusar esta escrita pendente? Esta ação não pode ser anulada.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'As escritas pendentes expiram 24 horas após a criação. A varredura GC horária recusa automaticamente as entradas expiradas.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Ações',
    },
    audit_walkthrough: {
        title: 'Forseti — Gerador de roteiro de auditoria',
        subtitle: 'Gere um roteiro estruturado para o arranque de uma auditoria: pergunta inicial por controlo, evidências esperadas, perguntas de seguimento, possíveis lacunas.',
        audit_picker_label: 'Auditoria',
        audit_picker_hint: 'Escolha uma auditoria planeada para gerar um roteiro.',
        run_label: 'Gerar roteiro',
        running: 'A gerar…',
        results_heading: 'Roteiro — {count} controlo(s)',
        tokens_label: '≈ {count} tokens',
        no_controls: 'Sem controlos no âmbito. Adicione controlos ao registo de auditoria.',
        col_control: 'Controlo',
        col_question: 'Pergunta inicial',
        col_evidence: 'Evidência',
        col_gaps: 'Possíveis lacunas',
        expected_evidence_label: 'Evidência esperada',
        followup_probes_label: 'Perguntas de seguimento',
        accept_label: 'Aceitar roteiro',
        reject_label: 'Rejeitar',
        export_label: 'Exportar DOCX',
        resolve_summary: 'Resolução: {status}',
        resolve_status_accepted: 'Aceito',
        resolve_status_rejected: 'Rejeitado',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Não foi possível gerar o roteiro. Tente novamente ou verifique o estado da auditoria.',
    },
    privilege_creep: {
        title: 'Forseti — Deteção de acumulação de privilégios',
        subtitle: 'Verificação periódica das atribuições de papéis. Os achados são consultivos — o Forseti nunca revoga automaticamente.',
        run_label: 'Verificar agora',
        running: 'A verificar…',
        findings_heading: '{count} achado(s)',
        no_findings: 'Sem acumulação de privilégios sinalizada.',
        col_user: 'Utilizador',
        col_job_function: 'Função',
        col_severity: 'Gravidade',
        col_unexpected: 'Permissões inesperadas',
        col_remediation: 'Remediação sugerida',
        col_status: 'Estado',
        severity_critical: 'Crítica',
        severity_high: 'Alta',
        severity_medium: 'Média',
        severity_low: 'Baixa',
        status_open: 'Aberto',
        status_dismissed: 'Descartado',
        status_remediated: 'Remediado',
        dismiss_label: 'Descartar',
        remediate_label: 'Marcar como remediado',
        note_label: 'Nota (opcional)',
        error_generic: 'Não foi possível verificar a acumulação de privilégios. Tente mais tarde.',
        advisory_banner: 'Estes achados são consultivos. O Forseti nunca revoga permissões automaticamente — analise com o proprietário do papel antes de agir.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Mapeamento automático de regras de conformidade',
            subtitle: 'Forseti percorre um pacote regulatório instalado e sugere regras de conformidade novas ou atualizadas com base nos artigos originais.',
            pack_picker_label: 'Pacote regulatório',
            pack_picker_hint: 'Selecione um pacote instalado para mapear.',
            run_label: 'Mapa do pacote',
            running: 'Mapeamento…',
            results_heading: '{count} sugestões',
            tokens_label: '≈ {count} tokens do seu balde mensal',
            no_suggestions: 'Sem sugestões — o conteúdo do pacote pode já estar totalmente coberto pelas regras existentes.',
            kind_new: 'Nova regra',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Nenhuma alteração necessária',
            col_kind: 'Gentil',
            col_title: 'Título proposto',
            col_existing: 'Regra existente',
            col_source: 'Fonte',
            proposed_body_label: 'Corpo proposto',
            source_quote_label: 'Citação da fonte',
            accept_selected: 'Aceitar selecionado',
            reject_all: 'Rejeitar tudo',
            accept_all: 'Aceitar tudo',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Não foi possível executar o mapeamento de regras. Tente novamente ou verifique o código do pacote.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Painel de sessão',
            access_log: 'Registo de acessos',
            justification_review: 'Revisão de justificações',
            investigations: 'Investigações',
            pattern_analysis: 'Análise de padrões',
            anchoring_status: 'Estado de ancoragem',
            configuration: 'Configuração',
            access_policies: 'Políticas de acesso',
            review_campaigns: 'Campanhas de revisão',
            evidence_packages: 'Pacotes de evidência',
        },
        session_dashboard: {
            title: 'Painel de sessão',
            loading: 'A carregar os dados do painel...',
            total_sessions: 'Total de sessões',
            active_sessions: 'Sessões ativas',
            avg_duration: 'Duração média',
            peak_day_sessions: 'Dia com mais atividade',
            auth_method_breakdown: 'Discriminação de métodos de autenticação',
            geo_country_breakdown: 'Discriminação por país',
            recent_session_events: 'Eventos de sessão recentes',
            user_id: 'ID do utilizador',
            session_id: 'ID da sessão',
            ip_address: 'Endereço IP',
            auth_method: 'Método de autenticação',
            started_at: 'Iniciado em',
            event_type: 'Tipo de evento',
            all_event_types: 'Todos os tipos de evento',
            filter_by_user: 'Filtrar por utilizador',
            no_active_sessions: 'Sem sessões ativas',
            no_session_events: 'Nenhum evento de sessão encontrado',
            revoke: 'Revogar',
            revoke_session: 'Revogar sessão',
            aria_revoke_session: 'Revogar a sessão {user}',
            revoke_reason: 'Motivo',
            revoke_placeholder: 'Forneça um motivo para revogar esta sessão...',
            session_label: 'Sessão:',
            load_failed: 'Falha ao carregar o painel de sessão. Por favor, tente novamente.',
            events_load_failed: 'Falha ao carregar os eventos de sessão. Por favor, tente novamente.',
            revoke_failed: 'Falha ao revogar a sessão. Por favor, tente novamente.',
            session_events_loaded: '{n} eventos de sessão carregados',
            event_types: {
                session_started: 'Sessão iniciada',
                session_refreshed: 'Sessão atualizada',
                session_expired: 'Sessão expirada',
                session_revoked: 'Sessão revogada',
                session_terminated: 'Sessão encerrada',
            },
        },
        access_log: {
            title: 'Registo de acessos',
            user_id: 'Utilizador ID',
            resource: 'Recurso',
            action: 'Ação',
            ip_address: 'IP Endereço',
            timestamp: 'Marca temporal',
            no_logs: 'Nenhum access log entries encontrado',
            filter_by_user: 'Filtrar por Utilizador ID...',
            load_failed: 'Falha ao load access log. Por favor, tente novamente.',
            export_success: 'Exportar initiated com sucesso',
            export_failed: 'Falha ao export access log. Por favor, tente novamente.',
            record_count: 'registo',
            record_count_plural: 'registos',
            unclassified: 'Não classificado',
            classification_missing: 'Classificação ausente',
            classification_level: 'Nível de Classificação',
            response_code: 'Codigo de resposta',
            access_surface: 'Canal de acesso',
            api_key: 'Chave API',
            classification_levels: {
                public: 'Público',
                internal: 'Interno',
                confidential: 'Confidencial',
                restricted: 'Restrito',
                unclassified: 'Não classificado',
            },
        },
        justification_review: {
            title: 'Revisão de justificações',
            user_id: 'Utilizador ID',
            resource: 'Recurso',
            justification: 'Justificação',
            submitted_at: 'Submetido em',
            reviewer: 'Revisor',
            no_reviews: 'Nenhum justification reviews encontrado',
            load_failed: 'Falha ao load justifications. Por favor, tente novamente.',
            review_failed: 'Falha ao review justification. Por favor, tente novamente.',
            decline_failed: 'Falha ao decline access. Por favor, tente novamente.',
            justification_count: 'justificação',
            justification_count_plural: 'justificações',
            reviewed: 'Revisto',
            concerning: 'Preocupante',
            escalated: 'Escalado',
            link_investigation_placeholder: 'Ligar a uma investigação (opcional)',
            review_queue: 'Fila de revisão',
            access_id_label: 'ID de acesso',
            access_id_placeholder: 'ID da entrada do log de acesso',
            resource_type_label: 'Tipo de recurso',
            resource_type_placeholder: 'ex. document, database',
            resource_id_label: 'ID do recurso',
            resource_id_placeholder: 'Identificador do recurso',
            reason_placeholder: 'Motivo para acessar este recurso...',
            create_failed: 'Falha ao create justification',
            unclassified: 'Não classificado',
            review_statuses: {
                pending: 'Pendente',
                reviewed: 'Revisado',
                concerning: 'Relativo',
                escalated: 'Escalado',
            },
            reason_codes: {
                business_need: 'Necessidade de negócios',
                legal_obligation: 'Obrigação Legal',
                data_subject_request: 'Solicitação do Titular dos Dados',
                audit_requirement: 'Requisito de auditoria',
                incident_response: 'Resposta a Incidentes',
                research: 'Pesquisa',
                administrative: 'Administrativo',
                other: 'Outro',
            },
            classification_level: 'Nível de Classificação',
            review_decision: 'Rever decisão',
        },
        investigations: {
            title: 'Espaço de investigação',
            new: 'Novo Investigation',
            subject: 'Titular',
            investigator: 'Investigador',
            opened_at: 'Aberto Em',
            findings: 'Constatações',
            no_investigations: 'Nenhum investigations encontrado',
            load_failed: 'Falha ao load investigations. Por favor, tente novamente.',
            create_failed: 'Falha ao create investigation. Por favor, tente novamente.',
            timeline_add_failed: 'Falha ao add timeline entry. Por favor, tente novamente.',
            link_access_failed: 'Falha ao link access ids. Por favor, tente novamente.',
            link_justification_failed: 'Falha ao link justification ids. Por favor, tente novamente.',
            close_failed: 'Falha ao close investigation. Por favor, tente novamente.',
            export_success: 'Exportar initiated com sucesso',
            export_failed: 'Falha ao export investigation. Por favor, tente novamente.',
            refresh_failed: 'Falha ao refresh investigation detail. Por favor, tente novamente.',
            update_failed: 'Falha ao atualizar a investigação',
            investigation_count: 'investigação',
            investigation_count_plural: 'investigações',
            title_placeholder: 'Título da investigação',
            description_placeholder: 'Descrição detalhada da investigação...',
            assignee_placeholder: 'ID de utilizador do atribuído (opcional)',
            timeline_action_placeholder: 'p.ex., Registos de acesso revistos',
            timeline_note_placeholder: 'Nota opcional',
            access_ids_placeholder: 'ID de acesso-1, ID de acesso-2',
            justification_ids_placeholder: 'justificativa-id-1, justificativa-id-2',
            findings_placeholder: 'Resuma as conclusões da investigação...',
            by_actor: 'by',
            close_investigation: 'Fechar investigação',
            add_timeline_failed: 'Falha ao add timeline entry. Por favor, tente novamente.',
            fetch_failed: 'Falha ao fetch investigations. Por favor, tente novamente.',
            change_status: 'Alterar status da investigação',
            details: 'Detalhes da investigação',
            link_evidence: 'Vincular evidência',
            link_justifications: 'Vincular justificativas',
            access_ids_label: 'IDs de acesso',
            justification_ids_label: 'IDs de justificativa',
            access_ids_link_placeholder: 'ID de acesso-1, ID de acesso-2',
            justification_ids_link_placeholder: 'justificativa-id-1, justificativa-id-2',
            investigations_loaded: '{n} investigations carregados',
            status_changed_to: 'Status alterado para {status}',
            add_timeline_entry: 'Adicionar entrada na linha do tempo',
            add_access_ids: 'Adicionar IDs de acesso',
            add_justification_ids: 'Adicionar IDs de justificativa',
            investigation_types: {
                anomaly_review: 'Revisão de anomalia',
                complaint_driven: 'Orientado a reclamações',
                scheduled_review: 'Revisão agendada',
                whistleblower_report: 'Relatório de denúncia',
            },
            severity_labels: {
                low: 'Baixo',
                medium: 'Médio',
                high: 'Alto',
                critical: 'Crítico',
            },
            status_labels: {
                open: 'Abrir',
                in_progress: 'Em andamento',
                pending_review: 'Revisão pendente',
                closed_substantiated: 'Fechado (fundamentado)',
                closed_unsubstantiated: 'Fechado (não fundamentado)',
                closed_inconclusive: 'Fechado (Inconclusivo)',
            },
            disposition_labels: {
                substantiated: 'Fundamentado',
                unsubstantiated: 'Infundado',
                inconclusive: 'Inconclusivo',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Análise de padrões',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'A carregar pattern data...',
            anomalies_detected: 'Anomalias detetadas',
            patterns: 'Padrões',
            by_user: 'Por Utilizador',
            by_resource: 'Por recurso',
            time_range: 'Intervalo de tempo',
            load_failed: 'Falha ao analyze patterns. Por favor, tente novamente.',
            top_resources: '10 principais',
            access_by_hour: 'Acesso por hora do dia',
            access_by_day: 'Acesso por Day de Semana',
            fetch_failed: 'Falha ao fetch pattern analysis. Por favor, tente novamente.',
            analysis_loaded: 'Análise de padrões carregada para {user}, {n} sessões encontradas',
            date_format_placeholder: 'DD/MM/AAAA',
            access_by_hour_heading: 'Acesso por hora do dia',
            access_by_day_heading: 'Acesso por dia da semana',
            by_classification: 'Por nível de classificação',
            top_accessed_resources: 'Recursos mais acessados',
            user_access_summary: 'Resumo de acesso do usuário',
            days: {
                sunday: 'Domingo',
                monday: 'Segunda-feira',
                tuesday: 'Terça-feira',
                wednesday: 'Quarta-feira',
                thursday: 'Quinta-feira',
                friday: 'Sexta-feira',
                saturday: 'Sábado',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Estado de ancoragem',
            loading: 'A carregar anchoring data...',
            anchored: 'Ancorado',
            pending: 'Pendente',
            failed: 'Falhou',
            last_anchor: 'Última ancoragem',
            no_records: 'Nenhum anchoring records encontrado',
            status_load_failed: 'Falha ao load anchoring status. Por favor, tente novamente.',
            proofs_load_failed: 'Falha ao load access proofs. Por favor, tente novamente.',
            verify_failed: 'Falha ao verify proof chain. Por favor, tente novamente.',
            anchor_failed: 'Falha ao anchor access proof. Por favor, tente novamente.',
            batch_anchor_failed: 'Falha ao batch anchor access proofs. Por favor, tente novamente.',
            proof_count: 'prova',
            proof_count_plural: 'provas',
            anchor_single: 'Ancoragem individual',
            anchor_batch: 'Ancoragem em lote',
            access_id_placeholder: 'Introduza access ID to anchor',
            batch_ids_placeholder: 'ID de acesso-1, ID de acesso-2, ID de acesso-3',
            verify_chain: 'Verificar cadeia',
            chain_breaks: 'Ruturas de cadeia',
            total_checked: 'Total verificado',
            anchor_placeholder: 'Introduza access ID to anchor',
            fetch_failed: 'Falha ao fetch anchoring status. Por favor, tente novamente.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Falha ao fetch proofs. Por favor, tente novamente.',
            access_id: 'ID de acesso',
            chain_sequence: 'Seq. cadeia',
            proof_hash_label: 'Hash da prova',
            batch_id: 'ID do lote',
            proofs_loaded: '{n} access proofs carregados',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Pendente',
                confirmed: 'Confirmado',
                failed: 'Falha',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Corrente',
                blockchain_anchor: 'Âncora Blockchain',
                zkp_stub: 'Esboço ZKP',
            },
        },
        configuration: {
            title: 'Configuração',
            session_settings: 'Sessão Definições',
            log_retention: 'Registo Retenção (days)',
            anomaly_threshold: 'Limiar de anomalia',
            anchoring_enabled: 'Ancoragem ativada',
            save_config: 'Guardar Configuração',
            load_failed: 'Falha ao load configuration. Por favor, tente novamente.',
            save_success: 'Configuração guardado com sucesso',
            save_failed: 'Falha ao save configuration. Por favor, tente novamente.',
            justification_rules: 'Regras de justificação',
            reason_codes: 'Códigos de motivo',
            anchoring_settings: 'Definições de ancoragem',
            anchoring_method: 'Método de ancoragem',
            anchoring_frequency: 'Frequência de ancoragem',
            batch_interval: 'Intervalo de lote (minutos)',
            eligibility_criteria: 'Critérios de elegibilidade',
            add_criterion_placeholder: 'Adicionar eligibility criterion...',
            retention_settings: 'Retenção Definições',
            standard_retention: 'Norma Retenção (days)',
            sensitive_retention: 'Sensível Retenção (days)',
            working_hours: 'Horas de trabalho',
            work_start: 'Início Hora',
            work_end: 'Fim Hora',
            timezone: 'Fuso horário',
            timezone_placeholder: 'p. ex. Asia/Ho_Chi_Minh',
            working_days: 'Dias úteis',
            concurrent_sessions: 'Limites de sessões simultâneas',
            concurrent_threshold: 'Limiar de sessões simultâneas',
            classification_level: 'Classificação Nível',
            prompt_mode: 'Modo de aviso',
            grace_period: 'Período de carência (s)',
            code_placeholder: 'código',
            is_custom: 'Personalizado',
            label_placeholder: 'Rótulo',
            description_placeholder: 'Descrição',
            custom: 'Personalizado',
            fetch_failed: 'Falha ao fetch configuration. Por favor, tente novamente.',
            classification_levels: {
                public: 'Público',
                internal: 'Interno',
                confidential: 'Confidencial',
                restricted: 'Restrito',
                unclassified: 'Não classificado',
            },
            prompt_modes: {
                reason_only: 'Razão Apenas',
                reason_text: 'Razão + Texto',
                reason_text_supervisor: 'Razão + Texto + Supervisor',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Corrente',
                blockchain_anchor: 'Âncora Blockchain',
                zkp_stub: 'Esboço ZKP',
            },
            anchoring_frequencies: {
                realtime: 'Em tempo real',
                batched: 'Em lote',
                on_demand: 'Sob demanda',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Políticas de acesso',
            create_policy: 'Criar política de acesso',
            policy_name: 'Nome da política',
            framework_code: 'Enquadramento',
            select_framework: 'Selecionar um enquadramento...',
            review_frequency: 'Frequência de revisão',
            scope: 'Âmbito',
            no_policies_found: 'Nenhuma política de acesso encontrada.',
            policy_created: 'Política de acesso criada',
            frequency_labels: {
                monthly: 'Mensal',
                quarterly: 'Trimestral',
                semi_annual: 'Semestral',
                annual: 'Anual',
            },
        },
        review_campaigns: {
            title: 'Campanhas de revisão de acesso',
            create_campaign: 'Criar campanha de revisão',
            reviewer: 'Revisor',
            due_date: 'Data de vencimento',
            certify: 'Certificar',
            no_campaigns_found: 'Nenhuma campanha de revisão de acesso encontrada.',
            status_open: 'Aberta',
            status_in_review: 'Em revisão',
            status_completed: 'Concluída',
            campaign_created: 'Campanha de revisão criada',
            review_certified: 'Revisão de acesso certificada',
            certify_campaign: 'Certificar campanha {id}',
            policy_id: 'ID da política',
            select_policy: 'Selecionar uma política...',
            reviewer_ids_label: 'Revisores',
            select_reviewers: 'Selecionar revisores...',
            status_labels: {
                open: 'Aberto',
                in_review: 'Em revisão',
                in_progress: 'Em curso',
                cancelled: 'Cancelada',
                completed: 'Concluído',
            },
        },
        evidence_packages: {
            title: 'Pacotes de evidência',
            generate_package: 'Gerar pacote de evidência',
            export_type: 'Tipo de exportação',
            package_type: 'Tipo de pacote',
            framework_scope: 'Âmbito do enquadramento',
            period: 'Período',
            generated_by: 'Gerado por',
            generated_at: 'Gerado em',
            no_packages_found: 'Nenhum pacote de evidência encontrado.',
            package_generated: 'Pacote de evidências gerado',
            export_type_full: 'Exportação completa',
            export_type_summary: 'Resumo',
            export_type_certification_only: 'Apenas certificação',
            package_type_access_review: 'Revisão de acesso',
            package_type_investigation: 'Investigação',
            package_type_certification: 'Certificação',
            package_type_authority_request: 'Solicitação de autoridade',
            period_start: 'Início do período',
            period_end: 'Fim do período',
            select_frameworks: 'Selecionar estruturas...',
            completeness_col: 'Completude',
            status_col: 'Estado',
            generated_by_col: 'Gerado por',
        },
        evidence_package_detail: {
            title: 'Detalhes do pacote de evidências',
            not_found: 'Pacote de evidências não encontrado.',
            package_metadata: 'Metadados do pacote',
            status: 'Estado',
            item_count: 'Número de itens',
            disposition: 'Disposição',
            closed_at: 'Fechado em',
            completeness_dashboard: 'Painel de completude',
            no_completeness_data: 'Nenhum dado de completude disponível.',
            controls_covered: 'controles cobertos',
            missing: 'Em falta',
            sign_off_tracker: 'Rastreador de aprovação',
            stage_framework_owner: 'Proprietário da estrutura',
            stage_legal_compliance: 'Jurídico e Conformidade',
            stage_internal_audit: 'Auditoria interna',
            approved: 'Aprovado',
            returned: 'Devolvido',
            pending: 'Pendente',
            approve: 'Aprovar',
            return_btn: 'Devolver',
            sign_off_submitted: 'Aprovação submetida',
            artifacts: 'Artefactos',
            no_artifacts: 'Nenhum artefacto encontrado.',
            template_id: 'ID de modelo',
            description: 'Descrição',
            classification: 'Classificação',
            evidence_type: 'Tipo de evidência',
            framework_section: 'Secção da estrutura',
            history: 'Histórico de aprovação',
            expand: 'Expandir',
            collapse: 'Recolher',
            action: 'Ação',
            actor: 'Ator',
            timestamp: 'Carimbo temporal',
            comment: 'Comentário',
            close_case: 'Fechar caso',
            confirm_close: 'Confirmar fecho',
            closure_notes: 'Notas de fecho',
            disposition_compliant: 'Conforme',
            disposition_non_compliant: 'Não conforme',
            disposition_partial: 'Parcial',
            case_closed: 'Pacote de evidências fechado',
            export_package: 'Exportar pacote',
            export_complete: 'Pacote de evidências exportado',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Painel de controlo',
            properties: 'Propriedades',
            groups: 'Grupos',
            bindings: 'Vinculações',
            change_requests: 'Pedidos de alteração',
            comparison: 'Comparação',
            inconsistencies: 'Inconsistências',
            snapshot_schedules: 'Agendamento de instantâneos',
            golden_templates: 'Modelos de referência',
            evidence_packs: 'Pacotes de evidência',
            audit: 'Auditoria',
        },
        select_property: 'Selecionar propriedade…',
        select_policy: 'Selecionar política…',
        select_properties: 'Selecionar propriedades…',
        select_target_properties: 'Selecionar propriedades alvo…',
        select_owner: 'Selecionar proprietário…',
        select_technical_contact: 'Selecionar contacto técnico…',
        property_statuses: {
            active: 'Ativo',
            inactive: 'Inativo',
            pending: 'Pendente',
            decommissioned: 'Desativado',
        },
        dashboard: {
            title: 'Painel de propriedades cruzadas',
            loading: 'A carregar dashboard data...',
            total_properties: 'Total de propriedades',
            active_properties: 'Propriedades ativas',
            snapshot_coverage: 'Cobertura de snapshots',
            properties_covered: 'propriedades cobertas',
            total_bindings: 'Total de vínculos',
            deployed_bindings: 'Implantado',
            pending_bindings: 'Implantação pendente',
            deployment_progress: 'Progresso da implantação',
            on_target: 'No alvo',
            below_target: 'Abaixo do alvo',
            target_label: 'Alvo',
            total_groups: 'Total Grupos',
            pending_changes: 'Alterações pendentes',
            inconsistencies: 'Inconsistências',
            recent_activity: 'Atividade recente',
            no_recent_activity: 'Sem atividade recente.',
            fetch_error: 'Falha ao load dashboard. Por favor, tente novamente.',
        },
        properties: {
            title: 'Registo de propriedades',
            new: 'Novo Property',
            edit: 'Editar propriedade',
            property_name: 'Nome',
            property_type: 'Tipo',
            identifier: 'Identificador',
            environment: 'Ambiente',
            owner_id: 'Proprietário ID',
            technical_contact_id: 'ID do contacto técnico',
            decommission: 'Descomissionar',
            confirm_decommission: 'Tem a certeza de que pretende descomissionar esta propriedade?',
            no_properties: 'Nenhum properties encontrado',
            all_environments: 'Todos os ambientes',
            all_types: 'Todos os tipos',
            cmp_instance_id: 'CMP ID da instância',
            deployment_date: 'Data de implantação',
            identifier_placeholder: 'por exemplo, www.example.com ou com.example.app',
            owner_name: 'Proprietário',
            technical_contact_name: 'Contacto técnico',
            sensitive_data_categories: 'Categorias de dados confidenciais',
            sensitive_data_categories_placeholder: 'Selecione categorias de dados confidenciais',
            requires_enhanced_review: 'Requer revisão aprimorada',
            sensitive_review_status: 'Revisão de dados confidenciais',
            sensitive_review_pending: 'Revisão pendente',
            sensitive_review_approved: 'Aprovado',
            sensitive_review_rejected: 'Rejeitado',
            sensitive_review_submit: 'Enviar avaliação',
            sensitive_review_approve: 'Aprovar',
            sensitive_review_reject: 'Rejeitar',
            sensitive_review_notes: 'Revise as notas',
            sensitive_review_notes_placeholder: 'Insira notas de revisão...',
            record_withdrawal: 'Retirada de registro',
            withdrawal_subject_id: 'ID do assunto',
            withdrawal_consent_id: 'ID de consentimento',
            withdrawal_reason: 'Razão',
            withdrawal_downstream_systems: 'Sistemas a jusante',
            withdrawal_downstream_placeholder: 'sistema-1, sistema-2',
            withdrawal_success: 'Retirada propagada com sucesso.',
            withdrawal_error: 'Falha ao propagar a retirada.',
            withdrawal_data_subject_id: 'ID do titular dos dados',
            withdrawal_data_subject_id_placeholder: 'p. ex. usr_abc123',
            withdrawal_consent_record_id: 'ID do registro de consentimento',
            withdrawal_consent_record_id_placeholder: 'p. ex. consent_xyz789',
            withdrawal_purposes: 'Finalidades retiradas',
            withdrawal_purposes_placeholder: 'Selecione as finalidades para retirar o consentimento para...',
            withdrawal_withdrawn_at: 'Data de retirada',
            withdrawal_tasks_created: 'Tarefas criadas',
            withdrawal_affected_bindings: 'Ligações Afetadas',
            withdrawal_affected_activities: 'Atividades de processamento afetadas',
            withdrawal_history: 'Histórico de retiradas',
        },
        groups: {
            title: 'Grupos de propriedades',
            new: 'Novo grupo',
            group_name: 'Grupo Nome',
            member_count: 'Membros',
            no_groups: 'Nenhum property groups encontrado',
            policies: 'Políticas',
            confirm_delete: 'Tem certeza de que deseja excluir este grupo?',
        },
        bindings: {
            title: 'Matriz de vinculações',
            property: 'Propriedade',
            purpose: 'Finalidade',
            legal_basis: 'Base jurídica',
            bound: 'Vinculado',
            no_bindings: 'Nenhum bindings encontrado',
            bind_policy: 'Política de vinculação',
            bulk_bind: 'Vinculação em massa',
            bind_policy_to_property: 'Vincular política à propriedade',
            bulk_bind_policy: 'Política de vinculação em massa',
            unbound: 'Não consolidado',
            policy_id: 'ID da política',
            version_placeholder: 'por exemplo, 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Vinculação',
            property_name: 'Nome da propriedade',
            policy_name: 'Nome da política',
            bindings_list: 'Lista de vinculações',
            binding_actions: {
                start_deploy: 'Iniciar implantação',
                mark_deployed: 'Marcar como implantado',
                rollback: 'Reverter',
            },
        },
        change_requests: {
            title: 'Processo de pedidos de alteração',
            new: 'Novo Change Request',
            requester: 'Requerente',
            change_type: 'Tipo de alteração',
            property: 'Propriedade',
            submitted_at: 'Submetido em',
            no_requests: 'Nenhum change requests encontrado',
            fetch_pipeline_error: 'Falha ao fetch pipeline. Por favor, tente novamente.',
            fetch_list_error: 'Falha ao fetch change requests. Por favor, tente novamente.',
            create_error: 'Falha ao create change request. Por favor, tente novamente.',
            action_error: 'Falha ao perform action. Por favor, tente novamente.',
            approver_name: 'Aprovador',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Todos',
            view_my_properties: 'Minhas Propriedades',
            stage_requests: 'Pedidos {stage}',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Espaço de comparação',
            select_properties: 'Selecione Properties to Compare',
            compare: 'Comparar',
            no_comparison: 'Selecione at least two properties to compare.',
            fetch_error: 'Falha ao fetch comparison reports. Por favor, tente novamente.',
            run_error: 'Falha ao run comparison. Por favor, tente novamente.',
            save_notes_error: 'Falha ao save notes. Por favor, tente novamente.',
            scan_complete: 'Inconsistency scan concluído com sucesso',
            scan_error: 'Falha ao run inconsistency scan. Por favor, tente novamente.',
            min_two_properties: 'Forneça pelo menos 2 IDs de propriedade para comparação.',
            view_inconsistencies: 'Ver inconsistências',
            click_to_select: 'Clique para selecionar duas ou mais propriedades',
            no_properties_available: 'Nenhuma propriedade disponível',
            no_matching_properties: 'Nenhuma propriedade correspondente',
            select_at_least_two_props: 'Selecione pelo menos duas propriedades no menu suspenso acima.',
            no_differences_found: 'Nenhuma diferença encontrada entre as propriedades selecionadas.',
            found_inconsistencies: 'Encontradas {count} inconsistências em {properties} propriedades e {bindings} ligações.',
        },
        inconsistencies: {
            title: 'Lista de inconsistências',
            property: 'Propriedade',
            field: 'Campo',
            expected: 'Esperado',
            actual: 'Real',
            severity: 'Gravidade',
            no_inconsistencies: 'Nenhum inconsistencies encontrado',
            trend: 'Tendência de Inconsistência',
            trend_chart_label: 'Gráfico de tendência de inconsistência mostrando contagens ao longo do tempo',
            acknowledge: 'Confirmar receção',
            resolve: 'Resolver',
            suppress: 'Suprimir',
            cr_id_placeholder: 'ID CR',
            view_all: 'Todos',
            view_my_properties: 'Minhas Propriedades',
            view_dpo_review: 'DPO Fila de revisão',
            change_request_id_required: 'O ID da solicitação de alteração é necessário para resolver essa inconsistência.',
        },
        snapshot_schedules: {
            title: 'Agendamento de instantâneos',
            new: 'Novo agendar',
            schedule_name: 'Agendar Nome',
            frequency: 'Frequência',
            next_run: 'Seguinte Executar',
            last_run: 'Último Executar',
            no_schedules: 'Nenhum snapshot schedules encontrado',
            target_type: 'Tipo de destino',
            target_id: 'ID de destino',
            custom_frequency_days: 'Frequência personalizada (dias)',
            time_of_day: 'Hora do dia',
            time: 'Hora',
            retention_days: 'Retenção (dias)',
            execute_now: 'Execute agora',
            placeholder_days: 'por exemplo, 10',
            target_name: 'Nome do alvo',
        },
        golden_templates: {
            title: 'Modelos de referência',
            new: 'Novo modelo',
            template_name: 'Modelo Nome',
            property_type: 'Tipo de propriedade',
            no_templates: 'Nenhum golden templates encontrado',
            applicable_group_ids: 'IDs de grupo aplicáveis (separados por vírgula)',
            applicable_groups: 'Grupos Aplicáveis',
            purposes: 'Finalidades',
            legal_bases: 'Bases Legais',
            banner_config: 'Configuração de banner',
            consent_settings: 'Definições de consentimento',
            group_ids_placeholder: 'ID do grupo-1, ID do grupo-2',
            apply_to_property: 'Aplicar à Propriedade',
            apply_to_group: 'Aplicar ao Grupo',
            apply_success: 'Modelo aplicado com sucesso.',
            applications: 'Histórico de aplicações',
            no_applications: 'Nenhuma aplicação de modelo ainda.',
            no_configuration_details: 'O modelo não tem detalhes de configuração. Edite o modelo para adicionar finalidades, bases legais, configurações de banner ou configurações de consentimento.',
        },
        rollouts: {
            title: 'Implementações de modelos',
            rollout_status: 'Estado da implementação',
            confirmed: 'Confirmado',
            pending: 'Pendente',
            failed: 'Falhado',
            confirm: 'Confirmar Propriedade',
            confirm_success: 'Propriedade confirmada na implementação.',
            can_close: 'Todas as propriedades confirmadas — implementação concluída',
            cannot_close: 'A aguardar confirmações de propriedades',
            no_rollouts: 'Nenhuma implementação encontrada.',
            in_progress: 'Em progresso',
            completed: 'Concluído',
            cancelled: 'Cancelado',
            reject: 'Rejeitar Propriedade',
            reject_success: 'Propriedade rejeitada da implementação.',
            confirm_all: 'Confirmar Todos',
            reject_all: 'Rejeitar Todos',
            confirm_label: 'Confirmar implementação para',
            reject_label: 'Rejeitar implementação para',
            confirm_all_label: 'Confirmar Todas as Implementações Pendentes',
            reject_all_label: 'Rejeitar Todas as Implementações Pendentes',
            no_properties_in_rollout: 'Este lançamento não tem propriedades.',
        },
        analytics: {
            title: 'Análise de propriedades',
            opt_in_rate: 'Taxa de adesão',
            opt_out_rate: 'Taxa de recusa',
            withdrawal_rate: 'Taxa de retirada',
            total_consents: 'Total de consentimentos',
            total_withdrawals: 'Total de retiradas',
            compliance_score: 'Pontuação de conformidade',
            non_compliant: 'Propriedades não conformes',
            no_non_compliant: 'Todas as propriedades estão em conformidade.',
            threshold: 'Limiar de conformidade',
            property_id: 'ID da propriedade',
            compliant: 'Compatível',
            non_compliant_status: 'Não conforme',
            compliance_help_summary: 'Como a pontuação de conformidade é calculada e como posso corrigi-la?',
            compliance_help_intro: 'Uma propriedade é sinalizada como Não Conforme quando sua pontuação é inferior a 80%. A pontuação é calculada como:',
            compliance_help_with_template_label: 'Se um Golden Template for aplicado:',
            compliance_help_with_template_text: '% de chaves de configuração (finalidades, legal_bases, banner_config, consent_settings) que correspondem exatamente ao modelo.',
            compliance_help_no_template_label: 'Se nenhum Golden Template for aplicado:',
            compliance_help_no_template_text: '% de vinculações (tipos de consentimento atribuídos à propriedade) com deployment_status = implantado. Uma propriedade sem associações tem como padrão 100%.',
            compliance_help_fix_intro: 'Para remover uma propriedade desta lista, siga um destes procedimentos:',
            compliance_help_fix_deploy_label: 'Implantar suas ligações',
            compliance_help_fix_deploy_text: 'abra a página Binding Matrix e defina o status de implantação de cada ligação como implantado.',
            compliance_help_fix_template_label: 'Aplicar um modelo dourado',
            compliance_help_fix_template_text: 'use Aplicar ao grupo no modelo e confirme a implementação desta propriedade. Certifique-se de que a configuração da propriedade corresponda ao modelo.',
            compliance_help_fix_decommission_label: 'Desativar o imóvel',
            compliance_help_fix_decommission_text: 'use o botão Desativar no modal de detalhes da propriedade se ela não estiver mais em uso.',
            compliance_help_fix_no_bindings_label: 'Remover todas as ligações',
            compliance_help_fix_no_bindings_text: 'se uma propriedade não tiver modelo nem vinculações, ela pontuará 100% por padrão.',
        },
        evidence_packs: {
            title: 'Pacotes de evidência',
            new: 'Novo Pack',
            pack_name: 'Nome do pacote',
            properties: 'Propriedades',
            generated_at: 'Gerado em',
            no_packs: 'Nenhum evidence packs encontrado',
            generate: 'Gerar',
            content_type_consent_records: 'Consentimento Registos',
            content_type_policy_versions: 'Versões da política',
            content_type_binding_history: 'Histórico de vinculação',
            content_type_change_requests: 'Pedidos de alteração',
            content_type_inconsistency_reports: 'Relatórios de inconsistência',
            content_type_snapshot_data: 'Dados de instantâneo',
            content_type_audit_trail: 'Trilha de auditoria',
        },
        audit: {
            title: 'Auditoria de propriedades de consentimento',
            event_type: 'Evento Tipo',
            entity_type: 'Tipo de entidade',
            entity_id: 'ID da entidade',
            actor: 'Ator',
            timestamp: 'Marca temporal',
            no_events: 'Nenhum audit events encontrado',
            entity_type_property: 'Propriedade',
            entity_type_group: 'Grupo',
            entity_type_binding: 'Vinculação',
            entity_type_change_request: 'Pedido de alteração',
            entity_type_golden_template: 'Modelo dourado',
            entity_type_snapshot_schedule: 'Agendamento instantâneo',
            entity_type_inconsistency: 'Inconsistência',
            entity_type_evidence_pack: 'Pacote de evidência',
            action_created: 'Criado',
            action_updated: 'Atualizado',
            action_deleted: 'Eliminado',
            action_decommissioned: 'Desativado',
            action_status_changed: 'Estado alterado',
            action_submitted: 'Submetido',
            action_approved: 'Aprovado',
            action_rejected: 'Rejeitado',
            action_deployed: 'Implementado',
            action_rolled_back: 'Revertido',
            action_paused: 'Pausado',
            action_resumed: 'Retomado',
            action_cancelled: 'Cancelado',
            action_acknowledged: 'Reconhecido',
            action_resolved: 'Resolvido',
            action_suppressed: 'Suprimido',
            action_executed: 'Executado',
        },
        property_types: {
            website: 'Site',
            mobile_app: 'Aplicação móvel',
            ott: 'OTT',
            iot_device: 'Dispositivo IoT',
            kiosk: 'Quiosque',
            api_service: 'Serviço API',
            sdk: 'SDK',
        },
        environments: {
            production: 'Produção',
            staging: 'Pré-produção',
            development: 'Desenvolvimento',
            testing: 'Teste',
        },
        group_types: {
            brand: 'Marca',
            region: 'Região',
            product_line: 'Linha de produtos',
            business_unit: 'Unidade de negócio',
            custom: 'Personalizado',
        },
        target_types: {
            property: 'Propriedade',
            group: 'Grupo',
            all: 'Todos',
        },
        frequencies: {
            hourly: 'A cada hora',
            daily: 'Diário',
            weekly: 'Semanal',
            monthly: 'Mensal',
            custom: 'Personalizado',
        },
        binding_statuses: {
            current: 'Atual',
            outdated: 'Desatualizado',
            pending: 'Pendente',
            deploying: 'A implementar',
            rolled_back: 'Revertido',
        },
        sensitive_categories: {
            health: 'Dados de saúde',
            biometric: 'Dados biométricos',
            genetic: 'Dados Genéticos',
            racial_ethnic: 'Origem racial/étnica',
            political_opinions: 'Opiniões Políticas',
            religious_beliefs: 'Crenças Religiosas',
            trade_union: 'Filiação Sindical',
            sexual_orientation: 'Orientação Sexual',
            criminal_records: 'Registos criminais',
            financial: 'Dados Financeiros',
            children: 'Dados infantis',
            geolocation: 'Geolocalização precisa',
        },
        export_csv: 'Exportar CSV',
        exporting: 'Exportando...',
        page_x_of_y: 'Pagina {page} de {total}',
        regulatory: {
            capture_method_guidance: 'PDPL exige prova de consentimento que corresponda ao canal de coleta. Os tipos de propriedade são mapeados para métodos de captura de consentimento: site/OTT = consentimento baseado em banner, aplicativo móvel/SDK = fluxo de consentimento no aplicativo, quiosque = captura de consentimento físico/na tela, serviço API = consentimento programático, dispositivo IoT = mecanismo de consentimento no nível do dispositivo.',
            dashboard_metrics_info: 'Este painel fornece ao DPO uma visão única da postura de consentimento em todas as propriedades digitais. As principais métricas incluem: porcentagem de cobertura de vinculação (implantada versus total de vinculações), adesão ao modelo por meio do widget de inconsistências, solicitações de alteração pendentes que exigem revisão e detalhamento do status da propriedade mostrando propriedades com estados ativos, inativos ou desativados. Propriedades com categorias de dados confidenciais são rastreadas por meio do registro de propriedades.',
            change_request_impact_info: 'As solicitações de alteração que afetam as propriedades de consentimento devem documentar o impacto regulatório: quais requisitos PDPL são afetados, se a alteração afeta o processamento de dados confidenciais (Art. 2.4) e se a alteração requer novo consentimento dos titulares dos dados. O resumo do impacto em cada solicitação de mudança detalha as referências regulatórias PDPL para evidências de auditoria.',
            outdated_binding_warning: 'As vinculações com status "desatualizado" indicam que uma propriedade executa uma versão de política que não é atual. Essas propriedades podem não refletir os requisitos PDPL mais recentes ou atualizações de políticas organizacionais. Revise e atualize vinculações desatualizadas imediatamente para manter a conformidade com PDPL em todas as propriedades de consentimento.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Painel de controlo',
            market_packs: 'Pacotes de mercado',
            language_packs: 'Pacotes de idiomas',
            frameworks: 'Quadros regulamentares',
            mappings: 'Mapeamentos de módulos',
            submissions: 'Submissões de pacotes',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Pré-visualização da tradução',
            audit: 'Registo de auditoria',
        },
        dashboard: {
            title: 'Painel de localização',
            loading: 'A carregar dashboard data...',
            active_market_packs: 'Ativo Mercado Packs',
            published_packs_available: 'Pacotes de mercado publicados disponíveis',
            language_packs: 'Pacotes de idioma',
            total_language_packs: 'Total de pacotes de idioma registados',
            active_regulatory_frameworks: 'Ativo Regulatory Enquadramentos',
            frameworks_active: 'Enquadramentos com estado ativo',
            pending_pack_submissions: 'Submissões de pacotes pendentes',
            submissions_awaiting_review: 'Submissões aguardando revisão',
            recent_audit_events: 'Recente Auditoria Eventos',
            no_recent_audit: 'Sem eventos de auditoria recentes.',
            event_type: 'Evento Tipo',
            entity_type: 'Tipo de entidade',
            entity_id: 'ID da entidade',
            actor: 'Ator',
            timestamp: 'Marca temporal',
        },
        market_packs: {
            title: 'Catálogo de pacotes de mercado',
            new: 'Novo Market Pack',
            pack_name: 'Nome do pacote',
            market: 'Mercado',
            locale: 'Idioma',
            no_packs: 'Nenhum market packs encontrado',
        },
        language_packs: {
            title: 'Pacotes de idiomas',
            new: 'Novo Language Pack',
            language: 'Idioma',
            locale_code: 'Código de idioma',
            completeness: 'Completude',
            completeness_detail: 'Detalhe de integridade',
            overall_completeness: 'Completude Geral',
            total_keys: 'Total de chaves',
            translated_keys: 'Chaves traduzidas',
            missing_keys: 'Chaves ausentes',
            namespace_breakdown: 'Divisão de namespace',
            namespace: 'Espaço de nomes',
            translated: 'Traduzido',
            no_packs: 'Nenhum language packs encontrado',
            install_language_pack: 'Instalar pacote de idioma',
            available_packs: 'Pacotes de idioma disponíveis',
            install: 'Instalar',
            all_installed: 'Todos os pacotes de idioma já estão instalados.',
            install_success: 'Pacote de idioma instalado com sucesso.',
            market: 'Mercado',
            featured: 'Destaque',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Quadros regulamentares',
            new: 'Novo enquadramento',
            framework_name: 'Enquadramento Nome',
            jurisdiction: 'Jurisdição',
            effective_date: 'Data de vigência',
            no_frameworks: 'Nenhum regulatory frameworks encontrado',
            view_requirements: 'Ver requisitos',
            hide_requirements: 'Ocultar requisitos',
            show_legacy: 'Mostrar quadros legais antigos',
            effective_until: 'Vigente até',
            effective_on: 'Vigente a partir de',
            replaced_by: 'Substituído por',
            replaces_label: 'Substitui',
            status_active: 'Ativo',
            status_draft: 'Rascunho',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Antigo',
            status_upcoming: 'Próximo',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Lista de observação',
            watchlist_empty: 'Nenhum rascunho ou estruturas futuras',
            watchlist_empty_desc: 'Frameworks com status “rascunho” ou “próximo” aparecerão aqui para monitoramento.',
            expected_effective: 'Eficácia esperada',
            effective_date_tbd: 'Data de vigência: TBD',
            activate: 'Ativar',
            ai_law_title: 'Lei do Vietnã AI',
            ai_risk_classification: 'AI Classificação de risco',
            ai_system_dossier: 'AI Dossiê do Sistema',
            ai_labeling_checklist: 'AI Rotulagem e divulgação',
            ai_incident_report: 'AI Relatório de incidente',
            ai_impact_assessment: 'AI Avaliação de impacto',
            ai_sandbox_application: 'Aplicativo sandbox',
            ai_sandbox_monitoring: 'Monitoramento de sandbox',
            ai_sandbox_completion: 'Conclusão da sandbox',
            ai_submission_tracker: 'Rastreador de envio',
            ai_evidence_capture: 'Captura de evidências',
            risk_level_minimal: 'Risco Mínimo',
            risk_level_limited: 'Risco Limitado',
            risk_level_high: 'Alto risco',
            risk_level_unacceptable: 'Risco Inaceitável',
            submission_status_draft: 'Rascunho',
            submission_status_submitted: 'Enviado',
            submission_status_returned: 'Devolvido para alteração',
            submission_status_accepted: 'Aceito',
            submission_status_rejected: 'Rejeitado',
            submission_status_closed: 'Fechado',
            ecommerce_law_title: 'Lei de comércio eletrônico do Vietnã',
            ecommerce_platform_classification: 'Classificação da plataforma',
            ecommerce_seller_register: 'Cadastro de Vendedor',
            ecommerce_complaint_register: 'Registro de reclamações',
            ecommerce_takedown_register: 'Registro de remoção',
            ecommerce_submission_tracking: 'Rastreamento de envio',
            ecommerce_dossier_checklist: 'Lista de verificação do dossiê',
            ecommerce_cross_border: 'Conformidade transfronteiriça',
            ecommerce_trustmark: 'Programa de Marca de Confiança',
            ecommerce_notification: 'Notificação',
            ecommerce_registration: 'Cadastro',
            ecommerce_termination: 'Rescisão',
            ecommerce_reporting_readiness: 'Preparação para relatórios',
            ecommerce_evidence_capture: 'Captura de evidências',
            submission_status_approved: 'Aprovado',
            cybersec_law_2025_title: 'Lei de Segurança Cibernética do Vietnã (2025)',
            cybersec_system_classification: 'Classificação do Sistema',
            cybersec_risk_assessment: 'Avaliação de Risco',
            cybersec_incident_report: 'Relatório de incidente',
            cybersec_authority_cooperation: 'Cooperação de Autoridade',
            cybersec_data_localization: 'Localização de dados',
            cybersec_vendor_security: 'Segurança do fornecedor',
            cybersec_submission_tracker: 'Rastreador de envio',
            cybersec_evidence_capture: 'Captura de evidências',
            cybersec_dossier_checklist: 'Lista de verificação do dossiê',
            cybersec_classification_general: 'Geral',
            cybersec_classification_important: 'Importante',
            cybersec_classification_critical: 'Crítico',
            data_law_title: 'Lei de Dados do Vietnã',
            data_inventory: 'Inventário de dados',
            data_classification: 'Classificação de dados',
            data_sharing: 'Compartilhamento de dados',
            data_governance: 'Governança de Dados',
            data_submission_tracker: 'Rastreador de envio',
            data_evidence_capture: 'Captura de evidências',
            data_dossier_checklist: 'Lista de verificação do dossiê',
            telecom_law_title: 'Lei de Telecomunicações do Vietnã',
            telecom_authority_request: 'Solicitação de autoridade',
            telecom_disclosure: 'Divulgação',
            telecom_confidentiality: 'Confidencialidade',
            telecom_submission_tracker: 'Rastreador de envio',
            telecom_evidence_capture: 'Captura de evidências',
            telecom_dossier_checklist: 'Lista de verificação do dossiê',
            pdpl_law_title: 'Lei de Proteção de Dados Pessoais do Vietnã',
            pdpl_breach_notification: 'Notificação de violação',
            pdpl_dpo_notification: 'DPO Notificação de compromisso',
            pdpl_data_subject_notification: 'Notificação do Titular dos Dados',
            pdpl_submission_tracker: 'Rastreador de envio',
            pdpl_evidence_capture: 'Captura de evidências',
            pdpl_dossier_checklist: 'Lista de verificação do dossiê',
            pdpl_authority_filing: 'Arquivamento de autoridade',
            pdpl_mops_filing: 'Arquivamento MoPS',
        },
        mappings: {
            title: 'Mapeamentos de módulos',
            new: 'Novo mapeamento',
            module: 'Módulo',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Consentimento',
            module_dpia: 'DPIA',
            module_incident: 'Incidente',
            module_rights_request: 'Solicitação de direitos',
            module_data_mapping: 'Mapeamento de dados',
            module_classification: 'Classificação',
            module_monitoring: 'Monitoramento',
            module_governance: 'Governança',
            module_vendor: 'Fornecedor',
            module_risk: 'Risco',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Pacote de mercado',
            mapping_status: 'Mapeamento Estado',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Nenhum module mappings encontrado',
            incomplete_entries_warning: '{count} mapeamento(s) oculto(s) por falta de ID de framework, ID de requisito ou módulo.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Submissões de pacotes',
            new: 'Novo Submission',
            submitter: 'Submetedor',
            pack: 'Pacote',
            submitted_at: 'Submetido em',
            no_submissions: 'Nenhum pack submissions encontrado',
            installed_sector_overlays_title: 'Sector Overlays ({count}) [PT]',
            installed_sector_overlays_aria: 'Installed Sector Overlays [PT]',
            installed_col_name: 'Name [PT]',
            installed_col_code: 'Code [PT]',
            installed_col_sector: 'Sector [PT]',
            installed_col_mode: 'Mode [PT]',
            installed_col_status: 'Status [PT]',
            installed_refresh: 'Refresh [PT]',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Pré-visualização da tradução',
            select_locale: 'Selecione Locale',
            select_module: 'Selecione Module',
            key: 'Key',
            value: 'Valor',
            no_translations: 'Nenhum translations encontrado',
        },
        audit: {
            title: 'Trilha de auditoria de localização',
            event_type: 'Evento Tipo',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Tipo de entidade',
            entity_id: 'ID da entidade',
            entity_name: 'Nome da entidade',
            actor: 'Ator',
            timestamp: 'Marca temporal',
            payload: 'Carga útil',
            collapse_payload: 'Recolher detalhes da carga',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Expandir detalhes da carga',
            collapse: 'Recolher',
            expand: 'Expandir',
            all_entity_types: 'Todos os tipos de entidade',
            no_events: 'Nenhum audit events encontrado',
        },
        market_packs_detail: {
            installed_at: 'Instalado em',
            pack_details_label: 'Detalhes do pacote',
            market_code: 'Código de mercado',
            supported_locales: 'Localidades suportadas',
            min_platform_version: 'Versão mínima da plataforma',
            pack_information: 'Informação do pacote',
            pack_contents: 'Conteúdo do pacote',
            included_frameworks: 'Quadros incluídos',
            pack_artifacts: 'Artefatos do pacote',
            no_artifacts: 'Nenhum artefato encontrado para este pacote',
            pack_not_found: 'Pacote não encontrado',
        },
        mappings_form: {
            framework_id: 'ID da estrutura',
            requirement_id: 'ID do requisito',
            module_label: 'Módulo',
            mapping_type: 'Tipo de mapeamento',
            configuration_json: 'Configuração (JSON)',
            conflict_resolution: 'Resolução de conflitos',
            is_override: 'É substituição',
            all_modules: 'Todos os módulos',
            all_mapping_types: 'Todos os tipos de mapeamento',
        },
        submissions_detail: {
            submission_id: 'ID da submissão',
            validation: 'Validação',
            reviewers: 'Revisores',
            start_review: 'Iniciar revisão',
            hide_comments: 'Ocultar comentários',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Comentários de revisão',
            loading_comments: 'Carregando comentários...',
            no_comments: 'Ainda sem comentários.',
            add_comment: 'Adicionar comentário',
            reject_submission: 'Rejeitar submissão',
            reason_for_rejection: 'Motivo da rejeição',
            reviewer_ids: 'IDs dos revisores (separados por vírgulas)',
            submit_new_pack: 'Submeter novo pacote',
            pack_type: 'Tipo de pacote',
            market_code: 'Código de mercado',
            target_locales: 'Localidades de destino',
            pack_file: 'Arquivo do pacote',
            pack_format_info: 'Formato do pacote: os pacotes do Market devem incluir arquivos JSON específicos do local com traduções de namespace/chave. Os pacotes de idiomas devem fornecer traduções para as localidades de destino especificadas. Consulte a documentação para esquema detalhado.',
            accepted_formats: 'Formatos aceitos: .json ou .zip. Os arquivos JSON devem conter um objeto simples ou com namespace de pares de valores-chave de tradução. Os arquivos ZIP devem conter um ou mais arquivos de localidade JSON.',
            comma_separated_locales: 'Códigos de localidade separados por vírgulas para este pacote de idioma',
            confirm_approval: 'Confirmar aprovação',
            approve_message: 'Aprovar esta submissão de pacote?',
            submission_label: 'Submissão',
            all_pack_types: 'Todos os tipos de pacote',
            passed: 'aprovado',
            failed_label: 'falhou',
            market_code_required: 'Código de mercado é obrigatório',
            version_required: 'Versão é obrigatória',
            pack_type_regulatory: 'Regulatório',
            framework_code: 'Código do quadro',
            jurisdiction_code: 'Código de jurisdição',
            pack_source: 'Fonte',
            pack_source_manual: 'Manuais',
            pack_version: 'Versão do pacote',
            install_regulatory_pack: 'Instalar pacote regulatório',
            regulatory_pack_desc: 'Carregar um pacote de quadro regulatório (.zip)',
            framework_required: 'O código do quadro regulamentar é obrigatório',
            jurisdiction_required: 'O código de jurisdição é obrigatório',
        },
        catalog_detail: {
            packs_installed_of: 'de {total} pacotes instalados',
            install_all: 'Instalar tudo',
            installing: 'Instalando...',
            removing: 'Removendo...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Requer',
            tab_catalog: 'Catálogo',
            tab_installed: 'Instalado',
            details: 'Detalhes',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Localidade de origem',
            target_locale: 'Localidade de destino',
            locales_must_differ: 'As localidades de origem e destino devem ser diferentes.',
            all_keys_translated: 'Todas as chaves traduzidas',
            keys_missing: '{count} chave(s) ausente(s)',
            show_missing_only: 'Mostrar apenas ausentes',
            namespace: 'Espaço de nomes',
            source: 'Fonte',
            target: 'Destino',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Estruturas',
            tab_watchlist: 'Lista de acompanhamento',
            authority: 'Autoridade',
            requirements_count: 'Requisitos',
            all_types: 'Todos os tipos',
            collapse_all: 'Recolher tudo',
            expand_all: 'Expandir tudo',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtrado para',
            clear_filter: 'Limpar filtro',
            no_requirements: 'Nenhum requisito encontrado para esta estrutura.',
            requirements_label: 'Requisitos',
            status_updated: 'Status atualizado com sucesso',
            overview_tab: 'Visão geral',
            forms_tab: 'Formulários',
            procedures_tab: 'Procedimentos',
            fines_risks_tab: 'Coimas e riscos',
            deadlines_tab: 'Prazos',
            guidance_tab: 'Orientação',
            guidance_empty: 'Nenhum conteúdo de orientação para esta estrutura.',
            guidance_loading: 'Carregando orientação…',
            guidance_section: 'Seção de orientação',
            guidance_view: 'Ver orientação',
            guidance_map_to_workflow: 'Mapear para fluxo de trabalho',
            guidance_link_evidence: 'Evidência de link',
            guidance_filter_type: 'Tipo',
            guidance_filter_category: 'Categoria',
            guidance_filter_module: 'Módulo',
            guidance_filter_status: 'Estado',
            guidance_filter_search: 'Orientação de pesquisa',
            guidance_filter_all: 'Todos',
            guidance_table_reference: 'Referência',
            guidance_table_name: 'Nome da orientação',
            guidance_table_type: 'Tipo',
            guidance_table_category: 'Categoria',
            guidance_table_effective_date: 'Data Efetiva',
            guidance_table_modules: 'Módulos Relacionados',
            guidance_table_actions: 'Ações',
            guidance_reference_only_banner: 'Apenas para referência — aplica-se ao setor {{sector}}, mas não à sua organização.',
            guidance_open_official_source: 'Abrir fonte oficial',
            guidance_no_detail: 'Não há detalhes adicionais disponíveis para esta orientação.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Orientação Relacionada',
            deadlines_empty: 'Nenhum prazo regulatório encontrado para este quadro.',
            deadlines_sla_type: 'Tipo de SLA',
            deadlines_value: 'Prazo',
            deadlines_unit: 'Unidade',
            deadlines_extension: 'Extensão',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Referência legal',
            framework_stack: 'Estrutura do quadro',
            layer_parent_law: 'Lei principal',
            layer_implementing_decree: 'Decreto de execução',
            layer_enforcement_decree: 'Decreto sancionatório',
            layer_admin_procedures: 'Procedimentos administrativos',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Instrumentos subordinados',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Ativo',
            status_draft: 'Rascunho',
            draft_enforcement_badge: 'Rascunho — ainda não promulgado',
            effective_date_draft: 'Rascunho — ainda não em vigor',
            effective_date: 'Data de entrada em vigor',
            forms_table_id: 'ID do formulário',
            forms_table_name: 'Nome',
            forms_table_source: 'Fonte',
            forms_table_category: 'Categoria',
            forms_table_type: 'Tipo',
            fill_form: 'Preencher formulário',
            view_reference: 'Ver referência',
            forms_empty: 'Nenhum formulário encontrado para este quadro.',
            forms_loading: 'A carregar formulários...',
            filter_by_source_layer: 'Filtrar por camada de origem',
            all_layers: 'Todas as camadas',
            procedures_id: 'ID procedimento',
            procedures_name: 'Procedimento',
            procedures_trigger: 'Ativador',
            procedures_steps: 'Passos',
            procedures_outputs: 'Resultados',
            procedures_related_forms: 'Formulários relacionados',
            procedures_submission_channels: 'Canais de envio',
            procedures_empty: 'Nenhum procedimento encontrado para este quadro.',
            fines_area: 'Área de infração',
            fines_articles: 'Artigos',
            fines_individual: 'Coima (individual)',
            fines_organization: 'Coima (organização)',
            fines_revenue: 'Escalamento por receita',
            fines_deadlines: 'Prazos',
            fines_remediation: 'Medidas corretivas',
            fines_related: 'Procedimentos relacionados',
            fines_empty: 'Sem dados de sanções disponíveis para este quadro.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Tipo de risco',
            fines_risk_triggers: 'O que o desencadeia',
            fines_risk_only_note: 'Estas entradas descrevem risco de conformidade e operacional, não penalidades monetárias. Quando aplicáveis, as multas são definidas no quadro principal.',
            fines_enforcement_pending: 'Quadro de sanções ainda não publicado. Os dados de sanções serão adicionados quando o instrumento de aplicação for promulgado.',
            view_details: 'Ver detalhes',
            back_to_list: 'Voltar à lista',
            description: 'Descrição',
            jurisdiction: 'Jurisdição',
            lineage: 'Linhagem',
            replaces: 'Substitui',
            replaced_by: 'Substituído por',
            requirements: 'Requisitos',
            tab_navigation: 'Separadores de detalhe do quadro',
            forms_table_actions: 'Ações',
            create_form: 'Criar formulário',
            mark_promulgated: 'Marcar como promulgado',
            status_promulgated: 'Promulgado',
            promulgated_success: 'Estado da camada atualizado para promulgado',
            transition_review: 'Revisão de transição',
            transition_review_desc: 'Este quadro tem uma revisão de transição pendente para a adoção da lei sucessora.',
            transition_review_by: 'Prazo de revisão',
            transition_review_status: 'Estado da revisão',
            transition_review_notes: 'Notas',
            transition_review_pending: 'Pendente',
            transition_review_in_progress: 'Em curso',
            transition_review_completed: 'Concluída',
            transition_review_successor: 'Quadro sucessor',
            transition_review_lead_time: 'Tempo de preparação',
            transition_review_lead_time_days: '{days} dias antes da data de entrada em vigor',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Revisões de transição de formulários',
            transition_review_forms_desc: 'Revise cada modelo operacional quanto à relevância no quadro sucessor.',
            transition_review_outcome: 'Resultado',
            transition_review_outcome_carry_forward: 'Manter',
            transition_review_outcome_carry_forward_revised: 'Manter (revisto)',
            transition_review_outcome_retire: 'Retirar',
            transition_review_outcome_split: 'Dividir',
            transition_review_outcome_awaiting_decree: 'Aguardando decreto',
            transition_review_no_forms: 'Nenhuma revisão de transição de formulários configurada.',
            transition_review_update_success: 'Revisão de transição atualizada com sucesso',
            transition_review_operational_note: 'Estes são modelos operacionais de apoio ao processo, não contagens de formulários legais. A atribuição legal permanece no quadro antigo até a lei sucessora entrar em vigor.',
            rights_tab: 'Direitos',
            rights_empty: 'Nenhum direito do titular de dados definido para este quadro.',
            rights_code: 'Código',
            rights_name: 'Direito',
            rights_legal_basis: 'Base legal',
            rights_elaborating_articles: 'Artigos detalhados',
            rights_description: 'Descrição',
            requirements_reference: 'Referência',
            requirements_mandatory: 'Obrigatório',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Fonte',
            category_legal_basis: 'Base jurídica',
            category_data_subject_rights: 'Direitos do Titular dos Dados',
            category_notification: 'Notificação',
            category_assessment: 'Avaliação',
            category_governance: 'Governança',
            category_record_keeping: 'Manutenção de registros',
            category_cross_border: 'Transfronteiriço',
            category_technical_measures: 'Medidas Técnicas',
            category_incident_reporting: 'Relatório de incidentes',
            category_risk_management: 'Gestão de Risco',
            category_supply_chain: 'Cadeia de Suprimentos',
            category_certification: 'Certificação',
            child_frameworks: '{count} estrutura filha',
            child_frameworks_plural: '{count} estruturas filhas',
            replaces_prefix: 'Substitui:',
            replaced_by_prefix: 'Substituído por:',
            responsible_prefix: 'Responsável:',
            watchlist_empty_hint: 'Frameworks com status “rascunho” ou “próximo” aparecerão aqui para monitoramento.',
            framework_count: 'Estrutura {count}',
            framework_count_plural: 'Estruturas {count}',
            hiding_legacy: '(ocultando {count} legado)',
            label_VN_PDPD_DECREE_13: 'Decreto PDPD 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Lei 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Decreto 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Minuta de Execução 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Lei de Dados',
            label_VN_DATA_LAW_DECREE_165_2025: 'Decreto-Lei de Dados 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Rascunho de bolsas de dados 2026',
            label_VN_TELECOM_LAW_2023: 'Lei das Telecomunicações 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Decreto de Telecomunicações 163/2024',
            label_VN_CYBERSEC_LAW: 'Lei da Cibersegurança (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Decreto Cibersec 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Lei de Cibersegurança 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'Lei do Comércio Eletrónico 122/2025',
            label_VN_AI_LAW_2026: 'AI Lei 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Empresa',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Autoridade',
            sla_type_rights_request_ack: 'Reconhecimento de solicitação de direitos',
            'sla_type_Access control review': 'Revisão de controle de acesso',
            'sla_type_Annual governance attestation': 'Atestado anual de governança',
            'sla_type_Audit review cycle': 'Ciclo de revisão de auditoria',
            'sla_type_Authority request response': 'Resposta de solicitação de autoridade',
            'sla_type_Change notification processing': 'Alterar processamento de notificação',
            'sla_type_Complaint acknowledgment': 'Reconhecimento da reclamação',
            'sla_type_Complaint resolution': 'Resolução de reclamações',
            'sla_type_Confidentiality policy review': 'Revisão da política de confidencialidade',
            'sla_type_Cybersecurity incident full report': 'Relatório completo de incidente de segurança cibernética',
            'sla_type_Cybersecurity incident initial report': 'Relatório inicial de incidente de segurança cibernética',
            'sla_type_Data inventory review': 'Revisão do inventário de dados',
            'sla_type_Disclosure log review cycle': 'Ciclo de revisão do registro de divulgação',
            'sla_type_Incident classification decision': 'Decisão de classificação de incidente',
            'sla_type_Information system classification review': 'Revisão da classificação do sistema de informação',
            'sla_type_Notification processing (sales website/app)': 'Processamento de notificações (site/aplicativo de vendas)',
            'sla_type_Pen test cycle': 'Ciclo de teste de caneta',
            'sla_type_Registration processing (service platform)': 'Processamento de registro (plataforma de serviço)',
            'sla_type_Risk assessment cycle': 'Ciclo de avaliação de risco',
            'sla_type_Takedown decision (after review)': 'Decisão de remoção (após revisão)',
            'sla_type_Termination notice (public)': 'Aviso de rescisão (público)',
            sla_type_ai_impact_assessment_completion: 'AI conclusão da avaliação de impacto',
            sla_type_ai_sandbox_application_review: 'AI análise do aplicativo sandbox',
            sla_type_ai_serious_incident_report: 'AI relatório de incidente grave',
            sla_type_ai_system_registration: 'AI registro do sistema',
            sla_type_authority_request_response: 'Resposta de solicitação de autoridade',
            sla_type_certificate_issuance_review: 'Revisão de emissão de certificado',
            sla_type_complaint_handling_approach_notice: 'Aviso de abordagem para tratamento de reclamações',
            sla_type_complaint_incomplete_info_request: 'Solicitação de informações incompleta da reclamação',
            sla_type_data_governance_attestation: 'Atestado de governança de dados',
            sla_type_dsr_acknowledgment: 'Reconhecimento de DSR',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Retirar/restringir/objetar',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Terceiros',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Acessar/corrigir/fornecer',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Terceiros',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Exclusão',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Exclusão, terceiro',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Medidas de proteção',
            sla_type_telecom_authority_request_response: 'Resposta de solicitação de autoridade de telecomunicações',
            sla_type_rights_request_completion: 'Conclusão da solicitação de direitos',
            sla_type_incident_authority_notification: 'Notificação de autoridade de incidente',
            sla_type_incident_subject_notification: 'Notificação do assunto do incidente',
            sla_type_dpia_completion: 'DPIA conclusão',
            sla_type_data_retention_review: 'Revisão de retenção de dados',
            sla_type_authority_filing_review: 'Revisão de arquivamento de autoridade',
            sla_type_authority_supplement_response: 'Resposta do suplemento de autoridade',
            sla_type_authority_resubmission: 'Reenvio de autoridade',
            sla_type_authority_filing_supplement_response: 'Resposta do suplemento de arquivamento da autoridade',
            sla_type_authority_filing_resubmission: 'Reenvio de pedido de autoridade',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'dias úteis',
            unit_calendar_days: 'dias corridos',
            unit_hours: 'horas',
            form_category_dpa: 'APD',
            form_category_dsr: 'DSR',
            form_category_consent: 'Consentimento',
            form_category_privacy: 'Privacidade',
            form_category_breach: 'Violação',
            form_category_dpia: 'DPIA',
            form_category_register: 'Cadastre-se',
            form_category_monitoring: 'Monitoramento',
            form_category_vendor: 'Fornecedor',
            form_category_org: 'Organizacional',
            form_category_sector: 'Setor',
            form_category_data_mapping: 'Mapeamento de dados',
            form_category_governance: 'Governança',
            form_category_incident: 'Incidente',
            form_category_authority_submission: 'Envio de autoridade',
            form_category_notice: 'Aviso',
            form_category_assessment_report: 'Relatório de Avaliação',
            form_category_request: 'Solicitação',
            form_category_dossier: 'Dossiê',
            form_category_filing: 'Arquivamento',
            module_consent: 'Consentimento',
            module_rights_request: 'Solicitação de direitos',
            module_classification: 'Classificação',
            module_dpia: 'DPIA',
            module_governance: 'Governança',
            module_monitoring: 'Monitoramento',
            module_register: 'Cadastre-se',
            module_risk: 'Risco',
            module_data_mapping: 'Mapeamento de dados',
            module_incident: 'Incidente',
            module_assessment: 'Avaliação',
            module_vendor: 'Fornecedor',
            module_policy: 'Política',
            module_audit: 'Auditoria',
            module_training: 'Treinamento',
            module_discovery: 'Descoberta',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Orientação de cálculo',
            enum_guidance_type_implementation_guidance: 'Orientação de implementação',
            enum_guidance_type_interpretive_note: 'Nota Interpretativa',
            enum_guidance_type_legal_guidance: 'Orientação Jurídica',
            enum_guidance_type_procedural_guidance: 'Orientação Processual',
            enum_guidance_type_sector_guidance: 'Orientação Setorial',
            enum_guidance_type_technical_methodology: 'Metodologia Técnica',
            enum_guidance_category_annual_reporting: 'Relatório Anual',
            enum_guidance_category_authority_cooperation: 'Cooperação de Autoridade',
            enum_guidance_category_authority_coordination: 'Coordenação de Autoridade',
            enum_guidance_category_business_continuity: 'Continuidade de Negócios',
            enum_guidance_category_capital_adequacy: 'Adequação de capital',
            enum_guidance_category_complaint_handling: 'Tratamento de reclamações',
            enum_guidance_category_consent: 'Consentimento',
            enum_guidance_category_consent_management: 'Gerenciamento de consentimento',
            enum_guidance_category_consumer_protection: 'Defesa do Consumidor',
            enum_guidance_category_control_environment: 'Ambiente de controle',
            enum_guidance_category_cross_border: 'Transfronteiriço',
            enum_guidance_category_cross_border_transfers: 'Transferências transfronteiriças',
            enum_guidance_category_data_classification: 'Classificação de dados',
            enum_guidance_category_data_localization: 'Localização de dados',
            enum_guidance_category_data_protection_impact_assessment: 'Avaliação de impacto na proteção de dados',
            enum_guidance_category_direct_marketing: 'Marketing Direto',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Governança',
            enum_guidance_category_ict_risk_management: 'Gestão de riscos de TIC',
            enum_guidance_category_incident_management: 'Gerenciamento de Incidentes',
            enum_guidance_category_internal_audit: 'Auditoria Interna',
            enum_guidance_category_isms_documentation: 'Documentação do SGSI',
            enum_guidance_category_lawful_basis: 'Base Legal',
            enum_guidance_category_licensing: 'Licenciamento',
            enum_guidance_category_local_presence: 'Presença Local',
            enum_guidance_category_logging_and_retention: 'Registro e retenção',
            enum_guidance_category_logical_and_physical_access: 'Acesso lógico e físico',
            enum_guidance_category_operational_resilience_testing: 'Teste de resiliência operacional',
            enum_guidance_category_outsourcing: 'Terceirização',
            enum_guidance_category_platform_registration: 'Cadastro na plataforma',
            enum_guidance_category_quality_of_service: 'Qualidade de Serviço',
            enum_guidance_category_risk_classification: 'Classificação de Risco',
            enum_guidance_category_risk_management: 'Gestão de Risco',
            enum_guidance_category_scope_definition: 'Definição de escopo',
            enum_guidance_category_sectoral_localisation: 'Localização Setorial',
            enum_guidance_category_subscriber_protection: 'Proteção do Assinante',
            enum_guidance_category_supply_chain: 'Cadeia de Suprimentos',
            enum_guidance_category_system_classification: 'Classificação do Sistema',
            enum_guidance_category_system_operations: 'Operações do sistema',
            enum_guidance_category_telecommunications_data: 'Dados de Telecomunicações',
            enum_guidance_category_third_party_risk: 'Risco de Terceiros',
            enum_guidance_category_transitional_provisions: 'Disposições Transitórias',
            enum_guidance_category_vulnerability_management: 'Gerenciamento de vulnerabilidades',
            enum_guidance_status_active: 'Ativo',
            enum_guidance_status_draft: 'Rascunho',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Gerenciamento de acesso',
            enum_module_ai_governance: 'AI Governança',
            enum_module_asset_management: 'Gestão de Ativos',
            enum_module_audit_trail: 'Trilha de auditoria',
            enum_module_breach_notification: 'Notificação de violação',
            enum_module_capital_adequacy: 'Adequação de capital',
            enum_module_classification: 'Classificação',
            enum_module_consent: 'Consentimento',
            enum_module_consent_management: 'Gerenciamento de consentimento',
            enum_module_cross_border_transfers: 'Transferências transfronteiriças',
            enum_module_data_localization: 'Localização de dados',
            enum_module_data_mapping: 'Mapeamento de dados',
            enum_module_data_protection: 'Proteção de Dados',
            enum_module_data_subject_rights: 'Direitos do Titular dos Dados',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Criptografia',
            enum_module_governance: 'Governança',
            enum_module_incident: 'Incidente',
            enum_module_incident_management: 'Gerenciamento de Incidentes',
            enum_module_internal_audit: 'Auditoria Interna',
            enum_module_isms_documentation: 'Documentação do SGSI',
            enum_module_isms_governance: 'Governança do SGSI',
            enum_module_logging_and_monitoring: 'Registro e monitoramento',
            enum_module_monitoring: 'Monitoramento',
            enum_module_policy_management: 'Gerenciamento de Políticas',
            enum_module_risk: 'Risco',
            enum_module_risk_management: 'Gestão de Risco',
            enum_module_training: 'Treinamento',
            enum_module_vendor: 'Fornecedor',
            enum_module_vendor_management: 'Gestão de Fornecedores',
            enum_module_vendor_risk: 'Risco do fornecedor',
            enum_module_vulnerability_management: 'Gerenciamento de vulnerabilidades',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Acesso',
            category_availability: 'Disponibilidade',
            category_change_management: 'Gestão de Mudanças',
            category_classification: 'Classificação',
            category_communication_and_information: 'Comunicação e Informação',
            category_compliance: 'Conformidade',
            category_confidentiality: 'Confidencialidade',
            category_control_activities: 'Atividades de controle',
            category_control_environment: 'Ambiente de controle',
            category_incident: 'Incidente',
            category_internal_audit: 'Auditoria Interna',
            category_internal_control: 'Controle Interno',
            category_logical_and_physical_access: 'Acesso lógico e físico',
            category_mis_data: 'Dados do Sistema de Informação de Gestão',
            category_monitoring: 'Monitoramento',
            category_monitoring_activities: 'Atividades de monitoramento',
            category_outsourcing: 'Terceirização',
            category_privacy: 'Privacidade',
            category_processing_integrity: 'Integridade de Processamento',
            category_reporting: 'Relatórios',
            category_risk_assessment: 'Avaliação de Risco',
            category_risk_mitigation: 'Mitigação de riscos',
            category_sector_applicability: 'Aplicabilidade Setorial',
            category_system_operations: 'Operações do sistema',
            category_training: 'Treinamento',
            category_vendor: 'Fornecedor',
            guidance_related_forms_advisory: 'Alguns formulários possuem orientações relacionadas — veja no',
            guidance_related_procedures_advisory: 'Alguns procedimentos possuem orientações relacionadas — veja no',
            guidance_related_requirements_advisory: 'Alguns requisitos têm orientações relacionadas — veja no',
        },
        channels: {
            title: 'Canais de submissão e prazos',
            channels: 'Canais de submissão',
            add_channel: 'Adicionar canal',
            channel_type: 'Tipo',
            name: 'Nome',
            instructions: 'Instruções',
            active: 'Ativo',
            no_channels: 'Nenhum canal de submissão configurado',
            portal: 'Portal',
            in_person: 'Presencial',
            postal: 'Postal',
            email: 'E-mail',
            add: 'Adicionar',
            deadlines: 'Prazos regulatórios',
            add_deadline: 'Adicionar prazo',
            label: 'Rótulo',
            days: 'Dias',
            day_type: 'Tipo de dia',
            calendar_days: 'Dias corridos',
            working_days: 'Dias úteis',
            no_deadlines: 'Nenhum prazo configurado',
            description: 'Descrição',
            failed_load: 'Falha ao load channel data',
            channel_added: 'Canal adicionado',
            channel_added_announce: 'Canal de submissão adicionado',
            failed_add_channel: 'Falha ao add channel',
            deadline_added: 'Prazo adicionado',
            deadline_added_announce: 'Prazo regulatório adicionado',
            failed_add_deadline: 'Falha ao add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Vinculação da estrutura',
            modules: 'Módulos vinculados',
            add_module: 'Adicionar módulo',
            module_code: 'Código do módulo',
            required: 'Obrigatório',
            link: 'Vincular',
            no_modules: 'Nenhum módulo vinculado',
            notes: 'Notas',
            personas: 'Personas vinculados',
            add_persona: 'Adicionar persona',
            persona_id: 'ID do persona',
            role_description: 'Descrição do papel',
            no_personas: 'Nenhum persona vinculado',
            failed_load: 'Falha ao load framework links',
            module_linked: 'Módulo vinculado',
            module_linked_announce: 'Módulo vinculado com sucesso',
            failed_link_module: 'Falha ao link module',
            persona_linked: 'Persona vinculado',
            persona_linked_announce: 'Persona vinculado com sucesso',
            failed_link_persona: 'Falha ao link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Pendente',
                assessed: 'Avaliado',
                applied: 'Aplicado',
                dismissed: 'Descartado',
            },
            title: 'Atualizações regulatórias e painel de estruturas',
            page_description: 'Coverage at a glance for every installed regulatory framework, plus the in-product change log of regulatory updates: amendments, effective dates, and impact assessments. [PT]',
            coverage_dashboard: 'Painel de cobertura da estrutura',
            no_frameworks: 'Nenhuma estrutura encontrada',
            framework: 'Estrutura',
            modules: 'Módulos',
            templates: 'Modelos',
            forms: 'Formulários',
            channels: 'Canais',
            deadlines: 'Prazos',
            pending: 'Atualizações pendentes',
            updates: 'Atualizações regulatórias',
            create_update: 'Criar atualização',
            framework_id: 'ID da estrutura',
            update_title: 'Título',
            description: 'Descrição',
            effective_date: 'Data de vigência',
            amendment_ref: 'Referência de alteração',
            create: 'Criar',
            no_updates: 'Sem atualizações regulatórias',
            status: 'Estado',
            impact: 'Impacto',
            change_summary: 'Resumo das alterações',
            assess: 'Avaliar',
            failed_load: 'Falha ao load data',
            update_created: 'Atualização criada',
            update_created_announce: 'Atualização regulatória criada',
            failed_create: 'Falha ao create update',
            update_assessed: 'Atualização avaliada',
            failed_assess: 'Falha ao assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Enquadramentos relacionados',
            panel_intro: 'Como este enquadramento se relaciona com outros. Uma camada opcional pode aplicar-se sob vários enquadramentos superiores em simultâneo; cada um aparece aqui exatamente uma vez.',
            outgoing_heading: 'Este enquadramento aplica-se a',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Aplica-se sob este enquadramento',
            outgoing_caption: 'Enquadramentos para os quais este aponta',
            incoming_caption: 'Enquadramentos que dependem deste',
            col_framework: 'Enquadramento',
            col_relationship: 'Relação',
            col_scope: 'Origem',
            scope_global: 'Do conteúdo do pacote',
            scope_org: 'Específico da organização',
            load_failed: 'Não foi possível carregar os enquadramentos relacionados',
            relationship_kinds: {
                parent: 'Enquadramento superior',
                applies_to: 'Aplica-se a',
                cross_reference: 'Referência cruzada',
                supersedes: 'Substitui',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Banking [PT]',
                insurance: 'Insurance [PT]',
                healthcare: 'Healthcare [PT]',
                education: 'Education',
                ict_critical: 'Critical ICT [PT]',
                telecom: 'Telecommunications [PT]',
                energy: 'Energy [PT]',
                transport: 'Transport [PT]',
                digital_services: 'Digital services [PT]',
                digital_technology_industry: 'Indústria de tecnologia digital',
                manufacturing: 'Manufacturing',
                public_administration: 'Public administration [PT]',
                payments: 'Payments [PT]',
                publishing: 'Edição',
                crypto_assets: 'Criptoativos',
                other: 'Other [PT]',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sector [PT]',
            direct_scope_label: 'Direct legal scope [PT]',
            chip_direct: 'Directly applicable [PT]',
            chip_reference: 'Reference only [PT]',
            banner_direct_copy: 'Directly applicable to your organization. Sector reports and deadlines are active. [PT]',
            banner_reference_copy: 'Reference only — your organization is not classified in this sector. [PT]',
            edit_mode_button: 'Edit mode [PT]',
            edit_form_title: 'Change install mode [PT]',
            mode_direct_label: 'Direct — legally applicable [PT]',
            mode_direct_help: 'Schedules sector deadlines and dashboard widgets. [PT]',
            mode_reference_label: 'Reference only [PT]',
            mode_reference_help: 'Browseable but no deadlines or widgets. [PT]',
            reason_note_label: 'Reason (optional) [PT]',
            reason_note_placeholder: 'e.g. Bank subsidiary acquired [PT]',
            cancel: 'Cancel [PT]',
            confirm: 'Confirm [PT]',
            confirming: 'Saving… [PT]',
            save: 'Save [PT]',
            saving: 'Saving… [PT]',
            toast_mode_changed: 'Sector overlay mode updated. [PT]',
            toast_installed: 'Sector overlay installed. [PT]',
            toast_sectors_saved: 'Sector self-identification saved. [PT]',
            install_modal_title: 'Install sector overlay [PT]',
            install_modal_intro: 'Sector overlays are opt-in regulatory packs scoped to a single industry. [PT]',
            install_modal_footer: 'You can change install mode later from the framework detail page. [PT]',
            install_button: 'Install [PT]',
            installed_label: 'Installed [PT]',
            uninstall_button: 'Desinstalar',
            uninstall_confirm: 'Confirmar desinstalação',
            uninstall_cancel: 'Cancelar',
            suggested_label: 'Suggested [PT]',
            catalog_page_title: 'Sector overlays [PT]',
            catalog_page_intro: 'Optional regulatory packs scoped to a single industry sector. [PT]',
            catalog_empty_title: 'No sector overlays available [PT]',
            catalog_empty_description: 'Sector overlay packs published in your platform will appear here. [PT]',
            authority_label: 'Authority [PT]',
            org_sectors_title: 'Industry sectors [PT]',
            org_sectors_intro: 'Select the industry sectors your organization operates in. [PT]',
            widget_title: 'Sector overlay reports due [PT]',
            widget_aria_label: 'Sector overlay reports due [PT]',
            widget_empty: 'No upcoming sector overlay deadlines. [PT]',
            audit_event_mode_changed: 'Sector overlay install mode changed [PT]',
            audit_event_installed: 'Sector overlay installed [PT]',
            view_details_button: 'View details → [PT]',
            banner_aria_label: 'Sector overlay status [PT]',
            toast_load_failed: 'Failed to load sector overlays [PT]',
            toast_install_failed: 'Failed to install sector overlay [PT]',
            toast_uninstall_confirm_required: 'Type "{packCode}" to confirm uninstall. [PT]',
            toast_uninstalled: 'Uninstalled {packCode}. [PT]',
            toast_uninstall_failed: 'Failed to uninstall sector overlay [PT]',
            uninstall_warning_destructive_label: 'Destructive action. [PT]',
            uninstall_warning_body: 'Uninstalling will delete the framework record and any persisted regulatory deadlines tied to {frameworkCode} from this organisation. [PT]',
            uninstall_warning_type_to_confirm: 'Type {packCode} to confirm. [PT]',
            uninstall_in_progress: 'Uninstalling… [PT]',
            widget_view_all: 'View all sector overlays → [PT]',
            widget_due_in_days: 'Due in {days}d [PT]',
            widget_overdue_days: 'Overdue by {days}d [PT]',
            widget_due_today: 'Due today [PT]',
            widget_due_unknown: '— [PT]',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
                product_identification_certificate_receipt: 'Certificate synchronisation receipt',
                product_identification_connection_readiness: 'Connection and transition readiness record',
                product_identification_event_record: 'Significant supply-chain event record',
                product_identification_provider_agreement: 'Service-provider data-responsibility agreement',
                product_identification_scope_determination: 'Product scope and risk-tier determination',
                product_identification_uid_register: 'UID and master-data register entry',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_identification_scope_determinations: 'Product Scope Determinations',
                product_identification_sector_watch: 'Sector Watch',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Phased obligations [PT]',
            panel_aria_label: 'Phased obligations [PT]',
            panel_intro: 'Some clauses in this framework activate later than the framework itself. [PT]',
            phased_effective_date_label: 'Phased effective: [PT]',
            status_pending: 'Pending [PT]',
            status_active: 'Active [PT]',
            status_early: 'Active (early-implemented) [PT]',
            notice_window_label: 'Authority notice window: [PT]',
            days_label: 'days [PT]',
            recipient_label: 'Recipient: [PT]',
            early_implement_button: 'Mark early-implemented [PT]',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Mark phased obligation as early-implemented [PT]',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent. [PT]',
            effective_at_label: 'Effective from [PT]',
            reason_label: 'Reason (optional) [PT]',
            reason_placeholder: 'e.g. Board approved early adoption 2026-09-01 [PT]',
            confirm: 'Confirm [PT]',
            toast_early_implemented: 'Early implementation recorded. [PT]',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Phased obligation marked early-implemented [PT]',
            widget_rolling_window_label: 'Phased activations within 90 days [PT]',
            notice_due_label: 'Notice due by the end of [PT]',
            notice_due_time_suffix: '(organisation time) [PT]',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day. [PT]',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent. [PT]',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday. [PT]',
                day_after_is_day_one: 'Computed: the day after implementation is day 1. [PT]',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period. [PT]',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies. [PT]',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states. [PT]',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read. [PT]',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due. [PT]',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run. [PT]',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read. [PT]',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Painel de controlo',
            partner_orgs: 'Organizações parceiras',
            service_tiers: 'Níveis de serviço',
            deliverable_templates: 'Modelos de entregáveis',
            deliverables: 'Entregáveis',
            brand_profiles: 'Perfis de marca',
            support_tickets: 'Tickets de suporte',
            workflow_templates: 'Modelos de fluxo de trabalho',
            workflows: 'Fluxos de trabalho',
            escalation: 'Escalamento',
            reports: 'Relatórios',
            audit: 'Registo de auditoria',
        },
        dashboard: {
            title: 'Painel de parceiros',
            loading: 'A carregar dashboard data...',
            total_managed_tenants: 'Total de inquilinos geridos',
            avg_health_score: 'Avg Saúde Pontuação',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Tickets abertos',
            upcoming_deliverables: 'Entregáveis futuros',
            escalated_tickets: 'Tickets escalados',
            managed_tenants: 'Inquilinos geridos',
            partner_tier: 'Parceiro Nível',
            managed_tenants_count: 'Contagem de inquilinos geridos',
            onboarding_date: 'Data de integração',
            view_details: 'Ver detalhes',
            no_partner_orgs: 'Nenhum partner organizations encontrado',
        },
        partner_orgs: {
            title: 'Organizações parceiras',
            new: 'Novo Partner Org',
            contact_name: 'Contacto Nome',
            contact_email: 'Contacto E-mail',
            partner_tier: 'Parceiro Nível',
            managed_orgs: 'Organizações geridas',
            onboarding_date: 'Data de integração',
            no_orgs: 'Nenhum partner organizations encontrado',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contacto Telefone',
            metadata_json: 'Metadados (JSON)',
            change_status: 'Alterar estado',
            new_status: 'Novo estado',
            invalid_json_metadata: 'JSON inválido no campo de metadados',
            suspended: 'Suspenido',
            offboarded: 'Desintegrado',
            all_tiers: 'Todos os níveis',
            silver: 'Prata',
            gold: 'Ouro',
            platinum: 'Platina',
            status_offboarded: 'Desintegrado',
            status_suspended: 'Suspenido',
            tier_gold: 'Ouro',
            tier_platinum: 'Platina',
            tier_silver: 'Prata',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Níveis de serviço',
            new: 'Novo nível',
            tier_name: 'Nível Nome',
            monthly_fee: 'Mensalidade',
            features: 'Funcionalidades',
            no_tiers: 'Nenhum service tiers encontrado',
            tier_code: 'Código de nível',
            response_time_hrs: 'Resposta Hora (hrs)',
            resolution_time_hrs: 'Tempo de resolução (horas)',
            review_frequency: 'Rever Frequência',
            sla_response_time: 'SLA Resposta Hora (hours)',
            sla_resolution_time: 'SLA Resolution Hora (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'A comparar...',
            compare: 'Comparar',
            comparison: 'Comparação de níveis de serviço',
            attribute: 'Atributo',
            deprecate: 'Tornar obsoleto',
            deprecated: 'Obsoleto',
            select_compare: 'Selecione at least 2 tiers to compare',
            comparison_title: 'Comparação de níveis de serviço',
            status_deprecated: 'Obsoleto',
            activate: 'Ativar',
            resolution_time: 'Tempo de resolução',
            response_time: 'Resposta Hora',
            select_at_least_two: 'Selecione at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Modelos de entregáveis',
            new: 'Novo modelo',
            template_name: 'Modelo Nome',
            deliverable_type: 'Tipo de entregável',
            no_templates: 'Nenhum deliverable templates encontrado',
            frequency: 'Frequência',
            est_effort_hrs: 'Esforço est. (h)',
            estimated_effort: 'Esforço estimado (horas)',
            associated_module: 'Módulo associado',
            linked_tiers_count: 'Contagem de níveis ligados',
            evidence_requirements: 'Requisitos de evidência (separados por vírgulas)',
            tier_ids: 'IDs de níveis (separados por vírgulas)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'Uma vez',
            frequency_monthly: 'Mensal',
            frequency_quarterly: 'Trimestral',
            frequency_annually: 'Anualmente',
            type_report: 'Relatório',
            type_review: 'Rever',
            type_assessment: 'Avaliação',
            type_meeting: 'Reunião',
            type_configuration: 'Configuração',
            freq_one_time: 'Uma vez',
            freq_monthly: 'Mensal',
            freq_quarterly: 'Trimestral',
            freq_annually: 'Anualmente',
            status_deprecated: 'Obsoleto',
        },
        deliverables: {
            title: 'Entregáveis',
            new: 'Novo Deliverable',
            partner: 'Parceiro',
            deliverable_type: 'Tipo de entregável',
            due_date: 'Data de vencimento',
            no_deliverables: 'Nenhum deliverables encontrado',
            assigned_to: 'Atribuído Para',
            completion_date: 'Data de conclusão',
            update_status: 'Atualizar Estado',
            hide_details: 'Ocultar Detalhes',
            view_details: 'Ver detalhes',
            evidence_ids: 'IDs de evidências',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Aprovação do cliente',
            bulk_update: 'Atualização em massa',
            bulk_update_status: 'Bulk Atualizar Estado',
            generate_schedule: 'Gerar Agendar',
            generate_deliverable_schedule: 'Gerar Deliverable Agendar',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organização ID',
            service_tier_id: 'ID do nível de serviço',
            filter_assigned_to: 'Filtrar por Atribuído Para',
            select_status: 'Selecione status...',
            new_status: 'Novo estado',
            upcoming: 'Futuro',
            overdue: 'Em atraso',
            status_deferred: 'Adiado',
            status_overdue: 'Em atraso',
            status_upcoming: 'Futuro',
        },
        brand_profiles: {
            title: 'Perfis de marca',
            new: 'Novo Brand Profile',
            partner: 'Parceiro',
            brand_name: 'Nome da marca',
            primary_color: 'Cor primária',
            logo_url: 'URL do logótipo',
            no_profiles: 'Nenhum brand profiles encontrado',
            secondary_color: 'Cor secundária',
            header_text: 'Texto do cabeçalho',
            footer_text: 'Texto do rodapé',
            contact_email: 'Contacto E-mail',
            contact_phone: 'Contacto Telefone',
            contact_website: 'Site web de contacto',
            contact_address: 'Contacto Endereço',
            is_default: 'É Predefinido',
            set_default: 'Definir predefinição',
            preview: 'Pré-visualização',
            report_header_preview: 'Pré-visualização do cabeçalho do relatório',
            report_footer_preview: 'Pré-visualização do rodapé do relatório',
            preview_description: 'Esta é uma pré-visualização do cabeçalho do relatório com as cores de marca selecionadas.',
            contact_website_short: 'Site web',
            logo_preview: 'Pré-visualização do logótipo',
        },
        support_tickets: {
            title: 'Tickets de suporte',
            new: 'Novo Ticket',
            partner: 'Parceiro',
            subject: 'Titular',
            assigned_to: 'Atribuído Para',
            no_tickets: 'Nenhum support tickets encontrado',
            category: 'Categoria',
            priority: 'Prioridade',
            escalation_tier: 'Escalamento Nível',
            sla_status: 'SLA Estado',
            update_status: 'Atualizar Estado',
            escalate: 'Escalar',
            add_note: 'Adicionar Note',
            escalate_ticket: 'Escalar ticket',
            escalate_to_tier: 'Escalar Para Nível',
            notes_timeline: 'Linha cronológica de notas',
            no_notes: 'Sem notas ainda.',
            attachments: 'Anexos',
            no_attachments: 'Sem anexos.',
            client_org_id: 'ID Org. do cliente',
            content: 'Conteúdo',
            note_type: 'Tipo',
            view_detail: 'Ver Detail',
            hide: 'Ocultar',
            new_status: 'Novo estado',
            all_categories: 'Todas as categorias',
            all_priorities: 'Todas as prioridades',
            all_tiers: 'Todos os níveis',
            ticket_not_found: 'Ticket não encontrado',
            ticket_id: 'ID do ticket',
            client_org: 'Org. do cliente',
            deescalate: 'Desescalar',
            deescalate_ticket: 'Desescalar o ticket',
            deescalate_to_tier: 'De-escalate Para Nível',
            add_attachment: 'Adicionar anexo',
            file: 'Ficheiro',
            linked_entity: 'Entidade ligada',
            sla_target: 'Destino',
            breached: 'Violado',
            remaining_hours: 'restantes',
            na: 'N/A',
            tickets_found: 'tickets encontrados',
            reason: 'Motivo',
            target: 'Destino',
            upload: 'Carregar',
        },
        workflow_templates: {
            title: 'Modelos de fluxo de trabalho',
            new: 'Novo modelo',
            template_name: 'Modelo Nome',
            steps: 'Passos',
            no_templates: 'Nenhum workflow templates encontrado',
            category: 'Categoria',
            view_steps: 'Ver Steps',
            hide_steps: 'Ocultar passos',
            step: 'Passo',
            step_title: 'Título',
            step_description: 'Descrição',
            assignee_role: 'Atribuído Papel',
            sla_hours: 'Horas SLA',
            evidence_required: 'Evidência Obrigatório',
            guidance_notes: 'Notas de orientação',
            add_step: 'Adicionar Step',
            no_steps: 'Sem passos definidos.',
            no_steps_defined: 'Sem passos definidos.',
            templates_found: 'modelos encontrados',
            all_categories: 'Todas as categorias',
        },
        workflows: {
            client_org: 'Org. do cliente',
            title: 'Instâncias de fluxo de trabalho',
            new: 'Novo fluxo de trabalho',
            template: 'Modelo',
            partner: 'Parceiro',
            current_step: 'Passo atual',
            progress: 'Progresso',
            no_workflows: 'Nenhum workflow instances encontrado',
            instances_found: 'instâncias encontradas',
        },
        escalation: {
            title: 'Escalamento Painel de controlo',
            ticket: 'Bilhete',
            partner: 'Parceiro',
            escalated_at: 'Escalado em',
            reason: 'Motivo',
            no_escalations: 'Nenhum escalations encontrado',
            time_in_tier: 'Hora em Nível',
            auto_evaluate: 'Avaliação auto.',
            priority_critical: 'Crítico',
            priority_high: 'Alto',
            priority_medium: 'Médio',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Desescalado do painel',
            sla_breached: 'SLA violado',
            sla_remaining: 'SLA restante: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Org. do cliente',
            title: 'Relatórios',
            partner: 'Parceiro',
            report_type: 'Relatório Tipo',
            generated_at: 'Gerado em',
            no_reports: 'Nenhum reports encontrado',
            generate: 'Gerar Relatório',
        },
        audit: {
            title: 'Parceiro Auditoria Trail',
            event_type: 'Evento Tipo',
            entity_type: 'Tipo de entidade',
            entity: 'Entidade',
            entity_id: 'ID da entidade',
            actor: 'Ator',
            timestamp: 'Marca temporal',
            no_events: 'Nenhum audit events encontrado',
            events_found: 'eventos encontrados',
            client_org: 'Organização cliente',
            all_actors: 'Todos os intervenientes',
            event_type_search: 'Pesquisar tipo de evento',
            event_type_created: 'Criado',
            event_type_updated: 'Atualizado',
            event_type_deleted: 'Excluído',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Organização parceira',
            entity_type_service_tier: 'Nível de serviço',
            entity_type_deliverable_template: 'Modelo de entrega',
            entity_type_deliverable_instance: 'Instância de entrega',
            entity_type_brand_profile: 'Perfil da marca',
            entity_type_support_ticket: 'Tíquete de suporte',
            entity_type_workflow_template: 'Modelo de fluxo de trabalho',
            entity_type_workflow_instance: 'Instância de fluxo de trabalho',
            entity_type_generated_report: 'Relatório gerado',
            entity_type_audit_export: 'Exportação de auditoria',
        },
        no_permission_title: 'Permissões insuficientes',
        no_permission_message: 'Você não tem permissão para acessar o espaço de trabalho do parceiro.',
        status_active: 'Ativo',
        status_suspended: 'Suspenso',
        status_offboarded: 'Offboard',
        status_completed: 'Concluído',
        status_cancelled: 'Cancelado',
        status_deprecated: 'Obsoleto',
        status_new: 'Novo',
        status_triaged: 'Triagem',
        status_in_progress: 'Em andamento',
        status_waiting_on_client: 'Esperando no cliente',
        status_waiting_on_vendor: 'Esperando pelo fornecedor',
        status_escalated: 'Escalado',
        status_resolved: 'Resolvido',
        status_closed: 'Fechado',
        status_pending: 'Pendente',
        status_skipped: 'Ignorado',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Integração',
        category_incident_escalation: 'Escalação de Incidentes',
        category_config_change: 'Alteração de configuração',
        category_periodic_review: 'Revisão Periódica',
        category_offboarding: 'Desativação',
        category_custom: 'Personalizado',
        category_configuration: 'Configuração',
        category_incident: 'Incidente',
        category_compliance_question: 'Pergunta de conformidade',
        category_feature_request: 'Solicitação de recurso',
        category_bug_report: 'Relatório de erros',
        priority_critical: 'Crítico',
        priority_high: 'Alto',
        priority_medium: 'Médio',
        priority_low: 'Baixo',
        tier_silver: 'Prata',
        tier_gold: 'Ouro',
        tier_platinum: 'Platina',
        report_type_compliance_summary: 'Resumo de conformidade',
        report_type_risk_overview: 'Visão geral dos riscos',
        report_type_incident_report: 'Relatório de incidente',
        report_type_consent_status: 'Status de consentimento',
        report_type_program_maturity: 'Maturidade do Programa',
        report_type_executive_pack: 'Pacote Executivo',
        note_type_internal: 'Interno',
        note_type_client_visible: 'Cliente visível',
        status_updated: 'Status atualizado com sucesso',
        deliverable_status_updated: 'Status da entrega atualizado com sucesso',
        ticket_status_updated: 'Status do ticket atualizado com sucesso',
        ticket_escalated: 'Ticket escalado com sucesso',
        note_added: 'Nota adicionada com sucesso',
        attachment_uploaded: 'Anexo enviado com sucesso',
        ticket_deescalated: 'Ticket desescalado com sucesso',
        auto_evaluation_completed: 'Avaliação automática concluída',
        template_created: 'Modelo de fluxo de trabalho criado com sucesso',
        template_updated: 'Modelo de fluxo de trabalho atualizado com sucesso',
        brand_set_default: 'Perfil de marca definido como padrão com sucesso',
        brand_created: 'Perfil da marca criado com sucesso',
        brand_updated: 'Perfil da marca atualizado com sucesso',
        tier_created: 'Camada de serviço criada com sucesso',
        tier_updated: 'Camada de serviço atualizada com sucesso',
        tier_deprecated: 'Camada de serviço obsoleta com sucesso',
        tier_activated: 'Camada de serviço ativada com sucesso',
        period: 'Período',
        format: 'Formato',
        period_start: 'Início do período',
        period_end: 'Fim do período',
        reports_count: '{count} relatórios',
        reports_count_one: '{count} relatório',
        metric: 'Métrica',
        value: 'Valor',
        showing_chart_view: 'Mostrando visualização de gráfico',
        showing_table_view: 'Mostrando visualização da tabela de dados',
        evidence_ids_placeholder: 'por exemplo ev-001, ev-002',
        confirm_cancel_workflow: 'Tem certeza de que deseja cancelar este fluxo de trabalho? Esta ação não pode ser desfeita.',
        export_csv: 'Exportar CSV',
        compliance_qualifications_title: 'Qualificações de serviço de conformidade',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Alinhamento de Saída Regulatória',
        regulatory_output_desc: 'Cada tipo de modelo é mapeado para uma categoria de resultados regulatórios: Relatórios produzem documentação de conformidade, Revisões validam a eficácia do controle, Avaliações cobrem avaliações de risco (DPIA, análise de lacunas), Reuniões documentam sessões de governança. Os Requisitos de Evidência definem os artefatos que cada entrega deve referenciar para preparação para auditoria.',
        sla_compliance_title: 'SLA Acompanhamento para entrega de conformidade',
        sla_compliance_desc: 'Os SLAs de tempo de resposta e resolução são contratualmente vinculativos para a prestação de serviços de conformidade. A frequência de revisão determina a frequência com que as entregas de conformidade dos parceiros são avaliadas. As violações de SLA em tickets críticos para conformidade acionam o escalonamento automático e são registradas na trilha de auditoria do parceiro para obter evidências regulatórias.',
        regulatory_engagement_title: 'Padrões de envolvimento regulatório',
        regulatory_engagement_desc: 'As categorias de fluxo de trabalho são mapeadas para tipos de envolvimento regulatório: a integração cobre a configuração inicial de conformidade e a análise de lacunas, a revisão periódica lida com avaliações de conformidade recorrentes, o escalonamento de incidentes gerencia a resposta a violações regulatórias, a exclusão garante que as obrigações de retenção de dados sejam cumpridas. Cada etapa pode exigir a coleta de evidências para a integridade da trilha de auditoria.',
        compliance_escalation_title: 'Procedimentos de escalonamento críticos para conformidade',
        compliance_escalation_desc: 'L1 lida com questões operacionais padrão. L2 cobre falhas que impactam a conformidade (prazos regulatórios não cumpridos, lacunas de controle). L3 é reservado para cenários de violação regulatória que exigem atenção executiva imediata. A violação SLA em tickets L2/L3 gera entradas de trilha de auditoria automaticamente. Todas as ações de escalonamento e desescalonamento são registradas permanentemente.',
        audit_regulatory_title: 'Trilha de evidências regulatórias',
        audit_regulatory_desc: 'Essa trilha de auditoria fornece registros invioláveis para conformidade regulatória. Cada ação do parceiro (criação, atualização, exclusão, escalonamento, mudança de status) é registrada com identidade do ator, carimbo de data/hora e diferença de carga útil completa. Use filtros de tipo de entidade para isolar evidências por domínio de conformidade. Exporte resultados filtrados para produzir documentação pronta para auditoria para inspeções regulatórias.',
        evidence_trail_title: 'Trilha de evidências entre módulos',
        evidence_trail_desc: 'Cada etapa do fluxo de trabalho pode coletar IDs de evidências vinculadas a resultados, eventos de auditoria e artefatos de conformidade em todos os módulos. Avançar uma etapa com IDs de evidências cria uma cadeia verificável que conecta fluxos de trabalho de parceiros à documentação regulatória. Fluxos de trabalho concluídos com cadeias de evidências completas servem como prova da entrega do compromisso de conformidade.',
        evidence_ids_label: 'IDs de evidências',
    },
    org_groups: {
        title: 'Grupos de organizações',
        new: 'Adicionar grupo',
        group_name: 'Grupo Nome',
        member_count: 'Contagem de membros',
        root_org: 'Organização raiz',
        no_groups: 'Nenhum org groups encontrado',
        add_organization: 'Adicionar Organização',
        select_organization: 'Selecione uma organização...',
        no_available_orgs: 'Nenhuma organização disponível para adicionar.',
        no_members: 'Nenhuma organização membro neste grupo.',
        member_added: 'Organização adicionada ao grupo.',
        member_removed: 'Organização removida do grupo.',
        already_in_group: 'Esta organização já pertence a um grupo.',
        org_not_found: 'Organização não encontrada.',
        add_member_error: 'Falha ao adicionar organização ao grupo.',
        remove_member_error: 'Falha ao remover organização do grupo.',
        confirm_remove_member: 'Tem a certeza de que pretende remover "{name}" deste grupo?',
        load_error: 'Falha ao carregar detalhes do grupo de organizações.',
        page_title_list: 'Grupos organizacionais',
        page_title_detail: 'Detalhes do grupo organizacional',
        page_title_create: 'Criar grupo organizacional',
        page_title_settings: 'Configurações do grupo organizacional',
        page_title_users: 'Usuários do grupo',
        page_title_reports: 'Relatórios de grupo',
        page_title_dashboard: 'Painel do grupo',
        page_title_audit: 'Auditoria de Grupo',
        page_title_org_audit: 'Auditoria de Grupo Organizacional',
        page_title_shared_vendors: 'Fornecedores Compartilhados',
        page_title_shared_frameworks: 'Estruturas Compartilhadas',
        page_title_shared_forms: 'Modelos de formulário compartilhados',
        page_title_merged_vendors: 'Fornecedores mesclados',
        page_title_settings_inheritance: 'Herança de configurações',
        announce_loaded_groups: 'Grupos organizacionais {count} carregados',
        announce_loaded_group: 'Grupo carregado {name}',
        announce_loaded_users: 'Usuários do grupo {count} carregados',
        announce_loaded_reports: 'Relatórios {count} carregados',
        announce_loaded_vendors: '{count} fornecedores compartilhados carregados',
        announce_loaded_frameworks: 'Estruturas compartilhadas {count} carregadas',
        announce_loaded_templates: 'Modelos de formulário compartilhado {count} carregados',
        announce_loaded_audit: 'Eventos de auditoria {count} carregados',
        announce_loaded_merged: 'Carregado {shared} compartilhado e {local} fornecedores locais',
        announce_dashboard_loaded: 'Painel carregado.',
        announce_dashboard_refreshed: 'Painel atualizado.',
        announce_dashboard_refresh_failed: 'Falha ao atualizar o painel.',
        announce_refreshing: 'Atualizando painel...',
        announce_settings_loaded: 'Herança de configurações carregada.',
        announce_settings_saved: 'Configurações salvas com sucesso.',
        announce_saving_settings: 'Salvando configurações...',
        announce_error_saving: 'Erro ao salvar as configurações.',
        announce_group_dissolved: 'Grupo dissolvido.',
        announce_creating_group: 'Criando grupo organizacional...',
        announce_group_created: 'Grupo organizacional criado com sucesso.',
        announce_error_creating: 'Erro ao criar o grupo organizacional.',
        announce_vendor_published: 'Fornecedor publicado com sucesso.',
        announce_vendor_retired: 'Vendedor aposentado.',
        announce_framework_published: 'Estrutura publicada com sucesso.',
        announce_framework_retired: 'Estrutura retirada.',
        announce_template_published: 'Modelo de formulário publicado com sucesso.',
        announce_template_retired: 'Modelo de formulário retirado.',
        announce_role_assigned: 'Função atribuída com sucesso.',
        announce_error_assign_role: 'Erro ao atribuir função.',
        announce_role_revoked: 'Função revogada com sucesso.',
        announce_report_generating: 'Gerando relatório...',
        announce_report_requested: 'Geração de relatório solicitada.',
        announce_error_report: 'Erro ao solicitar relatório.',
        announce_report_deleted: 'Relatório excluído.',
        announce_search_applied: 'Pesquisa aplicada.',
        announce_search_cleared: 'Pesquisa limpa.',
        announce_tab_selected: 'Guia {tab} selecionada',
        announce_error_add_member: 'Erro ao adicionar membro ao grupo.',
        announce_category_reverted: '{category} revertido para herdado do pai.',
        announce_category_overridden: '{category} definido para substituição local.',
        announce_override_saved: 'Substituição {category} salva.',
        announce_export_started: 'Exportando auditoria como {format}...',
        announce_export_completed: 'Exportação como {format} concluída.',
        announce_export_failed: 'Falha na exportação.',
        announce_filter_org: 'Filtrado por organização {name}',
        announce_filter_org_cleared: 'Filtro da organização limpo',
        announce_filter_event: 'Filtrado por tipo de evento {type}',
        announce_filter_cleared: 'Filtro limpo',
        announce_download: 'Baixando {name} como {format}',
        error_load_groups: 'Falha ao carregar grupos organizacionais. Por favor, tente novamente.',
        error_load_users: 'Falha ao carregar usuários do grupo.',
        error_load_reports: 'Falha ao carregar relatórios de grupo.',
        error_load_dashboard: 'Falha ao carregar o painel do grupo.',
        error_load_settings: 'Falha ao carregar as configurações do grupo.',
        error_load_vendors: 'Falha ao carregar fornecedores mesclados.',
        error_save_settings: 'Falha ao salvar as configurações.',
        error_dissolve_group: 'Falha ao dissolver o grupo.',
        error_assign_role: 'Falha ao atribuir função.',
        error_create_group: 'Falha ao criar o grupo organizacional. Por favor, tente novamente.',
        error_request_report: 'Falha ao solicitar relatório.',
        error_group_name_required: 'O nome do grupo é obrigatório.',
        success_settings_saved: 'Configurações salvas com sucesso.',
        confirm_retire_vendor: 'Tem certeza de que deseja retirar o fornecedor "{name}"?',
        confirm_retire_framework: 'Tem certeza de que deseja descontinuar a estrutura "{name}"?',
        confirm_retire_template: 'Tem certeza de que deseja retirar o modelo de formulário "{name}"?',
        confirm_dissolve_group: 'Tem certeza de que deseja dissolver "{name}"? Esta ação não pode ser desfeita.',
        confirm_revoke_role: 'Tem certeza de que deseja revogar a função "{role}" de "{name}"?',
        confirm_delete_report: 'Tem certeza de que deseja excluir o relatório "{name}"?',
        dissolve_group: 'Dissolver Grupo',
        dissolve_warning: 'Isso dissolverá permanentemente o grupo organizacional. Todas as organizações membros serão removidas.',
        count_groups: 'grupo {count}',
        count_groups_plural: '{count} grupos',
        count_users: '{count} usuário',
        count_users_plural: '{count} usuários com funções de grupo',
        count_events: 'Evento {count}',
        count_events_plural: '{count} eventos',
        count_reports: '{count} relatório',
        count_reports_plural: '{count} relatórios',
        count_vendors: '{count} fornecedor compartilhado pelo grupo',
        count_vendors_plural: '{count} fornecedores compartilhados no grupo',
        count_frameworks: 'Estrutura {count} compartilhada por todo o grupo',
        count_frameworks_plural: 'Estruturas {count} compartilhadas em todo o grupo',
        count_templates: 'Modelo {count} compartilhado pelo grupo',
        count_templates_plural: 'Modelos {count} compartilhados no grupo',
        count_shared_vendors: '{count} fornecedores compartilhados',
        count_local_vendors: '{count} fornecedores locais',
        merged_combined_view: 'Visão combinada de {shared} fornecedores compartilhados e {local} fornecedores locais',
        dashboard: {
            title: 'Painel do grupo',
            loading: 'A carregar dashboard data...',
            total_groups: 'Total Grupos',
            total_members: 'Total de membros',
            active_groups: 'Ativo Grupos',
            overall_compliance_score: 'Pontuação geral de conformidade',
            group_risk_level: 'Nível de risco do grupo',
            sla_compliance: 'SLA Conformidade',
            compliance_posture: 'Postura de Conformidade',
            risk_heatmap: 'Mapa de calor de risco',
            sla_performance: 'SLA Desempenho',
            recent_incidents: 'Incidentes Recentes',
            recent_activity: 'Atividade recente',
            refresh_label: 'Atualizar dados do painel',
            score_good: 'Bom',
            score_needs_attention: 'Precisa de atenção',
            score_critical: 'Crítico',
            trend_up: 'Tendências',
            trend_down: 'Tendência de queda',
            trend_stable: 'Estável',
            risk_low: 'Low',
            risk_medium: 'Médio',
            risk_high: 'Alto',
            risk_critical: 'Crítico',
            risk_unknown: 'Desconhecido',
            col_organization: 'Organização',
            col_score: 'Pontuação',
            col_trend: 'Tendência',
            col_open_issues: 'Problemas em aberto',
            col_critical_issues: 'Questões Críticas',
            col_health: 'Saúde',
            col_vendor_risk: 'Risco do fornecedor',
            col_data_risk: 'Risco de dados',
            col_compliance_risk: 'Risco de conformidade',
            col_total_cases: 'Total de casos',
            col_on_time: 'Na hora certa',
            col_breached: 'Violado',
            col_sla_percent: 'SLA%',
            col_avg_days: 'Média de dias',
            sr_vendor_risk: 'Pontuação de risco do fornecedor: {score}',
            sr_data_risk: 'Pontuação de risco de dados: {score}',
            sr_compliance_risk: 'Pontuação de risco de conformidade: {score}',
            sr_sla_breaches: 'Aviso: violações de {count} SLA',
            sr_severity: 'Gravidade: {severity}',
        },
        detail: {
            title: 'Detalhe do grupo',
            members: 'Membros',
            settings: 'Definições',
            audit: 'Auditoria',
            sr_loading: 'Carregando detalhes do grupo organizacional',
            sr_loading_hierarchy: 'Carregando hierarquia',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'organização membro',
            root_org_label: 'organização raiz',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Utilizadores do grupo',
            add_user: 'Adicionar utilizador',
            no_users: 'Sem utilizadores neste grupo.',
            role_group_admin: 'Administrador do grupo',
            role_group_reader: 'Leitor de grupo',
            sr_loading: 'Carregando usuários do grupo',
        },
        reports: {
            title: 'Relatórios do grupo',
            no_reports: 'Nenhum reports encontrado',
            generate_report: 'Gerar novo relatório',
            report_type_compliance: 'Resumo de conformidade',
            report_type_risk: 'Visão geral dos riscos',
            report_type_sla: 'SLA Desempenho',
            report_type_incident: 'Relatório de incidente',
            report_type_executive: 'Resumo Executivo',
            report_type_board: 'Relatório do Conselho',
            status_queued: 'Na fila',
            status_generating: 'Gerando',
            status_completed: 'Concluído',
            status_failed: 'Falha',
            period_start: 'Início do período',
            period_end: 'Fim do período',
            sr_loading: 'Carregando relatórios de grupo',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Fornecedores partilhados',
            no_vendors: 'Nenhum shared vendors encontrado',
            merged_vendors: 'Fornecedores fundidos',
            publish_form_label: 'Publicar fornecedor compartilhado',
            sr_loading: 'Carregando fornecedores compartilhados',
            sr_loading_merged: 'Carregando fornecedores mesclados',
            local_vendors: 'Fornecedores locais',
            source_shared: 'Compartilhado do grupo',
            source_local: 'Fornecedor local',
            category_technology: 'Tecnologia',
            category_consulting: 'Consultoria',
            category_outsourcing: 'Terceirização',
            category_cloud: 'Nuvem',
            category_data_processing: 'Processamento de Dados',
            category_other: 'Outro',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Médio',
            risk_tier_high: 'Alto',
            risk_tier_critical: 'Crítico',
            compliance_compliant: 'Compatível',
            compliance_non_compliant: 'Não conforme',
            compliance_pending: 'Pendente',
            compliance_under_review: 'Em revisão',
            status_active: 'Ativo',
            status_retired: 'Aposentado',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Código da estrutura',
            title: 'Quadros partilhados',
            no_frameworks: 'Nenhum shared frameworks encontrado',
            publish_form_label: 'Publicar estrutura compartilhada',
            sr_loading: 'Carregando estruturas compartilhadas',
            status_active: 'Ativo',
            status_retired: 'Aposentado',
        },
        shared_forms: {
            title: 'Modelos de formulários partilhados',
            no_forms: 'Nenhum shared form templates encontrado',
            publish_form_label: 'Publicar modelo de formulário compartilhado',
            sr_loading: 'Carregando modelos de formulário compartilhados',
            status_active: 'Ativo',
            status_retired: 'Aposentado',
        },
        audit: {
            title: 'Auditoria do grupo',
            consolidated_title: 'Trilha de auditoria consolidada',
            consolidated_subtitle: 'Eventos de auditoria entre organizações para este grupo',
            audit_log_title: 'Grupo Auditoria Registo',
            event_type: 'Evento Tipo',
            actor: 'Ator',
            timestamp: 'Marca temporal',
            source_org: 'Org. de origem',
            org: 'Org',
            summary: 'Resumo',
            details: 'Detalhes',
            no_events: 'Nenhum audit events encontrado',
            all_events: 'Todos Eventos',
            all_orgs: 'Todas as org.',
            organization: 'Organização',
            from: 'De',
            to: 'To',
            search_events: 'Pesquisar eventos...',
            clear: 'Limpar',
            events_24h: 'Eventos (24h)',
            events_7d: 'Eventos (7d)',
            active_orgs: 'Org. ativas',
            exporting: 'A exportar...',
            export_csv: 'Exportar CSV',
            export_json: 'Exportar JSON',
            back_to_group: 'Voltar para Grupo',
            back_to_detail: 'Voltar ao detalhe',
            sr_loading: 'Carregando eventos de auditoria',
            sr_loading_summary: 'Carregando resumo de auditoria',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Grupo Definições',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Herança de definições',
            inheritance_subtitle: 'Configurar quais categorias de configurações serão herdadas do grupo em vez de usar substituições locais',
            sla_settings: 'SLA Definições',
            calendar_settings: 'Calendário Definições',
            notification_settings: 'Notificação Definições',
            intake_settings: 'Definições de entrada',
            cmp_integration: 'Integração CMP',
            overridden: 'Substituído',
            inheriting: 'A herdar',
            hide_defaults: 'Ocultar predefinições',
            show_group_defaults: 'Mostrar Grupo Defaults',
            group_defaults: 'Predefinições do grupo',
            inherit_from_group: 'Inherit de Grupo',
            use_local_override: 'Usar substituição local',
            local_override_json: 'Substituição local (JSON)',
            save_override: 'Guardar substituição',
            saving: 'A guardar...',
            sr_loading: 'Carregando herança de configurações',
            sr_loading_settings: 'Carregando configurações do grupo',
            sr_enabled: 'Habilitado',
            sr_disabled: 'Desativado',
            sr_override_active: 'Substituição local ativa',
            sr_inherited: 'Herdado do pai',
            danger_zone: 'Zona de perigo',
            sharing_options: 'Opções de compartilhamento',
            advanced_options: 'Opções Avançadas',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Ativo',
        status_dissolved: 'Dissolvido',
        status_pending: 'Pendente',
        status_suspended: 'Suspenso',
    },
    integrations: {
        nav: {
            webhooks: 'Webhooks',
            domains: 'Domínios',
            export_schedules: 'Agendamento de Exportações',
            email_log: 'Registo de Correio Eletrónico',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Utilizadores',
            roles: 'Funções',
            profile: 'O Meu Perfil',
        },
        security: {
            mfa: 'Definições de MFA',
            sso: 'Configuração de SSO',
            api_keys: 'Chaves de API',
            service_accounts: 'Contas de Serviço',
            ip_allowlist: 'Lista de IPs Permitidos',
            delegations: 'Delegações',
            break_glass: 'Acesso de Emergência',
            session_settings: 'Definições de Sessão',
            active_sessions: 'Sessões Ativas',
            access_reviews: 'Revisões de Acesso',
            my_reviews: 'As Minhas Revisões',
            role_analytics: 'Análise de Funções',
            attestation_schedules: 'Agendamento de Atestações',
            sod_rules: 'Regras de SoD',
            sod_violations: 'Violações de SoD',
        },
        module_config: {
            title: 'Configuração de Módulos',
            subtitle: 'Ative ou desative módulos de funcionalidades para a sua organização. Os módulos desativados ficam ocultos na navegação.',
            enabled: 'Ativado',
            disabled: 'Desativado',
            save: 'Guardar Configuração',
            saving: 'A guardar...',
            saved: 'Configuração guardada com sucesso',
            nav_label: 'Configuração de Módulos',
            settings: 'Definições',
            webhooks: 'Webhooks',
            domains: 'Domínios',
            export_schedules: 'Agendamento de Exportações',
            email_log: 'Registo de Correio Eletrónico',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Arquivos',
            configuration: 'Configuração',
            browser: 'Navegador de arquivos',
            dashboard: 'Painel de arquivos',
        },
        immutable_audit_trails: {
            nav_group_label: 'Auditoria e Prova',
            nav_settings: 'Configurações',
            nav_status: 'Status da prova',
            nav_integrity_center: 'Centro de Integridade',
            nav_usage: 'Uso e créditos',
            settings_title: 'Trilhas de auditoria imutáveis — Configurações',
            settings_subtitle: 'Configurações por organização para o complemento Immutable Audit Trails.',
            settings_banner: 'O complemento Immutable Audit Trails não está habilitado para sua organização. Ative-o aqui para começar a ancorar provas para eventos de auditoria de Nível A e Nível B.',
            status_title: 'Trilhas de auditoria imutáveis – status de prova',
            status_subtitle: 'Relacionamentos de prova por evento entre módulos.',
            status_banner: 'O complemento Immutable Audit Trails não está habilitado para sua organização. Visite Configurações para habilitá-lo.',
            integrity_center_title: 'Trilhas de auditoria imutáveis — Centro de Integridade',
            integrity_center_subtitle: 'Verifique as provas e exporte pacotes de integridade.',
            integrity_center_banner: 'O complemento Immutable Audit Trails não está habilitado para sua organização. Visite Configurações para habilitá-lo antes de executar a verificação ou exportar pacotes de integridade.',
            usage_title: 'Trilhas de auditoria imutáveis – uso e créditos',
            usage_subtitle: 'Saldo de crédito, compras de pacotes e histórico de uso.',
            usage_banner: 'Ative trilhas de auditoria imutáveis nas configurações para começar a consumir créditos de prova.',
            module_tips_placeholder: 'Dicas e orientações para esta superfície estão disponíveis na Central de Ajuda.',
            column_label: 'Prova',
            no_proof_aria: 'Nenhuma prova registrada',
            proof_status_receipt_label: 'recibo {id}',
            in_progress_hint: 'Em andamento — atualiza automaticamente',
            status_pills: {
                pending: 'Pendente',
                submitted: 'Enviado',
                anchored: 'Ancorado',
                confirmed: 'Confirmado',
                retrying: 'Tentando novamente',
                paused_cap: 'Pausado (limite)',
                failed: 'Falha',
                failed_auth: 'Falha na autenticação',
            },
            status_tooltips: {
                pending: 'Pendente — na fila para o serviço de prova. Passará para Enviado em breve; nenhuma ação necessária.',
                submitted: 'Enviado — seu evento foi enviado para o serviço de prova. Para o modo batch, ele está aguardando para ser selado e ancorado na corrente. Atualizações automaticamente; nenhuma ação necessária.',
                anchored: 'Ancorado – o lote foi escrito na cadeia e aguarda confirmação final. Atualizações automaticamente; nenhuma ação necessária.',
                confirmed: 'Confirmado – a prova está ancorada na cadeia e verificável.',
                retrying: 'Tentando novamente — ocorreu um erro temporário. O reconciliador está tentando novamente automaticamente; nenhuma ação necessária.',
                paused_cap: 'Pausado — seu limite mensal de comprovação de crédito foi atingido. Resgate mais créditos ou espere até o próximo mês de cobrança.',
                failed: 'Falha — a ancoragem não foi bem-sucedida. O reconciliador tentará novamente automaticamente; entre em contato com o suporte se persistir.',
                failed_auth: 'Falha na autenticação — o serviço de prova rejeitou nossas credenciais. Verifique a página Configurações de trilhas de auditoria imutáveis.',
            },
            mode_pills: {
                direct: 'Direto',
                batch: 'Lote',
            },
            drawer: {
                title: 'Detalhes da prova',
                close: 'Fechar',
                section_proof: 'Prova',
                section_batch: 'Associação em lote',
                status: 'Estado',
                proof_reference: 'Referência de prova',
                receipt_id: 'ID do recibo',
                taxonomy_class: 'Aula de taxonomia',
                units_consumed: 'Unidades consumidas',
                created_at: 'Criado',
                verification_ref: 'Referência de verificação de cadeia',
                chain: 'Corrente',
                tx_hash: 'Hash Tx',
                block_ref: 'Bloquear referência',
                batch_reference: 'Referência de lote',
                leaf_index: 'Índice de folhas',
                local_event_hash: 'Hash de evento local',
                show_inclusion: 'Mostrar caminho de inclusão',
                hide_inclusion: 'Ocultar caminho de inclusão',
                privacy_note: 'A carga útil está com hash; nenhum dado pessoal foi deixado pela ComplianceOne para este evento.',
                verify_now: 'Verifique agora',
                verify_pending: 'Verificação em andamento…',
                verify_verified: 'Verificado – verificações locais + do lado da cadeia aprovadas.',
                verify_issue_prefix: 'Problema de verificação: ',
                verify_failed: 'Falha na chamada de verificação.',
                verify_result_prefix: 'Veredicto de verificação: ',
                verify_pending_note: 'Verificação pendente: esta prova ainda aguarda ancoragem na cadeia. Tente novamente depois que o lote estiver selado.',
                awaiting_anchor_note: 'Esta prova faz parte de um lote aberto. A verificação fica disponível quando o lote é selado e ancorado na cadeia — normalmente dentro da janela de lote configurada pela organização. O selo de status será atualizado automaticamente.',
                terminal_error_note: 'Esta prova não conseguiu ancorar. O reconciliador de status de prova tentará novamente automaticamente; a verificação manual ainda não tem nada para verificar.',
            },
            integrity_center: {
                section_run: 'Executar verificação',
                section_progress: 'Execute o progresso',
                section_issues: 'Problemas',
                period_start: 'Início do período',
                period_end: 'Fim do período',
                module_filter: 'Módulos (separados por vírgula, opcionais)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Executar verificação',
                export_button: 'Pacote de integridade de exportação',
                export_success: 'Pacote de integridade exportado.',
                export_failed: 'Falha na exportação.',
                empty_disabled: 'Ative trilhas de auditoria imutáveis nas configurações para executar passagens de verificação.',
                error_period_required: 'Escolha uma data de início e de término.',
                error_start_run: 'Falha ao iniciar a execução.',
                processed: 'Processado: {processed} de {total}',
                col_verdict: 'Veredicto',
                col_count: 'Contar',
                col_proof_link: 'Link de prova',
                col_module: 'Módulo',
                col_event_type: 'Tipo de evento',
                col_expected: 'Hash esperado',
                col_recomputed: 'Hash recalculado',
                col_raised_at: 'Criado em',
                any_verdict: 'Qualquer veredicto',
                no_issues: 'Nenhum problema encontrado.',
            },
            settings_page: {
                load_error: 'Falha ao carregar as configurações.',
                save: 'Salvar',
                save_success: 'Configurações salvas.',
                save_error: 'Falha ao salvar.',
                discard: 'Descartar alterações',
                read_only_banner: 'Você tem acesso somente leitura. Entre em contato com um administrador da plataforma para fazer alterações.',
                section_status: 'Status e ativação',
                enabled_label: 'Complemento ativado',
                enabled_help: 'Quando ativado, cada evento de auditoria de Camada A e Camada B é criptografado e ancorado.',
                section_mode: 'Modo de prova padrão',
                mode_direct: 'Direto (uma âncora por evento)',
                mode_batch: 'Lote (âncora Merkle-root por grupo)',
                mode_hybrid: 'Híbrido (padrão de registro por classe)',
                batch_window_label: 'Janela de lote (minutos, 1–60)',
                section_taxonomy: 'Taxonomia de eventos protegidos',
                taxonomy_help: 'Substituições de classe por evento. Camada A → modo direto, Camada B → modo em lote, Desligado → não verificado.',
                taxonomy_search: 'Substituições de pesquisa',
                taxonomy_reset: 'Redefinir para os padrões',
                taxonomy_event_class: 'Classe de evento',
                taxonomy_override: 'Substituir',
                taxonomy_actions: 'Ações',
                taxonomy_clear: 'Limpar',
                taxonomy_no_overrides: 'Sem substituições – usando padrões de registro.',
                taxonomy_add_class: 'event_class.código',
                taxonomy_add: 'Adicionar substituição',
                section_caps: 'Limites e limites',
                cap_label: 'Limite mensal (unidades; em branco = ilimitado)',
                warn_label: 'Limite de aviso (unidades; deve ser menor que o limite)',
                auto_pause_label: 'Pausar automaticamente novas provas quando o limite for atingido',
                section_credentials: 'Credenciais',
                credential_id: 'ID da credencial',
                credential_rotated_at: 'Última rotação',
                rotate_button: 'Girar credencial',
                rotate_success: 'Credencial rotacionada. Credencial antiga válida por mais {n} minutos.',
                rotate_failed: 'Falha na rotação de credenciais.',
                rotate_modal_title: 'Alternar credencial AesirX?',
                rotate_modal_body: 'A rotação emite uma nova credencial. A credencial antiga permanece aceita por AesirX por um período de carência de 60 minutos para que os trabalhadores a bordo possam terminar seus trabalhos atuais. Após 60 minutos, a credencial antiga é revogada.',
                rotate_modal_confirm: 'Gire agora',
                tier_a_off_modal_title: 'Desativar a revisão para uma classe de evento?',
                tier_a_off_modal_body: 'Definir uma classe de evento como "Desativada" impede que o toque de prova ancore esses eventos. A arquitetura §6 explica a implicação regulatória: os eventos de nível A são relevantes para os reguladores; desativá-los pode prejudicar a defensabilidade da trilha de auditoria.',
                tier_a_off_modal_confirm: 'Eu entendo – aplicar substituição',
                error_warn_lt_cap: 'O limite de aviso deve ser inferior ao limite mensal.',
                error_window_range: 'A janela do lote deve estar entre 1 e 60 minutos.',
                error_run_in_progress: 'Uma execução de verificação está em andamento; não é possível desativar o complemento agora.',
            },
            status_page: {
                filter_status: 'Estado',
                filter_mode: 'Modo de prova',
                filter_module: 'Módulo',
                filter_source_collection: 'Coleção de fontes',
                filter_from_date: 'De',
                filter_to_date: 'Para',
                filter_search: 'Pesquisar',
                any_status: 'Qualquer status',
                any_mode: 'Qualquer modo',
                col_module: 'Módulo',
                col_event_type: 'Tipo de evento',
                col_proof_status: 'Estado',
                col_proof_mode: 'Modo',
                col_created_at: 'Criado',
                col_units: 'Unidades',
                empty_disabled: 'O complemento Immutable Audit Trails não está habilitado para sua organização. Visite Configurações para habilitá-lo.',
                empty_enabled_no_rows: 'Nenhum link de prova corresponde aos filtros atuais.',
                export_csv: 'Exportar CSV',
                export_xlsx: 'Exportar XLSX',
                export_success: 'Exportação concluída',
                export_failed: 'Falha na exportação',
            },
            usage_page: {
                section_alerts: 'Alertas',
                alert_dismiss: 'Dispensar',
                alert_dismiss_aria: 'Dispensar alerta {id}',
                alert_dismissed: 'Alerta dispensado',
                alert_dismiss_failed: 'Falha ao descartar alerta',
                section_balance: 'Saldo de crédito',
                balance_label: 'Créditos disponíveis',
                balance_hint: 'Unidades restantes (1 unidade = 1 prova de evento protegido).',
                section_current_month: 'Mês atual',
                consumed_label: 'Consumido',
                granted_label: 'Concedido',
                refunded_label: 'Reembolsado',
                net_label: 'Rede',
                section_history: 'Histórico de uso',
                history_kind: 'Gentil',
                history_units: 'Unidades',
                history_balance_after: 'Saldo depois',
                history_notes: 'Notas',
                history_at: 'Quando',
                cap_warning_banner: 'Você ultrapassou o limite de aviso para este mês de faturamento. Considere resgatar créditos adicionais.',
                cap_paused_banner: 'Limite mensal atingido. Os envios de provas serão pausados ​​até o próximo mês ou até que o limite seja aumentado.',
                section_redeem: 'Resgatar pacote de crédito',
                redeem_purchase_id: 'Número da fatura',
                redeem_package: 'Pacote',
                redeem_button: 'Resgatar',
                redeem_pending: 'Resgatando…',
                redeem_success: 'Créditos resgatados',
                redeem_failed: 'Falha ao resgatar',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'unidades',
                alert_dismissing: 'Dispensando…',
                alert_kind: {
                    cap_reached: 'Limite mensal atingido',
                    low_balance: 'Saldo baixo',
                    auth_failure: 'Falha de autenticação',
                    merkle_mismatch: 'Incompatibilidade de Merkle',
                    batch_terminal_failure: 'Falha no terminal em lote',
                    direct_terminal_failure: 'Falha no terminal de envio direto',
                },
                usage_kind: {
                    consume: 'Consumir',
                    refund: 'Reembolso',
                    grant: 'Conceder',
                    adjustment: 'Ajuste',
                    reservation: 'Reserva',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Iniciante',
                package_growth: 'Crescimento',
                package_enterprise: 'Empresa',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Comprar mais créditos',
                    purchase_title: 'Comprar mais créditos',
                    purchase_subtitle: 'Escolha um pacote. Será encaminhado para o checkout seguro da Stripe e a sua fatura chegará por e-mail.',
                    checkout_button: 'Continuar para o checkout',
                    checkout_pending: 'A abrir o checkout…',
                    cancel_button: 'Cancelar',
                    purchase_error: 'Não foi possível iniciar o checkout. Tente novamente em breve.',
                    redeem_subtitle: 'Já pagou? Selecione o pacote que adquiriu e introduza o ID da fatura do seu e-mail para adicionar os créditos.',
                    error_not_found: 'Não foi encontrada nenhuma fatura com esse número. Verifique o número da fatura e tente novamente.',
                    error_not_paid: 'Esta fatura ainda não está marcada como paga.',
                    error_org_mismatch: 'Esta fatura não foi emitida para a sua organização.',
                    error_package_mismatch: 'Esta fatura não corresponde ao pacote selecionado.',
                    error_billing_unavailable: 'Não foi possível contactar o serviço de faturação. Tente novamente em breve.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Configuração de arquivos',
            subtitle: 'Configure limites de upload de arquivos, políticas de retenção e substituições por tipo de proprietário para a superfície de anexos da plataforma.',
            section_global: 'Padrões globais',
            section_global_help: 'Esses limites se aplicam a todos os arquivos enviados por usuários na sua organização, a menos que uma substituição por tipo de proprietário abaixo os altere.',
            section_overrides: 'Substituições por tipo de proprietário',
            section_overrides_help: 'Substitua os limites globais para um tipo de proprietário específico. Os valores do tipo por proprietário sempre vencem a configuração global.',
            max_file_size_label: 'Tamanho máximo do arquivo',
            max_file_size_hint: 'Por upload individual (MiB)',
            max_total_attachments_label: 'Total máximo de anexos',
            max_total_attachments_hint: 'Em toda a organização',
            max_total_size_label: 'Armazenamento total máximo',
            max_total_size_hint: 'Em toda a organização (MiB)',
            allowed_mime_types_label: 'Tipos de arquivos permitidos',
            allowed_mime_types_placeholder: 'Selecione os tipos MIME',
            retention_days_label: 'Período de retenção (dias)',
            retention_days_hint: 'Por quanto tempo os arquivos devem ser mantidos antes de serem elegíveis para exclusão. Deixe em branco para nenhuma política de retenção.',
            legal_hold_label: 'Retenção legal',
            legal_hold_hint: 'Quando ativado, a exclusão irreversível de um arquivo desse tipo de proprietário requer a permissão de substituição de retenção legal e a aceitação explícita do operador.',
            save: 'Salvar alterações',
            saving: 'Salvando…',
            reset: 'Redefinir',
            save_success: 'Configurações salvas',
            empty_state_title: 'Usando padrões de plataforma',
            empty_state_body: 'Sua organização está atualmente executando a política de arquivo padrão da plataforma. Edite qualquer valor abaixo para criar uma substituição no nível da organização.',
            read_only_banner: 'Você tem acesso somente leitura à configuração do arquivo. Entre em contato com um administrador da plataforma para fazer alterações.',
            owner_type_default_badge: 'Padrão',
            owner_type_overridden_badge: 'Substituído',
            owner_type_label: 'Tipo de proprietário',
            owner_type_collapse: 'Recolher',
            owner_type_expand: 'Expandir',
            override_save: 'Salvar substituição',
            override_remove: 'Remover substituição',
            mime_labels: {
                'application/pdf': 'PDF documento',
                'image/png': 'Imagem PNG',
                'image/jpeg': 'Imagem JPEG',
                'image/jpg': 'Imagem JPG',
                'application/msword': 'Documento Word (legado)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Documento do Word',
                'application/vnd.ms-excel': 'Planilha Excel (herdada)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Planilha Excel',
                'text/csv': 'CSV arquivo',
                'text/plain': 'Texto simples',
                'message/rfc822': 'Mensagem de e-mail',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Navegador de arquivos',
            subtitle: 'Pesquise, filtre e gerencie todos os anexos carregados na organização.',
            filter_owner_type: 'Tipo de proprietário',
            filter_uploader: 'Enviado por',
            filter_from: 'Carregado depois',
            filter_to: 'Enviado antes',
            filter_size_min: 'Tamanho mínimo (MiB)',
            filter_size_max: 'Tamanho máximo (MiB)',
            filter_mime: 'Tipo de arquivo',
            filter_deleted_state: 'Estado de exclusão',
            filter_deleted_any: 'Todos os arquivos',
            filter_deleted_only: 'Somente excluído',
            filter_deleted_exclude: 'Excluir excluído',
            filter_orphaned: 'Apenas órfão',
            filter_search_placeholder: 'Pesquisar nome do arquivo…',
            filter_apply: 'Aplicar filtros',
            filter_clear: 'Limpar filtros',
            col_filename: 'Nome do arquivo',
            col_owner_type: 'Tipo de proprietário',
            col_owner_entity: 'Proprietário',
            col_uploader: 'Enviado por',
            col_uploaded_at: 'Enviado',
            col_size: 'Tamanho',
            col_mime: 'Tipo',
            col_state: 'Estado',
            col_actions: 'Ações',
            state_active: 'Ativo',
            state_deleted: 'Excluído',
            state_orphaned: 'Órfão',
            action_view_detail: 'Ver detalhes',
            action_download: 'Baixar',
            action_ai_edits: 'AI Edições',
            action_soft_delete: 'Exclusão suave',
            action_restore: 'Restaurar',
            action_hard_delete: 'Exclusão forçada',
            bulk_soft_delete: 'Exclusão reversível selecionada',
            bulk_export_csv: 'Exportar CSV',
            bulk_select_all: 'Selecionar tudo',
            reason_label: 'Razão',
            reason_min_10_chars: 'O motivo é obrigatório (pelo menos 10 caracteres)',
            reason_min_20_chars: 'O motivo é obrigatório (pelo menos 20 caracteres)',
            legal_hold_override_label: 'Substituir retenção legal',
            legal_hold_override_warning: 'Este arquivo está sob retenção legal. A exclusão forçada será registrada na trilha de auditoria com seu motivo e sinalizador de substituição.',
            empty_state_title: 'Nenhum arquivo corresponde aos seus filtros',
            empty_state_body: 'Ajuste seus filtros para ver os resultados ou desmarque-os para visualizar todos os arquivos.',
            pagination_prev: 'Anterior',
            pagination_next: 'Próximo',
            pagination_page: 'Página {page} de {total}',
            pagination_size_label: 'Por página',
            pagination_total: 'Total: {count}',
            toast_soft_deleted: 'Arquivo excluído de forma reversível',
            toast_restored: 'Arquivo restaurado',
            toast_hard_deleted: 'Arquivo excluído permanentemente',
            toast_bulk_deleted: '{count} arquivo(s) excluído(s)',
            toast_error: 'Falha na operação',
            confirm_soft_delete_title: 'Arquivo de exclusão reversível',
            confirm_restore_title: 'Restaurar arquivo',
            confirm_hard_delete_title: 'Excluir arquivo permanentemente',
            confirm_bulk_soft_delete_title: 'Excluir arquivos selecionados de forma reversível',
            confirm_submit: 'Confirmar',
            confirm_cancel: 'Cancelar',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Detalhes do arquivo',
            close: 'Fechar',
            meta_filename: 'Nome do arquivo',
            meta_size: 'Tamanho',
            meta_mime: 'Tipo',
            meta_owner_type: 'Tipo de proprietário',
            meta_owner_entity: 'Entidade proprietária',
            meta_uploader: 'Enviado por',
            meta_uploaded_at: 'Enviado',
            meta_storage_path: 'Caminho de armazenamento',
            meta_deleted_state: 'Estado de exclusão',
            audit_section_title: 'Trilha de auditoria',
            audit_load_more: 'Carregar mais eventos',
            audit_empty: 'Nenhum evento de auditoria registrado para este arquivo.',
            audit_config_changed: 'Configuração alterada',
            audit_soft_deleted: 'Excluído de forma reversível',
            audit_restored: 'Restaurado',
            audit_hard_deleted: 'Excluído permanentemente',
            audit_downloaded: 'Baixado',
            audit_quota_breached: 'Tentativa de violação de cota',
            audit_owner_type_download_denied: 'Obtenção recusada',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} por {actor} em {at}',
            action_download: 'Baixar',
            action_soft_delete: 'Exclusão suave',
            action_restore: 'Restaurar',
            action_hard_delete: 'Exclusão forçada',
            effective_settings_title: 'Política eficaz',
            effective_max_file_size: 'Tamanho máximo do arquivo',
            effective_retention_days: 'Retenção',
            effective_legal_hold: 'Retenção legal',
            effective_legal_hold_yes: 'Ativo',
            effective_legal_hold_no: 'Nenhum',
        },
        files_dashboard: {
            title: 'Painel de arquivos',
            subtitle: 'Uso de armazenamento, crescimento e arquivos órfãos em toda a organização.',
            kpi_total_files: 'Total de arquivos',
            kpi_total_size: 'Armazenamento total',
            kpi_near_quota: 'Uso de cota',
            kpi_orphaned: 'Arquivos órfãos',
            chart_by_owner_type: 'Arquivos por tipo de proprietário',
            chart_growth_30d: 'Crescimento em 30 dias',
            toggle_count: 'Por contagem',
            toggle_size: 'Por tamanho',
            orphaned_panel_title: 'Arquivos órfãos (20 principais)',
            orphaned_view_all: 'Ver todos os arquivos órfãos',
            orphaned_empty: 'Nenhum arquivo órfão encontrado.',
            growth_empty: 'Nenhuma atividade de upload nos últimos 30 dias.',
            retention_n_days: '{days} dia(s)',
            retention_none: 'Nenhuma política',
        },
    },
    category_labels: {
        'user-guide': 'Guia do usuário',
        'admin-guide': 'Guia de administração',
        'getting-started': 'Primeiros passos',
        faq: 'Perguntas frequentes',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Chaves',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Administrador',
        aesirx: 'AesirX',
        purposes: 'Finalidades',
        preferences: 'Preferências',
        onboarding: 'Integração',
        'first-steps': 'Primeiros passos',
        login: 'Entrar',
        navigation: 'Navegação',
        users: 'Usuários',
        user: 'Usuário',
        roles: 'Funções',
        role: 'Função',
        'access-control': 'Controle de acesso',
        'access-management': 'Gerenciamento de acesso',
        permissions: 'Permissões',
        permission: 'Permissão',
        consent: 'Consentimento',
        'consent-management': 'Gerenciamento de consentimento',
        privacy: 'Privacidade',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Auditoria',
        auditing: 'Auditoria',
        breach: 'Violação',
        incident: 'Incidente',
        incidents: 'Incidentes',
        notification: 'Notificação',
        notifications: 'Notificações',
        reporting: 'Relatórios',
        risk: 'Risco',
        vendor: 'Fornecedor',
        vendors: 'Fornecedores',
        'vendor-risk': 'Risco do fornecedor',
        assessment: 'Avaliação',
        assessments: 'Avaliações',
        dpia: 'DPIA',
        'cross-border': 'Transfronteiriço',
        transfer: 'Transferência',
        transfers: 'Transferências',
        deletion: 'Exclusão',
        retention: 'Retenção',
        'data-mapping': 'Mapeamento de dados',
        discovery: 'Descoberta',
        classification: 'Classificação',
        workflow: 'Fluxo de trabalho',
        workflows: 'Fluxos de trabalho',
        template: 'Modelo',
        templates: 'Modelos',
        catalog: 'Catálogo',
        monitoring: 'Monitoramento',
        tasks: 'Tarefas',
        task: 'Tarefa',
        'task-management': 'Gerenciamento de tarefas',
        organizations: 'Organizações',
        org: 'Organização',
        governance: 'Governança',
        compliance: 'Conformidade',
        forms: 'Formulários',
        form: 'Formulário',
        connectors: 'Conectores',
        connector: 'Conector',
        integration: 'Integração',
        integrations: 'Integrações',
        security: 'Segurança',
        encryption: 'Criptografia',
        backup: 'Cópia de segurança',
        recovery: 'Recuperação',
        maintenance: 'Manutenção',
        settings: 'Configurações',
        configuration: 'Configuração',
        sla: 'SLA',
        escalation: 'Escalada',
        remediation: 'Correção',
        evidence: 'Evidência',
        'case-management': 'Gerenciamento de Casos',
        profile: 'Perfil',
        delegation: 'Delegação',
        'service-accounts': 'Contas de serviço',
        license: 'Licença',
        'license-management': 'Gerenciamento de licenças',
        plan: 'Plano',
        plans: 'Planos',
        dashboard: 'Painel',
        'web-consent': 'Consentimento da Web',
        'partner-marketplace': 'Mercado de parceiros',
        partner: 'Parceiro',
        partners: 'Parceiros',
        deliverables: 'Entregáveis',
        'service-tiers': 'Níveis de serviço',
        'support-tickets': 'Tíquetes de suporte',
        'audit-trail': 'Trilha de auditoria',
        'first-time-setup': 'Configuração inicial',
        setup: 'Configuração',
        quickstart: 'Início rápido',
        tour: 'Passeio',
        tours: 'Passeios',
        'sample-data': 'Dados de amostra',
        'data-subject': 'Titular dos dados',
        'data-subjects': 'Titulares dos dados',
        pseudonymous: 'Pseudônimo',
        identified: 'Identificado',
        'help-center': 'Central de Ajuda',
        article: 'Artigo',
        articles: 'Artigos',
        search: 'Pesquisar',
        'platform-settings': 'Configurações da plataforma',
        'platform-config': 'Configuração da plataforma',
        'module-config': 'Configuração do módulo',
        'module-configuration': 'Configuração do módulo',
        'rights-requests': 'Solicitações de direitos',
        'access-accountability': 'Responsabilidade de acesso',
        'data-discovery': 'Descoberta de dados',
        'incident-ops': 'Operações de Incidentes',
        'deletion-orchestration': 'Orquestração de exclusão',
        'compliance-forms': 'Formulários de Conformidade',
        'program-governance': 'Governança do Programa',
        localization: 'Localização',
        'privacy-scanner': 'Verificador de privacidade',
        'consent-properties': 'Propriedades de consentimento',
        acceptances: 'Aceitações',
        access: 'Acesso',
        'access-review': 'Revisão de acesso',
        'access-reviews': 'Acessar avaliações',
        accountability: 'Responsabilidade',
        acknowledgment: 'Reconhecimento',
        'action-plans': 'Planos de Ação',
        activation: 'Ativação',
        activations: 'Ativações',
        'active-response': 'Resposta Ativa',
        activity: 'Atividade',
        adequacy: 'Adequação',
        'admin-guide': 'Guia de administração',
        aggregation: 'Agregação',
        agreements: 'Acordos',
        ai: 'AI',
        alerts: 'Alertas',
        analytics: 'Análise',
        anchoring: 'Ancoragem',
        approval: 'Aprovação',
        approvals: 'Aprovações',
        apps: 'Aplicativos',
        assets: 'Ativos',
        assignments: 'Tarefas',
        attestation: 'Atestado',
        'audit-log': 'Registro de auditoria',
        'audit-pack': 'Pacote de auditoria',
        authentication: 'Autenticação',
        authority: 'Autoridade',
        'auto-play': 'Reprodução automática',
        automated: 'Automatizado',
        automation: 'Automação',
        baseline: 'Linha de base',
        batches: 'Lotes',
        bidirectional: 'Bidirecional',
        bilingual: 'Bilíngue',
        bindings: 'Ligações',
        blockchain: 'Blockchain',
        board: 'Conselho',
        brand: 'Marca',
        brds: 'BRDs',
        'break-glass': 'Quebra-vidro',
        builder: 'Construtor',
        bulk: 'Massa',
        'bulk-sync': 'Sincronização em massa',
        bundles: 'Pacotes',
        calendar: 'Calendário',
        campaigns: 'Campanhas',
        cases: 'Casos',
        categories: 'Categorias',
        category: 'Categoria',
        certification: 'Certificação',
        chain: 'Corrente',
        change: 'Mudança',
        'change-awareness': 'Conscientização sobre a mudança',
        'change-log': 'Registro de alterações',
        'change-management': 'Gestão de Mudanças',
        'change-requests': 'Solicitações de mudança',
        'cmp-validation': 'CMP Validação',
        collection: 'Coleção',
        comparison: 'Comparação',
        completeness: 'Completude',
        completion: 'Conclusão',
        'compliance-posture': 'Postura de Conformidade',
        'compliance-proof': 'Prova de Conformidade',
        'compliance-reviews': 'Avaliações de conformidade',
        config: 'Configuração',
        confirmation: 'Confirmação',
        conflicts: 'Conflitos',
        consolidation: 'Consolidação',
        continuous: 'Contínuo',
        contracts: 'Contratos',
        controls: 'Controles',
        cookies: 'Biscoitos',
        cooperation: 'Cooperação',
        coordination: 'Coordenação',
        'corporate-governance': 'Governança Corporativa',
        'corrective-actions': 'Ações Corretivas',
        coverage: 'Cobertura',
        'credential-vault': 'Cofre de credenciais',
        credentials: 'Credenciais',
        cycles: 'Ciclos',
        dashboards: 'Painéis',
        'data-assets': 'Ativos de dados',
        'data-flows': 'Fluxos de dados',
        'data-sensitivity': 'Sensibilidade de dados',
        'data-steward': 'Administrador de dados',
        deadlines: 'Prazos',
        'decree-356': 'Decreto 356',
        dedup: 'Deduplicação',
        definitions: 'Definições',
        deliverable: 'Entregável',
        departments: 'Departamentos',
        'dependency-order': 'Ordem de Dependência',
        deployment: 'Implantação',
        detection: 'Detecção',
        diff: 'Diferença',
        'digital-properties': 'Propriedades Digitais',
        dimensions: 'Dimensões',
        disable: 'Desativar',
        'disaster-recovery': 'Recuperação de desastres',
        disclosure: 'Divulgação',
        documentation: 'Documentação',
        documents: 'Documentos',
        domains: 'Domínios',
        dossier: 'Dossiê',
        dpa: 'DPA',
        drift: 'Deriva',
        'drift-detection': 'Detecção de deriva',
        'due-diligence': 'Devida Diligência',
        email: 'Email',
        emergency: 'Emergência',
        enable: 'Habilitar',
        'enterprise-map': 'Mapa Empresarial',
        erasure: 'Apagamento',
        evaluation: 'Avaliação',
        'event-bus': 'Ônibus de eventos',
        exceptions: 'Exceções',
        execution: 'Execução',
        executive: 'Executivo',
        exercises: 'Exercícios',
        export: 'Exportar',
        exports: 'Exportações',
        filing: 'Arquivamento',
        filter: 'Filtro',
        filtering: 'Filtragem',
        findings: 'Descobertas',
        flows: 'Fluxos',
        'follow-up': 'Acompanhamento',
        forensic: 'Forense',
        forensics: 'Perícia',
        'form-filler': 'Preenchimento de formulário',
        framework: 'Estrutura',
        frameworks: 'Estruturas',
        'gap-analysis': 'Análise de lacunas',
        'gap-report': 'Relatório de lacunas',
        gaps: 'Lacunas',
        gates: 'Portões',
        'getting-started': 'Primeiros passos',
        'golden-templates': 'Modelos Dourados',
        grc: 'GRC',
        'group-management': 'Gestão de Grupo',
        groups: 'Grupos',
        'guided-tours': 'Visitas Guiadas',
        guides: 'Guias',
        handling: 'Manuseio',
        'hands-on': 'Prática',
        health: 'Saúde',
        help: 'Ajuda',
        hierarchy: 'Hierarquia',
        history: 'História',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Provedor de identidade',
        implementation: 'Implementação',
        import: 'Importar',
        improvement: 'Melhoria',
        'incident-response': 'Resposta a Incidentes',
        inconsistencies: 'Inconsistências',
        instances: 'Instâncias',
        intake: 'Ingestão',
        'intake-key': 'Chave de entrada',
        interactive: 'Interativo',
        international: 'Internacional',
        inventory: 'Inventário',
        investigation: 'Investigação',
        investigations: 'Investigações',
        'ip-allowlist': 'Lista de permissões de IP',
        issues: 'Problemas',
        justification: 'Justificativa',
        justifications: 'Justificativas',
        kanban: 'Kanban',
        keyboard: 'Teclado',
        keys: 'Chaves',
        'knowledge-base': 'Base de Conhecimento',
        koncernstyring: 'Governança Corporativa',
        kpi: 'KPI',
        labeling: 'Rotulagem',
        language: 'Idioma',
        'language-packs': 'Pacotes de idiomas',
        languages: 'Idiomas',
        lawfulness: 'Legalidade',
        layout: 'Disposição',
        'legal-basis': 'Base jurídica',
        'lessons-learned': 'Lições aprendidas',
        levels: 'Níveis',
        library: 'Biblioteca',
        lifecycle: 'Ciclo de vida',
        lineage: 'Linhagem',
        links: 'Ligações',
        listings: 'Listagens',
        live: 'Ao vivo',
        locale: 'Local',
        log: 'Log',
        logging: 'Registro',
        'managed-service': 'Serviço gerenciado',
        mapping: 'Mapeamento',
        mappings: 'Mapeamentos',
        'market-packs': 'Pacotes de Mercado',
        marketplace: 'Mercado',
        maturity: 'Maturidade',
        metrics: 'Métricas',
        models: 'Modelos',
        module: 'Módulo',
        modules: 'Módulos',
        mps: 'MPS',
        'multi-factor': 'Multifator',
        'multi-market': 'Multimercado',
        'multi-org': 'Multiorganização',
        'multi-property': 'Multipropriedade',
        'my-tasks': 'Minhas tarefas',
        obligations: 'Obrigações',
        offboarding: 'Desativação',
        oidc: 'OIDC',
        orchestration: 'Orquestração',
        'org-groups': 'Grupos organizacionais',
        organization: 'Organização',
        overdue: 'Atrasado',
        overview: 'Visão geral',
        'owner-review': 'Avaliação do proprietário',
        package: 'Pacote',
        packages: 'Pacotes',
        packs: 'Pacotes',
        pagination: 'Paginação',
        'parent-child': 'Pai-filho',
        patterns: 'Padrões',
        performance: 'Desempenho',
        periodic: 'Periódico',
        periods: 'Períodos',
        personal: 'Pessoal',
        'personal-data': 'Dados Pessoais',
        personas: 'Personagens',
        planning: 'Planejamento',
        platform: 'Plataforma',
        playbooks: 'Manuais',
        poc: 'PoC',
        'point-in-time': 'Ponto no tempo',
        policies: 'Políticas',
        portfolio: 'Portfólio',
        'post-incident': 'Pós-incidente',
        'power-user': 'Usuário avançado',
        practice: 'Pratique',
        pricing: 'Preços',
        'privacy-impact': 'Impacto na privacidade',
        procedures: 'Procedimentos',
        processes: 'Processos',
        processing: 'Processamento',
        'processing-activities': 'Atividades de processamento',
        procurement: 'Aquisições',
        productivity: 'Produtividade',
        profiles: 'Perfis',
        programs: 'Programas',
        progress: 'Progresso',
        proof: 'Prova',
        propagation: 'Propagação',
        properties: 'Propriedades',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Formulário Público',
        quality: 'Qualidade',
        questionnaires: 'Questionários',
        'quick-links': 'Links rápidos',
        'quick-start': 'Início rápido',
        'quick-starts': 'Inícios rápidos',
        raci: 'RACI',
        readiness: 'Prontidão',
        'real-time': 'Tempo real',
        reassessments: 'Reavaliações',
        reconciliation: 'Reconciliação',
        record: 'Gravar',
        records: 'Registros',
        recurring: 'Recorrente',
        register: 'Cadastre-se',
        registry: 'Registro',
        regulations: 'Regulamentos',
        regulator: 'Regulador',
        regulatory: 'Regulatório',
        'regulatory-frameworks': 'Marcos Regulatórios',
        remap: 'Remapear',
        reminders: 'Lembretes',
        reports: 'Relatórios',
        requests: 'Solicitações',
        requirements: 'Requisitos',
        response: 'Resposta',
        'response-plans': 'Planos de Resposta',
        'response-team': 'Equipe de Resposta',
        'response-templates': 'Modelos de resposta',
        'response-time': 'Tempo de resposta',
        responses: 'Respostas',
        responsibility: 'Responsabilidade',
        results: 'Resultados',
        review: 'Revisão',
        reviews: 'Avaliações',
        'right-to-delete': 'Direito de excluir',
        rights: 'Direitos',
        'risk-tiers': 'Níveis de risco',
        rollouts: 'Lançamentos',
        rollup: 'Acúmulo',
        ropa: 'ROPA',
        routing: 'Roteamento',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Regras',
        runs: 'Corre',
        saml: 'SAML',
        scan: 'Digitalizar',
        scanning: 'Digitalizando',
        scc: 'SCC',
        schedule: 'Cronograma',
        scheduled: 'Agendado',
        schedules: 'Horários',
        scheduling: 'Agendamento',
        scores: 'Pontuações',
        scoring: 'Pontuação',
        scripts: 'Roteiros',
        'security-reviews': 'Avaliações de segurança',
        'sensitive-data': 'Dados confidenciais',
        sensitivity: 'Sensibilidade',
        'separation-of-duties': 'Separação de Funções',
        service: 'Serviço',
        sessions: 'Sessões',
        'setup-wizard': 'Assistente de configuração',
        severity: 'Gravidade',
        'shared-governance': 'Governança Compartilhada',
        'shared-settings': 'Configurações compartilhadas',
        shortcuts: 'Atalhos',
        sidebar: 'Barra lateral',
        signals: 'Sinais',
        'single-sign-on': 'Logon único',
        snapshots: 'Instantâneos',
        sod: 'SoD',
        sorting: 'Classificando',
        sources: 'Fontes',
        staging: 'Encenação',
        standards: 'Padrões',
        status: 'Estado',
        structure: 'Estrutura',
        'sub-processor': 'Subprocessador',
        'sub-processors': 'Subprocessadores',
        submissions: 'Envios',
        subprocessors: 'Subprocessadores',
        subscription: 'Assinatura',
        suggestions: 'Sugestões',
        summaries: 'Resumos',
        supplements: 'Suplementos',
        support: 'Suporte',
        sync: 'Sincronizar',
        system: 'Sistema',
        systems: 'Sistemas',
        tables: 'Tabelas',
        tagging: 'Marcação',
        'tamper-proof': 'À prova de adulteração',
        taxonomies: 'Taxonomias',
        taxonomy: 'Taxonomia',
        tenants: 'Inquilinos',
        termination: 'Rescisão',
        'third-party': 'Terceiros',
        'ticket-sync': 'Sincronização de ingressos',
        tickets: 'Ingressos',
        tiers: 'Níveis',
        tokens: 'Fichas',
        totp: 'TOTP',
        traceability: 'Rastreabilidade',
        tracking: 'Rastreamento',
        trail: 'Trilha',
        training: 'Treinamento',
        translation: 'Tradução',
        translations: 'Traduções',
        transparency: 'Transparência',
        trends: 'Tendências',
        unified: 'Unificado',
        updates: 'Atualizações',
        validation: 'Validação',
        vault: 'Cofre',
        'vendor-requests': 'Solicitações de fornecedores',
        verification: 'Verificação',
        versioning: 'Versionamento',
        versions: 'Versões',
        vietnam: 'Vietnã',
        violations: 'Violações',
        visualization: 'Visualização',
        waivers: 'Isenções',
        'web-records': 'Registros da Web',
        website: 'Site',
        websites: 'Sites',
        weights: 'Pesos',
        'white-label': 'Etiqueta Branca',
        withdrawal: 'Retirada',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Pedidos de Direitos',
        data_mapping: 'Registo de Atividades de Tratamento',
        governance: 'Governação',
        consent: 'Governação de Consentimento',
        consent_properties: 'Propriedades de consentimento',
        assessments: 'AIPD e Avaliações',
        classification: 'Classificação de Dados',
        monitoring: 'Programas de Monitorização',
        program: 'Programa de Monitorização',
        incidents: 'Resposta a Incidentes',
        connectors: 'Conectores',
        vendor_risk: 'Risco do fornecedor',
        deletion_orchestration: 'Orquestração de exclusão',
        discovery: 'Descoberta de dados',
        incident_ops: 'Operações de incidentes',
        program_governance: 'Governança de programas',
        access_accountability: 'Responsabilidade de acesso',
        localization: 'Localização',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Espaço de trabalho de parceiros',
        privacy_scanner: 'Scanner de privacidade',
        onboarding: 'Integração',
        compliance_forms: 'Formulários de Conformidade',
        audit_trail: 'Trilha de Auditoria',
        data_subjects: 'Titulares dos Dados',
        organizations: 'Organizações',
        task_management: 'Gerenciamento de tarefas',
        deletion_orch: 'Orquestração de exclusão',
        forms: 'Formulários de conformidade',
        partner_marketplace: 'Mercado de parceiros',
        dashboard: 'Painel de controlo',
        platform_settings: 'Configurações da plataforma',
        help_center: 'Centro de Ajuda',
    },
    module_descriptions: {
        rights_requests: 'Gerir pedidos de direitos dos titulares de dados, incluindo acesso, eliminação e portabilidade. Acompanhar o ciclo de vida dos pedidos, atribuir responsáveis e cumprir prazos regulamentares.',
        data_mapping: 'Mapear fluxos de dados na sua organização. Acompanhar atividades de tratamento, categorias de dados, finalidades, políticas de retenção e integrações de sistemas.',
        governance: 'Supervisão e as evidências que a sustentam. Faça a gestão do registo de fornecedores e do seu ciclo de vida, aprove as decisões que este módulo controla, vigie os desvios de conformidade e mantenha os registos de confiança e identidade, de artefactos regulamentares, de pagamentos e eKYC, e de evidências da plataforma.',
        consent: 'Gerir a recolha, armazenamento e retirada de consentimento. Acompanhar registos de consentimento, finalidades e integração com plataformas de gestão de consentimento.',
        consent_properties: 'Configurar propriedades de consentimento, grupos e modelos de referência. Definir configurações de consentimento reutilizáveis para implementação consistente.',
        assessments: 'Realizar Avaliações de Impacto sobre a Proteção de Dados (AIPD) e avaliações de risco de privacidade. Avaliar atividades de tratamento quanto a riscos de conformidade.',
        classification: 'Classificar ativos de dados por nível de sensibilidade. Aplicar etiquetas de taxonomia, acompanhar registos do ciclo de vida e identificar lacunas de classificação.',
        monitoring: 'Configurar programas de monitorização para acompanhar métricas de conformidade, indicadores-chave de risco e eficácia dos controlos ao longo do tempo.',
        incidents: 'Reportar e gerir incidentes de privacidade e violações de dados. Acompanhar fluxos de trabalho de investigação, notificação e remediação.',
        connectors: 'Configurar conectores de fontes de dados e integrações. Ligar sistemas externos para descoberta automatizada de dados e monitorização.',
        vendor_risk: 'Avaliar e monitorizar riscos de fornecedores terceiros. Pontuar fornecedores, acompanhar mitigação de riscos e gerir configurações de risco de fornecedores.',
        deletion_orchestration: 'Orquestrar a eliminação de dados em todos os sistemas. Acompanhar pedidos de eliminação, verificar cadeias de prova e garantir a remoção completa dos dados.',
        discovery: 'Descobrir dados pessoais em sistemas ligados. Analisar fontes de dados para construir um inventário abrangente de dados.',
        incident_ops: 'Gestão operacional de incidentes com manuais de procedimentos, fluxos de trabalho de escalonamento e acompanhamento de SLA para resolução de incidentes.',
        program_governance: 'Acompanhar a maturidade global do programa de privacidade, pontuações de conformidade, lacunas de evidência e relatórios executivos em toda a organização.',
        access_accountability: 'Investigar e auditar padrões de acesso de utilizadores. Acompanhar configurações de acesso, monitorizar atividade suspeita e garantir a responsabilização.',
        localization: 'Instalar e gerir pacotes de localização de mercado. Adicionar quadros regulamentares, pacotes de idiomas e conteúdo de conformidade específico da região.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Colaborar com parceiros externos. Partilhar dados de conformidade, acompanhar obrigações de parceiros e gerir relações com parceiros.',
        privacy_scanner: 'Analisar websites quanto a problemas de conformidade de privacidade. Monitorizar a utilização de cookies, scripts de rastreamento e a presença de políticas de privacidade em tempo real.',
        onboarding: 'Orientar novas organizações na configuração da plataforma. Assistente de integração passo a passo para configuração inicial e importação de dados.',
        compliance_forms: 'Criar e gerir formulários de conformidade a partir de um catálogo de modelos. Preencher formulários regulamentares, acompanhar submissões e gerar relatórios.',
        audit_trail: 'Visualizar uma trilha de auditoria completa de todas as ações do sistema. Acompanhar quem fez o quê, quando e onde em todos os módulos.',
        organizations: 'Gerir organizações e definições multi-inquilino. Configurar perfis de organizações, hierarquias e relações entre organizações.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Explique anomalias',
            explaining: 'Explicando…',
            results_heading: 'Explicações de anomalias ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} itens',
            remediation_label: 'Etapas de correção',
            items_detail_label: 'Itens afetados',
            accept_label: 'Aceitar explicação',
            reject_label: 'Rejeitar explicação',
            error_generic: 'Não foi possível explicar as anomalias. Tente novamente ou verifique se a verificação foi concluída com sucesso.',
        },
        cookie_insight: {
            insight_label: 'Insight de cookie e rastreador AI',
            producing: 'Produzindo insights…',
            results_heading: 'Informações sobre cookies e rastreadores ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} itens',
            classification_label: 'Classificação',
            severity_label: 'Gravidade',
            banner_copy_label: 'Cópia de banner sugerida',
            retention_label: 'Conselhos sobre retenção',
            citations_label: 'Citações',
            kind_cookie: 'Biscoito',
            kind_tracker: 'Rastreador',
            class_necessary: 'Necessário',
            class_analytics: 'Análise',
            class_marketing: 'Comercialização',
            class_social: 'Sociais',
            accept_label: 'Aceitar insights',
            reject_label: 'Rejeitar insights',
            no_items: 'Nenhum cookie ou rastreador foi detectado nesta verificação.',
            error_generic: 'Não foi possível produzir o insight do cookie. Tente novamente ou verifique se a verificação foi concluída com sucesso.',
        },
        monitoring: {
            title: 'Monitorização de Privacidade',
            new_scan: 'Nova Análise',
            scan_url: 'URL a Analisar',
            scan_frequency: 'Frequência de Análise',
            last_scan: 'Última Análise',
            score: 'Pontuação de Privacidade',
            issues: 'Problemas Encontrados',
            history: 'Histórico de Análises',
            settings: 'Definições de Monitorização',
        },
        real_time: {
            title: 'Analisador em Tempo Real',
            scan_now: 'Analisar Agora',
            scanning: 'A analisar...',
            results: 'Resultados da Análise',
            no_results: 'Ainda sem resultados de análise.',
            cookies: 'Biscoitos',
            trackers: 'Rastreadores',
            consent_banners: 'Faixas de Consentimento',
            third_party: 'Scripts de Terceiros',
            archive: 'Arquivo de Resultados',
            settings: 'Definições do Analisador',
        },
        dashboard: {
            page_title: 'Painel de monitoramento de privacidade',
            privacy_monitoring: 'Monitoramento de privacidade',
            plan_label: '{package} plano',
            expires_label: 'expira {date}',
            settings: 'Configurações',
            refresh: 'Atualizar',
            refreshing_dashboard: 'Atualizando painel de monitoramento',
            refresh_aria: 'Atualizar painel de monitoramento',
            not_configured_title: 'Monitoramento de privacidade não configurado',
            not_configured_desc: 'Conecte seu serviço de monitoramento de privacidade AesirX para começar a monitorar a conformidade do site.',
            configure_monitoring: 'Configurar monitoramento',
            license_expired_title: 'Licença expirada',
            license_expired_desc: 'Sua licença de monitoramento de privacidade AesirX expirou. Por favor, renove-o para evitar interrupção do serviço.',
            compliance_score: 'Pontuação de Conformidade',
            cookies_detected: 'Cookies detectados',
            trackers_label: 'Rastreadores',
            open_issues: 'Problemas em aberto',
            score_good: 'Bom',
            score_needs_improvement: 'Precisa de melhorias',
            score_critical: 'Crítico',
            action_required: 'Ação necessária',
            cookies_sr: 'cookies detectados',
            trackers_sr: 'rastreadores encontrados',
            open_issues_sr: 'questões em aberto',
            action_required_sr: 'ação necessária',
            metric_label: 'Métrica',
            value_label: 'Valor',
            status_label: 'Estado',
            aria_metrics: 'Métricas de monitoramento de privacidade',
            plan_limit_warning: 'Verificando URL: {current}/{max} — você atingiu o limite do seu plano.',
            manage_domains: 'Gerencie domínios ou atualize seu plano.',
            monitored_domains: 'Domínios Monitorados',
            domain_added: 'Domínio {domain} adicionado',
            domain_removed: 'Domínio {domain} removido',
            failed_add_domain: 'Falha ao adicionar domínio',
            failed_remove_domain: 'Falha ao remover o domínio',
            failed_load: 'Falha ao carregar o painel de monitoramento',
            new_domain_aria: 'Novo domínio URL',
            add_domain: 'Adicionar domínio',
            adding_domain: 'Adicionando…',
            no_domains: 'Nenhum domínio monitorado configurado ainda.',
            aria_domains_table: 'Domínios monitorados',
            col_domain: 'Domínio',
            col_compliance_score: 'Pontuação de Conformidade',
            col_status: 'Estado',
            col_cookies: 'Biscoitos',
            col_trackers: 'Rastreadores',
            col_issues: 'Problemas',
            col_last_scan: 'Última verificação',
            col_action: 'Ação',
            compliance_status_sr: 'status de conformidade',
            showing_chart: 'Mostrando visualização de gráfico',
            showing_table: 'Mostrando visualização da tabela de dados',
            loading_sr: 'Carregando painel de monitoramento',
            col_framework_tags: 'Etiquetas de estrutura',
            col_last_monitored: 'Último monitorado',
            framework_tags_sr: 'quadros regulamentares aplicáveis',
            last_monitored_sr: 'carimbo de data/hora de monitoramento contínuo',
            audit_trail_heading: 'Trilha de auditoria',
            audit_trail_info: 'Cada varredura de monitoramento recebe carimbo de data/hora e é registrada. As alterações de conformidade em nível de domínio produzem um registro de auditoria imutável que pode ser exportado para revisão regulatória.',
            cybersecurity_law_info_heading: 'Detecção de leis de segurança cibernética',
            cybersecurity_law_info: 'Os domínios monitorados são automaticamente verificados em relação aos requisitos da lei de segurança cibernética (por exemplo, Lei de Segurança Cibernética do Vietnã, Diretiva NIS2). As tags da estrutura indicam quais regulamentos se aplicam. Os problemas são categorizados por estrutura regulatória para remediação direcionada.',
        },
        scan: {
            page_title: 'Verificação em tempo real',
            title: 'Scanner de privacidade em tempo real',
            settings: 'Configurações',
            results_archive: 'Arquivo de resultados',
            launch_scan: 'Iniciar verificação',
            target_url: 'Alvo URL',
            protocol_aria: 'Protocolo',
            url_required: 'URL é obrigatório',
            url_whitespace: 'Remova os espaços em branco do URL',
            url_invalid: 'Insira um domínio válido (por exemplo, exemplo.com)',
            add_subpages: 'Adicionar subpáginas',
            add_subpages_aria: 'Adicionar subpáginas, {count} adicionado',
            start_scan: 'Iniciar verificação',
            launching: 'Lançamento…',
            scan_launched: 'Verificação iniciada com sucesso',
            scan_failed: 'Falha ao iniciar a verificação',
            scan_progress: 'Progresso da verificação',
            scanning_in_progress: 'Verificação em andamento…',
            scan_id: 'ID de digitalização',
            status_label: 'Estado',
            status_unknown: 'unknown',
            progress_label: 'Progresso',
            pages_label: 'Páginas',
            refresh_status: 'Atualizar status',
            new_scan: 'Nova digitalização',
            scan_completed: 'Verificação concluída, resultados disponíveis',
            failed_check_status: 'Falha ao verificar o status da verificação',
            scan_results: 'Resultados da verificação',
            main_page: 'Página principal',
            page_n: 'Página {n}',
            result_pages_nav: 'Páginas de resultados',
            page_url: 'Página URL',
            risk_score: 'Pontuação de risco',
            risk_score_sr: 'pontuação de risco',
            cookies_label: 'Biscoitos',
            cookies_sr: 'cookies detectados',
            scripts_label: 'Roteiros',
            scripts_sr: 'scripts detectados',
            issues_label: 'Problemas',
            issues_sr: 'problemas encontrados',
            issues_found: 'Problemas encontrados',
            aria_issues_table: 'Problemas encontrados durante a verificação',
            col_severity: 'Gravidade',
            col_category: 'Categoria',
            col_title: 'Título',
            col_remediation: 'Correção',
            severity_sr: 'severity',
            scan_result_metrics: 'Métricas de resultados de verificação',
            view_full_detail: 'Ver detalhes completos',
            col_consent_category: 'Categoria de consentimento',
            consent_category_sr: 'categorização de consentimento',
            compliance_evidence_heading: 'Evidência de Conformidade',
            compliance_evidence_info: 'As descobertas da verificação são vinculadas automaticamente aos casos de conformidade. Cada problema pode ser atribuído a uma categoria de consentimento (essencial, funcional, analítico, marketing) e produz evidências prontas para auditoria para revisão regulatória.',
            rule_config_heading: 'Configuração de regras',
            rule_config_info: 'As regras de verificação são configuráveis de acordo com a estrutura regulatória. Verificações de cookies, detecção de scripts e análise de fornecedores podem ser ativadas ou desativadas em Configurações. Regras personalizadas podem ser definidas para atender aos requisitos de conformidade da sua organização.',
            case_linkage_heading: 'Vinculação de Casos de Conformidade',
            case_linkage_info: 'Os problemas encontrados durante as verificações em tempo real podem ser vinculados a casos de conformidade para rastreamento e correção. Navegue até a página Detalhes do resultado da verificação para visualizar associações de casos.',
            subpages_modal_title: 'Adicionar subpáginas',
            subpages_modal_desc: 'Adicione até 5 URLs de subpágina para verificar junto com o domínio principal.',
            subpage_n: 'Subpágina {n}',
        },
        result_detail: {
            page_title: 'Detalhe do resultado da verificação',
            title: 'Resultado da verificação',
            back_to_archive: 'Voltar ao arquivo',
            failed_load: 'Falha ao carregar o resultado da verificação',
            loading_sr: 'Carregando resultado da verificação…',
            scan_id: 'ID de digitalização',
            target_url: 'Alvo URL',
            status_label: 'Estado',
            status_sr: 'status',
            started: 'Iniciado',
            completed: 'Concluído',
            pages_scanned: 'Páginas digitalizadas',
            https_label: 'HTTPS',
            highest_risk_score: 'Maior pontuação de risco',
            most_cookies: 'A maioria dos biscoitos',
            most_vendors: 'A maioria dos fornecedores terceirizados',
            most_issues: 'A maioria dos problemas',
            across_pages: 'entre páginas',
            highest_values_aria: 'Valores mais altos em todas as páginas',
            scanned_pages_nav: 'Páginas digitalizadas',
            main_page: 'Página principal',
            page_n: 'Página {n}',
            page_url: 'Página URL',
            risk_score: 'Pontuação de risco',
            overall_risk_sr: 'pontuação geral de risco',
            cookies_label: 'Biscoitos',
            cookies_sr: 'cookies detectados',
            vendors_label: 'Fornecedores terceirizados',
            vendors_sr: 'fornecedores terceirizados detectados',
            issues_label: 'Problemas',
            issues_sr: 'problemas encontrados',
            risk_breakdown_aria: 'Detalhamento da pontuação de risco',
            issues_found: 'Problemas encontrados',
            aria_issues_table: 'Problemas encontrados durante a verificação',
            col_severity: 'Gravidade',
            col_category: 'Categoria',
            col_title: 'Título',
            col_remediation: 'Correção',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies detectados',
            col_name: 'Nome',
            col_domain: 'Domínio',
            col_path: 'Caminho',
            col_secure: 'Seguro',
            col_httponly: 'Somente http',
            col_samesite: 'MesmoSite',
            vendors_heading: 'Fornecedores terceirizados ({count})',
            aria_vendors_table: 'Fornecedores terceirizados detectados',
            col_hostname: 'Nome do host',
            scripts_heading: 'Scripts/Beacons ({count})',
            aria_scripts_table: 'Scripts e beacons detectados',
            col_url: 'URL',
            col_type: 'Tipo',
            links_heading: 'Ligações',
            first_party: 'Primeira parte',
            third_party: 'Terceiros',
            col_risk_category: 'Categoria de risco',
            col_regulatory_framework: 'Marco Regulatório',
            col_actionable_detail: 'Detalhes acionáveis',
            risk_category_sr: 'categoria de risco regulatório',
            risk_category_mapping_heading: 'Mapeamento de categoria de risco',
            risk_category_mapping_info: 'Cada descoberta de varredura é mapeada para uma categoria de risco regulatório (coleta de dados, transferência transfronteiriça, gerenciamento de consentimento, retenção de dados, controles de segurança). Este mapeamento permite uma remediação direcionada priorizada pelo impacto regulatório.',
            compliance_case_heading: 'Associação de casos de conformidade',
            compliance_case_info: 'Os resultados da verificação são vinculados a casos de conformidade para rastreamento de ponta a ponta. Os problemas podem ser escalados diretamente dos resultados da verificação para o fluxo de trabalho do caso de conformidade.',
        },
        results_archive: {
            page_title: 'Arquivo de resultados de verificação',
            title: 'Arquivo de resultados de verificação',
            back_to_scanner: 'Voltar ao scanner',
            failed_load: 'Falha ao carregar os resultados da verificação',
            results_loaded: 'Resultados da verificação carregados, registros {count}',
            total_label: 'Total: {count}',
            aria_table: 'Arquivo de resultados da verificação',
            col_scan_date: 'Data da verificação',
            col_target_url: 'Alvo URL',
            col_duration: 'Duração',
            col_issues_found: 'Problemas encontrados',
            col_risk_score: 'Pontuação de risco',
            col_status: 'Estado',
            col_actions: 'Ações',
            scan_status_sr: 'status da verificação',
            view_result: 'Ver',
            no_results: 'Nenhum resultado de verificação encontrado.',
            loading_sr: 'Carregando resultados da verificação',
            pagination_aria: 'Paginação dos resultados da verificação',
            col_risk_category: 'Categoria de risco',
            risk_category_sr: 'categoria de risco regulatório',
            archive_retention_heading: 'Retenção de Arquivo',
            archive_retention_info: 'Os resultados da verificação são retidos pelo período especificado na política de retenção de dados da sua organização. Os resultados arquivados incluem o contexto completo da verificação (tags de estrutura, categorias de risco, orientações de remediação) e podem ser exportados como CSV para evidências regulatórias.',
            export_context_info: 'Os arquivos CSV exportados incluem contexto regulatório: categoria de risco, estruturas aplicáveis e detalhes de correção acionáveis para cada descoberta.',
        },
        scanner_settings: {
            page_title: 'Configurações do scanner',
            title: 'Configurações do scanner',
            back_to_scanner: 'Voltar ao scanner',
            failed_load: 'Falha ao carregar a configuração do scanner',
            endpoint_url: 'Ponto final URL',
            save_success: 'Configuração do scanner salva com sucesso.',
            save_failed: 'Falha ao salvar a configuração do scanner',
            test_connection: 'Conexão de teste',
            testing: 'Testando...',
            saving: 'Salvando...',
            loading_sr: 'Carregando configurações do scanner',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Configurações de monitoramento de privacidade',
            title: 'Configurações de monitoramento',
            back_to_dashboard: 'Voltar ao painel',
            failed_load: 'Falha ao carregar a configuração de monitoramento',
            credentials_heading: 'AesirX Credenciais',
            connected_as: 'Conectado como {username}',
            credentials_desc: 'Insira as credenciais da sua conta AesirX para conectar o Monitoramento de privacidade. Encontre seu ID de membro na página de perfil da sua conta AesirX.',
            client_id: 'ID do cliente',
            client_secret: 'Segredo do cliente',
            username: 'AesirX Nome de usuário (e-mail)',
            password: 'AesirX Senha',
            placeholder_keep: '(deixe em branco para continuar existente)',
            credential_form_aria: 'AesirX formulário de credencial',
            test_connection: 'Conexão de teste',
            testing: 'Testando…',
            test_aria: 'Testar conexão AesirX',
            testing_aria: 'Testando conexão',
            save_configuration: 'Salvar configuração',
            saving: 'Salvando…',
            save_aria: 'Salvar credenciais',
            saving_aria: 'Salvando credenciais',
            credentials_saved: 'Credenciais AesirX salvas com sucesso.',
            credentials_saving: 'AesirX credenciais salvas. Recarregando configuração…',
            credentials_save_failed: 'Falha ao salvar credenciais',
            client_id_required: 'O ID do cliente e o nome de usuário são obrigatórios.',
            all_fields_required: 'Todos os campos de credenciais são obrigatórios.',
            email_heading: 'Notificações por e-mail',
            receive_emails: 'Receba e-mails de relatórios de verificação',
            email_toggle_aria: 'Alternar e-mails de relatório de verificação',
            only_on_change: 'Somente enviar e-mail quando o status do risco mudar',
            only_on_change_aria: 'Alternar e-mail somente na mudança de status de risco',
            emails_enabled: 'Verificar e-mails de relatório ativados',
            emails_disabled: 'Verificar e-mails de relatório desativados',
            only_change_on: 'Somente envio de e-mail sobre mudança de risco',
            only_change_off: 'Envio de e-mail em cada digitalização',
            email_failed: 'Falha ao atualizar a configuração de e-mail',
            email_settings_aria: 'Configurações de notificação por e-mail',
            loading_sr: 'Carregando configurações de monitoramento',
        },
        entity_types: {
            user: 'Usuário',
            case: 'Caso',
            incident: 'Incidente',
            assessment: 'Avaliação',
            vendor: 'Fornecedor',
            consent: 'Consentimento',
            policy: 'Política',
            data_map: 'Mapa de dados',
        },
        actions: {
            create: 'Criar',
            update: 'Atualizar',
            delete: 'Excluir',
            view: 'Ver',
            export: 'Exportar',
            status_change: 'Mudança de status',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Painel de Formulários',
            total_templates: 'Total de Modelos',
            installed: 'Instalado',
            available: 'Disponível',
            by_market: 'Por Mercado',
            back_dashboard: 'Voltar para Painel de controlo',
            browse_templates: 'Explorar modelos',
            catalog_coverage: 'Cobertura do catálogo',
            quick_start: 'Início rápido',
            recent_instances: 'Instâncias recentes',
            total_instances: 'Total de instâncias',
            fetch_failed: 'Falha ao fetch dashboard data. Por favor, tente novamente.',
            no_instances: 'Nenhum instances encontrado',
            form_metrics: 'Visão geral de métricas de formulários',
            templates_total: 'modelos no total',
            instances_total: 'instâncias no total',
            installed_of_available: '{installed} de {available} instalado',
            templates_in_category: 'modelos em {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Modelos de Formulários',
            new: 'Novo Modelo',
            edit: 'Editar Modelo',
            name: 'Nome do Modelo',
            form_type: 'Tipo de Formulário',
            market: 'Mercado',
            language: 'Idioma',
            schema: 'Esquema do Formulário',
            preview: 'Pré-visualizar',
            duplicate: 'Duplicar Modelo',
            grid_view: 'Vista em grelha',
            list_view: 'Lista Ver',
            fetch_failed: 'Falha ao fetch templates. Por favor, tente novamente.',
            filter_templates: 'Filtrar modelos',
            version_history: 'Histórico de versões',
            no_version_history: 'Sem histórico de versões — esta é a versão inicial.',
            form_id: 'ID do formulário',
            source_instrument: 'Instrumento de origem',
            issuer: 'Emissor',
            all_frameworks: 'Todas as estruturas',
            view_mode: 'Modo de visualização',
            classification: 'Classificação',
        },
        catalog: {
            title: 'Catálogo de Formulários',
            install: 'Instalar',
            uninstall: 'Desinstalar',
            open_form: 'Open form',
            install_all: 'Instalar Todos',
            installed_tab: 'Instalados',
            catalog_tab: 'Catálogo',
            search_placeholder: 'Pesquisar modelos...',
            no_templates: 'Nenhum modelo encontrado.',
            template_count: '{count} modelos',
            all_markets: 'Todos os mercados',
            sections: 'Secções',
            all_categories: 'Todas as categorias',
            category: 'Categoria',
            category_install_failed: 'Falha ao install category templates. Por favor, tente novamente.',
            category_install_success: 'Modelos de categoria instalados.',
            fetch_failed: 'Falha ao fetch templates. Por favor, tente novamente.',
            fetch_installed_failed: 'Falha ao fetch installed templates. Por favor, tente novamente.',
            fields: 'Campos',
            install_failed: 'Falha ao install template. Por favor, tente novamente.',
            install_success: 'Modelo installed com sucesso.',
            installing: 'A instalar...',
            removing: 'A remover...',
            uninstall_confirm: 'Tem a certeza de que pretende desinstalar este modelo?',
            uninstall_failed: 'Falha ao uninstall template. Por favor, tente novamente.',
            uninstall_success: 'Modelo uninstalled com sucesso.',
            installation_summary: 'Resumo da instalação',
            filter_catalog: 'Filtrar catálogo',
            all_frameworks: 'Todas as estruturas',
            templates_installed_count: '{count} modelos instalados com sucesso',
            all_already_installed: 'Todos os modelos nesta categoria já estão instalados',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'ID do formulário',
            issuer: 'Emissor',
            catalog_tabs: 'Abas do catálogo de formulários',
        },
        filler: {
            back_templates: 'Voltar para Modelos',
            no_sections: 'Nenhum sections encontrado',
            draft_created: 'Rascunho criado.',
            draft_saved: 'Rascunho guardado.',
            fetch_template_failed: 'Falha ao fetch template. Por favor, tente novamente.',
            save_draft: 'Guardar Rascunho',
            save_draft_failed: 'Falha ao save draft. Por favor, tente novamente.',
            saving: 'A guardar...',
            select_option: 'Selecione option',
            submit: 'Submeter',
            submit_failed: 'Falha ao submit. Por favor, tente novamente.',
            submitting: 'A submeter...',
            title: 'Assistente de formulário',
            form_id_label: 'ID do formulário',
            schema_label: 'Esquema',
            form_number_label: 'Form',
            framework_label: 'Estrutura',
            issuer_label: 'Emissor',
            source_label: 'Fonte',
            market_label: 'Mercado',
            section_of: 'Seção {current} de {total}',
            form_navigation: 'Navegação do formulário',
            form_progress: 'Progresso do formulário: Seção {current} de {total}',
            removed_item: 'Item {index} removido de {field}',
            added_item: 'Novo item adicionado a {field}',
            go_to_previous: 'Ir para a seção anterior, seção {current} de {total}',
            go_to_next: 'Ir para a próxima seção, seção {current} de {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Estado de submissão',
            advance: 'Avançar estado de submissão...',
            not_started: 'Não iniciado',
            drafting: 'Rascunho',
            internal_review: 'Revisão interna',
            approved_for_submission: 'Aprovado para submissão',
            submitted_to_authority: 'Submetido à autoridade',
            supplement_requested: 'Complemento solicitado',
            supplement_in_progress: 'Complemento em andamento',
            resubmitted: 'Reenviado',
            accepted: 'Aceite',
            rejected: 'Rejeitado',
            closed: 'Encerrado',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Aprovado Por',
            form_answers: 'Respostas do formulário',
            column_header: 'Form',
            no_answers: 'Nenhum answers encontrado',
            view_outputs: 'Ver Outputs',
            approve: 'Aprovar',
            approve_failed: 'Falha ao approve. Por favor, tente novamente.',
            approving: 'A aprovar...',
            detail_title: 'Detalhe da instância',
            fetch_failed: 'Falha ao fetch instances. Por favor, tente novamente.',
            generate_failed: 'Falha ao generate output. Por favor, tente novamente.',
            generate_output: 'Gerar resultado',
            generating: 'A gerar...',
            submitted_at: 'Submetido em',
            instance_metadata: 'Metadados da instância do formulário',
            filing_status_steps: 'Etapas do status de arquivamento',
            current_status: 'status atual',
            filing_status_update_failed: 'Falha ao atualizar o estado da submissão',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Voltar à instância',
            no_outputs_msg: 'Ainda não foram gerados resultados.',
            no_outputs_title: 'Sem resultados',
            output_name: 'Nome de saída',
            download: 'Transferir',
            download_failed: 'Falha ao download. Por favor, tente novamente.',
            fetch_failed: 'Falha ao fetch outputs. Por favor, tente novamente.',
            format: 'Formato',
            generated_at: 'Gerado em',
            generated_by: 'Gerado por',
            size: 'Tamanho',
            title: 'Resultados do formulário',
            print: 'Imprimir',
            print_outputs: 'Imprimir saídas do formulário',
            printing: 'Imprimindo saídas do formulário',
            pdf_preview: 'Visualização PDF',
            downloading: 'Baixando {name}',
            preview_in_new_window: 'Visualizar {name} ({format}) em nova janela',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Âmbito e aplicabilidade',
            risk: 'Avaliação de riscos e mapeamento de controlos',
            policy: 'Políticas de segurança organizacional',
            localization: 'Localização de dados e presença local',
            vendor: 'Terceiros e cadeia de fornecimento',
            authority: 'Tratamento de pedidos de autoridades',
            incident: 'Resposta a incidentes e evidências',
            training: 'Formação e preparação',
        },
        market_finalization_domains: {
            data_law: 'Lei de Dados — Governança e Controles Operacionais',
            pdpl_2025: 'PDPL 2025 — Governança de Privacidade e Direitos dos Titulares de Dados',
            decree_356: 'Decreto 356/2025 — Controles de Fornecedores e Transfronteiriços',
            telecom: 'Lei de Telecomunicações — Confidencialidade e Solicitações de Autoridades',
        },
        nav: {
            filing_case_links: 'Ligações de processo',
            section_routing: 'Encaminhamento de secções',
            submission_packages: 'Pacotes de submissão',
        },
        filing_case_links: {
            title: 'Ligações de processo',
            link_form: 'Ligar formulário ao processo',
            filing_case_type: 'Tipo de processo',
            form_instance: 'Instância de formulário',
            select_form_instance: 'Selecionar uma instância de formulário...',
            select_filing_case: 'Selecionar um processo...',
            no_links_found: 'Nenhuma ligação de processo encontrada.',
            linked: 'Ligado',
            filing_case_id: 'ID do caso de arquivamento',
            link_created: 'Link de caso de arquivamento criado',
            link_deleted: 'Ligação de processo eliminada',
            aria_delete_link: 'Eliminar ligação {name}',
            case_type_dpia_dossier: 'Dossiê AIPD',
            case_type_cross_border_filing: 'Arquivamento transfronteiriço',
            case_type_breach_notification: 'Notificação de violação',
            no_instances_guidance: 'Ainda sem instâncias de formulário. Crie uma a partir de Modelos de formulário.',
            no_filings_guidance: 'Ainda sem processos de arquivo. Crie um a partir de Avaliações.',
        },
        section_routing: {
            title: 'Encaminhamento de secções',
            assign_section: 'Atribuir secção',
            department: 'Departamento',
            select_department: 'Selecionar um departamento...',
            assignee: 'Atribuído a',
            select_assignee: 'Selecionar um atribuído...',
            section: 'Secção',
            select_section: 'Selecionar uma secção...',
            select_form_instance: 'Selecionar uma instância de formulário...',
            no_assignments_found: 'Nenhuma atribuição de secção encontrada.',
            select_instance_prompt: 'Selecione uma instância de formulário para ver as atribuições de secção.',
            complete_section: 'Concluir secção',
            status_pending: 'Pendente',
            status_in_progress: 'Em progresso',
            status_completed: 'Concluído',
            status_reviewed: 'Revisto',
            form_instance_id: 'ID da instância do formulário',
            assignment_created: 'Atribuição de seção criada',
            assignment_deleted: 'Atribuição de secção eliminada',
            status_updated: 'Status da seção atualizado',
            aria_delete_assignment: 'Eliminar atribuição {name}',
            deadline: 'Prazo',
            deadline_help: 'Opcional. Atribuições após esta data são exibidas como atrasadas.',
            overdue: 'Atrasado',
            days_remaining: 'dias restantes',
            approval_requested: 'Submetido para revisão',
            form_approved: 'Formulário aprovado',
            form_rejected: 'Formulário rejeitado',
            no_departments_guidance: 'Nenhum departamento configurado. Configure nas Definições da organização.',
            no_sections_guidance: 'Nenhuma secção definida no modelo. Certifique-se de que o modelo de formulário tem secções.',
            select_instance_first: 'Selecione primeiro uma instância de formulário para carregar as secções disponíveis.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Pacotes de submissão',
            assemble_package: 'Montar pacote de submissão',
            dossier: 'Dossiê',
            select_dossier: 'Selecionar um dossiê...',
            package_label: 'Etiqueta do pacote',
            cover_letter: 'Carta de apresentação',
            checklist: 'Lista de verificação',
            evidence_index: 'Índice de evidências',
            completeness: 'Completude',
            incomplete_warning: 'Não é possível montar: o formulário tem secções incompletas.',
            no_packages_found: 'Nenhum pacote de submissão encontrado.',
            version: 'Versão',
            package_assembled: 'Pacote de submissão montado',
            package_deleted: 'Pacote de submissão eliminado',
            aria_delete_package: 'Eliminar pacote {name}',
            status_draft: 'Rascunho',
            status_complete: 'Completo',
            status_submitted: 'Submetido',
            status_accepted: 'Aceite',
            status_rejected: 'Rejeitado',
            package_updated: 'Pacote de submissão atualizado',
            document_generated: 'Documento gerado',
            documents: 'Documentos',
            generate: 'Gerar',
            preview: 'Pré-visualizar',
            download: 'Descarregar',
            download_all: 'Descarregar tudo',
            no_dossiers_guidance: 'Ainda sem dossiês. Crie um a partir de Avaliações.',
            package_plan_help: 'Incluir no plano do pacote (geração automática de documentos disponível):',
            cover_letter_help: 'Carta de apresentação oficial dirigida à autoridade destinatária',
            checklist_help: 'Lista de verificação de conformidade que verifica todos os requisitos de submissão',
            evidence_index_help: 'Índice de documentos comprovativos referenciados na submissão',
            completeness_skipped: 'Verificação de completude ignorada — nenhuma instância de formulário associada a este dossiê.',
            completeness_unavailable: 'Verificação de completude indisponível — a prosseguir com a montagem do pacote.',
            duplicate_package_error: 'Já existe um pacote de submissão para este dossiê.',
            doc_cover_letter: 'Carta de apresentação',
            doc_checklist: 'Lista de verificação',
            doc_evidence_index: 'Índice de evidências',
            export_csv: 'Exportar CSV',
            export_success: 'Ligações dos casos de arquivo exportadas com sucesso',
            no_assignments_guidance: 'Ainda sem atribuições de secções. Atribua secções a departamentos e membros da equipa para iniciar o encaminhamento.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Selecionar um modelo',
            hint: 'Escolha um modelo de formulário regulamentar ou comece com um formulário em branco.',
            market_filter: 'Filtrar por mercado',
            framework_filter: 'Filtrar por enquadramento',
            all_markets: 'Todos os mercados',
            all_frameworks: 'Todos os enquadramentos',
            search_placeholder: 'Pesquisar modelos...',
            blank_form: 'Formulário em branco / genérico',
            blank_form_desc: 'Criar sem modelo — preencher os campos manualmente.',
            no_templates: 'Nenhum modelo encontrado para este filtro.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Selecionar',
            link_error: 'Falha na ligação do modelo — pode ligar um modelo mais tarde a partir da vista de Edição.',
            internal_badge: 'Modelo interno',
            legacy_badge: 'Obsoleto',
            draft_badge: 'Rascunho',
            upcoming_badge: 'Próximo',
            hidden_badge: 'Oculto',
            show_hidden_toggle: 'Mostrar modelos ocultos / obsoletos',
            show_hidden_toggle_hint: 'Vista de administrador: inclui modelos marcados como internal_only, hidden, legacy ou draft.',
        },
        cover_letter_modal: {
            title: 'Carta de apresentação: pré-visualização e edição',
            iframe_title: 'Pré-visualização da carta de apresentação',
            stage_label: 'Fase',
            stage_initial: 'Submissão inicial',
            stage_supplement: 'Resposta suplementar',
            stage_resubmission: 'Resubmissão',
            subject_label: 'Assunto',
            body_label: 'Parágrafo principal',
            notes_label: 'Notas adicionais',
            refresh_preview: 'Atualizar pré-visualização',
            confirm: 'Confirmar e guardar',
            regenerate: 'Regenerar',
            snapshot_saved: 'Carta de apresentação guardada',
        },
        related_panel: {
            title: 'Formulários regulamentares relacionados',
            open_form: 'Preencher formulário',
            no_forms: 'Nenhum formulário relacionado encontrado.',
            hint_vn: 'Formulários regulamentares específicos do VN disponíveis — selecione um formulário para abrir o assistente de conformidade.',
            internal_badge: 'Interno',
            official_badge: 'Oficial',
            loading: 'A carregar formulários relacionados...',
            error: 'Não foi possível carregar os formulários relacionados.',
            form_id: 'ID do formulário',
            form_name: 'Nome do formulário',
            framework: 'Enquadramento regulamentar',
        },
        view_form: 'Ver formulário',
        edit_form: 'Editar formulário',
        save_draft: 'Guardar rascunho',
        module_settings: 'Definições do módulo',
        validation_errors: 'Por favor, corrija os erros de validação',
        custom_name_help: 'Opcional — por omissão, o nome do modelo se deixado em branco',
        select_placeholder: 'Selecionar...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Anterior',
        next_section: 'Seguinte',
        loading: 'A carregar...',
        array_add: 'Adicionar',
        array_remove: 'Remover',
        array_item_label: '{label} item {index}',
        array_add_item: 'Adicionar item a {label}',
        array_remove_item: 'Remover item {index} de {label}',
        legacy_banner: 'Este registo foi criado antes dos formulários unificados. Alguns dados podem divergir entre a entidade e a instância do formulário.',
        field_required: '{label} é obrigatório',
        section_announcement: 'Seção {current} de {total}: {title}',
        sections_nav_label: 'Seções do formulário',
        no_sections: 'Nenhuma seção de modelo disponível.',
        section_label_tpl: 'Seção {index}: {title}',
        field_shown_announcement: '{field} agora é exibido.',
        field_required_announcement: '{field} agora é obrigatório.',
        submit_blocked_hint_one: '1 campo obrigatório ainda precisa de uma resposta.',
        submit_blocked_hint_many: '{count} campos obrigatórios ainda precisam de uma resposta.',
        module_settings_auto_applied: 'As configurações específicas do módulo são aplicadas automaticamente.',
        operation_failed: 'Falha na operação',
        failed_to_load_form: 'Falha ao carregar o formulário',
        failed_to_save_form: 'Falha ao guardar o formulário',
        failed_to_submit_form: 'Falha ao submeter o formulário',
        failed_to_load_template: 'Falha ao carregar o modelo',
        failed_to_save_draft: 'Falha ao guardar o rascunho',
        failed_to_create: 'Falha ao criar',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Catálogo de Pacotes',
        install: 'Instalar Pacote',
        uninstall: 'Desinstalar Pacote',
        install_batch: 'Instalar Conjunto',
        installed_tab: 'Instalados',
        catalog_tab: 'Catálogo',
        pack_details: 'Detalhes do Pacote',
        frameworks: 'Enquadramentos',
        requirements: 'Requisitos',
        artifacts: 'Artefactos',
        language_pack: 'Pacote de Idioma',
        dependencies: 'Dependências',
        no_packs: 'Nenhum pacote encontrado.',
    },
    platform_pages: {
        users: {
            title: 'Utilizadores',
            new: 'Novo Utilizador',
            invite: 'Convidar Utilizador',
            edit: 'Editar Utilizador',
            name: 'Nome',
            email: 'E-mail',
            role: 'Função',
            status: 'Estado',
            identity_type: 'Tipo de Identidade',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Último Início de Sessão',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Desativar',
            reactivate: 'Reativar',
            unlock: 'Desbloquear',
            reset_password: 'Repor Palavra-passe',
            remove: 'Remover',
            remove_confirm: 'Remover "{name}"? Esta ação é permanente e o utilizador perderá todo o acesso. O registo é mantido para fins de auditoria.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Funções',
            new: 'Nova Função',
            edit: 'Editar Função',
            name: 'Nome da Função',
            description: 'Descrição',
            permissions: 'Permissões',
            users_count: 'Utilizadores',
            detail: 'Detalhe da Função',
            all_tiers: 'Todos os níveis',
            tier: 'Nível',
            type_system: 'Sistema',
            type_custom: 'Personalizado',
            role_name_placeholder: 'p. ex. responsável regional de conformidade',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'O Meu Perfil',
            name: 'Nome',
            display_name: 'Nome de exibição',
            email: 'E-mail',
            phone: 'Telefone',
            department: 'Departamento',
            job_title: 'Cargo',
            language: 'Idioma',
            role: 'Função',
            persona: 'Persona detetada',
            last_login: 'Último Início de Sessão',
            save: 'Guardar alterações',
            save_success: 'Perfil atualizado com sucesso',
            module_tips_section: 'Dicas de módulo',
            tips_toggle: 'Mostrar dicas regulatórias nos módulos',
            dismissed_tips: 'Dicas ignoradas',
            reset_dismissed: 'Repor todas as dicas ignoradas',
            notification_prefs: 'Preferências de notificação',
            notification_prefs_link: 'Gerir preferências de notificação →',
            change_password: 'Alterar Palavra-passe',
            current_password: 'Palavra-passe Atual',
            new_password: 'Nova Palavra-passe',
            confirm_password: 'Confirmar Palavra-passe',
            accessibility: 'Acessibilidade',
            high_contrast: 'Modo de alto contraste',
            high_contrast_desc: 'Aumentar o contraste para melhor legibilidade. Cumpre WCAG 2.1 AAA.',
            passwords_no_match: 'As senhas não coincidem',
            password_changed_logout: 'Senha alterada. Você será desconectado.',
            failed_update: 'Falha ao atualizar perfil',
            failed_change_password: 'Falha ao alterar senha',
            none: 'Nenhum',
            never: 'Nunca',
            password_requirements: 'Mín. 12 caracteres, maiúscula, minúscula, dígito, caractere especial',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'Definições de MFA',
            mfa_enable: 'Ativar MFA',
            mfa_disable: 'Desativar MFA',
            mfa_setup: 'Digitalize este código QR com a sua aplicação de autenticação',
            sso_title: 'Configuração de SSO',
            sso_provider: 'Fornecedor',
            sso_client_id: 'ID do Cliente',
            sso_redirect: 'URI de Redirecionamento',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'Chaves de API',
            api_keys_new: 'Nova Chave de API',
            api_keys_name: 'Nome da Chave',
            api_keys_last_used: 'Última Utilização',
            api_keys_expires: 'Expira',
            api_keys_revoke: 'Revogar',
            service_accounts_title: 'Contas de Serviço',
            ip_allowlist_title: 'Lista de IPs Permitidos',
            ip_add: 'Adicionar Endereço IP',
            ip_address: 'Endereço IP',
            ip_description: 'Descrição',
            delegations_title: 'Delegações',
            delegations_new: 'Nova Delegação',
            delegations_from: 'Do Utilizador',
            delegations_to: 'Para o Utilizador',
            delegations_expires: 'Expira',
            break_glass_title: 'Acesso de Emergência',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Solicitar Acesso',
            break_glass_reason: 'Motivo do Acesso',
            break_glass_duration: 'Duração',
            session_settings_title: 'Definições de Sessão',
            eviction_policy_section: 'Política de despejo',
            session_timeout: 'Tempo Limite da Sessão (minutos)',
            session_max_concurrent: 'Máximo de Sessões Simultâneas',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Sessões Ativas',
            active_sessions_device: 'Dispositivo',
            active_sessions_ip: 'Endereço IP',
            active_sessions_started: 'Iniciada',
            active_sessions_revoke: 'Revogar Sessão',
            active_sessions_confirm_terminate: 'Tem certeza de que deseja encerrar esta sessão?',
            active_sessions_confirm_terminate_current: 'Esta é a sua sessão atual. Encerrá-lo irá desconectá-lo imediatamente. Continuar?',
            active_sessions_confirm_terminate_all: 'Tem certeza de que deseja encerrar todas as outras sessões? Apenas a sua sessão atual permanecerá ativa.',
            active_sessions_terminated: 'Sessão encerrada com sucesso.',
            active_sessions_all_terminated: 'Todas as outras sessões foram encerradas.',
            active_sessions_terminate_all: 'Encerre todas as outras sessões',
            active_sessions_last_activity: 'Última atividade',
            active_sessions_current: 'Sessão Atual',
            active_sessions_revoke_selected: 'Revogar selecionadas',
            active_sessions_confirm_terminate_selected: 'Tem a certeza de que pretende terminar as sessões selecionadas?',
            active_sessions_selected_terminated: 'Sessões selecionadas terminadas com sucesso.',
            ip_allowlist_desc: 'Gerencie endereços IP permitidos para acesso API.',
            ip_no_entries: 'Nenhum endereço IP configurado.',
            session_settings_desc: 'Configure o tempo limite da sessão e os limites de sessões simultâneas.',
            session_settings_save_policy: 'Política de Salvar Sessão',
            session_settings_saved: 'Configurações de sessão atualizadas com sucesso.',
            session_settings_idle_timeout: 'Tempo limite de inatividade (minutos)',
            session_settings_absolute_timeout: 'Tempo limite absoluto (minutos)',
            session_settings_help_idle: 'A sessão expirará após este período de inatividade.',
            session_settings_help_absolute: 'A sessão expirará após esta duração total, independentemente da atividade.',
            session_settings_help_max: 'Número máximo de sessões simultâneas por usuário.',
            sso_note: 'As alterações na configuração do SSO podem exigir que os usuários se autentiquem novamente.',
            service_accounts_warning: 'Esta chave API será mostrada apenas uma vez. Copie-o agora e guarde-o com segurança.',
            service_accounts_new: 'Nova conta de serviço',
            service_accounts_name: 'Nome da conta',
            service_accounts_role: 'Função',
            service_accounts_created: 'Criado',
            service_accounts_desc: 'Gerencie contas de serviço máquina a máquina para integrações API.',
            break_glass_warning: 'O acesso de emergência é auditado e registrado. Use apenas para emergências genuínas.',
            break_glass_min_chars: '{count} / {min} mínimo de caracteres',
            break_glass_permission_count: '{count} permissões selecionadas',
            break_glass_submitted: 'Solicitação de quebra de vidro enviada. Aguardando aprovação.',
            break_glass_approved: 'Pedido de quebra de vidro aprovado.',
            break_glass_denied: 'Pedido de quebra de vidro negado.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Confirmar ativação',
            break_glass_pending: 'Solicitações pendentes',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Tempo restante',
            break_glass_expired: 'Expirado',
            break_glass_approved_denied_by: 'Aprovado/recusado por',
            delegations_desc: 'Gerencie delegações de permissões temporárias entre usuários.',
            delegations_my: 'Minhas delegações',
            delegations_to_me: 'Delegado a mim',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Todas as permissões',
            delegations_scope_specific: 'Permissões específicas',
            delegations_created: 'Delegação criada com sucesso.',
            delegations_revoked: 'Delegação revogada.',
            delegations_confirm_revoke: 'Tem certeza de que deseja revogar esta delegação?',
            delegations_confirm_revoke_title: 'Confirmar revogação',
            delegations_permission_count: '{count} permissões',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Estado',
            api_keys_prefix: 'Prefixo chave',
            api_keys_permissions_count: 'Permissões',
            api_keys_ip_count: 'Restrições de IP',
            api_keys_created: 'Criado',
            api_keys_confirm_revoke_title: 'Confirmar revogação',
            api_keys_confirm_revoke_message: 'Tem certeza de que deseja revogar a chave API "{name}"? Esta ação não pode ser desfeita.',
            api_keys_confirm_rotate_title: 'Confirmar rotação',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'Chave API criada com sucesso.',
            api_keys_revoked_success: 'A chave API "{name}" foi revogada.',
            api_keys_rotated_success: 'A chave API "{name}" foi girada. Copie a nova chave abaixo.',
            api_keys_load_error: 'Falha ao carregar chaves API.',
            api_keys_create_error: 'Falha ao criar a chave API.',
            api_keys_revoke_error: 'Falha ao revogar a chave API.',
            api_keys_rotate_error: 'Falha ao girar a chave API.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minutos',
            break_glass_duration_30: '30 minutos',
            break_glass_duration_60: '60 minutos (1h)',
            break_glass_duration_120: '120 minutos (2h)',
            break_glass_duration_240: '240 minutos (4h)',
            sod_rules_title: 'Regras de segregação de funções',
            sod_violations_report: 'Relatório de violações',
            sod_create_rule: 'Criar regra',
            sod_col_name: 'Nome',
            sod_col_description: 'Descrição',
            sod_col_conflict_count: 'Contagem de conflitos',
            sod_col_severity: 'Gravidade',
            sod_col_status: 'Estado',
            sod_col_actions: 'Ações',
            sod_no_rules: 'Nenhuma regra SoD encontrada',
            sod_severity_warning: 'Aviso',
            sod_severity_blocking: 'Bloqueio',
            sod_active: 'Ativo',
            sod_inactive: 'Inativo',
            sod_edit: 'Editar',
            sod_exceptions: 'Exceções',
            sod_deactivate: 'Desativar',
            sod_confirm_deactivate_title: 'Confirmar desativação',
            sod_confirm_deactivate_message: 'Desativar regra "{name}"? Ele não sinalizará mais violações.',
            sod_deactivate_error: 'Falha ao desativar a regra',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Revisões de Acesso',
            campaigns: 'Campanhas de Revisão',
            new_campaign: 'Nova Campanha',
            campaign_name: 'Nome da Campanha',
            scope: 'Âmbito',
            reviewers: 'Revisores',
            due_date: 'Data Limite',
            progress: 'Progresso',
            my_reviews: 'As Minhas Revisões',
            approve: 'Aprovar',
            revoke: 'Revogar',
            analytics: 'Análise de Funções',
            analytics_title: 'Análise de Funções',
            permission_usage: 'Utilização de Permissões',
            role_coverage: 'Cobertura de Funções',
            campaign_detail: 'Detalhes da campanha',
            campaign_description: 'Descrição',
            campaign_status: 'Estado',
            campaign_deadline: 'Prazo',
            campaign_scope_type: 'Tipo de escopo',
            campaign_completion: 'Conclusão',
            campaign_total_items: 'Total de itens',
            campaign_pending_items: 'Pendente',
            campaign_confirmed_items: 'Confirmado',
            campaign_revoked_items: 'Revogado',
            campaign_escalated_items: 'Escalado',
            campaign_launch: 'Campanha de lançamento',
            campaign_cancel: 'Cancelar campanha',
            campaign_confirm_launch: 'Tem certeza de que deseja lançar esta campanha?',
            campaign_confirm_cancel: 'Tem certeza de que deseja cancelar esta campanha?',
            campaign_launched: 'Campanha lançada com sucesso.',
            campaign_cancelled: 'Campanha cancelada.',
            review_items: 'Revisar itens',
            review_item_user: 'Usuário',
            review_item_role: 'Função',
            review_item_reviewer: 'Revisor',
            review_item_decision: 'Decisão',
            review_item_justify: 'Justificativa',
            review_item_confirm: 'Confirmar',
            review_item_revoke: 'Revogar',
            review_item_escalate: 'Escalar',
            all_decisions: 'Todas as decisões',
            bulk_action: 'Ação em massa',
            bulk_confirm: 'Confirmação em massa',
            bulk_revoke: 'Revogar em massa',
            selected_count: '{count} selecionado',
            justify_required: 'É necessária justificativa',
            scope_all_users: 'Todos os usuários',
            scope_role_owner: 'Proprietário da função',
            scope_department: 'Departamento',
            back_to_campaigns: 'Voltar para campanhas',
            recommendations: 'Recomendações',
            refreshing: 'Refrescante...',
            decide: 'Decidir',
            decision_by: 'Decisão por',
            loading_items: 'Carregando itens...',
            loading_review_items: 'Carregando itens de revisão...',
            campaign_not_found: 'Campanha não encontrada',
            loading_campaign: 'Carregando detalhes da campanha...',
            campaign_completed: 'Campanha concluída com sucesso.',
            confirm_cancel_title: 'Confirmar Cancelar',
            justify_bulk_confirm: 'Justificativa para confirmação em massa:',
            justify_bulk_revoke: 'Justificativa para revogação em massa:',
            items_selected: '{count} itens selecionados',
            justify_bulk_required: 'A justificativa é necessária para ações em massa',
            create_campaign_title: 'Criar campanha de revisão de acesso',
            name_required: 'O nome é obrigatório',
            scope_type_required: 'O tipo de escopo é obrigatório',
            reviewer_strategy_required: 'A estratégia do revisor é necessária',
            deadline_required: 'O prazo é obrigatório',
            reviewer_id_required: 'O ID do revisor é obrigatório para uma estratégia de usuário específica',
            invalid_json: 'JSON inválido',
            scope_filter_label: 'Filtro de escopo (JSON)',
            reviewer_strategy_label: 'Estratégia do Revisor',
            reviewer_id_label: 'ID do revisor',
            deadline_label: 'Prazo',
            create_campaign_btn: 'Criar campanha',
            scope_type_label: 'Tipo de escopo',
            scope_by_role: 'Por função',
            scope_by_department: 'Por Departamento',
            scope_custom: 'Personalizado',
            reviewer_manager: 'Gerente',
            reviewer_specific_user: 'Usuário específico',
            enter_json_filter: 'Insira o filtro JSON',
            hint_label: 'Dica:',
            role_usage: 'Uso de função',
            over_privileged_users: 'Usuários com privilégios excessivos',
            role_name_col: 'Nome da função',
            assigned_users: 'Usuários atribuídos',
            active_users_30d: 'Usuários ativos (30d)',
            total_perms: 'Total de autorizações',
            used_perms: 'Autorizações usadas',
            unused_perms: 'Autorizações não utilizadas',
            no_data_available: 'Não há dados disponíveis',
            roles_col: 'Funções',
            total_col: 'Total',
            used_col: 'Usado',
            unused_pct: '% não utilizada',
            current_count: 'Contagem atual',
            recommended_count: 'Contagem recomendada',
            rationale: 'Justificativa',
            loading_analytics: 'Carregando análises...',
            loading_role_analytics: 'Carregando dados de análise de função...',
            computed_at: 'Calculado em:',
            form_errors: 'O formulário tem {count} erro(s). Por favor, corrija-os.',
            scope_filter_invalid_json: 'O filtro de escopo contém JSON inválido',
            scope_hint_all_users: 'Nenhum filtro é necessário – todos os usuários serão incluídos.',
            review_type_label: 'Tipo de revisão',
            review_type_required: 'O tipo de revisão é obrigatório',
            reviewer_ids_label: 'IDs dos revisores',
            reviewer_ids_hint: 'Insira IDs de usuário separados por vírgulas',
            reviewer_ids_required: 'Pelo menos um ID de revisor é necessário',
            start_date_label: 'Data de início',
            start_date_required: 'A data de início é obrigatória',
            end_date_label: 'Data de fim',
            end_date_required: 'A data de fim é obrigatória',
            review_type_user_access: 'Acesso de usuário',
            review_type_role_permissions: 'Permissões de função',
            review_type_service_accounts: 'Contas de serviço',
            review_type_privileged_access: 'Acesso privilegiado',
            review_type_application_access: 'Acesso a aplicação',
            auto_revoke_label: 'Revogar automaticamente ao negar',
            campaigns_title: 'Acesse campanhas de revisão',
            create_campaign_label: '+ Criar campanha',
            no_campaigns_found: 'Nenhuma campanha encontrada',
            loading_campaigns: 'Carregando campanhas...',
            no_reviews_assigned: 'Nenhuma avaliação atribuída a você',
            loading_reviews: 'Carregando avaliações...',
            review_decision_title: 'Rever decisão',
            justification_label: 'Justificativa',
            announce_loaded_review_items: '{count} itens de revisão carregados',
            announce_loaded_campaigns: '{count} campanhas carregadas',
            announce_decision_submitted: 'Decisão "{decision}" enviada para item de revisão',
            announce_decision_submitted_for: 'Decisão "{decision}" enviada para {name}',
            announce_bulk_decision: '{decision} em massa aplicado a {count} itens',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Agendamento de Atestações',
            new: 'Novo Agendamento',
            edit: 'Editar Agendamento',
            create_title: 'Criar agendamento de atestação',
            edit_title: 'Editar agendamento de atestação',
            schedule_history: 'Histórico do agendamento',
            next_run_date: 'Data da próxima execução',
            schedule_name: 'Nome do Agendamento',
            frequency: 'Frequência',
            next_run: 'Próxima Execução',
            last_run: 'Última Execução',
            history: 'Histórico de Atestações',
            description: 'Descrição',
            scope: 'Escopo',
            status: 'Estado',
            created_at: 'Criado',
            updated_at: 'Atualizado',
            trigger_now: 'Acione agora',
            back_to_schedules: 'Voltar aos horários',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Agenda acionada com sucesso.',
            deleted: 'Agenda excluída.',
            saved: 'Agenda salva com sucesso.',
            campaigns_generated: 'Campanhas geradas',
            run_date: 'Data de execução',
            run_status: 'Status de execução',
            create_schedule: 'Criar cronograma',
            view_history: 'Ver histórico',
            deactivate: 'Desativar',
            overdue: 'OVERDUE',
            overdue_sr: 'O cronograma está atrasado',
            confirm_trigger_title: 'Confirmar ação',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Confirmar desativação',
            confirm_deactivate_message: 'Tem certeza de que deseja desativar esta programação?',
            triggered_success: 'Agenda acionada com sucesso.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Agenda desativada.',
            name_label: 'Nome *',
            frequency_label: 'Frequência *',
            scope_type_label: 'Tipo de escopo *',
            scope_filter_label: 'Filtro de escopo (JSON)',
            reviewer_strategy_label: 'Estratégia do Revisor *',
            reviewer_id_label: 'ID do revisor *',
            frequency_quarterly: 'Trimestralmente',
            frequency_semi_annual: 'Semestral',
            frequency_annual: 'Anual',
            scope_all_users: 'Todos os usuários',
            scope_by_role: 'Por função',
            scope_by_department: 'Por Departamento',
            scope_custom: 'Personalizado',
            reviewer_manager: 'Gerente',
            reviewer_role_owner: 'Proprietário da função',
            reviewer_specific_user: 'Usuário específico',
            computed_next_run_date: 'Data calculada da próxima execução',
            current_scheduled_date: 'Data agendada atual',
            recomputed_frequency_changed: '(recalculado - frequência alterada)',
            estimated_days_hint: 'Estimado com base em {days} dias a partir de hoje',
            validation_name_required: 'O nome é obrigatório',
            validation_frequency_required: 'A frequência é obrigatória',
            validation_scope_type_required: 'O tipo de escopo é obrigatório',
            validation_reviewer_strategy_required: 'A estratégia do revisor é necessária',
            validation_reviewer_id_required: 'O ID do revisor é obrigatório para uma estratégia de usuário específica',
            validation_invalid_json: 'JSON inválido',
            validation_scope_filter_invalid_json: 'O filtro de escopo contém JSON inválido',
            form_errors: 'O formulário tem {count} erro(s). Por favor, corrija-os.',
            create_schedule_btn: 'Criar cronograma',
            schedule_history_title: 'Histórico de agendamento',
            campaign_name_col: 'Nome da campanha',
            items_col: 'Itens',
            no_campaigns_yet: 'Nenhuma campanha gerada ainda',
            loading_history: 'Carregando histórico...',
            loading_schedule: 'Carregando programação...',
            total_campaigns: 'Total de campanhas: {count}',
            results_per_page: 'Resultados por página',
            page_size_option: '{size} / página',
        },
        sod: {
            title: 'Regras de Segregação de Funções',
            new: 'Nova Regra',
            edit: 'Editar Regra',
            create_title: 'Criar regra SoD',
            edit_title: 'Editar regra SoD',
            rule_name: 'Nome da Regra',
            conflicting_permissions: 'Permissões Conflituantes',
            permission_a: 'Permissão A',
            permission_b: 'Permissão B',
            violations: 'Violações de SoD',
            violation_user: 'Utilizador',
            violation_rule: 'Regra Violada',
            violation_date: 'Detetado Em',
            exceptions: 'Exceções de SoD',
            exception_reason: 'Motivo da Exceção',
            exception_approver: 'Aprovado Por',
            exception_status: 'Status de exceção',
            has_exception: 'Tem exceção',
            no_exception: 'Sem exceção',
            grant_exception: 'Exceção de concessão',
            granting: 'Concedendo...',
            grant_new_exception: 'Conceder nova exceção',
            exceptions_for: 'Exceções para: {name}',
            back_to_rules: 'Voltar às regras',
            no_violations: 'Nenhuma violação encontrada',
            no_exceptions_found: 'Nenhuma exceção encontrada para esta regra',
            all_exception_statuses: 'Todos os status de exceção',
            user_id_label: 'ID do usuário',
            justification_label: 'Justificativa',
            compensating_controls_label: 'Controles de compensação',
            expires_at_label: 'Expira em',
            granting_exception_for: 'Concedendo uma exceção para a regra: {name}',
            severity: 'Gravidade',
            severity_warning: 'Aviso',
            severity_blocking: 'Bloqueio',
            violation_count: '{count} violação(ões)',
            exception_count: '{count} exceção(ões)',
            rule_description: 'Descrição',
            confirm_delete: 'Tem certeza de que deseja excluir esta regra?',
            deleted: 'Regra excluída.',
            framework_code: 'Enquadramento',
            name_label: 'Nome *',
            severity_label: 'Gravidade *',
            conflict_pairs_legend: 'Pares de permissão de conflito',
            conflict_pairs_hint: 'Cada par define duas permissões que não devem ser mantidas pelo mesmo usuário.',
            placeholder_permission_a: '-- Permissão A --',
            placeholder_permission_b: '-- Permissão B --',
            permission_a_for_pair: 'Permissão A para par {index}',
            permission_b_for_pair: 'Permissão B para par {index}',
            conflict_pair_label: 'Par de conflito {index}',
            remove_conflict_pair: 'Remover par conflitante {index}',
            add_pair: '+ Adicionar par',
            placeholder_name: 'por exemplo Separação de aprovação de pagamento',
            create_rule: 'Criar regra',
            loading_rule: 'Carregando regra SoD...',
            failed_load_rule: 'Falha ao carregar a regra.',
            failed_create_rule: 'Falha ao criar regra SoD.',
            failed_update_rule: 'Falha ao atualizar a regra SoD.',
            validation_name_required: 'O nome é obrigatório.',
            validation_pair_required: 'É necessário pelo menos um par de permissões de conflito.',
            validation_pair_both_required: 'Par {index}: Ambas as permissões devem ser selecionadas.',
            validation_pair_must_differ: 'Par {index}: A permissão A e a permissão B devem ser diferentes.',
            added_conflict_pair: 'Adicionado par de conflito {index}',
            removed_conflict_pair: 'Par de conflito removido {index}',
            announce_loaded_violations: 'Violações {count} carregadas',
            announce_loaded_exceptions: 'Exceções {count} carregadas',
            announce_loaded_rules: 'Regras SoD {count} carregadas',
            announce_loading_exceptions: 'Carregando exceções…',
            regulatory_info: {
                sod_context: 'As regras SoD aplicam a separacao regulamentar de funcoes exigida pela PDPL. As violacoes associadas a um framework_code podem ser incluidas nos pacotes de evidencias (UC-VN-09).',
                violation_export_context: 'As exportacoes de violacoes SoD incluem framework_code, funcoes em conflito, gravidade, criador e data de detecao para pacotes de evidencias (P-VN-10).',
                exception_context: 'As excecoes SoD produzem evidencias: aprovador, justificacao, controlos compensatorios e data de expiracao. As excecoes expiradas permanecem visiveis (UC-VN-09 Passo 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'Integração CMP',
            status: 'Estado da Ligação',
            endpoint: 'Endpoint CMP',
            last_sync: 'Última Sincronização',
            configure: 'Configurar',
        },
        purpose_mapping: {
            title: 'Mapeamento de Finalidades',
            cmp_purpose: 'Finalidade CMP',
            grc_purpose: 'Finalidade GRC',
            mapping_status: 'Estado do Mapeamento',
            auto_map: 'Mapear Automaticamente',
            new_mapping: 'Novo Mapeamento',
        },
        webhook: {
            title: 'Configuração de Webhooks',
            webhook_url: 'URL do Webhook',
            secret: 'Segredo do Webhook',
            events: 'Tipos de Eventos',
            test_webhook: 'Testar Webhook',
        },
        bulk_sync: {
            title: 'Sincronização em Massa',
            sync_now: 'Sincronizar Agora',
            last_run: 'Última Execução',
            records_synced: 'Registos Sincronizados',
            sync_history: 'Histórico de Sincronização',
            syncing: 'A sincronizar...',
        },
        sync_settings: {
            sync_failures: 'Falhas de sincronização',
            sync_secret_warning: 'Segredo de sincronização (guarde agora — não será mostrado novamente):',
            push_description: 'Configure o GRC Suite para enviar atualizações de estado de casos ao AesirX CMP.',
            technical_description: 'Esta configuração controla como o GRC Suite envia atualizações de status do caso para AesirX CMP.',
            direction_label: 'Direção',
            direction_value: 'GRC Suite → AesirX CMP (saída)',
            auth_label: 'Autenticação',
            auth_value: 'Assinatura HMAC-SHA256 no cabeçalho X-GRC-Signature',
            retry_label: 'Tentar novamente Política',
            retry_value: '3 tentativas com recuo exponencial (30s, 2min, 10min)',
            inbound_label: 'Config. de entrada',
            inbound_value: 'O webhook de entrada (CMP → GRC) está configurado em',
            cmp_webhook_settings: 'CMP Webhook Definições',
            page_title: 'Definições de Sincronização CMP',
            outbound_sync_configured: 'Sincronização de saída configurada.',
            configure_sync_failed: 'Falha ao configurar sincronização',
            configuration_updated: 'Configuração atualizada.',
            update_configuration_failed: 'Falha ao atualizar configuração',
            connection_test_failed: 'Teste de conexão falhou',
            test_result_message: 'Teste enviado para {endpoint}. Estado: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Novo → Under Review (assigned)',
                under_review_to_identity_verification: 'Under Rever → Identidade Verificação',
                under_review_to_in_progress: 'Under Rever → Em curso',
                in_progress_to_completed: 'Em curso → Concluído',
                in_progress_to_denied: 'Em curso → Negado',
                any_to_closed: 'Qualquer → Fechado',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhooks',
            new: 'Novo Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Eventos',
            secret: 'Segredo',
            active: 'Ativo',
            test: 'Testar',
            create_failed: 'Falha ao create webhook. Por favor, tente novamente.',
            create_success: 'Webhook criado com sucesso',
            deactivate_failed: 'Falha ao deactivate webhook. Por favor, tente novamente.',
            deactivate_success: 'Webhook desativado.',
            fetch_failed: 'Falha ao fetch webhooks. Por favor, tente novamente.',
            send_test_failed: 'Falha ao send test. Por favor, tente novamente.',
            test_failed: 'Webhook test falhou.',
            test_success: 'Teste do webhook com sucesso.',
            edit_title: 'Editar webhook',
            announce_loaded: 'Webhooks carregados',
            update_success: 'Webhook atualizado com sucesso.',
            update_failed: 'Falha ao atualizar o webhook.',
            activate_success: 'Webhook ativado.',
            activate_failed: 'Falha ao ativar o webhook.',
        },
        domains: {
            title: 'Domínios',
            new: 'Novo Domínio',
            domain_name: 'Nome do Domínio',
            verified: 'Verificado',
            verify: 'Verificar',
            add_failed: 'Falha ao add domain. Por favor, tente novamente.',
            add_success: 'Domínio added com sucesso',
            domain_placeholder: 'p. ex. grc.example.com',
            fetch_failed: 'Falha ao fetch domains. Por favor, tente novamente.',
            remove_failed: 'Falha ao remove domain. Por favor, tente novamente.',
            remove_success: 'Domínio removed com sucesso',
            txt_record: 'Registo TXT',
            verify_dns_failed: 'A verificação DNS falhou.',
            verify_failed: 'Falha ao verify domain. Por favor, tente novamente.',
            verify_success: 'Domínio verificado com sucesso',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Agendamento de Exportações',
            new: 'Novo Agendamento',
            schedule_name: 'Nome',
            frequency: 'Frequência',
            format: 'Formato',
            last_run: 'Última Execução',
            next_run: 'Próxima Execução',
            create_failed: 'Falha ao create schedule. Por favor, tente novamente.',
            create_success: 'Agendar criado com sucesso',
            day_of_month: 'Dia do mês',
            day_of_week: 'Dia da semana',
            deactivate_failed: 'Falha ao deactivate schedule. Por favor, tente novamente.',
            deactivate_success: 'Agendamento desativado.',
            fetch_failed: 'Falha ao fetch schedules. Por favor, tente novamente.',
            freq_daily: 'Diário',
            freq_monthly: 'Mensal',
            freq_weekly: 'Semanal',
            recipients: 'Destinatários',
            time_utc: 'Hora (UTC)',
            trigger_failed: 'Falha ao trigger export. Por favor, tente novamente.',
            trigger_success: 'Exportar triggered com sucesso.',
            type_audit_trail: 'Trilha de auditoria',
            type_compliance_report: 'Conformidade Relatório',
            type_custom: 'Personalizado',
            type_incident_report: 'Incidente Relatório',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'Registo de Correio Eletrónico',
            recipient: 'Destinatário',
            subject: 'Assunto',
            sent_at: 'Enviado Em',
            status: 'Estado',
            resend: 'Reenviar',
            fetch_failed: 'Falha ao fetch email logs. Por favor, tente novamente.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organizações',
        create: 'Adicionar organização',
        edit: 'Editar Organização',
        name: 'Nome',
        slug: 'Lesma',
        timezone: 'Fuso horário',
        locale: 'Localidade predefinida',
        parent: 'Organização principal',
        group: 'Grupo',
        org_group: 'Grupo de Organizações',
        no_group: '(Nenhum - não pertence a um grupo)',
        created: 'Criado',
        no_parent: '(Nenhum - autónomo)',
        no_orgs: 'Nenhum organizations encontrado',
        load_error: 'Falha ao load organizations. Por favor, tente novamente.',
        save_error: 'Falha ao save organization. Por favor, tente novamente.',
        created_msg: 'Organização criado',
        updated: 'Organização atualizado',
        deactivate: 'Desativar',
        deactivated: 'Organização desativada',
        confirm_deactivate: 'Tem a certeza de que pretende desativar esta organização?',
        loaded_results: 'Organizations carregados, {count} resultados',
        form_loaded: 'Formulário de Organization carregado',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'Inglês (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Todos os quadros',
        assigned_at: 'Atribuído em',
    },
    intake_configs: {
        title: 'Configurações de entrada públicas',
        create: 'Adicionar Config',
        edit: 'Editar config. de entrada',
        label_col: 'Rótulo',
        key: 'Chave de entrada',
        key_hint: 'O URL público será: /rights-request?key=',
        mapped_org: 'Organização de destino',
        status: 'Estado',
        enabled: 'Ativado',
        disabled: 'Desativado',
        request_types: 'Ativado Pedido Types',
        language: 'Predefinido Idioma',
        public_url: 'URL pública',
        no_configs: 'Nenhuma config. de entrada encontrada. Crie uma para ativar formulários de pedido de direitos.',
        load_error: 'Falha ao load intake configs. Por favor, tente novamente.',
        save_error: 'Falha ao save. Por favor, tente novamente.',
        delete_error: 'Falha ao delete. Por favor, tente novamente.',
        created_msg: 'Configuração criada',
        updated: 'Configuração atualizada',
        deleted: 'Config. de entrada eliminada',
        confirm_delete: 'Eliminar esta configuração de entrada?',
        select_types: 'Selecione at least one request type',
        select_org: 'Selecione target organization',
        routing_mode: 'Modo de Encaminhamento',
        single_org: 'Organização Única',
        org_group_mode: 'Grupo de Organizações',
        org_group: 'Grupo de Organizações',
        select_group: 'Selecione um grupo de organizações...',
        group_root_hint: 'Definido automaticamente para a organização raiz do grupo quando o grupo é selecionado.',
        loaded_results: 'Intake configurations carregados, {count} resultados',
        form_loaded: 'Formulário de Intake configuration carregado',
    },
    frequency: {
        weekly: 'Semanal',
        monthly: 'Mensal',
        quarterly: 'Trimestral',
        semi_annually: 'Semestral',
        annually: 'Anual',
        custom: 'Personalizado',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Calendário',
            all_tasks: 'Todas as tarefas',
            my_tasks: 'Minhas tarefas',
            overdue: 'Em atraso',
            board: 'Quadro',
            workflows: 'Fluxos de trabalho',
        },
        list: {
            title: 'Todas as tarefas',
            my_title: 'Minhas tarefas',
            overdue_title: 'Tarefas em atraso',
            new_task: 'Criar tarefa',
            no_tasks: 'Nenhuma tarefa encontrada.',
            empty_state: 'Nenhuma tarefa encontrada. As tarefas são geradas a partir de fluxos de trabalho de módulos como avaliações, incidentes e campanhas de revisão.',
            total: 'Total de tarefas',
            search_placeholder: 'Pesquisar tarefas...',
            bulk_actions: 'Ações em massa',
            change_status: 'Alterar estado',
            change_priority: 'Alterar prioridade',
            assign: 'Atribuir',
            sr_my_tasks_count: '{count} tarefas atribuídas a si',
            sr_days_overdue: '{count} dias em atraso',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Detalhe da tarefa',
            description: 'Descrição',
            workflow_progress: 'Progresso do fluxo',
            checklist: 'Lista de verificação',
            dependencies: 'Dependências',
            comments: 'Comentários',
            activity_timeline: 'Cronologia de atividade',
            assignees: 'Atribuídos',
            add_assignee: 'Adicionar atribuído',
            remove_assignee: 'Remover atribuído',
            due_date: 'Data de vencimento',
            start_date: 'Data de início',
            sla_status: 'Estado SLA',
            sla_on_track: 'No prazo',
            sla_at_risk: 'Em risco',
            sla_breached: 'Violado',
            tags: 'Etiquetas',
            source_entity: 'Entidade de origem',
            view_source: 'Ver origem',
            add_dependency: 'Adicionar dependência',
            remove_dependency: 'Remover dependência',
            write_comment: 'Escrever um comentário...',
            post_comment: 'Publicar',
            edit_comment: 'Editar',
            delete_comment: 'Excluir',
            add_checklist_item: 'Adicionar item',
            no_dependencies: 'Sem dependências',
            no_comments: 'Sem comentários ainda',
            blocks: 'Bloqueia',
            blocked_by: 'Bloqueado por',
            related: 'Relacionado',
            working_days_remain: 'dias úteis restantes',
            created_by: 'Criado por',
            completed_at: 'Concluído em',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Sem atribuídos',
            add_assignee_placeholder: 'Introduzir ID de utilizador...',
            edited: 'editado',
            timeline: 'Cronologia de atividade',
            no_timeline: 'Sem atividade ainda',
            timeline_reason: 'Reason',
            no_workflow: 'Sem fluxo de trabalho atribuído',
            add_dependency_placeholder: 'Introduzir ID de tarefa...',
            sr_status_updated: 'Status atualizado para {status}',
            sr_priority_updated: 'Prioridade atualizada para {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Executar como fluxo de trabalho',
            convert_modal_title: 'Converter tarefa em execução de fluxo de trabalho',
            convert_workflow_label: 'Fluxo de trabalho',
            convert_workflow_placeholder: 'Escolha um fluxo de trabalho…',
            convert_attachments_label: 'Anexos',
            convert_attachments_empty: 'Não há anexos nesta tarefa.',
            convert_persona_label: 'Substituição de persona (opcional)',
            convert_submit: 'Iniciar execução do fluxo de trabalho',
            convert_starting: 'Iniciando a execução do fluxo de trabalho…',
            convert_started: 'Execução do fluxo de trabalho iniciada — execução de abertura…',
            convert_disabled_in_flight: 'Um fluxo de trabalho executado nesta tarefa já está em andamento.',
            linked_workflow_chip: 'De {workflow_title}',
            linked_chat_chip: 'Do bate-papo AI',
            linked_more: '+{count} mais',
        },
        calendar: {
            title: 'Calendário de tarefas',
            page_title: 'Calendário',
            create_task: 'Criar tarefa',
            month: 'Mês',
            week: 'Semana',
            day: 'Dia',
            today: 'Hoje',
            more_tasks: '+{count} mais',
            no_tasks_day: 'Sem tarefas neste dia',
            all_day: 'Dia inteiro',
            quick_create_tooltip: 'Criar tarefa para este dia',
            view_changed: 'Visualização do calendário alterada para {view}',
            tasks_in_view: '{count} tarefas na visualização',
            task_in_view: '{count} tarefa em exibição',
            filter_my_tasks: 'Minhas tarefas',
            filter_overdue: 'Em atraso',
            filter_high_priority: 'Prioridade alta',
            filter_all: 'Todas',
        },
        filters: {
            assignee: 'Atribuído',
            me: 'Eu',
            role: 'Papel',
            status: 'Estado',
            priority: 'Prioridade',
            task_type: 'Tipo de tarefa',
            source_module: 'Módulo de origem',
            organization: 'Organização',
            sla_breached_only: 'Mostrar apenas SLA violado',
            clear_filters: 'Limpar filtros',
            apply_filters: 'Aplicar',
            source_label: 'Fonte',
            source_manual: 'Manuais',
            source_module_kind: 'Gerado por módulo',
            source_chat: 'Do bate-papo AI',
            source_workflow_run: 'Da execução do fluxo de trabalho AI',
        },
        status: {
            pending: 'Pendente',
            in_progress: 'Em andamento',
            blocked: 'Bloqueado',
            completed: 'Concluído',
            overdue: 'Em atraso',
            escalated: 'Escalado',
            cancelled: 'Cancelado',
        },
        priority: {
            critical: 'Crítico',
            high: 'Alto',
            medium: 'Médio',
            low: 'Baixo',
        },
        task_types: {
            manual_task: 'Tarefa manual',
            dsr_case: 'Caso DSR',
            dsr_review: 'Revisão DSR',
            dsr_escalation: 'Escalação DSR',
            dpia_review: 'Revisão DPIA',
            dpia_approval: 'Aprovação DPIA',
            transfer_review: 'Revisão de transferência',
            incident_task: 'Tarefa de incidente',
            incident_notification: 'Notificação de incidente',
            playbook_step: 'Passo do manual',
            regulator_deadline: 'Prazo regulatório',
            monitoring_task: 'Tarefa de monitoramento',
            kpi_alert: 'Alerta KPI',
            vendor_review: 'Revisão de fornecedor',
            vendor_questionnaire: 'Questionário de fornecedor',
            governance_review: 'Revisão de governança',
            drift_alert: 'Alerta de desvio',
            governance_approval: 'Aprovação de governança',
            deletion_task: 'Tarefa de exclusão',
            deletion_verification: 'Verificação de exclusão',
            access_review: 'Revisão de acesso',
            sod_alert: 'Alerta SoD',
            consent_workflow: 'Fluxo de consentimento',
            consent_alert: 'Alerta de consentimento',
            property_review: 'Revisão de propriedade',
            inconsistency_alert: 'Alerta de inconsistência',
            form_review: 'Revisão de formulário',
            dossier_deadline: 'Prazo do dossiê',
            submission_task: 'Tarefa de submissão',
            translation_review: 'Revisão de tradução',
            governance_task: 'Tarefa de governança',
            reporting_deadline: 'Prazo de relatório',
            scanner_alert: 'Alerta do scanner',
            classification_review: 'Revisão de classificação',
            lifecycle_task: 'Tarefa de ciclo de vida',
            discovery_review: 'Revisão de descoberta',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Atribuição de formação',
        task_type_groups: {
            training: 'Formação',
            manual: 'Manuais',
            dsr: 'DSR / Pedidos de direitos',
            assessments: 'Avaliações',
            incidents: 'Incidentes',
            incident_ops: 'Operações de incidentes',
            monitoring: 'Monitoramento',
            vendor_risk: 'Risco de fornecedores',
            governance: 'Governança',
            deletion: 'Exclusão',
            access_review: 'Revisão de acesso',
            consent: 'Consentimento',
            consent_properties: 'Propriedades de consentimento',
            forms: 'Formulários',
            localization: 'Localização',
            program_governance: 'Governança de programas',
            privacy_scanner: 'Scanner de privacidade',
            classification: 'Classificação',
            discovery: 'Descoberta',
            forseti: 'Forseti',
        },
        create: {
            title: 'Criar tarefa',
            task_title: 'Título',
            task_description: 'Descrição',
            task_type: 'Tipo de tarefa',
            task_type_label: 'Tipo de tarefa',
            task_type_placeholder: 'Selecionar um tipo de tarefa',
            priority: 'Prioridade',
            assignees: 'Atribuídos',
            assignee_search_placeholder: 'Pesquisar utilizadores...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Remover {name}',
            due_date: 'Data de vencimento',
            start_date: 'Data de início',
            checklist: 'Lista de verificação',
            tags: 'Etiquetas',
            add_checklist_item: 'Adicionar item de checklist',
            validation: {
                title_required: 'O título é obrigatório',
                title_max_length: 'O título deve ter 500 caracteres ou menos',
                description_max_length: 'A descrição deve ter 5000 caracteres ou menos',
                due_date_before_start: 'A data de vencimento deve ser posterior à data de início',
                max_reminders: 'Máximo 5 lembretes por tarefa',
            },
            reminders: {
                title: 'Lembretes',
                add: 'Adicionar lembrete',
                at_due_date: 'Na data de vencimento',
                minutes_before: '{count} minutos antes',
                hours_before: '{count} horas antes',
                days_before: '{count} dias antes',
                weeks_before: '{count} semanas antes',
                custom: 'Personalizado',
                channel_platform: 'Plataforma',
                channel_email: 'E-mail',
                channel_both: 'Ambos',
                offset_label: 'Lembrete {n}: momento',
                channel_label: 'Lembrete {n}: canal',
                remove_label: 'Remover lembrete {n}',
            },
            workflow: {
                label: 'Fluxo de trabalho',
                none: 'Nenhum (gerenciamento de status manual)',
                steps_count: '{count} etapas',
                preview_label: 'Passos do fluxo',
            },
        },
        errors: {
            fetch_failed: 'Falha ao carregar tarefas.',
            save_failed: 'Falha ao salvar tarefa.',
            delete_failed: 'Falha ao excluir tarefa.',
            status_change_failed: 'Falha ao alterar estado da tarefa.',
            assign_failed: 'Falha ao atribuir tarefa.',
            comment_failed: 'Falha ao salvar comentário.',
            checklist_failed: 'Falha ao atualizar a lista de verificação.',
            dependency_failed: 'Falha ao atualizar a dependência.',
            workflow_failed: 'Falha ao salvar fluxo de trabalho.',
            workflow_activate_failed: 'Falha ao ativar fluxo.',
            workflow_delete_failed: 'Falha ao excluir fluxo.',
            template_install_failed: 'Falha ao instalar modelo.',
            search_failed: 'Falha ao pesquisar tarefas.',
            convert_failed: 'Não foi possível iniciar a execução do fluxo de trabalho.',
            convert_quota_exhausted: 'Balde de tokens baixo. Complete antes de voltar a correr.',
        },
        dependency_types: {
            blocks: 'Bloqueia',
            blocked_by: 'Bloqueado por',
            related: 'Relacionado',
        },
        workflow: {
            current_step: 'Passo atual',
            transition: 'Transição',
            history: 'Histórico de passos',
            no_workflow: 'Sem fluxo de trabalho atribuído',
            detail_title: 'Detalhes do fluxo de trabalho',
            list_title: 'Definições de fluxos',
            create_workflow: 'Criar fluxo de trabalho',
            browse_templates: 'Explorar modelos',
            template_catalog_title: 'Catálogo de modelos',
            name: 'Nome',
            description: 'Descrição',
            task_types: 'Tipos de tarefa',
            steps: 'Passos',
            version: 'Versão',
            created: 'Criado',
            actions: 'Ações',
            status_draft: 'Rascunho',
            status_active: 'Ativo',
            status_archived: 'Arquivado',
            activate: 'Ativar',
            archive: 'Arquivar',
            save: 'Salvar',
            edit: 'Editar',
            delete: 'Excluir',
            clone: 'Clonar',
            install: 'Instalar',
            installed: 'Instalado',
            system: 'Sistema',
            general_settings: 'Configurações gerais',
            default_assignment: 'Atribuição padrão',
            default_sla: 'SLA padrão',
            workflow_steps: 'Passos do fluxo',
            add_step: 'Adicionar passo',
            step_name: 'Nome do passo',
            step_status: 'Valor de estado',
            assignment_strategy: 'Estratégia de atribuição',
            assignment_role: 'Papel de atribuição',
            sla_hours: 'Horas SLA',
            sla_calendar_type: 'Tipo de calendário',
            working_days: 'Dias úteis',
            calendar_days: 'Dias corridos',
            is_terminal: 'Passo terminal',
            required_fields: 'Campos obrigatórios',
            checklist_template: 'Modelo de checklist',
            transitions: 'Transições',
            add_transition: 'Adicionar transição',
            from_step: 'De',
            to_step: 'Para',
            permission: 'Permissão',
            sla_action: 'Ação SLA',
            notify: 'Notificar',
            escalation_rules: 'Regras de escalação',
            add_rule: 'Adicionar regra',
            trigger: 'Gatilho',
            threshold: 'Limiar',
            action: 'Ação',
            target_role: 'Papel alvo',
            trigger_sla_threshold: 'Limiar SLA',
            trigger_sla_breach: 'Violação SLA',
            trigger_blocked_duration: 'Duração bloqueada',
            trigger_manual: 'Manuais',
            action_notify: 'Notificar',
            action_escalate: 'Escalar nível',
            action_reassign: 'Reatribuir',
            sla_start: 'Iniciar',
            sla_pause: 'Pausar',
            sla_resume: 'Retomar',
            sla_stop: 'Parar',
            strategy_manual: 'Manuais',
            strategy_role_based: 'Baseado em papel',
            strategy_round_robin: 'Rodada Robin',
            strategy_least_loaded: 'Menos carregado',
            strategy_creator: 'Criador',
            confirm_delete: 'Tem certeza de que deseja excluir este fluxo?',
            confirm_archive: 'Tem certeza de que deseja arquivar este fluxo?',
            cannot_delete_active: 'Fluxos ativos não podem ser excluídos. Arquive primeiro.',
            validation_errors: 'Erros de validação do fluxo',
            step_count: '{count} etapas',
            no_workflows: 'Nenhum fluxo encontrado.',
            no_templates: 'Nenhum modelo disponível.',
            sr_templates_count: '{count} modelos disponíveis',
            template_installed: 'Modelo instalado com sucesso.',
            activate_success: 'Fluxo ativado com sucesso.',
            archive_success: 'Fluxo arquivado com sucesso.',
            save_success: 'Fluxo salvo com sucesso.',
            delete_success: 'Fluxo excluído com sucesso.',
            applicable_task_types: 'Tipos de tarefa aplicáveis',
            steps_column: 'Etapas',
            status_value_hint: 'Chave de estado do fluxo usada pelas transições. Letras minúsculas, números e sublinhados.',
            from_status: 'Do estado',
            to_status: 'Para o estado',
            required_permissions: 'Permissões necessárias',
            transition_required_fields: 'Campos obrigatórios (transição)',
            auto_assign: 'Atribuição automática',
            auto_assign_role: 'Função de atribuição automática',
            threshold_percent: 'Limite (%)',
            blocked_hours: 'Horas bloqueadas',
            notify_roles: 'Funções a notificar',
            escalate_to_role: 'Escalar para a função',
            notification_template: 'Modelo de notificação',
            action_escalate_tier: 'Nível de escalonamento',
            create_modal_title: 'Criar fluxo de trabalho',
            create_modal_description: 'Defina etapas, transições e regras de escalonamento. O fluxo de trabalho é criado como rascunho.',
            create_modal_footer_note: 'Criado como rascunho. Ative-o assim que o grafo for validado.',
            validation_name_required: 'O nome do fluxo de trabalho é obrigatório.',
            validation_task_types_required: 'Selecione pelo menos um tipo de tarefa aplicável.',
            validation_min_one_step: 'Adicione pelo menos uma etapa.',
            validation_min_one_transition: 'Adicione pelo menos uma transição.',
            validation_step_name_required: 'O nome da etapa é obrigatório.',
            validation_status_value_required: 'O valor do estado é obrigatório.',
            validation_status_value_duplicate: 'O valor do estado deve ser único entre as etapas.',
            validation_transition_endpoints_required: 'Selecione um estado de origem e um estado de destino.',
            validation_transition_unknown_status: 'Esta transição faz referência a um estado que nenhuma etapa define.',
            validation_sla_positive: 'As horas de SLA devem ser maiores que zero. Deixe em branco para nenhum SLA.',
            validation_threshold_percent_range: 'O limite deve estar entre 0 e 100.',
            validation_blocked_hours_positive: 'As horas bloqueadas devem ser maiores que zero.',
            warning_no_terminal_step: 'Nenhuma etapa terminal. A ativação falhará até que uma etapa seja marcada como terminal.',
            warning_step_no_outgoing: 'Etapas não terminais sem transições de saída: {statuses}',
            warning_unreachable_steps: 'Etapas inacessíveis a partir da primeira etapa: {statuses}',
            orphaned_transitions_warning: '{count} transições fazem referência a uma etapa excluída.',
            remove_orphaned_transitions: 'Remover transições órfãs',
            transitions_renamed_announcement: '{count} transições atualizadas para o novo valor de estado.',
            add_checklist_item: 'Adicionar item da lista de verificação',
            checklist_item_label: 'Item da lista de verificação',
        },
        columns: {
            priority: 'Prioridade',
            title: 'Título',
            status: 'Estado',
            type: 'Tipo',
            assignees: 'Atribuído(s)',
            due_date: 'Data de vencimento',
            source: 'Origem',
            actions: 'Ações',
        },
        groups: {
            overdue: 'Em atraso',
            due_today: 'Vence hoje',
            due_this_week: 'Vence esta semana',
            due_later: 'Vence depois',
            no_due_date: 'Sem data de vencimento',
            closed: 'Concluídas ou canceladas',
        },
        board: {
            title: 'Quadro de tarefas',
            drag_hint: 'Arraste tarefas entre colunas para alterar o estado',
            empty_column: 'Sem tarefas',
            keyboard_hint: 'Teclado: setas para navegar, espaço para pegar tarefa, esquerda/direita para selecionar coluna, Enter para soltar, Escape para cancelar.',
            sr_moving: 'Movendo {title}. Sobre {column}. Pressione Enter para soltar, Escape para cancelar.',
            sr_tasks_shown: '{count} tarefas exibidas',
            sr_picked_up: '{title} coletado',
            sr_returned: '{title} retornou para {column}',
            sr_dropped: '{title} solto em {column}, posição {position} de {total}',
            sr_move_cancelled: 'Movimentação cancelada',
            sr_over_column: 'Sobre {column}',
            sr_moved_to: '{title} movido para {column}',
            invalid_transition: 'Nao e possivel mover a tarefa de {from} para {to}.',
            terminal_status: 'Esta tarefa esta em um estado terminal e nao pode ser movida.',
            invalid_drop_target: 'Destino invalido',
        },
        team: {
            title: 'Carga de trabalho da equipa',
            assignee: 'Atribuído',
            active: 'Ativo',
            overdue: 'Em atraso',
            blocked: 'Bloqueado',
            completed_7d: 'Concluído (7d)',
            load: 'Carga',
            load_high: 'Alto',
            load_medium: 'Médio',
            load_normal: 'Normais',
        },
        graph: {
            title: 'Gráfico de Dependência',
            list_view: 'Visualização de lista',
            graph_view: 'Visualização de gráfico',
            zoom_in: 'Ampliar',
            zoom_out: 'Diminuir zoom',
            reset_zoom: 'Redefinir',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Sem dependências para visualizar.',
        },
        add_dependency_modal: {
            title: 'Adicionar dependência',
            search_placeholder: 'Pesquisar tarefas por título ou ID...',
            no_results: 'Nenhuma tarefa encontrada.',
            this_blocks: 'Esta tarefa bloqueia selecionados',
            this_blocked_by: 'Esta tarefa está bloqueada por selecionados',
            related: 'Relacionado',
            max_dependencies: 'Máximo de 10 dependências por tarefa.',
            circular_error: 'Não é possível adicionar: criaria uma dependência circular.',
        },
        conflict_detection: {
            title: 'Detecção de conflitos',
            unresolved: 'não resolvido',
            no_conflicts: 'Nenhum conflito detectado',
            section: 'Seção',
            field: 'Campo',
            dept_a: 'Departamento A',
            value_a: 'Valor A',
            dept_b: 'Departamento B',
            value_b: 'Valor B',
            severity: 'Gravidade',
            status: 'Estado',
            resolved: 'Resolvido',
            open: 'Aberto',
            resolve: 'Resolver',
            load_failed: 'Falha ao load conflicts',
            resolve_success: 'Conflito resolvido com sucesso',
            resolve_failed: 'Falha ao resolve conflict',
            severity_critical: 'Crítico',
            severity_warning: 'Aviso',
            severity_info: 'Informações',
        },
        consolidation: {
            title: 'Progresso de consolidação',
            add_child: 'Adicionar subtarefa',
            child_task_id: 'ID da subtarefa',
            department_id: 'ID do departamento',
            link: 'Vincular',
            overall_completion: 'Conclusão geral',
            departments_complete: 'Departamentos concluídos',
            overdue: 'Atrasado',
            ready: 'Pronto',
            not_ready: 'Não pronto',
            master_status: 'Status principal',
            department_breakdown: 'Detalhamento por departamento',
            department: 'Departamento',
            status: 'Estado',
            completion: 'Conclusão',
            due_date: 'Data de vencimento',
            overdue_flag: 'Atrasado',
            child_tasks: 'Subtarefas',
            task: 'Tarefa',
            load_failed: 'Falha ao load consolidation data',
            child_linked: 'Subtarefa vinculada com sucesso',
            link_failed: 'Falha ao link child task',
            confirm_unlink: 'Tem a certeza de que pretende desvincular esta subtarefa?',
            child_unlinked: 'Subtarefa desvinculada',
            unlink_failed: 'Falha ao desvincular a subtarefa',
            unlink: 'Desvincular',
            status_completed: 'Concluído',
            status_in_progress: 'Em andamento',
            status_pending: 'Pendente',
            select_child_task: 'Selecionar subtarefa',
            select_department: 'Selecionar departamento',
        },
        section_assignments: {
            title: 'Atribuições de seção',
            assign_section: 'Atribuir seção',
            section_ref: 'Ref. da seção',
            section_label: 'Rótulo da seção',
            department_id: 'ID do departamento',
            department: 'Departamento',
            assignee_id: 'ID do atribuído',
            assignee: 'Cessionário',
            mandatory: 'Obrigatório',
            assign: 'Atribuir',
            no_assignments: 'Sem atribuições de seção',
            status: 'Estado',
            completion: 'Conclusão',
            mark_complete: 'Concluir',
            load_failed: 'Falha ao load section assignments',
            assign_success: 'Seção atribuída com sucesso',
            assign_failed: 'Falha ao assign section',
            status_updated: 'Status atualizado',
            status_update_failed: 'Falha ao atualizar o estado',
            section_ref_placeholder: 'por exemplo 3.2',
            status_completed: 'Concluído',
            status_in_progress: 'Em andamento',
            status_pending: 'Pendente',
            select_department: 'Selecione o departamento',
            select_assignee: 'Selecione o responsável',
        },
        overdue_columns: {
            days_overdue: 'Dias em atraso',
        },
        regulatory_info: {
            deadline_context: 'Ao criar tarefas DPIA, as datas de vencimento devem estar alinhadas com os prazos de envio PDPL: entrada departamental = 10 dias uteis. Os prazos de ciberseguranca e telecomunicacoes tambem se aplicam.',
            overdue_deadline_context: 'Prazos regulatorios dos dados do pacote: entrada departamental PDPL = 10 dias uteis, resposta complementar = 15 dias uteis, reenvio = 10 dias uteis. Tarefas com SLA violado sao distinguidas visualmente.',
            section_assignment_context: 'Fluxo DPIA multidepartamental (PDPL-P1): o DPO atribui secoes do dossie a departamentos. Prazo de entrada departamental: 10 dias uteis.',
            template_catalog_context: 'Os modelos do sistema cobrem processos especificos da estrutura: DPIA PDPL, certificacao da Lei de Dados, resposta a incidentes de ciberseguranca, solicitacoes de autoridade de telecomunicacoes e registo de sistemas de IA. Os modelos do sistema nao sao editaveis.',
            workflow_instantiation_context: 'Os modelos instalados podem ser instanciados em fluxos de trabalho ativos. As tarefas sao criadas automaticamente com datas de vencimento calculadas.',
        },
        overdue: {
            days_overdue: 'dias em atraso',
        },
    },
    notifications: {
        bell: {
            title: 'Notificações',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Notificações',
            mark_all_read: 'Marcar tudo como lido',
            no_notifications: 'Sem notificações',
            filter: {
                all: 'All',
                unread: 'Não lido',
                tasks: 'Tarefas',
                approvals: 'Aprovações',
                alerts: 'Alertas',
            },
        },
        preferences: {
            title: 'Preferências de notificação',
            description: 'Configure como você recebe notificações',
            category: {
                label: 'Categoria',
                task_assigned: 'Tarefa atribuída',
                task_sla_approaching: 'SLA Aproximando-se',
                task_overdue: 'Tarefa atrasada',
                task_completed: 'Tarefa concluída',
                task_escalated: 'Tarefa Escalada',
                workflow_transition: 'Transição do Fluxo de Trabalho',
                approval_requested: 'Aprovação solicitada',
                comment_added: 'Comentário adicionado',
                weekly_digest: 'Resumo Semanal',
                sla_breach_report: 'SLA Relatório de violação',
                system_alert: 'Alerta do sistema',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plataforma',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Horário de silêncio',
                start: 'Iniciar',
                end: 'End',
                timezone: 'Fuso horário',
            },
            digest: {
                title: 'Frequência de resumo',
                daily: 'Diariamente',
                weekly: 'Semanalmente',
                none: 'Nenhum',
            },
            watched: {
                title: 'Entidades monitoradas',
                empty: 'Nenhuma entidade monitorada',
            },
            save: 'Salvar preferências',
            saving: 'Salvando...',
            saved_success: 'Preferências salvas com sucesso',
            load_error: 'Não foi possível carregar as preferências salvas. Mostrando padrões — as alterações serão salvas quando você clicar em Salvar.',
            enabled: 'Habilitado',
            disabled: 'Desativado',
            unwatch: 'Desassistir',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Fila de administração de notificações',
            description: 'Monitorizar o estado de entrega de e-mails e retentar mensagens falhadas',
            load_error: 'Falha ao carregar dados da fila de e-mails',
            retry_error: 'Falha ao retentar e-mail',
            filter_status: 'Filtrar por estado',
            all_statuses: 'Todos',
            refresh: 'Atualizar',
            status_queued: 'Em fila',
            status_sent: 'Enviado',
            status_failed: 'Falhado',
            status_bounced: 'Devolvido',
            status_sending: 'Sending',
            col_recipient: 'Destinatário',
            col_category: 'Categoria',
            col_template: 'Modelo',
            col_status: 'Estado',
            col_retries: 'Tentativas',
            col_created: 'Criado',
            col_sent: 'Enviado em',
            col_actions: 'Ações',
            no_items: 'Nenhum item encontrado na fila de e-mails',
            retry: 'Retentar',
            retrying: 'A retentar...',
            prev: 'Anterior',
            next: 'Seguinte',
            access_denied_title: 'Acesso de administrador necessário',
            access_denied: 'Esta página está disponível apenas para administradores. Contacte o seu administrador se precisar de acesso.',
            announce_queue_loaded: 'Fila de e-mail carregada, {count} itens',
            announce_processed: '{count} e-mails processados',
            process_now: 'Processar agora',
            processing_ellipsis: 'Processando…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'Assistência de IU',
            activate: 'Ativar',
            deactivate: 'Desativar',
            connected: 'Conectado',
            disconnected: 'Desconectado',
            disconnected_hint: 'Desconectado - execute `npm run relay` para conectar-se ao Claude CLI',
            open_panel: 'Abra o painel UI Assist',
            history: 'História',
            settings: 'Configurações',
        },
        prompt: {
            placeholder: 'Descreva a mudança que você deseja fazer…',
            send: 'Enviar',
            cancel: 'Cancelar',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Processando…',
            complete: 'Completo',
            error: 'Erro',
            abort: 'Abortar',
            abort_confirm: 'Tem certeza de que deseja abortar?',
            aborted_hint: 'A sessão foi interrompida — pode continuar a partir daqui.',
            undo: 'Desfazer',
            redo: 'Refazer',
            retry: 'Tentar novamente',
            dismiss: 'Dispensar',
        },
        label: {
            component: 'Componente',
            element: 'Elemento',
            file: 'Arquivo',
        },
        response: {
            you: 'You',
            claude: 'Cláudio',
            empty: 'Nenhuma mensagem ainda. Selecione os elementos e envie um prompt para começar.',
        },
        panel: {
            tab_conversation: 'Bate-papo',
            tab_tasks: 'Tarefas',
            tab_sprint: 'Corrida',
            tab_sessions: 'Sessões',
            tab_lobby: 'Saguão',
            follow_up: 'Acompanhamento',
            followup_placeholder: 'Faça uma pergunta complementar…',
            interrupt_placeholder: 'Escreva para interromper e redirecionar…',
            send_interrupt: 'Enviar & Interromper',
            will_interrupt: 'Interromperá a resposta atual e enviará esta em vez disso',
            expand: 'Expandir para tela cheia',
            collapse: 'Recolher para painel lateral',
            no_sessions: 'Nenhuma sessão anterior. Selecione elementos e envie uma instrução para começar.',
            session_active: 'Ativa',
            session_ended: 'Encerrada',
            session_messages: '{count} mensagens',
            switch_session: 'Mudar para esta sessão',
            load_session: 'Carregar histórico da sessão',
            session_restored: 'Sessão restaurada do histórico',
            archive_session: 'Sessão de arquivamento',
            archive_confirm: 'Arquivar esta sessão? O arquivo da sessão será excluído.',
            new_session: 'Nova sessão',
            new_session_desc: 'Use Ativar para selecionar um elemento da UI e iniciar uma nova sessão',
            start_new_session: 'Iniciar nova sessão',
            no_active_session_chat: 'Inicie uma nova sessão ou use Ativar para selecionar um elemento de UI na página para começar.',
            no_active_session_tasks: 'Inicie uma nova sessão ou selecione uma sessão existente para criar tarefas.',
            no_active_session_sprint: 'Inicie uma nova sessão ou selecione uma sessão existente para planejar um sprint.',
            previous_sessions: 'Sessões anteriores',
        },
        tasks: {
            none_found: 'Nenhuma tarefa encontrada na resposta. Peça a Claude para sugerir tarefas.',
            selected: 'selected',
            create_selected: 'Criar tarefas',
            execute_tasks: 'Executar tarefas',
            creating: 'Criando…',
            created: 'tarefas criadas',
            create_error: 'Falha ao criar tarefa',
            plan_sprint: 'Planejar Sprint',
            priority_critical_desc: 'Bloqueante — corrigir imediatamente',
            priority_high_desc: 'Importante — resolver neste sprint',
            priority_medium_desc: 'Normal — planear para próximo sprint',
            priority_low_desc: 'Desejável — candidato para backlog',
            add_custom: 'Adicionar tarefa',
            add_title_placeholder: 'Título da tarefa…',
            add_desc_placeholder: 'Descrição (opcional)',
            add_save: 'Adicionar',
            add_cancel: 'Cancelar',
        },
        sprint: {
            title: 'Planejador de sprints',
            assigned: 'assigned',
            new_placeholder: 'Nome do sprint (por exemplo, Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Pendências',
            move_to: 'Mude para…',
            empty: 'Crie um grupo de sprint e atribua tarefas a ele.',
            create_all: 'Crie todas as tarefas',
            remove: 'Remover',
            drop_here: 'Atribua tarefas do backlog abaixo',
            add_task: 'Adicionar tarefa do backlog…',
        },
        feature_disabled: 'O UI Assist não está ativado',
        permission_denied: 'Você não tem permissão para usar o UI Assist',
    },
    license: {
        title: 'Licença e Pacote',
        current_license: 'Licença Atual',
        license_key: 'Chave de licença',
        activate: 'Ativar licença',
        deactivate: 'Desativar licença',
        validate_now: 'Valide agora',
        status: 'Status da licença',
        active: 'Ativo',
        expired: 'Expirado',
        suspended: 'Suspenso',
        invalid: 'Inválido',
        pending: 'Pendente',
        no_license: 'Sem licença',
        expires_at: 'Expira',
        last_validated: 'Última validação',
        domain: 'Domínio Licenciado',
        activation_success: 'Licença ativada com sucesso.',
        activation_failed: 'Falha na ativação da licença.',
        domain_mismatch: 'O domínio da licença não corresponde ao domínio atual.',
        deactivation_success: 'Licença desativada.',
        validation_success: 'Licença validada com sucesso.',
        validation_failed: 'A validação da licença falhou.',
        enter_key: 'Insira sua chave de licença',
        confirm_deactivate: 'Tem certeza de que deseja desativar esta licença? As restrições do módulo serão aplicadas com base no nível padrão.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Pacote',
        module_column: 'Módulo',
        current_tier: 'Pacote Atual',
        light: 'Luz',
        standard: 'Padrão',
        enterprise_unlimited: 'Empresa Ilimitada',
        change_package: 'Alterar pacote',
        upgrade: 'Atualizar',
        downgrade: 'Downgrade',
        target_tier: 'Pacote de destino',
        new_license_key: 'Chave de licença para pacote de destino',
        confirm_change: 'Confirmar alteração do pacote',
        change_success: 'Pacote alterado com sucesso.',
        change_failed: 'Falha na alteração do pacote.',
        modules_included: 'Módulos incluídos',
        modules_restricted: 'Não disponível no pacote atual',
        upgrade_to_enable: 'Atualize para ativar este módulo.',
        module_availability: 'Disponibilidade do Módulo',
        comparison: 'Comparação de pacotes',
        power_users: 'Usuários avançados',
        unlimited: 'Ilimitado',
        impact_warning: 'O downgrade desativará os seguintes módulos:',
        invalid_license: 'A chave de licença fornecida não é válida para este pacote.',
        tier_mismatch: 'A chave de licença é para um nível de pacote diferente.',
    },
    board_report: {
        title: 'Relatório de Conformidade para o Conselho',
        subtitle: 'Resumo executivo de conformidade em todos os módulos',
        date_from: 'Data de início',
        date_to: 'Data de fim',
        language: 'Idioma do relatório',
        generate: 'Gerar relatório',
        generating: 'A gerar relatório...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Transferir PDF',
        close: 'Fechar relatório',
        open_board_report: 'Gerar relatório do conselho',
        close_board_report: 'Fechar relatório do conselho',
        report_period: 'Período do relatório',
        generated_at: 'Gerado em',
        generated_by: 'Gerado por',
        organization: 'Organização',
        section_rights_requests: 'Pedidos de direitos',
        section_vendors: 'Fornecedores',
        section_dpias: 'AIPD',
        section_consent: 'Políticas de consentimento',
        section_data_mapping: 'Mapeamento de dados',
        section_incidents: 'Incidentes',
        section_classification: 'Classificação de dados',
        section_governance: 'Governação',
        open: 'Aberto',
        closed: 'Fechado',
        sla_compliance: 'Conformidade SLA',
        total: 'Total',
        expired_dpa: 'DPA expirado',
        high_risk: 'Alto risco',
        completed: 'Concluído',
        overdue: 'Em atraso',
        policies_active: 'Políticas ativas',
        coverage: 'Cobertura',
        systems: 'Sistemas',
        completeness: 'Completude',
        avg_resolution_days: 'Méd. resolução (dias)',
        labeled: 'Etiquetado',
        unlabeled_systems: 'Sistemas sem etiqueta',
        compliance_score: 'Pontuação de conformidade',
        drift_events_open: 'Desvios abertos',
        no_data: 'Sem dados disponíveis para o período selecionado.',
        error_date_range: 'Selecione a data de início e a data de fim.',
        table_category: 'Categoria',
        table_metric: 'Métrica',
        table_value: 'Valor',
        showing_chart_view: 'A mostrar vista de gráfico',
        showing_table_view: 'A mostrar vista de tabela de dados',
        error_generate: 'Não foi possível gerar o relatório do conselho.',
        playbook_evidence_title: 'Evidência de execução de playbooks',
        playbook_evidence_description: 'Atividade de execução por playbook e ficheiros de evidência anexados para o período do relatório.',
        playbook_name: 'Manual',
        playbook_execution_count: 'Execuções',
        playbook_steps_total: 'Passos',
        playbook_steps_required: 'Evidência requerida',
        playbook_steps_attached: 'Evidência anexada',
        playbook_coverage_pct: 'Cobertura',
        playbook_evidence_files: 'Ficheiros de evidência',
        playbook_no_evidence: 'Sem execuções de playbook neste período.',
        playbook_download_file: 'Descarregar ficheiro',
    },
    framework_module_links: {
        title: 'Vinculação de Estrutura Regulatória',
        modules: 'Módulos Vinculados',
        add_module: 'Adicionar Módulo',
        module_code: 'Código do Módulo',
        required: 'Obrigatório',
        notes: 'Notas',
        link: 'Vincular',
        no_modules: 'Nenhum módulo vinculado',
        personas: 'Personas Vinculadas',
        add_persona: 'Adicionar Persona',
        persona_id: 'ID da Persona',
        role_description: 'Descrição do Papel',
        no_personas: 'Nenhuma persona vinculada',
    },
    framework_channels: {
        title: 'Canais de Submissão & Prazos',
        channels: 'Canais de Submissão',
        add_channel: 'Adicionar Canal',
        channel_type: 'Tipo',
        name: 'Nome',
        instructions: 'Instruções',
        active: 'Ativo',
        no_channels: 'Nenhum canal de submissão configurado',
        portal: 'Portal',
        in_person: 'Pessoalmente',
        postal: 'Correio',
        email: 'E-mail',
        add: 'Adicionar',
        deadlines: 'Prazos Regulatórios',
        add_deadline: 'Adicionar Prazo',
        label: 'Rótulo',
        days: 'Dias',
        day_type: 'Tipo de Dia',
        calendar_days: 'Dias de Calendário',
        working_days: 'Dias Úteis',
        description: 'Descrição',
        no_deadlines: 'Nenhum prazo configurado',
    },
    regulatory_updates: {
        title: 'Atualizações Regulatórias & Painel de Estruturas',
        coverage_dashboard: 'Painel de Cobertura de Estruturas',
        no_frameworks: 'Nenhuma estrutura encontrada',
        framework: 'Estrutura Regulatória',
        status: 'Estado',
        modules: 'Módulos',
        templates: 'Modelos',
        forms: 'Formulários',
        channels: 'Canais',
        deadlines: 'Prazos',
        pending: 'Atualizações Pendentes',
        updates: 'Atualizações Regulatórias',
        create_update: 'Criar Atualização',
        framework_id: 'ID da Estrutura',
        update_title: 'Título',
        description: 'Descrição',
        effective_date: 'Data de Vigência',
        create: 'Criar',
        no_updates: 'Nenhuma atualização regulatória',
        impact: 'Impacto',
        assess: 'Avaliar',
    },
    vehicle_monitoring: enVehicleMonitoring,
    record_links: enRecordLinks,
    questionnaire_seed: enQuestionnaireSeed,
    module_tips: ptModuleTips,
    framework_rights: {
        be_informed: 'Direito de ser informado',
        consent: 'Direito ao consentimento',
        refuse_consent: 'Direito de recusar o consentimento',
        withdraw_consent: 'Direito de retirar o consentimento',
        access: 'Direito de acesso',
        rectification: 'Direito de retificação',
        data_provision: 'Direito à provisão de dados',
        deletion: 'Direito à eliminação',
        restriction_of_processing: 'Direito à limitação do tratamento',
        object_to_processing: 'Direito de oposição ao tratamento',
        complaint: 'Direito de apresentar reclamação',
        denunciation: 'Direito de denúncia',
        lawsuit: 'Direito de ação judicial',
        claim_damages: 'Direito a indemnização por danos',
        request_protection_measures: 'Direito de solicitar medidas de proteção',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Diretiva 2002/58/CE (Diretiva Privacidade Eletrónica)',
            description: 'Diretiva principal para transposições nacionais de privacidade eletrônica. Abrange cookies, privacidade de comunicações eletrónicas, marketing direto. Cada estado membro deve transpor para a legislação nacional.',
        },
        EU_GDPR: {
            display_name: 'Regulamento Geral de Proteção de Dados (UE) 2016/679',
            description: 'O Regulamento Geral de Proteção de Dados (GDPR) é um regulamento da UE sobre proteção de dados e privacidade. Aplica-se diretamente em todos os estados membros da UE/EEE.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Diretiva (UE) 2022/2555 (NIS2)',
            description: 'Diretiva pai para transposições nacionais NIS2. Abrange gerenciamento de riscos de segurança cibernética, relatórios de incidentes e segurança da cadeia de suprimentos para entidades essenciais e importantes.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Lei do Vietnã AI (134/2025/QH15) - Em vigor em 1º de março de 2026',
            description: 'Lei do Vietnã AI (Luật Trí tuệ nhân tạo 134/2025/QH15) que rege o desenvolvimento, implantação e uso de sistemas de inteligência artificial. Estabelece deveres de classificação de risco, transparência e rotulagem, requisitos de notificação de incidentes, obrigações de avaliação de impacto e um mecanismo de testes controlados (sandbox) para atividades AI no Vietnã.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Lei de Segurança Cibernética do Vietnã (era 24/2018)',
            description: 'Lei de Cibersegurança do Vietname (Lei n.º 24/2018/QH14) que estabelece requisitos para segurança cibernética, localização de dados e comunicação de incidentes. Ativo até ser substituído pela Lei 116/2025/QH15 em vigor em 01/07/2026.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Decreto 53 – Implementação da Lei de Segurança Cibernética',
            description: 'Decreto 53/2022/ND-CP que implementa a Lei de Segurança Cibernética, abrangendo determinação dentro do escopo, gatilhos de localização de dados, requisitos de presença local e fluxos de trabalho de cooperação entre autoridades.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Lei de Segurança Cibernética (116/2025/QH15)',
            description: 'Lei consolidada do Vietnã sobre segurança cibernética (Lei 116/2025/QH15), substituindo a Lei 24/2018/QH14, em vigor em 1º de julho de 2026. Consolida o escopo do Decreto 53/2022/NĐ-CP (ver VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Lei de Dados do Vietnã',
            description: 'Lei de Dados do Vietnã (Lei 60/2024/QH15) que estabelece requisitos abrangentes de governança de dados, incluindo inventário de dados, classificação, governança de compartilhamento e governança de transferência transfronteiriça de dados. Sobrepõe-se a VN_PDPL_LAW_2025 (aspectos de dados pessoais) e VN_CYBERSEC_LAW_2025 (localização/classificação de dados).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Decreto 165/2025/NĐ-CPĐ-CP (Implementação da Lei de Dados)',
            description: 'Decreto 165/2025/NĐ-CP que implementa a Lei de Dados do Vietnã (Lei 60/2024/QH15). Fornece formulários oficiais para avaliação de impacto de transferência transfronteiriça de dados, relatórios anuais de avaliação de risco e acesso a bancos de dados nacionais. Define 11 formulários oficiais de Mẫu số.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Projeto de Decreto sobre Operações de Bolsas de Dados 2026',
            description: 'Projeto de decreto relativo ao funcionamento de plataformas de intercâmbio de dados (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Regula a classificação da plataforma, elegibilidade dos participantes, controles de listagem de produtos e serviços de dados, testes de sandbox, fluxo de trabalho de transações, contratação eletrônica, certificados de direitos de propriedade sobre dados e tratamento de reclamações/disputas. Atualmente em consulta pública; ainda não promulgado. Cita Lei de Dados 60/2024/QH15, Lei de Proteção de Dados Pessoais 91/2025/QH15, Lei de Segurança Cibernética 116/2025/QH15 e Lei de Transações Eletrônicas 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Lei do Comércio Eletrônico (122/2025/QH15)',
            description: 'Lei do Vietnã sobre Comércio Eletrônico (Luật Thương mại điện tử 122/2025/QH15) que estabelece um regime nacional unificado para o comércio eletrônico, abrangendo operações de plataforma nacionais e transfronteiriças, governança e responsabilidade da plataforma, proteção ao consumidor, controles de vendedores e afiliados, tratamento de reclamações, moderação de conteúdo e aplicação de remoção, e prontidão de procedimentos administrativos para o sistema estadual de gerenciamento de comércio eletrônico.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Decreto 13/2023/NĐ-CP',
            description: 'Decreto 13/2023/ND-CP, o instrumento operacional para funções de PDPL, incluindo arquivamento de dossiês, artefatos de governança, mecânica transfronteiriça e procedimentos administrativos MoPS.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Lei de Proteção de Dados Pessoais (91/2025/QH15)',
            description: 'Lei de Proteção de Dados Pessoais do Vietnã (Lei 91/2025/QH15) que estabelece uma estrutura abrangente de proteção de dados pessoais, em vigor a partir de 1º de janeiro de 2026. Abrange temas de conformidade A–J: bases de processamento legais, consentimento de dados confidenciais, direitos do titular dos dados (acesso/retificação/apagamento/restrição/objeção/portabilidade), notificação de violação às autoridades e aos titulares dos dados, DPIA para processamento de alto risco, governança de transferência transfronteiriça, Designação DPO, registros de atividades de processamento, obrigações contratuais do processador. Obrigações de apresentação de autoridade via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Referências cruzadas: VN_PDPL_DECREE_356_2025 (decreto de implementação), VN_CYBERSEC_LAW_2025 (sobreposições de segurança cibernética), VN_DATA_LAW (governança de dados).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Decreto 356/2025/ND-CP (PDPL Implementação)',
            description: 'Decreto 356/2025/ND-CP que implementa a Lei de Proteção de Dados Pessoais (Lei 91/2025/QH15). Abrange arquivamento de dossiês, artefatos de governança, mecânica transfronteiriça e certificação de prestadores de serviços. Inventário do formulário anexo: Phụ lục I–VIII (avaliação de impacto, transferência transfronteiriça, certificação). Procedimentos administrativos MoPS via VN_PDPL_ADMIN_PROCEDURES_778_2026 (Decisão 778/QĐ-BCA-A05). Fluxo de trabalho de certificação: aplicação → emissão → solicitação de alteração → reemissão → revogação. Referências cruzadas: VN_PDPL_LAW_2025 (lei parental), VN_CYBERSEC_LAW_2025 (localização de dados), VN_DATA_LAW (sobreposições de governança de dados).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS Procedimentos Administrativos A05 (Decisão 778/QĐ-BCA-A05)',
            description: 'Decisão 778/QĐ-BCA-A05 (fevereiro de 2026) — Procedimentos administrativos para proteção de dados pessoais no âmbito A05 (MPS). Define procedimentos de envio de dossiês, processamento e emissão de resultados, incluindo formulários DLCN-HC e formulários estatutários Mẫu số.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Projeto de Decreto de Execução 2026',
            description: 'Projeto de decreto de aplicação e sanções por violações da Lei de Proteção de Dados Pessoais (Lei 91/2025/QH15). Define faixas de penalidade, fatores agravantes/atenuantes e procedimentos de execução. Atualmente em estado de projeto pendente de promulgação.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Lei de Telecomunicações do Vietnã de 2023',
            description: 'Lei de Telecomunicações do Vietnã de 2023 (Luật Viễn thông), uma sobreposição setorial para empresas de telecomunicações/ISP/plataformas com obrigações de confidencialidade, controle de divulgação e tratamento de solicitações de autoridade. Referências cruzadas: VN_CYBERSEC_LAW_2025 (segurança do sistema de informação), VN_PDPL_LAW_2025 (processamento de dados do usuário), VN_DATA_LAW (governança de dados).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Decreto 163/2024/NĐ-CPĐ-CP (Implementação da Lei de Telecomunicações)',
            description: 'Decreto 163/2024/NĐ-CP que implementa a Lei das Telecomunicações (Lei 24/2023/QH15). Fornece formulários oficiais para notificação de serviços de telecomunicações, licenciamento, registro e planos de desenvolvimento de data center. Define 7 formulários oficiais de Mẫu số.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Reclamações, disputas e gestão de riscos',
            description: 'Os operadores de plataforma devem fornecer recepção de reclamações, fluxo de trabalho de tratamento de disputas com preservação de provas, identificação de riscos e controlos de alerta precoce, e revisão periódica dos riscos de privacidade, segurança cibernética e gestão de acesso.',
        },
        vn_dp_draft_e: {
            title: 'Fluxo de trabalho de transação e certificado de direitos de propriedade sobre dados',
            description: 'As transações exigem registro de conta, aprovação de listagem, contratação eletrônica, confirmação de pagamento, registro de entrega e emissão de certificado de direitos de propriedade sobre os dados, quando aplicável. O histórico e a rastreabilidade das transações devem ser preservados em um registro de transações.',
        },
        vn_dp_draft_d: {
            title: 'Sandbox e controles de ambiente de teste',
            description: 'Os ambientes de teste devem ser isolados, com acesso restrito, monitorados em termos de segurança e revisados antes de qualquer saída ser extraída. Nenhum dado de origem pode ser extraído do ambiente de teste e o encerramento/limpeza das sessões de teste deve ser registrado.',
        },
        vn_dp_draft_c: {
            title: 'Controles de listagem de produtos e serviços de dados',
            description: 'As listagens devem ser apoiadas por evidências de origem legal e de propriedade/direito de uso, registros de validação técnica, divulgações de qualidade e registros de aprovação e revisão de listagem na plataforma. Os dados proibidos devem ser filtrados antes da listagem.',
        },
        vn_dp_draft_b: {
            title: 'Elegibilidade e integração do participante',
            description: 'Os operadores de plataforma devem verificar o estatuto jurídico, a capacidade jurídica e os requisitos comerciais condicionais para vendedores e compradores, registar contas de transações e pagamentos e manter um registo de participantes.',
        },
        vn_dp_draft_a: {
            title: 'Classificação da plataforma de dados e modelo operacional',
            description: 'Os operadores de plataforma devem estabelecer uma classificação do tipo de plataforma (plataforma de dados nacional versus outras plataformas de dados), definir a elegibilidade e as responsabilidades do operador e documentar preços, taxas, regras de operação e governação técnica.',
        },
        d165_r5: {
            title: 'Acesso às informações do banco de dados nacional',
            description: 'Os pedidos para extrair/utilizar informações da base de dados nacional de dados digitais utilizam formulários prescritos (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Relatório anual de avaliação de risco',
            description: 'Os relatórios anuais de avaliação de risco devem ser preenchidos usando os formulários prescritos (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Aviso de rescisão de transferência transfronteiriça',
            description: 'A rescisão da transferência transfronteiriça deve ser notificada através de formulários prescritos (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Notificação do resultado da avaliação',
            description: 'A autoridade emite avisos de resultados de avaliação usando o formulário prescrito (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Arquivamento de avaliação de impacto de transferência transfronteiriça de dados',
            description: 'As organizações devem apresentar dossiês de avaliação de impacto de transferência/processamento de dados transfronteiriços usando formulários prescritos (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Consentimento para cookies e tecnologias semelhantes',
            description: 'O armazenamento ou acesso a informação num equipamento terminal só é permitido mediante consentimento prévio e informado, exceto para armazenamento tecnicamente necessário.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Confidencialidade das comunicações',
            description: 'Os Estados-Membros devem garantir a confidencialidade das comunicações e dos dados de tráfego conexos através de uma rede pública de comunicações e de serviços de comunicações eletrónicas acessíveis ao público.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Consentimento para marketing direto (opt-in)',
            description: 'A utilização de sistemas de comunicações eletrónicas para marketing direto requer o consentimento prévio do assinante.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Aceitação suave para clientes existentes',
            description: 'O marketing direto para clientes existentes para produtos/serviços semelhantes é permitido sem consentimento, se a opção de exclusão for fornecida.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Notificação de violação para provedores de telecomunicações',
            description: 'Os prestadores de serviços de comunicações eletrónicas acessíveis ao público devem notificar a autoridade nacional competente das violações de dados pessoais.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Direito de oposição ao marketing direto',
            description: 'Os assinantes terão a oportunidade de se oporem ao recebimento de mensagens de marketing direto a qualquer momento e gratuitamente.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Retenção e eliminação de dados de tráfego',
            description: 'Os dados de tráfego relativos a assinantes e utilizadores devem ser apagados ou tornados anónimos quando já não forem necessários para a transmissão, exceto para faturação ou pagamentos de interligação.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Condições de processamento de dados de localização',
            description: 'Os dados de localização que não sejam dados de tráfego só poderão ser tratados quando tornados anónimos ou com o consentimento do utilizador, na medida e pela duração necessária.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Legalidade do processamento — consentimento',
            description: 'O tratamento só será lícito se o titular dos dados tiver dado consentimento para o tratamento dos seus dados pessoais para uma ou mais finalidades específicas.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Legalidade do processamento — contrato',
            description: 'O tratamento é necessário para a execução de um contrato do qual o titular dos dados é parte.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Legalidade do tratamento — obrigação legal',
            description: 'O tratamento é necessário para o cumprimento de uma obrigação legal a que o responsável pelo tratamento esteja sujeito.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Legalidade do tratamento — interesses vitais',
            description: 'O tratamento é necessário para proteger os interesses vitais do titular dos dados ou de outra pessoa singular.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Legalidade do tratamento — interesse público',
            description: 'O tratamento é necessário para o desempenho de uma tarefa de interesse público ou no exercício de autoridade oficial.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Legalidade do tratamento — interesses legítimos',
            description: 'O tratamento é necessário para efeitos dos interesses legítimos prosseguidos pelo responsável pelo tratamento ou por um terceiro.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Processamento de categorias especiais (dados sensíveis)',
            description: 'O tratamento de categorias especiais de dados pessoais (origem racial, opiniões políticas, saúde, etc.) é proibido, salvo condições específicas do art. 9(2) são atendidos.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Condições para consentimento',
            description: 'O controlador deve demonstrar que o consentimento foi dado. O consentimento deve ser dado livremente, específico, informado e inequívoco. A retirada deve ser tão fácil quanto dar consentimento.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Consentimento da criança — serviços digitais',
            description: 'O tratamento de dados pessoais de uma criança com idade inferior a 16 anos (ou idade inferior definida pelo Estado-Membro, não inferior a 13 anos) requer o consentimento dos pais para serviços da sociedade da informação.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Informação e comunicação transparentes',
            description: 'O Controlador fornecerá informações relativas ao processamento de forma concisa, transparente, inteligível e facilmente acessível.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Informações para coleta direta',
            description: 'Quando os dados pessoais forem coletados do titular dos dados, o responsável pelo tratamento fornecerá informações específicas no momento da coleta.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Informações para cobrança indireta',
            description: 'Caso os dados pessoais não tenham sido obtidos do titular dos dados, o responsável pelo tratamento fornecerá informações específicas dentro de um prazo razoável.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Direito de acesso',
            description: 'O titular dos dados tem o direito de obter a confirmação do tratamento e do acesso aos dados pessoais e informações complementares.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Direito à retificação',
            description: 'O titular dos dados tem o direito de obter a retificação dos dados pessoais inexatos sem demora injustificada.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Direito ao apagamento (direito ao esquecimento)',
            description: 'O titular dos dados terá o direito de obter o apagamento dos dados pessoais sem demora injustificada, sempre que se apliquem motivos específicos.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Direito à restrição do processamento',
            description: 'O titular dos dados terá o direito de obter a restrição do processamento quando se aplicarem condições específicas.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Obrigação de notificação — retificação/apagamento/restrição',
            description: 'O Controlador comunicará qualquer retificação, apagamento ou restrição a cada destinatário a quem os dados foram divulgados.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Direito à portabilidade dos dados',
            description: 'O titular dos dados tem o direito de receber os seus dados pessoais num formato estruturado, de uso corrente e legível por máquina.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Direito de oposição',
            description: 'O titular dos dados terá o direito de se opor ao tratamento baseado no interesse público ou em interesses legítimos, incluindo a definição de perfis.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Tomada de decisão individual automatizada, incluindo criação de perfil',
            description: 'O titular dos dados tem o direito de não ficar sujeito a uma decisão baseada exclusivamente num tratamento automatizado que produza efeitos jurídicos ou igualmente significativos.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Notificação de violação à autoridade de supervisão (72h)',
            description: 'Em caso de violação de dados pessoais, o responsável pelo tratamento notificará a autoridade de controlo no prazo de 72 horas após tomar conhecimento, a menos que seja pouco provável que resulte em risco.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Notificação de violação aos titulares dos dados (alto risco)',
            description: 'Quando uma violação for suscetível de resultar num elevado risco para os direitos e liberdades, o responsável pelo tratamento deve comunicar a violação ao titular dos dados sem demora injustificada.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Resposta às solicitações do titular dos dados (30 dias)',
            description: 'O Controlador responderá às solicitações do titular dos dados sem demora injustificada e no prazo de um mês após o recebimento, prorrogável por mais dois meses para solicitações complexas.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Avaliação de impacto na proteção de dados',
            description: 'Quando o processamento for suscetível de resultar em um alto risco para os direitos e liberdades, o responsável pelo tratamento deverá realizar um DPIA antes do processamento.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Consulta prévia à autoridade de supervisão',
            description: 'O controlador deverá consultar a autoridade supervisora quando um DPIA indicar que o processamento resultaria em alto risco sem medidas de mitigação.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA lista de processamento obrigatório',
            description: 'DPIA é necessário para avaliação/criação de perfil sistemática, processamento em larga escala de categorias especiais e monitoramento sistemático de áreas públicas.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Registros de atividades de processamento (controlador)',
            description: 'Cada controlador deverá manter um registro das atividades de processamento sob sua responsabilidade, incluindo finalidades, categorias, destinatários, transferências e retenção.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Registros de atividades de processamento (processador)',
            description: 'Cada subcontratante deverá manter um registo de todas as categorias de atividades de tratamento realizadas em nome de um responsável pelo tratamento.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Segurança de processamento',
            description: 'O controlador e o processador devem implementar medidas técnicas e organizacionais adequadas para garantir a segurança adequada ao risco.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Proteção de dados desde a concepção e por defeito',
            description: 'O Controlador deve implementar medidas adequadas para garantir que apenas os dados pessoais necessários para cada finalidade específica sejam processados (minimização de dados).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Designação do responsável pela proteção de dados',
            description: 'O controlador e o processador designarão um DPO onde o processamento for realizado por uma autoridade pública, envolver monitoramento regular e sistemático ou processamento em grande escala de categorias especiais.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Princípio geral para transferências',
            description: 'Qualquer transferência de dados pessoais para um país terceiro só ocorrerá se as condições do Capítulo V forem cumpridas.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Transferências baseadas em decisão de adequação',
            description: 'Uma transferência pode ocorrer se a Comissão decidir que o país terceiro garante um nível de proteção adequado.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Transferências sujeitas a salvaguardas adequadas',
            description: 'Na ausência de uma decisão de adequação, as transferências podem ocorrer quando são fornecidas salvaguardas adequadas, incluindo SCC ou BCR.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Cláusulas contratuais padrão',
            description: 'As cláusulas contratuais-tipo adotadas pela Comissão ou por uma autoridade de supervisão proporcionam salvaguardas adequadas para as transferências internacionais.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Regras corporativas vinculativas',
            description: 'As regras empresariais vinculativas aprovadas por uma autoridade de supervisão competente proporcionam salvaguardas adequadas para as transferências internacionais intragrupo.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Derrogações para situações específicas',
            description: 'Na ausência de adequação ou salvaguardas, as transferências podem ocorrer com base no consentimento explícito, necessidade contratual, interesse público, reivindicações legais, interesses vitais ou registo público.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Responsabilidade e treinamento do órgão de administração',
            description: 'Os órgãos de gestão de entidades essenciais e importantes devem aprovar medidas de gestão de riscos de cibersegurança e receber formação regular.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Medidas de gestão de riscos de cibersegurança',
            description: 'As entidades essenciais e importantes devem tomar medidas técnicas, operacionais e organizacionais adequadas e proporcionais para gerir os riscos de cibersegurança.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Políticas e procedimentos de tratamento de incidentes',
            description: 'As entidades devem implementar políticas sobre tratamento de incidentes, incluindo procedimentos de detecção, análise, contenção e resposta.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Continuidade de negócios e gestão de crises',
            description: 'As entidades devem implementar medidas de continuidade de negócios, incluindo gestão de backup, recuperação de desastres e gestão de crises.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Segurança da cadeia de suprimentos',
            description: 'As entidades devem abordar os aspetos relacionados com a segurança das relações com fornecedores diretos e prestadores de serviços, incluindo avaliações de risco da cadeia de abastecimento.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Segurança em redes e sistemas de informação',
            description: 'As entidades devem implementar segurança na aquisição, desenvolvimento e manutenção de redes e sistemas de informação, incluindo o tratamento de vulnerabilidades.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Tratamento e divulgação de vulnerabilidades',
            description: 'As entidades devem implementar políticas e procedimentos para divulgação e tratamento coordenado de vulnerabilidades.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Eficácia da avaliação de riscos de segurança cibernética',
            description: 'As entidades devem implementar políticas e procedimentos para avaliar a eficácia das medidas de gestão de riscos de cibersegurança.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Criptografia e políticas de criptografia',
            description: 'As entidades devem implementar políticas e procedimentos relativos ao uso de criptografia e, quando apropriado, criptografia.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'Segurança de RH, controle de acesso, gerenciamento de ativos',
            description: 'As entidades devem implementar políticas de segurança de recursos humanos, de controlo de acessos e de gestão de ativos.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Autenticação multifator',
            description: 'As entidades devem usar soluções de autenticação multifatorial ou de autenticação contínua, comunicações seguras de voz/vídeo/texto e sistemas seguros de comunicação de emergência.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Aviso prévio ao CSIRT (24h)',
            description: 'As entidades devem enviar um alerta precoce ao CSIRT competente no prazo de 24 horas após tomarem conhecimento de um incidente significativo.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Notificação de incidente ao CSIRT (72h)',
            description: 'As entidades devem apresentar uma notificação de incidente ao CSIRT competente no prazo de 72 horas após tomarem conhecimento de um incidente significativo.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Relatório final para CSIRT (1 mês)',
            description: 'As entidades devem apresentar um relatório final ao CSIRT competente o mais tardar um mês após a notificação do incidente.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Determinação de aplicabilidade para atividades AI',
            description: 'Determine se a organização realiza atividades AI sujeitas à Lei do Vietnã AI e documente a decisão de aplicabilidade.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI classificação de risco do sistema por nível de risco',
            description: 'Classifique cada sistema AI por nível de risco (mínimo, limitado, alto, inaceitável) usando a metodologia de classificação guiada.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI Manutenção de entrada de registro do sistema',
            description: 'Mantenha um registro de sistemas AI com nome do sistema, proprietário, finalidade, contexto de implantação, nível de risco e envolvimento do fornecedor.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI Dossiê do sistema com lista de verificação de artefatos e evidências',
            description: 'Crie e mantenha um dossiê do sistema com lista de verificação de artefatos necessários, biblioteca de evidências e histórico de versões.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Obrigações de transparência para operadores de sistema AI',
            description: 'Implementar medidas de transparência para sistemas AI, incluindo notificação ao usuário, divulgação do envolvimento de AI e documentação do sistema.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: "AI rotulagem de saída, marca d'água e assinatura de metadados",
            description: "Implementar rotulagem e marca d'água para conteúdo gerado por AI, incluindo assinatura de metadados, quando aplicável.",
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Requisitos de divulgação para conteúdo gerado por AI',
            description: 'Divulgar aos usuários quando o conteúdo é gerado por AI e fornecer mecanismos para que os usuários identifiquem o envolvimento de AI.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Explicabilidade e deveres de transparência voltados para o usuário',
            description: 'Forneça resultados explicáveis para decisões AI que afetam indivíduos e mantenha documentação de transparência voltada para o usuário.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Obrigações de supervisão humana para sistemas AI de alto risco',
            description: 'Garantir que mecanismos de supervisão humana estejam em vigor para sistemas AI de alto risco, incluindo recursos de escalonamento e substituição.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI monitoramento e detecção de incidentes',
            description: 'Implemente recursos de monitoramento para detectar incidentes, mau funcionamento e resultados adversos do sistema AI.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Relatório de incidente grave AI à autoridade',
            description: 'Relate incidentes graves AI à autoridade competente dentro do prazo prescrito (72 horas).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI conformidade com o cronograma de notificação de incidentes',
            description: 'Cumpra os cronogramas de notificação para incidentes AI, incluindo notificação inicial, atualizações e relatórios de encerramento.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI avaliação de impacto para sistemas de alto risco',
            description: 'Realizar avaliações de impacto para sistemas AI de alto risco cobrindo riscos, mitigações, supervisão humana e planos de monitoramento.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Testes controlados/determinação de elegibilidade de sandbox',
            description: 'Determine a elegibilidade para participação em testes controlados (sandbox) e documente a avaliação de elegibilidade.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Envio de aplicativos sandbox e rastreamento de aprovação',
            description: 'Envie solicitações de sandbox à autoridade competente e acompanhe o status de aprovação durante todo o ciclo de vida do envio.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Pontos de verificação de monitoramento de sandbox e conclusão/saída',
            description: 'Realize pontos de verificação de monitoramento periódicos durante a participação no sandbox e prepare relatórios de conclusão/saída.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Rotulagem da documentação de isenção/exceção com justificativa',
            description: 'Documente quaisquer isenções ou exceções de rotulagem com justificativa e evidências de apoio.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Localização de dados para usuários vietnamitas',
            description: 'Os dados dos utilizadores vietnamitas sobre serviços prestados no Vietname devem ser armazenados no território do Vietname.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Relatório de incidentes de segurança cibernética',
            description: 'Os incidentes de cibersegurança devem ser comunicados às autoridades competentes em tempo útil.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Classificação do sistema de informação',
            description: 'Os sistemas de informação devem ser classificados por nível de importância e aplicadas medidas de segurança adequadas.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Avaliação de risco de segurança cibernética',
            description: 'Avaliações regulares de risco de segurança cibernética devem ser realizadas para sistemas de informação.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Determinação no escopo (árvore de decisão)',
            description: 'As organizações devem determinar se estão no âmbito do Decreto 53 utilizando a árvore de decisão prescrita.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Avaliação e preparação da localização de dados',
            description: 'As organizações devem avaliar os requisitos de localização de dados e demonstrar prontidão.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Lista de verificação de preparação para armazenamento e retenção',
            description: 'Uma lista de verificação de prontidão para armazenamento e retenção deve ser preenchida para conformidade com a localização de dados.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Avaliação de requisitos de presença local',
            description: 'As organizações devem avaliar se os requisitos de presença local se aplicam e planejar adequadamente.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Recebimento e verificação de solicitação de autoridade',
            description: 'As solicitações de autoridade devem ser recebidas, verificadas e processadas de acordo com os procedimentos prescritos.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Resposta da solicitação da autoridade dentro do prazo',
            description: 'As respostas aos pedidos das autoridades devem ser fornecidas dentro do prazo prescrito.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Log de tratamento de solicitação de autoridade',
            description: 'Deve ser mantido um registo de todas as atividades de tratamento de pedidos de autoridade.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Aplicabilidade e determinação dentro do escopo (consolidado)',
            description: 'As organizações devem determinar se estão abrangidas pela lei consolidada de cibersegurança, abrangendo tanto o âmbito da antiga Lei 24/2018 como o do Decreto 53/2022.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Classificação do sistema de informação por nível de importância',
            description: 'Os sistemas de informação devem ser classificados por nível de importância (geral, importante, crítico) com as correspondentes obrigações de segurança.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Estrutura de governança da segurança cibernética e pessoal responsável',
            description: 'As organizações devem estabelecer estruturas de governação da segurança cibernética e designar pessoal responsável.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Avaliação periódica de riscos de segurança cibernética',
            description: 'Avaliações regulares de risco de segurança cibernética devem ser realizadas para todos os sistemas de informação classificados.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Medidas de segurança por classificação de sistema de informação',
            description: 'Devem ser aplicadas medidas técnicas de segurança adequadas a cada nível de classificação do sistema de informação.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Controle de acesso e gerenciamento de identidade',
            description: 'Devem ser implementadas medidas de controlo de acesso e gestão de identidade para os sistemas de informação.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Registro, monitoramento e trilha de auditoria',
            description: 'Os sistemas de informação devem manter recursos de registro, monitoramento e trilha de auditoria.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Gerenciamento de vulnerabilidades e patches',
            description: 'Os processos de gerenciamento de vulnerabilidades e patches devem ser estabelecidos e mantidos.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Localização de dados para dados de usuários vietnamitas',
            description: 'Os dados dos utilizadores vietnamitas sobre serviços prestados no Vietname devem ser armazenados no território do Vietname.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Requisitos de presença local para prestadores de serviços estrangeiros',
            description: 'Os prestadores de serviços estrangeiros que operam no Vietname devem manter a presença local conforme prescrito.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Arquitetura de armazenamento local e documentação de fluxo internacional',
            description: 'A arquitetura de armazenamento local deve ser documentada e os fluxos de dados transfronteiriços devem ser mapeados e controlados.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Detecção e resposta a incidentes de segurança cibernética',
            description: 'As organizações devem ter capacidades para detectar e responder a incidentes de segurança cibernética.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Comunicação obrigatória de incidentes à autoridade competente',
            description: 'Incidentes de segurança cibernética qualificados devem ser relatados a MPS/MIC dentro dos prazos prescritos.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Cumprimento do cronograma de notificação de incidentes',
            description: 'As notificações de incidentes devem cumprir os requisitos de cronograma prescritos (relatório inicial em 24 horas, relatório completo em 72 horas).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Tratamento e cooperação de solicitações de autoridade',
            description: 'As solicitações da autoridade devem ser tratadas prontamente e a cooperação fornecida de acordo com os procedimentos prescritos.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Registro de caso de solicitação de autoridade e registro de divulgação',
            description: 'Todas as solicitações e divulgações de autoridade devem ser registradas em um registro de caso com trilha de auditoria completa.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Avaliação de segurança de fornecedores e terceiros',
            description: 'Os fornecedores terceirizados com acesso a sistemas de informação devem passar por uma avaliação de segurança.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Acesso a dados do fornecedor e controles de notificação de incidentes',
            description: 'O acesso aos dados do fornecedor deve ser documentado e as obrigações contratuais de notificação de incidentes devem ser estabelecidas.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Treinamento e exercícios de conscientização sobre segurança',
            description: 'Devem ser realizados treinamentos regulares de conscientização sobre segurança cibernética e exercícios práticos.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Pen test periódico e auditoria de segurança',
            description: 'Testes de penetração periódicos e auditorias de segurança devem ser realizados em sistemas de informação classificados.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Manter inventário de dados digitais',
            description: 'As organizações devem manter um inventário de dados digitais abrangente, catalogando todos os ativos de dados.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Classificação de dados por sensibilidade e importância',
            description: 'Os dados devem ser classificados de acordo com os níveis de sensibilidade e importância definidos pela Lei de Dados.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Definir regras de tratamento por nível de classificação',
            description: 'Devem ser estabelecidas regras de tratamento para cada nível de classificação de dados.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Acompanhe o compartilhamento e divulgação de dados com aprovações',
            description: 'Todas as atividades de compartilhamento e divulgação de dados devem ser acompanhadas com as aprovações apropriadas.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Governança de transferência de dados transfronteiriça',
            description: 'As transferências transfronteiriças de dados devem cumprir os requisitos de governação previstos na Lei de Dados.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Manter políticas e responsabilidades de governança',
            description: 'As organizações devem manter políticas de governação de dados e atribuir responsabilidades de governação.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Atestado anual de governança',
            description: 'Um atestado anual de governança deve ser preenchido para confirmar a conformidade contínua.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Avaliação de risco de dados',
            description: 'Uma avaliação de risco de dados deve ser realizada para identificar e mitigar os riscos de governança de dados.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Responsabilidade do operador da plataforma e obrigações de governação',
            description: 'Estabelecer estruturas de responsabilização dos operadores de plataforma, incluindo políticas de governação, responsabilidades de monitorização e obrigações de aplicação.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Publicação de regras da plataforma e transparência de informações',
            description: 'Publique regras da plataforma, condições de negociação e divulgações operacionais de maneira transparente e acessível.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'Notificação de site/aplicativo de vendas de comércio eletrônico para MOIT',
            description: 'Envie notificação ao MOIT para sites e aplicativos de vendas de comércio eletrônico via Online.gov.vn ou canais designados.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'Registro de plataforma/marketplace de serviços de comércio eletrônico no MOIT',
            description: 'Registre plataformas e mercados de serviços de comércio eletrônico no MOIT, incluindo envio de dossiês e rastreamento de aprovação.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Manutenção de cadastro de vendedores (nacionais e estrangeiros)',
            description: 'Mantenha um registro de todos os vendedores na plataforma, incluindo vendedores nacionais e estrangeiros, com integração, verificação e acompanhamento do ciclo de vida.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Registro de vendedor ao vivo e controles de conformidade',
            description: 'Mantenha um registro de vendedores de transmissão ao vivo com verificação de identidade, rastreamento de atividades e controles de conformidade.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Registro de afiliados e parceiros de marketing',
            description: 'Mantenha um registro de comerciantes afiliados e parceiros de referência com status de contrato, diretrizes de conteúdo e reconhecimentos de conformidade.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Listagem de produtos e aplicação de mercadorias proibidas/condicionais',
            description: 'Implementar controles de listagem de produtos, incluindo triagem, monitoramento e ações de fiscalização de mercadorias proibidas e condicionais.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Registro de reclamações de consumidores com registros, ações e trilha de auditoria',
            description: 'Mantenha um registro de reclamações com registros de entrada, ações de investigação, resultados de resolução e trilha de auditoria completa.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Procedimento de tratamento de reclamações e prazos de resolução',
            description: 'Estabeleça procedimentos de tratamento de reclamações com prazos definidos de reconhecimento, investigação e resolução.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Moderação de conteúdo, monitoramento e aplicação de remoção',
            description: 'Implemente procedimentos de moderação, monitoramento e aplicação de remoção de conteúdo para conteúdo ilegal, proibido ou violador.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Registro de ações de remoção e execução com preservação de evidências',
            description: 'Mantenha um registro de ações de remoção e execução com registros de decisões, preservação de evidências e notificações ao vendedor.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Política da plataforma e registro de divulgação (versões, datas de publicação, aceitação)',
            description: 'Mantenha um registro das políticas e divulgações da plataforma com histórico de versões, datas de publicação e evidências de aceitação.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Deveres de informação ao consumidor - informações precisas sobre o produto e anti-engano',
            description: 'Garanta informações precisas e claras sobre produtos e serviços e implemente medidas para evitar práticas enganosas na plataforma.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Condições de funcionamento da plataforma estrangeira e registo/notificação',
            description: 'Cumprir as condições operacionais para plataformas estrangeiras que atendem usuários vietnamitas, incluindo requisitos de registro ou notificação.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Representação local ou autorização para plataformas estrangeiras',
            description: 'Designe um representante local ou entidade autorizada no Vietnã para plataformas estrangeiras, conforme exigido por lei.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Coordenação de vendedor transfronteiriço e tratamento de reclamações',
            description: 'Coordenar o tratamento de vendedores transfronteiriços e a resolução de reclamações, incluindo escalonamento e gestão de evidências.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Interação do sistema de gerenciamento de comércio eletrônico estadual e prontidão para relatórios',
            description: 'Preparar-se para a interação com o sistema estadual de gestão de comércio eletrônico, incluindo mapeamento de campos de dados e capacidade de geração de relatórios.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Notificação de alteração de material pós-aprovação',
            description: 'Envie notificações sobre alterações materiais nos detalhes, políticas ou operações da plataforma após a aprovação inicial.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Notificação de rescisão e desativação',
            description: 'Envie notificações de rescisão ou desativação ao encerrar operações de comércio eletrônico, incluindo aviso público e tratamento de proteção ao consumidor.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Elegibilidade e manutenção do programa de classificação de marca de confiança e credibilidade',
            description: 'Avalie a elegibilidade para programas de classificação de selo de confiança e credibilidade e mantenha evidências de conformidade para participação contínua.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Consentimento para processamento de dados pessoais',
            description: 'O processamento de dados pessoais requer o consentimento do titular dos dados, a menos que se aplique uma isenção.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Direito de acesso',
            description: 'Os titulares dos dados têm o direito de aceder aos seus dados pessoais na posse do responsável pelo tratamento.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Direito à retificação',
            description: 'Os titulares dos dados têm o direito de solicitar a retificação dos dados pessoais inexatos.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Direito ao apagamento',
            description: 'Os titulares dos dados têm o direito de solicitar o apagamento dos seus dados pessoais.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Direito de restringir o processamento',
            description: 'Os titulares dos dados têm o direito de solicitar a restrição do tratamento dos seus dados pessoais.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Direito de oposição',
            description: 'Os titulares dos dados têm o direito de se opor ao tratamento dos seus dados pessoais.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Direito de retirar o consentimento',
            description: 'Os titulares dos dados têm o direito de retirar o consentimento previamente dado a qualquer momento.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Notificação de violação à autoridade (72h)',
            description: 'O controlador de dados deve notificar o Ministério de Segurança Pública no prazo de 72 horas após a descoberta de uma violação de dados pessoais.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Notificação de violação aos titulares dos dados',
            description: 'O controlador de dados deve notificar os titulares dos dados afetados sobre uma violação de dados pessoais sem demora injustificada.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA para processamento de alto risco',
            description: 'Uma Avaliação de Impacto na Proteção de Dados deve ser realizada para atividades de processamento que possam resultar em alto risco.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Avaliação de transferência transfronteiriça',
            description: 'Deve ser realizada uma avaliação de impacto antes da transferência de dados pessoais para fora do Vietname.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Registro de atividades de processamento',
            description: 'Os controladores de dados devem manter um Registro de Atividades de Processamento (RoPA) documentando todas as atividades de processamento.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Registros de contrato de processamento de dados',
            description: 'Devem ser mantidos registros de acordos de processamento de dados com processadores e subprocessadores.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Salvaguardas de transferência transfronteiriça',
            description: 'As transferências de dados pessoais para fora do Vietname exigem salvaguardas adequadas e avaliações documentadas.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Confirmação em até 2 dias úteis',
            description: 'As solicitações do titular dos dados devem ser confirmadas no prazo de 2 dias úteis após o recebimento.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Conclusão em 15 dias úteis',
            description: 'As solicitações dos titulares dos dados devem ser concluídas no prazo de 15 dias úteis, com uma possível prorrogação de 5 dias úteis.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'Arquivamento do dossiê de avaliação de impacto do MoPS',
            description: 'As organizações que realizam tratamentos de alto risco devem apresentar um dossiê de avaliação de impacto ao Ministério da Segurança Pública, utilizando o procedimento administrativo prescrito.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'Arquivamento de dossiê de transferência transfronteiriça MoPS',
            description: 'As organizações que transferem dados pessoais para fora do Vietname devem apresentar um dossiê de avaliação do impacto da transferência transfronteiriça junto do Ministério da Segurança Pública.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Certificação de provedor de serviços de processamento',
            description: 'As organizações que prestam tratamento de dados pessoais como serviço devem obter um certificado de processamento de serviço do Ministério da Segurança Pública.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Processamento do dossiê de avaliação de impacto quando os gatilhos são atendidos',
            description: 'Um dossiê de avaliação de impacto do processamento deve ser arquivado quando os gatilhos prescritos forem atendidos.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Dossiê de impacto de transferência transfronteiriça',
            description: 'Um dossiê de impacto de transferência transfronteiriça deve ser preparado e arquivado para transferências internacionais de dados.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Designação do responsável pela proteção de dados',
            description: 'Um responsável pela proteção de dados deve ser designado conforme exigido pelo Decreto 13.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Artefatos de governança (políticas, funções, procedimentos)',
            description: 'Os artefactos de governação, incluindo políticas, funções e procedimentos, devem ser mantidos.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Completude do pacote de evidências e trilha de auditoria imutável',
            description: 'Os pacotes de evidências devem estar completos e uma trilha de auditoria imutável deve ser mantida.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Suporte ao fluxo de trabalho de arquivamento de casos',
            description: 'Os fluxos de trabalho de arquivamento de casos devem ser apoiados para avaliações de impacto e dossiês transfronteiriços.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Requisitos contratuais do processador',
            description: 'Os requisitos contratuais para processadores de dados devem ser estabelecidos e monitorados.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Revisão anual de conformidade',
            description: 'Uma revisão anual de conformidade deve ser realizada para verificar a adesão contínua.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Registros de submissão de arquivamento de autoridade',
            description: 'Registros de todas as submissões de pedidos de autoridade devem ser mantidos.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Bases legais para processamento de dados pessoais',
            description: 'O processamento de dados pessoais deve ser baseado em bases legais, conforme definido pelo PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Direitos do titular dos dados (acesso, retificação, apagamento, restrição, oposição, portabilidade)',
            description: 'Os titulares dos dados têm direitos abrangentes, incluindo acesso, retificação, apagamento, restrição, oposição e portabilidade.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Notificação de violação à autoridade',
            description: 'Os responsáveis ​​pelo tratamento de dados devem notificar a autoridade competente sobre violações de dados pessoais dentro do prazo prescrito.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Notificação de violação aos titulares dos dados',
            description: 'Os controladores de dados devem notificar os titulares dos dados afetados sobre violações de dados pessoais.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Avaliação de impacto na proteção de dados',
            description: 'Deve ser realizada uma avaliação de impacto na proteção de dados para atividades de tratamento de alto risco.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Governança de transferência transfronteiriça',
            description: 'As transferências transfronteiriças de dados pessoais devem cumprir os requisitos de governação.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Designação do responsável pela proteção de dados',
            description: 'As organizações devem designar um responsável pela proteção de dados, conforme necessário.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Registros de atividades de processamento',
            description: 'As organizações devem manter registros de todas as atividades de processamento.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Obrigações contratuais do processador',
            description: 'As obrigações contratuais devem ser estabelecidas com os processadores de dados.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Arquivamento do dossiê de avaliação de impacto',
            description: 'Os dossiês de avaliação de impacto devem ser apresentados à autoridade competente conforme prescrito.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Dossiê de impacto de transferência transfronteiriça',
            description: 'Um dossiê de impacto de transferência transfronteiriça deve ser preparado e arquivado para transferências internacionais de dados.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO compromisso e notificação',
            description: 'Deve ser nomeado um responsável pela proteção de dados e a autoridade notificada.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Políticas e procedimentos de governança',
            description: 'As políticas e procedimentos de governação devem ser estabelecidos e mantidos.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Pacote de evidências e trilha de auditoria imutável',
            description: 'Os pacotes de evidências devem estar completos e uma trilha de auditoria imutável deve ser mantida.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Ciclo de vida do arquivamento de casos',
            description: 'Os ciclos de vida de arquivamento de casos devem ser gerenciados através de etapas prescritas.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Requisitos contratuais do processador',
            description: 'Os requisitos contratuais para processadores de dados devem ser estabelecidos e monitorados.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Certificação de provedor de processamento de serviços',
            description: 'As organizações que prestam serviços de processamento de dados devem obter certificação.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Registros de submissão de arquivamento de autoridade',
            description: 'Registros de todas as submissões de pedidos de autoridade devem ser mantidos.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Revisão anual de conformidade',
            description: 'Uma revisão anual de conformidade deve ser realizada para verificar a adesão contínua.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Medidas técnicas de segurança de dados',
            description: 'Medidas técnicas para segurança de dados devem ser implementadas e mantidas.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Decreto 356 requisitos oficiais do formulário Mẫu số',
            description: 'Os 13 formulários oficiais de procedimento administrativo Mẫu số (Mẫu số 01a–10) prescritos pelo Decreto 356/2025/NĐ-CPĐ-CP devem ser utilizados para os respectivos arquivamentos junto ao Ministério de Segurança Pública. Os itens do Phụ lục I – VIII são mapeamentos internos da estrutura de dados, não formulários regulatórios oficiais.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA envio de dossiê para A05',
            description: 'As organizações devem enviar dossiês de DPIA para A05/MPS para atividades de processamento que envolvam dados pessoais sensíveis ou processamento em grande escala.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Apresentação de dossiê de transferência transfronteiriça',
            description: 'As organizações devem enviar dossiês de transferência de dados transfronteiriços para revisão e aprovação A05/MPS.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Processando pedido de certificado de serviço',
            description: 'Os prestadores de serviços de processamento de dados devem solicitar certificados de A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Notificação de tratamento de dados pessoais',
            description: 'Os controladores devem notificar A05/MPS sobre atividades de processamento de dados pessoais usando formulários prescritos (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Relatório anual de situação',
            description: 'Os responsáveis pelo tratamento devem apresentar relatórios anuais sobre a situação do tratamento de dados pessoais (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Confidencialidade das informações do usuário por padrão',
            description: 'As informações do usuário devem ser tratadas como confidenciais por padrão.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Controles mínimos de proteção organizacional e técnica',
            description: 'Devem ser implementados controles mínimos de proteção organizacional e técnica.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Divulgação apenas com base adequada',
            description: 'A divulgação de informações do usuário só é permitida com base legal adequada.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Registro de divulgação obrigatória e auditabilidade',
            description: 'Todas as divulgações devem ser registradas e auditáveis.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Fluxo de trabalho de aprovação interna para divulgações',
            description: 'Um fluxo de trabalho de aprovação interno deve ser estabelecido para todas as divulgações.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Tratamento de resposta de solicitação de autoridade',
            description: 'As solicitações de autoridade devem ser tratadas e respondidas de acordo com os procedimentos prescritos.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'O titular dos dados tem o direito de ser informado sobre as atividades de tratamento de dados pessoais.',
        consent: 'O titular dos dados tem o direito de consentir no tratamento dos seus dados pessoais.',
        refuse_consent: 'O titular dos dados tem o direito de não consentir o tratamento dos seus dados pessoais.',
        withdraw_consent: 'O titular dos dados tem o direito de solicitar a retirada do consentimento previamente dado.',
        access: 'O titular dos dados tem o direito de visualizar e aceder aos seus dados pessoais na posse do responsável pelo tratamento.',
        rectification: 'O titular dos dados tem o direito de corrigir ou solicitar a correção dos dados pessoais inexatos.',
        data_provision: 'O titular dos dados tem o direito de solicitar o fornecimento dos seus dados pessoais (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'O titular dos dados tem o direito de solicitar a eliminação ou destruição dos dados pessoais, ressalvadas as exceções previstas na lei.',
        restriction_of_processing: 'O titular dos dados tem o direito de solicitar a limitação do tratamento dos seus dados pessoais.',
        object_to_processing: 'O titular dos dados tem o direito de enviar um pedido de oposição ao tratamento dos seus dados pessoais.',
        complaint: 'O titular dos dados tem o direito de apresentar reclamação (khiếu nại) relativamente ao tratamento de dados pessoais.',
        denunciation: 'O titular dos dados tem o direito de denunciar (tố cáo) relativamente a violações da proteção de dados pessoais.',
        lawsuit: 'O titular dos dados tem o direito de intentar uma ação judicial (khởi kiện) por violações dos seus direitos de proteção de dados.',
        claim_damages: 'O titular dos dados tem o direito de reclamar uma indemnização pelos danos causados por violações da proteção de dados pessoais.',
        request_protection_measures: 'O titular dos dados tem o direito de solicitar às autoridades competentes ou organizações/indivíduos relevantes que implementem medidas para proteger os seus dados pessoais.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Gestão e monitoramento de riscos (rascunho)',
            steps: {
                '1': 'Identifique categorias de risco',
                '2': 'Mantenha controles de alerta precoce',
                '3': 'Revise os riscos de coleta, armazenamento, processamento, compartilhamento e transação',
                '4': 'Registre riscos de privacidade, segurança cibernética e gerenciamento de acesso',
                '5': 'Acionar ações de correção',
                '6': 'Preservar o histórico de avaliações',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Reclamações e disputas (rascunho)',
            steps: {
                '1': 'Receber registro de reclamação/disputa',
                '2': 'Verifique a integridade das informações exigidas; solicitar suplemento dentro de 24 horas se estiver incompleto',
                '3': 'No prazo de 3 dias úteis após receber uma solicitação completa, notifique as partes sobre a abordagem de tratamento e o cronograma esperado',
                '4': 'Solicite respostas e documentos comprovativos',
                '5': 'Avalie os fatos da disputa e a responsabilidade da plataforma',
                '6': 'Resultado da resolução de registros',
                '7': 'Preservar evidências e encerrar caso',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Entrega e certificado de direitos de propriedade sobre dados (minuta)',
            steps: {
                '1': 'Entregar produto/serviço de acordo com o método de contrato',
                '2': 'Registrar evento e horário de entrega',
                '3': 'Gerar certificado de direitos de propriedade sobre dados',
                '4': 'Registrar a conclusão da transação',
                '5': 'Habilite pesquisa/verificação posterior quando necessário',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Iniciação e contratação de transação (rascunho)',
            steps: {
                '1': 'Revise a listagem e o resultado da avaliação',
                '2': 'Iniciar fluxo de trabalho de compra, negociação ou leilão',
                '3': 'Gerar contrato eletrônico',
                '4': 'Garanta a aceitação de ambas as partes',
                '5': 'Registrar a conclusão do pagamento',
                '6': 'Autorizar entrega',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Acesso ao sandbox/ambiente de teste (rascunho)',
            steps: {
                '1': 'Enviar solicitação de sandbox',
                '2': 'Definir escopo, dados permitidos, período e permissões de processamento',
                '3': 'Provisionar ambiente técnico isolado',
                '4': 'Monitore a segurança e o acesso durante os testes',
                '5': 'Revise a saída antes da extração',
                '6': 'Aprovar a extração somente quando permitido',
                '7': 'Feche a sessão de teste e limpe o ambiente',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Listagem de produtos/serviços de dados (rascunho)',
            steps: {
                '1': 'Criar aplicativo de listagem',
                '2': 'Capture evidências de origem e propriedade/direito de uso',
                '3': 'Registre características técnicas, formato, detalhes API e restrições',
                '4': 'Registrar divulgações de qualidade',
                '5': 'Enviar dossiê de listagem para análise da plataforma',
                '6': 'A plataforma realiza verificações de integridade, legalidade e técnicas',
                '7': 'Aprovar ou rejeitar listagem',
                '8': 'Se aprovado, publicar listagem com resultado da avaliação em anexo',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Integração do participante (rascunho)',
            steps: {
                '1': 'Determine o tipo de participante: vendedor ou comprador',
                '2': 'Verifique o status legal e a capacidade legal',
                '3': 'Verifique as condições adicionais se linhas de negócios condicionais se aplicarem',
                '4': 'Registrar conta de transação',
                '5': 'Vincular conta de pagamento',
                '6': 'Aprovar participação na plataforma',
                '7': 'Armazene o registro do participante e a trilha de auditoria',
            },
        },
        AI_A1: {
            name: 'AI Registro / Notificação do Sistema',
            steps: {
                '1': 'Verificação de aplicabilidade e classificação do sistema AI',
                '2': 'Construir dossiê de registro/notificação',
                '3': 'Aprovação interna',
                '4': 'Enviar via canal',
                '5': 'Ciclo de alteração – se retornado, atualize e reenvie',
                '6': 'Fechar e armazenar confirmação',
            },
        },
        AI_A2: {
            name: 'AI Fluxo de trabalho de classificação de risco',
            steps: {
                '1': 'Criar entrada de registro do sistema AI',
                '2': 'Executar assistente de classificação',
                '3': 'Anexar evidências',
                '4': 'Aprovar classificação',
                '5': 'Vincular resultados ao dossiê do sistema',
            },
        },
        AI_A3: {
            name: 'Conformidade com Rotulagem e Divulgação',
            steps: {
                '1': 'Identifique as obrigações de rotulagem por sistema e caso de uso',
                '2': 'Implementar avisos de rotulagem',
                '3': 'Capture evidências – capturas de tela, cópia da IU, metadados técnicos',
                '4': 'Lidar com isenções e registrar justificativa',
                '5': 'Aprovar e versão',
            },
        },
        AI_A4: {
            name: 'Relatório de incidentes graves',
            steps: {
                '1': 'Criar registro de incidente AI',
                '2': 'Classificar gravidade',
                '3': 'Executar ações de contenção e captura de evidências',
                '4': 'Gerar exportação de modelo de relatório de incidentes graves',
                '5': 'Enviar à autoridade por meio do canal',
                '6': 'Rastreie as respostas e o fechamento da autoridade',
            },
        },
        AI_A5: {
            name: 'AI Avaliação de impacto',
            steps: {
                '1': 'Criar registro de avaliação',
                '2': 'Preencher as seções obrigatórias',
                '3': 'Anexe evidências – fluxos de dados, documentação de modelo, controles',
                '4': 'Aprovação interna',
                '5': 'Exportar relatório e armazenar em dossiê',
            },
        },
        AI_A6: {
            name: 'Testes Controlados/Participação em Sandbox',
            steps: {
                '1': 'Registro completo de elegibilidade e prontidão',
                '2': 'Preparar aplicação e exportar',
                '3': 'Enviar via canal',
                '4': 'Rastrear aprovação',
                '5': 'Execute pontos de verificação e registros de monitoramento',
                '6': 'Relatório de conclusão completo e pacote de encerramento',
            },
        },
        CYBERSEC_C1: {
            name: 'Avaliação Crítica da Segurança do Sistema de Informação',
            steps: {
                '1': 'Classificar os sistemas de informação de acordo com os requisitos nacionais de cibersegurança',
                '2': 'Realizar avaliação de segurança e avaliação de risco para sistemas classificados',
                '3': 'Implementar medidas de segurança necessárias e controles de linha de base',
                '4': 'Documente evidências de conformidade e programe revisões periódicas',
            },
        },
        CYBERSEC_C2: {
            name: 'Notificação de incidentes de segurança cibernética',
            steps: {
                '1': 'Detecte, classifique e avalie o incidente de segurança cibernética',
                '2': 'Executar ações de resposta imediata e preservação de evidências',
                '3': 'Reportar à autoridade e participar na coordenação nacional de resposta a incidentes',
                '4': 'Conduza a revisão pós-incidente e documente as lições aprendidas',
            },
        },
        CYBERSEC_C3: {
            name: 'Verificação de conformidade de localização de dados',
            steps: {
                '1': 'Avalie se os gatilhos de localização de dados se aplicam com base no tipo de serviço e nas categorias de dados',
                '2': 'Verifique se a arquitetura de armazenamento local e os controles de fluxo transfronteiriços estão em vigor',
                '3': 'Avaliar os requisitos de presença local (filial/escritório de representação) quando aplicável',
                '4': 'Documentar evidências de conformidade e mantê-las para análise da autoridade',
            },
        },
        CYBERSEC_C4: {
            name: 'Resposta à solicitação de cooperação da autoridade',
            steps: {
                '1': 'Receber e verificar a legitimidade e o escopo da solicitação de autoridade',
                '2': 'Reunir e preparar as informações solicitadas ou executar as ações necessárias',
                '3': 'Enviar resposta à autoridade dentro do prazo exigido',
                '4': 'Registre a divulgação e feche o caso',
            },
        },
        CYBERSEC_C5: {
            name: 'Verificação de qualificação de pessoal de segurança cibernética',
            steps: {
                '1': 'Avalie a postura de segurança do fornecedor usando a estrutura de avaliação de segurança cibernética',
                '2': 'Verifique o escopo de acesso aos dados do fornecedor e registre-se no log de acesso aos dados do fornecedor',
                '3': 'Revise a cláusula de notificação de incidentes do fornecedor e os requisitos contratuais de segurança cibernética',
                '4': 'Agende revisões periódicas de segurança do fornecedor e monitore a conformidade',
            },
        },
        DATA_D1: {
            name: 'Inventário de dados e configuração de classificação',
            steps: {
                '1': 'Catalogar todos os ativos de dados com tipos e fontes no registro de inventário de dados',
                '2': 'Classifique cada ativo por nível de sensibilidade',
                '3': 'Definir regras de tratamento por nível de classificação',
                '4': 'Capture evidências — capturas de tela, diagramas de arquitetura, controles de acesso',
                '5': 'Agende revisões periódicas',
            },
        },
        DATA_D2: {
            name: 'Governança de Compartilhamento de Dados',
            steps: {
                '1': 'Avaliar novos acordos de partilha através da avaliação de risco de partilha de dados',
                '2': 'Fluxo de trabalho de aprovação interno completo',
                '3': 'Registrar evento de compartilhamento no log de compartilhamento/divulgação de dados',
                '4': 'Capture evidências – registros de aprovação, documentos contratuais',
                '5': 'Revisão periódica dos acordos de partilha',
            },
        },
        DATA_D3: {
            name: 'Avaliação de transferência de dados transfronteiriça',
            steps: {
                '1': 'Identifique transferências transfronteiriças no inventário de dados',
                '2': 'Realizar avaliação de risco de transferência',
                '3': 'Aplicar salvaguardas e medidas de governança',
                '4': 'Documento na secção transfronteiriça do atestado de governação',
                '5': 'Exportar pacote de avaliação para auditoria',
            },
        },
        DATA_D4: {
            name: 'Atestado Anual de Governança',
            steps: {
                '1': 'Rever a matriz RACI de governação',
                '2': 'Verifique o status da política (governança, classificação, compartilhamento, retenção)',
                '3': 'Pacote completo de atestados',
                '4': 'Obtenha aprovação interna',
                '5': 'Atestado de exportação e arquivo',
            },
        },
        DATA_D5: {
            name: 'Tratamento de incidentes de dados',
            steps: {
                '1': 'Classifique o incidente usando registro de decisão',
                '2': 'Execute a resposta a incidentes de acordo com o manual',
                '3': 'Capture evidências – registros, capturas de tela, comunicações',
                '4': 'Realizar revisão pós-incidente',
                '5': 'Se a notificação da autoridade for necessária: crie um caso de envio e envie para MIC',
                '6': 'Fechar e arquivar',
            },
        },
        ECOM_P1: {
            name: 'Notificação (site/aplicativo de vendas)',
            steps: {
                '1': 'Determine a classificação: site/aplicativo de vendas vs mercado/plataforma de serviço',
                '2': 'Crie um registro de dossiê interno com a identidade da empresa, informações do site/aplicativo e divulgações necessárias',
                '3': 'Gere pacote de evidências — capturas de tela de divulgações, URLs de políticas, histórico de versões',
                '4': 'Enviar via Online.gov.vn – fazer upload de documentos, registrar carimbo de data/hora e confirmação',
                '5': 'Rastrear o processamento - se for devolvido para alteração, registre os motivos e reenvie',
                '6': 'Fechar – confirmação da loja, evidência de link e trilha de auditoria',
            },
        },
        ECOM_P2: {
            name: 'Cadastro (Plataformas de Serviços/Marketplaces)',
            steps: {
                '1': 'Determine o modelo e as obrigações da plataforma – mercado versus comércio misto versus comércio social',
                '2': 'Construir dossiê de registro — formulário de inscrição, esquema de prestação de serviços, T&Cs, regras da plataforma',
                '3': 'Aprovações internas — atribuir proprietário legal/de conformidade, aprovar para envio',
                '4': 'Enviar via Online.gov.vn – carregar dossiê, registrar carimbo de data/hora',
                '5': 'Ciclo de processamento — se for devolvido para alteração, atualize e reenvie',
                '6': 'Aprovação e operacionalização — armazene a confirmação, mantenha registros de conformidade contínuos',
            },
        },
        ECOM_P3: {
            name: 'Notificação de atualização/alteração',
            steps: {
                '1': 'Identificar o gatilho da mudança – alteração material nos itens do dossiê',
                '2': 'Gerar pacote de mudanças — resumo de mudanças, documentos atualizados, atualizações de evidências',
                '3': 'Enviar atualização e registrar o status de envio',
                '4': 'Rastreie o processamento e feche',
            },
        },
        ECOM_P4: {
            name: 'Rescisão/Desativação',
            steps: {
                '1': 'Preparar pacote de rescisão – data de cessação, motivo, plano de tratamento de proteção ao consumidor, prova de aviso público',
                '2': 'Enviar rescisão – registrar carimbo de data/hora e confirmação',
                '3': 'Fechar – armazena o status final e as evidências',
            },
        },
        ECOM_P5: {
            name: 'Programa de classificação de selo de confiança/credibilidade',
            steps: {
                '1': 'Revisão de elegibilidade e prontidão',
                '2': 'Preparar dossiê usando formulário oficial',
                '3': 'Enviar e acompanhar',
                '4': 'Mantenha evidências de conformidade contínuas',
            },
        },
        PDPL_P1: {
            name: 'DPIA Envio de arquivamento para MPS',
            steps: {
                '1': 'Preencher DPIA usando o formulário do Decreto 356 (Mẫu số 02a/02b)',
                '2': 'Reunir documentos de cobertura e evidências de apoio',
                '5': 'Reter dossiê e evidência de envio para trilha de auditoria',
            },
        },
        PDPL_P2: {
            name: 'Pedido de transferência transfronteiriça',
            steps: {
                '1': 'Preencher o dossiê de transferência transfronteiriça usando os formulários do Decreto 356 (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Solicitação de certificado de serviço de processamento de dados',
            steps: {
                '1': 'Preparar dossiê de solicitação de certificação de acordo com os requisitos do Decreto 356',
                '2': 'Envie a inscrição para MPS com os documentos comprovativos necessários',
                '3': 'Responda a MPS comentários de revisão e solicitações de suplementos',
            },
        },
        PDPL_P4: {
            name: 'Processamento de solicitação de direitos do titular dos dados',
            steps: {
                '1': 'Receber e registrar solicitação; verificar a identidade do titular dos dados no prazo de 2 dias úteis',
                '2': 'Avalie o tipo de solicitação e determine o prazo aplicável (15/10/20 dias por tipo)',
                '3': 'Execute ações de atendimento em sistemas e departamentos relevantes',
                '4': 'Preparar e enviar resposta ao titular dos dados com resultado e justificativa',
                '5': 'Encerrar o caso e reter evidências para auditoria; documentar qualquer justificativa de recusa',
            },
        },
        PDPL_P5: {
            name: 'Notificação de violação para MPS',
            steps: {
                '1': 'Detectar e avaliar a violação; determinar se a obrigação de notificação se aplica e apurar o prazo de notificação aplicável a partir dos registos de prazos instalados',
                '2': 'Preparar pacote de notificação de acordo com os requisitos PDPL',
                '3': 'Enviar a notificação para MPS dentro do prazo de notificação apurado a partir dos registos de prazos instalados, contado desde a descoberta',
                '4': 'Responder a MPS solicitações de acompanhamento; enviar suplementos e evidências de coordenação',
            },
        },
        TELECOM_T1: {
            name: 'Tratamento de solicitação de autoridade',
            steps: {
                '1': 'Receber e registrar a solicitação de autoridade por meio do registro de entrada',
                '2': 'Verifique a identidade da autoridade, a base jurídica e o escopo da solicitação',
                '3': 'Realizar revisão jurídica interna e obter aprovação da administração',
                '4': 'Preparar pacote de resposta com evidências reunidas',
                '5': 'Esboçar e aprovar carta de resposta',
                '6': 'Crie um caso de envio e envie a resposta por meio do canal',
                '7': 'Divulgação de log e reconhecimento de autoridade de rastreamento',
                '8': 'Encerrar o caso e reter evidências para trilha de auditoria',
            },
        },
        TELECOM_T2: {
            name: 'Gestão de Divulgação',
            steps: {
                '1': 'Receber solicitação de divulgação (consentimento do usuário, obrigação legal ou solicitação de autoridade)',
                '2': 'Fluxo de trabalho completo de aprovação de divulgação',
                '3': 'Executar divulgação e registrar no registro de log',
                '4': 'Capture evidências – registros de aprovação, registros de comunicação',
                '5': 'Revisão periódica do registro de divulgação',
            },
        },
        TELECOM_T3: {
            name: 'Política de confidencialidade e configuração de controle de acesso',
            steps: {
                '1': 'Elaborar e aprovar política de confidencialidade',
                '2': 'Elaborar e aprovar política de controle de acesso e registro',
                '3': 'Atividades de processamento de documentos no registro de processamento de informações do usuário',
                '4': 'Capturar evidências – documentos políticos, evidências de implementação',
                '5': 'Agende revisões periódicas',
            },
        },
        TELECOM_T4: {
            name: 'Avaliação de incidentes de telecomunicações',
            steps: {
                '1': 'Classifique e avalie o incidente',
                '2': 'Determinar os requisitos de notificação',
                '3': 'Se a notificação da autoridade for necessária: crie um caso de envio',
                '4': 'Capturar e preservar evidências',
                '5': 'Resolução e encerramento',
            },
        },
        TELECOM_T5: {
            name: 'Gerenciamento de retenção e exclusão',
            steps: {
                '1': 'Definir cronograma de retenção e exclusão por categoria de dados',
                '2': 'Implementar procedimentos de exclusão',
                '3': 'Capturar evidências – exclusão de registros',
                '4': 'Revisão periódica de cronogramas',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registro/Notificação/Prontidão Administrativa',
            'ECOM-FINE-B': 'Reclamações e Defesa do Consumidor',
            'ECOM-FINE-C': 'Monitoramento e aplicação de remoções',
            'ECOM-FINE-D': 'Operações Transfronteiriças',
            'ECOM-FINE-E': 'Regras e transparência da plataforma',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Falhas no tratamento de solicitações de autoridade',
            'TELECOM-FINE-B': 'Confidencialidade e divulgação não autorizada',
            'TELECOM-FINE-C': 'Controle de acesso e falhas de registro',
            'TELECOM-FINE-D': 'Violações de retenção e exclusão',
            'TELECOM-FINE-E': 'Falhas na avaliação de incidentes de telecomunicações',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Até 100 milhões de VND',
                fine_org: 'Até 200 milhões de VND',
                deadlines: 'Imediatamente após a detecção',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Até 100 milhões de VND',
                fine_org: 'Até 200 milhões de VND',
                deadlines: 'Obrigação contínua',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Até 100 milhões de VND',
                fine_org: 'Até 200 milhões de VND',
                deadlines: 'Obrigação contínua',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Até 100 milhões de VND',
                fine_org: 'Até 200 milhões de VND',
                deadlines: 'Obrigação contínua',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Até 100 milhões de VND',
                fine_org: 'Até 200 milhões de VND',
                deadlines: 'Conforme especificado na solicitação de autoridade',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Até 80 milhões de VND',
                fine_org: 'Até 160 milhões de VND',
                deadlines: 'Obrigação contínua',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Até 80 milhões de VND',
                fine_org: 'Até 160 milhões de VND',
                deadlines: 'Obrigação contínua',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Até 80 milhões de VND',
                fine_org: 'Até 160 milhões de VND',
                deadlines: 'Obrigação contínua',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Até 80 milhões de VND',
                fine_org: 'Até 160 milhões de VND',
                deadlines: 'Conforme especificado pelas obrigações de tratamento de reclamações',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Até 80 milhões de VND',
                fine_org: 'Até 160 milhões de VND',
                deadlines: 'Antes de iniciar as operações de comércio eletrônico',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Operação contínua da plataforma',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de Plataforma / Risco',
                '2': 'Operador de Plataforma / Segurança',
                '3': 'Operador de Plataforma / Risco',
                '4': 'Operador de Plataforma / Segurança',
                '5': 'Operador de Plataforma',
                '6': 'Conformidade',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Um participante envia uma solicitação de reclamação ou disputa',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de Plataforma',
                '2': 'Operador de Plataforma',
                '3': 'Operador de Plataforma',
                '4': 'Operador de Plataforma',
                '5': 'Operador de Plataforma / Jurídico',
                '6': 'Operador de Plataforma',
                '7': 'Conformidade',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'A entrega é concluída após o cumprimento do contrato e das condições de pagamento',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Vendedor / Operador de Plataforma',
                '2': 'Operador de Plataforma',
                '3': 'Operador de Plataforma',
                '4': 'Operador de Plataforma',
                '5': 'Operador de Plataforma',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Um comprador inicia uma transação de dados, produto ou serviço',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Comprador',
                '2': 'Comprador/Operador de Plataforma',
                '3': 'Operador de Plataforma',
                '4': 'Comprador/Vendedor',
                '5': 'Operador de Plataforma / Pagamento',
                '6': 'Operador de Plataforma',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Um participante deseja avaliar produtos/serviços de dados no ambiente isolado',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Participante',
                '2': 'Operador/Participante da Plataforma',
                '3': 'Operador de Plataforma / TI',
                '4': 'Operador de Plataforma / Segurança',
                '5': 'Operador de Plataforma',
                '6': 'Operador de Plataforma',
                '7': 'Operador de Plataforma / TI',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Um vendedor busca listar dados, produtos ou serviços de dados para negociação',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Vendedor',
                '2': 'Vendedor / Jurídico',
                '3': 'Vendedor / TI',
                '4': 'Vendedor',
                '5': 'Vendedor',
                '6': 'Operador de Plataforma',
                '7': 'Operador de Plataforma',
                '8': 'Operador de Plataforma',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Um vendedor ou comprador ingressa na plataforma de dados',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Operador de Plataforma',
                '2': 'Operador de Plataforma / Jurídico',
                '3': 'Operador de Plataforma / Jurídico',
                '4': 'Operador de Plataforma',
                '5': 'Operador de Plataforma',
                '6': 'Operador de Plataforma',
                '7': 'Conformidade',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Início do tratamento de dados pessoais que exige avaliação de impacto',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Equipe de privacidade',
                '2': 'Equipe de privacidade',
                '3': 'Jurídico/Conformidade',
                '4': 'DPO / Equipe de privacidade',
                '5': 'Equipe de Conformidade',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Intenção de transferir dados pessoais para fora do Vietname',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Equipe de privacidade',
                '2': 'Jurídico/Conformidade',
                '3': 'DPO / Equipe de privacidade',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organização que fornece serviços de processamento de dados que exigem certificação',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Jurídico/Conformidade',
                '2': 'Jurídico/Conformidade',
                '3': 'DPO / Equipe de privacidade',
                '4': 'Equipe de Conformidade',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Recebimento de uma solicitação de direitos do titular dos dados',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Equipe de Privacidade / Atendimento ao Cliente',
                '2': 'DPO / Equipe de privacidade',
                '3': 'Líderes de Departamento / TI',
                '4': 'DPO / Equipe de privacidade',
                '5': 'Equipe de Conformidade',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Descoberta de uma violação ou violação da proteção de dados pessoais',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Equipe de resposta a incidentes / DPO',
                '2': 'DPO / Equipe de privacidade',
                '3': 'Jurídico/Conformidade',
                '4': 'DPO / Equipe de resposta a incidentes',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Decreto 13 Art. 24',
        'MoPS Admin Procedures': 'Procedimentos administrativos do MoPS',
        'Annual cycle': 'Ciclo anual',
        'Decree 13/2023/NĐ-CP Art. 24': 'Decreto 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Decreto 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Decreto 356/2025/NĐ-CPĐ-CP Art. 5 cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Decreto 356/2025/NĐ-CPĐ-CP Art. 5 cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Decreto 356/2025/NĐ-CPĐ-CP Art. 5 cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Decreto 356/2025/NĐ-CPĐ-CP Art. 5 cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Decreto 356/2025/NĐ-CPĐ-CP Art. 5 cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Decreto 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Projeto de Decreto sobre Plataformas de Dados 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Lei 122/2025/QH15 Cap. III; NĐ 98/2020; NA 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Lei 122/2025/QH15 Cap. IV (Defesa do Consumidor)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Lei 122/2025/QH15 - Procedimento operacional Online.gov.vn',
        'Law 134/2025/QH15 Article 12': 'Lei 134/2025/QH15 Artigo 12',
        'Law 134/2025/QH15 Article 21': 'Lei 134/2025/QH15 Artigo 21',
        'Law 134/2025/QH15 Article 27': 'Lei 134/2025/QH15 Artigo 27',
        'Law 134/2025/QH15 Articles 9–10': 'Lei 134/2025/QH15 Artigos 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Lei 24/2023/QH15 — mapeamento de artigo pendente',
        'Law 60/2024/QH15 (internal control target)': 'Lei 60/2024/QH15 (alvo de controle interno)',
        'Law 91/2025/QH15 Art. 21': 'Lei 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Lei 91/2025/QH15 Art. 23',
        'Policy-defined': 'Definido por política',
        Quarterly: 'Trimestralmente',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 procedimento administrativo',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'meta de controle interno (Lei 116/2025/QH15 Art. 24(2)(b), 41(5) — cronograma exato pendente de instrumento de implementação)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'meta de controle interno (Lei 116/2025/QH15 Art. 41 (3) - cronograma exato pendente de instrumento de implementação)',
        'source verification pending': 'verificação de origem pendente',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C) [PT]',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A) [PT]",
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A) [PT]',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A) [PT]',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C) [PT]',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D) [PT]',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D) [PT]',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D) [PT]',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C) [PT]',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B) [PT]',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C) [PT]',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A) [PT]',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Internal Audit Governance [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C) [PT]',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D) [PT]',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management Information System (Layer 4) [PT]',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3) [PT]',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G) [PT]',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G) [PT]',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Outsourcing Controls [PT]',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G) [PT]',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G) [PT]',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G) [PT]',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G) [PT]',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C) [PT]',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01 [PT]',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5) [PT]',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1) [PT]',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C) [PT]',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A) [PT]',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E) [PT]',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01 [PT]',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A) [PT]',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F) [PT]',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B) [PT]',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2) [PT]',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D) [PT]',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (Assembleia Nacional)',
        'Ministry of Public Security / A05 Department': 'Ministério da Segurança Pública / Departamento A05',
        'Ministry of Public Security': 'Ministério da Segurança Pública',
        'Ministry of Information and Communications': 'Ministério da Informação e Comunicações',
        'Government of Vietnam': 'Governo do Vietnã',
        'European Data Protection Board (EDPB)': 'Conselho Europeu para a Proteção de Dados (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / CSIRT nacionais',
        'State Bank of Vietnam': 'Banco Estatal do Vietnã',
    },
    tour_titles: {
        access_accountability: 'Acesse o tour de responsabilidade',
        assessments: 'Tour de Avaliações',
        audit_trail: 'Tour pela trilha de auditoria',
        classification: 'Tour de Classificação',
        compliance_forms: 'Tour pelos formulários de conformidade',
        connectors: 'Tour pelos Conectores',
        consent_properties: 'Tour pelas propriedades de consentimento',
        consent: 'Tour de consentimento',
        dashboard: 'Tour pelo painel',
        data_mapping: 'Tour de mapeamento de dados',
        data_subjects: 'Tour pelos titulares dos dados',
        deletion_orchestration: 'Tour de orquestração de exclusão',
        discovery: 'Tour de descoberta',
        governance: 'Tour de Governança',
        help_center: 'Tour pela Central de Ajuda',
        incident_ops: 'Tour de operações de incidentes',
        incidents: 'Tour de Incidentes',
        localization: 'Tour de localização',
        monitoring: 'Tour de monitoramento',
        onboarding: 'Tour de integração',
        organizations: 'Tour pelas Organizações',
        partner_workspace: 'Tour pelo espaço de trabalho do parceiro',
        platform_settings: 'Tour pelas configurações da plataforma',
        privacy_scanner: 'Tour do scanner de privacidade',
        program_governance: 'Tour de Governança do Programa',
        rights_requests: 'Tour de Solicitações de Direitos',
        task_management: 'Tour de gerenciamento de tarefas',
        vendor_risk: 'Tour de risco do fornecedor',
    },
    tour_descriptions: {
        access_accountability: 'Como Líder de Auditoria Interna (P-VN-10), revise as evidências de acesso, o uso de permissões de auditoria e investigue padrões anômalos para demonstrar a integridade e a confidencialidade do GDPR Artigo 5(1)(f).',
        assessments: 'Como Empresa DPO (P-VN-01), conduza DPIAs seguindo o Decreto 356 Mau so 02a, identifique lacunas de conformidade e avalie os riscos de transferência transfronteiriça sob PDPL.',
        audit_trail: 'Como líder de auditoria interna (P-VN-10), consulte evidências entre módulos, monte pacotes de auditoria e exporte registros invioláveis para envios regulatórios.',
        classification: 'Como administrador de dados (P-VN-05), defina taxonomias de classificação de dados, atribua rótulos de confidencialidade, aplique políticas de retenção e resolva lacunas de cobertura necessárias para conformidade com PDPL.',
        compliance_forms: 'Como Empresa DPO (P-VN-01), navegue, preencha e envie formulários regulatórios oficiais vietnamitas, incluindo Phu luc I, Phu luc II e Mau so modelos para arquivamentos do Comitê PDP.',
        connectors: 'Como líder de sistemas de TI (P-VN-05), capture os requisitos de negócios para integrações, mapeie conectores para fluxos de dados de conformidade e verifique a prontidão do POC antes de entrar em operação.',
        consent_properties: 'Como Empresa DPO (P-VN-01), configure a implantação do CMP: registro de propriedade digital, modelos dourados para banners de consentimento consistentes PDPL e matrizes vinculativas que vinculam propriedades a atividades de processamento.',
        consent: 'Como Empresa DPO (P-VN-01), gerencie o ciclo de vida da política de consentimento: defina os propósitos de coleta, aplique os direitos de retirada do PDPL e integre-se à sua Plataforma de gerenciamento de consentimento.',
        dashboard: 'Como Empresa DPO (P-VN-01), oriente-se em torno do centro de comando de postura de conformidade que apresenta prazos PDPL, violação de SLAs e KPIs de prontidão para auditoria.',
        data_mapping: 'Como administrador de dados (P-VN-05), crie seu inventário ROPA, mapeie fluxos de dados transfronteiriços e mantenha o registro de atividades de processamento exigido por PDPL.',
        data_subjects: 'Como líder de conformidade empresarial ou educacional, gerencie perfis unificados de titulares de dados que vinculam registros de consentimento, solicitações de direitos e atividades de cronograma em toda a plataforma.',
        deletion_orchestration: 'Como o DPO (P-VN-01), verifique a exclusão de dados ponta a ponta em sistemas internos e fornecedores terceirizados, com cadeias de prova criptográfica para GDPR conformidade com o direito de exclusão do Artigo 17.',
        discovery: 'Como administrador de dados (P-VN-05), execute verificações automatizadas para descobrir dados pessoais em sistemas conectados, revise descobertas preparadas e alimente os resultados no módulo Classificação para rotulagem PDPL.',
        governance: 'Como Gerente de Risco do Fornecedor (P-VN-08), mantenha o registro do fornecedor, rastreie as cadeias de subprocessadores, avalie os riscos de transferência transfronteiriça e aplique os manuais de governança sob PDPL.',
        help_center: 'Para todas as pessoas – DPOs, auditores, líderes de TI e gerentes de conformidade – encontre respostas, guias de procedimentos e documentação específica do módulo para dar suporte aos seus fluxos de trabalho diários de conformidade.',
        incident_ops: 'Como Comandante do Incidente (P-VN-09), ative playbooks de resposta, rastreie a conformidade do SLA em relação à janela de notificação de 72 horas do PDPL, gerencie escalonamentos e coordene a resposta do fornecedor.',
        incidents: 'Como Comandante do Incidente (P-VN-09), detecte e relate violações de dados com obrigações de notificação de 72 horas PDPL ao Comitê PDP, faça triagem por gravidade e crie dossiês de incidentes com nível de auditoria.',
        localization: 'Como a Empresa DPO (P-VN-01), gerencie o ciclo de vida da estrutura regulatória: instale pacotes do mercado vietnamita, ative as estruturas PDPL e do Decreto 13, mapeie os requisitos para os módulos da plataforma e gerencie as traduções.',
        monitoring: 'Como Empresa DPO (P-VN-01), estabeleça programas contínuos de monitoramento de conformidade, rastreie mudanças regulatórias, meça KPIs e gerencie casos de interação regulatória em PDPL.',
        onboarding: 'Como instrutor de conformidade (P-VN-02), forneça treinamento por meio de tours guiados, carregue dados de amostra realistas para exercícios práticos e execute o assistente de configuração para cenários de início rápido.',
        organizations: 'Como DPO (P-VN-01), configure estruturas de conformidade multiorganizacionais, estabeleça hierarquias de departamentos e encaminhe solicitações de titulares de dados em seu grupo corporativo.',
        partner_workspace: 'Como gerente de contas de parceiros, integre organizações parceiras, configure níveis de serviço, rastreie resultados de conformidade e gerencie o mercado de parceiros.',
        platform_settings: 'Como administrador da plataforma, gerencie todas as configurações operacionais – identidade, segurança, integrações e acesso a módulos que sustentam seu programa de conformidade.',
        privacy_scanner: 'Como Analista de Privacidade (P-VN-06), execute varreduras de monitoramento contínuo para detectar violações de consentimento de cookies, rastreadores não autorizados e lacunas de políticas - gerando evidências de privacidade eletrônica e conformidade com GDPR.',
        program_governance: 'Navegue entre modelos de maturidade, avaliações, lacunas de evidências, resumos executivos e muito mais. Cada submódulo contribui para uma visão holística da saúde interna do seu programa.',
        rights_requests: 'Como Empresa DPO (P-VN-01), gerencie solicitações de direitos do titular dos dados sob PDPL com prazos legais de 15 dias, verificação de identidade e trilhas de resolução de nível de auditoria.',
        task_management: 'Direcione tarefas de conformidade entre personas, monitore prazos SLA e automatize fluxos de trabalho que mantêm sua equipe responsável.',
        vendor_risk: 'Como Gerente de Risco do Fornecedor (P-VN-08), avalie o risco de terceiros, aplique revisões de portas e acione reavaliações – diferente da Governança do Programa, que rastreia sua maturidade interna.',
    },
    consolidation: {
        title: 'Casos de consolidacao',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Criar caso de consolidação',
        case_count: 'case',
        created: 'Caso de consolidação criado',
        create: 'Criar caso',
        creating: 'Criando...',
        cancel: 'Cancelar',
        no_cases: 'Nenhum caso de consolidação encontrado.',
        all_statuses: 'Todos os status',
        all_purposes: 'Todos os fins',
        filter_status: 'Filtrar por status',
        filter_purpose: 'Filtrar por finalidade',
        col_title: 'Título',
        col_purpose: 'Objetivo',
        col_status: 'Estado',
        col_progress: 'Progresso',
        col_deadline: 'Prazo',
        col_department: 'Departamento',
        col_version: 'Versão',
        col_evidence: 'Evidência',
        sections_accepted: 'accepted',
        prev: 'Anterior',
        next: 'Próximo',
        page: 'Página',
        prev_page: 'Página anterior',
        next_page: 'Próxima página',
        field_title: 'Título',
        field_purpose: 'Objetivo',
        field_framework: 'Código Estrutural',
        field_deadline: 'Prazo',
        field_reviewer: 'ID do usuário do revisor',
        title_placeholder: 'Insira o título do caso',
        framework_placeholder: 'por exemplo, PDPD-2023',
        reviewer_placeholder: 'Insira o ID do usuário do revisor',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Arquivamento',
            cross_border_filing: 'Arquivamento Transfronteiriço',
            annual_governance_attestation: 'Atestado Anual de Governança',
            breach_package: 'Pacote de violação',
            audit_inspection_pack: 'Pacote de inspeção de auditoria',
        },
        statuses: {
            draft: 'Rascunho',
            collecting: 'Coletando',
            consolidating: 'Consolidando',
            review: 'Revisão',
            corrections: 'Correções',
            pending_approval: 'Aprovação pendente',
            approved: 'Aprovado',
            completed: 'Concluído',
            closed: 'Fechado',
            rejected: 'Rejeitado',
        },
        section_statuses: {
            pending: 'Pendente',
            in_progress: 'Em andamento',
            submitted: 'Enviado',
            accepted: 'Aceito',
            needs_correction: 'Precisa de correção',
            corrected: 'Corrigido',
        },
        flag_types: {
            missing_section: 'Seção ausente',
            conflicting_entry: 'Entrada conflitante',
            stale_entry: 'Entrada obsoleta',
            missing_evidence: 'Evidências ausentes',
            incomplete_data: 'Dados incompletos',
            duplicate_entry: 'Entrada duplicada',
        },
        flag_severities: {
            critical: 'Crítico',
            warning: 'Aviso',
            info: 'Informações',
        },
        sections_title: 'Seções',
        no_sections: 'Nenhuma seção atribuída ainda.',
        flags_title: 'Bandeiras',
        no_flags: 'Nenhum sinalizador detectado.',
        open: 'open',
        approval_chain: 'Cadeia de aprovação',
        pending: 'Pendente',
        audit_trail: 'Trilha de auditoria',
        no_audit: 'Nenhum evento de auditoria.',
        auto_assign: 'Atribuir seções automaticamente',
        start_collection: 'Iniciar coleção',
        review_case: 'Revisar caso',
        generate_export: 'Gerar exportação',
        review_title: 'Revisão de Consolidação',
        advance_approval: 'Avançar para aprovação',
        back_to_case: 'Voltar ao caso',
        accept: 'Aceitar',
        return_section: 'Retorno',
        return_comment: 'Retornar comentário...',
        resolve: 'Resolver',
        dismiss: 'Dispensar',
        resolution_notes: 'Notas de resolução...',
        notes: 'Notas',
        section_accepted: 'Seção aceita',
        section_returned: 'Seção devolvida para correção',
        advanced_to_approval: 'Caso avançado para aprovação pendente',
        section_data: 'Dados da seção',
        save_progress: 'Salvar progresso',
        submit_section: 'Enviar seção',
        submitting: 'Enviando...',
        resubmit: 'Reenviar',
        progress_saved: 'Progresso salvo',
        section_submitted: 'Seção enviada',
        correction_required: 'Correção necessária',
        evidence_title: 'Evidência',
        no_evidence: 'Nenhuma evidência anexada.',
        add_evidence: 'Adicionar evidências',
        evidence_added: 'Evidência adicionada',
        evidence_files: 'arquivos de evidências',
        uploaded_by: 'enviado por',
        comments_title: 'Comentários',
        no_comments: 'Nenhum comentário ainda.',
        comment_placeholder: 'Adicione um comentário...',
        add_comment: 'Adicionar comentário',
        comment_added: 'Comentário adicionado',
        view_data: 'Ver dados enviados',
        export_title: 'Exportação de Dossiê',
        export_generated: 'Pacote de exportação gerado',
        regenerate: 'Regenerar',
        regenerating: 'Regenerando...',
        generated_at: 'Gerado em',
        artifacts_title: 'Exportar artefatos',
        artifact_type: 'Artefato',
        file_name: 'Nome do arquivo',
        file_size: 'Tamanho',
        generated: 'Gerado',
        download: 'Baixar',
        artifacts: {
            master_report: 'Relatório Mestre',
            evidence_bundle: 'Pacote de evidências (ZIP)',
            contributor_lineage: 'Linhagem do Contribuidor',
            conflict_resolution: 'Resolução de Conflitos',
            approval_history: 'Histórico de aprovação',
        },
        official_forms: 'Formulários Oficiais',
        forms_description: 'Os seguintes formulários oficiais podem ser gerados a partir dos dados consolidados. Os formulários oficiais são separados dos papéis de trabalho internos.',
        announce_loaded_cases: '{count} casos de consolidação carregados',
        announce_case_detail_loaded: 'Detalhe do caso carregado',
        announce_case_transitioned: 'Caso transferido para {status}',
        announce_flag_resolved: 'Sinalização resolvida',
        announce_flag_dismissed: 'Bandeira rejeitada',
        case_detail_loaded: 'Detalhe do caso carregado',
        sections_auto_assigned: 'Seções atribuídas automaticamente pela hierarquia do departamento',
        errors: {
            load_failed: 'Falha ao carregar dados. Por favor, tente novamente.',
            create_failed: 'Falha ao criar o caso. Por favor, tente novamente.',
            required_fields: 'Por favor preencha todos os campos obrigatórios.',
            not_found: 'Não encontrado.',
            comment_required: 'O comentário é necessário ao retornar uma seção.',
            notes_required: 'Notas são necessárias ao resolver ou descartar um sinalizador.',
        },
    },
    rights_requests: {
        import: {
            title: 'Importar casos',
            nav_title: 'Importação em massa',
            description: 'Faça upload de um ficheiro CSV para importar em massa casos de pedido de direitos.',
            select_file: 'Selecionar ficheiro CSV',
            file_help: 'CSV com cabeçalhos correspondentes aos campos do caso.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Ficheiro demasiado grande. O tamanho máximo é 2MB.',
            preview: 'Pré-visualização',
            columns_detected: 'Colunas detetadas: {count}',
            column_mapping: 'Mapeamento de colunas',
            import_button: 'Importar',
            import_complete: 'Importação concluída',
            import_error: 'A importação falhou',
            result: 'Resultado da importação',
            total: 'Total',
            imported: 'Importado',
            skipped: 'Ignorado',
            failed: 'Falhado',
            errors: 'Erros',
            error_row: 'Linha',
            error_field: 'Campo',
            error_message: 'Mensagem',
            history: 'Histórico de importação',
            no_history: 'Sem histórico de importação.',
            history_file: 'Nome do ficheiro',
            history_date: 'Data',
        },
        batch: {
            nav_title: 'Processo em lote',
            page_title: 'Processo em lote DSR',
            page_subtitle: 'Carregue um CSV de DSRs pendentes. Forseti elabora uma resposta de confirmação por solicitação e enfileira uma tarefa de verificação por linha. O DPO analisa e aceita cada um antes de enviar.',
            upload_heading: 'Carregar',
            csv_hint: 'Colunas obrigatórias: request_id, requester_email, request_type. Opcional: prazo (ISO-8601), notas.',
            rows_heading: '{count} linhas analisadas',
            submit_label: 'Executar lote',
            submitting: 'Elaboração…',
            col_request_id: 'ID da solicitação',
            col_email: 'E-mail do solicitante',
            col_type: 'Tipo',
            col_status: 'Estado',
            col_actions: 'Ações',
            status_queued: 'Na fila',
            status_drafted: 'Elaborado',
            status_failed: 'Falha',
            status_accepted: 'Aceito',
            status_rejected: 'Rejeitado',
            status_parsing_error: 'Erro de análise',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Aceitar',
            reject: 'Rejeitar',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Gatilhos de avaliação',
        subtitle: 'Configure a criação automatizada de avaliações e notificações DPO para fluxos de trabalho de privacidade.',
        auto_create_dpia_label: 'Criar automaticamente DPIA ao processar atividade marcada como DPIA obrigatória',
        auto_notify_new_purpose_label: 'Notificar automaticamente DPO quando uma nova finalidade for criada com categorias de dados confidenciais',
        auto_notify_cross_border_label: 'Notificar automaticamente DPO quando uma transferência internacional for adicionada à atividade de processamento',
        default_dpo_label: 'Padrão DPO',
        default_dpo_placeholder: 'Selecione o padrão DPO...',
        notification_channels_label: 'Canais de Notificação',
        channel_in_app: 'No aplicativo',
        channel_email: 'Email',
        save_success: 'As configurações do gatilho de avaliação foram salvas.',
        save_error: 'Falha ao salvar as configurações do acionador de avaliação.',
        fetch_error: 'Falha ao carregar as configurações do acionador de avaliação.',
        fetch_users_error: 'Falha ao carregar usuários.',
        announce_loaded: 'Configurações do gatilho de avaliação carregadas',
        dpia_status: 'DPIA Estado',
        dpia_linked: 'DPIA Vinculado',
        dpia_required_no_dpia: 'DPIA Obrigatório — Não criado',
        create_dpia: 'Criar DPIA',
        create_dpia_success: 'DPIA criado e vinculado à atividade.',
        create_dpia_error: 'Falha ao criar DPIA para atividade.',
        view_dpia: 'Ver DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Perfis de Titulares de Dados',
        nav_title: 'Titulares de Dados',
        nav: {
            profiles: 'Perfis',
        },
        create: 'Criar Perfil',
        edit: 'Editar Perfil',
        delete: 'Eliminar Perfil',
        delete_confirm: 'Tem certeza de que deseja excluir este perfil do titular dos dados?',
        display_name: 'Nome de exibição',
        email: 'Email',
        phone: 'Telefone',
        employee_id: 'ID do funcionário',
        student_id: 'ID de estudante',
        notes: 'Notas',
        external_refs: 'Referências Externas',
        cases_count: 'Casos',
        workflows_count: 'Registros de consentimento',
        last_activity: 'Última atividade',
        timeline: 'Linha do tempo',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Nenhum perfil de titular de dados encontrado.',
        search_placeholder: 'Pesquise por nome, e-mail, telefone ou ID...',
        create_success: 'Perfil do titular dos dados criado.',
        create_error: 'Falha ao criar o perfil do titular dos dados.',
        update_success: 'Perfil do titular dos dados atualizado.',
        update_error: 'Falha ao atualizar o perfil do titular dos dados.',
        delete_success: 'Perfil do titular dos dados excluído.',
        delete_error: 'Falha ao excluir o perfil do titular dos dados.',
        link_case_success: 'Caso vinculado ao perfil.',
        link_case_error: 'Falha ao vincular o caso ao perfil.',
        link_workflow_success: 'Fluxo de trabalho vinculado ao perfil.',
        link_workflow_error: 'Falha ao vincular o fluxo de trabalho ao perfil.',
        load_error: 'Falha ao carregar perfis de titulares de dados.',
        detail_load_error: 'Falha ao carregar o perfil do titular dos dados.',
        timeline_load_error: 'Falha ao carregar a linha do tempo.',
        tab_cases: 'Casos de DSR',
        tab_consent: 'Registros de consentimento',
        tab_timeline: 'Linha do tempo',
        tab_consent_workflows: 'Fluxos de trabalho de consentimento',
        tab_web_consent: 'CMP Consentimento',
        web_consent_empty: 'Nenhum registro de consentimento CMP encontrado para este assunto.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Falha ao carregar registros de consentimento CMP.',
        col_policy_version: 'Versão da política',
        col_versions: 'Versões',
        col_consent_version: 'Consentimento',
        col_cookie_declaration_version: 'Declaração de Cookies',
        col_purposes: 'Finalidades',
        purpose_granted: 'Concedido',
        purpose_denied: 'Negado',
        granted_domains: 'Concedido',
        denied_domains: 'Negado',
        col_action: 'Ação',
        col_timestamp: 'Carimbo de data e hora',
        col_domain: 'Domínio',
        no_cases: 'Nenhum caso vinculado.',
        no_workflows: 'Nenhum fluxo de trabalho de consentimento vinculado.',
        no_timeline: 'Nenhum evento na linha do tempo.',
        case_id: 'ID do caso',
        case_status: 'Estado',
        case_type: 'Tipo de solicitação',
        case_created: 'Criado',
        workflow_id: 'ID do fluxo de trabalho',
        workflow_type: 'Tipo',
        workflow_status: 'Estado',
        workflow_created: 'Criado',
        timeline_date: 'Data',
        timeline_event: 'Evento',
        timeline_details: 'Detalhes',
        timeline_time: 'Hora',
        timeline_type: 'Tipo',
        timeline_summary: 'Resumo',
        type_case: 'Caso DSR',
        type_consent_workflow: 'Fluxo de trabalho de consentimento',
        profile_info: 'Informações do perfil',
        announce_loaded: 'Perfis de titulares de dados carregados',
        announce_detail_loaded: 'Perfil do titular dos dados carregado',
        select_case: '— Selecione um caso —',
        select_workflow: '— Selecione um fluxo de trabalho —',
        no_cases_hint: 'Vincule casos para vê-los aqui.',
        no_workflows_hint: 'Vincule fluxos de consentimento para vê-los aqui.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} sistemas · {flows} fluxos · {crossBorder} transferências internacionais · {highRisk} risco elevado/crítico',
            switch_to_list: 'Mudar para vista de lista',
            switch_to_list_aria: 'Mudar para vista de tabela',
            svg_aria: 'Mapa de fluxos de dados: {systems} sistemas, {flows} fluxos, {crossBorder} transferências internacionais',
            selected_system: 'Sistema selecionado',
            drill_hint: 'Abra o separador «Lista» para editar os fluxos, AIPD e atribuições de fornecedores associados a este sistema.',
        },
    },
    packages: {
        light: {
            name: 'Luz',
            description: 'Ferramentas essenciais de conformidade de privacidade para equipas pequenas',
        },
        standard: {
            name: 'Padrão',
            description: 'Plataforma de conformidade completa para organizações em crescimento',
        },
        enterprise_unlimited: {
            name: 'Empresa Ilimitada',
            description: 'Conformidade de nível empresarial com suporte multi-organização e serviços dedicados',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Configurações do provedor',
            subtitle: 'Configure o provedor Claude para o consultor jurídico Forseti AI.',
            healthHeading: 'Saúde do provedor',
            statusLabel: 'Estado',
            statusOk: 'OK',
            statusFailed: 'Indisponível',
            providerLabel: 'Provedor',
            providerClaudeApi: 'Cláudio API',
            providerCliRelay: 'Relé Claude CLI',
            providerAesirManaged: 'Tokens gerenciados pela Aesir',
            lastCheckedLabel: 'Última verificação',
            reasonLabel: 'Razão',
            refreshButton: 'Atualizar',
            apiKeyHeading: 'Chave de Cláudio API',
            apiKeyHint: 'Definir uma tecla API ativa o modo Claude API. Deixe não definido para usar a retransmissão CLI local (modo desenvolvedor/auto-hospedado).',
            apiKeyLabel: 'Chave API',
            apiKeyMinLength: 'Mínimo 32 caracteres. Criptografado em repouso; nunca mais foi mostrado.',
            apiKeyTooShort: 'A chave API deve ter pelo menos 32 caracteres.',
            apiKeySaved: 'Chave API salva. Saúde refrescante…',
            apiKeyCleared: 'API chave apagada. Forseti agora usa a retransmissão CLI local.',
            setKeyButton: 'Salvar chave API',
            clearKeyButton: 'Limpar tecla API (usar relé CLI)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Uso',
            recentHeading: 'Atividade recente',
            historyHeading: 'Últimos 12 períodos',
            noActivity: 'Nenhuma atividade ainda neste período.',
            featureLabel: 'Recurso',
            whenLabel: 'Quando',
            periodLabel: 'Período',
            byFeatureHeading: 'Este mês por recurso',
            byFeatureEmpty: 'Nenhuma atividade de recurso ainda neste período.',
            featureColumn: 'Recurso',
            featureUnitsColumn: 'Unidades',
            featureRunsColumn: 'Corre',
            byUserHeading: 'Este mês por usuário (visualização do administrador)',
            byUserEmpty: 'Nenhuma atividade por usuário ainda neste período.',
            userColumn: 'Usuário',
            reconciliationLinkLabel: 'Ver relatório de desvio de reconciliação',
            reconciliationLinkHint: 'Apenas administrador interno da Aesir.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Bate-papo',
                workflow: 'Execução do fluxo de trabalho',
                tabular: 'Extração tabular',
                redline: 'Revisão da linha vermelha',
                title: 'Título da conversa',
                unknown: 'Outro',
            },
        },
        reconciliation: {
            title: 'Reconciliação de cotas Forseti',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Voltar ao uso do Forseti',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Esta página é restrita aos administradores internos da Aesir. Se você acredita que deveria ter acesso, entre em contato com seu representante AesirX.',
        },
        projects: {
            title: 'Projetos',
            subtitle: 'Espaços de trabalho para documentos, conversas, fluxos de trabalho e revisões.',
            modeLabel: 'Ver',
            tabMine: 'Meus projetos',
            tabShared: 'Compartilhado comigo',
            searchLabel: 'Pesquisar projetos',
            searchPlaceholder: 'Pesquisar…',
            newButton: 'Novo projeto',
            colName: 'Nome',
            colVisibility: 'Visibilidade',
            colDocs: 'Documentos',
            colMembers: 'Membros',
            colUpdated: 'Atualizado',
            visibilityOwn: 'Possuir apenas',
            visibilityShared: 'Compartilhado',
            emptyMine: 'Nenhum projeto ainda. Crie um para começar.',
            emptyShared: 'Nenhum projeto foi compartilhado com você.',
            pageHint: 'Exibindo {shown} de {total}.',
            createTitle: 'Novo projeto',
            createSubmit: 'Criar projeto',
            fieldName: 'Nome',
            fieldDescription: 'Descrição (opcional)',
            fieldCmNumber: 'Número do caso/assunto (opcional)',
            fieldVisibility: 'Visibilidade',
            errorNameRequired: 'O nome é obrigatório.',
            errorSelectorRequired: 'Selecione um usuário ou grupo organizacional para adicionar.',
            renameButton: 'Renomear',
            renamePrompt: 'Renomear projeto',
            deleteButton: 'Excluir',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Excluir esta pasta? Os documentos contidos permanecerão no projeto, apenas não arquivados.',
            removeMemberConfirm: 'Remover este membro?',
            foldersHeading: 'Pastas',
            foldersEmpty: 'Ainda não há pastas.',
            addFolderButton: 'Nova pasta',
            addFolderTitle: 'Nova pasta',
            documentsHeading: 'Documentos',
            documentsEmpty: 'Ainda não há documentos anexados.',
            colFilename: 'Nome do arquivo',
            colAdded: 'Adicionado',
            unattachButton: 'Desanexar',
            membersHeading: 'Membros',
            membersEmpty: 'Somente proprietário.',
            addMemberButton: 'Adicionar membro',
            addMemberTitle: 'Adicionar membro',
            addMemberSubmit: 'Adicionar membro',
            colMember: 'Membro',
            colRole: 'Função',
            selectorTypeLabel: 'Adicionar por',
            selectorUser: 'ID do usuário',
            selectorGroup: 'ID do grupo organizacional',
            fieldUserId: 'Usuário',
            fieldGroupId: 'Grupo organizacional',
            fieldRole: 'Função',
            roleAdmin: 'Administrador',
            roleEditor: 'Editor',
            roleViewer: 'Visualizador',
            notFound: 'Projeto não encontrado.',
            selectUserPlaceholder: 'Selecione um usuário…',
            selectGroupPlaceholder: 'Selecione um grupo organizacional…',
            noUsersAvailable: 'Nenhum usuário disponível.',
            noGroupsAvailable: 'Nenhum grupo organizacional disponível.',
            loadFailed: 'Falha ao carregar opções.',
            deleteProjectTitle: 'Excluir projeto?',
            deleteFolderTitle: 'Excluir pasta?',
            removeMemberTitle: 'Remover membro?',
            removeMemberConfirmGeneric: 'Remover este membro do projeto?',
            addDocumentButton: 'Adicionar documento',
            addDocumentTitle: 'Adicionar documento ao projeto',
            fieldDocument: 'Selecionar documento',
            selectDocPlaceholder: 'Procurar um documento...',
            ownerLabel: 'Proprietário do sistema',
        },
        chat: {
            sidebarLabel: 'Bate-papos',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Seu AI Consultor Jurídico e de Conformidade — baseado nos dados do seu locatário e nos pacotes regulatórios instalados.',
            personaSelectorLabel: 'Pessoa',
            personaHeading: 'Escolha uma persona',
            startChatButton: 'Iniciar um novo bate-papo',
            startingChat: 'Iniciando bate-papo…',
            recentHeading: 'Bate-papos recentes',
            recentEmpty: 'Ainda não há bate-papos – inicie um acima.',
            untitledChat: 'Novo bate-papo',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Conversa',
            detailSubtitle: 'Pessoa: {persona}',
            composeHeading: 'Envie uma mensagem',
            composeLabel: 'Mensagem Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Pessoa',
            presetPersona: 'Persona predefinida',
            changePersona: 'Alterar persona',
            hidePersonaOptions: 'Ocultar opções de persona',
            starterHeading: 'Comece com uma destas ações',
            renameButton: 'Renomear',
            renamePrompt: 'Renomear bate-papo',
            deleteButton: 'Excluir',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Bate-papo não encontrado.',
            emptyChat: 'Nenhuma mensagem ainda. Diga olá para iniciar uma conversa.',
            roleUser: 'Você',
            roleAssistant: 'Forseti',
            roleSystem: 'Sistema',
            thinkingLabel: 'Pensando',
            toolCallPlaceholder: 'Chamada de ferramenta: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Edições de documentos propostas (V{version}).',
            disclaimer: 'Forseti apóia seu julgamento – ele não substitui um advogado licenciado.',
            sendButton: 'Enviar',
            sending: 'Enviando…',
            composePlaceholder: 'Pergunte ao Forseti…',
            composingPlaceholder: 'Forseti está respondendo…',
            streamingPlaceholder: 'Forseti está digitando…',
            promptInjectionTitle: 'Forseti derrubou alguns tokens de controle.',
            promptInjectionBody: 'Padrões despojados: {patterns}. Forseti verá apenas o texto da sua mensagem.',
            rateLimitTitle: 'Você está enviando mensagens muito rápido.',
            rateLimitRetryAfter: 'Tente novamente em {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti não tem certeza aqui.',
            counselReviewBody: 'Confirme com um advogado licenciado antes de agir de acordo com esta resposta.',
            legalConclusionRefused: 'Forseti recusou-se a emitir uma conclusão legal sem fundamentação. Tente search_regulatory primeiro ou procure um advogado.',
            toolCallDone: 'Feito.',
            toolCallArgsLabel: 'Argumentos',
            toolCallResultLabel: 'Resultado',
            toolResultLogged: 'Resultado da ferramenta capturado no log de auditoria.',
            toolResultAria: 'resultado da ferramenta',
            documentEditAria: 'edição de documento',
            createTaskFromMessage: 'Criar tarefa a partir disso',
            createTaskHeading: 'Criar tarefa a partir desta mensagem',
            createTaskTitleLabel: 'Título',
            createTaskTitleRequired: 'O título é obrigatório.',
            createTaskDescriptionLabel: 'Descrição',
            createTaskAssigneeLabel: 'Cessionário',
            createTaskAssigneePlaceholder: 'Selecione o responsável…',
            createTaskAssigneeNone: '- não atribuído -',
            createTaskDueDateLabel: 'Data de vencimento',
            createTaskWorkflowLabel: 'Fluxo de trabalho vinculado (opcional)',
            createTaskWorkflowPlaceholder: 'Selecione um fluxo de trabalho…',
            createTaskWorkflowNone: '- nenhum -',
            renameModalSubmit: 'Renomear',
            renameModalSaving: 'Salvando…',
            deleteConfirmTitle: 'Excluir bate-papo?',
            createTaskSubmit: 'Criar tarefa',
            createTaskSubmitting: 'Criando…',
            cancelButton: 'Cancelar',
            autoRouteHintMessage: 'Esta questão está mais na casa do leme de {persona}.',
            autoRouteSwitchButton: 'Mudar para {persona}',
            autoRouteSwitching: 'Trocando…',
            handoffSuggestionMessage: 'Ou abra um novo chat com {persona} levando esta questão adiante.',
            handoffSuggestionButton: 'Inicie um novo bate-papo com {persona}',
            handoffSuggestionWorking: 'Abrindo…',
            handoffSuggestionDismiss: 'Agora não',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Especialista em conformidade',
                'forseti-legal': 'Consultor jurídico',
                'forseti-dpo': 'Consultor DPO',
                'forseti-risk': 'Analista de risco',
                'forseti-auditor': 'Olhar do auditor',
            },
            exportButton: 'Exportar PDF',
            exportRunning: 'A exportar…',
            exportSuccess: 'Exportado: {filename} (abrir o explorador de ficheiros).',
            exportOpenFile: 'Abrir no explorador de ficheiros',
            exportDownload: 'Transferir PDF',
            citation: {
                verifyBadge: '[VERIFICAR]',
                verifyAria: 'a citação não pôde ser verificada - revise antes de confiar',
                popoverAria: 'Fonte para {label}',
                openSource: 'Página de código aberto',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'Evento gerado por AI da trilha de auditoria do Forseti.',
        },
        rowAction: {
            ask: 'Pergunte a Forseti',
            tooltipOpen: 'Pergunte a Forseti sobre isso {recordType}',
            tooltipLocked: 'Este {recordType} está bloqueado — Forseti pode responder perguntas, mas não pode fazer alterações.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — sobre esse disco',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Resumo',
            suggestedPersona: 'Personagem sugerida',
            askForseti: 'Pergunte a Forseti',
            runWorkflow: 'Execute um fluxo de trabalho',
            viewLineage: 'Ver linhagem de auditoria',
            createPdplEvidencePack: 'Criar pacote de evidências PDPL',
            prepareBreachDraft: 'Preparar rascunho da notificação',
            primaryActions: {
                projectSummary: 'Resumir documento do projeto',
                supplementSummary: 'Resumir pedido suplementar',
                authoritySummary: 'Resumir pedido da autoridade',
                rightsRequestResponse: 'Rascunhar resposta DSR',
                dpiaQuickReview: 'Executar revisão rápida de DPIA',
                taskControlWalkthrough: 'Percorrer evidência de controlo',
                vendorRiskNarrative: 'Gerar narrativa de risco do fornecedor',
                incidentLetter: 'Rascunhar carta de violação PDPL',
                ropaSufficiency: 'Executar verificação de suficiência RoPA',
                vendorDpaReview: 'Executar revisão DPA do fornecedor',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'O Forseti apenas prepara conteúdo preliminar para o Mẫu số 08 associado. Não decide se a notificação é obrigatória nem apresenta ou envia o formulário.',
            breachDraftReady: 'Rascunho do Mẫu số 08 pronto para revisão',
            breachDraftReviewBeforeSelecting: 'Rever antes de selecionar',
            workflowHeading: 'Fluxos de trabalho relevantes',
            workflowLoading: 'A carregar fluxos de trabalho relevantes…',
            workflowEmpty: 'Não há fluxos de trabalho relevantes disponíveis para este registo.',
            workflowError: 'Não foi possível carregar os fluxos de trabalho relevantes.',
            selectWorkflow: 'Selecionar {title}',
            workflowSelected: 'Fluxo de trabalho selecionado. O executor incorporado abre no passo seguinte.',
            workflowRunSelected: 'Executar o fluxo de trabalho selecionado',
            workflowStarting: 'A iniciar o fluxo de trabalho…',
            workflowRunError: 'Não foi possível iniciar este fluxo de trabalho.',
            workflowCreateGuided: 'Criar um fluxo de trabalho guiado para este registo',
            lineageHeading: 'Linhagem de auditoria deste registo',
            lineageLoading: 'A carregar linhagem de auditoria…',
            lineageEmpty: 'Não há eventos de linhagem do Forseti registados para este registo.',
            lineageError: 'Não foi possível carregar a linhagem de auditoria.',
            retry: 'Tentar novamente',
            chatContextLoading: 'A carregar o contexto do registo…',
            chatGuidance: {
                general: 'O Forseti pode resumir este registo, identificar informações em falta e sugerir passos de revisão.',
                privacy: 'O Forseti pode rever factos de privacidade, verificar obrigações dos referenciais instalados e ajudar a preparar um rascunho para revisão humana.',
                compliance: 'O Forseti pode identificar lacunas de conformidade, fundamentar requisitos em fontes instaladas e sugerir próximos passos com responsáveis definidos.',
                legal: 'O Forseti pode resumir o registo, localizar fontes fundamentadas e ajudar a preparar um rascunho para revisão jurídica.',
                risk: 'O Forseti pode avaliar os riscos registados, assinalar factos em falta e sugerir passos de revisão proporcionais.',
                audit: 'O Forseti pode examinar lacunas de evidência, relacionar o registo com os controlos instalados e sugerir um acompanhamento pronto para auditoria.',
            },
            starterPrompts: {
                identifyGaps: 'Que informações ou evidências faltam neste registo?',
                reviewFrameworks: 'Que obrigações dos nossos referenciais instalados devo rever para este registo?',
                prepareDraft: 'Ajude-me a preparar um rascunho baseado apenas neste registo e em fontes instaladas devidamente fundamentadas.',
                nextSteps: 'Que passos de revisão devo realizar a seguir?',
                summarizeRecord: 'Resuma este registo e assinale os pontos que exigem revisão humana.',
                assessRisk: 'Avalie os riscos registados, identifique os factos em falta e sugira passos de revisão.',
            },
            disclaimer: 'Forseti apóia seu julgamento – ele não substitui um advogado licenciado.',
            recordType: {
                project: 'Projeto',
                dpia: 'DPIA',
                dossier_filing: 'Arquivamento de Dossiê',
                transfer_assessment: 'Transferência transfronteiriça',
                vendor: 'Fornecedor',
                vendor_contract: 'Contrato de fornecedor',
                vendor_acceptance: 'Aceitação de risco do fornecedor',
                incident: 'Incidente',
                incident_op: 'Operação de Incidente',
                breach_notification: 'Notificação de violação',
                authority_request: 'Solicitação de autoridade',
                supplement_request: 'Solicitação de Suplemento',
                rights_request: 'Solicitação de direitos',
                contract: 'Contrato',
                task: 'Tarefa',
                data_mapping_activity: 'Atividade RoPA',
                compliance_rule: 'Regra de Conformidade',
                program: 'Programa',
                governance_document: 'Documento de Governança',
                remediation_plan: 'Plano de Remediação',
                cross_border_filing: 'Arquivamento Transfronteiriço',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Peça ao Forseti para redigir isso',
            drafting: 'Elaboração…',
            hint_pick_activity: 'Escolha uma atividade de processamento vinculada para ativar o rascunho do Forseti.',
            draft_origin_label: 'Rascunho do Forseti – revisão por campo',
            tokens_label: '≈ {count} tokens do seu balde mensal',
            accept_selected: 'Aceitar selecionado',
            reject_all: 'Rejeitar rascunho',
            field_checkbox_label: 'Aplicar rascunho do Forseti para {field}',
            field_labels: {
                description: 'Descrição',
                purpose: 'Objetivo',
                data_subjects: 'Titulares dos dados',
                data_types: 'Tipos de dados',
                necessity_justification: 'Necessidade e proporcionalidade',
                consultation_notes: 'Notas de consulta',
                dpo_opinion: 'DPO opinião',
                system_ids: 'Sistemas vinculados',
                vendor_ids: 'Fornecedores vinculados',
            },
            error_generic: 'Forseti não conseguiu redigir este DPIA. Tente novamente ou refine o registro de atividades.',
            variant_legend: 'Estrutura',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Nenhum framework instalado disponível',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Atenção: {count} conflitos entre estruturas se aplicam a esta variante. Revise antes de aceitar.',
        },
        formDraft: {
            button_label: 'Pedir ao Forseti para redigir este formulário',
            drafting: 'A redigir…',
            draft_origin_label: 'Rascunho do Forseti — reveja cada campo',
            tokens_label: '≈ {count} tokens do seu limite mensal',
            accept_selected: 'Aceitar selecionados',
            reject_all: 'Rejeitar rascunho',
            field_checkbox_label: 'Aplicar o rascunho do Forseti para {field}',
            excluded_note: '{count} campo(s) não podem ser redigidos (referências, anexos ou só de leitura).',
            error_generic: 'O Forseti não conseguiu redigir este formulário. Tente novamente.',
            empty_draft: 'O Forseti não produziu rascunhos de campos para este formulário.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Peça a Forseti para redigir este TIA',
            drafting: 'Elaboração de TIA…',
            hint_pick_destination: 'Defina o país de destino no registro de transferência antes que Forseti possa fazer o draft.',
            draft_origin_label: 'Rascunho do Forseti TIA – revisão por campo',
            tokens_label: '≈ {count} tokens do seu balde mensal',
            accept_selected: 'Aceitar selecionado',
            reject_all: 'Rejeitar rascunho',
            field_checkbox_label: 'Aplicar rascunho do Forseti para {field}',
            error_generic: 'Forseti não pôde redigir este TIA. Tente novamente ou refine o registro de transferência.',
            usage_details: 'Detalhes de utilização da IA',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Atualizações de registo recomendadas',
            mechanism_recommendation: 'Mecanismo de transferência: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Defina um prazo de revisão antes de atualizar esta transferência sensível.',
            processing_activity_warning: 'Associe pelo menos uma atividade de tratamento / registo ROPA antes de concluir a TIA.',
            go_to_field: 'Ir para o campo',
            adequacy_label: 'Regime de destino',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Atalho de transição/EEE',
            adequacy_status_unknown: 'Desconhecido – definir destino',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Atenção: {count} conflitos entre estruturas se aplicam a este regime de transferência. Revise antes de aceitar.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Factos obrigatórios em falta',
            missing_facts_hint: 'A Forseti assinalou factos que não constam do registo. Os campos que deles dependem não são pré-selecionados — forneça os factos ou aceite o campo deliberadamente.',
            risk_label: 'Risco calibrado',
            risk_low: 'Baixo',
            risk_medium: 'Médio',
            risk_high: 'Alto',
            jurisdiction_label: 'Acionadores de jurisdição',
            jurisdiction_none_installed: 'Nenhum quadro instalado abrange esta avaliação — nenhum regime jurídico foi avaliado. Instale um pacote regulamentar adequado antes de se basear nas conclusões jurídicas deste rascunho.',
            jurisdiction_none_assessed: 'O Forseti não devolveu qualquer avaliação de jurisdição. Nada foi avaliado — isto não é uma conclusão de que nenhum regime se aplica.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'A carregar os nomes dos regimes avaliados…',
            trigger_applicable: 'Aplica-se',
            trigger_not_applicable: 'Não acionado',
            trigger_unknown: 'Desconhecido — facto em falta',
            recipient_role_label: 'Papel do destinatário',
            recipient_role_controller: 'Responsável pelo tratamento',
            recipient_role_processor: 'Subcontratante',
            recipient_role_joint_controller: 'Responsável conjunto',
            recipient_role_unknown: 'Desconhecido — não indicado',
            opt_in_hint: 'Conclusão jurídica — selecione apenas se a aceitar com os factos disponíveis.',
        },
        mcp: {
            pageTitle: 'Chaves do conector MCP',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Emitir nova chave',
            issueModalTitle: 'Emitir chave MCP API',
            labelField: 'Etiqueta',
            labelHint: 'Uma breve descrição (por exemplo, "AI laboratório de capacidade — pesquisa regulatória").',
            permittedToolsField: 'Ferramentas permitidas',
            expiresAtField: 'Expira (opcional)',
            expiresAtHint: 'Deixe em branco para nunca expirar.',
            submit: 'Chave do problema',
            cancel: 'Cancelar',
            keyDisplayedOnce: 'Copie esta chave agora – você NÃO poderá vê-la novamente. Armazene-o em seu gerenciador de segredos (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'Eu copiei - feche',
            colLabel: 'Etiqueta',
            colPrefix: 'Prefixo',
            colStatus: 'Estado',
            colTools: 'Ferramentas',
            colCreatedAt: 'Criado',
            colLastUsed: 'Usado pela última vez',
            colCalls: 'Chamadas',
            colActions: 'Ações',
            statusActive: 'Ativo',
            statusRevoked: 'Revogado',
            statusExpired: 'Expirado',
            revokeAction: 'Revogar',
            rotateAction: 'Girar',
            confirmRevokeTitle: 'Revogar esta chave MCP?',
            confirmRevokeBody: 'Os clientes externos do Claude que usarem esta chave perderão o acesso imediatamente. Isto não pode ser desfeito.',
            confirmRevoke: 'Revogar chave',
            rotatedTitle: 'Chave girada',
            rotatedBody: 'A nova chave de texto simples está abaixo. A tecla antiga continua funcionando por 5 minutos para que todas as chamadas durante o voo sejam encerradas.',
            emptyState: 'Nenhuma chave MCP emitida ainda.',
            errorIssue: 'Não foi possível emitir a chave MCP.',
            errorList: 'Não foi possível carregar as chaves MCP.',
            errorRevoke: 'Não foi possível revogar a chave.',
            errorRotate: 'Não foi possível girar a chave.',
            neverUsed: '-',
            neverExpires: 'Nunca',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Memória Forseti',
            pageSubtitle: 'Navegue e selecione o contexto específico da organização que Forseti acumulou em conversas e decisões. As entradas selecionadas são bloqueadas contra retenção noturna e permanecem disponíveis para todas as pessoas.',
            filterCategory: 'Categoria',
            filterAllCategories: 'Todas as categorias',
            totalCount: '{count} entradas',
            colAnchor: 'Âncora',
            colCategory: 'Categoria',
            colSource: 'Fonte',
            colContent: 'Conteúdo',
            colCreatedAt: 'Criado',
            colActions: 'Ações',
            editButton: 'Editar',
            deleteButton: 'Excluir',
            promoteButton: 'Promover para curadoria',
            emptyState: 'Ainda não há lembranças – Forseti acumulará contexto enquanto você conversa.',
            categories: {
                framework_interpretation: 'Interpretação estrutural',
                process_preference: 'Preferência de processo',
                escalation_threshold: 'Limite de escalonamento',
                decision_context: 'Contexto de decisão',
                glossary_term: 'Termo do glossário',
                auto_inferred: 'Auto-inferido',
            },
            sources: {
                admin_curated: 'Curadoria',
                user_explicit: 'Explícito',
                auto: 'Inferido',
            },
            deleteConfirmTitle: 'Excluir esta memória?',
            deleteConfirmBody: 'A exclusão removerá esta âncora de todas as futuras inicializações de bate-papo do Forseti. Âncora: {anchor}',
            deleteConfirmLabel: 'Excluir',
            deleteCancelLabel: 'Cancelar',
            editModalTitle: 'Editar memória',
            editAnchorLabel: 'Âncora',
            editCategoryLabel: 'Categoria',
            editContentLabel: 'Conteúdo',
            editContentCount: '{n}/{max} caracteres',
            editEmptyError: 'O conteúdo não pode estar vazio.',
            editTooLongError: 'O conteúdo deve ter ≤1000 caracteres.',
            editAutoPromoteHint: 'Salvar essa memória autoinferida irá marcá-la como confirmada pelo usuário e removê-la da varredura de retenção.',
            editCancelButton: 'Cancelar',
            editSaveButton: 'Salvar',
            editSaving: 'Salvando…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Linhagem de auditoria',
            subtitle: 'Rastreie cada artefato gerado por AI anexado a um registro. Bate-papos, chamadas de ferramentas, tarefas, rascunhos de evidências e versões de documentos.',
            filterForm: 'Filtros',
            recordType: 'Tipo de registro',
            recordId: 'ID do registro',
            dateStart: 'De',
            dateEnd: 'Para',
            search: 'Pesquisar',
            loading: 'Carregando…',
            exportPdf: 'Exportar PDF',
            exportCsv: 'Exportar CSV',
            nodesHeading: 'Nós de linhagem',
            colType: 'Tipo',
            colLabel: 'Etiqueta',
            colOccurredAt: 'Ocorreu em',
            colActor: 'Ator',
            colId: 'ID',
            noResults: 'Nenhuma atividade de consultor para este registro.',
            draftBadge: 'Rascunho gerado por AI',
            prev: 'Anterior',
            next: 'Próximo',
            viewToggleTable: 'Ver como tabela',
            viewToggleGraph: 'Ver como gráfico',
            graphAriaLabel: 'Gráfico de linhagem (visual). Use a visualização de tabela para tecnologia assistiva.',
            graphLegendDraft: 'Rascunho de evidência AI',
            graphEmpty: 'Não há nós para representar graficamente.',
            paginationAriaLabel: 'Paginação',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Falha na exportação: {message}',
            truncatedNotice: 'Esta linhagem não está completa. A plataforma lê no máximo um número fixo de linhas de cada fonte e o limite foi atingido para: {sources}. As linhas além de um limite, se existirem, não são mostradas. Reduza o intervalo de datas para incluí-las.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Projeto',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Fornecedor',
                incident: 'Incidente',
                incident_op: 'Operação de incidente',
                rights_request: 'Solicitação de direitos',
                contract: 'Contrato',
                task: 'Tarefa',
                data_mapping_activity: 'Atividade de mapeamento de dados',
                compliance_rule: 'Regra de conformidade',
                program: 'Programa',
            },
            nodeTypes: {
                chat: 'Bate-papo',
                tool_call: 'Chamada de ferramenta',
                workflow_run: 'Execução do fluxo de trabalho',
                task: 'Tarefa',
                evidence: 'Evidência',
                document_version: 'Versão do documento',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Derivado',
                hash_mismatch: 'Incompatibilidade de hash',
                merkle_mismatch: 'Incompatibilidade de Merkle',
                source_missing: 'Fonte ausente',
                not_found: 'Não encontrado',
                chain_unreachable: 'Cadeia inacessível',
                service_unreachable: 'Serviço inacessível',
                pending: 'Ancoragem pendente',
            },
        },
        evidenceDraft: {
            draftBadge: 'RASCUNHO gerado por AI',
            pendingReviewBadge: 'Revisão pendente',
            approvedBadge: 'Aprovado',
            rejectedBadge: 'Rejeitado',
            moveToReviewButton: 'Marcar como pronto para revisão',
            approveButton: 'Aprovar',
            rejectButton: 'Rejeitar',
            rejectReasonLabel: 'Motivo da rejeição',
            humanSignoffBanner: 'Forseti apóia seu julgamento – ele não substitui um advogado licenciado. As evidências geradas por AI requerem aprovação humana antes de serem consideradas evidências formais.',
            pageTitle: 'AI rascunhos de evidências',
            pageSubtitle: 'Revise, aprove ou rejeite as evidências geradas por AI. Os rascunhos devem ser aprovados por um ser humano antes de serem considerados evidências formais.',
            filterAll: 'Tudo acionável',
            filterDrafts: 'Somente rascunhos',
            filterPending: 'Revisão pendente',
            filterApproved: 'Aprovado recentemente',
            filterRejected: 'Rejeitado recentemente',
            colId: 'ID da evidência',
            colStatus: 'Estado',
            colSource: 'Fonte',
            colCreatedAt: 'Criado',
            colActions: 'Ações',
            empty: 'Não há rascunhos de evidências AI neste status.',
            confirmApprove: 'Aprova esta evidência gerada por AI como formal?',
            confirmReject: 'Rejeite este rascunho de evidências. Isto não pode ser desfeito.',
            reasonRequired: 'É necessário um motivo de rejeição.',
            rejectModalTitle: 'Rejeitar rascunho de evidência',
            approveModalTitle: 'Aprovar rascunho de evidência',
            operationFailed: 'A operação falhou. Por favor, tente novamente.',
            moveToReviewSuccess: 'Marcado como pronto para revisão.',
            approveSuccess: 'Aprovado.',
            rejectSuccess: 'Rejeitado.',
            sourceLabelWorkflowRunTitle: 'Fluxo de trabalho: {title}',
            sourceLabelWorkflowRunId: 'Execução do fluxo de trabalho {id}',
            sourceLabelChatTitle: 'Bate-papo: {title}',
            sourceLabelChatId: 'Bate-papo Forseti {id}',
            sourceLabelEmpty: '-',
            filtersAriaLabel: 'Status do filtro',
        },
        workflows: {
            pageTitle: 'Fluxos de trabalho IA',
            pageSubtitle: 'Modelos de prompt de IA guardados que produzem tarefas e rascunhos de evidências.',
            newWorkflow: 'Novo fluxo de trabalho',
            newWorkflowHeading: 'Novo fluxo de trabalho IA',
            tabAll: 'Todos',
            tabBuiltin: 'Pacote integrado',
            tabMine: 'Os meus fluxos de trabalho',
            tabDetail: 'Detalhes',
            tabLinkedTasks: 'Tarefas vinculadas',
            builtinBadge: 'Integrado',
            builtinSubtitle: 'Fluxo de trabalho integrado.',
            userSubtitle: 'Fluxo de trabalho personalizado.',
            emptyHint: 'Ainda não há fluxos de trabalho.',
            loadTimeoutError: 'Não foi possível carregar os fluxos de trabalho após 10 segundos. Tente novamente, ou verifique o separador Rede do navegador para um pedido pendurado.',
            loadError: 'Falha ao carregar fluxos de trabalho.',
            hideError: 'Falha ao ocultar.',
            unhideError: 'Falha ao mostrar.',
            retry: 'Tentar novamente',
            producesTasks: 'Produz tarefas',
            producesEvidence: 'Produz rascunhos de evidências',
            hideButton: 'Ocultar da minha lista',
            unhideButton: 'Mostrar',
            runButton: 'Executar',
            editButton: 'Editar',
            shareButton: 'Partilhar',
            backToList: 'Voltar à lista',
            requireTitleAndPrompt: 'O título e o prompt são obrigatórios.',
            fieldTitle: 'Título',
            fieldDescription: 'Descrição',
            fieldType: 'Tipo',
            fieldPersona: 'Afinidade da persona',
            fieldPractice: 'Prática',
            fieldPrompt: 'Solicitação (remarcação)',
            fieldProducesTasks: 'Produz tarefas',
            fieldProducesEvidence: 'Produz rascunhos de evidências',
            fieldProjectId: 'Projeto (opcional)',
            fieldAttachmentIds: 'Anexos',
            fieldParentRecord: 'Registo principal (opcional)',
            fieldPersonaOverride: 'Substituição de persona (opcional)',
            typeAssistant: 'Assistente',
            typeTabular: 'Tabular',
            personaNone: '— nenhum —',
            parentNone: '— nenhum —',
            parentIdPlaceholder: 'ID do registo principal',
            selectProjectPlaceholder: 'Selecionar um projeto…',
            selectProjectFirst: 'Selecione um projeto para escolher anexos.',
            selectAttachmentsPlaceholder: 'Selecionar documentos…',
            noAttachmentsInProject: 'Este projeto não tem documentos anexados.',
            selectParentRecordPlaceholder: 'Selecionar um registo…',
            selectParentRecordFirst: 'Selecione primeiro um tipo de registo.',
            noRecordsForType: 'Não há registos deste tipo disponíveis.',
            shareUserSelectorLabel: 'Destinatários (utilizadores)',
            shareGroupSelectorLabel: 'Destinatários (grupos da organização)',
            selectUsersPlaceholder: 'Selecionar utilizadores…',
            selectOrgGroupsPlaceholder: 'Selecionar grupos da organização…',
            loadingOptions: 'A carregar…',
            loadFailed: 'Falha ao carregar opções.',
            parentRecordTypes: {
                dpia: 'AIPD',
                vendor: 'Fornecedor',
                incident: 'Incidente',
                rights_request: 'Pedido de direitos',
                contract: 'Contrato',
                data_mapping_activity: 'Atividade RoPA',
                compliance_rule: 'Regra de conformidade',
                program: 'Programa',
            },
            cancelButton: 'Cancelar',
            closeButton: 'Fechar',
            createButton: 'Criar',
            creating: 'A criar…',
            saveButton: 'Guardar',
            saving: 'A guardar…',
            shareWorkflowHeading: 'Partilhar fluxo de trabalho',
            shareIntro: 'A partilhar: {title}',
            shareUserIds: 'IDs de utilizador (separados por espaço ou vírgula)',
            shareGroupIds: 'IDs de grupo da organização (separados por espaço ou vírgula)',
            shareAllowEdit: 'Permitir aos destinatários editar',
            shareRequireRecipient: 'Forneça pelo menos um id de utilizador ou de grupo.',
            shareSuccess: 'Partilhado.',
            sharing: 'A partilhar…',
            runWorkflowHeading: 'Executar: {title}',
            runStarting: 'A iniciar…',
            runStatusLabel: 'Estado: {status}',
            runProducedCounts: 'Tarefas: {tasks} · Rascunhos de evidências: {evidence}',
            runPanelHeading: 'Execução do fluxo de trabalho',
            runPanelHeadingWithTitle: 'Execução do fluxo de trabalho: {title}',
            runViewTasks: 'Ver tarefas geradas',
            runViewEvidence: 'Ver rascunho de evidência',
            runResultHeading: 'Resultado do consultor',
            runNoResult: 'A execução foi concluída, mas não devolveu um resultado revisável. Não foram criadas tarefas, evidências nem anexos. Tente novamente com o registo ou os documentos necessários selecionados.',
            runCitationWarning: 'Algumas citações não puderam ser verificadas automaticamente. Reveja-as antes de confiar neste resultado.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Não foi possível carregar a execução do fluxo de trabalho.',
            runStreamFailed: 'A ligação ao progresso do fluxo de trabalho falhou.',
            runStatusValues: {
                pending: 'Pendente',
                running: 'Em execução',
                completed: 'Concluído',
                succeeded: 'Bem-sucedido',
                failed: 'Falhou',
                cancelled: 'Cancelado',
            },
            runProgressStatus: 'Estado: {status}',
            runProgressTaskCreated: 'Tarefa criada.',
            runProgressEvidenceDrafted: 'Rascunho de evidência criado.',
            runProgressAttachmentGenerated: 'Anexo gerado.',
            guidedWorkflowTitle: 'Fluxo de trabalho guiado para {recordType}',
            guidedWorkflowDescription: 'Criado para registos do tipo {recordType}. Reveja e personalize este fluxo de trabalho antes de o guardar.',
            guidedWorkflowPrompt: 'Reveja o registo do tipo {recordType} fornecido, identifique factos em falta e proponha próximos passos fundamentados para revisão humana.',
            promptHeading: 'Alerta',
            howItWorksHeading: 'O que acontece quando executas este fluxo de trabalho',
            builtinGuidance: 'O Forseti revê os registos e documentos que selecionar, devolve um resultado que o operador pode rever e cria tarefas ou evidências em rascunho apenas quando o resultado o suporta. Nada é aprovado ou submetido automaticamente.',
            editWorkflowHeading: 'Editar fluxo de trabalho',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Fluxo de trabalho não encontrado.',
            schedules: {
                tabTitle: 'Agendamentos',
                sectionHeading: 'Execuções agendadas',
                empty: 'Ainda não há agendamentos. Crie um para executar este fluxo de trabalho automaticamente.',
                addButton: 'Adicionar agendamento',
                addHeading: 'Agendar fluxo de trabalho',
                editHeading: 'Editar agendamento',
                fieldFrequency: 'Frequência',
                fieldDayOfWeek: 'Dia da semana',
                fieldDayOfMonth: 'Dia do mês',
                fieldTime: 'Hora (UTC)',
                fieldTimezone: 'Fuso horário',
                fieldProject: 'Projeto (opcional)',
                fieldAttachments: 'Anexos por defeito',
                fieldPersonaOverride: 'Substituição de persona (opcional)',
                frequencyDaily: 'Diária',
                frequencyWeekly: 'Semanal',
                frequencyMonthly: 'Mensal',
                weekdaySun: 'Domingo',
                weekdayMon: 'Segunda-feira',
                weekdayTue: 'Terça-feira',
                weekdayWed: 'Quarta-feira',
                weekdayThu: 'Quinta-feira',
                weekdayFri: 'Sexta-feira',
                weekdaySat: 'Sábado',
                statusActive: 'Ativo',
                statusPaused: 'Pausado',
                nextRunLabel: 'Próxima execução',
                lastRunLabel: 'Última execução',
                pauseButton: 'Pausar',
                resumeButton: 'Retomar',
                deleteButton: 'Eliminar',
                triggerButton: 'Executar agora',
                savingButton: 'A guardar…',
                saveButton: 'Guardar',
                triggered: 'Execução iniciada — verifique o histórico de execuções.',
            },
            branching: {
                helpHeading: 'Sintaxe de prompt condicional',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Conflitos entre estruturas',
            pageSubtitle: 'Catálogo selecionado de onde seus pacotes regulatórios instalados interagem de maneiras conflitantes. Superfícies Forseti; os humanos decidem.',
            noInstalledPacks: 'Nenhum pacote regulatório instalado para esta organização — instale pelo menos dois pacotes para revelar conflitos entre estruturas.',
            noConflicts: 'Nenhum conflito corresponde aos filtros atuais.',
            allAcknowledged: 'Todos os conflitos visíveis foram reconhecidos. Bom trabalho.',
            columnSeverity: 'Gravidade',
            columnTitle: 'Título',
            columnFrameworks: 'Estruturas',
            columnTopic: 'Tópico',
            columnStatus: 'Estado',
            filterFrameworkPlaceholder: 'Todas as estruturas',
            filterTopicPlaceholder: 'Todos os tópicos',
            filterSeverityPlaceholder: 'Todas as gravidades',
            filterStatusPlaceholder: 'Todos os status',
            filterStatusAcknowledged: 'Reconhecido',
            filterStatusUnacknowledged: 'Não reconhecido',
            severityCritical: 'Crítico',
            severityHigh: 'Alto',
            severityMedium: 'Médio',
            severityLow: 'Baixo',
            statusAcknowledged: 'Reconhecido',
            statusInReview: 'Em revisão',
            statusResolved: 'Resolvido',
            statusWontFix: 'Não vou consertar',
            statusUnacknowledged: 'Não reconhecido',
            drawerSummaryHeading: 'Resumo',
            drawerSideAHeading: 'Lado A',
            drawerSideBHeading: 'Lado B',
            drawerRequirementsLabel: 'Requisitos citados',
            drawerProvisionsLabel: 'Disposições citadas',
            drawerSuggestedResolutionHeading: 'Resolução sugerida',
            drawerLegalCounselBadge: 'Revisão do advogado necessária',
            drawerAckHeading: 'Reconhecimento',
            drawerAckStatusLabel: 'Estado',
            drawerAckAssigneeLabel: 'Atribuído a',
            drawerAckDueDateLabel: 'Data de vencimento',
            drawerAckNotesLabel: 'Notas',
            drawerAckButtonAcknowledge: 'Reconhecer',
            drawerAckButtonUpdate: 'Atualizar',
            drawerAckButtonUnacknowledge: 'Desconhecer',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Pesquisar título ou resumo…',
            searchButton: 'Pesquisar',
            searchButtonAriaLabel: 'Execute a pesquisa de conflitos',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Falha na exportação: {message}',
            exportAriaLabel: 'Conflitos de exportação',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Revisões tabulares',
            pageSubtitle: 'Compare N documentos em M atributos com citações fundamentadas.',
            newReview: 'Nova revisão',
            newReviewHeading: 'Nova revisão tabular',
            emptyHint: 'Ainda não há revisões tabulares.',
            docsLabel: '{n} documentos',
            colsLabel: '{n} colunas',
            detailSubtitle: '{docs} documentos · {cols} colunas',
            addColumn: 'Adicionar coluna',
            addColumnHeading: 'Adicionar coluna',
            columnInfo: 'Ver prompt e formato',
            columnInfoAria: 'Mostrar o prompt e o formato da coluna {name}',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Formato de retorno',
            columnInfoPresets: 'Valores permitidos',
            columnInfoCitations: 'Citações',
            columnInfoCitationsRequired: 'Obrigatório',
            removeColumn: 'Remover coluna',
            removeColumnAria: 'Remover coluna {name}',
            removeColumnConfirm: 'Remover a coluna "{name}"? As suas células extraídas serão eliminadas.',
            deleteReview: 'Eliminar',
            deleteReviewAria: 'Eliminar revisão {title}',
            deleteReviewConfirm: 'Eliminar a revisão "{title}"? Isto remove permanentemente as suas colunas, células e discussões.',
            runAll: 'Executar todos',
            running: 'A executar…',
            cancel: 'Cancelar',
            exportXlsx: 'Exportar Excel',
            exportCsv: 'Exportar CSV',
            exporting: 'A exportar…',
            backToList: 'Voltar à lista',
            notFound: 'Revisão tabular não encontrada.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Anexe documentos para iniciar a revisão.',
            noColumns: 'Adicione colunas para definir o que extrair.',
            tableAriaLabel: 'Grelha de extração',
            threadsCountAria: '{n} publicação(ões) de discussão',
            startDiscussionAria: 'Iniciar discussão',
            docColumn: 'Documento',
            statusPending: 'Pendente',
            statusRunning: 'A executar…',
            statusFailed: 'Falhou: {msg}',
            statusCancelled: 'Cancelado',
            citationsCount: '{n} citação(ões)',
            citationsView: 'Ver trechos de origem',
            citationPage: 'Página {n}',
            titleRequired: 'O título é obrigatório.',
            fieldTitle: 'Título',
            fieldProjectId: 'Projeto (opcional)',
            fieldAttachmentIds: 'Anexos',
            fieldColumnName: 'Nome da coluna',
            fieldColumnNameHelp: 'Um rótulo curto e descritivo para esta coluna na grelha de extração.',
            columnNamePlaceholder: 'p. ex. Data de início do contrato',
            fieldExtractionPrompt: 'Prompt de extração',
            fieldExtractionPromptHelp: 'Descreva a informação que pretende extrair. Seja específico para obter melhores resultados.',
            columnPromptPlaceholder: 'O que deve a Forseti procurar nos documentos?',
            fieldReturnFormat: 'Formato de retorno',
            fieldReturnFormatHelp: 'Controla como a IA formata a sua resposta (por exemplo, como data ou número).',
            fieldPresets: 'Valores permitidos (um por linha)',
            fieldPresetsPlaceholder: 'sim\nnão\nparcial',
            fieldCitationsRequired: 'Citação obrigatória',
            formatShortText: 'Texto curto',
            formatLongText: 'Texto longo',
            formatEnum: 'Enum',
            formatNumber: 'Número',
            formatDate: 'Data',
            formatList: 'Lista',
            columnRequireFields: 'O nome e o prompt são obrigatórios.',
            addColumnError: 'Falha ao adicionar a coluna.',
            cancelButton: 'Cancelar',
            createButton: 'Criar',
            creating: 'A criar…',
            addButton: 'Adicionar',
            adding: 'A adicionar…',
            runConfirmHeading: 'Executar extração',
            estimateBody: 'Estimado ~{units} unidades faturáveis (confiança {confidence}).',
            starting: 'A iniciar…',
            confirmRun: 'Confirmar execução',
            charts: {
                title: 'Gráficos de coluna',
                tabsAria: 'Colunas dos gráficos',
                enumAria: 'Distribuição de valores enum',
                numberAria: 'Histograma de distribuição numérica',
                dateAria: 'Linha temporal de datas',
                enumEmpty: 'Sem células concluídas.',
                numberEmpty: 'Sem valores numéricos.',
                numberExcluded: '{n} célula(s) excluídas (vazias / não numéricas / não concluídas).',
                dateEmpty: 'Sem valores de data.',
                dateExcluded: '{n} célula(s) excluídas.',
                unsupportedFormat: 'Formato de coluna não suportado.',
            },
            threads: {
                drawerTitle: 'Discussão',
                loading: 'A carregar…',
                empty: 'Ainda não há publicações. Inicie a conversa.',
                composerLabel: 'Novo comentário',
                composerPlaceholder: 'Adicionar um comentário…',
                post: 'Publicar',
                posting: 'A publicar…',
                edit: 'Editar',
                editLabel: 'Editar publicação',
                delete: 'Eliminar',
                save: 'Guardar',
                cancel: 'Cancelar',
                close: 'Fechar',
                editedBadge: 'editado',
                loadError: 'Falha ao carregar a tópico',
                postError: 'Falha ao publicar',
                editError: 'Falha ao editar',
                deleteError: 'Falha ao eliminar',
            },
            compare: {
                pageTitle: 'Comparar revisões',
                leftReviewLabel: 'Revisão à esquerda',
                rightReviewLabel: 'Revisão à direita',
                pickReview: 'Escolha uma revisão para comparar',
                noSharedDocuments: 'Estas revisões não partilham documentos — a comparação requer pelo menos um anexo comum.',
                sharedDocumentsHeading: 'Documentos partilhados',
                leftOnlyHeading: 'Colunas apenas na revisão à esquerda',
                rightOnlyHeading: 'Colunas apenas na revisão à direita',
                differs: 'Difere',
                identical: 'Idêntico',
                emptyValue: '—',
                compareButton: 'Comparar com…',
                documentHeading: 'Documento',
                errorMissingIds: 'Os IDs das revisões à esquerda e à direita são obrigatórios.',
                errorComparisonFailed: 'A comparação falhou.',
                errorLoadFailed: 'Falha ao carregar revisões.',
                pickerLoading: 'A carregar…',
                pickerNoOtherReviews: 'Não há outras revisões disponíveis para comparar.',
                pickerCancel: 'Cancelar',
            },
        },
        documentReview: {
            pageTitle: 'Revisão de documentos',
            pageSubtitle: 'Aceite ou rejeite as edições sugeridas por AI neste documento.',
            sidePanelLabel: 'AI linhas vermelhas',
            sidePanelHeading: 'AI linhas vermelhas',
            noPendingEdits: 'Nenhuma edição pendente.',
            pendingCount: '{count} pendente',
            acceptAllButton: 'Aceitar tudo',
            rejectAllButton: 'Rejeitar tudo',
            acceptAllTitle: 'Aceitar todas as edições pendentes?',
            acceptAllConfirm: 'Isso aceitará {count} edições pendentes e criará uma ou mais novas versões. Continuar?',
            editsHeading: 'Edições',
            editsEmpty: 'Nenhuma edição para revisar.',
            versionsHeading: 'Histórico de versões',
            versionsEmpty: 'Ainda não há versões.',
            editLabel: 'Editar',
            editLabelWithId: 'Editar #{id}',
            acceptButton: 'Aceitar',
            rejectButton: 'Rejeitar',
            accepting: 'Aceitando…',
            rejecting: 'Rejeitando…',
            diffDeletedAria: 'Excluído',
            diffInsertedAria: 'Inserido',
            statusPending: 'Pendente',
            statusAccepted: 'Aceito',
            statusRejected: 'Rejeitado',
            versionLabel: 'Versão',
            versionAriaLabel: 'Versão {n}',
            versionAriaLabelCurrent: 'Versão {n} ({current})',
            versionCurrentTag: 'atual',
            versionSourceLabel: 'Fonte',
            versionCreatedLabel: 'Criado',
            versionModelLabel: 'Modelo',
            sourceUpload: 'Carregar',
            sourceUserUpload: 'Carregamento do usuário',
            sourceAssistantEdit: 'Edição do assistente',
            sourceUserAccept: 'Edição aceita pelo usuário',
            sourceUserReject: 'Edição rejeitada pelo usuário',
            sourceGenerated: 'Gerado',
            extractionExtracted: 'Texto extraído',
            extractionFailed: 'Falha na extração de texto',
            extractionUnsupported: 'Não foi possível extrair o texto',
            extractionPages: '{n} páginas',
            extractionWords: '{n} palavras',
            extractionReasonScanned: 'Isto parece um documento digitalizado ou apenas de imagens. O Forseti ainda não o consegue ler — carregue um ficheiro de texto ou cole o conteúdo diretamente.',
            extractionReasonEncrypted: 'O ficheiro está protegido por palavra-passe. Remova a proteção e carregue-o novamente.',
            extractionReasonMalformed: 'Não foi possível analisar o ficheiro, que pode estar corrompido.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'O ficheiro é demasiado grande para processar.',
            extractionReasonTimeout: 'O processamento demorou demasiado. Experimente um ficheiro mais pequeno ou simples.',
            extractionReasonUnknown: 'Não há mais detalhes disponíveis.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Texto do documento',
            textPaneTitle: 'Texto do documento',
            textPaneVersion: 'Versão {n}',
            textPaneEmpty: 'Não há texto disponível para esta versão. PDFs digitalizados e alguns tipos de documento não têm texto extraível.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Revisão de documentos',
            pageTitle: 'Revisão de documentos',
            pageSubtitle: 'Documentos da sua organização que têm edições sugeridas pela IA pendentes de revisão ou versões originadas pelo Forseti.',
            colDocument: 'Documento',
            colLinkedRecord: 'Registo vinculado',
            colPendingEdits: 'Edições pendentes',
            colLastActivity: 'Última atividade',
            colOpen: 'Abrir',
            open: 'Abrir',
            empty: 'Ainda não há documentos sob revisão do Forseti. Os documentos entram nesta fila quando o Forseti sugere uma edição durante um chat, quando um fluxo de IA produz um redline, ou quando envia um documento para revisão usando o botão acima.',
            loading: 'A carregar…',
            loadError: 'Falha ao carregar os documentos sob revisão.',
            retryButton: 'Tentar novamente',
            noLinkedRecord: '—',
            pendingZero: 'Nenhum',
            sendForReviewButton: 'Enviar um documento para revisão',
            sendForReviewTitle: 'Enviar um documento para revisão',
            sendForReviewSubtitle: 'Carregue um documento .txt, .md, .pdf ou .docx (até 50 MB). É adicionado à fila de revisão e o Forseti irá fazer uma passagem de redline sobre ele.',
            sendForReviewTabUpload: 'Carregar novo',
            sendForReviewPickerLabel: 'Pesquisar anexos',
            sendForReviewPickerPlaceholder: 'Digite para pesquisar…',
            sendForReviewPickerEmpty: 'Não há anexos correspondentes. Tente uma pesquisa diferente.',
            sendForReviewSubmit: 'Enviar para revisão',
            sendForReviewCancel: 'Cancelar',
            sendForReviewSubmitting: 'A enviar…',
            sendForReviewError: 'Não foi possível enviar o documento para revisão. Tente novamente.',
            sendForReviewSuccess: 'Documento enviado para revisão.',
            sendForReviewAcceptedExt: 'Aceites: {ext}.',
            sendForReviewMaxSize: 'Tamanho máx. {mb} MB.',
        },
        chats: {
            exportError: 'A exportação falhou.',
        },
    },
    pdplEvidencePacks: {
        title: 'Pacotes de provas PDPL',
        subtitle: 'Submissão controlada pelo cliente para o MoPS A05',
        nav: { emailProviders: 'Fornecedores de email' },
        providers: {
            pageTitle: 'Fornecedores de email',
            pageSubtitle: 'Configure o fornecedor de correio de saída da sua organização para submissões de pacotes de provas PDPL ao MoPS A05.',
            architectureRuleHeading: 'Regra de arquitetura (bloqueada)',
            architectureRuleBody: 'A AesirX não é remetente dos emails de pacotes de provas. Cada submissão usa um fornecedor que a sua organização possui e controla. A AesirX nunca envia em seu nome.',
            empty: 'Ainda não há fornecedores configurados.',
            createCta: 'Criar fornecedor',
            statusPendingConsent: 'A aguardar consentimento',
            statusPendingConnectionCheck: 'A aguardar teste de ligação',
            statusActive: 'Ativo',
            statusConnectionCheckFailed: 'Teste de ligação falhou',
            statusDisabled: 'Desativado',
            acceptConsentCta: 'Aceitar consentimento',
            runConnectionCheckCta: 'Executar teste de ligação',
            disableCta: 'Desativar',
            disableConfirm: 'Desativar este fornecedor interrompe os envios diretos até que um novo fornecedor seja configurado e o consentimento seja confirmado novamente. Continuar?',
            columnDisplayName: 'Nome',
            columnFromAddress: 'Endereço do remetente',
            columnStatus: 'Estado',
            columnLastCheck: 'Último teste',
            columnActions: 'Ações',
            createModalTitle: 'Novo fornecedor SMTP',
            createDisplayNameLabel: 'Nome a apresentar',
            createFromAddressLabel: 'Endereço do remetente',
            createReplyToLabel: 'Endereço de resposta (opcional)',
            createSmtpHostLabel: 'Servidor SMTP',
            createSmtpPortLabel: 'Porta SMTP',
            createSmtpUsernameLabel: 'Utilizador SMTP',
            createSmtpPasswordLabel: 'Palavra-passe SMTP',
            createSmtpEncryptionLabel: 'Encriptação',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implícito)',
            createSmtpEncryptionNone: 'Nenhuma',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Em breve',
            createOauthDisabledHint: 'Os fornecedores OAuth ainda não estão ativados. Use SMTP por agora.',
            createSubmitCta: 'Criar fornecedor',
            createCancelCta: 'Cancelar',
            createErrorInvalidFromAddress: 'O endereço do remetente deve ser uma caixa de email válida.',
            createErrorDuplicateFromAddress: 'Já existe outro fornecedor configurado para este endereço do remetente.',
            createErrorGeneric: 'Não foi possível criar o fornecedor. Reveja o formulário e tente novamente.',
            consentModalTitle: 'Confirmar regra de arquitetura',
            consentLocaleToggleEn: 'Inglês',
            consentLocaleToggleVi: 'Vietnamita',
            consentAcknowledgeCta: 'Confirmo — aceitar consentimento',
            consentCancelCta: 'Cancelar',
            consentErrorMismatch: 'O texto do consentimento foi modificado e já não corresponde ao aviso canónico. Reabra a caixa de diálogo e submeta novamente.',
            testModalTitle: 'Teste de ligação',
            testRunningCta: 'A testar ligação…',
            testRunCta: 'Executar teste de ligação',
            testRetryCta: 'Repetir teste de ligação',
            testSuccessHeading: 'Ligação bem-sucedida',
            testSuccessBody: 'Autenticado contra {host}:{port} em {latencyMs} ms. O fornecedor está pronto para envios diretos.',
            testFailureHeading: 'Ligação falhou',
            testFailureBody: 'O teste não pôde ser concluído. Reveja o motivo abaixo e tente novamente depois de corrigido.',
            testFailureReasonLabel: 'Motivo da falha',
            testCloseCta: 'Fechar',
            detailDrawerTitle: 'Detalhes do fornecedor',
            detailDrawerHistoryHeading: 'Histórico de testes de ligação',
            detailDrawerConsentHeading: 'Confirmação de consentimento',
            detailDrawerNoHistory: 'Ainda não há testes de ligação registados.',
            detailDrawerNoConsent: 'O consentimento ainda não foi confirmado.',
            outcomeSuccess: 'Sucesso',
            outcomeFailure: 'Falha',
        },
        builder: {
            pageTitle: 'Montar pacote de provas',
            pageSubtitle: 'Carregue os 6 ficheiros das partes, escolha o tipo de pacote e monte o ZIP determinístico de 8 partes para submissão.',
            packTypeLabel: 'Tipo de pacote',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Parte 01 — Carta de apresentação (PDF)',
            part02AuthorityForm: 'Parte 02 — Formulário da autoridade (PDF)',
            part03Supporting: 'Parte 03 — Provas de apoio (≥1)',
            part04Safeguards: 'Parte 04 — Salvaguardas',
            part04SafeguardsRequiredOtia: '(obrigatório para OTIA)',
            part05PriorCorrespondence: 'Parte 05 — Correspondência anterior (opcional)',
            part06Signature: 'Parte 06 — Provas de assinatura e carimbo (≥1)',
            uploadPickFile: 'Escolher ficheiro',
            uploadPickFiles: 'Escolher ficheiros',
            uploadRemoveFile: 'Remover',
            requiredMissing: 'Parte obrigatória em falta',
            assembleCta: 'Montar pacote',
            assembling: 'A montar…',
            assembleSuccessHeading: 'Pacote montado',
            assembleSuccessSummary: '{fileCount} ficheiros · {bytes} bytes · SHA-256 {shaPrefix}…',
            composeNextCta: 'Redigir rascunho de e-mail',
            warningsHeading: 'Avisos de validação',
            assembleError: 'Não foi possível montar o pacote. Reveja os dados e tente novamente.',
            forsetiAssistHeading: 'Assistência IA Forseti (opcional)',
            forsetiAssistHint: 'Execute o PDPL Evidence Pack Assembly Advisor para destacar partes em falta, redigir prosa de carta de apresentação e sinalizar lacunas de salvaguarda. AesirX não é o remetente — a IA apenas aconselha; você aprova cada artefacto.',
            forsetiAssistCta: 'Abrir biblioteca de fluxos Forseti',
            forsetiAiRunIdLabel: 'ID de execução IA Forseti vinculado (cole para registar a assistência IA)',
        },
        draft: {
            pageTitle: 'Rascunho de e-mail',
            pageSubtitle: 'Redija e edite o rascunho de e-mail. O envio direto chegará num lançamento posterior.',
            composeCta: 'Redigir rascunho',
            composing: 'A redigir…',
            noDraftYet: 'Ainda não há rascunho. Redija um para começar.',
            localeToggleLabel: 'Idioma',
            localeEn: 'Inglês',
            localeVi: 'Vietnamita',
            subjectLabel: 'Assunto',
            bodyLabel: 'Corpo',
            saveCta: 'Guardar alterações',
            saving: 'A guardar…',
            saveError: 'Não foi possível guardar o rascunho. Tente novamente.',
            manifestSummaryLabel: 'Resumo do manifesto',
            coverLetterLabel: 'Carta de apresentação (PDF)',
            coverLetterDownloadCta: 'Transferir PDF',
            editHistoryHeading: 'Histórico de edições',
            editHistoryEmpty: 'Ainda não há edições.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 aviso de canal não oficial',
            uploadSentProofCta: 'Carregar prova de envio manual',
            versionLabel: 'Versão',
            statusDraft: 'Rascunho',
            statusEdited: 'Editado',
            statusFinalized: 'Finalizado',
        },
        sentProof: {
            modalTitle: 'Carregar prova de envio manual',
            architectureLockNote: 'A AesirX não é o remetente. Esta prova regista o envio manual que efetuou através do seu próprio cliente de correio.',
            fileLabel: 'Ficheiro de prova (.eml, .png, .jpg)',
            envelopeFromLabel: 'De',
            envelopeToLabel: 'Para',
            envelopeSubjectLabel: 'Assunto (no envio)',
            envelopeSentAtLabel: 'Enviado às',
            envelopeAttachmentShaLabel: 'SHA-256 do anexo (no envio)',
            uploadCta: 'Carregar prova',
            uploading: 'A carregar…',
            uploadSuccess: 'Prova de envio carregada.',
            uploadErrorInvalidMime: 'Tipo de ficheiro não permitido. Use .eml, .png, .jpg ou .jpeg.',
            uploadErrorGeneric: 'Não foi possível carregar a prova. Tente novamente.',
            existingProofsHeading: 'Provas de envio registadas',
            existingProofsEmpty: 'Ainda não há provas de envio registadas.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Pacote de provas',
            notFoundMessage: 'Pacote não encontrado — pode ter sido removido ou a ligação está incorreta.',
            manifestHeading: 'Manifesto',
            dossierLabel: 'Processo',
            packShaLabel: 'SHA-256 do pacote',
            fileCountLabel: 'Ficheiros',
            bytesLabel: 'Tamanho',
            composeDraftCta: 'Redigir rascunho',
            sendComingSoonCta: 'Envio direto: em breve',
            sendComingSoonHint: 'O envio direto está em desenvolvimento ativo. Use "Carregar prova de envio manual" na página do rascunho até estar disponível.',
            sendCta: 'Enviar através do fornecedor configurado',
            sendDisabledNoProviderHint: 'Configure primeiro um fornecedor de correio controlado pelo cliente. A AesirX nunca envia em seu nome.',
            timelineHeading: 'Histórico de envios',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Enviar pacote de provas',
            providerPickerLabel: 'Fornecedor de correio configurado',
            providerPickerHint: 'A AesirX nunca envia em seu nome. Escolha um dos fornecedores configurados em baixo.',
            providerPickerEmpty: 'Não existem fornecedores ativos configurados. Configure um na página de Fornecedores de Correio antes de enviar.',
            providerPickerEmptyCta: 'Abrir Fornecedores de Correio',
            draftPickerLabel: 'Rascunho de correio',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Ainda não foi redigido nenhum rascunho para este pacote. Redija um primeiro na página do rascunho.',
            recipientToLabel: 'Para',
            recipientToHint: 'Um ou mais endereços de e-mail, separados por vírgulas.',
            recipientCcLabel: 'Cc (separados por vírgulas)',
            recipientCcHint: 'Opcional. Lista de endereços de e-mail separados por vírgulas.',
            recipientBccLabel: 'Bcc (separados por vírgulas)',
            recipientBccHint: 'Opcional. Lista de endereços de e-mail separados por vírgulas.',
            consentReaffirmHeading: 'Reafirmar o consentimento do fornecedor',
            submitCta: 'Enviar agora',
            sending: 'A enviar…',
            cancelCta: 'Cancelar',
            sendSuccess: 'Envio registado. O pacote foi transmitido através do seu fornecedor configurado.',
            sendErrorGeneric: 'Não foi possível enviar o pacote. Reveja o erro abaixo e tente novamente.',
            sendErrorAlreadyFinalized: 'Este rascunho já foi utilizado para um envio bem-sucedido. Redija um novo rascunho antes de enviar de novo.',
            sendErrorInvalidRecipients: 'Um ou mais endereços de destinatário não são caixas de correio válidas.',
            // Timeline
            timelineEmpty: 'Ainda não há envios registados. Use "Enviar através do fornecedor configurado" acima para transmitir através do seu fornecedor SMTP / Microsoft 365 / Google Workspace controlado pelo cliente.',
            timelineProviderLabel: 'Enviado através do seu fornecedor configurado',
            timelineSentAtLabel: 'Enviado',
            timelineRecipientLabel: 'Destinatário',
            timelineOutcomeSent: 'Enviado',
            timelineOutcomeFailed: 'Falhou',
            timelineViewDetailsCta: 'Ver detalhes',
            // Authority-response chips
            authorityChipNone: 'A aguardar resposta',
            authorityChipAcknowledgement: 'Acusação de receção',
            authorityChipAcceptance: 'Aceitação',
            authorityChipRejection: 'Recusa',
            authorityChipSupplementRequest: 'Pedido de complemento',
            authorityChipNoResponse: 'Sem resposta',
            // Hard-copy chips
            hardCopyChipNone: 'Sem seguimento em papel',
            hardCopyChipPending: 'Expedido — entrega pendente',
            hardCopyChipDelivered: 'Entregue',
            // Drawer
            drawerTitle: 'Registo de envio',
            drawerEnvelopeHeading: 'Envelope',
            drawerEnvelopeFromLabel: 'De',
            drawerEnvelopeToLabel: 'Para',
            drawerEnvelopeCcLabel: 'Cc',
            drawerEnvelopeBccLabel: 'Cco',
            drawerEnvelopeSubjectLabel: 'Assunto',
            drawerEnvelopeMessageIdLabel: 'ID da mensagem',
            drawerOutcomeHeading: 'Resultado',
            drawerSnapshotHeading: 'Instantâneo da cadeia de custódia',
            drawerSnapshotPackShaLabel: 'SHA-256 do pacote no envio',
            drawerSnapshotDraftVersionLabel: 'Versão do rascunho no envio',
            drawerSnapshotAttachmentLabel: 'ZIP transmitido',
            drawerSnapshotAttachmentDownloadCta: 'Transferir ZIP',
            drawerAuthorityResponseHeading: 'Resposta da autoridade',
            drawerHardCopyHeading: 'Seguimento em papel',
            drawerSentByLabel: 'Enviado por',
            drawerSendIdLabel: 'ID do registo de envio',
            drawerSmtpResponseLabel: 'Resposta SMTP',
            drawerFailureReasonLabel: 'Motivo da falha',
            drawerLatencyLabel: 'Latência',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Aceite',
            drawerRejectedLabel: 'Rejeitado',
            drawerCloseCta: 'Fechar',
            // Authority-response panel
            authorityRecordCta: 'Registar resposta',
            authorityReclassifyCta: 'Reclassificar',
            authorityClassificationLabel: 'Classificação',
            authorityReceivedAtLabel: 'Recebida em',
            authorityFreeTextLabel: 'Notas (opcional)',
            authorityEvidenceLabel: 'Ficheiro de prova (.eml / .pdf / .png / .jpg — opcional)',
            authorityReasonLabel: 'Motivo da reclassificação',
            authorityHistoryHeading: 'Histórico de reclassificações',
            authorityHistoryEmpty: 'Não há reclassificações registadas.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Guardar resposta',
            authoritySubmitting: 'A guardar…',
            authorityErrorInvalidMime: 'Tipo de ficheiro de prova não permitido. Use .eml, .pdf, .png ou .jpg.',
            authorityErrorGeneric: 'Não foi possível guardar a resposta. Tente novamente.',
            authorityRecordedAtLabel: 'Registada em',
            authorityRecordedByLabel: 'Registada por',
            authorityEvidenceFilenameLabel: 'Prova',
            authorityEvidenceDownloadCta: 'Transferir prova',
            // Hard-copy panel
            hardCopyRecordCta: 'Registar seguimento em papel',
            hardCopyConfirmCta: 'Confirmar entrega',
            hardCopyEditNotesCta: 'Editar notas de entrega',
            hardCopyPostedAtLabel: 'Expedido em',
            hardCopyCourierLabel: 'Estafeta',
            hardCopyTrackingLabel: 'Número de rastreio',
            hardCopyRecipientLabel: 'Destinatário na etiqueta',
            hardCopyReceiptLabel: 'Prova de receção (.pdf / .png / .jpg — opcional)',
            hardCopyReceiptDownloadCta: 'Transferir comprovativo',
            hardCopyDeliveredAtLabel: 'Entregue em',
            hardCopyDeliveryNotesLabel: 'Notas de entrega (opcional)',
            hardCopySubmitCta: 'Guardar seguimento',
            hardCopySubmitting: 'A guardar…',
            hardCopyErrorInvalidMime: 'Tipo de ficheiro de recibo não permitido. Use .pdf, .png ou .jpg.',
            hardCopyErrorGeneric: 'Não foi possível guardar o seguimento. Tente novamente.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.47.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    product_identification: {
        common: {
            retry: 'Retry',
            loading: 'Loading…',
            cancel: 'Cancel',
            close: 'Close',
            no_access_title: 'No access',
            no_access_message: 'Your role cannot read this register.',
            install_direct: 'Installed Direct',
            install_reference: 'Installed for reference',
            reference_mode_body: 'The overlay that declares this register is installed for reference. Scope determinations can still be recorded and read here, and the sector watch read; no duty and no clock is run from them until the overlay is installed Direct from the Overlay Catalog.',
        },
        participation: {
            compulsory: 'Compulsory',
            voluntary: 'Voluntary',
            outside_scope: 'Outside the scope',
            compulsory_rules_disapplied: 'Compulsory rules disapplied',
            not_determined: 'Open determination',
            unclassified: 'No class declared',
        },
        scope: {
            title: 'Product Scope Determinations',
            subtitle: 'A person’s recorded reading, per product line, of whether the identification and traceability regime reaches it, with the instrument relied on and a second person’s review. This register records the reading; it never decides it.',
            page_guide: 'Record one determination per product line from the options the installed pack declares. Nothing is preselected or suggested. Recording again keeps the earlier version, and compulsory and voluntary lines are always counted separately.',
            load_failed: 'The scope determinations could not be loaded.',
            none_declared: 'No installed framework declares the scope-determination register.',
            applicability_heading: 'Who the regime reaches, as the installed pack states it',
            mandatory_scope: 'Compulsory',
            voluntary_scope: 'Voluntary',
            out_of_scope: 'Outside the scope',
            mandatory_rules_disapplied: 'Compulsory rules disapplied',
            list_status_note: 'Status of the lists',
            counts_heading: 'Product lines by class',
            filter_label: 'Class',
            filter_all: 'All classes',
            table_caption: 'Current determination per product line',
            col_product: 'Product line',
            col_class: 'Class',
            col_option: 'Determination',
            col_instrument: 'Instrument relied on',
            col_determined_by: 'Determined by',
            col_reviewed_by: 'Reviewed by',
            col_determined_on: 'Determined on',
            col_version: 'Version',
            col_actions: 'Actions',
            empty: 'No determination is recorded yet.',
            empty_filtered: 'No product line is in this class.',
            record_button: 'Record a determination',
            record_again: 'Record again',
            history_button: 'History',
            duties_button: 'Duties',
            form_title: 'Record a scope determination',
            field_product: 'Product line',
            field_framework: 'Framework',
            choose: 'Choose…',
            no_default_note: 'Nothing is preselected and nothing is suggested: the reading is yours.',
            instrument_required: 'Required for the determination chosen.',
            cited_basis_note_heading: 'The installed pack’s reading of the lists',
            reviewer_same: 'The reviewer must be a person other than the one who determined it.',
            field_required: 'This field is required.',
            save: 'Save',
            saving: 'Saving…',
            save_failed: 'The determination could not be saved.',
            saved: 'Determination recorded.',
            products_failed: 'The product lines could not be loaded.',
            no_products: 'No product line is in the product register yet.',
            conflict: 'Someone recorded a determination for this line meanwhile. Reload the register and try again.',
            history_title: 'Determination history — {product}',
            history_failed: 'The history could not be loaded.',
            status_current: 'Current',
            status_superseded: 'Superseded',
            duties_title: 'Duties for {product}',
            duties_failed: 'The duties could not be loaded.',
            duties_no_task: "No task or deadline is created here from these duties. Where the decree states a period, the requirement's own text states it.",
            duties_reference: 'Installed for reference: no duty is shown. Install the overlay Direct to run them.',
            duties_compulsory_heading: 'Compulsory',
            duties_voluntary_heading: 'Voluntary',
            duties_role_heading: 'Attached to a role',
            duties_open: 'Open determination: the scope of this line is still to be determined. This is not a finding that no duty applies.',
            duties_counts: 'Compulsory: {applies} · Applies to how you take part (voluntary): {voluntary}',
            duties_none: 'No requirement is declared.',
            col_requirement: 'Requirement',
            col_state: 'Status for this line',
            col_reference: 'Reference',
        },
        duty_state: {
            applies: 'Applies (compulsory)',
            applies_voluntarily: 'Applies to how you take part (voluntary)',
            not_raised_by_option: 'Not raised by this determination',
            open_determination: 'Open determination',
            role_attached: 'Attached to a role; read its condition',
            voluntary_practice: 'Voluntary practice',
        },
        watch: {
            title: 'Sector Watch',
            subtitle: 'What the installed pack tracks as still to be issued by an authority, with the status it records and when it must be checked again, and the clocks the pack places on authorities. Nothing here is owed by your organisation.',
            page_guide: 'Read each tracked output as the installed pack states it. A recheck date that has passed reads as recheck overdue; nothing is re-dated. The clocks on authorities run on the authority, not on your organisation.',
            load_failed: 'The sector watch could not be loaded.',
            items_heading: 'Outputs still to be issued',
            items_caption: 'Tracked outputs, as the installed pack states them',
            col_output: 'Output',
            col_status: 'Status',
            col_basis: 'Basis of the status',
            col_checked: 'Checked on',
            col_recheck: 'Recheck due',
            col_install: 'Install mode',
            status_not_issued: 'Not issued',
            status_not_retrieved: 'Named, text not retrieved',
            status_announced_operational: 'Announced operational',
            status_issued: 'Issued',
            recheck_overdue: 'Recheck overdue',
            existing_basis: 'Instruments recorded as the existing basis',
            note_label: 'Note',
            items_none: 'No installed framework tracks an output for this register.',
            clocks_heading: 'Clocks on authorities and periods not stated',
            clocks_caption: 'Records the installed pack places on authorities, or whose period it does not state',
            col_clock: 'Record',
            col_kind: 'What it means for your organisation',
            col_period: 'Period as stated',
            col_binds: 'Applies from',
            authority_clock: 'Clock on the ministry — nothing is owed by the organisation',
            set_by_instrument: 'Set by the ministry instrument or sector law',
            no_period: 'No period stated',
            period_months: '{n} months',
            period_days: '{n} days',
            period_other: '{n} ({unit})',
            not_stated: 'Not stated',
            clocks_none: 'No clock on an authority is declared.',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Relatórios e notificações concluídos do processo',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=pt.js.map